// App translations — English dictionary.
// SOURCE OF TRUTH for the AppTranslations type (see ../app-translations.types.ts).
// Statically imported by useAppTranslations (default language + instant fallback).

export const appTranslationsEn = {
  // Practice tab (tab-bar remodel)
  practicePage: {
    title: 'Practice',
    subtitle: 'What to work on today',
    goToPracticeHint: 'Want to practice more? Open the Practice tab',
    nextUp: {
      eyebrow: 'Next up',
      fromCoach: 'From coach {name}',
      forNextQuest: 'For your next quest',
      weakestSkill: 'Your weakest skill',
      homeworkLastLesson: 'homework from your last lesson',
      homeworkFromCoach: 'homework from your coach',
      startDrill: 'Start drill',
    },
  },

  // Club tab "This week" practice board (docs/plans/2026-10-15-practice-week-v1-spec.md)
  practiceWeek: {
    eyebrow: 'This week',
    title: 'Practice board',
    xp: '{count} XP',
    you: 'You',
    unnamedPlayer: 'Player',
    logDrillToJoin: 'Log a drill to get on the board',
    newWeekStart: 'New week. Log a drill to start the board.',
    navSubtitle: "This week's practice among club-mates",
    emptyTitle: 'No board this week yet',
    goToPractice: 'Go to Practice',
    privacyTitle: 'Club practice board',
    privacyToggleLabel: 'Show me on the board',
    privacyToggleDesc: 'Club-mates see your practice this week on the Club tab',
    lastWeekEyebrow: 'Last week',
    daysLeftMany: '{count} days left',
    daysLeftOne: '1 day left',
    lastDay: 'Last day',
    newWeekChip: 'New week',
    youAreRank: "You're #{rank}",
    youLead: "You're leading",
    xpThisWeek: '{count} XP this week',
    xpLastWeek: '{count} XP last week',
    notOnBoardTitle: 'Not on the board yet',
    hiddenTitle: "You're hidden from the board",
    hiddenBody: 'Turn it back on in Privacy settings',
    finishedLastWeek: 'You finished #{rank} last week',
    gapToCatch: '{xp} XP to catch {name}',
    podiumLine: 'Podium line',
    logDrill: 'Log a drill',
    daysOne: 'Practised on 1 day',
    daysMany: 'Practised on {count} days',
    drillsOne: '1 drill',
    drillsMany: '{count} drills',
  },

  // In-app feature tour (InteractiveTour)
  tour: {
    back: 'Back',
    close: 'Close',
    last: 'Finish',
    next: 'Next',
    open: 'Open',
    skip: 'Skip tour',
    welcomeTitle: 'Welcome to Strokone',
    welcomeBody: 'Practice, coaching and your progress, all in one place. Here is a quick look around.',
    practiceTitle: 'Practice',
    practiceBody: 'Your home base. Your next drill waits at the top, picked from your coach\'s homework or your weakest skill.',
    coachTitle: 'Coach',
    coachBody: 'Lessons, homework and messages from your coach. No coach yet? Find one at your club here.',
    progressTitle: 'Progress',
    progressBody: 'Your path from First Skills to Master. Complete quests to level up.',
    clubTitle: 'Club',
    clubBody: 'Events, members and chat for your club.',
    profileTitle: 'Profile',
    profileBody: 'Your record: stats, rounds and trophies. Heading out on the course? Tap Play a round.',
    doneTitle: 'You\'re ready',
    doneBody: 'Open Practice and start your first drill.',
  },

  // Navigation translations
  navigation: {
    // Main pages
    practice: 'Practice',
    progress: 'Progress',
    coach: 'Coach',
    club: 'Club',
    home: 'Home',
    dashboard: 'Dashboard',
    play: 'Play', 
    profile: 'Profile',
    social: 'Social',
    search: 'Search',
    
    // Play & Gaming
    addPastRound: 'Add Past Round',
    browseClubs: 'Browse Clubs', 
    soloRound: 'Solo Round',
    scoreEntry: 'Score Entry',
    scorePreview: 'Score Preview',
    roundComplete: 'Round Complete',
    multiplayer: 'Multiplayer',
    createGame: 'Create Game',
    gameLobby: 'Game Lobby',
    gameComplete: 'Game Complete',
    joinGame: 'Join Game',
    
    // Club & Community
    clubHub: 'Club Hub',
    clubActivity: 'Progression', 
    clubDetails: 'Club Details',
    members: 'Members',
    clubChat: 'Club Chat',
    myClubs: 'My Clubs',
    
    // Tournament & Competition
    tournament: 'Tournament',
    leaderboard: 'Leaderboard',
    activityLeaderboard: 'Activity Leaderboard',
    championship: 'Championship',
    mostActive: 'Most Active',
    underHandicap: 'Under Handicap',
    playingStreaks: 'Playing Streaks',
    
    // Analytics & Performance
    trophyRoom: 'Trophy Room',
    statistics: 'Statistics', 
    courses: 'Courses',
    activities: 'Activities',
    posts: 'Posts',
    performanceZones: 'Performance Zones',
    courseLeaderboard: 'Course Leaderboard',
    courseRecords: 'Course Records',
    roundHistory: 'Round History',
    courseAnalytics: 'Course Analytics',
    roundAnalytics: 'Round Analytics',
    metricAnalysis: 'Metric Analysis',
    clubAnalysis: 'Club Analysis',
    analytics: 'Analytics',
    
    // Communication
    messages: 'Messages',
    chat: 'Chat',
    notifications: 'Notifications',
    inviteFriends: 'Invite Friends',
    friends: 'Friends',
    feedback: 'Feedback',
    expenses: 'Expenses',
    events: 'Events',
    myEvents: 'My events',
    activityAnalytics: 'Activity analytics',
    scoringAnalytics: 'Scoring analytics',
  },
  
  // Common translations
  common: {
    expand: 'Expand',
    loading: 'Loading',
    error: 'Error',
    errorTitle: 'Error',
    failed: 'Failed',
    failedToLoad: 'Failed to load',
    failedToSave: 'Failed to save',
    pleaseTryAgain: 'Please try again.',
    success: 'Success',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    invite: 'Invite',
    edit: 'Edit',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    search: 'Search',
    filter: 'Filter',
    sort: 'Sort',
    select: 'Select',
    confirm: 'Confirm',
    tryAgain: 'Try Again',
    noData: 'No Data',
    progress: 'Progress',
    total: 'Total',
    acceptAll: 'Accept All',
    skip: 'Skip',
    details: 'Details',
    preview: 'Preview',
    play: 'Play',
    all: 'All',
    status: 'Status',
    membership: 'Membership',
    live: 'Live',
    offline: 'Offline',
    refresh: 'Refresh',
    events: 'Events',
    profile: 'Profile',
    thisMonth: 'This Month',
    thisWeek: 'This Week',
    older: 'Older',
    score: 'Score',
    hcp: 'HCP',
    lastPlayed: 'Last played',
    noActivityFound: 'No Activity Found',
    avgHandicap: 'Avg Handicap',
    consistency: 'Consistency',
    minimumRequired: 'minimum required',
    monthlyPerformance: 'Monthly Performance',
    insufficientData: 'Insufficient Data',
    // Communications
    sending: 'Sending...',
    updating: 'Updating...',
    subject: 'Subject',
    message: 'Message',
    characters: 'characters',
    cancelEdit: 'Cancel Edit',
    editCancelled: 'Edit cancelled',
    share: 'Share',
    unknown: 'Unknown',
    unknownError: 'Unknown error',
    update: 'Update',
    authenticationRequired: 'Authentication required',
    resolved: 'Resolved',
    notes: 'Notes',
    email: 'Email',
    vs: 'vs',
    pending: 'Pending',
    settings: 'Settings',
    hide: 'Hide',
    completed: 'Completed',
    players: 'Players',
    yes: 'Yes',
    no: 'No',
    add: 'Add',
    adding: 'Adding...',
    selected: 'Selected',
    current: 'Current',
    inProgress: 'In Progress',
    optional: 'optional',
    today: 'Today',
    yesterday: 'Yesterday',
    active: 'Active',
    inactive: 'Inactive',
    configure: 'Configure',
    reply: 'Reply',
    send: 'Send',
    loadMore: 'Load more',
    unread: 'Unread',
    staff: 'Staff',
    applicant: 'Applicant',
    risk: 'Risk',
    compare: 'Compare',
    viewAll: 'View All',
    export: 'Export',
    fullReport: 'Full report',
    viewStats: 'View stats',
    sendEmail: 'Send email',
    viewMember: 'View member',
    sendMessage: 'Send message',
    unassigned: 'Unassigned',
    done: 'Done',
    saved: 'Saved',
    untitled: 'Untitled',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    upcoming: 'Upcoming',
    recent: 'Recent',
    saving: 'Saving...',
    member: 'Member',
    actions: 'Actions',
    coach: 'Coach',
    joined: 'Joined',
    video: 'Video',
    exercise: 'Exercise',
    play2: 'Play',
    emptyState: {
      clearFilter: 'Clear Filter',
      resetToAllTime: 'Reset to all time',
      contactSupport: 'Contact support',
      couldNotLoad: "Couldn't load right now",
      somethingWentWrong: 'Something went wrong. Check your connection and try again.',
    },
  },

  // Error translations
  errors: {
    dataLoadFailed: 'Some data could not be loaded',
    tryRefreshingOrContactSupport: 'Try refreshing the page or contact support if the problem persists.',
    errorLoadingData: 'Error Loading Data',
    unableToLoadPerformanceTrends: 'Unable to load performance trends.',
    notEnoughPerformanceData: 'Not enough performance data available for the selected period.'
  },

  // Placeholder translations
  placeholders: {
    search: 'Search...',
    searchByName: 'Search by name...',
    searchByNameOrEmail: 'Search by name or email...',
    enterName: 'Enter name',
    enterEmail: 'Enter email',
    enterPassword: 'Enter your password',
    enterPhone: 'Phone number',
    enterPostalCode: 'Postal code',
    enterAddress: 'Enter address',
    selectStatus: 'Select status',
    selectCoach: 'Select coach...',
    selectFormat: 'Select format',
    allStatus: 'All Status',
    allTiers: 'All Tiers',
    sortBy: 'Sort by',
    addNotes: 'Add notes...',
    guestName: 'Guest name',
    fullName: 'Full name',
    courseName: 'Course name',
    courseAddress: 'Course address',
    writeNote: 'Write your note here...',
    searchParticipants: 'Search participants...',
    courseDescription: 'Brief description of the course',
    specialRequests: 'Special requests or notes...',
    searchEvents: 'Search events...',
    handicap: 'HCP',
    taskTitle: 'Enter task title...',
    description: 'Enter description...',
    estimatedTime: 'e.g., 30',
    assignmentNotes: 'Add any notes or instructions for this assignment...',
    shiftNotes: 'Optional notes about this shift',
    chooseResolution: 'Choose resolution...',
    documentReasoning: 'Document your reasoning for this resolution...',
    searchMembers: 'Search members...'
  },

  // Coach Videos tab (coaching portal)
  coachVideos: {
    source: 'Source',
    all: 'All',
    uploaded: 'Uploaded',
    links: 'Links',
    addVideo: 'Add Video',
    videoLibrary: 'Video Library',
    videoCount: '{count} video',
    videoCountPlural: '{count} videos',
    loadingVideos: 'Loading videos...',
    searchVideos: 'Search videos...',
    noVideosMatchSearch: 'No videos match your search',
    noUploadedVideosYet: 'No uploaded videos yet',
    noLinkedVideosYet: 'No linked videos yet',
    noVideosYet: 'No videos yet',
    tryAdjustingSearch: 'Try adjusting your search terms',
    uploadFromDevice: 'Upload a video from your phone or computer',
    addYoutubeOrVimeo: 'Add a YouTube or Vimeo link',
    addFirstVideo: 'Add your first video to build your coaching library',
    uncategorized: 'Uncategorized',
    link: 'Link',
    youtube: 'YouTube',
    vimeo: 'Vimeo',
    addNewVideo: 'Add a new video',
    uploadOrLinkYoutube: 'Upload or link a YouTube video',
    // Add Video modal
    pasteLink: 'Paste Link',
    upload: 'Upload',
    youtubeOrVimeoUrl: 'YouTube or Vimeo URL',
    supportsYoutubeVimeo: 'Supports YouTube and Vimeo links',
    title: 'Title',
    category: 'Category',
    descriptionOptional: 'Description (optional)',
    tagsOptional: 'Tags (comma-separated, optional)',
    uploadVideo: 'Upload Video',
    tapToRecordOrSelect: 'Tap to record or select video',
    mp4MovUpTo100mb: 'MP4, MOV up to 100MB',
    uploading: 'Uploading...',
    adding: 'Adding...',
    failedToAddVideo: 'Failed to add video',
    categoryDriving: 'Driving',
    categoryIronPlay: 'Iron Play',
    categoryShortGame: 'Short Game',
    categoryPutting: 'Putting',
    categoryBunker: 'Bunker',
    categoryMental: 'Mental',
    categoryFitness: 'Fitness',
    categoryWarmUp: 'Warm-up',
    // Section labels
    detailsSectionLabel: 'Details',
    optionalSectionLabel: 'Optional',
    titleRequired: 'Title *',
    categoryRequired: 'Category *',
    descriptionLabel: 'Description',
    tagsLabel: 'Tags',
    separateWithCommas: 'Separate with commas',
    titlePlaceholder: 'e.g. Driver tempo drill',
    selectCategory: 'Select category',
    tagPlaceholder: 'putting, drill, beginner',
    addToLibrary: 'Add to your coaching library',
  },

  // Coach My Profile page (/club-admin/coaching/my-profile)
  coachMyProfile: {
    profilePhotoAndName: 'Profile Photo & Name',
    change: 'Change',
    firstName: 'First Name',
    lastName: 'Last Name',
    acceptingStudents: 'Accepting Students',
    notAccepting: 'Not Accepting',
    aboutYou: 'About You',
    bioTip: 'Tip: Mention your teaching style, notable achievements, and what students can expect',
    bioPlaceholder: 'Tell prospective students about yourself, your coaching philosophy, and what makes your approach unique...',
    specialties: 'Specialties',
    specialtiesHint: 'Select the areas where you excel (choose up to 5)',
    addCustomSpecialtyPlaceholder: 'Add custom specialty...',
    add: 'Add',
    certifications: 'Certifications',
    addCertificationPlaceholder: 'Add certification (e.g., PGA Professional, TPI Certified)...',
    experienceAndRates: 'Experience & Rates',
    yearsOfExperience: 'Years of Experience',
    years: 'years',
    hourlyRate: 'Hourly Rate',
    perHour: '/hour',
    maximumStudents: 'Maximum Students',
    students: 'students',
    acceptingNewStudents: 'Accepting New Students',
    yes: 'Yes',
    no: 'No',
    saving: 'Saving...',
    saveProfile: 'Save Profile',
    completeYourProfile: 'Complete Your Profile',
    completionMessage: 'Members are 3x more likely to choose coaches with complete profiles',
    createCoachProfileTitle: 'Create Your Coach Profile',
    createCoachProfileDesc: 'Set up your coach profile to start managing students and scheduling sessions.',
    creatingProfile: 'Creating Profile...',
    createCoachProfile: 'Create Coach Profile',
    profileCreatedToast: 'Coach profile created! You can now edit your profile.',
    noClubSelected: 'No club selected',
    failedToCreateProfile: 'Failed to create profile',
    maxSpecialtiesAllowed: 'Maximum 5 specialties allowed',
    imageSizeError: 'Image must be under 5MB',
    pleaseSelectImage: 'Please select an image file',
    failedToUploadAvatar: 'Failed to upload avatar',
    profileUpdatedToast: 'Profile updated successfully!',
    failedToSaveProfile: 'Failed to save profile',
    livePreview: 'Live Preview',
    asSeenByMembers: 'As seen by members',
    yourName: 'Your Name',
    yearsExp: 'years exp.',
    bioPreviewPlaceholder: 'Your bio will appear here',
    moreCount: '+{count} more',
    certificationCount: '{count} Certification',
    certificationCountPlural: '{count} Certifications',
    requestCoaching: 'Request Coaching',
    tipsTitle: 'Tips for a Great Profile',
    tipPhoto: 'Add a professional photo to increase trust',
    tipBio: 'Write a bio that highlights your unique approach',
    tipCertifications: 'List certifications to build credibility',
    // Specialty labels (for display)
    specialtyDriving: 'Driving',
    specialtyIronPlay: 'Iron Play',
    specialtyShortGame: 'Short Game',
    specialtyPutting: 'Putting',
    specialtyBunker: 'Bunker Play',
    specialtyMental: 'Mental Game',
    specialtyJunior: 'Junior Coaching',
    specialtyBeginner: 'Beginners',
    specialtyCompetition: 'Competition Prep',
  },

  // Coaching Revenue page (/club-admin/coaching/revenue)
  coachingRevenue: {
    title: 'Revenue Dashboard',
    subtitle: 'Track your coaching earnings and payments',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    thisYear: 'This Year',
    allTime: 'All Time',
    export: 'Export',
    totalRevenue: 'Total Revenue',
    paid: 'Paid',
    pending: 'Pending',
    avgPerSession: 'Avg per Session',
    averagePrice: 'Average price',
    sessions: 'sessions',
    noCoachProfile: 'No Coach Profile',
    noCoachProfileDesc: 'You need a coach profile to view revenue data. Please set up your coach profile first.',
    noRevenueData: 'No revenue data available',
    revenueByLessonType: 'Revenue by Lesson Type',
    revenueByStudent: 'Revenue by Student',
    noDataAvailable: 'No data available',
    lessonType: 'Lesson Type',
    status: 'Status',
    allTypes: 'All Types',
    allStatus: 'All Status',
    student: 'Student',
    location: 'Location',
    amount: 'Amount',
    date: 'Date',
    actions: 'Actions',
    practiceArea: 'Practice Area',
    markPaid: 'Mark Paid',
    noTransactionsMatch: 'No transactions match your filters',
    noTransactionsYet: 'No transactions yet',
    showingTransactions: 'Showing {from}-{to} of {total} transactions',
    transactionDetails: 'Transaction Details',
    sessionPrice: 'Session Price',
    sessionDetails: 'Session Details',
    dateAndTime: 'Date & Time',
    paymentStatus: 'Payment Status',
    sendReceipt: 'Send Receipt',
    downloadPdf: 'Download PDF',
    activityLog: 'Activity Log',
    paymentCompleted: 'Payment completed',
    paymentPending: 'Payment pending',
    payAtClub: 'Pay at Club',
    freeSession: 'Free session',
    sessionScheduled: 'Session scheduled',
    sessionBooked: 'Session booked',
    reminderSent: 'Reminder sent',
    paymentReceived: 'Payment received',
    selectClubFirst: 'Select a golf club first',
    selectClubForReminders: 'Use the club selector at the top of the page to enable sending reminders',
    searchPlaceholder: 'Search by student name...',
    // Session types
    typeLesson: 'Lesson',
    typePlayingLesson: 'Playing Lesson',
    typePractice: 'Practice',
    typeAssessment: 'Assessment',
    typeFitting: 'Club Fitting',
    // Status labels (for badges)
    statusFree: 'Free',
    statusFailed: 'Failed',
    statusRefunded: 'Refunded',
    // Filter chip labels (compact)
    filterAll: 'All',
    filterPlaying: 'Playing',
    // STATUS_PILL labels
    statusPaid: 'Paid',
    statusPending: 'Pending',
    statusPayAtClub: 'Pay at Club',
    // Toast / reminder
    sendingReminder: 'Sending…',
    sendReminder: 'Send Payment Reminder',
    marking: 'Marking…',
    markAsPaid: 'Mark as Paid',
    sessionCancelled: 'Session cancelled',
  },

  // Coaching Settings page (/club-admin/coaching/settings)
  coachingSettings: {
    coachingActive: 'Coaching Active',
    coachingPaused: 'Coaching Paused',
    membersCanViewAndBook: 'Members can view coaches and book lessons',
    coachingHidden: 'Coaching features are hidden from members',
    active: 'Active',
    paused: 'Paused',
    toastEnabled: 'Coaching enabled for members',
    toastDisabled: 'Coaching disabled',
    toastFailedUpdate: 'Failed to update',
    toastChangesDiscarded: 'Changes discarded',
    toastSettingsSaved: 'Settings saved',
    bookingSettings: 'Booking Settings',
    bookingSettingsDesc: 'Configure how members book lessons',
    allowMemberBooking: 'Allow Member Booking',
    membersBookDirectly: 'Members can book lessons directly',
    requireCoachApproval: 'Require Coach Approval',
    coachMustConfirm: 'Coach must confirm bookings',
    bookingInfoTip: 'When coaching is active, members will see the Coaching section in their Club Hub and can browse available coaches.',
    myCoachSettings: 'My Coach Settings',
    paymentPreferences: 'Payment Preferences',
    paymentPreferencesDesc: 'How students pay for your lessons',
    defaultPaymentMethod: 'Default Payment Method',
    paymentOnline: 'Online Payment (Card)',
    paymentPayAtClub: 'Pay at Club',
    paymentCash: 'Cash',
    paymentFree: 'Free (No Payment)',
    acceptedPaymentMethods: 'Accepted Payment Methods',
    acceptCardViaStripe: 'Accept card payments via Stripe',
    studentPaysAtProShop: 'Student pays at pro shop',
    acceptCashDirectly: 'Accept cash payments directly',
    bookingPolicies: 'Booking Policies',
    bookingPoliciesDesc: 'Rules for booking and cancellation',
    minimumBookingNotice: 'Minimum Booking Notice',
    hoursInAdvance: 'hours in advance',
    maximumAdvanceBooking: 'Maximum Advance Booking',
    daysInAdvance: 'days in advance',
    autoAcceptBookings: 'Auto-accept Bookings',
    skipManualApproval: 'Skip manual approval',
    cancellationWindow: 'Cancellation Window',
    hoursBeforeLesson: 'hours before lesson',
    notifications: 'Notifications',
    notificationsDesc: 'When to notify you about bookings',
    newBookings: 'New Bookings',
    whenStudentBooks: 'When student books',
    cancellations: 'Cancellations',
    whenStudentCancels: 'When student cancels',
    payments: 'Payments',
    whenPaymentReceived: 'When payment received',
    saving: 'Saving...',
    savePaymentSettings: 'Save Payment Settings',
    unsavedChanges: 'You have unsaved changes',
    discard: 'Discard',
    save: 'Save',
    saveChanges: 'Save Changes',
    coachingSettingsTitle: 'Coaching Settings',
    configurePaymentPrefs: 'Configure your payment and booking preferences',
    defaultPaymentMethodPreselected: 'This will be pre-selected when students book lessons',
    cancellationPolicyForStudents: 'Cancellation Policy (shown to students)',
    newBookingNotifications: 'New Booking Notifications',
    whenStudentBooksLesson: 'Get notified when a student books a lesson',
    cancellationNotifications: 'Cancellation Notifications',
    whenStudentCancelsLesson: 'Get notified when a student cancels a lesson',
    paymentNotifications: 'Payment Notifications',
    whenPaymentIsReceived: 'Get notified when a payment is received',
    clubAffiliations: 'Club Affiliations',
    invite: 'Invite',
    skipManualApprovalDesc: 'Automatically confirm booking requests without manual approval',
    studentsCanCancelDesc: 'Students can cancel without penalty up to this time before the session',
    calendlyConnectionError: 'Connection Error',
    calendlyUnableToCheck: 'Unable to check Calendly status',
    calendlySynced: 'Synced',
    calendlyConnect: 'Connect Calendly',
    calendlySyncBookings: 'Sync your bookings automatically',
    calendlyDisconnect: 'Disconnect',
    calendlyConnecting: 'Connecting...',
    calendlyConnectCta: 'Connect',
    calendlyViewPage: 'View Scheduling Page',
    stripePayments: 'Stripe Payments',
    stripePaymentsDesc: 'Accept card payments directly to your bank account',
    stripeConnected: 'Connected',
    stripeNotConnected: 'Not connected',
    stripeConnectCta: 'Connect Stripe',
    stripeConnectedTo: 'Connected to',
    stripeDisconnect: 'Disconnect',
    stripeRequiredForOnline: 'Connect Stripe to accept card payments',
    stripeOnlinePaymentsGated: 'Connect your Stripe account to accept online payments',
    stripeConnectFailed: 'Failed to connect Stripe',
    stripeCallbackVerifying: 'Verifying your Stripe account…',
    stripeCallbackSuccess: 'Your Stripe account is connected',
    stripeCallbackError: 'Could not verify Stripe connection',
  },

  // Coach Messages tab (coaching portal)
  coachMessages: {
    title: 'Messages',
    searchPlaceholder: 'Search students...',
    noStudentsYet: 'No students yet',
    noStudentsDesc: 'Add students to start messaging them.',
    noStudentsMatchSearch: 'No students matching',
    tapToStartMessaging: 'Tap to start messaging',
    noMessagesYet: 'No messages yet',
    opening: 'Opening...',
    you: 'You',
    now: 'Now',
    // Onboarding (no profile / no students)
    stayConnected: 'Stay connected with every student',
    stayConnectedDesc: 'Direct messaging between you and your students — keep the coaching conversation going between sessions.',
    benefit1Title: 'One conversation per student',
    benefit1Desc: 'All your messages with a student in one thread — always in context with their sessions.',
    benefit2Title: 'Share feedback instantly',
    benefit2Desc: "Send notes, encouragement or corrections right after a lesson while it's still fresh.",
    benefit3Title: 'Students can reach you too',
    benefit3Desc: "Students message you directly from their app — you'll never miss a question.",
    setupProfileFirst: 'Set up your profile first',
    setupProfileFirstDesc: 'Create your coach profile so students can find and message you.',
    setupProfileCta: 'Set up profile',
    getFirstStudent: 'Get your first student',
    getFirstStudentDesc: 'Affiliate with a club so members can find you, or go to My Students to add someone directly.',
    findAClub: 'Find a club',
    myStudents: 'My Students',
  },

  coachPricing: {
    trackRevenue: 'Track your coaching revenue',
    trackRevenueDesc: "See everything you've earned, set your rates, and understand which students and session types drive your income.",
    benefit1Title: 'Earnings overview',
    benefit1Desc: 'Total revenue by week, month, year — with a breakdown by session type.',
    benefit2Title: 'Revenue by student',
    benefit2Desc: 'See which students contribute most and spot opportunities to grow.',
    benefit3Title: 'Set your rates',
    benefit3Desc: 'Configure your hourly rate and session pricing directly from your profile.',
    setupProfileFirst: 'Set up your profile first',
    setupProfileFirstDesc: 'Create your coach profile to unlock revenue tracking and set your rates.',
    setupProfileCta: 'Set up profile',
    filters: 'Filters',
    period: 'Period',
    clear: 'Clear',
    exportCsv: 'Export CSV',
    periodWeek: 'This Week',
    periodMonth: 'This Month',
    periodYear: 'This Year',
    periodAll: 'All Time',
  },

  coachSettings: {
    createProfileFirst: 'Create your coach profile first',
    createProfileFirstDesc: 'Settings like notifications, availability and club affiliations will be available once your profile is set up.',
    setupProfileCta: 'Set up profile',
  },

  coachSessions: {
    buildSessions: 'Build practice sessions for your students',
    buildSessionsDesc: 'Create reusable plans with exercises and videos, send them to students, and track their progress — all from one place.',
    feature1Title: 'Exercises',
    feature1Desc: 'A library of golf drills — browse by skill area or create your own.',
    feature2Title: 'Videos',
    feature2Desc: 'Attach YouTube or Vimeo clips to reinforce your coaching concepts.',
    feature3Title: 'Session plans',
    feature3Desc: 'Combine exercises and videos into a plan, then send it to any student.',
    setupProfileFirst: 'Set up your profile first',
    setupProfileFirstDesc: 'Create your coach profile so students can find you and you can start sending sessions.',
    setupProfileCta: 'Set up profile',
    getFirstStudent: 'Get your first student',
    getFirstStudentDesc: 'Once you have students you can build and send them practice plans. Affiliate with a club or add a student directly.',
    findAClub: 'Find a club',
    myStudents: 'My Students',
    howItWorks: 'How it works',
    step1Label: 'Build a plan',
    step1Sub: 'Add exercises and videos into a reusable session.',
    step2Label: 'Send to students',
    step2Sub: 'Deliver the same plan to one or many students.',
    step3Label: 'Track progress',
    step3Sub: "See who's done what, right from this page.",
    readyToBuild: 'Ready to build your first session?',
    readyToBuildDesc: 'Create once, send to any student, track their progress here.',
    buildASession: 'Build a session',
    sort: 'Sort',
    sortLastSent: 'Last sent',
    sortAZ: 'A–Z',
    sortBy: 'Sort by',
    reset: 'Reset',
    sentAgo: 'Sent {ago}',
    deleteConfirm: 'Delete "{title}"? This can\'t be undone.',
  },

  // Coach Schedule Tab translations
  coachSchedule: {
    // View modes
    month: 'Month',
    week: 'Week',
    today: 'Today',
    time: 'Time',

    // Legend
    lesson: 'Lesson',
    playingLesson: 'Playing Lesson',
    practice: 'Practice',
    practiceSession: 'Practice Session',
    assessment: 'Assessment',
    clubFitting: 'Club Fitting',
    playing: 'Playing',

    // Calendar
    monthTotal: '{month} total',
    sessions: 'sessions',
    session: 'session',
    hours: 'h',

    // Actions
    blockTime: 'Block Time',
    groupSession: 'Group Session',
    addSession: 'Add Session',
    add: 'Add',

    // Day detail
    noSessionsOnDay: 'No sessions on this day',
    selectAnotherDate: 'Select another date or add a new session',
    notAvailableOnDay: 'Day off',
    notAvailableOnDayFull: 'You\'re not available on this day',
    notAvailableOnDaySub: 'This is a day off — update your availability in your profile',
    group: 'Group',
    student: 'Student',
    groupSessionStudents: 'Group Session ({count} students)',
    now: 'NOW',
    confirmed: 'Confirmed',
    pending: 'Pending',
    start: 'Start',
    starting: 'Starting...',
    notes: 'Notes',
    reschedule: 'Reschedule',
    cancel: 'Cancel',
    practiceArea: 'Practice Area',
    more: '+{count} more',

    // Booking requests
    // Onboarding empty state (no coach profile yet)
    onboardingTitle: 'Get your coaching schedule ready',
    onboardingSubtitle: 'Complete these steps to start booking sessions with students.',
    step1Title: 'Create your coach profile',
    step1Desc: 'Add your bio, specialties, and rates so students can find you.',
    step1Cta: 'Set up profile',
    step2Title: 'Affiliate with a club',
    step2Desc: 'Join a club to appear in searches and coach affiliated members.',
    step2Cta: 'Find clubs',
    step3Title: 'Your first student will follow',
    step3Desc: 'Once your profile is live, students can request coaching from you.',

    bookingRequests: 'Booking Requests',
    noPendingRequests: 'No pending requests',
    noBookingsYet: 'No bookings yet',
    accept: 'Accept',
    decline: 'Decline',
    declined: 'Declined',
    dateAndTime: 'Date & Time',
    playerNotes: 'Player Notes',
    location: 'Location',
    viewStudent: 'View Student',
    payment: 'Payment',
    prepareSession: 'Prepare Session',
    paid: 'Paid',
    unpaid: 'Unpaid',

    // Session notes dialog
    sessionNotes: 'Session Notes',
    focusAreas: 'Focus Areas',
    preparationNotes: 'Preparation Notes',
    coachNotes: 'Coach Notes',
    studentFeedback: 'Student Feedback',
    noNotesYet: 'No notes for this session yet.',
    close: 'Close',
    viewFullProfile: 'View Full Profile',

    // Toast messages
    sessionStarted: 'Session started',
    failedToStartSession: 'Failed to start session',

    // Create Group Session Modal
    createGroupSession: 'Create Group Session',
    selectStudentsMin: 'Select Students (min 2)',
    noStudentsAvailable: 'No students available',
    selectedCount: '{count} selected',
    date: 'Date',
    sessionType: 'Session Type',
    duration: 'Duration',
    mins30: '30 min',
    mins60: '60 min',
    mins90: '90 min',
    locationOptional: 'Location (optional)',
    notesOptional: 'Notes (optional)',
    failedToCreateSession: 'Failed to create session',
    creating: 'Creating...',
    createGroupSessionCount: 'Create Group Session ({count})',
    groupSessionCreated: 'Group session created',

    // Schedule session UI strings
    nowInSession: 'Now · in session',
    nextLesson: 'Next lesson',
    away: 'away',
    prepare: 'Prepare',

    // Needs closeout section
    needsCloseout: 'Needs Closeout',
    pastLessonsWithoutNotes: 'Past lessons without notes',
    closeOut: 'Close out',
    editBlock: 'Edit Block',
    saveChanges: 'Save Changes',
    eyebrowSchedule: 'SCHEDULE',
    bookLesson: 'Book a Lesson',

    // Session status labels
    blocked: 'Blocked',
    noShow: 'No-show',
    cancelled: 'Cancelled',
    completed: 'Completed',

    // Recurrence labels
    weekly: 'Weekly',
    biweekly: 'Biweekly',
    monthly: 'Monthly',

    // Edit/delete actions
    edit: 'Edit',
    delete: 'Delete',

    // Cancel session dialog
    cancelSession: 'Cancel Session',
    cancelSessionConfirm: 'Are you sure you want to cancel this session with {name}? This action cannot be undone.',
    cancellationReason: 'Reason for cancellation',
    cancellationReasonPlaceholder: 'e.g., Coach unavailable, Weather conditions, etc.',
    cancellationReasonPlaceholderPlayer: 'e.g., Family emergency, Injury, Scheduling conflict...',
    cancelling: 'Cancelling...',
    optional: 'optional',
    keepSession: 'Keep Session',
    yesCancelSession: 'Yes, Cancel Session',

    allSessionsClosedOut: 'All sessions closed out',
    todaySuffix: '· today',
    daysAgoSuffix: '{n}d ago',

    // Calendar day headers
    daySun: 'Sun',
    dayMon: 'Mon',
    dayTue: 'Tue',
    dayWed: 'Wed',
    dayThu: 'Thu',
    dayFri: 'Fri',
    daySat: 'Sat',

    // Player fallback
    unknownPlayer: 'Unknown Player',

    // Cancel session toasts
    sessionCancelled: 'Session Cancelled',
    sessionCancelledDesc: 'The session has been cancelled successfully.',
    errorTitle: 'Error',
    failedToCancelSession: 'Failed to cancel session',
  },

  coachAvailabilityCard: {
    title: 'Availability Hours',
    subtitle: "Set your default working hours. Members will see when you're available to book.",
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
    available: 'Available',
    off: 'Off',
    dayOff: 'Day off',
    saving: 'Saving...',
    saveAvailability: 'Save Availability',
  },

  // Gauge component labels
  gauge: {
    peers: 'Peers',
    you: 'You',
    target: 'Target'
  },

  // Golf-specific translations
  golf: {
    score: 'Score',
    par: 'Par',
    toPar: 'To Par',
    handicap: 'Handicap',
    holes: 'Holes',
    rounds: 'Rounds',
    putts: 'Putts',
    puttingAverage: 'Putting Average',
    greensInRegulation: 'Greens in Regulation',
    fairwaysHit: 'Fairways Hit',
    scrambling: 'Scrambling',
    gir: 'GIR',
    fir: 'FIR',
    driving: 'Driving',
    approach: 'Approach',
    shortGame: 'Short Game',
    putting: 'Putting',
    front9: 'Front 9',
    back9: 'Back 9',
    eagle: 'Eagle',
    birdie: 'Birdie',
    bogey: 'Bogey',
    doubleBogey: 'Double Bogey',
    albatross: 'Albatross',
    holeInOne: 'Hole in One',
    course: 'Course',
    tee: 'Tee',
    club: 'Club',
    distance: 'Distance',
    yardage: 'Yardage',
    strokesGained: 'Strokes Gained',
    totalStrokes: 'Total Strokes',
    backToDashboard: 'Back to Dashboard',
    playARound: 'Play a Round',
    noRoundsEmptyState: {
      title: 'No Rounds Yet',
      subtitle: 'Start tracking your {metric}',
      message: 'Play a round and come back to see your performance information.'
    }
  },

  // Dashboard translations
  dashboard: {
    welcome: 'Welcome! Here are your key golf metrics & insights',
    recentRounds: 'Recent Rounds',
    viewAllRounds: 'View All Rounds',
    showLess: 'Show Less',
    quickStats: 'Quick Stats',
    performanceOverview: 'Performance Overview',
    recentActivity: 'Recent Activity',
    performanceSummary: 'Performance Summary',
    keyPerformanceMetrics: 'Key Performance Metrics',
    bestRound: 'Best Round',
    bestHole: 'Best Hole',
    vsRecent: 'vs Recent',
    avgScore: 'Avg Score',
    rounds: 'Rounds',
    metricsTab: 'Metrics',
    recent: 'Recent',
    score: 'Score',
    toPar: 'To Par',
    fairways: 'Fairways',
    gir: 'GIR',
    fir: 'FIR',
    noData: 'No Data',
    tapToExplore: 'Tap to explore',
    loading: {
      personalized: 'Loading your personalized dashboard...',
      performance: 'Loading your performance data...'
    },
    metrics: {
      title: 'Key Performance Metrics',
      handicap: 'Handicap Index',
      averageScore: 'Scoring',
      accuracy: 'Accuracy',
      gir: 'Greens in Regulation',
      fir: 'Fairways Hit',
      putting: 'Putting',
      scrambling: 'Scrambling',
      clubConsistency: 'Driving',
      strokesGained: 'Strokes Gained'
    },

    // Additional Dashboard Content
    handicapIndex: 'Handicap Index',
    currentPerformanceLevel: 'Your current performance level',
    currentHandicap: 'Current Handicap',
    clubConsistency: 'Driving',
    strokesGained: 'Strokes Gained',
    vsField: 'vs field',
    thisMonth: 'this month',
    improving: 'Improving',
    analyzing: 'Analyzing',
    
    // Error Messages
    failedToLoadRounds: 'Failed to load rounds',
    failedToLoadRecentRounds: 'Failed to load recent rounds',
    failedToDeleteRound: 'Failed to delete round',
    errorLoadingData: 'Error loading data. Please refresh the page.',
    
    // Recent Rounds Section
    roundsCount: '{count} rounds',
    multiplayerGame: 'Multiplayer game',
    round: 'Round',
    roundDeletedSuccessfully: '{roundType} deleted successfully',
    noRoundsFound: 'No rounds found',
    startPlayingToBuildHistory: 'Start playing to build your round history',
    startPlaying: 'Start Playing',
    putts: 'Putts',
    completed: 'Completed',
    view: 'View',
    
    // Delete Dialog
    deleteRound: 'Delete Round',
    deleteRoundConfirmation: 'Are you sure you want to delete this round? This action cannot be undone and will remove all associated scores and statistics.',
    cancel: 'Cancel',

    // Hide Round (for tournament rounds)
    hideRound: 'Hide Round',
    hideRoundConfirmation: 'This will hide the tournament round from your dashboard and statistics. The official tournament result will remain unchanged.',

    // Filters
    filters: {
      label: 'Filters',
      roundsLabel: 'rounds',
      timeRange: {
        last5: 'Last 5',
        last10: 'Last 10',
        last20: 'Last 20',
        ytd: 'YTD',
        allTime: 'All Time',
        custom: 'Custom'
      },
      gameMode: {
        all: 'All Games',
        solo: 'Solo Rounds',
        multiplayer: 'Multiplayer',
        tournament: 'Tournament'
      },
      course: {
        all: 'All Courses',
        selected: 'Selected Course'
      },
      holeCount: {
        all: 'All Holes',
        nine: '9 Holes',
        eighteen: '18 Holes'
      }
    },

    // Hero Section Stats
    lastScore: 'Last Score',
    bestScore: 'Best Score',
    totalRounds: 'Total Rounds',

    // Tabs
    tabs: {
      statistics: 'Statistics',
      rounds: 'Rounds'
    },

    // Empty State
    emptyState: {
      title: 'No rounds played yet',
      description: 'Play a round and come back to see your Performance information.',
      cta: 'Start Your First Round'
    },

    // Error State
    error: {
      loadFailed: 'Failed to load dashboard data',
      retry: 'Retry'
    },

    // Dev Only Badge
    devOnly: 'Dev Only',

    // Round Types (for RoundCard)
    roundTypes: {
      tournament: 'Tournament',
      multiplayer: 'Multiplayer',
      solo: 'Solo'
    },
    inStats: 'In stats',

    // Filter Bottom Sheets
    bottomSheets: {
      gameMode: {
        title: 'Game Mode',
        generalPlay: 'General Play',
        singles: 'Singles',
        multiplayer: 'Multiplayer',
        tournament: 'Tournament',
        roundsCount: '{count} Rounds'
      },
      rounds: {
        title: 'Select Rounds',
        quickSelect: 'Quick Select',
        selectDateInterval: 'Select date interval',
        yearToDate: 'Year to Date',
        apply: 'Apply',
        dayNames: ['M', 'T', 'W', 'T', 'F', 'S', 'S']
      },
      course: {
        title: 'Select Course',
        allCourses: 'All Courses',
        recentlyPlayed: 'Recently Played',
        noCoursesYet: 'No courses played yet'
      },
      holeCount: {
        title: 'Hole Count',
        allRounds: 'All Rounds',
        eighteenHoles: '18 Holes',
        nineHoles: '9 Holes'
      }
    },

    // EnhancedBoldDashboard
    enhancedBold: {
      title: 'Enhanced Bold Dashboard',
      back: 'Back',
      last30Days: 'Last 30 days',
      performanceOverview: 'Performance Overview',
      avgScore: 'Avg Score',
      playSoloRound: 'Play Solo Round',
      viewFullAnalytics: 'View Full Analytics',
      noDataAvailable: 'No data available',
      playFirstRound: 'Play Your First Round',
    },

    // GoalManager
    goalManager: {
      yourGolfGoals: 'Your Golf Goals',
      trackProgressSubtitle: 'Track your progress towards improvement',
      addGoal: 'Add Goal',
      editGoalTitle: 'Edit Goal',
      createNewGoalTitle: 'Create New Goal',
      setTargetDescription: 'Set a target to track your improvement',
      metricType: 'Metric Type',
      selectMetric: 'Select a metric',
      targetValue: 'Target Value',
      description: 'Description',
      descriptionPlaceholder: 'e.g., Break 80 consistently by summer',
      targetDate: 'Target Date (Optional)',
      targetDateDescription: 'When do you want to achieve this goal?',
      cancel: 'Cancel',
      updateGoal: 'Update Goal',
      createGoal: 'Create Goal',
      loadingGoals: 'Loading goals...',
      noGoalsTitle: 'No goals set yet',
      noGoalsDescription: 'Create your first goal to start tracking your progress',
      createFirstGoal: 'Create Your First Goal',
      achieved: 'Achieved',
      progress: 'Progress',
      current: 'Current',
      target: 'Target',
      daysRemaining: '{count} days remaining',
      dueToday: 'Due today!',
      daysOverdue: '{count} days overdue',
      goalCreated: 'Goal created successfully!',
      goalCreatedError: 'Failed to create goal: {error}',
      goalDeleted: 'Goal deleted successfully!',
      goalDeletedError: 'Failed to delete goal',
      goalUpdated: 'Goal updated successfully!',
      goalUpdatedError: 'Failed to update goal',
      confirmDelete: 'Are you sure you want to delete this goal?',
    },

    // MetricDrilldownModal
    drilldown: {
      analysisTitle: '{label} Analysis',
      tapToExploreDescription: 'Tap to explore your performance data in detail',
      dashboardBreadcrumb: 'Dashboard',
      selectTimeRange: 'Select time range',
      last30Days: 'Last 30 Days',
      last90Days: 'Last 90 Days',
      last6Months: 'Last 6 Months',
      lastYear: 'Last Year',
      allTime: 'All Time',
      timelineView: 'Timeline',
      breakdownView: 'Breakdown',
      byCourseView: 'By Course',
      performanceSummary: 'Performance Summary',
      average: 'Average',
      best: 'Best',
      worst: 'Worst',
      trend: 'Trend',
      performanceTimeline: 'Performance Timeline',
      clickForDetails: 'Click for details',
      performanceTrends: 'Performance Trends',
      actual: 'Actual',
      movingAverage: 'Moving Average',
      byConditions: 'By Conditions',
      rounds: 'rounds',
      performanceByCourse: 'Performance by Course',
      insightsTitle: 'Insights & Recommendations',
      howToUpdateHandicap: 'How to Update Your Handicap',
      howToUpdateHandicapDesc: 'Go to your Profile → Handicap Management to update your current handicap index. Each update creates a historical record shown in this chart.',
      analysisFor: 'Analysis for {dateRange}',
    },

    // CachedDashboard
    cached: {
      loadedFromCache: 'Dashboard loaded from cache',
      smartInsights: 'Smart Insights',
      aiPoweredAnalysis: 'AI-powered performance analysis',
      recentTrends: 'Recent Trends',
      puttsDataPoints: 'Putts data points',
      girDataPoints: 'GIR data points',
      fairwayDataPoints: 'Fairway data points',
      scramblingDataPoints: 'Scrambling data points',
    },

    // CrossMetricExploration
    crossMetric: {
      title: 'Cross-Metric Exploration',
      tabRelated: 'Related Metrics',
      tabCorrelations: 'Correlations',
      tabInsights: 'Smart Insights',
      exploreRelatedMetrics: 'Explore Related Metrics',
      performanceCorrelations: 'Performance Correlations',
      currentFocus: 'Current focus',
      tapToExplore: 'Tap to explore',
      smartRecommendation: 'Smart Recommendation',
      smartRecommendationDesc: 'Based on your {metric} performance, focusing on putting practice could improve your overall scoring by an estimated 2-3 strokes.',
      viewPracticePlan: 'View Practice Plan',
      performancePattern: 'Performance Pattern',
      performancePatternDesc: 'Your best scoring rounds correlate with 65%+ GIR performance. Current GIR average is 42%.',
      improveGir: 'Improve GIR',
    },

    // ContextualInsights
    contextualInsights: {
      title: 'Contextual Insights',
      focused: '{metric} focused',
      tabAiInsights: 'AI Insights',
      tabPatterns: 'Patterns',
      tabGoals: 'Goals',
      evidence: 'Evidence:',
      recommendedActions: 'Recommended Actions:',
      impactSuffix: 'impact',
      confidenceSuffix: '% confidence',
      strengthSuffix: '% strength',
      conditions: 'Conditions:',
      break80Challenge: 'Break 80 Challenge',
      break80Active: 'Active',
      break80Progress: 'Current average: 83.4 • Target: 79.9 • Progress: 78%',
      break80Estimated: 'Estimated completion: 6-8 weeks',
      girImprovement: 'GIR Improvement',
      girInProgress: 'In Progress',
      girProgress: 'Current: 42% • Target: 50% • Progress: 84%',
      girOnTrack: 'On track for completion',
    },

    // ClubPerformanceCard
    clubPerformance: {
      title: 'Club Performance',
      trackDistances: 'Track club distances',
      consistency: 'Consistency',
      mostUsedClubs: 'Most Used Clubs',
      clubsTracked: 'Clubs Tracked',
      gapIssues: 'Gap Issues',
      mostConsistentLabel: 'Most',
      mostConsistentSuffix: 'Consistent',
      tapForDetails: 'Tap for detailed club analysis',
    },
  },

  // Metric Drilldown Page
  metricDrilldown: {
    invalidMetric: 'Invalid Metric',
    selectValidMetric: 'Please select a valid metric type.',
    loadingMetricAnalysis: 'Loading metric analysis...',
    errorLoadingData: 'Error Loading Data',
    failedToLoadMetricData: 'Failed to load metric data',
    metricNotAvailable: 'Metric Not Available',
    metricTypeNotSupported: 'The metric type "{metricType}" is not supported.',
    customFilters: 'Custom filters',
    dashboardFilters: 'Dashboard filters',
    reset: 'Reset',
    customizing: 'Customizing',
    customize: 'Customize'
  },

  // Club Hub translations
  clubHub: {
    questStatusCompleted: 'Completed',
    questStatusAvailable: 'Available',
    questStatusLocked: 'Locked',
    jumpToLeague: 'Jump to league',
    viewEvents: 'View events',
    // Main Navigation
    title: 'Club Hub',
    noMemberships: 'No Club Memberships',
    joinClubsDescription: 'Join golf clubs to connect with other players and track your progress.',
    findClubsToJoin: 'Find Clubs to Join',
    joinNewClub: 'Join New Club',
    
    // Club Stats & Info
    members: 'Members',
    courses: 'Courses',
    course: 'course',
    courses_plural: 'courses',
    unknown: 'Unknown',
    activeNow: '{count} active now',
    noActivity: 'No activity',
    
    // Activity Sections
    clubActivity: 'Progression',
    selectClubForActivity: 'Select a club to view activity',
    activeChallengesAvailable: 'Active club challenges available',
    courseRecords: 'Course Records',
    selectClubForRecords: 'Select a club to view records',
    holeByHoleRecords: 'Hole-by-hole course records',
    memberDirectory: 'Club Members',
    connectWithMembers: 'Connect with club members',
    membersOnline: '{memberCount} members • {onlineCount} online',
    chat: 'Chat',
    selectClubToChat: 'Select a club to chat',
    slackStyleMessaging: 'Club channels & announcements',

    // Tee Booking
    bookTeeTime: 'Book Tee Time',
    reserveYourSlot: 'Reserve your slot on the course',
    selectClubToBook: 'Select a club to book',

    // Official Updates
    officialUpdates: 'Official Updates',
    latestNewsDescription: 'Latest news and announcements from club management',
    featured: 'FEATURED',
    registerNow: 'Register Now',
    learnMore: 'Learn More',
    loading: 'Loading...',
    viewMoreAnnouncements: 'View More Announcements ({count} more)',
    noOfficialUpdates: 'No Official Updates',
    noOfficialUpdatesDescription: "Your club posts news, events, and updates here. When something's posted, you'll see it first.",
    checkBackLater: 'Check back later for club announcements and updates.',
    
    // Discovery Modal
    discoverClubs: 'Discover Clubs',
    connectWithGolfers: 'Connect with golfers at Belgian clubs',
    searchClubsPlaceholder: 'Search clubs near you...',
    myClubs: 'My Clubs',
    twoJoined: '2 joined',
    joinedCount: '{count} joined',
    membersActiveToday: '{memberCount} members • {activeCount} active today',
    active: 'Active',
    quiet: 'Quiet',
    discoverNewClubs: 'Discover New Clubs',
    joinClub: 'Join Club',
    join: 'Join',
    
    // Members Modal
    clubMembers: 'Club Members',
    searchMembersPlaceholder: 'Search members...',
    all: 'All',
    online: 'Online',
    similarHcp: 'Similar HCP',
    friend: 'Friend',
    pending: 'Pending',
    noMembersFound: 'No Members Found',
    tryAdjustingSearch: 'Try adjusting your search or filters.',
    noMembersAvailable: 'No members available to display.',
    lookingForFourth: 'Looking for a Fourth?',
    connectToComplete: 'Connect with club members to complete your group',
    postLookingForFourth: 'Post Looking for Fourth',

    // Navigation cards (Club Features grid)
    clubFeatures: 'Club Features',
    tabOfficial: 'Official',
    tabCommunity: 'Community',
    viewMorePosts: 'View More Posts',
    navLeaderboards: 'Leaderboards',
    navLeaderboardsDesc: 'Rankings & competitions',
    navCourseRecords: 'Course Records',
    navCourseRecordsDesc: 'Hole-by-hole bests',
    navTournaments: 'Tournaments',
    navTournamentsDesc: 'Competitions & events',
    navActivityHub: 'Activity Hub',
    navActivityHubDesc: 'Live member activity',

    // Toast Messages
    signInToAddFriends: 'Please sign in to add friends',
    friendRequestSent: 'Friend request sent to club member!',
    failedToSendFriendRequest: 'Failed to send friend request',
    signInToSendMessages: 'Please sign in to send messages',
    failedToCreateConversation: 'Failed to create conversation',
    failedToOpenMessaging: 'Failed to open messaging interface',
    
    // Additional missing keys
    selectAClub: 'Select a club',
    events: 'Events & Tournaments',
    upcomingEvents: 'View and register for upcoming events',
    selectClubToViewEvents: 'Select a club to view events',
    coachingPortal: 'Coaching Portal',
    coachingPortalDesc: 'View lessons, exercises, videos & progress',
    selectClubToViewActivity: 'Select a club to view activity',
    selectClubToViewRecords: 'Select a club to view records',
    latestNewsAnnouncements: 'Latest news and announcements from club management',
    searchClubsNearYou: 'Search clubs near you...',
    seraingDistance: 'Seraing • 15 min away',
    membersCount: '{count} members', 
    holesCount: '{count} holes',
    searchMembers: 'Search members...',
    similarHandicap: 'Similar HCP',
    handicapLastSeen: 'Handicap {handicap} • {lastSeen}',
    tryAdjustingFilters: 'Try adjusting your search or filters.',
    connectToCompleteGroup: 'Connect with club members to complete your group',
    oneCourse: '1 course',
    multipleCourses: '{count} courses',
    club: 'Club',
    clubStats: 'Club Stats',
    
    // Member Directory specific keys
    clubNotFound: 'Club Not Found',
    clubNotFoundDescription: 'The requested club could not be found.',
    backToClubs: 'Back to Clubs',
    membersWithCount: '{name} Members',
    membersCountOnlineCount: '{count} members • {onlineCount} online',
    name: 'Name',
    handicapSort: 'Handicap',
    activity: 'Activity',
    role: 'Role',
    clearSearch: 'Clear Search',
    playing: 'Playing',
    admins: 'Admins',
    admin: 'Admin',
    handicapPrefix: 'Handicap ',
    roundsAndAvg: '{count} rounds • Avg {avg}',
    onlineNow: 'Online now',
    currentlyPlaying: 'Currently playing',
    lastSeenAgo: 'Last seen {time}',
    viewProfile: "View {name}'s profile",
    findPlayers: 'Find Players',
    
    // Error states from service  
    noPublicChannelsFound: 'No public channels found for this club',
    failedToLoadMembers: 'Failed to load club members',
    
    // Activity Hub - Club Activity Page
    activityHub: 'Progression',
    clubSetupRequired: 'Club setup required',
    pleaseJoinClub: 'Please join a club or set up your club membership to access activity features.',
    unableToLoadQuestData: 'Unable to load quest data',
    unexpectedErrorOccurred: 'An unexpected error occurred',
    debugInfo: 'Debug Info',
    userIdLabel: 'User ID',
    clubLabel: 'Club',
    errorLabel: 'Error',
    noneLabel: 'none',
    
    // Quest System - Time Frames
    daily: 'Daily',
    weekly: 'Weekly', 
    monthly: 'Monthly',
    yearly: 'Yearly',
    permanent: 'Permanent',
    challenge: 'Challenge',
    expiringSoon: 'Expiring soon!',
    left: 'left',
    
    // Quest System - Level Titles  
    golfMaster: 'Golf Master',
    golfLegend: 'Golf Legend',
    golfPro: 'Golf Pro',
    advancedGolfer: 'Advanced Golfer',
    skilledGolfer: 'Skilled Golfer',
    experiencedGolfer: 'Experienced Golfer',
    developingGolfer: 'Developing Golfer',
    beginnerGolfer: 'Beginner Golfer',
    newAdventurer: 'New Adventurer',
    
    // Quest System - Status Messages
    anonymousPlayer: 'Anonymous Player',
    clickToJoinChallenge: 'Click to join this challenge',
    roundsCompleted: 'rounds completed',
    
    // Quest System - UI Elements
    league: 'League',
    level: 'Level',
    xpTotal: 'XP Total',
    noActiveStreak: 'No Active Streak',
    week: 'Week',
    weeks: 'Weeks',
    streak: 'Streak',
    completed: 'Completed',
    available: 'Available',
    totalXP: 'Total XP',
    leagueProgress: 'League Progress',
    questsUntil: 'quests until',
    leagueUnlock: 'League unlock',
    unlockedAllLeagues: 'You\'ve unlocked all leagues!',
    nextQuest: 'Next Quest',
    start: 'START',
    see: 'SEE',
    allQuestsComplete: 'All Quests Complete!',
    openTheoryQuiz: 'Open theory quiz',
    completedAllAvailableQuests: 'You\'ve completed all available quests in this league',
    
    // Quest System - Progress/Leaderboard Tabs
    progress: 'Progress',
    leaderboard: 'Leaderboard',
    badges: 'Badges',
    
    // Quest System - Loading States  
    loadingQuestProgression: 'Loading quest progression...',
    tryAgain: 'Try Again',
    readyToBegin: 'Ready to Begin?',
    golfQuestAdventureStarts: 'Your golf quest adventure starts here. Complete rounds to unlock exciting challenges.',
    gettingStarted: 'Getting Started',
    playFirstRoundUnlock: 'Play your first round to unlock quests and start earning league points!',
    
    // Quest System - Error Messages
    unableToLoadQuestTree: 'Unable to load quest tree',
    problemLoadingProgression: 'There was a problem loading your quest progression. Please check your connection and try again.',
    
    // Quest System - Footer
    questProgress: 'Quest Progress',
    completePlabel: 'Complete',
    
    // Quest System - Completion Indicator
    completingQuest: 'Completing quest...',
    
    // Quest System - Rank/Position Indicators (ClubQuestLeaderboard)
    rankImproved: 'Rank improved',
    rankDecreased: 'Rank decreased', 
    rankUnchanged: 'Rank unchanged',
    newToLeaderboard: 'New to leaderboard',
    you: 'You',
    
    // All Quests Label
    allQuests: 'All Quests',
    
    // Quest Detail Modal
    requirements: 'Requirements',
    whatYouNeedToDo: 'What You Need to Do',
    rewards: 'Rewards',
    leaguePoints: 'League Points',
    questCompleted: 'Quest Completed!',
    badge: 'Badge',
    achievement: 'Achievement',
    scoring: 'Scoring',
    
    // Quest Tree Sections
    firstSteps: 'First Steps',
    basicSkills: 'Basic Skills',
    courseDiscovery: 'Course Discovery',
    socialIntroduction: 'Social Introduction',
    scoringProgression: 'Scoring Progression',
    courseMastery: 'Course Mastery',
    skillRefinement: 'Skill Refinement',
    socialExpansion: 'Social Expansion',
    advancedSkills: 'Advanced Skills',
    leadershipQuests: 'Leadership Quests',
    eliteAchievements: 'Elite Achievements',
    golfMasteryPath: 'Golf Mastery Path',
    skillMastery: 'Skill Mastery',
    performanceTracking: 'Performance Tracking',
    advancedScoring: 'Advanced Scoring',
    expertSkills: 'Expert Skills',
    performanceExcellence: 'Performance Excellence',
    competitiveExcellence: 'Competitive Excellence',
    masteryQuests: 'Mastery Quests',
    legendarySkills: 'Legendary Skills',
    perfectGame: 'Perfect Game',
    
    // Quest Tree Descriptions
    scoringBasics: 'Scoring Basics',
    coreGolfFundamentals: 'Core Golf Fundamentals',
    exploreNewCourses: 'Explore New Courses',
    connectWithOtherGolfers: 'Connect with Other Golfers',
    improveYourScores: 'Improve Your Scores',
    masterAdvancedTechniques: 'Master Advanced Techniques',
    monitorYourProgress: 'Monitor Your Progress',
    conquerDifferentCourses: 'Conquer Different Courses',
    eliteScoreAchievement: 'Elite Score Achievement',
    professionalLevelTechniques: 'Professional-Level Techniques',
    consistentHighPerformance: 'Consistent High Performance',
    tournamentSuccess: 'Tournament Success',
    conquerAnyCourse: 'Conquer Any Course',
    golfMasteryElite: 'Golf Mastery Elite',
    legacyAchievements: 'Legacy Achievements',
    golfLegendStatus: 'Golf Legend Status',
    
    // League Names with proper formatting
    rookieLeague: 'Rookie League',
    regularLeague: 'Regular League',  
    veteranLeague: 'Veteran League',
    legendLeague: 'Legend League',
    
    // Quest Names (keeping English as they are proper nouns)
    firstRound: 'First Round',
    firstPar: 'First Par',
    firstBirdie: 'First Birdie',
    break100: 'Break 100',
    parSaver: 'Par Saver',
    break90: 'Break 90',
    birdieHunter: 'Birdie Hunter',
    break80: 'Break 80',
    breakPar: 'Break Par',
    fairwayFinder: 'Fairway Finder',
    greenHunter: 'Green Hunter',
    shortGameHero: 'Short Game Hero',
    
    // Additional Quest Names
    clubMember: 'Club Member',
    nineHoleWarrior: 'Nine Hole Warrior',
    playingPartner: 'Playing Partner',
    break110: 'Break 110',
    scoringProgress: 'Scoring Progress',
    experienceBuilder: 'Experience Builder',
    socialGolfer: 'Social Golfer',
    quickRounds: 'Quick Rounds',
    consistentGolfer: 'Consistent Golfer',
    skillBuilder: 'Skill Builder',
    courseExplorer: 'Course Explorer',
    multiCoursePlayer: 'Multi-Course Player',
    socialConnector: 'Social Connector',
    dailyPlayer: 'Daily Player',
    weekendWarrior: 'Weekend Warrior',
    
    // Quest Descriptions
    firstRoundDesc: 'Complete your first scored round of golf',
    firstParDesc: 'Make your first par in a round',
    firstBirdieDesc: 'Make your first birdie - one under par!',
    break100Desc: 'Shoot under 100 for 18 holes - major milestone!',
    parSaverDesc: 'Make 5 or more pars in a single round',
    break90Desc: 'Shoot under 90 for 18 holes',
    birdieHunterDesc: 'Make 10 total birdies across multiple rounds',
    break80Desc: 'Shoot under 80 for 18 holes - join the elite!',
    breakParDesc: 'Shoot even par or better - ultimate achievement!',
    
    // Additional Quest Descriptions
    clubMemberDesc: 'Join your first golf club to connect with other players',
    nineHoleWarriorDesc: 'Complete 5 nine-hole rounds',
    playingPartnerDesc: 'Play a round with another club member',
    break110Desc: 'Shoot under 110 for 18 holes - first major milestone!',
    break120Desc: 'Shoot under 120 for 18 holes',
    scoringProgressDesc: 'Show consistent scoring improvement over 5 rounds',
    experienceBuilderDesc: 'Complete 10 total rounds to build your golf experience',
    socialGolferDesc: 'Connect with 3 different golf partners',
    quickRoundsDesc: 'Complete 5 rounds in under 3.5 hours each',
    consistentGolferDesc: 'Play at least once every two weeks for a month',
    skillBuilderDesc: 'Practice at the driving range 5 times',
    courseExplorerDesc: 'Play rounds at 3 different golf courses',
    multiCoursePlayerDesc: 'Achieve your personal best score at 2+ courses',
    socialConnectorDesc: 'Help organize a group golf outing with 4+ players',
    dailyPlayerDesc: 'Play golf 3 days in a row',
    weekendWarriorDesc: 'Play both Saturday and Sunday for 3 consecutive weekends',
    steadyRoundDesc: 'Complete a round without losing a ball',
    noThreePuttDesc: 'Complete a round without a three-putt',
    distanceDriverDesc: 'Hit a drive over 250 yards',
    approachArtistDesc: 'Hit 50% of greens in regulation',
    sandSaverDesc: 'Successfully get out of bunkers',
    homeCourseDesc: 'Play 10 rounds at your home course',
    par3SpecialistDesc: 'Make par or better on all par 3s',
    par5ConquerorDesc: 'Eagle a par 5 hole',
    groupRoundDesc: 'Complete a round in a group of 3 or more',
    lessonLearnerDesc: 'Take a golf lesson or clinic',
    earlyBirdDesc: 'Complete a round before 8 AM',
    
    // Continue button 
    continue: 'Continue',
    
    // Quest Card UI 
    claiming: 'Claiming...',
    claim: 'Claim',
    claimed: 'Claimed',
    questPts: 'quest pts',
    xp: 'XP',
    
    // Quest Modal Additional
    skills: 'Skills',
    rookie: 'Rookie',
    member: 'Member',
    regular: 'Regular',
    veteran: 'Veteran',
    legend: 'Legend',
    complete: 'complete',
    
    // Leaderboard
    progression: 'Progression',
    classement: 'Leaderboard',
    clubQuestLeaderboard: 'Club Quest Leaderboard',
    allTime: 'All Time',
    month: 'Month',
    rank: 'RANK',
    athlete: 'ATHLETE',
    score: 'SCORE',
    
    // Quest Progress
    questsComplete: 'Quests Complete',
    
    // League Welcome Messages
    welcomeToYourGolfJourney: 'Welcome to Your Golf Journey!',
    keepUpTheGreatWork: 'Keep Up the Great Work!',
    yourePlayingLikeAPro: 'You\'re Playing Like a Pro!',
    incredibleGolfMastery: 'Incredible Golf Mastery!',
    amazingProgress: 'Amazing Progress!',
    
    // Quest Modal - Missing Strings
    milestones: 'Milestones',
    recentProgress: 'recent progress',
    daysToComplete: 'days to complete',
    playRounds: 'Play {count} rounds',
    scoreUnder: 'Score under {target}',
    
    // Level Requirements
    required: 'Required',
    levelRequired: 'Level {level}+ Required',
    
    // Quest Names - All individual quest names
    'First Round': 'First Round',
    'First Par': 'First Par', 
    'First Birdie': 'First Birdie',
    'Break 120': 'Break 120',
    'Break 110': 'Break 110',
    'Break 100': 'Break 100',
    'Steady Round': 'Steady Round',
    'Par Saver': 'Par Saver',
    'Fairway Finder': 'Fairway Finder',
    'Green Hunter': 'Green Hunter',
    'Short Game Hero': 'Short Game Hero',
    'No Three-Putt': 'No Three-Putt',
    'Distance Driver': 'Distance Driver',
    'Approach Artist': 'Approach Artist',
    'Sand Saver': 'Sand Saver',
    'Home Course': 'Home Course',
    'Course Explorer': 'Course Explorer',
    'Par 3 Specialist': 'Par 3 Specialist',
    'Par 5 Conqueror': 'Par 5 Conqueror',
    'Nine Hole Warrior': 'Nine Hole Warrior',
    'Playing Partner': 'Playing Partner',
    'Group Round': 'Group Round',
    'Club Member': 'Club Member',
    'Lesson Learner': 'Lesson Learner',
    'Break 95': 'Break 95',
    'Break 90': 'Break 90',
    'Break 85': 'Break 85',
    'Birdie Hunter': 'Birdie Hunter',
    'Eagle Quest': 'Eagle Quest',
    'Consistency King': 'Consistency King',
    'Weekend Warrior': 'Weekend Warrior',
    'Score Streak': 'Score Streak',
    'Fairway Pro': 'Fairway Pro',
    'Iron Master': 'Iron Master',
    'Short Game Wizard': 'Short Game Wizard',
    'Putting Expert': 'Putting Expert',
    'Recovery Artist': 'Scrambling Artist',
    'Sand Master': 'Sand Master',
    'Approach Ace': 'Approach Ace',
    'Driver Dominator': 'Driver Dominator',
    'Handicap Hunter': 'Handicap Hunter',
    'Round Analyzer': 'Round Analyzer',
    'Weakness Warrior': 'Weakness Warrior',
    'Tee Time Tracker': 'Tee Time Tracker',
    'Course Collector': 'Course Collector',
    'Local Legend': 'Local Legend',
    'Terrain Master': 'Terrain Master',
    'Length Challenge': 'Length Challenge',
    'Break 80': 'Break 80',
    'Break 75': 'Break 75',
    'Single Digit': 'Single Digit',
    'Eagle Hunter': 'Eagle Hunter',
    'Birdie Streak': 'Birdie Streak',
    'Consistency Master': 'Consistency Master',
    'Course Record Threat': 'Course Record Threat',
    'Tournament Score': 'Tournament Score',
    'Driving Accuracy': 'Driving Accuracy',
    'Iron Precision': 'Iron Precision',
    'Short Game Master': 'Short Game Master',
    'Putting Surgeon': 'Putting Surgeon',
    'Up-and-Down King': 'Up-and-Down King',
    'Bunker Specialist': 'Bunker Specialist',
    'Approach Master': 'Approach Master',
    'Power Driver': 'Power Driver',
    'Shot Shaper': 'Shot Shaper',
    'Trouble Master': 'Trouble Master',
    'Handicap Dropper': 'Handicap Dropper',
    'Statistical Guru': 'Statistical Guru',
    'Weakness Eliminator': 'Weakness Eliminator',
    'Mental Game': 'Mental Game',
    'Pressure Player': 'Pressure Player',
    'Course Manager': 'Course Manager',
    'Tournament Winner': 'Tournament Winner',
    'Match Play Master': 'Match Play Master',
    'Medal Winner': 'Medal Winner',
    'Qualifier': 'Qualifier',
    'Team Captain': 'Team Captain',
    'Club Champion': 'Club Champion',
    'Course Architect': 'Course Architect',
    'Resort Golfer': 'Resort Golfer',
    'Championship Venue': 'Championship Venue',
    'Altitude Master': 'Altitude Master',
    'Break Par': 'Break Par',
    'Under Par': 'Under Par',
    'Course Record': 'Course Record',
    'Scratch Golfer': 'Scratch Golfer',
    'Plus Handicap': 'Plus Handicap',
    'Double Eagle': 'Double Eagle',
    'Perfect Round': 'Perfect Round',
    'Major Mindset': 'Major Mindset',
    'Driving Machine': 'Driving Machine',
    'Iron Byron': 'Iron Byron',
    'Short Game Sorcerer': 'Short Game Sorcerer',
    'Putting Robot': 'Putting Robot',
    'Shot Maker': 'Shot Maker',
    'Distance Control': 'Distance Control',
    'Wind Master': 'Wind Master',
    'Pressure Proof': 'Pressure Proof',
    'Regional Champion': 'Regional Champion',
    'State Qualifier': 'State Qualifier',
    'Amateur Champion': 'Amateur Champion',
    'Pro Event': 'Pro Event',
    'Teaching Pro': 'Teaching Pro',
    'Course Designer': 'Course Designer',
    'Golf Ambassador': 'Golf Ambassador',
    'International Golfer': 'International Golfer',
    'Links Master': 'Links Master',
    'Desert Specialist': 'Desert Specialist',
    'Mountain Conqueror': 'Mountain Conqueror',
    'Tropical Tourist': 'Tropical Tourist',
    'Historic Venues': 'Historic Venues',
    'Hall of Fame': 'Hall of Fame',

    // Community feed social actions
    communityFeed: 'Community Feed',
    celebrate: 'Celebrate',
    congratulate: 'Congratulate',
  },

  // Club Community (Chat) translations
  clubCommunity: {
    // Loading states
    loadingBasic: 'Loading...',
    loadingMemberships: 'Loading club memberships...',
    loadingChannels: 'Loading club channels...',
    
    // Error states
    errorLoadingMemberships: 'Error loading club memberships',
    errorLoadingCommunity: 'Error loading club community',
    
    // No club state
    clubCommunityTitle: 'Club Community',
    joinClubToAccess: 'Join a club to access community features',
    debugInfo: 'Debug Info:',
    notFound: 'Not found',
    unknown: 'Unknown',
    visitBrowseClubs: 'Visit Browse Clubs to find and join a club',
    
    // Header and navigation
    members: 'Members',
    member: 'member',
    members_plural: 'members',
    unknownUser: 'Unknown User',
    
    // Search functionality
    searchMessages: 'Search messages...',
    
    // Empty states
    welcomeTo: 'Welcome to #',
    startTheConversation: 'Start the conversation!',
    
    // Message actions
    save: 'Save',
    cancel: 'Cancel',
    reply: 'Reply',
    edit: 'Edit',
    copy: 'Copy',
    deleteMessage: 'Delete',
    deleteMessageConfirm: 'Delete this message?',
    
    // Typing indicator
    typing: 'Typing…',
    
    // Sidebar
    channels: 'Channels',
    
    // Photo functionality
    photos: 'Photos',
    preview: 'Preview',
    uploadingPhotos: 'Uploading photos...',
    
    // Message input
    messageChannel: 'Message #',
    channel: 'channel',
    
    // Mention autocomplete
    handicap: 'Handicap:',
    
    // Announcement thread
    clubAnnouncement: 'Club Announcement',
    threadReply: 'reply',
    threadReplies: 'replies',
    threadReplyPlaceholder: 'Reply to announcement…',
    threadNoReplies: 'No replies yet. Be the first!',
    viewThread: 'View thread',

    // Channel descriptions
    channelDescriptions: {
      general: 'Main club discussion',
      courseTips: 'Share course knowledge and tips',
      announcements: 'Official club announcements and updates',
      playingToday: 'Find playing partners',
      tournaments: 'Tournament discussion and coordination',
      equipment: 'Discuss golf equipment',
      clubEvents: 'Club tournaments and events',
      roundShares: 'Automated round sharing'
    }
  },

  // Play Multiplayer translations
  playMultiplayer: {
    // Header
    title: 'Multiplayer Golf',
    subtitle: 'Play with friends and compete',
    
    // Create New Game Section
    createNewGameTitle: 'Create New Game',
    createNewGameDescription: 'Search for a club to start your multiplayer game',
    
    // Search States
    gettingYourLocation: 'Getting your location...',
    searchingClubs: 'Searching clubs...',
    
    // Search Results
    nearbyClubs: 'Nearby Clubs',
    noClubsFound: 'No clubs found',
    tryDifferentSearch: 'Try a different search term',
    
    // Empty State
    readyToCompete: 'Ready to compete?',
    searchInstructions: 'Search for a club or use location to find nearby clubs',
    
    // Join Existing Game Section
    joinExistingGameTitle: 'Join Existing Game',
    joinExistingGameDescription: 'Enter a join code to join a friend\'s game',
    joinCodeLabel: 'Join Code',
    joinCodePlaceholder: 'Enter 6-digit code',
    joinGameButton: 'Join Game',
    
    // Toast Messages
    pleaseEnterJoinCode: 'Please enter a join code',
    locationFound: 'Location found! Showing nearby clubs.',
    locationError: 'Could not get your location. Please search manually.',
    
    // Club Search Components
    findYourClub: 'Find your club...',
    findNearbyClubs: 'Find nearby clubs',
    selectClub: 'Select Club',
    course: 'course',
    courses: 'courses',
    away: 'away',
    nearby: 'Nearby',

    // Club Search Modal
    joinAClub: 'Join a Club',
    discoverGolfClubsInBelgium: 'Discover golf clubs in Belgium',
    searchClubsNearYouPlaceholder: 'Search clubs near you...',
    searchResults: 'Search Results',
    noClubsFoundMatchingSearch: 'No clubs found matching your search',
    featuredClubs: 'Featured Clubs',
    loadingFeaturedClubs: 'Loading featured clubs...',
    featured: 'Featured',
    joining: 'Joining...',
    join: 'Join'
  },

  // Create Multiplayer Game translations
  createMultiplayerGame: {
    // Header
    title: 'Create Game',
    subtitle: 'Set up multiplayer competition',
    
    // Loading States
    loadingCourseInformation: 'Loading course information...',
    
    // Tab Navigation
    createTab: 'Create',
    joinTab: 'Join',
    
    // Selected Course Section
    selectedCourse: 'Selected Course',
    golfCourse: 'Golf Course',
    holes: 'holes',
    par: 'Par',
    
    // Course History
    basedOnHistory: '💡 Based on your history:',
    
    // Game Details Section
    gameDetailsTitle: 'Game Details',
    gameTitleLabel: 'Game Title *',
    gameTitlePlaceholder: 'Friday Night Golf Challenge',
    
    // Create Button States
    createGame: 'Create Game',
    creatingGame: 'Creating Game...',
    
    // Join Game Section
    joinExistingGameTitle: 'Join Existing Game',
    joinExistingGameDescription: 'Enter a join code to join a friend\'s game',
    joinCodeLabel: 'Join Code',
    joinCodePlaceholder: 'ABCDEF',
    joinGame: 'Join Game',
    joiningGame: 'Joining Game...',
    
    // Toast Messages
    courseNotFound: 'Course not found',
    pleaseEnterGameTitle: 'Please enter a game title',
    pleaseSelectCourse: 'Please select a course',
    mustBeLoggedIn: 'You must be logged in to create a game',
    gameCreatedSuccessfully: 'Game "{gameTitle}" created successfully!',
    failedToCreateGame: 'Failed to create game. Please try again.',
    pleaseEnterValidJoinCode: 'Please enter a valid 6-digit join code',
    failedToJoinGame: 'Failed to join game. Please check the code and try again.',
    
    // Game Format Selector
    gameFormatTitle: 'Game Format',
    gameFormatDescription: 'Choose how you want to play',
    
    gameFormats: {
      individualPlay: 'Individual Play',
      individualDescription: 'Classic golf - each player for themselves',
      
      scramble: 'Scramble',
      scrambleDescription: 'Team format - play from the best shot',
      
      bestBall: 'Best Ball (Four-Ball)',
      bestBallDescription: 'Partners play their own ball, record best score',
      
      alternateShot: 'Alternate Shot (Foursomes)',
      alternateShotDescription: 'Partners alternate shots on same ball',
      
      aggregate: 'Aggregate',
      aggregateDescription: 'Team scores combined for total',
      
      easy: 'Easy',
      medium: 'Medium',
      advanced: 'Advanced',
      
      playersRange: '{min}-{max} players'
    },
    
    // Player Limit Selector
    playerLimitTitle: 'Player Limit',
    playerLimitDescription: 'Maximum number of players who can join',
    selectedPlayers: 'Selected: {count} players'
  },

  // Round Completion Modal translations
  roundCompletion: {
    title: 'Finish Round?',
    description: 'Choose what to do with this round',
    completeRound: 'Complete Round',
    completing: 'Completing...',
    deleteRound: 'Delete Round',
    deleting: 'Deleting...',

    // HorizontalStatsCards card headers
    performanceStats: 'Performance Stats',
    coreMetricsCard: 'Core Metrics',
    byHoleType: 'By Hole Type',
    accuracyCard: 'Accuracy',
    puttingProportionCard: 'Putting Proportion',
    recoveryCard: 'Recovery',
    drivingCard: 'Driving',
    accuracyTab: 'Accuracy',
    consistencyTab: 'Consistency',
    noFairwayData: 'No fairway data available',
    trackFairwaysTip: 'Track fairways on par 4s and 5s',
    noShotDataRecorded: 'No shot data recorded',
    noShotDataDescription: 'Shot distances will appear here when you track club and distance during your round.',
    noRecoveryDataShort: 'No recovery data available',

    // ScoreCardDisplay (round-completion)
    completeScorecardTitle: 'Complete Scorecard',
    front9Label: 'Front 9',
    back9Label: 'Back 9',
    parLabel: 'Par',
    totalLabel: 'Total',
    frontNineTitle: 'Front Nine',
    backNineTitle: 'Back Nine',
    statEagles: 'Eagles',
    statBirdies: 'Birdies',
    statPars: 'Pars',
    statBogeyPlus: 'Bogeys+',
    statTotalPutts: 'Total Putts',
    statAvgHole: 'Avg/Hole',
    scoreLegend: 'Score Legend',
    doublePlus: 'Double+',

    // RoundProgression
    roundProgressionTitle: 'Round Progression',
    holeLabel: 'Hole',
    finalHole: 'Final Hole',
    finalScore: 'Final Score',
    totalStrokesLabel: 'Total Strokes',
    legendUnderPar: 'Under Par',
    legendOverPar: 'Over Par',
    legendTrouble: 'Trouble',

    // RoundSummary
    roundSummaryTitle: 'Round Summary',
    unknownDate: 'Unknown Date',
    unknownTime: 'Unknown Time',
    golfCourse: 'Golf Course',
    locationUnknown: 'Location Unknown',
    dateUnknown: 'Date Unknown',
    roundDate: 'Round Date',
    teeTime: 'Tee Time',
    timeUnknown: 'Time Unknown',
    weatherLabel: 'Weather',
    weatherFair: 'Fair',
    temperatureLabel: 'Temperature',
    perfSummaryTitle: 'Performance Summary',
    perfRoundCompleted: 'Round completed successfully',
    perfNotesRecorded: 'Personal notes recorded',
    perfNoNotes: 'No additional notes',
    perfAnalyticsCalculated: 'Analytics and statistics calculated',
    perfDataSaved: 'Performance data saved to history',
    roundNotesTitle: 'Round Notes',
    shareRound: 'Share Round',
    viewHistory: 'View History',
    roundIdLabel: 'Round ID',
    dataSynced: 'Data synced and backed up',

    // HandicapDifferential
    handicapDiffTitle: 'Handicap Differential',
    handicapDiffLabel: 'Handicap Differential',
    qualityExceptional: 'Exceptional',
    qualityGreat: 'Great Round',
    qualityGood: 'Good Round',
    qualityAverage: 'Average Round',
    qualityChallenging: 'Challenging',
    totalScoreLabel: 'Total Score',
    courseRatingLabel: 'Course Rating',
    slopeRatingLabel: 'Slope Rating',
    formulaLabel: 'Formula',

    // ScoreDistribution (completion-new)
    scoreDistributionTitle: 'Score Distribution',
    scoreTypeEagles: 'Eagles',
    scoreDescEagles: '-2 or better',
    scoreTypeBirdies: 'Birdies',
    scoreDescBirdies: '-1 to par',
    scoreTypePars: 'Pars',
    scoreDescPars: 'Even par',
    scoreTypeBogeys: 'Bogeys',
    scoreDescBogeys: '+1 to par',
    scoreTypeDoubles: 'Doubles+',
    scoreDescDoubles: '+2 or worse',
    underParLabel: 'Under Par',
    evenParLabel: 'Even Par',
    overParLabel: 'Over Par',
    underParSuccess: 'Under Par Success',
    excellentScoring: 'Excellent scoring!',
    goodPerformance: 'Good performance',
    roomForImprovement: 'Room for improvement',
    bigNumbers: 'Big Numbers',
    perfectDamageControl: 'Perfect damage control!',
    goodCourseManagement: 'Good course management',
    focusAvoidBigScores: 'Focus on avoiding big scores',
    noScoreDataTitle: 'No score data available',
    noScoreDataDesc: 'Complete some holes to see your score distribution',

    // ScramblingAnalysis (completion-new)
    scramblingAnalysisTitle: 'Scrambling Analysis',
    scramblingExcellent: 'Outstanding short game!',
    scramblingGood: 'Solid scrambling ability!',
    scramblingAverage: 'Room to improve short game',
    scramblingPoor: 'Focus on up & down practice',
    scramblingNoData: 'No scrambling data',
    upAndDownSuccessRate: 'Up & Down Success Rate',
    tourAverageScrambling: 'Tour average: ~58%',
    successfulSaves: 'Successful Saves',
    failedAttempts: 'Failed Attempts',
    missedLabel: 'missed',
    successfulLabel: 'Successful',
    upAndDownsLabel: 'Up & downs',
    failedLabel: 'Failed',
    missedOpportunities: 'Missed opportunities',
    shortGameAssessment: 'Short Game Assessment',
    scramblingDefinitionTitle: 'Scrambling',
    scramblingDefinition: 'Making par or better after missing the green in regulation. This measures your short game ability to recover from missed approach shots.',
    perfectGirTitle: 'Perfect GIR Performance!',
    perfectGirDesc: 'You hit every green in regulation - no scrambling needed.',

    // ScoreCardDisplay (components/ScoreCardDisplay.tsx)
    scorecardTitle: 'Scorecard',
    avgPuttsLabel: 'Avg Putts',
    totalPuttsSmall: 'total putts',
    colHole: 'Hole',
    colPar: 'Par',
    colStrokes: 'Strokes',
    colScore: 'Score',
    colFW: 'FW',
    colGIR: 'GIR',
    colPutts: 'Putts',
    colResult: 'Result',
    noScorecardData: 'No scorecard data available',

    // UnifiedRoundCompletion buttons
    doneButton: 'Done',
    saveRound: 'Save Round',
    updateRound: 'Update Round',
    savingRound: 'Saving...',
    updatingRound: 'Updating...',

    // GameDetailsCard
    detailsTitle: 'Details',
    labelCourse: 'Course',
    labelDate: 'Date',
    labelDuration: 'Duration',
    labelPlayers: 'Players',
    labelPlayersCount: '{count} players',
    labelGameMode: 'Game Mode',
    labelScoring: 'Scoring',
    labelPenalties: 'Penalties',
    labelPenaltiesCount: '{count} strokes',
    labelWeather: 'Weather',
    weatherLoading: 'Loading weather...',
    gameModeIndividual: 'Individual Play',
    gameModeScramble: 'Scramble',
    gameModeFourBall: 'Four-Ball',
    gameModeAlternateShot: 'Foursomes',
    scoringStrokePlay: 'Stroke Play',
    scoringStableford: 'Stableford',
    scoringMatchPlay: 'Match Play',
    weatherClearSky: 'Clear sky',
    weatherMainlyClear: 'Mainly clear',
    weatherPartlyCloudy: 'Partly cloudy',
    weatherOvercast: 'Overcast',
    weatherFoggy: 'Foggy',
    weatherDrizzle: 'Drizzle',
    weatherFreezingDrizzle: 'Freezing drizzle',
    weatherRain: 'Rain',
    weatherFreezingRain: 'Freezing rain',
    weatherSnow: 'Snow',
    weatherSnowGrains: 'Snow grains',
    weatherRainShowers: 'Rain showers',
    weatherSnowShowers: 'Snow showers',
    weatherThunderstorm: 'Thunderstorm',
    weatherThunderstormHail: 'Thunderstorm with hail',
    weatherUnknown: 'Unknown',

    // IncludeInStatsCard
    includeInStatsLabel: 'Include in stats',
    includeInStatsSubtitle: 'Count towards your statistics',
    includeInStatsNeedHoles: 'Need {count} more hole{plural} (min. {min} holes)',

    // HorizontalStatsCards — inline labels
    avgPutts: 'avg putts',
    avgFir: 'avg FIR',
    avgGir: 'avg GIR',
    underParAxis: '← Under par | Par | Over par →',
    recoveryCentreLabel: 'recovery chances',
    statsToggle: 'Stats',
    distributionToggle: 'Distribution',
    allGreensHit: 'All greens hit in regulation',
    firHit: 'Hit',
    firMissRight: 'Miss Right',
    firMissLeft: 'Miss Left',
    firMissShort: 'Miss Short',
    girHit: 'Hit',
    girMissed: 'Missed',
    recoveryParSaves: 'Par Saves',
    recoveryBogey: 'Bogey',
    recoveryDoublePlus: 'Double Bogey+',
    recoveryScrambling: 'Scrambling',
    recoveryUpAndDown: 'Up & Down',
    recoverySandSaves: 'Sand Saves',
    missLeftLabel: 'Miss Left',
    hitFairwayLabel: 'Hit Fairway',
    missRightLabel: 'Miss Right',
    missedShortLabel: 'Missed Short',
    drivingNoData: 'No driver data',
    drivingTrackTip: 'Track driver shots to see consistency',
    drivingYourDrives: 'Your drives',
    drivingAvgLabel: '{avg}m avg',
    drivingDrives: 'Drives',
    drivingAverage: 'Average',
    drivingSpread: 'Spread',
    drivingHoleTooltip: 'Hole {hole}',
    missedShotCount: '{count} shot',
    missedShotCountPlural: '{count} shots',

    // PostRoundScannerCard
    scanner: {
      sectionTitle: 'Practice Scan',
      scanning: 'Scanning your round...',
      readingData: 'Reading your round data…',
      complete: 'Your focus areas',
      addToQueue: 'Add to Queue',
      added: 'Added',
      noData: 'Play more rounds to unlock',
      allSaved: 'All drills saved!',
      allSavedSub: 'Check your practice queue for what to work on next.',
    },
  },

  // Multiplayer Game Lobby translations
  multiplayerGameLobby: {
    // Toast Messages
    failedToLoadGameData: 'Failed to load game data',
    joinCodeCopied: 'Join code copied to clipboard!',
    gameStarted: 'Game started!',
    failedToStartGame: 'Failed to start game',

    // Error States
    gameNotFound: 'Game Not Found',
    gameNotFoundDescription: "The game you're looking for doesn't exist.",
    backToMultiplayer: 'Back to Multiplayer',

    // Main Content
    players: 'players',
    holes: 'holes',
    individual: 'Individual',

    // Tab Names
    tabPlayers: 'Players',
    tabTeams: 'Teams',
    tabShare: 'Share',

    // Players Tab
    currentPlayers: 'Current Players',
    inviteMorePlayers: 'Invite More Players',

    // Teams Tab
    teamManagement: 'Team Management',
    individualCompetition: 'Individual Competition',
    eachPlayerCompetes: 'Each player competes individually',

    // Share Tab
    joinCode: 'Join Code',
    shareCodeDescription: 'Share this code with players to join',

    // Game Controls
    startingGame: 'Starting Game...',
    startGame: 'Start Game',
    enterScores: 'Enter Scores',

    // Player Management
    playersInLobby: 'Players in Lobby',
    you: 'You',
    ready: 'Ready',
    host: 'Host',
    joined: 'Joined',
    playerManagement: 'Player Management',
    currentPlayersSection: 'Current Players',
    addPlayers: 'Add Players',

    // Input Placeholders
    enterPlayerName: 'Enter player name',
    enterPlayerNameDots: 'Enter player name...',
    searchFriendsPlaceholder: 'Search Friends by display name...',

    // Status Messages
    almostFull: 'Almost full!',
    spotsRemaining: 'Only {count} spot{plural} remaining.',
    cantRemoveYourself: "You can't remove yourself from the game",
    add: 'Add',
    
    // Player Actions
    playerAddedToGame: '{playerName} has been added to the game',
    playerRemovedFromGame: '{playerName} has been removed from the game',
    failedToAddPlayer: 'Failed to add player to game',
    failedToRemovePlayer: 'Failed to remove player from game',
    failedToAddFriendToGame: 'Failed to add friend to game',
    failedToAddContactToGame: 'Failed to add contact to game',
    failedToAddFriendsToGame: 'Failed to add selected friends to game',
    playerAlreadyInGame: '{playerName} is already in the game',
    addedFriendsToGame: 'Added {count} friend{s} to the game',
    maximumPlayersAllowed: 'Maximum {maxPlayers} players allowed',
    canOnlyAddMorePlayers: 'You can only add {count} more player{s}',
    selectAtLeastOneFriend: 'Please select at least one friend to add',
    pleaseEnterPlayerName: 'Please enter a player name',

    // QR Code & Sharing
    noShareLinksYet: 'No share links created yet',
    generateQRDescription: 'Generate a QR code or link to invite players',
    qrCodeReady: 'QR Code Ready',
    generateQRCode: 'Generate QR Code',
    linkReady: 'Link Ready',
    createLink: 'Create Link',
    inviteText: `You're invited to play golf! Join "{gameName}" using this link:`,

    // Player Search Tabs
    friends: 'Friends',
    contacts: 'Contacts',
    recent: 'Recent',
    manual: 'Manual',

    // Search and Selection
    searchFriendsPlaceholder2: 'Search friends by display name...',
    friendsSelected: '{count} friend{plural} selected',
    clearSelection: 'Clear',
    addSelected: 'Add Selected',

    // Contact Integration
    contactsNotSupported: 'Contacts API not supported',
    useManualEntry: 'Use manual entry to add players',
    syncContacts: 'Sync Contacts',
    syncing: 'Syncing...',
    contactsStoredLocally: 'Contacts are stored locally and never shared',
    accessContactsToAddPlayers: 'Access your contacts to quickly add players to the game',
    searchContactsPlaceholder: 'Search contacts by name or phone...',
    refresh: 'Refresh',
    syncedContacts: 'Synced {count} contacts successfully',
    failedToSyncContacts: 'Failed to sync contacts',

    // Recent Players
    noRecentPlayersFound: 'No recent players found',
    recentPlayersDescription: "Players you've played with recently will appear here",

    // Max Players Messages
    maximumPlayersReached: 'Maximum number of players ({maxPlayers}) reached.',
    canStillBrowse: 'You can still browse friends, contacts, and recent players below.',

    // Loading States
    loadingFriends: 'Loading friends...',
    loadingContacts: 'Loading contacts...',
    
    // Empty States & Search Results
    noFriendsFound: 'No friends found',
    addFriendsThroughSocial: 'Add friends through the Social tab',
    noFriendsMatchSearch: 'No friends match your search',
    tryDifferentSearchTerm: 'Try a different search term',
    inGame: 'In Game',
    clear: 'Clear',
    noContactsFound: 'No contacts found',
    noContactsMatchSearch: 'No contacts match your search',
    syncContactsToSeeHere: 'Sync contacts to see them here',


    // Share Tab
    shareGame: 'Share Game',
    shareGameDescription: 'Generate QR codes and shareable links to invite more players to your game.',
    shareGameWithAnyone: 'Share Game with Anyone',
    invitationSentVia: 'Invitation sent via {method}',

    // Modal Buttons
    cancel: 'Cancel',
    close: 'Close',
    save: 'Save',
    saving: 'Saving...',
    or: 'Or',

    // Game Details Labels
    scoring: 'Scoring',
    allowance: 'Allowance',
    gameMode: 'Game Mode',
    strokePlay: 'Stroke Play',

    // Share Modal
    shareCodeWithPlayers: 'Share this code with players to join the game',

    // Leaderboard Modal
    leaderboard: 'Leaderboard',
    loadingLeaderboard: 'Loading leaderboard...',
    groupStageStandings: 'Group Stage Standings',
    matchPlayInfo: 'Simplified Match Play: Results calculated as round-robin (all vs all). For bracket-style tournaments, use the Events feature.',
    failedToLoadLeaderboard: 'Failed to load leaderboard',

    // Edit Name Modal
    editGameName: 'Edit Game Name',
    chooseMemorableName: 'Choose a memorable name for your game',
    gameName: 'Game Name',
    enterGameName: 'Enter game name',
    gameNameUpdated: 'Game name updated!',
    failedToUpdateName: 'Failed to update name',

    // Settings Modal
    settings: 'Settings',
    settingsUpdatedSuccessfully: 'Settings updated successfully!',
    failedToUpdateSettings: 'Failed to update settings',

    // Leave/Delete Game Modal
    deleteGameQuestion: 'Delete Game?',
    leaveGameQuestion: 'Leave Game?',
    hostDeleteWarning: 'As the host, this will permanently delete the game for all players.',
    playerLeaveWarning: 'You will be removed from this game and will need to rejoin using the code.',
    deleting: 'Deleting...',
    leaving: 'Leaving...',
    deleteGame: 'Delete Game',
    leaveGame: 'Leave Game',
    gameDeleted: 'Game deleted',
    youHaveLeftTheGame: 'You have left the game',
    failedToDeleteGame: 'Failed to delete game',
    failedToLeaveGame: 'Failed to leave game'
  },

  // Multiplayer Score Entry translations
  multiplayerScoreEntry: {
    // Page Title and Navigation
    title: 'Multiplayer Score Entry',
    leaderboard: 'Leaderboard',
    gameDetailsTitle: 'Game Details',
    
    // Game Details Object
    gameDetails: {
      holesCount: '{count} holes',
      parCount: 'Par {count}',
      playingAs: 'Playing as: {player}',
      unknownPlayer: 'Unknown Player',
      liveStatus: 'Live'
    },
    
    // Loading States
    loading: {
      message: 'Loading multiplayer game...'
    },
    
    // Error States
    loadingMultiplayerGame: 'Loading multiplayer game...',
    failedToLoadGame: 'Failed to load game',
    gameNotFound: 'Game not found',
    gameNotFoundDescription: "The game you're looking for doesn't exist or you don't have access to it.",
    notPlayerInGame: 'You are not a player in this game',
    failedToLoadGameData: 'Failed to load game data',
    errorGameOrPlayerMissing: 'Error: Game or player data is missing',
    
    // Game Status and Info
    gameComplete: 'Game Complete!',
    allPlayersFinished: 'All players have finished their rounds.',
    viewResults: 'View Results',
    completeGame: 'Complete Game',
    manageGame: 'Manage Game',
    live: 'Live',
    playingAs: 'Playing as: {playerName}',
    
    // GPS and Tracking
    gpsDistanceTrackingActive: 'GPS Distance Tracking Active',
    
    // Toast Messages
    completingGame: 'Completing game...',
    gameCompletedWinner: 'Game completed! Winner: {winner} with {score} strokes',
    gameCompletedCheckResults: 'Game completed! Check out the final results and analytics.',
    failedToFinishGame: 'Failed to finish game: {error}',
    enterScoreBeforeCompleting: 'Please enter a score before completing the hole',
    allHolesCompleted: 'All holes completed! Check the leaderboard.',
    holeCompleted: '🎯 Hole {hole}: {scoreLabel}! Moving to hole {nextHole}',
    shotMarked: 'Shot {shotNumber} marked for hole {hole}!',
    shotMarkedGPS: 'Shot {shotNumber} marked for hole {hole}! (GPS tracked)',
    shotMarkedManual: 'Shot {shotNumber} marked for hole {hole}! (Manual entry - GPS unavailable)',
    failedToMarkShot: 'Failed to mark shot. Please try again.',
    unknownError: 'Unknown error',
    
    // Teammate Selection
    teammateSelection: {
      title: 'Teammate Selection',
      choosePlayer: 'Choose a player',
      selectPlayer: 'Select a player'
    },
    
    // Realtime Notifications  
    liveUpdates: 'Live Updates',
    hide: 'Hide',
    
    // Error Boundary
    errorBoundary: {
      title: 'Something went wrong',
      description: 'An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.',
      errorDetails: 'Error details',
      tryAgain: 'Try Again',
      refreshPage: 'Refresh Page'
    },
    
    // Phone Leaderboard
    phoneLeaderboard: {
      teamView: 'Team View',
      individualView: 'Individual View', 
      refreshing: 'Refreshing...',
      releaseToRefresh: 'Release to refresh',
      pullToRefresh: 'Pull to refresh',
      noScoresYet: 'No scores yet',
      startEntering: 'Start entering scores to see the leaderboard',
      holesProgress: '{completed}/{total} holes',
      unknownPlayer: 'Unknown Player',
      teamName: 'Team {number}',
      evenPar: 'E',
      totalScore: 'Total Score',
      holesPlayed: 'Holes Played'
    }
  },

  // Multiplayer Game Completion translations
  multiplayerGameCompletion: {
    // Page Title
    title: 'Game Complete',

    // Loading states
    loading: {
      analyzingPerformance: 'Analyzing game performance...',
      preparingAnalysis: 'Preparing game analysis...',
    },

    // Error states
    errors: {
      failedToLoad: 'Failed to Load Game',
      retry: 'Retry',
      retrying: 'Retrying...',
      backToDashboard: 'Back to Dashboard'
    },

    // Victory States
    victory: 'Victory!',
    top3Finish: 'Top 3 Finish!',
    roundComplete: 'Round Complete!',
    
    // Course Information
    golfCourse: 'Golf Course',
    
    // Toast Messages
    roundSharedSuccessfully: 'Round shared successfully!',
    failedToShareRound: 'Failed to share round',
    performanceSharedToFeed: 'Performance shared to feed!',
    performanceSharedDescription: 'Your multiplayer round has been shared with your friends',
    failedToSharePerformance: 'Failed to share performance',
    failedToLoadGameResults: 'Failed to load game results',
    
    // Badge and Trophy Messages
    earnedNewBadges: 'You earned {count} new badge{plural}!',
    earnedTrophy: '🏆 Earned {trophyName}!',
    
    // Golf Metrics Tooltips
    fairwaysInRegulationTitle: 'Fairways in Regulation',
    fairwaysInRegulationContent: 'Percentage of fairways hit off the tee on par 4 and par 5 holes. Good driving accuracy leads to better approach shots.',
    greensInRegulationTitle: 'Greens in Regulation',
    greensInRegulationContent: 'Percentage of greens reached in regulation (par-2 strokes). Higher percentages indicate better approach shot accuracy.',
    averagePuttsPerHoleTitle: 'Average Putts Per Hole',
    averagePuttsPerHoleContent: 'Average number of putts per hole during the round. Lower averages indicate better putting performance.',
    
    // Post Creation
    shareThoughtsPlaceholder: 'Share your thoughts about this round...',
    
    // Actions
    viewPost: 'View Post',
    shareVictory: 'Share This Victory!',
    
    // Player and Position Labels
    player: 'Player',
    positionSuffix1st: 'st',
    positionSuffix2nd: 'nd', 
    positionSuffix3rd: 'rd',
    positionSuffixTh: 'th',
    
    // Performance Assessments
    performanceExcellent: 'Excellent',
    performanceGood: 'Good',
    performanceAverage: 'Average',
    performanceNeedsWork: 'Needs Work',
    
    // Score Descriptions
    albatross: 'Albatross',
    eagle: 'Eagle',
    birdie: 'Birdie',
    par: 'Par',
    bogey: 'Bogey',
    doubleBogey: 'Double Bogey',
    evenPar: 'Even Par',
    double: 'Double',
    
    // Game Format
    strokePlay: 'Stroke Play',
    bestBall: 'Best Ball',
    
    // Leaderboard
    leader: 'Leader!',
    vsLeader: 'vs Leader',
    gameCompleted: 'Game completed',
    
    // Golf Performance Phrases
    allGreensInRegulation: 'All greens in regulation',
    perfectGirPerformance: 'Perfect GIR performance', 
    cleanRound: 'Clean round',
    strokesImpact: '{strokes} strokes impact',
    
    // Share Button States
    sharing: 'Sharing...',
    sharePerformance: 'Share Performance',
    
    // General
    achievement: 'Achievement',
    achievements: 'Achievements',
    noData: 'No data',
    missingParameters: 'Missing parameters',
    
    // Error Messages
    failedToLoadData: 'Failed to load data',
    errorOccurred: 'An error occurred',
    
    // NEW COMPREHENSIVE KEYS FOR FULL PAGE TRANSLATION
    // Game Status
    gameNotFound: 'Game Not Found',
    
    // Score Display
    position: 'Position',
    totalScore: 'Total Score',
    toPar: 'To Par',
    
    // Leaderboard
    finalLeaderboard: 'Final Leaderboard',
    
    // Putting Analysis
    putts: 'putts',
    averagePerHole: 'average per hole',
    onePutt: '1-Putt',
    twoPutts: '2-Putts', 
    threePutts: '3-Putts',
    puttingBreakdown: '{count} putts',
    
    // Scrambling Analysis
    scramblingAnalysis: 'Scrambling Analysis',
    upAndDownSuccess: 'Up-and-Down Success',
    successful: 'Successful',
    failed: 'Failed',
    saves: 'saves',
    attempts: 'attempts',
    aboveTourAverage: 'Above tour average (55%)',
    belowTourAverage: 'Below tour average (55%)',
    
    // Score Distribution
    scoreDistribution: 'Score Distribution',
    bird: 'BIRD',
    
    // Penalties
    penalties: 'PENALTIES',
    penaltyStrokes: 'Penalty Strokes',
    
    // Scorecard Table
    hole: 'Hole',
    parHeader: 'Par',
    score: 'Score',
    gir: 'GIR',
    puttsHeader: 'Putts',
    total: 'TOTAL',
    
    // Round Summary
    roundSummary: 'Round Summary',
    course: 'Course',
    gameFormat: 'Game Format',
    players: 'Players',
    playersCount: '{count} players',
    dateTime: 'Date & Time',
    finalPosition: 'Final Position',
    duration: 'Duration',
    
    // Game Formats (already exist above)
    
    // Action Buttons
    shareRound: 'Share Round',
    playAgain: 'Play Again',
    backToDashboard: 'Back to Dashboard',
    
    // Share Modal (sharePerformance already exists above)
    preview: 'Preview',
    addMessage: 'Add a message',
    includeLeaderboard: 'Include leaderboard',
    includePlayers: 'Include playing partners',
    includeAchievements: 'Include achievements',
    tagPlayers: 'Tag players',
    
    // Score Distribution Labels
    bog: 'BOG',
    dbl: 'DBL',
    parOrBetterRate: 'Par or Better Rate',
    
    // Analysis Sections
    performanceHighlights: 'Performance Highlights',
    roundProgression: 'Round Progression',
    holeAnalysis: 'Hole Analysis',
    bestHoles: 'Best Holes',
    troubleHoles: 'Trouble Holes',
    scoreBreakdown: 'Score Breakdown',
    completeScorecard: 'Complete Scorecard',
    underPar: 'Under Par',
    overPar: 'Over Par',
    
    // Table Headers
    fir: 'FIR',
    
    // Dynamic Messages
    wonAgainstPlayers: 'Won against {count} players!',
    finishedPosition: 'Finished {position} out of {total} players',
    you: '(You)',
    toParLowercase: 'to par',
    noOverParHoles: 'No over-par holes this round',
    amazingConsistency: 'Amazing consistency!',
    yourScore: 'Your Score',
    dashboard: 'Dashboard',

    // MultiplayerCompletionCard specific
    winner: 'Winner!',
    placeOf: 'place of',
    holes: 'holes',
    frontBack: 'Front | Back',
    avgPerHole: 'Avg/Hole',
    playingWith: 'Playing with',
    more: 'more'
  },

  // Course Details translations
  courseDetails: {
    title: 'Course Details',
    subtitle: 'Explore course information and stats',
    courseInformation: 'Course Information',
    courseStatistics: 'My Course Statistics',
    contactLocation: 'Contact & Location',
    courseDetails: 'Course Details',
    
    // Course Stats
    par: 'Par',
    holes: 'Holes',
    
    // Actions
    startRound: 'Start Round',
    previewCourse: 'Preview',
    leaderboards: 'Leaderboards',
    getDirections: 'Get Directions',

    // Contact Info
    address: 'Address',
    phone: 'Phone',
    website: 'Website',
    
    // Details
    hours: 'Hours',
    teeTimes: 'Tee Times',
    dawnToDusk: 'Dawn to Dusk',
    available: 'Available',
    
    // Loading & Errors
    loadingCourseDetails: 'Loading course details...',
    courseNotFound: 'Course not found',
    failedToLoadCourse: 'Failed to load course information',
    goBack: 'Go Back'
  },

  // Course Preview Map translations
  coursePreview: {
    loadingMap: 'Loading map...',
    myBag: 'My Bag',
    front: 'Front',
    middle: 'Middle',
    back: 'Back'
  },

  // Club Details translations
  clubDetails: {
    title: 'Club Details',
    subtitle: 'Explore club information and courses',
    loadingClubInformation: 'Loading club information...',
    loadingClubDetails: 'Loading club details...',
    unableToLoadClub: 'Unable to load club information',
    clubNotFound: 'Club not found',
    failedToLoadClub: 'Failed to load club information',
    goBack: 'Go Back',
    
    // Club Stats
    courses: 'Courses',
    members: 'Members',
    
    // Actions
    joinClub: 'Join Club',
    
    // Contact Info
    contactLocation: 'Contact & Location',
    address: 'Address',
    getDirections: 'Get Directions',
    openInMaps: 'Open in Maps',
    phone: 'Phone',
    website: 'Website',
    email: 'Email',
    
    // Club Details
    clubDetailsSection: 'Club Details',
    hours: 'Hours',
    dawnToDusk: 'Dawn to Dusk',
    membership: 'Membership',
    openToPublic: 'Open to Public',

    // Social Media & Amenities
    socialMedia: 'Follow Us',
    amenities: 'Facilities & Amenities',
    practice: 'Practice',
    services: 'Services',
    clubhouse: 'Clubhouse',
    additional: 'Additional',

    // Course Details
    holes: 'holes',
    gps: 'GPS'
  },

  // Club Events page (player-facing /club/:clubId/events)
  clubEvents: {
    pageTitle: 'Club Events',
    eventsCount: '{count} events · {upcoming} upcoming',
    noClubSelected: 'No Club Selected',
    noClubSelectedDesc: 'Please select a club to view events',
    backToClubHub: 'Back to Club Hub',
    unableToLoadEvents: 'Unable to load events',
    tryAgainLater: 'Please try again later',
    noEventsFound: 'No events found',
    adjustSearchCriteria: 'Try adjusting your search criteria',
    noUpcomingEvents: 'No upcoming events are currently available',
    clearSearch: 'Clear search',
    // Date filter options
    allDates: 'All dates',
    today: 'Today',
    thisWeek: 'This week',
    thisMonth: 'This month',
    // Type filter chips
    all: 'All',
    tournaments: 'Tournaments',
    social: 'Social',
    activities: 'Activities',
    // Event card
    participants: 'Participants',
    price: 'Price',
    free: 'Free',
    youreRegistered: "You're Registered",
    waitlistPosition: 'Waitlist #{position}',
    loginToRegister: 'Login to Register',
    registerNow: 'Register Now',
    registrationClosed: 'Registration Closed',
    // Registration dialog
    confirmRegistration: 'Confirm Registration',
    confirmRegistrationDesc: 'Review the event details and confirm your registration',
    capacity: 'Capacity:',
    entryFee: 'Entry Fee:',
    eventFull: 'This event is full. You will be added to the waitlist.',
    cancel: 'Cancel',
    confirmRegistrationBtn: 'Confirm Registration',
    registering: 'Registering...',
    registrationConfirmed: 'Registration confirmed!',
    addedToWaitlist: 'Added to waitlist (position {position})',
    failedToRegister: 'Failed to register for event',
    pleaseLoginToRegister: 'Please log in to register for events',
    paymentRequired: 'Please complete payment to confirm your registration',
    // Cancellation dialog
    cancelRegistration: 'Cancel Registration',
    cancelRegistrationDesc: "Are you sure? This cannot be undone.",
    status: 'Status:',
    reasonOptional: 'Reason (optional)',
    confirmed: 'Confirmed',
    keepRegistration: 'Keep Registration',
    cancelRegistrationBtn: 'Cancel Registration',
    cancelling: 'Cancelling...',
    cancelledSuccessfully: 'Registration cancelled successfully',
    failedToCancel: 'Failed to cancel registration',
    pleaseLoginToCancel: 'Please log in to cancel registration',
    invalidRegistration: 'Invalid registration data',
    alreadyCancelled: 'Registration is already cancelled',
    waitlistNotice: 'You will lose your position on the waitlist.',
    waitlistPromotion: 'The next person on the waitlist will be automatically notified.',
    // Payment dialog
    completePayment: 'Complete Payment',
    completePaymentDesc: 'Complete payment to confirm your registration',
    entryFeeLabel: 'Entry Fee:',
    paymentSuccess: 'Payment successful! Registration confirmed.',
    paymentError: 'Payment processed but failed to record. Please contact support.',
    payAtClubSuccess: 'Registration confirmed! Please pay at the club.',
    registrationCancelledOnPayCancel: 'Registration cancelled.',
  },

  // Metric Analysis translations
  metricAnalysis: {
    // Page-level messages
    loadingMetricAnalysis: 'Loading metric analysis...',
    errorLoadingData: 'Error Loading Data',
    failedToLoadMetricData: 'Failed to load metric data',
    invalidMetric: 'Invalid Metric',
    selectValidMetric: 'Please select a valid metric type.',
    metricNotAvailable: 'Metric Not Available',
    metricTypeNotSupported: 'The metric type "{metricType}" is not supported.',
    
    // Common analysis terms
    handicapIndex: 'Handicap Index',
    averageTerm: 'Average',
    trend: 'Trend',
    improvement: 'Improvement',
    performance: 'Performance',
    consistency: 'Consistency',
    target: 'Target',
    benchmark: 'Benchmark',
    
    // Performance levels
    elite: 'Elite',
    lowHandicap: 'Low Handicap',
    midHandicap: 'Mid Handicap',
    highHandicap: 'High Handicap',
    excellent: 'Excellent',
    good: 'Good',
    average: 'Average',
    needsWork: 'Needs Work',
    
    // Advice and tips
    focusOnConsistency: 'Focus on consistency and mental game. Consider competitive play.',
    goodConsistentPlay: 'Good, consistent play. Work on eliminating big numbers.',
    typicalMidHandicapper: 'Typical mid-handicapper. Focus on short game and course management.',
    keepPlayingImprovement: 'Keep playing! Significant improvement possible with practice.',
    playMoreRounds: 'Play more rounds to establish accurate handicap',
    focusOnConsistencyOverDistance: 'Focus on consistency over distance',
    trackProgressMonthly: 'Track progress monthly',
    workOnCourseManagement: 'Work on course management',
    practiceShortGame: 'Practice short game for quick improvement',
    
    // Chart and data terms
    evolution: 'Evolution',
    insights: 'Insights',
    education: 'Education',
    history: 'History',
    timeline: 'Timeline',
    progress: 'Progress',
    analysis: 'Analysis',
    breakdown: 'Breakdown',
    distribution: 'Distribution',
    comparison: 'Comparison',
    
    // Metric-specific terms
    greensInRegulation: 'Greens in Regulation',
    fairwaysHit: 'Fairways Hit',
    puttsPerRound: 'Putts per Round',
    scrambling: 'Scrambling',
    strokesGained: 'Strokes Gained',
    clubPerformance: 'Club Performance',
    scoringAverage: 'Scoring Average',
    
    // Time periods
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    last30Days: 'Last 30 Days',
    last6Months: 'Last 6 Months',
    thisYear: 'This Year',
    allTime: 'All Time',
    
    // Actions
    viewDetails: 'View Details',
    backToDashboard: 'Back to Dashboard',
    goBack: 'Go Back',
    refresh: 'Refresh',

    // Strokes Gained Analysis specific terms
    strokesGainedAnalysis: 'Strokes Gained Analysis',
    drivingAnalysis: 'Driving Analysis', 
    approachAnalysis: 'Approach Analysis',
    shortGameAnalysis: 'Short Game Analysis',
    trackPerformanceVsHandicap: 'Track your performance vs. handicap',
    
    // Club categories
    overall: 'Overall',
    driving: 'Driving',
    approach: 'Approach',
    short: 'Short',
    putting: 'Putting',
    
    // Analysis sections
    consistencyAnalysis: 'Consistency Analysis',
    // accuracyAnalysis: 'Accuracy Analysis', // Removed duplicate - defined as object below
    distanceAnalysis: 'Distance Analysis',
    gappingAnalysis: 'Gapping Analysis',
    
    // Consistency terms
    leastConsistent: 'Least Consistent',
    mostConsistent: 'Most Consistent',
    clubs: 'Clubs',
    
    // Accuracy terms  
    leastAccurate: 'Least Accurate',
    mostAccurate: 'Most Accurate',
    
    // Distance terms
    distancePerformanceAcrossClubs: 'Distance performance across your clubs',
    averageDistancesRecentRounds: 'Average distances based on recent rounds',
    notEnoughDataClubGaps: 'Not enough data to analyze club gaps',
    identifyDistanceGapsOverlaps: 'Identify distance gaps and overlaps in your club setup',
    wellGappedSetup: 'Well-Gapped Setup',
    goodDistanceSpacing: 'Your clubs have good distance spacing',
    distanceGaps: 'Distance Gaps',
    overlaps: 'Overlaps',
    avgGapSize: 'Avg Gap Size',
    clubsWithLargeGaps: 'Clubs with large distance gaps',
    clubsWithSimilarDistances: 'Clubs with similar distances',
    clubGappingTips: 'Club Gapping Tips',
    playMoreForClubStats: 'Play more rounds to see detailed club statistics',
    
    // Club performance terms
    medianDistance: 'Median Distance',
    rangeQ1Q3: 'Range (Q1-Q3)',
    shortRange: 'Short Range',
    longRange: 'Long Range',
    shotConditions: 'Shot Conditions',
    
    // Performance levels for clubs
    notEnoughData: 'Not Enough Data',
    par4Performance: 'Par 4 Performance', 
    par5Performance: 'Par 5 Performance',
    playingStyle: 'Playing Style',
    aggressivePlayer: 'Aggressive Player',
    precisionPlayer: 'Precision Player',
    powerPlayer: 'Power Player',
    developingPlayer: 'Developing Player',
    insufficientDataPlayingStyle: 'Insufficient Data for Playing Style Analysis',
    averageScore: 'Average Score',
    courseArchitectureInsight: 'Course Architecture Insight',
    correlation: 'Correlation',
    strength: 'Strength',
    
    // Playing style descriptions
    powerAndPrecision: 'Power & Precision',
    longAndStraightCompletePackage: 'Long and straight - the complete package',
    precisionPlayerDesc: 'Precision Player',
    accuracyOverPower: 'Accuracy over power',
    powerPlayerDesc: 'Power Player', 
    distanceWithRoomForAccuracy: 'Distance with room for accuracy',
    developingPlayerDesc: 'Developing Player',
    buildingFundamentals: 'Building fundamentals',
    
    // Status messages for data
    playMoreRoundsClubAnalysis: 'Play more rounds to see club performance analysis',
    playMoreRoundsClubDistance: 'Play more rounds to see club distance analysis',
    
    // Dynamic performance messages
    significantlyBetterThanExpected: 'Significantly better than expected',
    par3TeeShots: 'Par 3 tee shots',
    averageCloserToPin: 'On average 12% closer to pin',
    puttingGame: 'Putting Game',
    improvementMessage: "You've improved by 8.3 strokes compared to your 2 round prior average of 0.1 SG per round!",
    approachShotsLosing: "You're losing 3.3 strokes on your approach shots compared to your overall +1.3 strokes gained.",
    shortShotsLosing: "You're losing 0.5 strokes on your short shots compared to your overall +1.3 strokes gained.",
    puttsLosing: "You're losing 4.8 strokes on your putts compared to your overall +1.3 strokes gained.",
    overallStrokesGained: 'You gain 1.3 strokes per round compared to a 4 HCP',
    
    // Chart names
    totalSG: 'Total SG',
    drivingSG: 'Driving SG', 
    approachSG: 'Approach SG',
    shortGameSG: 'Short Game SG',
    puttingSG: 'Putting SG',
    
    // Tooltip content keys
    whatsThis: "What's This?",
    drivingDistanceAccuracyTooltip: 'This chart plots your driving distance against accuracy for recent rounds. Each point represents a round, showing the relationship between how far you hit it and how often you find the fairway. The ideal is high distance with high accuracy.',
    drivingAccuracyTooltip: 'Driving Accuracy measures the percentage of fairways hit with your tee shots on par 4s and 5s. The chart shows your accuracy percentage and miss patterns - whether you tend to miss left or right of the fairway.',
    drivingDistanceTooltip: 'Your Driving Distance chart identifies what your average distance is off the tee on par 4s and par 5s. This is calculated using your smart distances for any club that you hit on those tee shots, and we compare it to your target handicap\'s average.',
    approachByPinDistanceTooltip: 'Your Approach by Pin Distance chart shows you which approach shot distances on an average 18-hole round are gaining or losing you strokes. Are you better with your longer irons? Or do you have more control the closer you are to the green?',
    approachByTerrainTooltip: 'Your Approach by Terrain chart shows you which approach shot terrains on an average 18-hole round are gaining or losing you strokes. Seeing how you perform from the tee (on par 3s), fairway, rough or sand will give you an idea of where to practice before your next round.',
    girTooltip: 'Greens in Regulation (GIR) measures how often you hit the green in the regulation number of strokes (Par minus 2). This chart shows your GIR percentage and miss patterns - whether you typically miss short, long, left, or right of the pin.',
    chipByPinDistanceTooltip: 'Your Chip by Pin Distance chart shows you which chip shot distances on an average 18-hole round are gaining or losing you strokes. Are you better from just off the green? Able to go flag hunting from a little bit farther out?',
    sandByPinDistanceTooltip: 'Your Sand by Pin Distance chart shows you which sand shot distances on an average 18-hole round are gaining or losing you strokes. Are you better in bunkers around the green? Have some magic with longer greenside bunker shots?',
    puttingByHoleTooltip: 'Your Putting By Hole chart shows you what your putting momentum looks like on an average 18-hole round. Do you start off hot on the greens? Slump before the turn? Drain more putts at the tail end of a round?',
    puttingByFirstPuttLengthTooltip: 'Your Putting By 1st Putt Length chart shows you how you\'re performing from different putt lengths. We track your strokes gained from your first putt and break the distances down so you can see where you should focus when practicing on the greens.',
    trendAnalysisTooltip: 'Your Trend Analysis shows how each area of your game has progressed over your recent rounds. Each colored line represents a different aspect: Total SG (blue), Driving (green), Approach (purple), Short Game (orange), and Putting (red). This helps you identify which areas are improving or need more focus.',
    
    // Stroke Gained Insights section
    topThreeInsights: 'Top 3 Insights',
    whatToWorkOn: 'What To Work On',
    whatsHelpingYou: 'What\'s Helping You',
    
    // Score types and results
    birdies: 'Birdies',
    pars: 'Pars', 
    bogeys: 'Bogeys',
    doublePlus: 'Double+',
    
    // Hole shapes and terrains
    doglegLeft: 'Dogleg Left',
    straight: 'Straight',
    doglegRight: 'Dogleg Right',
    teeParThrees: 'Tee (Par 3s)',
    fairway: 'Fairway',
    rough: 'Rough',
    sand: 'Sand',
    
    // Chart labels and axes
    distanceYards: 'Distance (yards)',
    accuracyPercent: 'Accuracy (%)',
    strokesGainedLabel: 'Strokes Gained',
    
    // Common terms
    notAvailable: 'N/A',
    lastTwoRounds: 'LAST 2 RDS',
    missedGreens: 'Missed Greens',
    avgDistanceToPin: 'Avg. Dist. To Pin',
    upAndDown: 'Up & Down',
    
    // Par categories
    par3s: 'Par 3s',
    par4s: 'Par 4s', 
    par5s: 'Par 5s',
    
    // Round labels
    round1: 'Round 1',
    round2: 'Round 2',
    round3: 'Round 3',
    round4: 'Round 4',
    round5: 'Round 5',
    
    // Handicap Analysis specific keys
    officialHandicapDescription: 'Your official handicap index based on recent rounds',
    buildYourHandicap: 'Build Your Handicap',
    currentIndex: 'Current Index',
    handicapEvolution: 'Handicap Evolution',
    yourGolfImprovementJourney: 'Your golf improvement journey',
    buildHandicapDescription: 'Complete at least 5 rounds to establish your official handicap index and start tracking your improvement journey.',
    handicapMeasuresDescription: '💡 Your handicap measures your potential and tracks improvement over time',
    
    // Performance levels and labels
    bogeyGolfer: 'Bogey Golfer',
    singleDigit: 'Single Digit',
    scratchGolfer: 'Scratch Golfer',
    improving: 'Improving',
    declining: 'Declining',
    stable: 'Stable',
    noRoundsYet: 'No rounds yet',
    
    // Journey and milestone text
    handicapJourney: 'Handicap Journey',
    nextMilestone: 'Next Milestone',
    startingHandicap: 'Starting Handicap',
    currentHandicap: 'Current Handicap',
    totalChange: 'Total Change',
    strokesToGo: 'strokes to go',
    vsRecent: 'vs recent',
    next: 'Next',
    
    // Chart and data labels
    noHandicapHistory: 'No Handicap History',
    playMoreRoundsHandicapEvolution: 'Play more rounds to see your handicap evolution',
    lastNRounds: 'Last {count} rounds',
    
    // Performance Analysis
    performanceLevel: 'Performance Level',
    skillLevel: 'Skill Level',
    handicapBreakdown: 'Handicap Breakdown',
    recentTrend: 'Recent Trend',
    roundsUsed: 'Rounds Used',
    improvementTips: 'Improvement Tips',
    
    // Education section
    whatIsHandicap: 'What is a Handicap?',
    yourSkillLevelIndicator: 'Your skill level indicator',
    handicapDescription: 'Your handicap index represents your potential scoring ability. It\'s calculated from your best 8 scores out of your most recent 20 rounds.',
    howItWorks: 'How it works:',
    lowerHandicapBetter: '• Lower handicap = better player',
    updatedAfterEachRound: '• Updated after each round',
    allowsFairCompetition: '• Allows fair competition between players',
    basedOnCourseDifficulty: '• Based on course difficulty and conditions',
    
    // Handicap levels
    handicapLevels: 'Handicap Levels',
    scratch: 'Scratch',
    handicapSuffix: '+ handicap',
    
    // Calculation method
    howItsCalculated: 'How It\'s Calculated',
    stepByStep: 'Step-by-step:',
    calculateScoreDifferential: '1. Calculate score differential for each round',
    selectBestDifferentials: '2. Select best 8 differentials from recent 20 rounds',
    averageBestDifferentials: '3. Average the 8 best differentials',
    multiplyByNinetySix: '4. Multiply by 0.96 (96%)',
    truncateToOneDecimal: '5. Truncate to one decimal place',
    yourCalculatedIndex: 'Your calculated index',
    
    // Advice messages
    eliteAdvice: 'Focus on consistency and mental game. Consider competitive play.',
    lowHandicapAdvice: 'Work on course management and eliminating big numbers.',
    midHandicapAdvice: 'Focus on short game and approach shots for quick improvement.',
    bogeyGolferAdvice: 'Work on fundamentals: grip, stance, and consistent contact.',
    highHandicapAdvice: 'Focus on basic fundamentals and getting the ball in play.',
    
    // Scoring Analysis specific keys
    scoringAnalysis: 'Scoring Analysis',
    trackYourScoringPerformance: 'Track your scoring performance',
    noScoringDataAvailable: 'No Scoring Data Available',
    completedMoreRounds: 'Complete more rounds to see your scoring analysis.',
    
    // Performance levels
    developing: 'Developing',
    beginner: 'Beginner',
    
    // Advice messages for scoring
    eliteScoringAdvice: 'Exceptional scoring! Focus on consistency and mental game.',
    excellentScoringAdvice: 'Great scoring average. Focus on eliminating big numbers.',
    goodScoringAdvice: 'Solid scoring. Work on short game and course management.',
    averageScoringAdvice: 'Focus on avoiding double bogeys and improving putting.',
    developingScoringAdvice: 'Work on fundamentals and course management.',
    beginnerScoringAdvice: 'Focus on basic swing fundamentals and getting ball in play.',
    
    // Goal advice
    workOnBreaking70: 'Work on breaking 70 consistently',
    targetBreakingPar: 'Target breaking par more often',
    aimToBreak80: 'Aim to break 80 consistently',
    targetBreaking85: 'Target breaking 85 regularly',
    aimToBreak90: 'Aim to break 90 consistently',
    targetBreaking100: 'Target breaking 100 first',
    
    // Tab labels and navigation
    overview: 'Overview',

    // Chart and data labels
    scoringTrend: 'Scoring Trend',
    avgScore: 'AVG SCORE',
    currentPerformance: 'Current Performance',
    improvingTrend: 'Improving',
    gettingWorse: 'Getting worse',
    penalties: 'PENALTIES',
    perRoundAverage: 'Per round average',
    vsPeers: 'vs Peers',
    vsHandicap: 'vs Handicap',
    avg: 'avg',
    live: 'Live',
    static: 'Static',
    estimated: 'Est.',
    
    // Scoring distribution
    scoringDistribution: 'Scoring Distribution',
    frontVsBack9: 'Front vs Back 9',
    front9: 'Front 9',
    back9: 'Back 9',
    total18HoleAvg: 'Total 18-Hole Avg',
    vsPar: 'vs par',
    peerAvg: 'peer avg',
    performanceByHoleType: 'Performance by Hole Type',
    par3: 'Par 3',
    par4: 'Par 4',
    par5: 'Par 5',
    holes: 'holes',
    noScoringDataDistribution: 'No scoring data available for distribution analysis.',
    
    // Score-to-Par Analysis
    scoreToParAnalysis: 'Score-to-Par Analysis',
    scoreToParDescription: 'This analysis shows how many strokes over/under par you average on each hole type compared to what\'s expected for your handicap level.',
    greenBackground: 'Green background:',
    outperformingExpectations: 'You\'re outperforming expectations for your handicap level on this hole type.',
    redBackground: 'Red background:',
    underperformingExpectations: 'You\'re underperforming expectations - focus improvement here.',
    yourAvgToPar: 'Your Avg to Par',
    expected: 'Expected',
    vsExpected: 'vs expected',
    
    // Problem holes analysis
    problemHolesAnalysis: 'Problem Holes Analysis',
    playMoreRoundsProblemHoles: 'Play more rounds to see problem holes analysis',
    identifyHardestHoles: 'Identify your hardest holes vs easiest scoring opportunities',
    
    // Scoring distribution chart
    scoringDistributionPerHole: 'Scoring Distribution Per Hole',
    playMoreRoundsScoringDistribution: 'Play more rounds to see scoring distribution per hole',
    birdieOrBetter: 'Birdie or Better',
    birdieAbbrev: 'Birdie+',
    doubleAbbrev: 'Double+',
    noData: 'No Data',
    basedOnLast20Rounds: 'Based on last 20 rounds',
    totalHoleScores: 'total hole scores',
    swipeToSeeAllHoles: '← Swipe to see all holes →',
    
    // Penalty analysis
    penaltyStrokeAnalysis: 'Penalty Stroke Analysis',
    penaltyPerformance: 'Penalty Performance',
    yourAverage: 'Your Average',
    peerAverage: 'Peer Average',
    difference: 'Difference',
    scoreImpact: 'Score Impact',
    baseScoreNoPenalties: 'Base Score (no penalties)',
    penaltyStrokes: 'Penalty Strokes',
    totalScore: 'Total Score',
    penaltyReductionTips: 'Penalty Reduction Tips',
    playWithinAbilities: '• Play within your abilities - avoid risky shots',
    useCourseManagement: '• Use course management to avoid hazards',
    focusKeepBallInPlay: '• Focus on keeping the ball in play',
    practiceRecoveryShots: '• Practice recovery shots from rough/trouble',
    considerUnplayableLies: '• Consider taking unplayable lies when stuck',
    performanceAssessment: 'Performance Assessment',
    excellentPenaltyManagement: 'Excellent penalty management! You\'re avoiding penalty strokes better than peers.',
    focusPenaltyReduction: 'Focus on penalty reduction. Your penalty rate is higher than similar handicap golfers.',
    typicalPenaltyRate: 'Your penalty rate is typical for your handicap level.',

    // Improvement tips (from config)
    focusOnCourseManagement: 'Focus on course management',
    workOnShortGameImprovement: 'Work on short game for quick improvement',
    playWithinYourAbilities: 'Play within your abilities',
    avoidPenaltyStrokes: 'Avoid penalty strokes',
    practicePuttingReduceThreePutts: 'Practice putting to reduce three-putts',
    
    // Tab labels
    learn: 'Learn',
    
    
    // Scoring Analysis - Complete translation keys
    scoring: {
      // Navigation & Header (3 keys)
      navigation: {
        backToDashboard: 'Back to Dashboard',
        title: 'Scoring Analysis',
        subtitle: 'Track your scoring performance'
      },
      
      // Main Metrics (15 keys)
      metrics: {
        averageScore: 'Average Score',
        avgScore: 'AVG SCORE',
        trend: 'TREND', 
        penalties: 'PENALTIES',
        perRoundAverage: 'Per round average',
        currentPerformance: 'Current Performance',
        vsRecent: 'vs Recent',
        vsPeers: 'vs Peers',
        vsHandicap: 'vs Handicap',
        avg: 'avg',
        improving: 'Improving',
        gettingWorse: 'Getting worse',
        stable: 'Stable',
        live: 'Live',
        static: 'Static'
      },
      
      // Chart & Timeline (20 keys)
      chart: {
        scoringTrend: 'Scoring Trend',
        lastRounds: 'Last {count} rounds',
        round: 'Round',
        handicapBenchmark: 'Handicap ({value})',
        peerBenchmark: 'Peer ({value})',
        staticLabel: '[Static]',
        liveLabel: '[Live]',
        estimateLabel: '[Est.]',
        roundNumber: 'Round {number}',
        scoreValue: '{value} score',
        handicapTarget: 'Handicap target: {value}',
        peerAverage: 'Peer avg: {value}',
        basedOnRounds: 'Based on last 20 rounds',
        totalHoleScores: 'total hole scores',
        swipeHint: '← Swipe to see all holes →',
        percentage100: '100%',
        percentage80: '80%',
        percentage60: '60%',
        percentage40: '40%',
        percentage20: '20%',
        percentage0: '0%'
      },
      
      // Hole Analysis (25 keys)  
      holes: {
        hardestHoles: 'Hardest Holes',
        easiestHoles: 'Easiest Holes',
        opportunitiesText: 'Identify your hardest holes vs easiest scoring opportunities',
        holeWithPar: 'H{hole} (Par {par})',
        averageScore: '{value} avg',
        holeDetails: 'Hole {hole} (Par {par})',
        birdieOrBetter: 'Birdie or Better:',
        par: 'Par:',
        bogey: 'Bogey:',
        doublePlus: 'Double+:',
        average: 'Average: {value}',
        birdiePlus: 'Birdie+',
        parLabel: 'Par',
        bogeyLabel: 'Bogey',
        doublePlusLabel: 'Double+',
        noData: 'No Data',
        par3: 'Par 3',
        par4: 'Par 4', 
        par5: 'Par 5',
        front9: 'Front 9',
        back9: 'Back 9',
        total18: 'Total 18-Hole Avg',
        vsPar: 'vs Par: {value}',
        vsPeerAvg: 'vs {value} peer avg',
        holesCount: '{count} holes',
        yourAvgToPar: 'Your Avg to Par',
        expected: 'Expected: {value}',
        vsExpected: '{value} vs expected'
      },
      
      // Performance Categories (12 keys)
      performance: {
        elite: 'Elite',
        excellent: 'Excellent',
        good: 'Good', 
        average: 'Average',
        developing: 'Developing',
        beginner: 'Beginner',
        level: '{performance} Level',
        eliteAdvice: 'Exceptional scoring! Focus on consistency and mental game',
        excellentAdvice: 'Great scoring average. Focus on eliminating big numbers',
        goodAdvice: 'Solid scoring. Work on short game and course management',
        averageAdvice: 'Focus on avoiding double bogeys and improving putting',
        beginnerAdvice: 'Work on fundamentals and course management'
      },
      
      // Tips & Goals (25 keys)
      tips: {
        courseManagement: 'Focus on course management',
        shortGame: 'Work on short game for quick improvement',
        playWithinAbilities: 'Play within your abilities',
        avoidPenalties: 'Avoid penalty strokes',
        practicePutting: 'Practice putting to reduce three-putts',
        break70: 'Work on breaking 70 consistently',
        breakPar: 'Target breaking par more often',
        break80: 'Aim to break 80 consistently',
        break85: 'Target breaking 85 regularly',
        break90: 'Aim to break 90 consistently',
        break100: 'Target breaking 100 first',
        basicFundamentals: 'Focus on basic swing fundamentals and getting ball in play',
        avoidRiskyShots: 'Play within your abilities - avoid risky shots',
        courseManagementHazards: 'Use course management to avoid hazards',
        keepBallInPlay: 'Focus on keeping the ball in play',
        practiceRecovery: 'Practice recovery shots from rough/trouble',
        considerUnplayable: 'Consider taking unplayable lies when stuck',
        excellentPenaltyManagement: 'Excellent penalty management! You\'re avoiding penalty strokes better than peers',
        focusOnPenaltyReduction: 'Focus on penalty reduction. Your penalty rate is higher than similar handicap golfers',
        typicalPenaltyRate: 'Your penalty rate is typical for your handicap level'
      },
      
      // Sections & Headers (13 keys)
      sections: {
        overview: 'Overview',
        scoringDistribution: 'Scoring Distribution',
        performanceByHoleType: 'Performance by Hole Type',
        frontVsBack: 'Front vs Back 9',
        penaltyAnalysis: 'Penalty Stroke Analysis',
        penaltyPerformance: 'Penalty Performance',
        scoreImpact: 'Score Impact',
        penaltyTips: 'Penalty Reduction Tips',
        performanceAssessment: 'Performance Assessment',
        distributionPerHole: 'Scoring Distribution Per Hole',
        problemHoles: 'Problem Holes Analysis',
        scoreToParAnalysis: 'Score-to-Par Analysis'
      },
      
      // Empty States (6 keys)
      emptyStates: {
        noScoringData: 'No Scoring Data Available',
        completeMoreRounds: 'Complete more rounds to see your scoring analysis',
        playMoreForProblems: 'Play more rounds to see problem holes analysis',
        playMoreForDistribution: 'Play more rounds to see scoring distribution per hole',
        noDistributionData: 'No scoring data available for distribution analysis'
      },
      
      // Data Labels (25 keys)
      dataLabels: {
        yourAverage: 'Your Average',
        peerAverage: 'Peer Average',
        difference: 'Difference',
        baseScore: 'Base Score (no penalties)',
        penaltyStrokes: 'Penalty Strokes',
        totalScore: 'Total Score',
        analysisDescription: 'This analysis shows how many strokes over/under par you average on each hole type compared to what\'s expected for your handicap level',
        greenBackground: 'Green background: You\'re outperforming expectations for your handicap level on this hole type',
        redBackground: 'Red background: You\'re underperforming expectations - focus improvement here',
        averageScore: 'Average Score',
        description: 'Your average score per round over recent games',
        eliteAdvice: 'Exceptional scoring! Focus on consistency and mental game',
        excellentAdvice: 'Great scoring average. Focus on eliminating big numbers',
        goodAdvice: 'Solid scoring. Work on short game and course management',
        averageAdvice: 'Focus on avoiding double bogeys and improving putting'
      },

      // Round Breakdown Section
      roundBreakdown: {
        title: 'Round Breakdown',
        gross: 'Gross',
        net: 'Net'
      },

      // Per Round Chart
      perRoundChart: {
        recentRounds: 'Recent Rounds',
        roundsCount: '{count} rounds',
        zeroRounds: '0 rounds',
        holes: '{count} holes',
        avg: 'AVG',
        peerAvg: 'PEER AVG',
        target: 'TARGET',
        selectedRound: 'Selected Round',
        scoreToPar: 'Score to Par',
        netScore: 'Net Score',
        grossScore: 'Gross Score',
        recentRound: 'Recent round',
        peerAverage: 'Peer average',
        handicapTarget: 'Handicap target'
      },

      // Score Types for Labels
      scoreTypes: {
        eagle: 'Eagle',
        birdie: 'Birdie',
        par: 'Par',
        bogey: 'Bogey',
        doubleBogey: 'Double Bogey',
        doublePlus: 'Double+'
      },

      // Comparisons
      comparisons: {
        vsPar: 'vs par',
        peers: 'Peers',
        target: 'Target',
        noData: 'No data',
        underPar: 'Under par',
        overPar: 'Over par',
        parLegend: '← Under par | Par | Over par →'
      },

      // Explanation Dialogs
      explanations: {
        front9VsBack9: {
          title: 'Front 9 vs Back 9 Performance',
          description: 'Compares your scoring on the front nine versus back nine to identify patterns in your round. Many golfers score differently as rounds progress.',
          front9Label: 'Front 9',
          front9Description: 'Your scoring on holes 1-9. Starting strong often sets the tone for the round.',
          back9Label: 'Back 9',
          back9Description: 'Your scoring on holes 10-18. Fatigue, pressure, or improved warm-up can affect back nine performance.',
          patternLabel: 'The Pattern',
          patternDescription: 'Consistently worse back 9? Consider fitness and hydration. Worse front 9? Your warm-up may need work.',
          proTip: 'If your back 9 is consistently worse, try a lighter pre-round warm-up to save energy, stay hydrated, and eat a snack at the turn.'
        },
        holeType: {
          title: 'Performance by Hole Type',
          description: 'Analyzes your scoring based on hole par value to identify strengths and areas for improvement.',
          par3Label: 'Par 3s',
          par3Description: 'Short holes requiring precision. Often determines round outcome.',
          par4Label: 'Par 4s',
          par4Description: 'Most common hole type. Consistency here is key to good scores.',
          par5Label: 'Par 5s',
          par5Description: 'Birdie opportunities for longer hitters, par is a good score.',
          proTip: 'Focus practice time on your weakest hole type for the biggest improvement.'
        },
        scoringBreakdown: {
          title: 'Scoring Breakdown',
          description: 'Shows your score distribution across all rounds to understand scoring patterns.',
          birdiesLabel: 'Eagles & Birdies',
          birdiesDescription: 'Under-par scores showing your best performances.',
          parsLabel: 'Pars',
          parsDescription: 'Solid scores that maintain your handicap.',
          bogeysLabel: 'Bogeys & Worse',
          bogeysDescription: 'Over-par scores to minimize for improvement.',
          proTip: 'Reducing double bogeys is often easier and more effective than chasing birdies.'
        }
      }
    },
    
    // Fairway Analysis specific keys
    fairwayAnalysis: {
      // Navigation & Header (3 keys)
      navigation: {
        backToDashboard: 'Back to Dashboard',
        title: 'Fairway Analysis',
        subtitle: 'Track your driving accuracy'
      },
      
      // Main Metrics (15 keys)
      metrics: {
        fairwaysInRegulation: 'Fairways in Regulation',
        current: 'CURRENT',
        target: 'TARGET',
        last10Rounds: 'Last 10 rounds',
        forHandicap: 'For {handicap} handicap',
        vsRecent: 'vs Recent',
        vsPeers: 'vs Peers',
        vsHandicap: 'vs Handicap',
        avg: 'avg',
        live: 'Live',
        static: 'Static',
        percentile: 'th percentile',
        improving: 'Improving',
        gettingWorse: 'Getting worse',
        stable: 'Stable'
      },
      
      // Chart & Data Labels (20 keys)
      chart: {
        fairwayTrend: 'Fairway Trend',
        lastRounds: 'Last {count} rounds',
        round: 'Round',
        fairwayDistribution: 'Fairway Distribution',
        avgFIR: 'avg FIR',
        holes: 'Holes',
        fairwayHit: 'Fairway Hit',
        fairwayMiss: 'Fairway Miss',
        par4FIR: 'Par 4 FIR',
        par5FIR: 'Par 5 FIR',
        holesCount: '{hits}/{holes} holes',
        keyInsight: 'Key Insight',
        difference: '{difference}% difference',
        betterOnPar4s: '(better on Par 4s)',
        betterOnPar5s: '(better on Par 5s)',
        handicapBenchmark: 'Handicap ({percentage}%)',
        peerBenchmark: 'Peer ({percentage}%)',
        fairwayDistributionPerHole: 'Fairway Distribution Per Hole',
        basedOnLast10Rounds: 'Based on last 10 rounds',
        totalHoleScores: 'total hole scores'
      },
      
      // Insights & Analysis (15 keys)
      insights: {
        strongVsPeers: 'Strong vs Peers',
        strongVsPeersMessage: 'You\'re hitting {gap}% more fairways than similar app users ({handicap}-handicap)',
        belowPeerAverage: 'Below Peer Average',
        belowPeerAverageMessage: '{gap}% below peers - focus on accuracy over distance',
        aboveOfficialStandard: 'Above Official Standard',
        aboveOfficialStandardMessage: '{gap}% above official {handicap}-handicap standard - excellent driving accuracy',
        belowOfficialStandard: 'Below Official Standard',
        belowOfficialStandardMessage: '{gap}% below official standard - work on driver consistency',
        improvingAccuracy: 'Improving Accuracy',
        improvingAccuracyMessage: 'Your fairway accuracy has improved {trend}% over recent rounds',
        recentStruggles: 'Recent Struggles',
        recentStrugglesMessage: 'Fairway accuracy has declined {trend}% recently - check setup fundamentals',
        performanceInsights: 'Performance Insights',
        improvementTips: 'Improvement Tips',
        focusOnAccuracy: 'Focus on accuracy over distance'
      },
      
      // Sections & Headers (6 keys)
      sections: {
        overview: 'Overview',
        par4VsPar5: 'Par 4 vs Par 5',
        mostFairwayMisses: 'Most Fairway Misses',
        consistentDriving: 'Consistent Driving',
        focusOnHoles: 'Focus on holes needing accuracy work vs consistent driving performance',
        whatIsThis: 'What is this?'
      },
      
      // Empty States (5 keys)
      emptyStates: {
        noFairwayData: 'No Fairway Data Available',
        completeMoreRounds: 'Complete more rounds to see your fairway analysis',
        playMoreForProblems: 'Play more rounds to see fairway problem holes analysis',
        playMoreForDistribution: 'Play more rounds to see fairway distribution analysis',
        playMoreForPerHole: 'Play more rounds to see fairway distribution per hole'
      },
      
      // Data Labels (15 keys)
      dataLabels: {
        hole: 'H{hole} (Par {par})',
        missed: '{misses}/{total} missed',
        hit: '{hits}/{total} hit',
        par3sExcluded: 'Par 3s excluded',
        swipeToSeeAllHoles: '← Swipe to see all holes →',
        noData: 'No Data',
        total: 'Total: {total} rounds',
        fairway: 'Fairway:',
        miss: 'Miss:',
        par4Accuracy: 'Par 4 Accuracy:',
        par5Accuracy: 'Par 5 Accuracy:',
        keyInsightDescription: 'Comparing fairway accuracy on Par 4s vs Par 5s helps identify driving patterns and strategy preferences:',
        par4Description: 'Shows how you perform when accuracy is typically more important than distance. Many par 4s require precision to set up scoring opportunities.',
        par5Description: 'Shows how you perform when distance often matters more. Some golfers swing harder on par 5s, affecting accuracy.',
        proTip: 'Pro Tip: Consider using 3-wood or hybrid on tight par 4s if driver accuracy is inconsistent. Distance isn\'t everything!',
        yourAverage: 'Your Average:',
        handicap: 'Handicap:',
        peer: 'Peer:',
        sample: 'Sample:',
        rounds: 'rounds',
        live: 'Live',
        static: 'Static'
      },

      // Improvement Messages (3 keys)
      improvement: {
        belowPeerAverage: 'Below Peer Average',
        belowPeersMessage: '{gap}% below peers - focus on accuracy over distance',
        roomForImprovement: 'Room for improvement: {performance}% FIR is below your {target}% target. Focus on accuracy over distance.'
      },

      // Tips (5 keys)
      tips: {
        focusOnAccuracy: 'Focus on accuracy over distance',
        practiceAlignment: 'Practice with alignment aids',
        workOnSetup: 'Work on setup and stance',
        developTempo: 'Develop consistent tempo',
        clubSelection: 'Consider club selection strategy'
      },

      // Fairway Distribution explanation dialog
      fairwayDistributionExplanation: {
        title: 'Fairway in Regulation (FIR)',
        explanation: 'FIR measures how often your tee shot lands on the fairway on par 4s and par 5s. This chart shows where your misses go when you don\'t find the fairway.',
        hit: 'Hit',
        hitDesc: 'Tee shots that found the fairway, giving you a clean lie for your approach.',
        missRight: 'Miss Right',
        missRightDesc: 'Shots pushed or sliced right. Often indicates an open clubface at impact.',
        missLeft: 'Miss Left',
        missLeftDesc: 'Shots pulled or hooked left. Often indicates a closed clubface or over-the-top swing.',
        missShort: 'Miss Short',
        missShortDesc: 'Shots that didn\'t reach the fairway. Could indicate topped shots or hazards in play.',
        proTip: 'A consistent miss pattern (always left or always right) is easier to fix than random misses. Track your pattern and adjust your aim accordingly.',
        avgFir: 'avg FIR',
        holes: 'holes'
      },
      // Fairway By Par Section
      fairwayByParSection: {
        noDataAvailable: 'No accuracy data available',
        accuracyByHoleType: 'Accuracy by Hole Type',
        fairwayAccuracyByPar: 'Fairway Accuracy by Par',
        girByPar: 'Green in Regulation by Par',
        par3: 'Par 3',
        par4: 'Par 4',
        par5: 'Par 5',
        peers: 'Peers',
        target: 'Target',
        // FIR Dialog
        firDialogTitle: 'Fairway Accuracy by Par',
        firDialogExplanation: 'This chart compares your driving accuracy on Par 4s versus Par 5s. Par 3s are excluded since they don\'t have fairways.',
        firPar4Label: 'Par 4s',
        firPar4Desc: 'Typically shorter holes (350-475 yards) where accuracy is crucial for good approach shots.',
        firPar5Label: 'Par 5s',
        firPar5Desc: 'Longer holes (475+ yards) where you may use driver or fairway wood. Often more room for error.',
        firGapLabel: 'Performance Gap',
        firGapDesc: 'Large differences suggest adapting your strategy - maybe use 3-wood on Par 5s if accuracy drops significantly.',
        firProTip: 'If your FIR% is much lower on Par 5s, consider using a more accurate club off the tee even if it means sacrificing distance.',
        // GIR Dialog
        girDialogTitle: 'GIR by Hole Type',
        girDialogExplanation: 'This shows how often you hit greens in regulation across all hole types. Each requires different skills - short irons on Par 3s, mid-irons on Par 4s, long approaches on Par 5s.',
        girPar3Label: 'Par 3s',
        girPar3Desc: 'Tests your accuracy with short to mid-iron tee shots. No fairway advantage - straight from tee to green.',
        girPar4Label: 'Par 4s',
        girPar4Desc: 'Most common hole type. Success depends on both driving accuracy and approach shot precision.',
        girPar5Label: 'Par 5s',
        girPar5Desc: 'Often easier GIR percentages since you can lay up to your favorite yardage for the third shot.',
        girProTip: 'Par 5s typically have higher GIR% because you can manage distance better with three shots. Focus on Par 3 accuracy for quick scoring improvements.'
      },
      // GIR Performance Distribution
      girPerformanceDistribution: {
        noDataAvailable: 'No GIR data available for distribution analysis.',
        greenDistribution: 'Green Distribution (GIR)',
        avgGir: 'avg GIR',
        avgGirPercent: 'AVG. GIR %',
        holes: 'holes',
        hit: 'Hit',
        missed: 'Missed',
        byTeeAccuracy: 'BY TEE ACCURACY (PAR 4 & 5)',
        byTeeAccuracyLower: 'By Tee Accuracy (Par 4 & 5)',
        // Dialog
        dialogTitle: 'Greens in Regulation (GIR)',
        dialogExplanation: 'GIR measures how often you reach the green in the expected number of strokes (par minus 2). This chart shows your hit rate versus misses.',
        hitLabel: 'Hit',
        hitDesc: 'Approach shots that found the putting surface in regulation, setting up birdie putts.',
        missedLabel: 'Missed',
        missedDesc: 'Shots that missed the green, requiring a chip or pitch to recover. The key to improving GIR is better distance control.',
        proTip: 'GIR is strongly correlated with scoring. Each additional GIR typically saves 0.5-1 stroke per round. Focus on hitting more greens rather than getting closer to pins.'
      },
      // Tee Accuracy Breakdown
      teeAccuracyBreakdown: {
        noData: 'No data',
        fromFairway: 'From Fairway',
        offFairway: 'Off Fairway',
        peers: 'Peers',
        target: 'Target'
      }
    },

    // Accuracy Analysis specific keys
    accuracyAnalysis: {
      // Navigation & Header (3 keys)
      navigation: {
        backToDashboard: 'Back to Dashboard',
        title: 'Accuracy Analysis',
        subtitle: 'Track your overall accuracy'
      },
      
      // Main Labels
      overallAccuracy: 'Overall Accuracy',
      compositeMetric: 'Composite Metric',
      
      // Section Headers
      roundBreakdown: 'Round Breakdown',
      accuracyDistributions: 'Accuracy Distributions'
    },

    // Putting Analysis specific keys
    puttingAnalysis: {
      // Navigation & Header (3 keys)
      navigation: {
        breadcrumb: 'Analysis / Putting',
        title: 'Putting Analysis',
        subtitle: 'Track your putting performance'
      },

      // Metrics (2 keys)
      metrics: {
        puttsPerRound: 'Putts per Round',
        description: 'Average number of putts per round'
      },

      // Chart labels and tooltips (17 keys)
      chart: {
        puttingTrend: 'Putting Trend',
        puttingDistribution: 'Putting Distribution',
        performanceByDistance: 'Performance by Distance',
        girVsNonGir: 'GIR vs Non-GIR',
        lastRounds: 'Last {count} rounds',
        avgPutts: 'avg putts',
        holes: 'holes',
        rounds: 'rounds',
        roundBreakdown: 'Round Breakdown',
        puttingProportion: 'Putting Proportion',
        recentRounds: 'Recent Rounds',
        avg: 'Avg',
        peerAvg: 'Peer Avg',
        target: 'Target',
        selectedRound: 'Selected Round',
        peerAverage: 'Peer average',
        handicapTarget: 'Handicap target'
      },

      // Putting Proportion explanation dialog (5 keys)
      puttingProportionExplanation: {
        title: 'Putting Proportion',
        explanation: 'This chart shows the distribution of your putts across different categories. Understanding your putt distribution helps identify where strokes are being lost.',
        onePuttDesc: 'The goal! More 1-putts directly lower your scores. Focus on lag putting to set up more makeable second putts.',
        twoPuttDesc: 'The standard result for most greens. A high 2-putt percentage indicates solid distance control.',
        threePuttDesc: 'Each 3-putt costs you a stroke. Reducing these has the biggest impact on your putting average.',
        fourPlusPuttDesc: 'Rare but costly. Usually indicates a major misread or severe distance control issues.',
        proTip: 'Aim to minimize 3-putts first - they\'re more common than you think and easier to fix than making more 1-putts. Work on lag putting from 30+ feet.'
      },

      // Data labels for tooltips (12 keys)
      dataLabels: {
        hole: 'Hole {hole} (Par {par})',
        round: 'Round {round}',
        putts: '{putts} putts',
        handicapTarget: 'Handicap target: {target}',
        peerAvg: 'Peer avg: {avg}',
        onePutt: '1-Putt:',
        twoPutt: '2-Putt:',
        threePutt: '3-Putt:',
        fourPlusPutt: '4+ Putt:',
        total: 'Total: {total} rounds',
        noData: 'No Data',
        sample: 'Sample: {count} rounds'
      },

      // Insights and analysis (12 keys)
      insights: {
        strongVsPeers: 'Strong vs Peers',
        strongVsPeersMessage: 'You\'re putting {gap} strokes better than similar app users ({handicap}-handicap)',
        abovePeerAverage: 'Above Peer Average',
        abovePeerAverageMessage: '{gap} strokes above peers - focus on consistency and distance control',
        roomForImprovement: 'Room for Improvement',
        roomForImprovementMessage: '{gap} strokes above official {handicap}-handicap standard - focus on lag putting',
        belowOfficialStandard: 'Below Official Standard',
        belowOfficialStandardMessage: '{gap} strokes better than official standard - excellent putting performance',
        trendingBetter: 'Trending Better',
        trendingBetterMessage: 'Your putting has improved {trend} strokes over recent rounds',
        recentStruggles: 'Recent Struggles',
        recentStrugglesMessage: 'Putting has increased {trend} strokes recently - check fundamentals'
      },

      // Improvement Messages (3 keys)
      improvement: {
        belowPeerAverage: 'Below Peer Average',
        belowPeersMessage: '{gap}% below peers - focus on accuracy over distance',
        roomForImprovement: 'Room for improvement: {performance}% FIR is below your {target}% target. Focus on accuracy over distance.'
      },

      // Tips (5 keys)
      tips: {
        practiceLagPutting: 'Practice lag putting for distance control',
        workOnShortPutts: 'Work on short putts for confidence',
        readGreensCarefully: 'Read greens carefully',
        focusOnConsistentTempo: 'Focus on consistent tempo',
        practiceDifferentGreenSpeeds: 'Practice different green speeds'
      },

      // Sections (4 keys)
      sections: {
        overview: 'Overview',
        performanceInsights: 'Performance Insights',
        girVsNonGir: 'GIR vs Non-GIR',
        performanceByDistance: 'Performance by Distance'
      },

      // Empty states (5 keys)
      emptyStates: {
        noPuttingData: 'No Putting Data Available',
        noPuttingDataMessage: 'Complete more rounds to see your putting analysis.',
        noDistributionData: 'No putting data available for distribution analysis.',
        noGirData: 'No GIR putting data available for analysis.',
        noDistanceData: 'Complete more rounds to see distance-based putting analysis',
        playMoreForProportion: 'Play more rounds to unlock putting proportion.'
      },

      // Problem holes analysis (6 keys)
      problemHoles: {
        focusOnHoles: 'Focus on holes needing attention vs strong performance',
        highThreePuttRisk: 'High 3-Putt Risk',
        strongOnePuttHoles: 'Strong 1-Putt Holes',
        playMoreRounds: 'Play more rounds to see problem holes analysis',
        playMoreRoundsDistribution: 'Play more rounds to see putting distribution per hole',
        target: 'Target: <{target}%'
      },

      // GIR Analysis (8 keys)
      girAnalysis: {
        girPutting: 'GIR Putting',
        nonGir: 'Non-GIR',
        keyInsight: 'Key Insight:',
        keyInsightMessage: '{difference} putts difference {type}',
        moreOnGir: '(more on GIR)',
        moreOnNonGir: '(more on non-GIR)',
        whatIsThis: 'What is this?',
        explanation: 'Putting by GIR gives you much more meaningful putting insights than just overall averages. Here\'s why this matters:',
        girPuttingDesc: 'Shows your performance on greens you hit in regulation. These are typically longer, more challenging putts where 2-putts are expected.',
        nonGirPuttingDesc: 'Shows your performance after missing greens. These are usually shorter putts after chips or pitches where 1-putts are more achievable.',
        keyInsightDesc: 'This helps you identify whether putting struggles are due to poor approach shots (forcing longer putts) or actual putting technique issues.',
        proTip: 'If your non-GIR putting is much worse than GIR putting, focus on your short game around the greens. If both are similar, work on your putting technique.'
      },

      // Distance performance (6 keys)
      distancePerformance: {
        estimated: 'Estimated',
        keyOpportunities: 'Key Opportunities:',
        focusOn: 'Focus on {range} putting - you\'re {gap}% below target.',
        excellent: 'Excellent {range} putting - {gap}% above target!',
        consistentPerformance: 'Consistent performance across all distance ranges.',
        note: 'Distance calculations are estimated from putting patterns. For precise distance tracking, record actual putt distances during rounds.'
      },

      // Chart legends and labels (8 keys)
      chartLabels: {
        handicap: 'Handicap ({value})',
        peer: 'Peer ({value})',
        onePutt: '1-Putt',
        twoPutt: '2-Putt', 
        threePutt: '3-Putt',
        fourPlusPutt: '4+ Putt',
        gir: 'GIR',
        nonGir: 'Non-GIR'
      }
    },

    // Recovery Analysis specific keys (formerly scrambling)
    scramblingAnalysis: {
      // Navigation & Header (3 keys)
      navigation: {
        breadcrumb: 'Analysis / Scrambling',
        title: 'Scrambling Analysis',
        subtitle: 'Track how effectively you convert missed greens into saves'
      },

      // Metrics (2 keys)
      metrics: {
        scramblingPercentage: 'Scrambling %',
        description: 'Percentage of up-and-down saves when missing greens in regulation'
      },

      // Chart labels and tooltips (15 keys)
      chart: {
        scramblingTrend: 'Scrambling trend',
        saveTypeDistribution: 'Scrambling Outcomes',
        performanceByDistance: 'Scrambling Performance by Distance',
        scramblingDistributionPerHole: 'Scrambling Distribution Per Hole',
        lastRounds: 'Last {count} rounds',
        totalSaves: 'total scrambling chances',
        holes: 'Holes',
        rounds: 'rounds',
        recentRounds: 'Recent Rounds',
        selectedRound: 'Selected Round',
        avg: 'Avg',
        peer: 'Peer',
        target: 'Target',
        recentRound: 'Recent round',
        roundsCount: '{count} rounds'
      },

      // Data labels for tooltips (18 keys)
      dataLabels: {
        hole: 'Hole {hole} (Par {par})',
        round: 'Round {round}',
        upAndDown: 'Up & Down:',
        missed: 'Missed:',
        totalGirMisses: 'Total GIR misses: {total}',
        saves: 'Saves',
        noData: 'No Data',
        sample: 'Sample: {count} rounds',
        target: 'Target: {target}%',
        attempts: 'ATTEMPTS',
        success: 'SUCCESS',
        missedGirs: 'Missed GIRs',
        bunkerShots: 'Bunker Shots',
        successes: 'Successes',
        opportunities: 'Opportunities',
        scrambling: 'SCRAMBLING',
        sandSave: 'SAND SAVE',
        upAndDownLabel: 'UP & DOWN'
      },

      // Insights and analysis (12 keys)
      insights: {
        strongVsPeers: 'Strong vs Peers',
        strongVsPeersMessage: 'You\'re {gap}% better at recovery than similar app users ({handicap}-handicap)',
        abovePeerAverage: 'Above Peer Average',
        abovePeerAverageMessage: '{gap}% above peers - keep sharpening your short game fundamentals',
        roomForImprovement: 'Room for Improvement',
        roomForImprovementMessage: '{gap}% above official {handicap}-handicap standard - dial in chipping accuracy',
        belowOfficialStandard: 'Below Official Standard',
        belowOfficialStandardMessage: '{gap}% better than official standard - excellent scrambling performance',
        trendingBetter: 'Trending Better',
        trendingBetterMessage: 'Your scrambling rate has improved {trend}% over recent rounds',
        recentStruggles: 'Recent Struggles',
        recentStrugglesMessage: 'Scrambling rate has declined {trend}% recently - revisit short game fundamentals'
      },

      // Improvement Messages (3 keys)
      improvement: {
        belowPeerAverage: 'Below Peer Average',
        belowPeersMessage: '{gap}% below peers - focus on short game consistency',
        roomForImprovement: 'Room for improvement: {performance}% scrambling is below your {target}% target. Focus on chipping accuracy.'
      },

      // Tips (5 keys)
      tips: {
        practiceChipping: 'Practice chipping from various lies',
        workOnDistanceControl: 'Work on distance control around greens',
        focusOnLagPutting: 'Focus on lag putting after chips',
        practiceBunkerShots: 'Practice bunker shots regularly',
        developConsistentRoutine: 'Develop consistent short game routine'
      },

      // Sections (5 keys)
      sections: {
        overview: 'Overview',
        performanceInsights: 'Performance Insights',
        saveTypeDistribution: 'Scrambling Outcomes',
        performanceByDistance: 'Performance by Distance',
        shortGameBreakdown: 'Short Game Breakdown'
      },

      // Empty states (6 keys)
      emptyStates: {
        noScramblingData: 'No Scrambling Data Available',
        noScramblingDataMessage: 'Complete more rounds to see your scrambling analysis.',
        noDistributionData: 'No scrambling data available for distribution analysis.',
        noDistanceData: 'Complete more rounds to see distance-based scrambling analysis',
        loading: 'Loading scrambling analysis...',
        error: 'Error loading scrambling analysis data'
      },

      // Problem holes analysis (6 keys)
      problemHoles: {
        focusOnHoles: 'Identify scrambling weaknesses vs strengths when missing greens',
        lowScramblingHoles: 'Low Scrambling Holes',
        highScramblingHoles: 'High Scrambling Holes',
        playMoreRounds: 'Play more rounds to see problem holes analysis',
        playMoreRoundsDistribution: 'Play more rounds to see scrambling distribution per hole',
        saves: '{saves}/{total} saves'
      },

      // Distance performance (6 keys)
      distancePerformance: {
        estimated: 'Estimated',
        keyOpportunities: 'Key Opportunities:',
        focusOn: 'Focus on {range} scrambling - you\'re {gap}% below target.',
        excellent: 'Excellent {range} scrambling - {gap}% above target!',
        consistentPerformance: 'Consistent performance across all distance ranges.',
        note: 'Distance calculations are estimated from round patterns. For precise distance tracking, record actual distances during rounds.'
      },

      // Chart legends and labels (11 keys)
      chartLabels: {
        handicap: 'Handicap ({value}%)',
        peer: 'Peer ({value}%)',
        upAndDownSave: 'Up & Down Save',
        missedSave: 'Missed Save',
        parSaves: 'Par Saves',
        bogeySaves: 'Bogey',
        doubleBogeyPlus: 'Double Bogey+',
        missedGreens: 'Missed greens',
        scrambling: 'Scrambling',
        upAndDown: 'Up & Down',
        sandSaves: 'Sand Saves'
      },

      // Scrambling Outcomes explanation dialog
      recoveryOutcomesExplanation: {
        title: 'Scrambling Outcomes',
        explanation: 'This chart shows your score breakdown on holes where you missed the green in regulation. Understanding your scrambling patterns helps identify short game priorities.',
        parSavesDesc: 'When you miss the green but still make par. This is the definition of scrambling - the higher this percentage, the better your short game.',
        bogeySavesDesc: 'One-over par after missing the green. Acceptable outcome that limits damage.',
        doubleBogeyPlusDesc: 'Two or more over par after missing the green. These blow-up holes hurt your score the most.',
        proTip: 'Focus on eliminating double bogeys first - getting up and down for bogey is often smarter than a risky flop shot attempting par.'
      },

      // Short Game Overview Card
      shortGameOverview: {
        title: 'Short Game Overview',
        explanation: 'Compares your key short game metrics side-by-side to show your overall scrambling performance around the greens.',
        scramblingLabel: 'Scrambling',
        scramblingDesc: 'Making par or better after missing the green in regulation. Calculated from your actual scores - the PGA Tour standard metric.',
        upAndDownLabel: 'Up & Down',
        upAndDownDesc: 'Getting the ball in the hole in 2 shots from off the green (chip + putt). Tracked when you mark a successful up-and-down on the scorecard.',
        sandSavesLabel: 'Sand Saves',
        sandSavesDesc: 'Making par or better from a bunker. Bunker play is a specialized skill that can save or destroy a round.',
        proTip: 'If your Scrambling is higher than Up & Down, you\'re making long par putts after chips that aren\'t getting close. Focus on chip distance control!',
        peers: 'Peers',
        target: 'Target',
        noData: 'No short game data available. Play more rounds with stats tracking.'
      },

      // Scrambling Analysis Distribution
      recoveryAnalysisDistribution: {
        title: 'Scrambling Analysis',
        savesPercent: 'Saves %',
        putting: 'Putting',
        recoverySaveRate: 'Scrambling Save Rate',
        recoverySaveRateByLieType: 'Scrambling Save Rate by Lie Type',
        recoverySaveRateExplanation: 'Shows your scrambling success rate from different trouble situations. Higher percentages mean better scrambling skills from that lie.',
        waterHazard: 'Water Hazard',
        waterHazardDesc: 'Scrambling after taking a penalty drop from water. Success here depends on smart drop decisions and solid follow-up shots.',
        fairwayBunker: 'Fairway Bunker',
        fairwayBunkerDesc: 'Longer bunker shots where you need to advance the ball significant distance. Clean contact is critical here.',
        greensideBunker: 'Greenside Bunker',
        greensideBunkerDesc: 'Short bunker shots around the green. The most common bunker situation and often the most practiced.',
        recoveryProTip: 'Your lowest percentage shows where to focus practice. Most amateurs struggle with fairway bunkers because they require both distance and accuracy.',
        saves: 'saves',
        save: 'save',
        puttingAfterRecovery: 'Putting After Scrambling',
        puttingAfterRecoveryShots: 'Putting After Scrambling Shots',
        puttingAfterRecoveryExplanation: 'Shows how many putts you typically need to finish the hole after scrambling from trouble. Fewer putts indicates better chip/pitch proximity.',
        averagePuttsNeeded: 'Average putts needed when saving par from each lie type',
        putts: 'putts',
        lowAverage: 'Low Average (1.0-1.5)',
        lowAverageDesc: 'Excellent scrambling proximity - you\'re getting up and down efficiently with one-putt opportunities.',
        mediumAverage: 'Medium Average (1.5-2.0)',
        mediumAverageDesc: 'Solid scrambling work but leaving longer par putts. Focus on landing chips closer to the hole.',
        highAverage: 'High Average (2.0+)',
        highAverageDesc: 'Scrambling shots are leaving difficult putts. Work on distance control in your approach game.',
        puttingProTip: 'If your putt average is high from a specific lie, focus on getting your scrambling shot closer to the hole rather than just onto the green.',
        noRecoveryData: 'No scrambling data available. Play more rounds with shot tracking enabled.'
      }
    },

    // Club Analysis specific keys
    clubAnalysis: {
      // Navigation & Header (3 keys)
      navigation: {
        breadcrumb: 'Analysis / Club',
        title: 'Club Analysis',
        subtitle: 'Track your club performance and consistency'
      },

      // Metrics (1 key)
      metrics: {
        clubConsistency: 'Driving'
      },

      // Chart labels and tooltips
      chart: {
        clubDistanceChart: 'Club Distance Chart',
        distancePerformance: 'Distance Performance',
        averageDistance: 'Distance',
        meters: 'meters',
        metersUnit: '{value} meters',
        distanceMetersLabel: 'Distance (meters)',
        lastRounds: 'Last {count} rounds',
        totalClubs: 'total clubs',
        holes: 'Holes',
        rounds: 'rounds'
      },

      // Data labels for tooltips (16 keys)
      dataLabels: {
        hole: 'Hole {hole} (Par {par})',
        round: 'Round {round}',
        consistency: 'Consistency:',
        accuracy: 'Accuracy:',
        total: 'Total: {total} shots',
        noData: 'No Data',
        sample: 'Sample: {count} rounds',
        target: 'Target: {target}%',
        attempts: 'ATTEMPTS',
        success: 'SUCCESS',
        shots: 'shots',
        meters: 'meters',
        clubsTracked: 'Clubs Tracked',
        drivesTracked: 'Drives Tracked',
        medianDistance: 'Median Distance',
        shotConditions: 'Shot Conditions',
        mVsPeers: '{value}m vs peers',
        pctVsPeers: '{value}% vs peers',
        noPar4Data: 'No Par 4 data yet',
        noPar5Data: 'No Par 5 data yet'
      },

      // Insights and analysis
      insights: {
        yourGoToClub: 'Your Go-To Club',
        yourGoToClubMessage: '{club} is your most consistent club',
        distanceGapFound: 'Distance Gap Found',
        distanceGapFoundMessage: '{gap}m gap between {fromClub} and {toClub}',
        inconsistentClub: 'Inconsistent Club',
        inconsistentClubMessage: '{club} shows {consistency}% consistency',
        strongVsPeers: 'Strong vs Peers',
        strongVsPeersMessage: 'You\'re {gap}% more consistent than similar app users ({handicap}-handicap)',
        abovePeerAverage: 'Above Peer Average',
        abovePeerAverageMessage: '{gap}% above peers - focus on club consistency',
        roomForImprovement: 'Room for Improvement',
        roomForImprovementMessage: '{gap}% above official {handicap}-handicap standard - work on club selection',
        excellentConsistency: 'Excellent consistency across your bag',
        goodConsistency: 'Good consistency across your bag',
        developingConsistency: 'Developing consistency across your bag',
        gettingStarted: 'Getting Started',
        gettingStartedMessage: 'Play more rounds to see personalized recommendations',
        playMoreForStrengths: 'Play more rounds to see your strengths',
        playMoreForImprovements: 'Play more rounds to see improvement areas',
        excellentDriverConsistency: 'Excellent consistency - very reliable!',
        goodDriverConsistency: 'Good consistency with room for improvement',
        needsDriverImprovement: 'Needs improvement - focus on consistency'
      },

      // UI Labels and Headers (20 keys)
      uiLabels: {
        keyInsights: 'Key Insights',
        topPerformanceHighlights: 'Top performance highlights',
        performanceSummary: 'Performance Summary',
        averageConsistency: 'Average Consistency',
        yourGrade: 'Your Grade',
        whatToFocusOn: 'What to Focus On',
        practicePriorities: 'Practice priorities',
        playingStyle: 'Playing Style',
        basedOnYourData: 'Based on your data',
        strategyTip: 'Strategy Tip',
        distanceBenchmarks: 'Distance Benchmarks',
        yourPosition: 'Your Position',
        shortest: 'Shortest',
        longest: 'Longest',
        averageDistance: 'Distance',
        coachTip: 'Coach Tip',
        practicePlan: 'Practice Plan',
        useReliableClubs: 'Use these reliable clubs more often in pressure situations and when you need to find the green.',
        spendExtraTime: 'Spend extra time at the range with these clubs. Focus on tempo and setup consistency.',
        useInScoring: 'Use it more in scoring situations'
      },

      // Performance Messages (8 keys)
      performanceMessages: {
        veryReliableClubs: 'You have very reliable clubs!',
        solidPerformance: 'Solid performance with room to grow in long irons and driver consistency.',
        focusOnConsistency: 'Focus on consistency practice.',
        workOnFundamentals: 'Work on fundamentals and club familiarity.',
        excellentWork: 'Excellent work!',
        keepWorking: 'Keep working on your distance and consistency!',
        considerAddingClub: 'Consider adding a club to fill this distance gap.',
        practiceMoreOrReplace: 'Practice more or consider replacing'
      },

      // Playing Style Types (6 keys)
      playingStyleTypes: {
        precisionPlayer: 'Precision Player',
        precisionDescription: 'Accuracy over power',
        precisionTip: 'Consider taking a bit off for more control, especially on tight holes.',
        powerPlayer: 'Power Player',
        powerDescription: 'Distance with room for accuracy',
        developingPlayer: 'Developing Player',
        developingDescription: 'Building fundamentals',
        developingTip: 'Work with a pro on setup and swing basics. Distance will come with better technique.'
      },

      // Handicap Levels (4 keys)
      handicapLevels: {
        tourPro: 'Tour Pro',
        scratchGolfer: 'Scratch Golfer',
        singleDigit: 'Single Digit',
        midHandicap: 'Mid Handicap'
      },

      // Improvement Messages (3 keys)
      improvement: {
        belowPeerAverage: 'Below Peer Average',
        belowPeersMessage: '{gap}% below peers - focus on club consistency',
        roomForImprovement: 'Room for improvement: {performance}% consistency is below your {target}% target. Focus on club selection.'
      },

      // Tips (5 keys)
      tips: {
        practiceWithInconsistentClubs: 'Practice with inconsistent clubs',
        workOnTempo: 'Work on tempo and setup consistency',
        considerClubReplacement: 'Consider club replacement for very inconsistent clubs',
        focusOnReliableClubs: 'Focus on using reliable clubs in pressure situations',
        trackClubDistances: 'Track club distances regularly'
      },

            // Sections (14 keys)
      sections: {
        overview: 'Overview',
        analysis: 'Club Analysis',
        driving: 'Driving',
        performanceInsights: 'Performance Insights',
        clubPerformance: 'Club Performance',
        problemHolesAnalysis: 'Problem Holes Analysis',
        clubDistanceChart: 'Club Distance Chart',
        clubGapsAnalysis: 'Club Gaps Analysis',
        overallPerformance: 'Overall Performance',
        yourStrengths: 'Your Strengths',
        topPerformingClubs: 'Top performing clubs',
        areasToImprove: 'Areas to Improve',
        clubsNeedingAttention: 'Clubs needing attention',
        driverPerformance: 'Driver Performance',
        consistencyAnalysis: 'Consistency Analysis',
        drivingAccuracy: 'Driving Accuracy',
        accuracyAnalysis: 'Accuracy Analysis',
        distanceByHoleType: 'Distance by Hole Type',
        accuracyByHoleType: 'Accuracy by Hole Type'
      },

      // Empty states (9 keys)
      emptyStates: {
        noClubData: 'No Club Data Available',
        noClubDataMessage: 'Complete more rounds to see your club performance analysis.',
        noDistanceData: 'Play more rounds to see club distance analysis',
        noGapData: 'Not enough data to analyze club gaps',
        noDetailedStats: 'Play more rounds to see detailed club statistics',
        noDriverData: 'Hit more drives to see your analysis',
        needMoreDrives: 'Need at least 3 tracked tee shots',
        needMoreRounds: 'Complete more rounds with shot tracking to see club analysis',
        noDrivingData: 'Not enough data to analyze driving performance',
        noClubInsights: 'Not enough data to provide club insights'
      },

      // Problem holes analysis
      problemHoles: {
        focusOnClubs: 'Identify clubs needing attention vs reliable performers',
        leastConsistent: 'Least Consistent',
        mostConsistent: 'Most Consistent',
        leastAccurate: 'Least Accurate',
        mostAccurate: 'Most Accurate',
        playMoreRounds: 'Play more rounds to see club performance analysis',
        mostReliable: 'Most Reliable',
        clubs: 'Clubs'
      },

      // Club Gaps Analysis
      clubGaps: {
        identifyGaps: 'Identify distance gaps and overlaps in your club setup',
        wellGappedSetup: 'Well-Gapped Setup',
        wellGappedMessage: 'Your clubs have good distance spacing',
        distanceGaps: 'Distance Gaps',
        distanceGapsDesc: 'Clubs with large distance gaps',
        distanceOverlaps: 'Distance Overlaps',
        distanceOverlapsDesc: 'Clubs with similar distances',
        overlaps: 'Overlaps',
        avgGapSize: 'Avg Gap Size',
        gapRecommendation: 'Gap Recommendation',
        clubGappingTips: 'Club Gapping Tips',
        idealGap: 'Ideal gap between consecutive clubs: 9-14 meters',
        ydGap: '{gap}yd gap',
        ydOverlap: '{gap}yd overlap',
        idealActualGap: 'Ideal gap: {ideal}yd • Actual: {actual}yd',
        overlapConfusion: 'Similar distance ranges may cause club selection confusion',
        severity: {
          critical: 'Critical',
          high: 'High',
          medium: 'Medium',
          low: 'Low'
        },
        tips: {
          tip1: 'Ideal gap between consecutive clubs: 9-14 meters',
          tip2: 'Consider adding hybrid clubs to fill large gaps',
          tip3: 'Remove overlapping clubs to simplify decision-making',
          tip4: 'Test clubs at different swing speeds to find optimal gapping'
        }
      },

      // Distance performance (6 keys)
      distancePerformance: {
        estimated: 'Estimated',
        keyOpportunities: 'Key Opportunities:',
        focusOn: 'Focus on {range} consistency - you\'re {gap}% below target.',
        excellent: 'Excellent {range} consistency - {gap}% above target!',
        consistentPerformance: 'Consistent performance across all distance ranges.',
        note: 'Distance calculations are estimated from round patterns. For precise distance tracking, record actual distances during rounds.'
      },

      // Chart legends and labels (9 keys)
      chartLabels: {
        handicap: 'Handicap ({value}%)',
        peer: 'Peer ({value}%)',
        averageDistance: 'Distance',
        consistency: 'Consistency',
        consistencyLabel: 'CONSISTENCY',
        avg: 'AVG',
        accuracy: 'Accuracy',
        overall: 'Overall',
        shortRange: 'Short Range',
        longRange: 'Long Range',
        accuracyDecreasesWithDistance: '⚠️ Accuracy decreases with distance - consider shorter swings'
      },

      // Driving Analysis
      driving: {
        strategicDriving: 'Strategic driving analysis and playing style insights',
        par4VsPar5: 'Par 4 vs Par 5 Performance',
        par4Performance: 'Par 4 Performance',
        par5Performance: 'Par 5 Performance',
        averageScore: 'Average Score',
        fairways: 'Fairways',
        holesTracked: 'holes tracked',
        notEnoughData: 'Not Enough Data',
        needMoreHoles: 'Need 5+ Par {par} holes ({count} tracked)',
        playingStyle: 'Playing Style',
        aggressive: 'Aggressive',
        conservative: 'Conservative',
        strokesBetter: '{count} strokes better on Par {par}s',
        aggressivePlayer: 'Aggressive Player',
        aggressivePlayerDesc: 'You score better on Par 5s, indicating you take risks for birdie opportunities. Consider this strategy on longer holes with wider fairways.',
        precisionPlayer: 'Precision Player',
        precisionPlayerDesc: 'You score better on Par 4s, suggesting precision over power. Consider playing more conservatively on Par 5s to avoid big numbers.',
        aggressiveEmoji: '🎯 Aggressive',
        conservativeEmoji: '🛡️ Conservative',
        // Driver Accuracy Chart keys
        driverAccuracy: 'Driver Accuracy',
        driverAccuracySubtitle: 'Track your fairway hit rate when using driver',
        recentRounds: 'Recent Rounds',
        peerAverage: 'Peer average',
        peerAvg: 'Peer Avg',
        targetAccuracy: 'Target accuracy',
        selectedRound: 'Selected Round',
        driverFir: 'DRIVER FIR',
        driverShots: 'Driver Shots',
        fairwaysHit: 'Fairways Hit',
        playMoreWithDriver: 'Play more rounds with driver tracking to see your accuracy trends',
        // Distance mode keys
        peerDistance: 'Peer distance',
        targetDistance: 'Target distance',
        avgDistance: 'AVG DISTANCE',
        avgLabel: 'AVG',
        peerLabel: 'PEER',
        targetLabel: 'TARGET',
        max: 'MAX',
        min: 'MIN',
        drives: 'DRIVES',
        // DriverDistanceBoxPlot keys
        noDriverDistanceData: 'No driver distance data available',
        playMoreForDistribution: 'Play more rounds with driver tracking to see distribution',
        recent: 'Recent',
        q3: 'Q3',
        q1: 'Q1',
        median: 'Median',
        avg: 'Avg',
        needMoreDrives: 'Need 3+ drives for box plot',
        q1q3Range: 'Q1-Q3 Range',
        yourAvg: 'Your Avg',
        driver: 'Driver',
        shots: 'shots',
        consistency: 'Consistency'
      },

      // Performance by Distance (8 keys)
      performanceByDistance: {
        performanceByHoleDistance: 'Performance by Hole Distance',
        meters: 'meters',
        averageScore: 'Average Score',
        notEnoughData: 'Not Enough Data',
        playMoreRounds: 'Play rounds on courses with varied hole lengths to see distance performance analysis.',
        needMoreHoles: 'Need 3+ holes per distance range.',
        courseArchitectureInsight: 'Course Architecture Insight',
        courseArchitectureMessage: 'Golf course architects typically provide wider fairways for longer holes. If you struggle on long holes despite wider targets, consider distance training.'
      },

      // Driving Impact (12 keys)
      drivingImpact: {
        drivingImpactOnRound: 'Driving Impact on Round Performance',
        correlation: 'Correlation',
        strength: 'Strength',
        impact: 'Impact',
        highDrivingImpact: 'High Driving Impact',
        highDrivingImpactMessage: 'Your driving performance strongly correlates with round scores. Focus on driving accuracy and distance for maximum score improvement.',
        moderateDrivingImpact: 'Moderate Driving Impact',
        moderateDrivingImpactMessage: 'Driving affects your scores but isn\'t the only factor. Balance driving practice with short game improvement.',
        shortGameHero: 'Short Game Hero',
        shortGameHeroMessage: 'Your round scores don\'t depend heavily on driving performance. You\'re likely strong with your short game and recovery shots.',
        notEnoughData: 'Not Enough Data for Correlation Analysis',
        needMoreRounds: 'Need 3+ complete rounds ({count} tracked) to analyze the correlation between driving performance and round scores.'
      },

      // Club Insights (15 keys)
      clubInsights: {
        personalizedInsights: 'Personalized insights and recommendations for your club performance',
        mostConsistentClub: 'Most Consistent Club',
        mostConsistentMessage: 'Your {club} shows excellent consistency. Consider using it more often in scoring situations.',
        distanceGapsDetected: 'Distance Gaps Detected',
        distanceGapsMessage: '{count} distance gap{plural} found in your bag. Consider adjusting your club selection or adding clubs to optimize distance coverage.',
        focusOnConsistency: 'Focus on Consistency',
        focusOnConsistencyMessage: 'Your overall club consistency is {consistency}%. Work on tempo and setup consistency to improve distance control.',
        excellentConsistency: 'Excellent Consistency',
        excellentConsistencyMessage: 'Your {consistency}% club consistency is excellent. Focus on course management and strategy to further improve your scoring.',
        inconsistentClubAlert: 'Inconsistent Club Alert',
        inconsistentClubMessage: 'Your {club} shows {consistency}% consistency. Consider practicing with this club or replacing it with a more reliable option.',
        shortGameSpecialist: 'Short Game Specialist',
        shortGameSpecialistMessage: 'Your short clubs are {gap}% more consistent than your long clubs. Focus on getting your approach shots closer to the pin where you can excel.',
        powerPlayer: 'Power Player',
        powerPlayerMessage: 'Your long clubs are {gap}% more consistent than your short clubs. Focus on improving your short game to complement your driving strength.',
        wellBalancedSetup: 'Well-Balanced Club Setup',
        wellBalancedMessage: 'Your club setup shows good consistency ({consistency}%) and no significant gaps. Focus on course management and strategic play to maximize your scoring potential.',
        proTip: 'Pro Tip',
        proTipMessage: 'Track your club distances regularly and adjust your expectations based on course conditions. Wind, elevation, and pin position can significantly affect your club selection decisions.'
      },

      // Playing Style Analysis
      playingStyle: {
        powerAndPrecision: 'Power & Precision',
        powerAndPrecisionDescription: 'Long and straight - the complete package',
        powerAndPrecisionTip: 'Maintain your fundamentals and consider playing more aggressively on par 5s.',
        precisionPlayer: 'Precision Player',
        precisionPlayerDescription: 'Accuracy over power',
        precisionPlayerTip: 'Your accuracy is an asset - use it to your advantage with strategic play and course management.',
        powerPlayer: 'Power Player',
        powerPlayerDescription: 'Distance with room for accuracy',
        powerPlayerTip: 'Consider taking a bit off for more control, especially on tight holes.',
        developingPlayer: 'Developing Player',
        developingPlayerDescription: 'Building fundamentals',
        developingPlayerTip: 'Work with a pro on setup and swing basics. Distance will come with better technique.',
        insufficientData: 'Insufficient Data for Playing Style Analysis',
        insufficientDataMessage: 'Play more rounds with both Par 4s and Par 5s to determine your playing style preference.'
      },

      // Distance Benchmarks (8 keys)
      distanceBenchmarks: {
        distanceBenchmarks: 'Distance Benchmarks',
        tourPro: 'Tour Pro',
        scratch: 'Scratch',
        singleDigit: 'You (Single Digit)',
        midHandicap: 'Mid Handicap',
        yourLevel: 'Your level',
        eliteLevel: 'Elite level',
        expertLevel: 'Expert level',
        averageLevel: 'Average level',
        yourPosition: 'Your Position',
        yourPositionMessage: 'At {distance}m average, you\'re performing at {level} level. {message}'
      },

      // MetricExplanationDialog keys
      dialogs: {
        // Club Consistency Analysis
        clubConsistency: {
          title: 'Club Consistency Analysis',
          explanation: 'Compares your most and least consistent clubs based on how close shots land to your average distance. Identify clubs that need practice.',
          leastConsistent: 'Least Consistent',
          leastConsistentDesc: 'Shows the greatest distance variation. Focus on tempo and grip pressure.',
          mostConsistent: 'Most Consistent',
          mostConsistentDesc: 'Smallest distance spread - your most reliable club.',
          shotCount: 'Shot Count',
          shotCountDesc: 'More shots = more reliable data. 10+ shots recommended.',
          proTip: 'Your least consistent club might need a lesson, practice, or even replacement. Consider if it\'s worth the strokes it\'s costing you.'
        },
        // Club Accuracy Analysis
        clubAccuracy: {
          title: 'Club Accuracy Analysis',
          explanation: 'Compares your most and least accurate clubs based on how often shots finish on target. Accuracy issues may indicate swing or club problems.',
          leastAccurate: 'Least Accurate',
          leastAccurateDesc: 'Lowest on-target percentage. May need setup or alignment work.',
          mostAccurate: 'Most Accurate',
          mostAccurateDesc: 'Highest on-target rate - use in pressure situations.',
          accuracy: 'Accuracy %',
          accuracyDesc: 'Percentage of shots finishing on target. 70%+ is solid.',
          proTip: 'If a club is both inconsistent AND inaccurate, it might not be suited to your swing. Consider a fitting session or club replacement.'
        },
        // Distance Distribution
        distanceDistribution: {
          title: 'Distance Distribution',
          explanation: 'Shows the spread of your driver distances for each round using box plots. This helps you understand consistency - a narrow box means consistent distances, a wide box means more variation.',
          median: 'Median',
          medianDesc: 'The middle value of your drives - 50% are longer, 50% shorter.',
          q1q3Box: 'Q1-Q3 Box',
          q1q3BoxDesc: 'Shows where 50% of your drives land. Narrow = consistent.',
          whiskers: 'Whiskers',
          whiskersDesc: 'Show your full range from shortest to longest drive.',
          proTip: 'Consistency is key for course management. A tight distribution helps you plan your shots with confidence.'
        },
        // Driving Accuracy
        drivingAccuracy: {
          title: 'Driving Accuracy',
          explanation: 'Shows where your tee shots land - fairway, left rough, right rough, or short. Understanding your miss pattern is the first step to fixing it.',
          fairwayHit: 'Fairway Hit',
          fairwayHitDesc: 'Tee shots that found the fairway - your baseline accuracy.',
          missRight: 'Miss Right',
          missRightDesc: 'Shots finishing right of fairway. Often caused by over-rotation or slice.',
          missLeft: 'Miss Left',
          missLeftDesc: 'Shots finishing left of fairway. Common with hooks or pulls.',
          proTip: 'A consistent miss direction is actually useful - aim to compensate and play for your natural shot shape.'
        },
        // Driving Distance by Hole Type
        distanceByHoleType: {
          title: 'Driving Distance by Hole Type',
          explanation: 'Compares your average driving distance on par 4s versus par 5s. This reveals whether you swing harder on longer holes.',
          par4Distance: 'Par 4 Distance',
          par4DistanceDesc: 'Your average drive on par 4s - typically more controlled.',
          par5Distance: 'Par 5 Distance',
          par5DistanceDesc: 'Your average drive on par 5s - often more aggressive.',
          theGap: 'The Gap',
          theGapDesc: 'Difference between par 4 and par 5 drives. >10m suggests grip-it-and-rip-it mentality.',
          proTip: 'If your par 5 drives are longer but less accurate, the extra distance may cost you strokes. Consider using your par 4 swing for both.'
        },
        // Driving Accuracy by Hole Type
        accuracyByHoleType: {
          title: 'Driving Accuracy by Hole Type',
          explanation: 'Compares your fairway hit rate on par 4s versus par 5s. This reveals whether you lose accuracy when going for extra distance.',
          par4Accuracy: 'Par 4 Accuracy',
          par4AccuracyDesc: 'Fairway hit rate on par 4s - your baseline accuracy.',
          par5Accuracy: 'Par 5 Accuracy',
          par5AccuracyDesc: 'Fairway hit rate on par 5s - often lower if swinging harder.',
          proTip: 'On par 5s, finding the fairway often beats extra distance. A fairway shot leaves an easier second shot than rough 20 yards closer.'
        }
      }
    }

  },

  // Social translations
  social: {
    feed: 'Feed',
    posts: 'Posts',
    friends: 'Friends',
    following: 'Following',
    followers: 'Followers',
    likes: 'Likes',
    comments: 'Comments',
    share: 'Share',
    activity: 'Activity',
    
    // Page titles and headers
    pageTitle: 'Social Golf',
    loadingCommunity: 'Loading your golf community...',
    
    // Loading and status messages
    loadingFeed: 'Loading your social feed...',
    feedRefreshed: 'Feed refreshed!',
    loadingMorePosts: 'Loading more posts...',
    allCaughtUp: 'You\'re all caught up! 🎉',
    
    // Error handling
    errorLoadingFeed: 'Error loading feed: {error}',
    tryAgain: 'Try Again',
    
    // Post actions and dropdown menu
    editPost: 'Edit post',
    deletePost: 'Delete post',
    changeTo: 'Change to',
    privacy: {
      public: 'Public',
      friends: 'Friends',
      private: 'Private'
    },
    
    // Scorecard labels and metrics
    scorecard: {
      holes: 'holes',
      total: 'Total',
      place: 'Place',
      rounds: 'Rounds',
      score: 'Score',
      gir: 'GIR',
      putts: 'Putts',
      fir: 'FIR'
    },
    
    // Achievement badges
    achievements: {
      holeInOne: 'Hole-in-One!',
      perfectShot: 'Perfect shot!',
      eagleAchievement: 'Eagle Achievement',
      outstandingShot: 'Outstanding shot!',
      tournamentChampion: 'Tournament Champion',
      firstPlaceFinish: '1st place finish!',
      podiumFinish: 'Podium Finish',
      greatPerformance: 'Great performance!'
    },
    
    // Confirmation dialogs
    confirmDeletePost: 'Are you sure you want to delete this post?',
    
    // Toast messages
    noRoundDetails: 'This post does not have round details available.',
    
    // Fallback text
    unknownCourse: 'Unknown Course',
    
    // Date formatting (for internationalization)
    dateFormat: {
      justNow: 'just now',
      secondsAgo: '{count} seconds ago',
      minuteAgo: '1 minute ago',
      minutesAgo: '{count} minutes ago',
      hourAgo: '1 hour ago',
      hoursAgo: '{count} hours ago',
      dayAgo: '1 day ago',
      daysAgo: '{count} days ago',
      weekAgo: '1 week ago',
      weeksAgo: '{count} weeks ago',
      monthAgo: '1 month ago',
      monthsAgo: '{count} months ago',
      yearAgo: '1 year ago',
      yearsAgo: '{count} years ago',
      about: 'about',
      lessThan: 'less than',
      almost: 'almost',
      over: 'over',
      almostOver: 'almost over',
      today: 'today',
      yesterday: 'yesterday',
      tomorrow: 'tomorrow',
      days: 'days',
      day: 'day',
      weeks: 'weeks',
      week: 'week',
      months: 'months',
      month: 'month',
      years: 'years',
      year: 'year',
      ago: 'ago',
      in: 'in'
    },
    
    // Notifications
    notifications: {
      title: 'Notifications',
      markAllRead: 'Mark all read',
      noNotifications: 'No notifications yet',
      noNotificationsDescription: 'You\'ll see notifications here when people interact with your posts or follow you'
    },
    
    // Comment section
    commentSection: {
      unknownUser: 'Unknown User',
      loadingComments: 'Loading comments...',
      noCommentsYet: 'No comments yet',
      addCommentPlaceholder: 'Add a comment...'
    },
    
    // Post creation
    postCreation: {
      // Modal header
      title: 'Share Your Round',
      description: 'Share your golf round with friends and add your thoughts about the game.',
      
      // Sections
      preview: 'Preview',
      messageLabel: 'Add a message (optional)',
      photoLabel: 'Add a photo (optional)',
      sharingOptionsTitle: 'What to Share',
      privacyTitle: 'Privacy',
      
      // Sharing toggles
      includeScorecard: 'Include scorecard',
      includePerformanceStats: 'Include performance stats', 
      includeLeaderboard: 'Include leaderboard',
      
      // Photo upload
      addPhoto: 'Add a photo',
      removePhoto: 'Remove photo',
      clickToUpload: 'Click to upload',
      dragAndDrop: 'or drag and drop',
      fileTypes: 'PNG, JPG or GIF (MAX. 10MB)',
      
      // Privacy options
      friends: 'Friends',
      public: 'Public',
      
      // Action buttons
      notNow: 'Not Now',
      shareRound: 'Share Round',
      sharing: 'Sharing...',
      uploading: 'Uploading...',
      
      // Legacy fields (for compatibility)
      caption: 'Caption',
      photos: 'Photos',
      addPhotos: 'Add Photos',
      privacy: 'Privacy',
      placeholder: 'How was your round? Share your thoughts...'
    },
    
    // Completion Cards for Social Sharing
    completionCards: {
      // Solo completion card
      solo: {
        score: 'Score',
        gir: 'GIR',
        putts: 'Putts',
        fir: 'FIR',
        holes: 'holes',
        achievement: 'Achievement',
        greatPerformance: 'Great performance!',
        moreAchievements: '+{count} more achievements'
      },
      
      // Multiplayer completion card
      multiplayer: {
        players: 'Players',
        myScore: 'My Score',
        ranking: 'Ranking',
        leaderboard: 'Leaderboard',
        showMore: 'Show More',
        showLess: 'Show Less',
        morePlayers: '... and {count} more players'
      }
    },
    
    // Comments display
    commentsDisplay: {
      show: ' (show)',
      hide: ' (hide)',
      noComments: '0 comments',
      singular: 'comment',
      plural: 'comments'
    },

    // Posts section
    postsSection: {
      title: 'Posts',
      noPostsAvailable: 'No Posts Available',
      noPostsMessage: '{name} hasn\'t shared any posts yet or has set their social activity to private.'
    },

    // GIR Analysis
    gir: {
      // Navigation
      navigation: {
        breadcrumb: 'Analytics / GIR Analysis'
      },

      // Metrics
      label: 'Greens in Regulation',
      description: 'Percentage of holes where you reach the green in regulation',
      tips: [
        'Focus on approach shot accuracy',
        'Practice iron distances',
        'Choose conservative targets',
        'Work on distance control',
        'Improve club selection decisions'
      ],

      // Performance levels
      performance: {
        excellent: 'Excellent',
        strong: 'Strong', 
        average: 'Average',
        belowAverage: 'Below Average',
        focusArea: 'Focus Area'
      },

      // Advice
      advice: {
        excellent: 'Outstanding approach play! Focus on converting birdie opportunities.',
        strong: 'Great GIR percentage. Work on approach shot consistency.',
        average: 'Solid performance. Focus on iron accuracy and distance control.',
        belowAverage: 'Work on approach shots. Practice different yardages.',
        focusArea: 'Major improvement needed. Focus on iron fundamentals.'
      },

      // Chart and analysis
      chart: {
        peerAvg: 'Peer Avg'
      },

      // Tabs
      tabs: {
        overview: 'Overview'
      },

      // Problem holes
      holes: {
        lowGir: 'Low GIR',
        highGir: 'High GIR',
        holes: 'Holes',
        hit: 'hit'
      },

      // Empty states
      emptyStates: {
        noData: 'No GIR Data Available',
        completeMoreRounds: 'Complete more rounds to see your GIR analysis.',
        playMoreRounds: 'Play more rounds to see problem holes analysis',
        playMoreForDistribution: 'Play more rounds to see GIR distribution per hole',
        noDataAvailable: 'No Data'
      },

      // Data labels
      dataLabels: {
        trackYourGreens: 'Track your greens in regulation',
        greensInRegulation: 'Greens in Regulation',
        girAnalysis: 'GIR Analysis',
        girHit: 'GIR Hit',
        girMiss: 'GIR Miss',
        girPercent: 'GIR %',
        current: 'CURRENT',
        target: 'TARGET',
        vsRecent: 'vs Recent',
        vsPeers: 'vs Peers', 
        vsHandicap: 'vs Handicap',
        girTrend: 'GIR Trend',
        lastRounds: 'Last {count} rounds',
        basedOnRounds: 'Based on last {count} rounds • {total} total hole scores',
        swipeToSeeHoles: '← Swipe to see all holes →',
        identifyWeaknesses: 'Identify approach shot weaknesses vs strengths',
        backToDashboard: 'Back to Dashboard',
        hitRate: 'Hit Rate',
        forHandicap: 'For {handicap} handicap',
        round: 'Round',
        roundNumber: 'Round {number}',
        girDistribution: 'GIR Distribution',
        totalRounds: 'Total: {count} rounds'
      },

      // Tee Accuracy Impact section (in Accuracy Analysis page)
      teeAccuracyImpact: {
        title: 'GIR by Tee Accuracy',
        explanation: 'This chart compares how often you hit the green in regulation after tee shots that find the fairway versus those that miss it.',
        fromFairway: 'From Fairway',
        fromFairwayDesc: 'Your GIR percentage when your tee shot lands in the fairway. This shows your approach shot accuracy from ideal positions.',
        fromRoughTrouble: 'From Rough/Trouble',
        fromRoughTroubleDesc: 'Your GIR percentage when you miss the fairway. Lower numbers here indicate difficulty recovering from poor tee shots.',
        theGap: 'The Gap',
        theGapDesc: 'A large difference suggests your recovery shots need work. A small gap means you handle trouble well.',
        proTip: 'If there\'s a big gap between fairway and rough GIR%, focus on course management - sometimes laying up or using a more accurate club off the tee is smarter than going for distance.',
        emptyState: 'Tee accuracy breakdown requires recent round data with tee tracking.'
      }
    },

    // Toast messages for UserDiscovery.tsx
    toasts: {
      searchError: 'Search Error',
      failedToSearchUsers: 'Failed to search for users. Please try again.',
    },

    // UserDiscovery.tsx section
    discovery: {
      searchPlaceholder: 'Search for golfers...',
      tabSuggested: 'Suggested',
      tabPopular: 'Popular',
      tabSearchResults: 'Search Results',
      following: 'Following',
      follow: 'Follow',
      suggestedForYou: 'Suggested for You',
      noSuggestions: 'No suggestions available at the moment',
      popularGolfers: 'Popular Golfers',
      noPopularUsers: 'No popular users found',
      noUsersFoundFor: 'No users found for "{query}"',
    },

  },

  // Play/Game translations
  play: {
    soloRound: 'Solo Round',
    multiplayer: 'Multiplayer',
    createGame: 'Create Game',
    joinGame: 'Join Game',
    startRound: 'Start Round',
    endRound: 'End Round',
    scorecard: 'Scorecard',
    leaderboard: 'Leaderboard',
    gameHistoryStartsHere: 'Your game history starts here',
    gameHistoryStartsHereDescription: 'Every round you complete shows up here with scores, stats, and highlights.',
    noPlayersYet: 'No players yet',
    noPlayersYetDescription: 'Players who join this game will appear here for you to select.',
    noActivityFromFriends: 'No activity from friends yet',
    noActivityFromFriendsDescription: "When your playing partners complete rounds, you'll see their scores and highlights here.",
  },

  // Leaderboard translations
  leaderboard: {
    // Page titles and headers
    pageTitle: 'Leaderboard',
    tournamentStandings: 'Tournament Standings',
    invalidTournamentId: 'Invalid Tournament ID',
    tournamentNotFound: 'Tournament Not Found',
    
    // Navigation
    backToTournament: 'Back to Tournament',
    backShort: 'Back',
    backToHome: 'Back to Home',
    refresh: 'Refresh',
    
    // Tournament info format
    holesParInfo: '{holes} Holes • Par {par} • {type}',
    individual: 'Individual',
    team: 'Team',
    
    // Empty state
    noScoresYet: 'No Scores Yet',
    noScoresMessage: 'Players need to submit their scores before the leaderboard can be displayed.',
    noTeamDataMessage: 'No team data available yet. Team leaderboard will appear once teams have been created and scores are entered.',
    
    // Stats cards
    leadingScore: 'Leading Score',
    toPar: 'to par',
    averageScore: 'Average Score',
    roundsCompleted: 'Rounds Completed',
    parPerHole: 'Par per Hole',
    
    // Filters
    leaderboardView: 'Leaderboard View:',
    teamView: 'Team View',
    individualView: 'Individual View',
    
    // Table headers
    position: 'Pos',
    player: 'Player',
    score: 'Score',
    par: 'Par',
    through: 'Thru',
    
    // Actions
    enterScores: 'Enter Scores',
    tournamentDetails: 'Tournament Details',
    
    // Additional leaderboard keys
    title: 'Leaderboard',
    championshipLeaderboard: 'Championship Leaderboard',
    noParticipants: 'No participants registered yet',
    willPopulate: 'Leaderboard will populate as players register and submit scores',
    overall: 'Overall',
    round: 'Round',
    frontNine: 'Front Nine',
    backNine: 'Back Nine',
    teamMembers: 'Team Members',
    teamCombinedPoints: 'Team Combined Points',
    teamCombinedScores: 'Team Combined Scores',
    searchPlayers: 'Search players...',
    noPlayersFound: 'No players found',

    // Club leaderboard hub
    clubLeaderboards: 'CLUB LEADERBOARDS',
    yourRankings: 'YOUR RANKINGS',
    currentStandings: 'Current Standings',
    competitionCategories: 'COMPETITION CATEGORIES',
    clubActivityTitle: 'CLUB ACTIVITY',
    activePlayers: 'Active Players',
    newRecords: 'New Records',
    tapToExpand: 'Tap to expand',
    clubCompetitionCenter: 'Club competition center',
    topPerformers: 'TOP PERFORMERS',
    fullRankings: 'Full Rankings',
    yourPerformanceSummary: 'YOUR PERFORMANCE',
    currentRank: 'Current Rank',
    clubPercentile: 'Club Percentile',
    viewingLeaderboardFor: 'Viewing leaderboard for',
    yourRank: 'Your rank',
    underHcp: 'Under HCP',
    streaksLabel: 'Streaks',
    percentileLabel: 'Percentile',
    youLabel: 'You',
    leaderLabel: 'Leader',
    categoryNotFound: 'Category not found',
    loadingLeaderboard: 'Loading leaderboard...',
    errorLoadingLeaderboard: 'Error loading leaderboard',
    joinClubToView: 'Please join a club to view leaderboards',
    noActiveClub: 'No active club selected',
    categories: 'Categories'
  },

  // Course Leaderboard translations
  courseLeaderboard: {
    // Loading states
    loading: 'Loading...',
    courseNotFound: 'Course Not Found',
    courseNotLoaded: 'This course could not be loaded',
    loadingError: 'Failed to load course data',
    
    // Page navigation
    leaderboardsTitle: 'Leaderboards',
    
    // Section headers
    selectTees: 'Select Tees',
    courseRecords: 'Course Records',
    leaderboards: 'Leaderboards',
    chooseCategory: 'Choose a leaderboard category to view rankings',
    
    // Course record types
    kingOfCourse: 'King of Course',
    queenOfCourse: 'Queen of Course',
    courseRecord: 'Course Record',
    localLegend: 'Local Legend',
    
    // Record descriptions
    kingDescription: 'Best male scores across all tees',
    queenDescription: 'Best female scores across all tees',
    recordDescription: 'Best overall score regardless of gender',
    legendDescription: 'Most active players in the last 90 days',
    
    // Status messages
    noRecordSet: 'No record set',
    noActivityYet: 'No activity yet',
    noActivity90Days: 'No activity in the last 90 days',
    noRecordsFound: 'No records found for this category',
    noScoresRecorded: 'No scores recorded for this category',
    
    // Table headers
    rank: 'RANK',
    athlete: 'ATHLETE',
    score: 'SCORE',
    
    // Measurement units
    rounds: 'rounds',
    totalPlayers: 'Total Players',
    totalRounds: 'Total Rounds',
    
    // Labels and badges
    crBadge: 'CR',
    localLegendBadge: 'Local Legend',
    mostActive90Days: 'Most active in last 90 days',
    localLegend90Days: 'Local Legend (90 days)',
    
    // Leaderboard categories
    allTime: 'All-Time',
    allTimeMen: 'All-Time (Men)',
    allTimeWomen: 'All-Time (Women)',
    thisYear: 'This Year',
    thisYearMen: 'This Year (Men)',
    thisYearWomen: 'This Year (Women)',
    following: 'Following',
    netStroke: 'Net (Stroke)'
  },

  // Messaging translations
  messaging: {
    // Authentication
    signInRequired: 'Please sign in to view messages',
    
    // Search
    searchPlaceholder: 'Search conversations...',
    recentSearches: 'Recent searches',
    
    // Actions
    new: 'New',
    tryAgain: 'Try Again',
    startChatting: 'Start Chatting',
    cancel: 'Cancel',
    delete: 'Delete',
    
    // Empty states
    noMessagesYet: 'No Messages Yet',
    noMessagesDescription: 'Start a conversation with your golf buddies and stay connected on and off the course',
    
    // Time formatting
    justNow: 'Just now',
    
    // Conversation content
    unknownUser: 'Unknown User',
    conversation: 'Conversation',
    you: 'You:',
    roundShare: '🏌️ Round share',
    photo: '📷 Photo',
    channelCreated: 'Channel created',
    sayHello: 'Say hello!',
    
    // Delete confirmation
    deleteConversation: 'Delete Conversation',
    deleteConfirmMessage: 'Are you sure you want to delete this conversation? This action cannot be undone.',
    
    // Modal
    newConversation: 'New Conversation',
    directMessage: 'Direct Message',
    groupChat: 'Group Chat',
    searchUsers: 'Search users...',
    createGroupWithCount: 'Create Group ({count} members)',
    
    // User display
    handicapLabel: 'HCP',
    
    // Chat interface
    typeMessage: 'Type a message...',
    messageWillBeSentOffline: 'Message will be sent when online...',
    groupChatTitle: 'Group Chat',
    enterGroupName: 'Enter group name',
    membersCount: '{count} members',
    participantsCount: '{count} participants',
    participantsLabel: 'Participants ({count})',
    searchInConversation: 'Search in conversation...',
    searchUsersInChat: 'Search users...',
    sharedPhoto: 'Shared photo',
    typeYourMessage: 'Type your message...',
    groupInfo: 'Group Info',
    groupName: 'Group Name',
    edit: 'Edit',
    add: 'Add',
    addParticipants: 'Add Participants',
    remove: 'Remove',
    editMessage: 'Edit Message',
    addParticipantsOne: 'Add 1 Member',
    addParticipantsMany: 'Add {count} Members',
    muteNotifications: 'Mute Notifications',
    unmuteNotifications: 'Unmute Notifications',
    clearChat: 'Clear Chat',
    leaveChannel: 'Leave Channel',
    blockUser: 'Block User',
    reply: 'Reply',
    copy: 'Copy',
    replyingTo: 'Replying to {name}',
    loadingConversation: 'Loading conversation...',
    noMessagesYetInConversation: 'No messages yet',
    today: 'Today',
    yesterday: 'Yesterday',
    startConversation: 'Say something to get started',
    // GroupInfoModal.tsx keys
    participantSingular: 'participant',
    participantPlural: 'participants',
    membersSection: 'Members',
    adminLabel: 'Admin',
    leaveGroup: 'Leave Group',
    searchFriends: 'Search friends...',
    loading: 'Loading...',
    searchResultsLabel: 'Search Results',
    yourFriends: 'Your Friends',
    noUsersFound: 'No users found',
    tryDifferentSearchTerm: 'Try a different search term',
    noFriendsYet: 'No friends yet',
    addFriendsToInvite: 'Add friends to invite them to groups',
  },

  // Auth translations
  auth: {
    processingInvitation: 'Processing your invitation...',
    // Actions
    signIn: 'Sign In',
    signUp: 'Sign Up', 
    signOut: 'Sign Out',
    createAccount: 'Create Account',
    resetPassword: 'Reset Password',
    forgotPassword: 'Forgot Password',
    
    // Form fields
    email: 'Email',
    emailAddress: 'Email Address',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    createPassword: 'Create Password',
    
    // Form placeholders
    enterEmail: 'Enter your email',
    createStrongPassword: 'Create a strong password',
    confirmYourPassword: 'Confirm your password',
    
    placeholders: {
      enterEmail: 'Enter your email',
      createStrongPassword: 'Create a strong password',
      confirmYourPassword: 'Confirm your password'
    },
    
    // Headers & titles
    welcomeBack: 'Welcome Back',
    welcomeBackShort: 'Welcome back',
    joinFairwayLive: 'Join Fairway Live',
    signInToContinue: 'Sign in to continue your golf journey',
    coachSignIn: 'Coach Sign In',
    coachRegistration: 'Coach Registration',
    clubAdminSignIn: 'Club Admin Sign In',
    clubRegistrationTitle: 'Club Registration',
    joinAsCoach: 'Join as Coach',
    joinAsAdmin: 'Join as Admin',
    registerAsCoach: 'Register as Coach',
    accessCoachingDashboard: 'Access your coaching dashboard',
    createCoachAccount: 'Create your coach account to get started',
    accessClubDashboard: 'Access your club management dashboard',
    joinPremierGolfPlatform: 'Join the premier golf club management platform',
    needToRegisterClub: 'Need to register your club?',
    alreadyHaveClubAccount: 'Already have a club account?',
    newCoach: 'New coach?',

    // Toggle links
    dontHaveAccount: "Don't have an account?",
    alreadyHaveAccount: "Already have an account?",
    
    // Form aria labels
    signInForm: 'Sign in form',
    signUpForm: 'Sign up form',
    
    // Validation messages
    emailTooLong: 'Email address is too long.',
    emailIncomplete: 'Email address is incomplete.',
    passwordTooShort: 'Password must be at least 8 characters.',
    passwordTooLong: 'Password is too long.',
    passwordsDoNotMatch: 'Passwords do not match',
    passwordsMatch: 'Passwords match',
    passwordStrengthGood: 'Password strength: Good',
    
    // Success messages
    accountCreated: 'Account created!',
    successfullySignedIn: 'Successfully signed in.',
    checkEmailToVerify: 'Please check your email to verify your account.',
    resetEmailSent: 'Reset Email Sent!',
    
    // Show/hide password
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    showConfirmPassword: 'Show confirm password', 
    hideConfirmPassword: 'Hide confirm password',
    
    // Loading states
    creatingSession: 'Loading...',
    creatingAccount: 'Creating account...',
    sendingResetLink: 'Sending Reset Link...',
    
    // Social auth
    continueWithGoogle: 'Continue with Google',
    continueWithFacebook: 'Continue with Facebook',
    socialAuthFailed: '{provider} {mode} failed',
    
    // Forgot password
    checkYourEmail: 'Check Your Email',
    resetLinkSentToEmail: 'We\'ve sent a password reset link to {email}',
    emailSpamInstructions: 'Don\'t see the email? Check your spam folder or try again.',
    backToSignIn: 'Back to Sign In',
    resetYourPassword: 'Reset Your Password',
    resetPasswordInstructions: 'Enter your email address and we\'ll send you a link to reset your password.',
    validEmailAddress: 'Valid email address',
    sendResetLink: 'Send Reset Link',
    createNewPassword: 'Create New Password',
    createNewPasswordInstructions: 'Enter your new password below. Make sure it\'s strong and secure.',
    newPassword: 'New Password',
    confirmNewPassword: 'Confirm New Password',
    enterNewPassword: 'Enter your new password',
    confirmNewPasswordPlaceholder: 'Confirm your new password',
    updatePassword: 'Update Password',
    updatingPassword: 'Updating Password...',
    
    // Additional form elements
    orContinueWithEmail: 'or continue with email',
    skipToMainContent: 'Skip to main content',
    startTrackingProgress: 'Start tracking your golf progress today',
    
    // Auth toggle messages
    dontHaveAccountSignUp: "Don't have an account? Sign up",
    alreadyHaveAccountSignIn: "Already have an account? Sign in",
    
    // Validation messages
    validation: {
      pleaseEnterValidEmail: 'Please enter a valid email address',
      emailTooLong: 'Email address is too long',
      emailIncomplete: 'Email address is incomplete',
      localPartTooLong: 'Email local part is too long',
      invalidLocalPart: 'Email local part format is invalid',
      domainCommonTypo: 'Did you mean a different email domain?',
      invalidDomain: 'Email domain format is invalid',
      domainTooShort: 'Email domain is too short',
      passwordMinLength: 'Password must be at least 8 characters',
      passwordTooLong: 'Password is too long',
      passwordRequireTypes: 'Password must contain at least 3 of: lowercase, uppercase, numbers, symbols',
      passwordTooCommon: 'Password is too common, please choose a stronger one',
      passwordRepeatedChars: 'Password contains too many repeated characters',
      passwordsDoNotMatch: 'Passwords do not match',
      passwordsMatch: 'Passwords match',
      passwordStrengthGood: 'Password strength: Good',
      validEmailAddress: 'Valid email address'
    },
    
    // Error messages
    errors: {
      validationError: 'Validation Error',
      pleaseCheckInput: 'Please check your input',
      reviewHighlightedField: 'Please review the highlighted field',
      fixErrorsAndTryAgain: 'Fix the errors and try again',
      authenticationError: 'Authentication Error',
      passwordResetFailed: 'Password Reset Failed'
    },
    
    // Success messages
    success: {
      welcomeBack: 'Welcome back!',
      accountCreated: 'Account created!',
      signedIn: 'Successfully signed in',
      checkEmailToVerify: 'Please check your email to verify your account',
      resetEmailSent: 'Reset Email Sent!',
      resetLinkSentTo: 'We\'ve sent a reset link to',
      pleaseCheckEmail: 'Please check your email'
    },

    // Toast messages
    toasts: {
      differentAccount: 'Different Account',
      differentAccountDesc: 'This invitation is for {email}. Please log out and use that account, or register a new account with that email.',
      welcomeBack: 'Welcome back!',
      roleUpgraded: 'Role Upgraded!',
      roleUpgradedDesc: 'You\'ve been upgraded to {role} at this club.',
      welcomeBackActiveDesc: 'You\'re now active as {role} at this club.',
      alreadyMember: 'Already a member',
      alreadyMemberDesc: 'You\'re already a {role} at this club.',
      invitationError: 'Invitation Error',
      invitationErrorDesc: 'Failed to process invitation. Please try again.',
      welcome: 'Welcome!',
      welcomeAddedDesc: 'You have been added as a {role} to the club.',
      accountExists: 'Account Already Exists',
      accountExistsInvitationDesc: 'An account with {email} already exists. Please sign in instead to access your invitation.',
      accountExistsInvitationAction: 'Click "Already have an account? Sign in" below',
      accountExistsAdminDesc: 'An account with {email} already exists. To add admin access to your existing account, please sign in with your current password.',
      accountExistsAdminAction: 'Switch to "Sign In" and use your existing password',
      accountExistsRegularDesc: 'An account with {email} already exists.',
      accountExistsAction: 'Please sign in instead or use a different email address',
      accountExistsSignIn: 'Account Exists',
      loginError: 'Login Error',
      loginErrorDesc: 'Unable to get user information after login.',
      accessDenied: 'Access Denied',
      accessDeniedDesc: 'You do not have club admin access. Please contact your club administrator.',
      welcomeToTeam: 'Welcome to the Team!',
      welcomeToTeamDesc: 'You\'ve successfully joined {clubName} as an admin.',
      clubAccountCreated: 'Club Account Created',
      clubAccountCreatedDesc: 'Welcome to {clubName}! Your club admin account is ready.',
      invitationAcceptanceFailed: 'Invitation Acceptance Failed',
      clubRegistrationFailed: 'Club Registration Failed',
      invitationFailedDesc: 'Failed to accept invitation. Please contact the club administrator.',
      clubRegistrationFailedDesc: 'Your account was created but club setup failed. Please contact support.',
      welcomeBackSignIn: 'Welcome back!',
      accountCreatedSignUp: 'Account created!',
      signedInDesc: 'Successfully signed in',
      checkEmailDesc: 'Please check your email to verify your account',
      authenticationError: 'Authentication Error',
      passwordResetError: 'Password reset error',
      resetEmailSent: 'Reset Email Sent!',
      resetEmailSentDesc: 'We\'ve sent a reset link to {email}. Please check your email.',
      passwordResetFailed: 'Password Reset Failed',
      invitationSent: 'Invitation Sent!',
      invitationSentDesc: 'Please check your email inbox (and spam folder).',
      invitationResendFailed: 'Failed to Send',
      invitationResendFailedDesc: 'Please contact your club administrator.',
      invitationResendFailedGeneric: 'An error occurred. Please try again later.'
    },
    clubRegistration: {
      clubName: 'Club Name',
      clubNamePlaceholder: 'Enter your club name',
      clubNameVerified: 'Club verified',
      adminEmail: 'Admin Email',
      adminEmailPlaceholder: 'Enter admin email address',
      validatingEmail: 'Validating email...',
      password: 'Password',
      passwordPlaceholder: 'Create a password',
      confirmPassword: 'Confirm Password',
      confirmPasswordPlaceholder: 'Confirm your password',
      hideOptionalFields: 'Hide',
      showOptionalFields: 'Show',
      clubDetailsOptional: 'optional club details',
      address: 'Address',
      addressPlaceholder: 'Club address',
      phone: 'Phone',
      phonePlaceholder: 'Club phone number',
      website: 'Website',
      websitePlaceholder: 'https://...',
      description: 'Description',
      descriptionPlaceholder: 'Tell us about your club...',
      acceptingInvitation: 'Accepting invitation...',
      creatingClubAccount: 'Creating club account...',
      acceptInvitationAsCoach: 'Accept Invitation as Coach',
      acceptInvitationAsAdmin: 'Accept Invitation as Admin',
      createClubAccount: 'Create Club Account',
      required: '*'
    }
  },

  // Profile translations
  profile: {
    // Basic profile
    editProfile: 'Edit Profile',
    personalInfo: 'Personal Information',
    golfInfo: 'Golf Information',
    preferences: 'Preferences',
    
    // Profile page headers
    yourProfile: 'Your Profile',
    accountAndAchievements: 'Account & achievements',
    settings: 'Settings',

    // Error handling
    somethingWentWrong: 'Something went wrong',
    failedToLoadProfile: 'Failed to load profile data',
    tryAgain: 'Try Again',
    failedToGenerateQR: 'Failed to generate QR code. Please try again.',
    
    // QR Code functionality
    profileQR: 'Profile QR',
    qrCode: 'QR Code', 
    myGolfProfileQRCode: 'My Golf Profile QR Code',
    scanQRToAddFriend: 'Scan this QR code to add me as a friend!',
    golfBuddyContact: 'Golf Buddy contact',
    qrCodePrivate: 'Your QR code is private. If you share it with someone, they can scan it with their camera to add you as a friend.',
    share: 'Share',
    linkCopied: 'Link copied to clipboard!',
    failedToCopyLink: 'Could not copy link — please copy it manually.',

    // QR Scanner alerts
    qrScanningAvailable: 'QR scanning is available! This would open your camera to scan QR codes.',
    cameraNotAvailable: 'Camera access not available on this device.',
    
    // Settings/Account section
    accountSettings: 'Account Settings',
    account: 'Account',
    appPreferences: 'App Preferences', 
    security: 'Security',
    updateProfileInfo: 'Update your profile information',
    pushNotifications: 'Push Notifications',
    receiveNotifications: 'Receive notifications for mentions and updates',
    dataSync: 'Data Sync',
    syncDataAcrossDevices: 'Sync your data across devices',
    twoFactorAuth: 'Two-Factor Authentication',
    addExtraLayerSecurity: 'Add an extra layer of security',
    language: 'Language',
    selectLanguage: 'Select your preferred language',
    signOut: 'Sign Out',
    dataManagement: 'Data Management',
    exportData: 'Export My Data',
    exportDataDesc: 'Download all your personal data',
    deleteAccount: 'Delete Account',
    deleteAccountDesc: 'Permanently delete your account and data',
    deleteAccountTitle: 'Delete Your Account',
    deleteAccountWarning: 'This action is permanent and cannot be undone. All your data, rounds, and progress will be deleted.',
    deleteAccountConfirm: 'Delete My Account',
    deleteAccountError: 'Failed to request account deletion. Please contact support@strokone.com',

    // Profile components
    profile: 'Profile',
    memberSince: 'Member since',
    hcp: 'HCP',
    totalRounds: 'Rounds',
    hoursPlayed: 'Hours',
    badges: 'Badges',
    
    // Invite friends
    inviteFriends: 'Invite friends',
    shareYourGolfJourney: 'Share your golf journey',
    
    // Navigation cards
    activities: 'Activities',
    recentRoundsAndScores: 'Recent rounds & scores',
    statistics: 'Statistics',
    thisYearRounds: 'This year: {count} rounds',
    courses: 'Courses',
    coursesPlayed: '{count} courses played',
    courseHistory: '{name}\'s course history',
    noCourseDataAvailable: 'No Course Data Available',
    noPublicCourseData: '{name} hasn\'t played any courses yet or has set their course history to private.',
    performanceZones: 'Performance Zones',
    trophyRoom: 'Trophy Room',
    posts: 'Posts',
    socialFeedActivity: 'Social feed activity',
    parAnalysisPersonal: '{name}\'s par 3s, 4s, 5s analysis',
    noPerformanceDataAvailable: 'No Performance Data Available',
    noPerformanceDataMessage: '{name} hasn\'t completed enough rounds to show performance zone analysis or has set this data to private.',
    achievementsAndAwardsPersonal: '{name}\'s achievements & awards',
    noAchievementsAvailable: 'No Achievements Available',
    noAchievementsMessage: '{name} hasn\'t earned any trophies yet or has set their achievements to private.',
    golferProfile: 'Golfer Profile',
    profileNotFound: 'Profile Not Found',
    profileNotAvailable: 'This user\'s profile is not available or has been set to private.',
    goBack: 'Go Back',
    privateProfile: 'Private Profile',
    userPrivateProfile: 'This user has set their profile to private.',
    message: 'Message',
    addFriend: 'Add Friend',
    accept: 'Accept',
    cancel: 'Cancel',
    unfriend: 'Unfriend',
    clubs: 'Clubs',
    equipmentAndDistances: 'Equipment & distances',
    
    // Friends section
    friends: 'Friends',
    connection: 'connection',
    connections: 'connections',
    
    // Fallback user display
    golfer: 'Golfer',
    
    // Public activities specific
    recentGolfActivities: '{name}\'s recent golf activities',
    noRecentActivities: 'No Recent Activities', 
    noPublicActivities: '{name} hasn\'t completed any rounds recently or has set their activities to private.',
    privacyNotice: 'Privacy Notice',
    publicActivitiesDisclaimer: 'This shows only the activities that {name} has made public. Recent rounds and achievements may be hidden for privacy.',

    // Setup wizard and error states
    startSetupWizard: 'Start Setup Wizard',
    dataCouldntLoad: "Data couldn't load",
    profileDataTimedOut: "Your profile data timed out. This might be due to a slow connection. Try reloading to fetch your stats.",
    reloadPage: 'Reload Page',

    // QR and sharing
    shareProfileWithQR: 'Share profile with QR code',
    userAvatar: 'User Avatar',

    // Navigation cards
    eventsAndTournaments: 'Events & Tournaments',
    viewAndRegisterEvents: 'View and register for upcoming events',
    viewYourStats: 'View your performance statistics',
    viewYourCourses: "View courses you've played",
    feedback: 'Feedback',
    shareIdeasAndReportIssues: 'Share ideas and report issues',
    parAnalysis: 'Par 3s, 4s, 5s analysis',
    achievementsAndAwards: 'Achievements & awards',

    // Account settings
    helpAndSupport: 'Help & Support',
    replayFeatureTour: 'Replay Feature Tour',
    playARound: 'Play a round',
    playARoundDesc: 'Start live scoring or log a past round',
    learnAboutFeatures: 'Learn about all the features again',

    // Privacy settings
    privacySettings: 'Privacy',
    managePrivacySettings: 'Manage who can see your profile and data',
    privacyExplanation: 'These items are private by default. Friends can always see them. Toggle ON to make visible to everyone.',
    privacyNote: 'Your privacy settings are saved automatically',
    privacySaveError: 'Failed to save privacy settings',
    // Privacy toggle groups
    privacySocialTitle: 'Social',
    privacyGolfTitle: 'Golf Data',
    privacyEquipmentTitle: 'Equipment & Clubs',
    // Name display privacy
    privacyNameTitle: 'Name Display',
    privacyNickname: 'Nickname',
    privacyNicknamePlaceholder: 'e.g., GolfPro99',
    privacyShowRealName: 'Show real name',
    privacyShowRealNameDesc: 'If OFF, your nickname is shown to non-friends',
    // Privacy toggles
    privacyFriends: 'Friends & Followers',
    privacyFriendsDesc: 'Show your connections to non-friends',
    privacyPublications: 'Publications',
    privacyPublicationsDesc: 'Show your posts to non-friends',
    privacyProgression: 'Progression',
    privacyProgressionDesc: 'Show handicap history to non-friends',
    privacyActivity: 'Activity',
    privacyActivityDesc: 'Show recent activity to non-friends',
    privacyStatistics: 'Statistics',
    privacyStatisticsDesc: 'Show performance stats to non-friends',
    privacyCourses: 'Courses',
    privacyCoursesDesc: 'Show courses played to non-friends',
    privacyPerformanceZone: 'Performance Zone',
    privacyPerformanceZoneDesc: 'Show strokes gained to non-friends',
    privacyTrophyRoom: 'Trophy Room',
    privacyTrophyRoomDesc: 'Show trophies to non-friends',
    privacyClubs: 'Equipment',
    privacyClubsDesc: 'Show your clubs to non-friends',
    privacyClubAffiliations: 'Club Affiliations',
    privacyClubAffiliationsDesc: 'Show memberships to non-friends',

    // Trophy room
    trophyRoomTitle: 'Trophy Room',
    yourEarnedTrophies: 'Your earned trophies ({count} total)',
    trophyStats: 'Trophy Stats',
    earned: 'Earned',
    available: 'Available',
    earnedTrophiesCount: 'Earned Trophies ({count})',
    earnedOn: 'Earned {date}',
    roundNumber: 'Round #{id}',
    noTrophiesYet: 'No Trophies Yet',
    completeRoundsToEarn: 'Complete rounds and achieve milestones to earn your first trophy!',
    trophy: 'Trophy',
    greatAchievement: 'Great achievement!',

    // Club affiliation
    clubAffiliation: 'Club Affiliation',
    noClubMembership: 'No club membership',
    joinClubToStart: 'Join a club to get started',
    homeClub: 'Home Club',
    memberSinceDate: 'Member since {date}',
    viewAllClubs: 'View all {count} clubs',

    // Activities
    solo: 'Solo',
    multiplayer: 'Multiplayer',
    inStats: 'In stats',

    // Handicap management
    notSet: 'Not set',
    scratch: 'Scratch',
    unrated: 'Unrated',
    lowHandicap: 'Low handicap',
    midHandicap: 'Mid handicap',
    highHandicap: 'High handicap',
    beginner: 'Beginner',
    currentHandicap: 'Current Handicap',
    update: 'Update',
    clickUpdateToSetHandicap: 'Click "Update" to set your initial handicap',
    newHandicapLabel: 'New Handicap (0-54)',
    enterHandicap: 'Enter your handicap',

    // Profile details modal
    invalidHandicap: 'Invalid handicap',
    handicapMustBeBetween: 'Handicap must be between -10 and 54',
    profileUpdated: 'Profile updated',
    profileSavedHistoryFailed: 'Profile saved but history tracking failed. Contact support if this persists.',
    profileAndHandicapUpdated: 'Your profile and handicap history have been updated successfully.',
    profileUpdatedSuccess: 'Your profile has been updated successfully.',
    failedToSaveProfile: 'Failed to save profile.',
    failedToUpdateProfile: 'Failed to update profile. Please try again.',
    profileSettings: 'Profile Settings',
    saving: 'Saving...',
    save: 'Save',
    enterFirstName: 'Enter your first name',
    enterLastName: 'Enter your last name',
    enterNickname: 'Enter your nickname',
    nickname: 'Nickname',
    roundSharing: 'Round Sharing',
    requiredForRankings: 'Required for performance rankings (KOC/QOC)',
    whenIFinishARound: 'When I finish a round',

    // Navigation cards - My Bookings & Expenses
    myBookings: 'My Bookings',
    bookAndManageTeeTimes: 'Book and manage your tee times',
    myExpenses: 'My Expenses',
    trackSpendingAndSubscriptions: 'Track spending & subscriptions',
    golfProgression: 'Golf Progression',
    activeChallengesAvailable: 'Active club challenges available',

    // Activity heatmap
    heatmap: {
      activityTitle: 'Activity Trend',
      rounds: 'rounds',
      total: 'total',
      avg: 'avg',
    },
  },

  // Onboarding translations
  onboarding: {
    // WelcomeScreen
    welcomeTitle: 'Welcome to Strokone',
    welcomeTagline: 'Your golf journey starts here',
    letsGo: "Let's Go",

    // NameInputScreen
    niceToMeetYou: 'Nice to meet you!',
    whatShouldWeCallYou: 'What should we call you?',
    firstName: 'First name',
    firstNamePlaceholder: 'John',
    lastName: 'Last name (optional)',
    lastNamePlaceholder: 'Doe',
    nameHelperText: 'Your name helps others find and connect with you',
    continue: 'Continue',

    // HandicapScreen
    whatsYourHandicap: "What's your handicap?",
    handicapHelperText: 'This helps us track your progress',
    orQuickSelect: 'Or quick select:',
    handicapUpdateNote: "Don't worry, you can update this anytime in settings",
    iDontHaveOneYet: "I don't have one yet",
    handicap: 'Handicap',

    // HandicapScreen — option labels
    hcpBeginner: 'Beginner',
    hcpBeginnerSub: 'No HCP yet',
    hcpGettingStarted: 'Getting started',
    hcpImproving: 'Improving',
    hcpSolidPlayer: 'Solid player',
    hcpAdvanced: 'Advanced',
    hcpScratchOrBetter: 'Scratch or better',
    hcpExpert: 'Expert',
    hcpUnder18: 'Under 18',
    hcpUnder10: 'Under 10',

    // GoalsScreen
    whatBringsYou: 'What brings you to Strokone?',
    selectAllThatApply: 'Select all that apply',
    goalPracticeTitle: 'Practice With a Plan',
    goalPracticeDesc: 'Drills picked for your game',
    goalImproveTitle: 'Improve My Handicap',
    goalImproveDesc: 'Get better with data',
    goalConnectTitle: 'Connect with Golfers',
    goalConnectDesc: 'Play with friends',
    goalExploreTitle: 'Just Exploring',
    goalExploreDesc: 'See what Strokone offers',

    // AttributionScreen
    howDidYouHearAboutUs: 'How did you hear about us?',
    attributionHelperText: 'One tap — it helps us reach more golfers',
    sourceFriendTitle: 'Friend or playing partner',
    sourceFriendDesc: 'Someone recommended it to me',
    sourceClubTitle: 'My golf club or coach',
    sourceClubDesc: 'Through my club or a lesson',
    sourceSocialTitle: 'Social media',
    sourceSocialDesc: 'Instagram, TikTok, Facebook...',
    sourceAppStoreTitle: 'App Store',
    sourceAppStoreDesc: 'Found it while browsing',
    sourceSearchTitle: 'Google search',
    sourceSearchDesc: 'Searched for a golf app',
    sourceOtherTitle: 'Somewhere else',
    sourceOtherDesc: 'Press, event, or other',

    // HomeClubScreen
    doYouHaveAHomeClub: 'Do you have a home club?',
    connectWithClubCommunity: 'Connect with your club community',
    searchForYourClub: 'Search for your club...',
    selectClubToContinue: 'Select a club to continue',
    iDontHaveAHomeClub: "I don't have a home club",
    canChangeClubLater: 'You can always change this later in your profile',
    noClubsFound: 'No clubs found for',

    // PhotoScreen
    addAPhoto: 'Add a photo',
    helpFriendsRecognizeYou: 'Help friends recognize you on the course',
    lookingGreat: 'Looking great!',
    tapToAddPhoto: 'Tap the circle to add your photo',
    photoTip: 'A clear face photo helps your playing partners find you',
    photoTipLabel: 'Tip:',
    skipForNow: 'Skip for now',

    // InviteFriendsScreen
    findYourGolfBuddies: 'Find your golf buddies',
    followFriendsOnStrokone: 'Follow friends already on Strokone to see their rounds',
    searchForFriends: 'Search for friends...',
    searchResults: 'Search results',
    suggestedForYou: 'Suggested for you',
    added: 'Added',
    add: 'Add',
    noUsersFound: 'No users found for',
    tryDifferentSearch: 'Try a different search or invite by email',
    noSuggestions: 'No suggestions available',
    cantFindSomeone: "Can't find someone? Invite by email",
    invalidEmailError: 'Please enter a valid email',
    emailAlreadyAdded: 'This email is already added',
    maxInvitesReached: 'Maximum 5 invites at a time',
    emailInvitePlaceholder: 'friend@email.com',
    hi: 'Hi',

    // CompletionScreen
    youreAllSet: "You're all set",
    golfJourneyBeginsNow: 'Your golf journey begins now',
    startPlaying: 'Start Playing',
    whatsNext: "What's next?",
    exploreCourses: 'Explore Courses',
    findNewPlaces: 'Find new places to play',
    findFriends: 'Find Friends',
    connectWithGolfers: 'Connect with golfers',
    joinYourClub: 'Your Club',
    connectWithMembers: 'Connect with your club community',
    checkProgression: 'Level Up',
    trackYourJourney: 'Follow a clear progression path',
    practiceDaily: 'Practice daily',
    drillsForWeakestSkills: 'Drills picked for your weakest skills',
    yourCoach: 'Your coach',
    homeworkAndLessons: 'Homework and lessons in one place',
    startPractising: 'Start practising',
    profileSavedTitle: 'Profile saved!',
    profileSavedDesc: 'You are all set. Your first drill is waiting.',
    goalBadgePlanner: 'Planner',
    goalBadgeImprover: 'Improver',
    goalBadgeSocial: 'Social',
    goalBadgeExplorer: 'Explorer',

    // Shared — OnboardingScreen
    back: 'Back',
    skip: 'Skip',

    // Shared — OnboardingInput
    gender: 'Gender (optional)',
    male: 'Male',
    female: 'Female',
    dateOfBirth: 'Date of birth (optional)',
    dateOfBirthTitle: 'Date of Birth',

    // Legacy profile-setup flow keys (kept for backwards compatibility)
    profileSetup: {
      title: 'Complete Your Profile',
      subtitle: 'Help us personalize your golf experience',
      failedToSave: 'Failed to save profile data.',
      completeTitle: 'Profile setup complete!',
      completeDescription: 'Welcome to Fairway Live. Let\'s start tracking your golf journey.',
      continue: 'Continue'
    },
    basicInfo: {
      title: 'Basic Information',
      firstName: 'First Name',
      lastName: 'Last Name',
      gender: 'Gender',
      male: 'Male',
      female: 'Female',
      preferNotToSay: 'Prefer not to say',
      birthdateLabel: 'Date of Birth',
      birthdatePlaceholder: 'Select your birthdate',
      next: 'Next',
      requiredField: 'This field is required'
    },
    golfInfo: {
      title: 'Golf Information',
      handicapIndex: 'Handicap Index',
      validHandicap: 'Valid handicap index (-4.4 to 54)',
      homeCourse: 'Home Course',
      searchPlaceholder: 'Search courses...',
      completeSetup: 'Complete Setup',
      selectCourse: 'Please select your home course',
      previous: 'Previous',
      optional: 'Optional',
      handicapHelpText: 'Don\'t know your handicap? You can add it later.'
    },
    stepLabels: {
      basicInfo: 'Basic Info',
      preferences: 'Preferences'
    },
    avatarUpload: {
      addPhoto: 'Add profile photo'
    },
    profileSummary: {
      title: 'Profile Summary',
      name: 'Name:',
      handicap: 'Handicap:',
      homeCourse: 'Home Course:'
    },

    // Privacy Consent (used by PrivacyConsent.tsx)
    privacyConsent: {
      title: 'Privacy & Permissions',
      subtitle: 'Help us provide you with the best golf experience',
      dataCollection: 'Data Collection',
      dataCollectionDescription: 'We collect performance data to provide analytics and track your improvement',
      socialSharing: 'Social Sharing',
      socialSharingDescription: 'Allow others to see your public activities and achievements',
      locationServices: 'Location Services',
      locationServicesDescription: 'Enable GPS for accurate course tracking and statistics',
      notificationPermissions: 'Push Notifications',
      notificationPermissionsDescription: 'Get updates on tournaments, friend activities, and achievements',
      termsOfService: 'Terms of Service',
      privacyPolicy: 'Privacy Policy',
      iAgreeToThe: 'I agree to the',
      and: 'and',
      dataCollectionConsent: 'I consent to data collection for analytics',
      socialSharingConsent: 'Enable social sharing features',
      locationConsent: 'Allow location access for GPS features',
      notificationConsent: 'Enable push notifications',
      acceptAndContinue: 'Accept & Continue',
      required: 'Required',
      termsDescription: 'Agreement to our terms of use and service guidelines',
      readTerms: 'Read Terms →',
      privacyDescription: 'How we collect, use, and protect your personal data',
      readPolicy: 'Read Policy →',
      optional: 'Optional',
      marketingCommunications: 'Marketing Communications',
      marketingDescription: 'Receive tips, updates, and special offers via email',
      anonymousAnalytics: 'Anonymous Analytics',
      analyticsDescription: 'Help improve the app with anonymous usage data',
      acceptTermsPrivacyMessage: 'Please accept Terms of Service and Privacy Policy to continue'
    }
  },

  // Profile Posts translations
  profilePosts: {
    // Header
    title: 'My Posts',
    subtitle: 'Your social activity history',
    loadingMessage: 'Loading your posts...',
    
    // Filters
    filtersLabel: 'Filters',
    postsCount: '{count} posts',
    
    // Filter Labels
    filters: {
      timePeriod: 'Time Period',
      postType: 'Post Type', 
      visibility: 'Visibility',
      
      // Time options
      timeOptions: {
        allTime: 'All Time',
        thisYear: 'This Year',
        thisMonth: 'This Month',
        thisWeek: 'This Week'
      },
      
      // Post type options
      typeOptions: {
        allTypes: 'All Types',
        rounds: 'Rounds',
        badges: 'Badges',
        achievements: 'Achievements',
        milestones: 'Milestones'
      },
      
      // Visibility options
      visibilityOptions: {
        allVisibility: 'All Visibility',
        public: 'Public',
        friendsOnly: 'Friends Only',
        private: 'Private'
      }
    },
    
    // Empty state
    emptyState: {
      title: 'No posts yet',
      message: 'Share your golf achievements to build your social activity',
      createButton: 'Create Post'
    }
  },

  // Friends Page translations
  friends: {
    // Header
    title: {
      own: 'Your Friends',
      other: 'Friends'
    },
    connections: '{count} total connections',
    
    // Tabs  
    tabs: {
      followers: 'Followers',
      following: 'Following'
    },
    
    // Date formatting
    dates: {
      today: 'Today',
      yesterday: 'Yesterday', 
      daysAgo: '{count} days ago'
    },
    
    // Status messages
    status: {
      pendingRequest: 'Pending request',
      followingSince: 'Following since',
      pending: 'Pending'
    },
    
    // Actions
    actions: {
      accept: 'Accept',
      cancel: 'Cancel',
      unfriend: 'Unfriend'
    },
    
    // Empty states
    emptyStates: {
      followers: {
        ownNone: 'No followers yet',
        otherNone: 'No followers',
        ownMessage: 'Start sharing your golf journey to get followers!',
        otherMessage: 'This user doesn\'t have any followers yet.'
      },
      following: {
        ownNone: 'Not following anyone yet', 
        otherNone: 'Not following anyone',
        ownMessage: 'Start following other golfers to see their updates!',
        otherMessage: 'This user isn\'t following anyone yet.'
      }
    },
    
    // Pending requests modal
    pendingModal: {
      title: 'Pending Requests',
      count: '{count} pending requests',
      empty: {
        title: 'No pending requests',
        message: 'All caught up!'
      },
      requestTypes: {
        incoming: 'Wants to connect with you',
        outgoing: 'Request sent'
      },
      badges: {
        incoming: 'Incoming',
        outgoing: 'Outgoing'
      }
    }
  },

  // Profile Clubs translations
  clubs: {
    // Loading
    loading: 'Loading your golf bag...',
    
    // Header
    title: 'My Golf Bag',
    clubCount: {
      single: '1 club in your bag',
      multiple: '{count} clubs in your bag'
    },
    
    // Preferences
    preferences: {
      title: 'Bag Preferences',
      distanceUnits: 'Distance Units',
      units: {
        meters: 'Meters',
        yards: 'Yards'
      },
      handedness: 'Handedness',
      handednessOptions: {
        right: 'Right-handed',
        left: 'Left-handed'
      }
    },
    
    // Main content
    summary: {
      title: 'Track Your Clubs',
      description: 'Add distances for better club recommendations during rounds'
    },
    
    // Club list
    clubItem: {
      distanceNotSet: 'Distance not set',
      edit: 'Edit',
      setDistance: 'Set Distance'
    },
    
    // Add club dialog
    addDialog: {
      button: 'Add to My Bag',
      title: 'Select Club Type',
      selectNumber: 'Select Club Number',
      distanceLabel: 'Distance ({unit}) - Optional',
      distancePlaceholder: 'Default: {distance}m (or enter custom distance)',
      putterNote: 'Putters don\'t have carry distance',
      defaultNote: 'Smart default: {distance}m will be used if left empty',
      addButton: 'Add to My Bag',
      addButtonWithDefault: 'Add to My Bag ({distance}m default)'
    },
    
    // Edit distance dialog
    editDialog: {
      title: 'Update Club Distance',
      current: 'Current: {distance} {unit}',
      currentNotSet: 'Current: Not set {unit}',
      newDistanceLabel: 'New Distance ({unit})',
      placeholder: 'Enter distance in {unit}',
      cancel: 'Cancel',
      update: 'Update Distance'
    },

    // Distance picker dialog
    distancePicker: {
      title: 'Select Distance',
      resetToDefault: 'Reset to default ({distance}m)',
      confirm: 'Confirm'
    },

    // Empty state
    emptyState: {
      title: 'Build Your Golf Bag',
      description: 'Add your clubs to track distances and get personalized club recommendations during your rounds.',
      tip: '💡 Tip: Start with your most-used clubs'
    },
    
    // Toast messages
    messages: {
      clubAdded: 'Club added to your bag!',
      clubAddFailed: 'Failed to add club. Please try again.',
      clubRemoved: 'Club removed from your bag',
      clubRemoveFailed: 'Failed to remove club. Please try again.',
      preferencesUpdated: 'Preferences updated',
      preferencesUpdateFailed: 'Failed to update preferences. Please try again.',
      distanceUpdated: 'Club distance updated!',
      distanceUpdateFailed: 'Failed to update distance. Please try again.'
    },
    
    // Public profile specific
    public: {
      title: 'Clubs',
      subtitle: '{name}\'s equipment & distances',
      noClubDataAvailable: 'No Club Data Available',
      noPublicClubData: '{name} hasn\'t added their club information yet or has set it to private.'
    }
  },

  // Club Membership Carousel translations
  clubMembershipCarousel: {
    // Error states
    failedToLoad: 'Failed to load club memberships. Please check your database setup.',
    errorDetails: 'Error details',

    // Accessibility
    goToSlide: 'Go to slide {index}'
  },

  // Clubs Manage translations
  clubsManage: {
    // Page header
    title: 'Club Memberships',
    subtitle: 'Manage your golf club affiliations',

    // Hero stats card
    membershipSummary: 'Membership Summary',
    golfClubAffiliations: 'Your golf club affiliations',
    totalClubs: 'Total Clubs',
    adminRoles: 'Admin Roles',

    // Actions
    joinNewClub: 'Join New Club',
    joinFirstClub: 'Join Your First Club',

    // Empty state
    noClubMemberships: 'No Club Memberships',
    noMembershipsMessage: 'You haven\'t joined any golf clubs yet. Join a club to start playing with other members and unlock exclusive features!',

    // Member info labels
    memberSince: 'Member since:',
    location: 'Location:',
    courses: 'Courses:',

    // Buttons
    clubPage: 'Club Page',
    clubCard: 'Club Card',
    setAsHomeClub: 'Set as Home Club',

    // Home Club
    homeClub: 'Home Club',
    homeClubSet: 'Home club updated successfully',
    homeClubDescription: 'Your main club where you play most often',
    failedToSetHomeClub: 'Failed to set home club',

    // Section title
    yourMemberships: 'Your memberships',

    // Toast messages
    loadMembershipsFailed: 'Failed to load club memberships',

    // Club card fallbacks and stats
    unknownClub: 'Unknown Club',
    memberSincePrefix: 'Member since',
    totalMembers: 'Total Members',
    roundsPlayed: 'Rounds Played',
    activeToday: 'Active Today'
  },

  // Performance Zones translations
  performanceZones: {
    // Page header
    title: 'Performance Zones',
    subtitle: 'Records, Rankings & Achievements',
    
    // Loading states
    loading: 'Loading...',
    refreshing: 'Refreshing...',
    
    // Pull to refresh
    pullToRefresh: 'Pull to refresh',
    releaseToRefresh: 'Release to refresh',
    
    // Error states
    unableToLoadAchievements: 'Unable to load achievements',
    tryAgain: 'Try Again',
    
    // Hero stats card
    achievementSummary: 'Achievement Summary',
    eliteRecordsRankings: 'Your elite records & rankings',
    totalRecords: 'Total Records',
    localLegends: 'Local Legends', 
    topTenFinishes: 'Top 10 Finishes',
    bestPosition: 'Best Position',
    
    // Tab navigation
    records: 'Records',
    legends: 'Legends',
    topTen: 'Top 10',
    courses: 'Courses',
    
    // Records Tab
    noRecordsYet: 'No Records Yet',
    playRoundsToSetRecords: 'Play some rounds to start setting records!',
    holeRecords: 'Hole Records',
    courseRecords: 'Course Records',
    holeWithNumber: 'Hole {number}',
    
    // Record type abbreviations
    kingOfHole: 'KOH',
    queenOfHole: 'QOH', 
    holeRecord: 'HR',
    kingOfCourse: 'KOC',
    queenOfCourse: 'QOC',
    courseRecord: 'CR',
    
    // Local Legends Tab
    noLocalLegends: 'No Local Legends',
    playFrequentlyToBecomeLocalLegend: 'Play frequently at courses to become a Local Legend!',
    localLegend: 'Local Legend',
    active: 'Active',
    roundsIn90Days: '{count} rounds (90 days)',
    
    // Top 10 Tab
    noTopTenFinishes: 'No Top 10 Finishes',
    keepPlayingToBreakIntoTopTen: 'Keep playing to break into the top 10!',
    courseTotal: 'Course Total',
    score: 'Score',
    topPosition: 'Top {position}',
    
    // Starred Courses Tab
    loadingFavoriteCourses: 'Loading your favorite courses...',
    noCoursesAvailable: 'No Courses Available',
    checkBackLaterForLeaderboards: 'Check back later for course leaderboards!',
    favorites: 'Favorites',
    allCourses: 'All Courses',
    noFavoriteCoursesYet: 'No Favorite Courses Yet',
    browseAllCoursesToStar: 'Browse all courses and star your favorites to see them here',
    browseAllCourses: 'Browse All Courses',
    searchAllCourses: 'Search all courses...',
    searchFavorites: 'Search favorites...',
    searchingIn: 'Searching in {viewMode}...',
    allCoursesViewMode: 'all courses',
    favoritesViewMode: 'favorites',
    holes: 'holes',
    rounds: 'rounds',
    noCoursesFoundMatching: 'No courses found matching "{query}"',
    noCoursesAvailableEmpty: 'No courses available',
    
    // Virtual scroll list
    noItemsToDisplay: 'No items to display'
  },

  // Courses page translations
  courses: {
    // Page header
    title: 'Courses',
    subtitle: 'Your course performance history',
    subtitleShort: 'Course performance history',
    
    // Error states
    unableToLoadCourses: 'Unable to Load Courses',
    tryAgain: 'Try Again',
    backToProfile: 'Back to Profile',
    unableToLoadCourseData: 'Unable to load course data. Please try again.',
    failedToLoadRounds: 'Failed to load rounds',
    
    // Filter section
    filters: 'Filters',
    timePeriod: 'Time Period',
    sortBy: 'Sort By',
    
    // Filter options
    allTime: 'All Time',
    thisYear: 'This Year',
    thisMonth: 'This Month',
    recentlyPlayed: 'Recently Played',
    mostRounds: 'Most Rounds',
    bestAverage: 'Best Average',
    courseName: 'Course Name',
    
    // Summary stats
    coursesLabel: 'Courses',
    totalRounds: 'Rounds',
    overallAvg: 'Avg. Score',
    
    // Course card stats
    rounds: 'Rounds',
    avgScore: 'Avg Score',
    best: 'Best',
    toPar: 'To Par',
    lastPlayed: 'Last played',
    viewDetails: 'View Details',
    
    // Difficulty levels
    easy: 'Easy',
    moderate: 'Moderate',
    hard: 'Hard',
    
    // Default values
    unknownCourse: 'Unknown Course',
    locationNotSet: 'Location not set',
    
    // Empty state
    noCoursesPlayedYet: 'No courses played yet',
    startPlayingRoundsToBuild: 'Start playing rounds to build your course history',
    startPlaying: 'Start Playing',
    
    // Public profile specific
    playedCourses: '{name}\'s played courses'
  },

  // Trophy Room page translations
  trophyRoom: {
    // Page header
    title: 'Trophy Room',
    subtitle: 'Your golf achievements',
    
    // Loading state
    loadingYourTrophies: 'Loading your trophies...',
    
    // Hero section
    golfAchievements: 'Golf Achievements',
    trackDevelopmentMilestones: 'Track your golf development milestones',
    earned: 'Earned',
    available: 'Available',
    
    // Tab navigation
    overview: 'Overview',
    earnedTab: 'Earned',
    
    // Overview tab sections
    recentlyEarned: 'Recently Earned',
    achievementTiers: 'Achievement Tiers',
    availableTrophies: 'Available Trophies',
    
    // Earned tab
    achievementsWithCount: '{tierName} Achievements ({count})',
    
    // Tier information
    tiers: {
      bronze: {
        name: 'Bronze',
        description: 'Beginner achievements'
      },
      silver: {
        name: 'Silver', 
        description: 'Intermediate achievements'
      },
      gold: {
        name: 'Gold',
        description: 'Advanced achievements'
      },
      platinum: {
        name: 'Platinum',
        description: 'Elite achievements'
      }
    },
    
    // Empty state
    noAchievementsYet: 'No achievements yet. Keep playing to earn your first trophy!',
    startPlayingToEarn: 'Start playing rounds to earn achievements!',

    // Progress
    overallProgress: 'Overall Progress',
    progress: 'Progress',

    // League tiers (unified quest/badge system)
    leagues: {
      rookie: {
        name: 'Rookie',
        description: 'Beginning your golf journey'
      },
      regular: {
        name: 'Regular',
        description: 'Building solid foundations'
      },
      veteran: {
        name: 'Veteran',
        description: 'Experienced player achievements'
      },
      legend: {
        name: 'Legend',
        description: 'Elite mastery achievements'
      }
    }
  },

  // Badge system translations (unified with quests)
  badges: {
    collection: 'Badge Collection',
    earned: 'Earned',
    notEarned: 'Not Yet Earned',
    tierRookie: 'Rookie Badge',
    tierRegular: 'Regular Badge',
    tierVeteran: 'Veteran Badge',
    tierLegend: 'Legend Badge',
    earnedOn: 'Earned on',
    totalEarned: 'Total Earned',
    questBadges: 'Quest Badges',
    leagueBadges: 'League Badges',
    recentlyEarned: 'Recently Earned',
    lockedBadges: 'Locked Badges',
    completeQuestToUnlock: 'Complete quests to unlock badges'
  },

  playNew: {
    // Loading & Status
    loadingCourses: 'Loading your courses...',
    noCourses: 'No courses found',
    selectCourse: 'Select a course to get started',
    
    // Recent Rounds Section
    noRecentRounds: 'No Recent Rounds',
    recentRounds: 'Recent Rounds',
    viewAllRounds: 'View All Rounds',
    
    // Course Stats Section
    myCourseStats: 'My Course Stats',
    bestScore: 'Best Score',
    totalRounds: 'Total Rounds',
    averageScore: 'Avg Score',
    
    // Actions Section
    startRoundAt: 'Start Round at {courseName}',
    quickRound: 'Quick Round',
    scheduleFutureRound: 'Schedule Future Round',
    
    // Empty State
    startFirstRound: 'Start your first round to see stats',
    
    // Course Filter Section
    searchCourses: 'Search courses...',
    filterCourses: 'Filter courses',
    allCourses: 'All Courses',
    
    // Loading States
    loadingStats: 'Loading course statistics...',
    loadingRecentRounds: 'Loading recent rounds...',
    
    // Scheduled Rounds
    upcomingRounds: 'Upcoming Rounds',
    start: 'Start',
    today: 'Today',
    
    // Dynamic Stats Messages
    selectCourseForStats: 'Select a course to view your stats.',
    selectCourseForHistory: 'Select a course to view your round history.',
    averageScoreAt: 'Your average score at {courseName} is {score}.',
    noRoundsPlayedAt: 'No rounds played at {courseName} yet.',
    youPlayedRoundsAt: 'You\'ve played {count} rounds at {courseName}.',
    
    // Confirmation & Alert Messages
    cancelScheduledRound: 'Are you sure you want to cancel this scheduled round?',
    failedToCancelRound: 'Failed to cancel scheduled round',
    failedToStartRound: 'Failed to start round',
    
    // Multiplayer
    startMultiplayerRound: 'Start Multiplayer Round',
    createMultiplayer: 'Create Game',
    joinGame: 'Join Game',

    // My Bookings
    myBookings: 'My Bookings',
    upcomingBookingsCount: 'You have {count} upcoming booking(s)',
    noUpcomingBookings: 'No upcoming tee times',

    // Tabs
    favorites: 'Favorites',
    courses: 'Courses',

    // Empty States & Fallbacks
    unknownCourse: 'Unknown Course',
    unknownClub: 'Unknown Club',
    unknownLocation: 'Unknown Location',
    startTrackingRounds: 'Start by selecting a course to begin tracking your rounds',
    selectCourseViewStats: 'Select a course to view your stats.',
    selectCourseViewHistory: 'Select a course to view your round history.',

    // Error Messages
    failedToLoadRounds: 'Failed to load rounds data',
    failedToLoadCourses: 'Failed to load your recent courses'
  },

  // Ongoing Games Section
  ongoingGames: {
    continuePlaying: 'Continue Playing',
    justNow: 'Just now',
    hoursAgo: '{count}h ago',
    minutesAgo: '{count}m ago',
    daysAgo: '{count}d ago',
    resume: 'Resume',
    hole: 'Hole',
    holes: 'holes',
    players: 'players',
    solo: 'Solo',
    failedToLoadGame: 'Failed to load game',
    joinCodeCopied: 'Join code copied!',
    // Scoring formats (golf terms - kept in English per terminology guidelines)
    stableford: 'Stableford',
    matchPlay: 'Match Play',
    strokePlay: 'Stroke Play'
  },

  // Selection Components
  selection: {
    // Club Selection
    selectClub: 'Select Club',
    findYourGolfCourse: 'Find your golf course',
    findYourClub: 'Find your club...',
    nearbyClubs: 'Nearby Clubs',
    allClubs: 'All Clubs',
    findNearbyClubs: 'Find nearby clubs',
    findingLocation: 'Finding location...',
    loadingRecentClubs: 'Loading recent clubs...',
    locationAccessDenied: 'Location access denied',
    locationPermissionsMessage: 'Please enable location permissions in your browser settings to find nearby clubs.',
    searchResults: 'Search Results',

    // Course Selection
    selectCourse: 'Select Course',
    golfClub: 'Golf Club',
    unknown: 'Unknown',

    // Holes Selection
    holesToPlay: 'Holes to Play',
    singleLoop: 'Single loop',
    playNineTwice: 'Play the 9 twice',
    frontNine: 'Front Nine',
    backNine: 'Back Nine',
    fullRound: 'Full Round',

    // Tee Selection
    startingTee: 'Starting Tee',
    selectYourStartingTee: 'Select your starting tee',
    distanceUnavailable: 'Distance unavailable',
    noTeesAvailable: 'No tees available for this course',
    noTeesAvailableDescription: "This course hasn't had its tee boxes configured yet. Contact your club admin to set them up.",
    rating: 'Rating',
    slope: 'Slope',
    notAvailable: 'N/A',
    chooseHowManyHoles: 'Choose how many holes to play',
    nineHoles: '9 Holes',
    eighteenHoles: '18 Holes',

    // Course Selection
    loadingCourses: 'Loading courses...',
    noCoursesFound: 'No courses found for this club',
    noCoursesFoundDescription: 'Try a shorter name or browse all available courses.',
    chooseCourseToPlay: 'Choose a course to play',
    holes: 'holes'
  },

  // Game Settings translations
  gameSettings: {
    // Scoring Types
    scoringType: 'Scoring Type',
    noScoringTypesAvailable: 'No scoring types available',
    noScoringTypesDescription: "This event doesn't have any scoring formats configured yet.",
    strokePlay: 'Stroke Play',
    strokePlayDescription: 'Lowest total score wins',
    stableford: 'Stableford',
    stablefordDescription: 'Points-based scoring',
    matchPlay: 'Match Play',
    matchPlayDescription: 'Hole-by-hole competition',

    // Game Modes
    gameMode: 'Game Mode',
    solo: 'Solo',
    soloDescription: 'Play by yourself',
    individualPlay: 'Individual Play',
    individualPlayDescription: 'Play with friends, individual scores',
    scramble: 'Scramble',
    scrambleDescription: 'Team best shot',
    fourBall: 'Four-Ball',
    fourBallDescription: 'Best score per hole',
    foursomes: 'Foursomes',
    foursomesDescription: 'Partners alternate',
    bestBall: 'Best Ball',
    alternateShot: 'Alternate Shot',
    aggregate: 'Aggregate',
    team: 'Team',
    combinedScore: 'Combined Score',

    // Course Confirmation - Header
    roundSettings: 'Round Settings',
    customizeRoundAt: 'Customize your round at',

    // Course Confirmation - Hero Card
    holesToPlay: 'Holes to Play',
    par: 'Par',

    // Holes Labels
    frontNine: 'Front Nine',
    backNine: 'Back Nine',
    nineHoles: '9 Holes',
    eighteenHolesTwice: '18 Holes (9 played twice)',
    eighteenHoles: '18 Holes',

    // Input Mode Selection
    selectScorecardStyle: 'Select Scorecard Style',
    quickEntry: 'Score',
    gpsPlay: 'GPS + Score',
    quickEntryDescription: 'Quick score entry without the GPS map. Perfect for fast rounds or when you just want to track your scores.',
    gpsPlayDescription: 'Full experience with GPS map and score entry. Get precise distances and detailed shot tracking.',

    // Round Details Section
    roundDetails: 'Round Details',
    dateAndTime: 'Date & Time',

    // Round Type Section
    roundType: 'Round Type',
    teeSelection: 'Tee Selection',
    // scoringType: 'Scoring Type', // Removed duplicate - already defined above in Scoring Types section
    handicapAllowance: 'Handicap Allowance',

    // Loading States
    selectDate: 'Select',
    nowAuto: 'Now (auto)',
    loading: 'Loading...',
    noTees: 'No tees',
    configuring: 'Configuring...',
    continue: 'Continue',

    // Modal Titles
    startingTee: 'Starting Tee',
    roundDate: 'Round Date',

    // Calendar - Day Names
    daySun: 'SUN',
    dayMon: 'MON',
    dayTue: 'TUE',
    dayWed: 'WED',
    dayThu: 'THU',
    dayFri: 'FRI',
    daySat: 'SAT',

    // Time Selection
    timeCourseTimezone: 'Time (Course timezone)',
    useNow: 'Use Now',
    selected: 'Selected',
    ok: 'Ok',

    // Handicap Allowance Modal
    fullHandicap: 'Full handicap',
    usgaRecommended: 'USGA recommended',
    ofHandicapApplied: 'of handicap applied',
    applyAllowance: 'Apply'
  },

  // Score Entry translations
  scoreEntry: {
    // Hole labels
    hole: 'Hole',
    par: 'Par',
    completed: 'Completed',
    active: 'Active',
    saved: 'Saved',
    saving: 'Saving...',
    error: 'Error',

    // Validation
    scoreMustBeAtLeast1: 'Score must be at least 1',
    scoreCannotExceed12: 'Score cannot exceed 12',
    pleaseEnterValidNumber: 'Please enter a valid number',

    // GPS Tracking
    gpsTracking: 'GPS Tracking',
    front: 'Front',
    middle: 'Middle',
    back: 'Back',
    shotNumber: 'Shot #',
    lastShot: 'Last Shot:',
    markShot: 'Mark Shot',
    expand: 'Expand',
    minimize: 'Minimize',
    gpsNotAvailable: 'GPS data not available for this hole',

    // UI
    tapToEnter: 'Tap to enter',
    quickStats: 'Quick Stats'
  },

  // Team Management translations
  teamManagement: {
    // Team labels
    team: 'Team',
    teamHandicap: 'Team Handicap',
    scorecard: 'Scorecard',
    combinedScore: 'Combined Score',
    holesComplete: 'Holes Complete',
    noTeamScoresYet: 'No team scores yet',
    scoresWillAppear: 'scores will appear here as they\'re entered',
    extraHoles: 'Extra Holes',
    score: 'Score',

    // Player assignment
    noPlayersAssigned: 'No players assigned',
    unassignedPlayers: 'Unassigned Players',
    selectPlayersFor: 'Select Players for',
    selectPlayersToAdd: 'Select players to add to this team',
    handicapLabel: 'Handicap:',

    // Actions
    addTeam: 'Add Team',
    assignTeams: 'Assign Teams',
    clearAll: 'Clear All',
    editPlayers: 'Edit Players',
    addPlayers: 'Add Players',
    cancel: 'Cancel',
    confirm: 'Confirm',

    // Status
    host: 'Host',
    ready: 'Ready',
    noTeamsCreatedYet: 'No teams created yet',
    waitingForHostToCreateTeams: 'Waiting for host to create teams',

    // Strategy Modal
    chooseTeamAssignmentStrategy: 'Choose Team Assignment Strategy',
    selectHowToDistribute: 'Select how you want to distribute players across teams',
    random: 'Random',
    randomDescription: 'Randomly shuffle and distribute players evenly across all teams',
    handicapBased: 'Handicap-Based (Snake Draft)',
    handicapBasedDescription: 'Sort by handicap and use snake draft to create competitive teams',
    balanced: 'Balanced',
    balancedDescription: 'Distribute players to equalize total handicap across teams',

    // Toast messages
    maxTeamsReached: 'Maximum number of teams reached',
    teamDeleted: 'Team deleted',
    failedToDeleteTeam: 'Failed to delete team',
    teamUpdated: 'Team updated',
    failedToUpdateTeam: 'Failed to update team',
    onlyHostCanAssignTeams: 'Only the host can assign teams',
    createAtLeast2Teams: 'Create at least 2 teams first',
    teamsRandomlyAssigned: 'Teams randomly assigned',
    failedToAssignTeams: 'Failed to assign teams',
    teamsBalancedByHandicap: 'Teams balanced by handicap',
    teamsBalancedEvenly: 'Teams balanced evenly',
    failedToAutoBalance: 'Failed to auto-balance teams',
    onlyHostCanClearTeams: 'Only the host can clear teams',
    allTeamsCleared: 'All teams cleared',
    failedToClearTeams: 'Failed to clear teams'
  },

  // Sharing translations
  sharing: {
    // QR Code
    qrCodeAlreadyAvailable: 'QR code is already available!',
    qrCodeGenerated: 'QR code generated successfully!',
    failedToGenerateQRCode: 'Failed to generate QR code',
    scanToJoinGame: 'Scan to join the game',
    playersCanScanQRCode: 'Players can scan this QR code with their phone camera',
    scans: 'scans',
    expires: 'Expires',
    downloadQRCode: 'Download QR Code',
    qrCodeDownloaded: 'QR code downloaded!',
    failedToDownloadQRCode: 'Failed to download QR code',

    // Shareable Link
    shareableLinkAlreadyAvailable: 'Shareable link is already available!',
    shareableLinkCreated: 'Shareable link created!',
    failedToCreateShareableLink: 'Failed to create shareable link',
    shareableLink: 'Shareable Link',
    uses: 'uses',
    joinUrl: 'Join URL:',
    copyLink: 'Copy Link',
    copied: 'Copied!',
    share: 'Share',
    linkCopied: 'Link copied to clipboard!',
    failedToCopyLink: 'Failed to copy link',
    sharedSuccessfully: 'Shared successfully!',
    failedToShare: 'Failed to share link',
    linkDeactivated: 'Link deactivated',
    failedToDeactivateLink: 'Failed to deactivate link',

    // Empty state
    loadingShareOptions: 'Loading share options...',
    noSharingOptionsYet: 'No sharing options yet',
    generateQROrLink: 'Generate a QR code or shareable link',
    playersCanScanOrClick: 'Players can scan or click to join your game'
  },

  // Join Game translations
  joinGame: {
    // Validation toasts
    failedToValidateJoinCode: 'Failed to validate join code. Please try again.',
    invalidJoinCode: 'Invalid join code. Please check the code and try again.',
    gameNoLongerAccepting: 'This game is no longer accepting players',
    alreadyInGame: 'You\'re already in this game!',
    failedToLoadPlayers: 'Failed to load player list',
    pleaseSignInToJoin: 'Please sign in to join a game',
    joinedSuccessfully: 'Joined game successfully!',
    failedToJoinGame: 'Failed to join game. Please try again.',
    // UI labels
    signInRequired: 'Sign In Required',
    signInToJoinMultiplayer: 'Please sign in to join multiplayer games',
    signIn: 'Sign In',
    joiningGame: 'Joining Game...',
    pleaseWaitJoining: 'Please wait while we add you to the game',
    selectYourIdentity: 'Select Your Identity',
    game: 'Game',
    course: 'Course',
    format: 'Format',
    gameFormat: 'Game Format',
    chooseWhichPlayer: 'Choose which player you are:',
    back: 'Back',
    noAvailableSlots: 'No Available Slots',
    allSlotsClaimedMessage: 'All player slots have been claimed. Please check with the game host.',
    backToJoinCode: 'Back to Join Code',
    joinMultiplayerGame: 'Join Multiplayer Game',
    enterCodeToJoin: 'Enter the game code to join your friends',
    enterGameCode: 'Enter Game Code',
    gameCode: 'Game Code',
    enterCodePlaceholder: 'Enter 6-character code',
    validating: 'Validating...',
    continue: 'Continue',
    // Shareable link validation (JoinViaLink page)
    invalidLink: 'Invalid Link',
    linkNotFoundOrInactive: 'This link is not found or has been deactivated',
    linkExpired: 'Link Expired',
    linkLimitReached: 'Link Limit Reached',
    gameClosed: 'Game Closed',
    gameNotFound: 'Game Not Found',
    failedToValidateLink: 'Failed to validate link. Please try again.',
    validatingLink: 'Validating Link...',
    pleaseWait: 'Please wait...',
    enterCodeManually: 'Enter Code Manually',
    backToPlay: 'Back to Play',
    joinAGame: 'Join a Game',
    chooseHowToJoin: 'Choose how you want to join',
    typeThe6CharCode: 'Type the 6-character code',
    scanQrCode: 'Scan QR Code',
    useCameraToScanQr: 'Use camera to scan the game\'s QR code',
    joinViaLink: 'Join via Link',
    gotSharedLinkDescription: 'Got a shared link? Just tap it and you\'ll be redirected here automatically',
    askGameHostToShare: 'Ask the game host to share the code, QR code, or link with you',
    // QR scanner screen
    scanQrCodeTitle: 'Scan QR Code',
    pointAtQrCode: 'Point at QR code to scan',
    qrScannerTip: "Tip: You can also use your phone's camera app to scan the QR code - it will open the join link automatically",
    enterCodeManuallyLink: 'Or enter code manually',
    initializingCamera: 'Initializing camera...'
  },

  // Game Invitations translations
  invitations: {
    // Toast messages
    invitationAccepted: 'Invitation accepted! Joining game...',
    invitationDeclined: 'Invitation declined',
    failedToAccept: 'Failed to accept invitation',
    failedToDecline: 'Failed to decline invitation',
    // User labels
    unknownUser: 'Unknown User',
    invitedYouToPlay: 'invited you to play golf',
    // Status
    new: 'New',
    pending: 'Pending',
    accepted: 'Accepted',
    declined: 'Declined',
    expired: 'Expired',
    // Game details
    multiplayer: 'Multiplayer',
    format: 'Format',
    date: 'Date',
    // Actions
    accept: 'Accept',
    decline: 'Decline',
    // Card header
    joinGames: 'Join Games',
    // Loading states
    loadingInvitations: 'Loading invitations...',
    noGameInvitations: 'No game invitations',
    invitationsWillAppear: 'Invitations from friends will appear here',
    // Tabs
    history: 'History',
    noPendingInvitations: 'No pending invitations',
    noInvitationHistory: 'No invitation history'
  },

  // Schedule Round Modal translations
  scheduling: {
    // Day Names (short)
    daySu: 'Su',
    dayMo: 'Mo',
    dayTu: 'Tu',
    dayWe: 'We',
    dayTh: 'Th',
    dayFr: 'Fr',
    daySa: 'Sa',
    // Month Names
    monthJanuary: 'January',
    monthFebruary: 'February',
    monthMarch: 'March',
    monthApril: 'April',
    monthMay: 'May',
    monthJune: 'June',
    monthJuly: 'July',
    monthAugust: 'August',
    monthSeptember: 'September',
    monthOctober: 'October',
    monthNovember: 'November',
    monthDecember: 'December',
    // Modal Header
    scheduleFutureRound: 'Schedule Future Round',
    planYourNextGolfRound: 'Plan your next golf round',
    
    // Action Buttons
    cancel: 'Cancel',
    scheduling: 'Scheduling...',
    confirmAndSchedule: 'Confirm & Schedule',
    
    // Form Labels
    course: 'Course',
    date: 'Date',
    teeTime: 'Tee Time',
    roundType: 'Round Type',
    teeSelection: 'Tee Selection',
    notesOptional: 'Notes (Optional)',
    
    // Date Display
    selected: 'Selected:',
    
    // Round Type Options
    nineHoles: '9 Holes',
    eighteenHoles: '18 Holes',
    quickRound: 'Quick round',
    fullRound: 'Full round',
    courseHasHoles: 'Course has {count} holes',
    
    // Loading States
    loadingTees: 'Loading tees...',
    
    // Default Tee Options
    regularTees: 'Regular Tees',
    championshipTees: 'Championship Tees',
    mensTees: 'Men\'s Tees',
    forwardTees: 'Forward Tees',
    womensTees: 'Women\'s Tees',
    
    // Placeholder Text
    addNotesPlaceholder: 'Add any notes about your planned round...',
    
    // Validation Errors
    pleaseSelectFutureDate: 'Please select a future date',
    pleaseSelectTeeTime: 'Please select a tee time',
    pleaseSelectTees: 'Please select tees to play from',
    failedToScheduleRound: 'Failed to schedule round'
  },
  
  // Activity Trend Chart translations
  activityTrend: {
    summaryLabel: '{rounds} rounds and {hours} hours of golf this year',
    title: 'Activity Trend',
    thisYear: 'This year',
    noDataAvailable: 'No data available',
    averagePerMonth: 'Average per month',
    rounds: 'rounds'
  },

  // Add Past Round translations
  addPastRound: {
    // Page title and headers
    title: 'Add Past Round',
    addScore: 'Add score',
    subtitle: 'Add a past round to track your progress',
    helpText: 'Adding past rounds helps track your progress and improve your dashboard analytics.',

    // Club Selection section
    clubSelectionOptional: 'Club Selection (Optional)',
    searchForClub: 'Search for Club',
    searchClubsPlaceholder: 'Start typing to search clubs (optional)...',
    noClubsFound: 'No clubs found. Try a different search term.',
    courses: 'courses',

    // Course Selection section
    courseSelection: 'Course Selection',
    courseSelectionFiltered: 'Course Selection (Filtered by: {clubName})',
    searchResults: '{count} search results',
    selectCourse: 'Select Course',
    loadingCourses: 'Loading courses...',
    selectACourse: 'Select a course',
    selectCourseOrAddNew: 'Select a course or add new',
    addNewCourse: 'Add New Course',
    roundsPlayed: 'rounds played',

    // Course Search section
    searchForCourse: 'Search for Course *',
    searchCoursesPlaceholder: 'Start typing to search courses...',
    noCoursesFound: 'No courses found. Try a different search term.',

    // Manual course entry
    courseNameIfNotFound: 'Course Name (if not found above)',
    courseNamePlaceholder: 'e.g., Pine Valley Golf Club',
    location: 'Location',
    locationPlaceholder: 'e.g., Pine Valley, NJ',

    // Round Details section
    roundDetails: 'Round Details',
    roundDate: 'Round Date',
    course: 'Course',
    startingTee: 'Starting Tee',
    select: 'Select',
    date: 'Date *',
    totalScore: 'Total Score *',
    totalScorePlaceholder: 'e.g., 85',
    holes: 'Holes',
    nineHoles: '9 Holes',
    eighteenHoles: '18 Holes',
    coursePar: 'Course Par',
    courseParPlaceholder: '72',
    notesOptional: 'Notes (Optional)',
    notesPlaceholder: 'Any additional notes about this round...',

    // Course Rating section
    courseRatingSection: 'Course Rating',
    courseRating: 'Course Rating',
    slopeRating: 'Slope Rating',
    fillRoundDetails: 'Fill round details',

    // Score & Handicap section
    scoreAndHandicap: 'Score & Handicap',
    grossScore: 'Gross Score',
    enterScore: 'Enter score',
    handicapIndex: 'Handicap Index',
    fromYourProfile: 'From your profile',
    enter: 'Enter',
    grossScoreHint: 'Enter your 18-hole gross score and handicap',

    // Net Score section
    yourNetScore: 'Your Net Score',
    netScoreFormula: 'Gross {gross} − Course HCP {courseHcp} = Net {net}',
    courseHcpFormula: 'Course HCP: ({handicap} × {slope}/113) + ({rating} − {par})',

    // Actions and states
    addingRound: 'Adding Round...',
    addingScore: 'Adding score...',
    addPastRound: 'Add Past Round',
    addScoreButton: 'Add score',

    // Calendar
    calendar: {
      ok: 'Ok',
      dayNames: ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
    },

    // Modal titles
    modal: {
      selectClub: 'Select Club',
      findYourGolfCourse: 'Find your golf course',
      selectCourse: 'Select Course',
      startingTee: 'Starting Tee'
    },

    // Error messages
    selectCourseError: 'Please select a course from the search or enter a course name',
    failedToCreateCourse: 'Failed to create course',
    failedToAddRound: 'Failed to add past round',
    pastRoundAddedSuccess: 'Past round added successfully!',

    // Fallback values
    unknownCourse: 'Unknown Course',
    unknownClub: 'Unknown Club',
    unknownLocation: 'Unknown Location',
    unknownCity: 'Unknown City',
    unknownCountry: 'Unknown Country'
  },

  // Browse Clubs page translations
  browseClubs: {
    title: 'Club Search',
    subtitle: 'Belgium',
    resultsCount: 'results',
    searchPlaceholder: 'Search clubs by name or location...',
    filters: {
      title: 'Filters',
      clearAll: 'Clear all',
      sortBy: 'Sort by',
      sortOptions: {
        name: 'Name',
        courses: 'Course Count',
        location: 'Location'
      },
      hasWebsite: 'Has Website',
      websiteOptions: {
        all: 'All clubs',
        yes: 'With website',
        no: 'No website'
      },
      hasCourses: 'Has Courses',
      coursesOptions: {
        all: 'All clubs',
        yes: 'With courses',
        no: 'No courses'
      }
    },
    results: {
      clubsFound: 'clubs found',
      sortedBy: 'Sorted by',
      status: {
        open: 'Open'
      },
      courseCount: {
        single: 'course',
        plural: 'courses'
      }
    },
    emptyState: {
      title: 'No clubs found',
      messageWithFilters: 'Try adjusting your search or filters',
      messageNoFilters: 'No clubs available in the selected area',
      clearFiltersButton: 'Clear filters'
    },
    loadMore: 'Load More Clubs'
  },

  // Club Course Selection page translations
  clubCourseSelection: {
    selectCourse: 'Select Course',
    chooseCourseMultiplayer: 'Choose a course for your multiplayer game',
    chooseCourseSolo: 'Choose a course to play',
    availableCourses: 'Available Courses',
    toasts: {
      mustBeLoggedIn: 'You must be logged in to start a round',
      roundStarted: 'Round started!',
      roundConfigured: 'Round configured! Add players or start.',
      failedToConfigure: 'Failed to configure round:'
    }
  },

  // Play Solo page translations
  playSolo: {
    title: 'Start Solo Round',
    subtitle: 'Find your club and set up your round',
    tabs: {
      favorites: 'Favorites',
      courses: 'Courses'
    },
    search: {
      results: 'Search Results',
      nearbyClubs: 'Nearby Clubs',
      backToRecent: '← Back to Recent',
      allClubs: 'All Clubs'
    },
    loading: {
      recentClubs: 'Loading recent clubs...',
      clubs: 'Loading clubs...',
      favorites: 'Loading favorites...'
    },
    buttons: {
      viewAllClubs: 'View All Clubs'
    },
    emptyStates: {
      noRecentClubs: {
        title: 'No recent clubs',
        message: 'Start playing to see your recent clubs here'
      },
      noSearchResults: {
        title: 'No clubs found',
        message: 'Try a different search term'
      },
      noNearbyClubs: {
        title: 'No nearby clubs found',
        message: 'Try searching for a specific club name'
      },
      noFavorites: {
        title: 'No favorites yet',
        message: 'Play some rounds to see your favorite courses here'
      }
    },
    toasts: {
      locationFound: 'Location found! Showing nearby clubs sorted by distance.'
    }
  },

  // Solo Score Entry page translations
  soloScoreEntry: {
    title: 'Score Entry',
    loading: {
      round: 'Loading your round...',
      completing: 'Completing Round...'
    },
    errors: {
      loadRound: 'Failed to load round',
      roundNotFound: 'Round not found',
      loadRoundData: 'Failed to load round data',
      missingRoundId: 'Error: Round ID is missing',
      roundNotFoundDescription: "The round you're looking for doesn't exist or you don't have access to it.",
      unknownCourse: 'Unknown Course',
      enterScoreFirst: 'Please enter a score before completing the hole',
      markShotFailed: 'Failed to mark shot. Please try again.'
    },
    gps: {
      activeTitle: 'GPS Distance Tracking Active',
      fallbackMessage: 'GPS tracking unavailable for this course'
    },
    roundInfo: {
      title: 'Round Details',
      holes: 'Holes',
      tees: 'Tees',
      strokePlay: 'Stroke Play',
      matchPlay: 'Match Play'
    },
    shots: {
      marked: 'Shot {shotNumber} marked for hole {holeNumber}!',
      markedManual: 'Shot {shotNumber} marked for hole {holeNumber}! (Manual entry - GPS unavailable)'
    },
    completion: {
      holeComplete: 'Hole {hole}: {scoreLabel}! Moving to hole {nextHole}',
      roundComplete: 'Round completed! Preparing your analysis...',
      roundCompleted: 'Round completed!',
      completeButton: 'Complete Round',
      completingButton: 'Completing Round...'
    },
    scoreLabels: {
      eagle: 'Eagle',
      albatross: 'Albatross',
      birdie: 'Birdie',
      par: 'Par',
      bogey: 'Bogey',
      double: 'Double',
      triplePlus: 'Triple+'
    },
    holeStats: {
      fairwayHit: 'Hit',
      fairwayMiss: 'Miss',
      fairwayLabel: 'FW: {status}',
      girYes: 'Yes',
      girNo: 'No',
      girLabel: 'GIR: {status}'
    },
    holeActions: {
      unlockHole: 'Unlock hole for editing',
      lockHole: 'Lock hole to prevent accidental inputs',
      locked: 'Locked',
      unlocked: 'Unlocked',
      removeShot: 'Remove shot',
      processing: 'Processing...',
      markShot: 'Mark Shot',
      tapToEnterStrokes: 'Tap to enter strokes',
      continuePlayingActive: 'Click to continue playing',
      waitingToStart: 'Waiting to start'
    },
    
    // Comprehensive UI strings for scorecard components
    scorecard: {
      holeNumber: 'Hole {number}',
      parNumber: 'Par {number}',
      totalScore: 'Total Score',
      toPar: 'To Par', 
      strokes: 'Strokes',
      strokesLabel: 'Strokes',
      penaltyStrokes: 'Penalties',
      putts: 'Putts',
      puttsLabel: 'Putts: {count}',
      penaltiesLabel: 'Penalties: {count}',
      greenInRegulation: 'Green in Regulation (GIR)',
      greenInRegulationShort: 'GIR',
      fairway: 'Fairway',
      fairwayNotApplicable: '(N/A for Par 3)',
      yes: 'Yes',
      no: 'No',
      completeHole: 'Complete Hole {number}',
      completeHoleWithScore: 'Complete Hole {number} ({score})',
      completeHoleNoScore: 'Complete Hole {number} (No Score)',
      ready: 'Ready',
      selectStrokes: 'Select Strokes',
      gpsDistances: 'GPS Distances',
      viewCourseMap: 'Map',
      front: 'Front',
      middle: 'Middle',
      back: 'Back',
      markShotToSeeDistances: 'Mark shot to see distances',
      shotTracking: 'Shot Tracking',
      shotNumber: 'Shot #{number}',
      readyToMarkFirstShot: 'Ready to mark first shot',
      shotHistory: 'Shot History',
      
      // Club editing dialog
      editClubTitle: 'Edit Club for Shot #{number}',
      editClubDescription: 'Select the club you used for this shot. This will update your shot history.',
      currentClub: 'Current club:',
      selected: '(Selected)',
      showAllClubs: 'Show all clubs...',
      backToSuggested: '← Back to suggested clubs',
      clickToEditClub: 'Click to edit club',

      // Score format labels
      gross: 'Gross',
      net: 'Net',
      total: 'Total',
      points: 'Points',
      match: 'Match',
      grossScore: 'Gross Score',
      netEaglePlus: 'Net Eagle+',
      netBirdie: 'Net Birdie',
      netPar: 'Net Par',
      netBogey: 'Net Bogey',

      // Match play
      allSquare: 'All Square',
      starting: 'Starting',
      won: 'Won',
      lost: 'Lost',
      tied: 'Tied',
      result: 'Result',
      headToHead: 'Head-to-head competition',

      // Game formats
      strokePlay: 'Stroke Play',
      stableford: 'Stableford',
      matchPlay: 'Match Play',
      scramble: 'Scramble',
      fourBall: 'Four-Ball',
      foursomes: 'Foursomes',
      individual: 'Individual',

      // Actions & UI
      saving: 'Saving...',
      saveAllScores: 'Save All Scores',
      processing: 'Processing...',
      completeRound: 'Complete Round',
      tapToEnter: 'Tap to enter',
      expand: 'Expand',
      minimize: 'Minimize',
      fairwayHit: 'Hit',
      fairwayShort: 'Short',
      fairwayLeft: 'Left',
      fairwayRight: 'Right',

      // Messages
      settingsUpdated: 'Settings updated successfully!',
      failedToUpdateSettings: 'Failed to update settings',
      scoresSaved: 'Scores saved!',
      errorSavingScores: 'Error saving scores',
      errorCompletingRound: 'Error completing round',
      failedToDiscardRound: 'Failed to discard round. Please try again.',
      failedToLoadGame: 'Failed to load game data',
      pleaseEnterStrokes: 'Please enter strokes before locking',
      gpsSignalUnavailable: 'GPS Signal Unavailable',

      // Progress display
      holeOfTotal: 'Hole {current} of {total}',
      percentComplete: '{percent}% Complete',
      noHandicapData: 'No handicap data',
      holesCount: '{completed}/{total} holes',

      // Hole selector modal
      selectHole: 'Select Hole',
      par: 'Par',
      strokeIndex: 'S.I.',
      tapAnyHole: 'Tap any hole to enter score',
      viewScorecard: 'Scorecard',
      finishRound: 'Finish Round',
      continueScoring: 'Continue Scoring',
      effectivePar: 'Effective Par',

      // Live scorecard modal
      liveScorecard: 'Live Scorecard',
      closeScorecard: 'Close Scorecard',
      teamHandicap: 'Team Handicap',
      usgaScrambleFormula: 'USGA Scramble Formula',
      combined50: 'Combined 50%',
      playersCount: '{count} players',
      holes: 'Holes',
      score: 'Score',

      // On Green - Add Putts feature
      onGreen: 'On the Green',
      addPuttsToComplete: 'Add your putts to complete the hole',
      expectedScore: 'Expected Score',
      putt: 'putt'
    },

    // GPS fallback messages
    gpsFeatures: {
      notAvailableTitle: 'GPS Features Not Available',
      notAvailableMessage: 'GPS distance tracking isn\'t available for {courseName}. You can still use our enhanced traditional scorecard with smart stats and performance analytics.'
    }
  },

  // Multiplayer lobby translations
  multiplayerLobby: {
    players: 'Players',
    player: 'Player',
    teams: 'Teams',
    currentPlayers: 'Current Players',
    host: 'Host',
    ready: 'Ready',
    addPlayer: 'Add Player',
    addPlayers: 'Add Players',
    cancel: 'Cancel',
    done: 'Done',
    friends: 'Friends',
    recent: 'Recent',
    manual: 'Manual',
    searchFriends: 'Search friends...',
    loadingFriends: 'Loading friends...',
    noFriendsFound: 'No friends found',
    addFriendsHint: 'Add friends through the Social tab',
    noRecentPlayers: 'No recent players',
    recentPlayersHint: 'Players you\'ve played with will appear here',
    addGuestPlayer: 'Add a player by name (guest player)',
    enterPlayerName: 'Enter player name...',
    add: 'Add',
    added: 'Added',
    startingTee: 'Starting Tee',
    selectTee: 'Select a tee...',
    handicap: 'Handicap',
    playingHcp: 'Playing HCP',
    save: 'Save'
  },

  tournamentRoundCompletion: {
    title: 'Tournament Complete',
    matchResult: 'Match Result',
    backToTournament: 'Back to Tournament',
    goBack: 'Go Back',
    saveAndReturn: 'Save & Return',
    saving: 'Saving...',
    done: 'Done',
    matchOutcome: {
      victory: 'Victory',
      defeat: 'Defeat',
      halved: 'Halved',
    },
    loading: {
      scanningScorecard: 'Scanning Your Scorecard',
      analysisComplete: 'Analysis Complete!',
      somethingWentWrong: 'Something went wrong',
      percentComplete: '% complete',
      attemptingRecovery: 'Attempting to recover data... This may take a moment.',
    },
    errors: {
      failedToLoad: 'Failed to Load Round',
      errorBody: 'Something went wrong loading your round. Please try again, or go back to the tournament.',
      retry: 'Retry',
      retrying: 'Retrying...',
    },
    toasts: {
      roundSavedWithStats: 'Round saved to statistics!',
      roundSavedNoStats: 'Round saved (not included in statistics)',
      failedToSavePreference: 'Failed to save preference',
      failedToSave: 'Failed to save',
      failedToCreatePost: 'Failed to create post',
    },
  },

  soloRoundCompletion: {
    // Page title and navigation
    title: 'Round Complete',
    shareRound: 'Share Round',
    backToDashboard: 'Back to Dashboard',
    
    // Hero section
    personalBest: 'Personal Best!',
    monthlyBest: 'Monthly Best!',
    yearlyBest: 'Yearly Best!',
    outstandingRound: 'Outstanding Round!',
    toPar: 'TO PAR',
    
    // Loading states
    loading: {
      preparingAnalysis: 'Preparing your round analysis...',
      analyzingPerformance: 'Analyzing your round performance...',
      analyzingTournamentRound: 'Analyzing your tournament round...',
      validatingData: 'Validating round data...',
      analysisComplete: 'Round analysis complete!',
      loadingCompletion: 'Loading round completion...',
      retrying: 'Retrying...',
      retryingAttempt: 'Retrying... (attempt {attempt}/3)',
      scanningScorecard: 'Scanning Your Scorecard',
      somethingWentWrong: 'Something went wrong',
      percentComplete: '% complete',
      attemptingRecovery: 'Attempting to recover data... This may take a moment.'
    },

    // Error states
    errors: {
      failedToLoad: 'Failed to Load Round',
      failedToLoadData: 'Failed to load round data',
      generatedCacheMissing: 'Generated cache is missing essential data',
      failedToGenerateCache: 'Failed to generate cache',
      unknownError: 'Unknown',
      maxRetriesReached: 'Unable to load round data after {maxRetries} attempts.',
      temporaryIssue: 'This appears to be a temporary issue. Please try refreshing the page.',
      contactSupport: 'Please contact support if this problem persists.',
      retry: 'Retry',
      retrying: 'Retrying...',
      failedToLoadGame: 'Failed to Load Game',
      backToDashboard: 'Back to Dashboard',
      bodyNotFound: 'The requested data could not be found.',
      bodyConnection: 'Connection issue detected. Please check your internet connection and try again.',
      bodyTimeout: 'The request took too long to complete. Please try again.',
      bodyPermission: 'You do not have permission to access this data. Please check your login status.',
      bodyGeneric: 'An unexpected issue occurred. Please try again, and contact support if the problem persists.'
    },

    // Achievement section
    achievements: {
      title: 'Achievements Unlocked',
      noAchievements: 'No achievements earned this round',
      keepPlaying: 'Keep playing to unlock achievements!',
      points: '+{points} points'
    },
    
    // Core golf metrics
    coreMetrics: {
      title: 'Core Golf Metrics',
      fir: 'FIR',
      firTitle: 'Fairways in Regulation',
      firDescription: 'Percentage of fairways hit off the tee on par 4 and par 5 holes. Good driving accuracy leads to better approach shots.',
      gir: 'GIR',
      girTitle: 'Greens in Regulation',
      girDescription: 'Number of greens reached in regulation (par-2 strokes). Higher percentages indicate better approach shot accuracy.',
      excellent: 'Excellent',
      good: 'Good',
      average: 'Average',
      needsWork: 'Needs Work'
    },
    
    // Putting performance
    putting: {
      title: 'Putting Performance',
      totalPutts: 'Total Putts',
      totalPuttsDescription: 'Total number of putts taken during the round. Lower averages indicate better putting performance.',
      putts: 'putts',
      averagePerHole: 'average per hole',
      onePutt: '1-Putt',
      twoPutts: '2-Putts',
      threePutts: '3-Putts'
    },
    
    // Scrambling analysis
    scrambling: {
      title: 'Scrambling Analysis',
      upAndDownSuccess: 'Scrambling Success',
      upAndDown: 'Scrambling Success',
      allGreensInRegulation: 'All greens in regulation',
      savesOutOf: '{successes} scrambles out of {opportunities} missed greens',
      successful: 'Successful',
      saves: 'scrambles',
      failed: 'Missed',
      attempts: 'attempts',
      perfectGirPerformance: 'Perfect GIR performance',
      aboveTourAverage: 'Above tour average (55%)',
      belowTourAverage: 'Below tour average (55%)'
    },
    
    // Penalties
    penalties: {
      title: 'PENALTIES',
      penaltyStrokes: 'Penalty Strokes',
      strokesImpact: '+1.5 strokes impact'
    },
    
    // Score distribution
    scoreDistribution: {
      title: 'Score Distribution',
      bird: 'Bird',
      par: 'Par',
      bog: 'Bog',
      dbl: 'Dbl',
      frontBackNine: 'Front | Back Nine',
      parOrBetterRate: 'Par or Better Rate'
    },
    
    // Performance highlights
    performanceHighlights: {
      title: 'Performance Highlights'
    },
    
    // Round progression
    roundProgression: {
      title: 'Round Progression'
    },
    
    // Hole analysis
    holeAnalysis: {
      title: 'Hole Analysis',
      bestHoles: 'Best Holes',
      troubleHoles: 'Trouble Holes',
      noUnderParHoles: 'No under-par holes this round',
      noUnderPar: 'No under-par holes this round',
      keepWorkingOnBirdies: 'Keep working on those birdies!',
      keepWorking: 'Keep working on those birdies!',
      birdie: 'Birdie',
      double: 'Double',
      bogey: 'Bogey',
      noOverParHoles: 'No over-par holes this round',
      noOverPar: 'No over-par holes this round',
      perfectRound: 'Perfect round!'
    },
    
    // Complete scorecard
    scorecard: {
      title: 'Complete Scorecard',
      par: 'Par',
      score: 'Score',
      plusMinus: '+/-',
      fir: 'FIR',
      gir: 'GIR',
      putts: 'Putts',
      pen: 'Pen'
    },
    
    // Round summary
    summary: {
      title: 'Round Summary',
      course: 'Course',
      dateTime: 'Date & Time',
      weather: 'Weather',
      weatherUnavailable: 'Weather data unavailable',
      duration: 'Duration',
      durationFormat: '{hours}h {minutes}m • Solo',
      durationUnavailable: 'Duration unavailable • Solo'
    },
    
    // Action buttons
    actions: {
      shareRound: 'Share Round',
      backToDashboard: 'Back to Dashboard'
    },
    
    // Toast messages
    toasts: {
      analysisComplete: 'Round analysis complete! 🎉',
      analysisCompleteDescription: 'Your performance data has been analyzed and is ready to view.',
      roundSharedToFeed: 'Round shared to your feed!',
      failedToShareRound: 'Failed to share round',
      trophyEarned: '🏆 Earned {trophyName}!',
      trophyTier: 'Tier: {tier}',
      retryingAnalysis: 'Retrying data analysis ({attempt}/{maxAttempts})...',
      retryingDescription: 'Attempting to recover your round data.',
      retryingAfterError: 'Retrying after error ({attempt}/{maxAttempts})...',
      retryingErrorDescription: 'Attempting to recover from unexpected error.'
    },
    
    // Badge/Trophy types
    badgeTypes: {
      unknownTrophy: 'Unknown Trophy',
      achievement: 'Achievement',
      greatJob: 'Great job!'
    },
    
    // Additional performance indicators
    performanceLabels: {
      allGreensInRegulation: 'All greens in regulation',
      perfectGirPerformance: 'Perfect GIR performance',
      aboveTourAverage: 'Above tour average (55%)',
      belowTourAverage: 'Below tour average (55%)',
      durationUnavailable: 'Duration unavailable • Solo'
    },
    
    // Hole analysis
    holeLabels: {
      double: 'Double',
      bogey: 'Bogey',
      holeNumber: 'Hole'
    },
    
    // Weather and round info
    weatherInfo: {
      weatherDataUnavailable: 'Weather data unavailable'
    },
    
    
    // Fallback messages
    fallbacks: {
      golfCourse: 'Golf Course'
    }
  },
  
  soloRoundCelebration: {
    pageTitle: 'Round Complete',
    loading: 'Loading celebration...',
    errors: {
      noDataAvailable: 'No data available'
    },
    buttons: {
      goHome: 'Go Home',
      shareVictory: 'Share This Victory!',
      viewDetailedStats: 'View Detailed Stats'
    },
    achievements: {
      personalBest: 'Personal Best!',
      monthlyBest: 'Monthly Best!',
      yearlyBest: 'Yearly Best!',
      outstandingRound: 'Outstanding Round!',
      trophiesEarned: 'Trophies Earned!'
    },
    scoreLabels: {
      toPar: 'TO PAR',
      bird: 'BIRD',
      par: 'PAR',
      bogey: 'BOG',
      double: 'DBL'
    },
    metrics: {
      gir: 'GIR',
      fir: 'FIR',
      putts: 'PUTTS',
      scramble: 'SCRAMBLE',
      perHole: 'per hole'
    },
    performance: {
      excellent: 'Excellent',
      good: 'Good',
      average: 'Average',
      needsWork: 'Needs Work',
      aboveTourAvg: 'Above Tour Avg',
      belowTourAvg: 'Below Tour Avg',
      allGir: 'All GIR!',
      perfectGir: 'Perfect GIR',
      noData: 'No data'
    },
    altText: {
      selectedPhoto: 'Selected photo'
    },
    contextMessages: {
      greatRound: 'Great round!',
      congratulationsFirstRound: 'Congratulations on completing your first round!',
      bestRoundThisMonth: 'Best round this month!',
      bestRoundThisYear: 'Best round this year!',
      exceptionalRound: 'Exceptional round!',
      outstandingRound: 'Outstanding round!',
      solidRound: 'Solid round!',
      personalBestImproved: 'Personal Best! Improved by {improvement} stroke{plural}!',
      bestRoundEver: '{rank} best round ever!',
      topFiveRound: 'Top 5 round! ({rank} of {total})',
      firstTimePlayingCourse: 'First time playing {courseName}!',
      courseRecordImproved: 'Course record at {courseName}! Improved by {improvement} stroke{plural}!',
      ordinals: {
        first: '1st',
        second: '2nd', 
        third: '3rd'
      }
    }
  },
  
  ui: {
    loadingState: {
      defaultMessage: 'Loading...'
    },
    errorState: {
      defaultTitle: 'Something went wrong',
      defaultDescription: 'An unexpected error occurred. Please try again.',
      network: {
        title: 'No connection',
        description: 'You appear to be offline. Check your connection and try again.',
      },
      chunk: {
        title: 'Failed to load',
        description: 'This can happen after an app update. Tap retry — it usually fixes itself.',
      },
      crash: {
        title: 'Something went wrong',
        description: 'An unexpected error occurred. Try again, or go back and come back later.',
      },
      generic: {
        title: 'Something went wrong',
        description: 'An unexpected error occurred. Try again, or go back and come back later.',
      },
      actions: {
        tryAgain: 'Try again',
        retrying: 'Retrying...',
        goBack: 'Go back',
        goHome: 'Go home',
      },
      domains: {
        messages: {
          title: 'Messages unavailable',
          description: 'We hit a snag loading your messages. Try again — if it keeps happening, come back in a moment.',
        },
        chat: {
          title: 'Chat unavailable',
          description: 'We hit a snag loading the chat. Try again — if it keeps happening, come back in a moment.',
        },
        profile: {
          title: 'Profile unavailable',
          descriptionRetry: "We couldn't load your profile. Try again — it's usually a temporary issue.",
          descriptionFailed: "We couldn't load your profile after several attempts. Please go back to the dashboard.",
        },
        notifications: {
          title: 'Notifications unavailable',
          description: "We couldn't load your notifications. Try again in a moment.",
        },
      },
    },
    connectionStatus: {
      connected: 'Connected',
      offline: 'Offline'
    }
  },

  // Search page translations
  search: {
    // Tab system
    tabs: {
      friends: 'Friends',
      clubs: 'Clubs'
    },

    // Search placeholders  
    placeholders: {
      searchPeople: 'Search for people on Golf...',
      searchClubs: 'Search for Clubs on Golf...'
    },

    // Action tabs
    actions: {
      suggested: 'Suggested',
      facebook: 'Facebook', 
      contacts: 'Contacts',
      qrCode: 'QR Code'
    },

    // Main content sections
    suggestions: {
      title: 'Find Suggested {type}',
      description: 'We\'ll suggest {items} based on your activity and location'
    },

    facebook: {
      title: 'Find Facebook Friends',
      description: 'Connect with your Facebook friends who are also on the app',
      button: 'Connect Facebook',
      connecting: 'Connecting to Facebook...',
      failed: 'Facebook connection failed'
    },

    contacts: {
      title: 'Find From Contacts', 
      description: 'Find {type} from your phone contacts',
      button: 'Sync Contacts',
      notSupported: 'Contact sync not supported on this browser',
      failed: 'Contact sync failed. Please try again.',
      foundFriends: 'Found {count} friends from your contacts!',
      noFriends: 'No friends found in your contacts',
      noSelected: 'No contacts selected'
    },

    qr: {
      title: 'QR Code',
      yourCode: 'Your QR Code', 
      description: 'Generate your QR code or scan someone else\'s',
      shareDescription: 'Share this QR code with friends so they can add you',
      generateButton: 'Generate My QR Code',
      generating: 'Generating...',
      scanButton: 'Scan QR Code',
      shareButton: 'Share QR Code', 
      newButton: 'Generate New QR Code',
      shareTitle: 'Connect with me on Golf App',
      shareText: 'Scan this QR code to add me as a friend!',
      cameraUnavailable: 'Camera not available on this device',
      cameraFailed: 'Failed to access camera. Please check permissions.',
      generateFailed: 'Failed to generate QR code',
      shareFailed: 'Failed to share QR code. Please try again.',
      accessGranted: 'Camera access granted! QR scanner will open here.',
      shareSuccess: 'QR code shared successfully!',
      linkCopied: 'QR code link copied to clipboard!'
    },

    // Search results
    results: {
      noResults: 'No results found for "{query}"',
      handicap: 'Handicap: {value}',
      na: 'N/A',
      unknownUser: 'Unknown User',
      unknownCity: 'Unknown City',
      unknownCountry: 'Unknown Country',
      defaultLocation: 'Belgium',
      friend: 'Friend',
      course: 'course',
      courses: 'courses',
      par: 'Par {number}',
      courseGeneric: 'Course'
    },

    // Button actions
    buttons: {
      add: 'Add',
      accept: 'Accept', 
      sent: 'Sent',
      friends: 'Friends',
      join: 'Join',
      requested: 'Requested',
      view: 'View',
      details: 'Details'
    },

    // Club discovery empty state
    clubDiscovery: {
      title: 'Discover Premium Clubs',
      description: 'Search for exclusive golf clubs and world-class courses.',
      subtitle: 'Start typing to explore our curated collection.',
      location: 'Worldwide locations available'
    },

    // Error messages
    errors: {
      searchFailed: 'Search failed. Please try again.',
      friendRequestFailed: 'Failed to send friend request. Please try again.',
      signInToMessage: 'Please sign in to send messages',
      conversationFailed: 'Failed to create conversation',
      messagingFailed: 'Failed to open messaging interface',
      joinClubFailed: 'Failed to join club. Please try again.'
    },

    // Success messages
    success: {
      friendRequestSent: 'Friend request sent!',
      clubJoinSent: 'Club join request sent!',
      contactsFound: 'Found {count} contacts to search'
    }
  },

  // Notifications page translations
  notifications: {
    // Page header
    header: {
      title: 'Notifications',
      subtitle: 'Stay updated with your golf activity'
    },

    // Navigation tabs
    tabs: {
      activity: 'Activity',
      myFriends: 'My friends'
    },

    // Main actions
    actions: {
      markAllRead: 'Mark all read',
      settings: 'Settings'
    },

    // Loading states
    loading: {
      notifications: 'Loading notifications...',
      loading: 'Loading...',
      retrying: 'Retrying...'
    },

    // Error states
    errors: {
      noInternet: 'No Internet Connection', 
      failedToLoad: 'Failed to Load Notifications',
      tryAgain: 'Try Again'
    },

    // Connection status
    status: {
      online: 'Online',
      offline: 'Offline',
      retry: 'Retry {count}/3'
    },

    // Empty states
    empty: {
      title: 'No notifications yet',
      message: 'You\'ll see activity notifications here when they arrive'
    },

    // Load more
    loadMore: {
      button: 'Load More'
    },

    // Notification items
    items: {
      newNotification: 'New notification',
      justNow: 'Just now',
      minutesAgo: '{minutes}m ago',
      hoursAgo: '{hours}h ago', 
      daysAgo: '{days}d ago',
      groupCount: '{count} notifications',
      tapToShow: 'Tap to show details',
      tapToHide: 'Tap to hide details',
      recentActivity: 'Recent activity:',
      more: '+{count} more'
    },

    // Settings panel
    settings: {
      title: 'Notification Settings',
      subtitle: 'Customize when you receive push notifications',

      // Main settings
      doNotDisturb: {
        title: 'Do Not Disturb',
        description: 'Disable all notifications'
      },

      // New meaningful categories
      socialActivity: {
        title: 'Social Activity',
        description: 'Likes, comments, follows, @mentions'
      },

      gameInvites: {
        title: 'Game Invites',
        description: 'When someone invites you to play'
      },

      roundUpdates: {
        title: 'Round Updates',
        description: 'Friends completing rounds, sharing scores'
      },

      clubAnnouncements: {
        title: 'Club Announcements',
        description: 'Admin messages from your club'
      },

      achievements: {
        title: 'Achievements',
        description: 'Badges, milestones, handicap changes'
      },

      coaching: {
        title: 'Coaching',
        description: 'Session reminders, bookings, feedback'
      },

      teeTimes: {
        title: 'Tee Times',
        description: 'Booking confirmations and reminders'
      },

      payments: {
        title: 'Payments',
        description: 'Payment confirmations and failures'
      },

      categoriesTitle: 'Notification Categories',
      soundsTitle: 'Sound',
      doNotDisturbActive: 'All notifications are currently paused',
      autoSave: 'Changes saved automatically',

      sounds: {
        title: 'Notification Sounds',
        description: 'Play sound with notifications'
      },

      // Actions
      actions: {
        save: 'Save Settings',
        saving: 'Saving...',
        saved: 'Saved',
        cancel: 'Cancel'
      }
    }
  },

  // Profile Activities page translations  
  profileActivities: {
    // Page header
    header: {
      title: 'Activities',
      subtitle: 'Your golf activity history'
    },

    // Loading states
    loading: {
      activities: 'Loading your activities...'
    },

    // Filter section
    filters: {
      label: 'Filters',
      activitiesCount: '{count} activities',
      
      // Time period
      sheetTitle: 'Filter Rounds',
      gameModeLabel: 'Game Mode',
      holesLabel: 'Holes',
      roundsCount: '{count} rounds',
      allRounds: 'All Rounds',
      solo: 'Solo',
      multiplayer: 'Multiplayer',
      tournament: 'Tournament',
      allHoles: 'All Holes',
      nineHoles: '9 Holes',
      eighteenHoles: '18 Holes',
      seeNineHoles: 'See your 9-hole rounds',
      seeEighteenHoles: 'See your 18-hole rounds',

      timePeriod: {
        label: 'Time Period',
        allTime: 'All Time',
        thisYear: 'This Year',
        thisMonth: 'This Month',
        thisWeek: 'This Week'
      },

      // Activity type
      activityType: {
        label: 'Activity Type',
        allActivities: 'All Activities',
        roundsOnly: 'Rounds Only',
        practiceOnly: 'Practice Only'
      }
    },

    // Public profile shared component
    publicProfile: {
      // Loading states
      loadingMessage: 'Loading profile data...',
      
      // Error states  
      profileNotFound: 'Profile Not Found',
      profileNotFoundMessage: 'This user\'s profile is not available or has been set to private.',
      privateProfile: 'Private Profile',
      privateProfileMessage: 'This user has set their profile to private.',
      
      // Actions
      goBack: 'Go Back'
    },


    // Public profile statistics page
    publicProfileStatistics: {
      // Page header
      title: 'Statistics',
      subtitle: '{name}\'s golf statistics',
      fallbackName: 'Golfer',
      
      // Overview section
      overview: 'Overview',
      totalRounds: 'Total Rounds',
      handicap: 'Handicap',
      notAvailable: 'N/A',
      
      // Empty state
      noStatistics: 'No Statistics Available',
      noStatisticsMessage: '{name} hasn\'t played any rounds yet or has set their statistics to private.',
      
      // Privacy notice
      privacyNotice: 'Privacy Notice',
      privacyMessage: 'This shows only the statistics that {name} has made public. Some data may be hidden for privacy.'
    },

    // Activity card labels
    activity: {
      score: 'Score',
      toPar: 'To Par',
      fairways: 'Fairways',
      gir: 'GIR',  
      putts: 'Putts',
      completed: 'Completed',
      view: 'View'
    },

    // Score performance labels
    scoreLabels: {
      albatross: 'Albatross or better',
      eagle: 'Eagle average',
      birdie: 'Birdie average', 
      par: 'Par average',
      bogey: 'Bogey average',
      double: 'Double average',
      overPar: '+{score} average'
    },

    // Delete dialog
    deleteDialog: {
      title: 'Delete Round',
      description: 'Are you sure you want to delete this round at {courseName}? This action cannot be undone.',
      cancel: 'Cancel',
      delete: 'Delete Round'
    },

    // Empty state
    empty: {
      title: 'No activities yet',
      message: 'Start playing rounds to build your activity history',
      startButton: 'Start Playing'
    },

    // Error messages
    errors: {
      loadActivities: 'Failed to load activities',
      loadHistory: 'Failed to load activity history',
      invalidData: 'Invalid round data',
      deleteRound: 'Failed to delete round'
    },

    // Success messages
    success: {
      roundDeleted: '{roundType} deleted successfully',
      multiplayerGame: 'Multiplayer game',
      round: 'Round'
    },

    // Data labels
    data: {
      unknownCourse: 'Unknown Course',
      locationNotSet: 'Location not set'
    }
  },

  // Profile statistics page (personal)
  profileStatistics: {
    // Page header
    pageTitle: 'Statistics',
    pageSubtitle: 'Your golf performance data',

    // Tab navigation
    tabs: {
      rounds: 'Rounds',
      performance: 'Performance',
      courses: 'Courses'
    },

    // Section headers
    sectionHeaders: {
      activity: 'ACTIVITY',
      yearToDate: 'YEAR-TO-DATE',
      allTime: 'ALL TIME',
      performanceMetrics: 'PERFORMANCE METRICS',
      performanceTrends: 'PERFORMANCE TRENDS',
      courseStatistics: 'COURSE STATISTICS',
      scoringBreakdown: 'SCORING BREAKDOWN'
    },

    // Activity labels
    activity: {
      avgRoundsWeek: 'Avg Rounds/Week',
      avgTimeWeek: 'Avg Time/Week',
      avgDistanceWeek: 'Avg Distance/Week',
      avgScoreWeek: 'Avg Score/Week'
    },

    // Overview metrics
    overview: {
      rounds: 'Rounds',
      time: 'Time',
      distance: 'Distance',
      avgScore: 'Avg Score',
      bestScore: 'Best Score',
      coursesPlayed: 'Courses Played',
      timePlaying: 'Time Playing',
      careerBest: 'Career Best',
      yearsPlaying: 'Years Playing'
    },

    // Performance metrics
    performance: {
      bogeys: 'Bogeys',
      pars: 'Pars',
      parOrBetter: 'Par or Better',
      handicapIndex: 'Handicap Index',
      greensInRegulation: 'Greens in Regulation',
      fairwaysHit: 'Fairways Hit',
      puttsPerRound: 'Putts per Round',
      scrambling: 'Scrambling',
      birdies: 'Birdies',
      overallImprovement: 'Overall Improvement',
      comparedToLastQuarter: 'Compared to last quarter',
      thisYear: 'this year',
      thisMonth: 'this month',
      eaglesToo: 'eagles too!'
    },

    // Course information
    courses: {
      favoriteCourse: 'Favorite Course',
      bestCourseScore: 'Best Course Score',
      totalDistance: 'Total Distance',
      defaultCourseName: 'Royal Golf Club',
      recentCourses: 'RECENT COURSES'
    },

    // Scoring breakdown
    scoring: {
      doublePlus: 'Double+',
      birdiesPlus: 'Birdies+'
    },

    // Public profile context (for when viewing other players)
    public: {
      title: '{name}\'s Statistics',
      subtitle: '{name}\'s golf performance data',
      fallbackName: 'Golfer',
      totalRounds: 'Total Rounds',
      notAvailable: 'N/A',
      noStatistics: 'No Statistics Available',
      noStatisticsMessage: '{name} hasn\'t played any rounds yet or has set their statistics to private.',
      privacyNotice: 'Privacy Notice',
      privacyMessage: 'This shows only the statistics that {name} has made public. Some data may be hidden for privacy.'
    },

    // SkillHintSheet — stat metric info tooltips
    skillHint: {
      whatThisMeans: 'What this means',
      exercisesToImprove: 'Exercises to improve',
      seeAllInPractice: 'See all in My Practice',
      benchmark: 'Good benchmark',
      yourScore: 'Your score',
      long_game: {
        title: 'Long Game',
        description: 'How often you hit fairways (FIR) and reach the green in regulation (GIR). Ball striking is the foundation of lower scores.',
        benchmark: 'FIR 40%+, GIR 35%+',
      },
      putting: {
        title: 'Putting',
        description: 'Average putts per hole. Fewer putts means more one-putts and fewer three-putts — the fastest way to lower your score.',
        benchmark: '2.0 putts/hole or fewer',
      },
      short_game: {
        title: 'Short Game',
        description: 'Scrambling: how often you make par or better after missing the green. Strong recovery saves strokes on almost every hole.',
        benchmark: '35%+ scrambling',
      },
      bunker: {
        title: 'Bunker Play',
        description: 'How often you escape a bunker in one shot and save par or better. Sand saves are a high-leverage way to protect your score.',
        benchmark: '25%+ sand saves',
      },
      mental: {
        title: 'Mental Game',
        description: 'How consistent you are in the back 9 vs front 9, and how well you recover after a bad hole. Separates players of equal ball-striking ability.',
        benchmark: 'Stable back-9 score',
      },
    },
  },

  // Quests & Gamification
  quests: {
    dashboard: {
      // Error state
      unableToLoadQuestData: 'Unable to load quest data',
      pleaseRefreshPage: 'Please try refreshing the page',
      // League progress
      leagueProgress: 'League Progress',
      questPoints: 'quest points',
      progressTo: 'Progress to {tier}',
      pointsNeeded: '{points} points needed',
      // Challenge level
      challengeLevel: 'Challenge Level',
      level: 'Level {level}',
      challengeXp: 'challenge XP',
      progressToLevel: 'Progress to Level {level}',
      xpNeeded: '{xp} XP needed',
      // Stats
      completed: 'Completed',
      questPointsLabel: 'Quest Points',
      challengeXpLabel: 'Challenge XP',
      unclaimed: 'Unclaimed',
      // Quest categories
      availableQuests: 'Available Quests',
      all: 'All',
      scoring: 'Scoring',
      consistency: 'Consistency',
      milestones: 'Milestones',
      social: 'Social',
      special: 'Special',
      // Empty state
      noQuestsAvailable: 'No quests available',
      completeMoreRounds: 'Complete more rounds to unlock new quests!',
      noQuestsInCategory: 'No {category} quests available in your current tier.'
    },

    // ProgressJourney component (skill-tree/ProgressJourney.tsx)
    journey: {
      title: 'Golf Journey Map',
      subtitle: 'Advance through territories by earning league points',
      leaguePoints: 'League Points',
      progressTo: 'Progress to {name}',
      morePointsNeeded: '{count} more points needed',
      journeyProgress: 'Journey Progress',
      territoriesLabel: '{explored}/{total} territories',
      currentLocation: '📍 Current Location',
      explored: '✓ Explored',
      nextDestination: '🎯 Next Destination',
      locked: '🔒 Locked',
      startingPoint: 'Starting Point',
      leaguePointsBadge: '{count} League Points',
      territoryMapTitle: 'Territory Map',
      statsTerritoriesExplored: 'Territories Explored',
      statsLeaguePoints: 'League Points',
      statsPointsToNext: 'Points to Next',
      statsJourneyComplete: 'Journey Complete',
    },

    // ComprehensiveQuestOverview modal (quest/ComprehensiveQuestOverview.tsx)
    overview: {
      loadingQuestSystem: 'Loading quest system...',
      unableToLoadQuestData: 'Unable to load quest data',
      pleaseRefreshPage: 'Please try refreshing the page',
      close: 'Close',
      title: 'Quest System',
      subtitle: 'Complete quests to advance leagues and earn XP',
      currentLeague: 'Current League',
      leaguePoints: 'League Points',
      questsCompleted: 'Quests Completed',
      progressionHeading: 'Quest Achievements — League Progression',
      progressionSubtitle: 'Complete quests to earn league points and advance: First Skills → Course Ready → Developing → Advancing → Skilled → Elite → Master',
      leagueLabel: '{name} League',
      questsCount: '{count} quests',
      currentLeagueLabel: 'Current League',
      clickToViewRequirements: 'Click to view requirements',
      leagueLabels: {
        onboarding: 'First Skills',
        hcp_54: 'Course Ready',
        hcp_42: 'Developing',
        hcp_36: 'Advancing',
        hcp_28: 'Skilled',
        hcp_18: 'Elite',
        hcp_10: 'Master',
      },
    },
  },

  // Profile Feedback page
  profileFeedback: {
    title: 'Feedback',
    subtitle: 'Share your ideas',
    newButton: 'New',
    infoTitle: 'We Value Your Input!',
    infoDescription: 'Your feedback helps us improve. Report bugs, request features, or share suggestions.',
    tabs: {
      myFeedback: 'My Feedback',
      community: 'Community'
    },
    myFeedbackSection: {
      title: 'Your Submissions',
      subtitle: 'View and manage your feedback',
      loginRequired: 'Please log in to view your feedback'
    },
    communitySection: {
      title: 'Community Feedback',
      subtitle: 'Browse and vote on feedback'
    },
    submitDialog: {
      title: 'Submit Feedback',
      description: 'Help us improve by sharing your thoughts, reporting issues, or requesting features.'
    }
  },

  // Profile Tournaments page
  profileTournaments: {
    title: 'Tournaments',
    subtitle: 'Your schedule and history',
    tabs: {
      upcoming: 'Upcoming',
      past: 'Past'
    },
    status: {
      confirmed: 'Confirmed',
      waitlist: 'Waitlist',
      cancelled: 'Cancelled',
      pending: 'Pending'
    },
    emptyUpcoming: {
      title: 'No Upcoming Events',
      message: "You're not registered for any upcoming tournaments",
      button: 'Browse Events'
    },
    emptyPast: {
      title: 'No Past Events',
      message: 'Your tournament history will appear here'
    },
    loading: {
      title: 'Loading Tournaments',
      message: 'Fetching your tournament history...'
    }
  },

  // Tournament translations (shared across tournament components)
  tournament: {
    // Common
    backToTournament: 'Back to Tournament',
    backToEvent: 'Back to Event',
    viewDetails: 'View Details',
    enterScore: 'Enter Score',
    viewMatch: 'View Match',
    tryAgain: 'Try Again',

    // Status badges
    status: {
      live: 'Live',
      active: 'Active',
      activeRound: 'Active Round',
      upcoming: 'Upcoming',
      upcomingMatch: 'Upcoming Match',
      completed: 'Completed',
      waiting: 'Waiting',
      scheduled: 'Scheduled',
      inProgress: 'In Progress',
      complete: 'Complete'
    },

    // Formats
    formats: {
      strokePlay: 'Stroke Play',
      matchPlay: 'Match Play',
      stableford: 'Stableford',
      straightBracket: 'Straight Bracket',
      groupKnockout: 'Group Knockout',
      individual: 'Individual',
      team: 'Team'
    },

    // Progress
    progress: {
      roundProgress: 'Round Progress',
      matchProgress: 'Match Progress',
      roundOf: 'Round {current} of {total}',
      singleRound: 'Single Round',
      waitingForNextRound: 'Waiting for next round'
    },

    // Match card
    matchCard: {
      activeRoundN: 'Active Round {number}',
      deadline: 'Deadline',
      nextMatch: 'Next Match',
      vs: 'vs',
      groupPhase: 'Group Phase',
      position: 'Position',
      bracketPosition: 'Bracket Position'
    },

    // Bracket
    bracket: {
      title: 'Tournament Bracket',
      subtitle: 'View the tournament bracket and your matches',
      loading: 'Loading tournament bracket...',
      notAvailable: 'Bracket Not Available',
      notAvailableMessage: 'The tournament bracket will be available once matches are generated by the tournament admin.',
      tournamentNotAvailable: 'Tournament Not Available',
      unableToLoad: 'Unable to load tournament data.',
      yourMatch: 'Your Match',
      matchN: 'Match {number}',
      final: 'Final',
      semifinals: 'Semifinals',
      quarterfinals: 'Quarterfinals',
      roundOfN: 'Round of {number}',
      tbd: 'TBD',
      winner: 'Winner',
      champion: 'Champion',
      fullView: 'Full',
      legend: {
        yourMatch: 'Your Match',
        winner: 'Winner'
      }
    },

    // Leaderboard
    leaderboard: {
      title: 'Leaderboard',
      liveTitle: 'Live Leaderboard',
      loading: 'Loading leaderboard...',
      unableToLoad: 'Unable to load leaderboard',
      updating: 'Updating...',
      players: 'players',
      player: 'Player',
      updated: 'Updated',
      noScoresYet: 'No scores yet',
      scoresWillAppear: 'Scores will appear here as players submit them',
      noResults: 'No results available for this event',
      you: 'You',
      dnf: 'DNF',
      dq: 'DQ',
      netScore: 'Net',
      grossScore: 'Gross',
      gross: 'Gross',
      holes: 'holes',
      hcp: 'HCP',
      hdcp: 'Hdcp',
      thru: 'Thru',
      pts: 'Pts',
      showLess: 'Show Less',
      showAll: 'Show All',
      hidden: 'hidden',
      more: 'more',
      yourPosition: 'Your Position',
      of: 'of',
      matchPlayLeaderboard: 'Match Play Leaderboard',
      leaderboardReady: 'Leaderboard Ready',
      liveUpdatesEnabled: 'Live updates enabled',
      // Match play empty state
      noLeaderboardYet: 'No Leaderboard Yet',
      leaderboardWillAppear: 'The leaderboard will appear once tournament matches begin',
      playersNeedRegistered: 'Players need to be registered for the tournament',
      matchesNeedPlayed: 'Matches need to be played and results entered',
      leaderboardUpdatesAuto: 'Leaderboard updates automatically with new results',
      missingEventId: 'Missing eventId - cannot load match history',
      // Qualification rules
      qualificationRules: 'Qualification Rules',
      topAdvanceToKnockout: 'Top {count} advance to knockout round',
      bubbleZone: 'Positions {start}-{end} in bubble zone',
      belowEliminated: 'Below position {position} eliminated',
      // Match history
      loadingMatchHistory: 'Loading match history...',
      failedToLoadMatchHistory: 'Failed to load match history',
      noMatchesPlayedYet: 'No matches played yet',
      groupStage: 'Group Stage',
      vs: 'vs',
      won: 'Won',
      lost: 'Lost',
      tied: 'Tied',
      played: 'Played',
      totalMatches: 'Total matches'
    },

    // Score entry
    scoreEntry: {
      loadingScorecard: 'Loading Scorecard',
      preparingRound: 'Preparing your round...',
      unableToLoad: 'Unable to Load Scorecard',
      somethingWentWrong: 'Something went wrong. Please try again or go back to the tournament.',
      tournamentNotFound: 'Tournament Not Found',
      noAccess: "Tournament not found or you don't have access to it.",
      checkOrganizer: 'Please check with the tournament organizer.',
      gpsActive: 'GPS Distance Tracking Active',
      gpsUnavailable: 'GPS Signal Unavailable',
      enterScoresFor: 'Enter Scores For'
    },

    // Tournament header
    header: {
      participants: 'participants',
      holes: 'holes'
    },

    // Event results (member view)
    eventResults: {
      eventNotStarted: "Event hasn't started yet",
      resultsAvailableOn: 'Results will be available on {date}',
      eventLiveNow: 'Event Live Now!',
      scoreEntry: 'Score Entry',
      liveResults: 'Live Results',
      finalResults: 'Final Results',
      yourPerformance: 'Your Performance',
      finalPosition: 'Final Position',
      scoreVerified: 'Your score has been officially verified!',
      eventResults: 'Event Results',
      registrationRequired: 'Registration Required',
      viewPublicResults: 'View public results from this event',
      registerToParticipate: 'Register for this event to participate and track your results',
      loadingTournament: 'Loading tournament data...'
    },

    // Round names (abbreviated for tabs)
    roundAbbreviations: {
      full: 'Full',
      qf: 'QF',
      sf: 'SF',
      f: 'F',
      r: 'R'
    },

    // Tournament Detail page specific translations
    detail: {
      // Group standings
      groupStandings: {
        loading: 'Loading group standings...',
        error: 'Error loading group standings',
        tryAgainLater: 'Please try again later',
        noStandings: 'No group standings available yet',
        standingsWillAppear: 'Standings will appear once group matches are completed',
        debugInfo: 'Debug Info'
      },

      // Qualification status
      qualification: {
        qualified: '✅ Qualified',
        wildCard: '🎯 Wild Card',
        eliminated: 'Eliminated',
        topAdvance: 'Top 2 advance'
      },

      // Table headers
      tableHeaders: {
        player: 'Player',
        pts: 'Pts',
        p: 'P',
        w: 'W',
        d: 'D',
        l: 'L',
        diff: '+/-'
      },

      // Chat
      chat: 'Chat',

      // Matches
      matches: {
        yourUpcoming: 'Your Upcoming Matches',
        scheduled: 'Scheduled',
        deadline: 'Deadline:',
        tip: 'Tip:',
        tipMessage: 'You can play these matches in any order that works for you and your opponents. Coordinate with your opponents to arrange convenient times.',
        completed: 'Completed Matches',
        noMatchesYet: 'No matches scheduled yet',
        scheduleWillAppear: 'Your tournament schedule will appear here once matches are generated',
        noScheduleAvailable: 'No tournament schedule available',
        won: 'won',
        lost: 'lost',
        draw: 'draw'
      },

      // Results
      results: {
        won: 'Won',
        lost: 'Lost',
        tied: 'Tied',
        played: 'Played'
      },

      // Registration
      registration: {
        yourRegistration: 'Your Registration',
        confirmed: 'Confirmed',
        notRegistered: 'Not Registered'
      },

      // Tournament info
      info: {
        tournamentFormat: 'Tournament Format',
        currentPhase: 'Current Phase',
        tournamentType: 'Tournament Type',
        free: 'Free',
        unknown: 'Unknown',
        roundN: 'Round {number}'
      },

      // Tabs - dynamic tab labels from TournamentDetectionService
      tabs: {
        overview: 'Overview',
        standings: 'Standings',
        challenge: 'Challenge',
        results: 'Results',
        leaderboard: 'Leaderboard',
        enterScore: 'Enter Score',
        scoreEntry: 'Enter Score',
        groupStandings: 'Group Standings',
        knockoutBracket: 'Knockout Bracket',
        bracket: 'Bracket',
        brackets: 'Brackets',
        enterResults: 'Enter Results',
        eventInfo: 'Event Info'
      },

      // Tournament type descriptions
      tournamentTypes: {
        stablefordCompetition: 'Stableford Competition',
        matchPlayTournament: 'Match Play Tournament',
        strokePlayChampionship: 'Stroke Play Championship'
      },

      // Hero card
      hero: {
        tournamentDetails: 'Tournament Details'
      },

      // Leaderboard messages
      leaderboardMessages: {
        tournamentStartingSoon: 'Tournament Starting Soon',
        playersRegisteredMatchesBegin: '{count} players registered. The leaderboard will appear once matches begin.',
        matchPlayLeaderboard: 'Match Play Leaderboard',
        groupStageSubtitle: 'Group stage standings - Top players advance to knockout round',
        topPlayersAdvance: 'Top players advance to knockout round',
        playersRegisteredScoresAppear: '{count} players registered. Scores will appear as they\'re submitted.',
        interactiveLeaderboard: 'Interactive Leaderboard'
      },

      // Tournament information
      tournamentInfo: {
        title: 'Tournament Information'
      },

      // Overview stats
      overviewStats: {
        tournamentStatus: 'Tournament Status',
        players: 'Players',
        daysLeft: 'Days left',
        format: 'Format',
        stableford: 'Stableford',
        strokePlay: 'Stroke Play',
        matchPlay: 'Match Play',
        yourStats: 'Your Stats',
        yourPerformance: 'Your Performance',
        position: 'Position',
        points: 'Points',
        average: 'Average',
        bestRound: 'Best Round',
        totalPoints: 'Total Points',
        yourRounds: 'Your Rounds',
        roundHistory: 'Round History',
        roundN: 'Round {number}',
        pts: 'pts',
        noRoundsYet: 'No rounds played yet',
        roundHistoryWillAppear: 'Your round history will appear here once you submit scores',
        startYourJourney: 'Enter your first score to begin your tournament journey!',
        round: 'Round',
        total: 'Total',
        tournamentProgress: 'Tournament Progress',
        completed: 'Completed',
        active: 'Active',
        upcoming: 'Upcoming',
        matches: 'Matches',
        yourMatches: 'Your Matches',
        groupPhase: 'Group Phase',
        knockoutPhase: 'Knockout Phase',
        bracket: 'Bracket',
        groups: 'Groups',
        phase: 'Phase',
        scoreToPar: 'Score to Par',
        rounds: 'Rounds',
        yourTournamentRounds: 'Your Tournament Rounds',
        noRoundsConfigured: 'No rounds configured yet',
        roundsWillAppear: 'Rounds will appear here once configured by admin',
        pending: 'Pending',
        gross: 'Gross',
        champion: 'Champion!',
        congratulations: 'Congratulations on your victory',
        eliminated: 'Eliminated',
        betterLuckNextTime: 'Better luck next time',
        record: 'Record',
        won: 'Won',
        lost: 'Lost',
        tied: 'Tied',
        unknown: 'Unknown',
        scheduled: 'Scheduled',
        noMatchesYet: 'No matches yet',
        noMatchesYetDescription: 'Matches will appear here once the group stage begins.',
        matchHistoryWillAppear: 'Your match history will appear here'
      },

      // Matchplay score entry
      matchplayEntry: {
        back: 'Back',
        yourTournamentSchedule: 'Your Tournament Schedule',
        viewAndManageMatches: 'View and manage your matches for this tournament',
        you: 'You',
        vs: 'vs',
        match: 'Match',
        selectYourTees: 'Select Your Tees',
        chooseTees: 'Choose which tees you played from - see how it affects your handicaps',
        rating: 'Rating',
        slope: 'Slope',
        handicapAllocation: 'Handicap Allocation',
        selectMatchToViewAllocation: 'Select a match from your tournament schedule above to view handicap stroke allocation.',
        yourResult: 'Your Result',
        theirResult: 'Their Result',
        selectResult: 'Select result...',
        holesUp: 'holes up',
        holesDown: 'holes down',
        allSquare: 'All Square',
        andRemaining: '& {holes} remaining',
        submitResult: 'Submit Result',
        updateResult: 'Update Result',
        resultSubmitted: 'Result Submitted',
        resultAlreadySubmitted: 'This result has already been submitted.',
        knockoutRequiresWinner: 'A knockout match requires a winner. All Square is not allowed.',
        matchCompleted: 'Match Completed',
        awaitingResult: 'Awaiting Result',
        inProgress: 'In Progress',
        stroke: 'stroke',
        strokes: 'strokes',
        playingHandicap: 'Playing Handicap',
        courseHandicap: 'Course Handicap',
        whi: 'WHI',
        strokesReceived: 'Strokes Received',
        strokesGiven: 'Strokes Given',
        perHole: 'per hole',
        selectTeeFirst: 'Select a tee to see handicap calculations',
        loadingHandicaps: 'Loading handicaps...',
        matchN: 'Match {number}',
        roundN: 'Round {number}',
        loadingSchedule: 'Loading schedule...',
        noAdditionalMatches: 'No Additional Matches',
        noScheduledMatchesDescription: 'You currently have no other scheduled matches in this tournament.',
        otherScheduledMatches: 'Other Scheduled Matches',
        completedMatches: 'Completed Matches',
        planningTip: 'Planning tip',
        planningTipMessage: 'You can play these matches in any order that works for you and your opponents.',
        quarterFinal: 'Quarterfinal',
        semiFinal: 'Semifinal',
        final: 'Final',
        enterMatchResult: 'Enter Match Result',
        quickResultEntry: 'Quick result entry',
        matchPlayScoring: 'Match Play Scoring',
        submitMatchResult: 'Submit Match Result',
        // MatchStrokeAllocation
        courseHandicapAndStrokes: 'Course Handicap & Strokes',
        handicapStrokeAllocation: 'Handicap stroke allocation for match play',
        ph: 'PH',
        allowance: 'Allowance',
        playStraightUp: 'Play straight up!',
        receivesStrokes: 'receives {count} strokes',
        givesStrokes: 'gives {count} strokes',
        // MatchStrokeAllocation component
        loadingStrokeAllocation: 'Loading stroke allocation...',
        unableToLoadStrokeData: 'Unable to load course stroke data. Using default USGA stroke allocation.',
        errorLabel: 'Error',
        courseLabel: 'Course',
        teeLabel: 'Tee',
        slopeLabel: 'Slope',
        crLabel: 'CR',
        parLabel: 'Par',
        mensRatings: "Men's Ratings",
        womensRatings: "Women's Ratings",
        usgaFormula: 'USGA Formula',
        courseCalculationBreakdown: 'Course calculation breakdown',
        playerHandicap: "{player}'s Handicap",
        usingFallbackRatings: "Using {gender}'s ratings (fallback)",
        noHandicapData: 'No handicap data',
        strokeAllocationLabel: 'Stroke Allocation',
        frontNineLabel: 'Front Nine',
        backNineLabel: 'Back Nine',
        simple: 'Simple',
        detailed: 'Detailed',
        multipleStrokesPerHole: 'Multiple Strokes Per Hole',
        someHolesMultipleStrokes: 'Some holes receive multiple strokes due to high handicap difference',
        youGetStroke: 'You Get Stroke',
        opponentGetsStroke: 'Opponent Gets Stroke',
        strokeCount: 'Stroke Count',
        hideHoles: 'Hide Holes',
        seeWhichHoles: 'See Which Holes',
        usingHandicapIndexes: 'Using handicap indexes (no tee ratings available)',
        aboutTeeRatings: 'About Tee Ratings',
        courseNoTeeData: "This course doesn't have tee rating data configured. Using handicap indexes directly. If you have tee data, please send it to",
        // TournamentScorecardCard
        tournamentScorecard: 'Tournament Scorecard',
        detailedScoringWithGps: 'Detailed scoring with GPS and shot tracking',
        gpsDistances: 'GPS Distances',
        shotTracking: 'Shot Tracking',
        liveStats: 'Live Stats',
        useScorecardDescription: 'Use the scorecard for hole-by-hole scoring with GPS and shot tracking.',
        openTournamentScorecard: 'Open Tournament Scorecard',
        // ScorecardEntry specific
        opponent: 'Opponent',
        handicapStrokesApplied: 'Handicap strokes are applied automatically. Holes where you receive strokes are marked with a badge.'
      },

      // Stroke Play score entry
      strokePlayEntry: {
        // Section headers
        tournamentRounds: 'Tournament Rounds',
        enterYourScore: 'Enter Your Score',
        quickResultEntry: 'Quick result entry - use scorecard for full stats',
        totalScore: 'Total Score',
        totalGrossScore: 'Total Gross Score (18 holes)',

        // Round status badges
        roundStatus: {
          current: 'Current',
          done: 'Done',
          locked: 'Locked',
          upcoming: 'Upcoming',
          completedOrNotAvailable: 'Completed or not yet available'
        },

        // Submission states
        submissionNotAvailable: 'Submission Not Available',
        submittingScore: 'Submitting...',
        submitForRoundN: 'Submit for Round {number}',
        submitStrokePlayScore: 'Submit Stroke Play Score',
        scoreWillBeSubmittedForRound: 'Your score will be submitted for Round {number}',
        scoreWillBeAddedToLeaderboard: 'Your score will be added to the tournament leaderboard',

        // Success/Error messages
        scoreSubmitted: 'Score submitted',
        scoreSubmittedSuccess: 'Score submitted successfully!',
        failedToSubmit: 'Failed to submit score. Please try again.',
        pleaseEnterScore: 'Please enter your total score',
        pleaseEnterValidScore: 'Please enter a valid score between 50 and 150',
        cannotSubmitAtThisTime: 'Cannot submit score at this time',

        // Handicap section
        strokePlayHandicapStrokes: 'Stroke Play Handicap Strokes',
        strokeAllocationForRound: 'Stroke allocation for this round',
        playingHandicapLabel: 'Playing Handicap:',
        courseCalculationBreakdown: 'Course calculation breakdown',

        // Course info
        courseInformation: 'Course Information',
        teesLabel: 'Tees:',
        courseRatingLabel: 'Course Rating',
        slopeRatingLabel: 'Slope Rating',
        parLabel: 'Par:',
        strokeAllowanceLabel: 'Stroke Allowance:',

        // USGA calculation
        usgaCalculation: 'USGA Calculation',
        courseHandicapLabel: 'Course Handicap:',
        playingHandicapPercent: 'Playing Handicap ({percent}%):',
        netScoreLabel: 'Net Score:',

        // Score display
        gross: 'Gross',
        net: 'Net',
        toPar: 'To Par',
        indexPh: 'Index: {index} → PH: {ph} ({percent}%)',
        details: 'Details',

        // Front/Back nine
        frontNine: 'Front Nine',
        backNine: 'Back Nine',
        holes1to9: '1-9',
        holes10to18: '10+',

        // Legend
        receivesStroke: 'Receives Stroke',
        strokeCount: 'Stroke Count',

        // Input
        enterTotalScorePlaceholder: 'Enter your total score...',
        enterTotalScoreHelp: 'Enter your total score for all 18 holes',

        // Course ratings
        mensRatings: "Men's Ratings",
        womensRatings: "Women's Ratings",
        slope: 'Slope',
        cr: 'CR',
        par: 'Par',
        usgaFormula: 'USGA Formula'
      },

      // Stableford score entry
      stablefordEntry: {
        // Section headers
        tournamentRounds: 'Tournament Rounds',
        stablefordHandicapStrokes: 'Stableford Handicap Strokes',
        strokeAllocationForRound: 'Stroke allocation for this round',
        enterYourScore: 'Enter Your Score',
        quickResultEntry: 'Quick result entry - use scorecard for full stats',

        // Round status badges (reuse strokePlayEntry if possible)
        roundStatus: {
          current: 'Current',
          done: 'Done',
          locked: 'Locked',
          upcoming: 'Upcoming',
          completedOrNotAvailable: 'Completed or not yet available'
        },

        // Table headers
        tableHeaders: {
          hole: 'Hole',
          par: 'Par',
          gross: 'Gross',
          net: 'Net',
          points: 'Points',
          stroke: 'Stroke'
        },

        // Stableford points legend
        pointsLegend: {
          birdiePlus: 'Birdie+',
          par: 'Par',
          bogey: 'Bogey',
          twoOverPlus: '+2 Over'
        },

        // Score summary
        holesCompleted: 'Holes completed: {completed}/18',
        gross: 'Gross',
        net: 'Net',
        points: 'Points',

        // Submission
        submissionNotAvailable: 'Submission Not Available',
        submittingScore: 'Submitting...',
        submitForRoundN: 'Submit for Round {number}',
        submitStablefordScore: 'Submit Stableford Score',
        enterHoleScoresToSubmit: 'Enter your hole scores to submit',
        scoreWillBeSubmittedForRound: 'Your score will be submitted for Round {number}',
        scoreWillBeAddedToLeaderboard: 'Your score will be added to the tournament leaderboard',

        // Success/Error messages
        scoreSubmitted: 'Score submitted',
        scoreSubmittedSuccess: 'Score submitted successfully!',
        failedToSubmit: 'Failed to submit score. Please try again.',
        pleaseEnterAtLeastOneHole: 'Please enter at least one hole score',
        cannotSubmitAtThisTime: 'Cannot submit score at this time',

        // Handicap display
        playingHandicapLabel: 'Playing Handicap:',
        courseCalculationBreakdown: 'Course calculation breakdown',

        // Front/Back nine
        frontNine: 'Front Nine',
        backNine: 'Back Nine',
        holes1to9: '1-9',
        holes10to18: '10+',

        // Legend
        receivesStroke: 'Receives Stroke',
        strokeCount: 'Stroke Count'
      },

      // Tee selector
      teeSelector: {
        selectYourTees: 'Select Your Tees',
        chooseWhichTeesYouPlayedFrom: 'Choose which tees you played from',
        rating: 'Rating',
        slope: 'Slope'
      },

      // Loading states
      loading: {
        tournamentData: 'Loading tournament data...',
        tournamentConfig: 'Loading tournament configuration...'
      }
    }
  },

  // Tournament Demo & Testing
  tournamentDemo: {
    title: 'Tournament Demo & Testing',
    safeMode: 'Safe Mode',
    description: 'Test the complete tournament workflow without affecting real data. This demo creates temporary tournaments that can be safely deleted.',

    // Steps
    steps: {
      creatingTournament: 'Creating demo tournament...',
      addingPlayers: 'Adding demo players...',
      addingScores: 'Adding demo scores...',
      ready: 'Demo tournament ready! Check the links below.',
      cleaningUp: 'Cleaning up demo data...'
    },

    // Buttons
    buttons: {
      startDemo: 'Start Demo Tournament',
      creatingDemo: 'Creating Demo...',
      deleteDemoData: 'Delete Demo Data',
      cleaningUp: 'Cleaning Up...',
      viewTournament: 'View Tournament',
      scoreEntry: 'Score Entry',
      leaderboard: 'Leaderboard'
    },

    // Demo data
    tournamentName: 'Demo Tournament - Test Mode',
    demoPlayer: 'Demo Player {number}',

    // Success/Error messages
    messages: {
      created: 'Demo Created Successfully!',
      createdDescription: 'You can now test score entry and leaderboard functionality.',
      creationFailed: 'Demo Creation Failed',
      unknownError: 'Unknown error occurred',
      errorDuringSetup: 'Error occurred during demo setup',
      cleanedUp: 'Demo Cleaned Up',
      cleanedUpDescription: 'All demo data has been removed from the database.',
      cleanupFailed: 'Cleanup Failed'
    },

    // Demo info
    info: {
      title: 'Demo Tournament Created!',
      tournament: 'Tournament:',
      joinCode: 'Join Code:',
      players: 'Players:',
      playersDescription: '{count} demo players added with sample scores'
    },

    // Features list
    features: {
      creates9Hole: 'Demo creates a 9-hole tournament with 3 players and sample scores',
      differentLevels: 'Players have different completion levels to test leaderboard sorting',
      safelyContained: 'All data is safely contained and can be deleted',
      openInNewTabs: 'Open links in new tabs to test functionality',
      tryEnteringScores: 'Try entering additional scores to see real-time updates'
    }
  },

  // Club Event Detail page
  clubEventDetail: {
    // Header
    eventDetails: 'Event Details',
    eventNotFound: 'Event Not Found',

    // Event types
    eventType: '{type} Event',
    socialEvent: 'Social Event',
    golfEvent: 'Golf Event',
    tournamentEvent: 'Tournament Event',

    // Status badges
    live: 'Live',

    // Registration status cards
    adminAccess: 'Admin Access',
    adminAccessDesc: 'You have read access to this event',
    view: 'View',
    youreRegistered: "You're Registered!",
    status: 'Status: {status}',
    statusConfirmed: 'confirmed',
    statusPending: 'pending',
    statusWaitlisted: 'waitlisted',
    play: 'Play',
    cancel: 'Cancel',
    registrationOpen: 'Registration Open',
    joinThisEvent: 'Join this event',
    register: 'Register',

    // Tabs
    tabs: {
      overview: 'Overview',
      participants: 'Participants',
      results: 'Results'
    },

    // Overview tab
    eventInformation: 'Event Information',
    description: 'Description',
    noDescription: 'No description provided.',
    participants: 'Participants',
    free: 'Free',
    entryFee: 'Entry Fee',
    venue: 'Venue',
    schedule: 'Schedule',

    // Participants tab
    registeredParticipants: 'Registered Participants',
    unknownUser: 'Unknown User',
    registeredOn: 'Registered {date}',
    you: 'You',
    noParticipantsYet: 'No participants registered yet.',

    // Share dialog
    shareEvent: 'Share Event',
    shareEventDesc: 'Share this event with others',

    // Action dialog
    registerForEvent: 'Register for Event',
    cancelRegistration: 'Cancel Registration',
    confirmRegistration: 'Confirm your registration for this event',
    confirmCancelQuestion: 'Are you sure you want to cancel your registration?',
    commentOptional: 'Comment (Optional)',
    commentPlaceholder: 'Any additional comments...',
    confirmCancel: 'Confirm Cancel',

    // Payment methods
    paymentMethods: 'Payment Options',
    payOnline: 'Card',
    payAtClub: 'At Club',
    payCash: 'Cash',

    // Payment dialog
    completePayment: 'Complete Payment',
    completePaymentDesc: 'Complete your payment to confirm your registration.',

    // Toast messages
    toasts: {
      registerSuccess: 'Successfully registered for event!',
      registerFailed: 'Failed to register for event',
      cancelSuccess: 'Registration cancelled successfully',
      cancelFailed: 'Failed to cancel registration',
      linkCopied: 'Event link copied to clipboard!',
      registrationCancelled: 'Registration cancelled'
    }
  },

  // Feedback List component
  feedbackList: {
    loading: 'Loading feedback...',
    errorLoading: 'Error loading feedback',
    searchPlaceholder: 'Search feedback...',
    filtersButton: 'Filters',
    clearFilters: 'Clear Filters',
    clearFiltersLink: 'Clear filters',
    sort: {
      newestFirst: 'Newest First',
      oldestFirst: 'Oldest First',
      mostVoted: 'Most Voted',
      recentlyUpdated: 'Recently Updated'
    },
    filterLabels: {
      status: 'Status',
      category: 'Category'
    },
    itemSingular: 'item',
    itemPlural: 'items',
    noFeedbackFound: 'No feedback found'
  },

  // Feedback Submission Form component
  feedbackForm: {
    categories: {
      bug: { label: 'Bug Report', description: 'Report issues or problems' },
      feature: { label: 'Feature Request', description: 'Suggest new features' },
      improvement: { label: 'Improvement', description: 'Suggest enhancements' },
      question: { label: 'Question', description: 'Ask questions' }
    },
    fields: {
      category: 'Category',
      title: 'Title',
      titlePlaceholder: 'Brief summary of your feedback...',
      titleDescription: 'A clear, concise title that describes your feedback',
      description: 'Description',
      descriptionPlaceholder: 'Provide detailed information about your feedback...',
      descriptionHelp: 'Include as much detail as possible. Markdown formatting is supported.',
      visibility: 'Visibility',
      visibilityPlaceholder: 'Select visibility',
      visibilityDescription: 'Control who can see this feedback',
      attachments: 'Attachments (Optional)',
      attachmentsHelp: 'Add screenshots or files to help explain your feedback (max 10MB per file)'
    },
    visibility: {
      public: 'Public - Visible to everyone',
      private: 'Private - Only you and admins',
      internal: 'Internal - Admins only'
    },
    validation: {
      titleRequired: 'Title is required',
      titleMinLength: 'Title must be at least 3 characters',
      titleMaxLength: 'Title must be less than 200 characters',
      descriptionRequired: 'Description is required',
      descriptionMinLength: 'Please provide more details (at least 10 characters)',
      categoryRequired: 'Please select a category'
    },
    buttons: {
      chooseFiles: 'Choose Files',
      cancel: 'Cancel',
      submit: 'Submit Feedback',
      update: 'Update Feedback'
    },
    loading: {
      uploading: 'Uploading...',
      updating: 'Updating...',
      submitting: 'Submitting...'
    },
    messages: {
      submitSuccess: 'Feedback submitted successfully!',
      submitSuccessDescription: "Thank you for your input. We'll review it soon.",
      submitError: 'Failed to submit feedback',
      submitErrorDescription: 'Please try again or contact support if the issue persists.',
      updateSuccess: 'Feedback updated successfully!',
      updateError: 'Failed to update feedback',
      attachmentError: 'Feedback created but some attachments failed to upload',
      fileTooLarge: 'File {name} is too large (max 10MB)'
    }
  },

  // Course Round History page
  courseRoundHistory: {
    title: 'Round History',
    eyebrow: 'Round History',
    heroEyebrow: 'Course History',
    subtitle: 'Your performance at this course',
    emptyState: {
      title: 'No rounds found',
      noRoundsTitle: 'No rounds here yet',
      filterMessage: 'Try adjusting your filters to see more rounds.',
      noRoundsMessage: 'No rounds found for this course.',
      clearFilters: 'Clear Filters'
    },
    summary: {
      par: 'Par',
      rounds: 'Rounds',
      best: 'Best',
      average: 'Average'
    },
    filters: {
      title: 'Filters',
      roundsCount: 'rounds',
      timePeriod: 'Time Period',
      sortBy: 'Sort By',
      order: 'Order',
      scoreRange: 'Score Range',
      timePeriodOptions: {
        all: 'All Time',
        year: 'This Year',
        '90days': 'Last 90 Days',
        month: 'This Month'
      },
      timePeriodSubtitles: {
        all: 'Every round at this course',
        year: 'Past 12 months',
        '90days': 'Last 90 days',
        month: 'Last 30 days'
      },
      sortByOptions: {
        date: 'Date',
        score: 'Score',
        par_differential: 'To Par'
      },
      sortBySubtitles: {
        date: 'Newest first',
        score: 'Best score first',
        par_differential: 'Best vs par first'
      },
      orderOptions: {
        desc: 'Newest First',
        asc: 'Oldest First'
      },
      orderSubtitles: {
        desc: 'Highest / newest first',
        asc: 'Lowest / oldest first'
      },
      scoreRangeOptions: {
        all: 'All Scores',
        personal_best: 'Personal Best',
        recent_best: 'Recent Best',
        above_avg: 'Above Average'
      },
      scoreRangeSubtitles: {
        all: 'Show all rounds',
        personal_best: 'Your lowest ever',
        recent_best: 'Best of last 5',
        above_avg: 'Better than average'
      }
    },
    toast: {
      deleteSuccess: '{roundType} deleted successfully',
      deleteError: 'Failed to delete round',
      roundTypes: {
        multiplayer: 'Multiplayer game',
        solo: 'Round'
      }
    }
  },

  // Achievement translations (for database-stored trophy definitions)
  achievements: {
    firstSteps: {
      name: 'First Steps',
      description: 'Complete your very first round'
    },
    roundCompleted: {
      name: 'Round Completed',
      description: 'Successfully finished a round of golf'
    },
    perfectRoundBronze: {
      name: 'Perfect Round Bronze',
      description: 'Complete a round with no double bogeys or worse'
    },
    scoringMachine: {
      name: 'Scoring Machine',
      description: 'Record 5 rounds with scores under par'
    },
    consistentPlayer: {
      name: 'Consistent Player',
      description: 'Complete 10 rounds in a row'
    },
    birdieCollector: {
      name: 'Birdie Collector',
      description: 'Score 25 birdies across all rounds'
    },
    eagleEye: {
      name: 'Eagle Eye',
      description: 'Score your first eagle'
    },
    perfectRoundSilver: {
      name: 'Perfect Round Silver',
      description: 'Complete a round with no bogeys or worse'
    },
    perfectRoundGold: {
      name: 'Perfect Round Gold',
      description: 'Complete a round with only birdies and eagles'
    },
    milestoneMarker: {
      name: 'Milestone Marker',
      description: 'Complete 50 rounds'
    },
    centuryClub: {
      name: 'Century Club',
      description: 'Complete 100 rounds'
    },
    socialButterfly: {
      name: 'Social Butterfly',
      description: 'Share 10 rounds on your social feed'
    },
    tournamentWinner: {
      name: 'Tournament Winner',
      description: 'Win your first multiplayer tournament'
    },
    holeInOne: {
      name: 'Hole in One',
      description: 'Score an ace on any par 3'
    },
    courseConqueror: {
      name: 'Course Conqueror',
      description: 'Play 25 different courses'
    },
    weekendWarrior: {
      name: 'Weekend Warrior',
      description: 'Complete 5 rounds in a single weekend'
    },
    earlyBird: {
      name: 'Early Bird',
      description: 'Complete a round before 8 AM'
    },
    nightOwl: {
      name: 'Night Owl',
      description: 'Complete a round after 6 PM'
    },
    weatherWarrior: {
      name: 'Weather Warrior',
      description: 'Complete a round in challenging weather conditions'
    },
    comeback: {
      name: 'Comeback',
      description: 'Improve your score by 10 strokes from your previous round'
    }
  },

  // Invite Friends Page
  inviteFriendsPage: {
    title: 'Invite Friends',
    subtitle: 'Share your golf journey',
    loading: 'Preparing your invite...',
    qrCode: {
      title: 'Scan to Join',
      description: 'Have your friends scan this QR code to join you on the course',
      shareButton: 'Share Invite Link'
    },
    benefits: {
      title: 'Why invite friends?',
      playTogether: {
        title: 'Play Together',
        description: 'Join multiplayer rounds and compete in real-time'
      },
      competition: {
        title: 'Friendly Competition',
        description: 'Track progress and challenge each other to improve'
      },
      clubs: {
        title: 'Join Golf Clubs',
        description: 'Discover and join exclusive golf communities together'
      }
    },
    errors: {
      generateLink: 'Failed to generate invite link',
      generateLinkDescription: 'Please refresh the page to try again.',
      copyFailed: 'Failed to copy',
      copyFailedDescription: 'Please copy the link manually.'
    },
    success: {
      shared: 'Shared successfully!',
      sharedDescription: 'Your invite has been shared.',
      copied: 'Link copied!',
      copiedDescription: 'Invite link has been copied to your clipboard.'
    },
    shareData: {
      title: 'Join me on GolfScore!',
      text: 'Track your golf rounds, compete with friends, and improve your game. Join me on GolfScore!'
    }
  },

  // Course Records Page
  courseRecords: {
    title: 'Course Records',
    subtitle: 'Explore club information and courses',
    loading: 'Loading course records...',
    course: 'Course',
    championshipConditions: 'Championship Conditions',
    hallOfFame: {
      title: 'HALL OF FAME',
      subtitle: '{courseName} Championship Record',
      noRecordSet: 'NO RECORD SET',
      recordHolder: 'Course Record Holder',
      noRecordYet: 'No course record has been set yet',
      beTheFirst: 'Be the first to set the course record!',
      courseRecord: 'COURSE RECORD',
      under: 'UNDER',
      over: 'OVER',
      par: 'PAR',
      blackTees: 'Black'
    },
    teeNames: {
      black: 'Black',
      blue: 'Blue', 
      white: 'White',
      red: 'Red'
    },
    scoreTypes: {
      title: 'Score Type',
      gross: 'Gross Score',
      net: 'Net Score',
      grossDescription: 'Raw stroke count (no handicap adjustment)',
      netDescription: 'Handicap-adjusted scores using proper golf rules'
    },
    recordTimeline: {
      title: 'Record Timeline',
      currentRecord: 'Current Record',
      recordHolder: 'Course Record Holder',
      unknownPlayer: 'Unknown Player',
      noHistory: 'No record history available',
      recordsAppear: 'Records will appear here as they are set'
    },
    holeRecords: {
      frontNine: 'Front Nine Records',
      backNine: 'Back Nine Records',
      hole: 'Hole',
      par: 'Par',
      noRecord: 'No Record',
      notSet: 'Not Set',
      noRecordsAvailable: 'No Records Available',
      notSetUp: 'Records Not Set Up',
      unavailable: 'Records Unavailable',
      systemError: 'System Error'
    },
    scoreLabels: {
      albatross: 'Albatross',
      eagle: 'Eagle',
      birdie: 'Birdie',
      par: 'Par',
      ace: 'ACE!',
      record: 'Record'
    },
    clubRecord: {
      title: 'Your Club Record',
      personalBest: 'Personal Best',
      clubMember: 'Club Member',
      activeMember: 'Active Member',
      courseRanking: 'Course Ranking',
      ranked: 'Ranked',
      noScoreYet: 'No score yet',
      golfClub: 'Golf Club'
    },
    errors: {
      unableToLoad: 'Unable to load course records',
      tryAgain: 'Try Again',
      failedToLoad: 'Failed to load course records',
      noCourses: 'No courses found for this club.',
      navigateFromCourse: 'Navigate to this page from a specific course',
      goToPerformanceZones: 'Go to Performance Zones'
    }
  },

  // Course Analytics Page
  courseAnalytics: {
    title: 'Course Performance Analytics',
    loading: 'Loading course analytics...'
  },

  // Club Admin translations
  clubAdmin: {
    // Main dashboard
    dashboard: {
      title: 'Club Overview',
      subtitle: 'Monitor your club\'s performance and activity',
      timeframes: {
        daily: 'Daily View',
        weekly: 'Weekly View', 
        monthly: 'Monthly View',
        yearly: 'Yearly View'
      }
    },
    
    // Navigation
    navigation: {
      dashboard: 'Dashboard',
      members: 'Members',
      memberships: 'Memberships',
      memberProgress: 'Member Progression',
      courses: 'Courses',
      bookings: 'Bookings',
      carts: 'Golf Carts',
      events: 'Events',
      communication: 'Communication',
      announcements: 'Announcements',
      analytics: 'Analytics',
      settings: 'Settings',
      admin: 'Admin',
      home: 'Home',
      feedback: 'Feedback',
      // Staff section
      staffManagement: 'Staff Management',
      staffSchedule: 'Staff Schedule',
      people: 'People',
      // Tee Sheet section
      teeSheet: 'Tee Sheet',
      teeBookings: 'Bookings',
      checkIn: 'Check-in',
      calendar: 'Calendar',
      // Services section
      services: 'Services',
      range: 'Range',
      lockers: 'Lockers',
      configuration: 'Configuration',
      // Revenue section
      revenue: 'Revenue',
      transactions: 'Transactions',
      paymentSettings: 'Payment Settings',
      // Coaching section
      coaching: 'Coaching',
      myProfile: 'My Profile',
      // Settings section
      clubProfile: 'Club Profile',
      coaches: 'Coaches',
      // Reports section
      reports: 'Reports',
      overview: 'Overview',
      memberEngagement: 'Member Engagement',
      reportsRevenue: 'Revenue',
      communications: 'Communications'
    },

    // Page headers
    headers: {
      memberManagement: 'Member Management',
      bookingManagement: 'Booking Management',
      courseManagement: 'Course Management',
      cartManagement: 'Golf Cart Management',
      communication: 'Communication',
      announcementManagement: 'Announcement Management',
      eventsManagement: 'Events & Tournaments',
      analyticsHeader: 'Analytics Dashboard',
      clubSettings: 'Club Settings',
      teeSheet: 'Tee Sheet',
      checkInStation: 'Check-in Station',
      teeSheetCalendar: 'Tee Sheet Calendar',
      teeSheetAnalytics: 'Tee Sheet Analytics',
      teeSheetSettings: 'Tee Sheet Settings',
      rangeManagement: 'Range Management',
      lockerManagement: 'Locker Management',
      servicesConfig: 'Services Configuration',
      revenueDashboard: 'Revenue Dashboard',
      staffSchedule: 'Staff Schedule',
      peopleManagement: 'People Management',
      membershipManagement: 'Membership Management',
      coachingPortal: 'Coaching Portal',
      coachingSettings: 'Coaching Settings',
      coachRoster: 'Coach Roster',
      giveFeedback: 'Give Feedback',
      managingClub: 'Managing {clubName}',
      defaultManagement: 'Manage your golf club efficiently'
    },

    // Page descriptions
    descriptions: {
      memberManagement: 'Manage club members, roles, and membership status',
      staffSchedule: 'Manage tasks, schedules, and staff communication',
      peopleManagement: 'Manage staff members, administrators, and invitations',
      membershipManagement: 'Manage membership tiers, renewals, and applications',
      coachingPortal: 'Manage your students, sessions, exercises, and video content',
      coachingSettings: 'Configure coaching availability and booking settings',
      coachRoster: 'Manage coaches affiliated with your club — approve requests, invite coaches, and remove affiliations.',
      giveFeedback: 'Help us improve! Share your ideas, report bugs, or ask questions.',
      announcementManagement: 'Create and manage club announcements',
      revenueDashboard: 'Track revenue, transactions, and financial performance',
      transactions: 'View and manage all payment transactions',
      paymentSettings: 'Configure payment methods and billing settings',
      bookingManagement: 'Manage tee times, course availability, and member bookings',
      courseManagement: 'Manage course information, maintenance, and availability',
      teeSheet: 'Manage daily tee time availability',
      checkInStation: 'Pro shop check-in for arriving players',
      teeSheetCalendar: 'Manage availability and block times. Slots are automatically available based on your tee sheet configuration.',
      teeSheetAnalytics: 'View booking analytics and utilization insights',
      teeSheetSettings: 'Configure booking rules and operating hours',
      cartManagement: 'Manage golf cart inventory, bookings, and scheduling',
      rangeManagement: 'Manage range buckets, credits, and range card sales',
      lockerManagement: 'Manage locker assignments, renewals, and availability',
      servicesConfig: 'Enable or disable services available to members',
      communication: 'Manage communications, engage members, and track communication tasks',
      eventsManagement: 'Create and manage club events, tournaments, and activities',
      analyticsDescription: 'View comprehensive analytics and performance metrics',
      clubSettings: 'Configure club settings and preferences'
    },

    // Daily Briefing Dashboard
    dailyBriefing: {
      goodMorning: 'Good Morning',
      subtitle: "Here's what's happening at your club today",
      refresh: 'Refresh',
      lastUpdated: 'Last updated',

      // Operations
      todaysOperations: "Today's Operations",
      teeSheet: 'Tee Sheet',
      todaysEvents: "Today's Events",
      weather: 'Weather',
      courseStatus: 'Course Status',
      booked: 'booked',
      checkedIn: 'checked in',
      pending: 'pending',
      noShow: 'no-show',
      noEventsScheduled: 'No events scheduled',
      courseOpen: 'All holes open for play',
      courseClosed: 'Course is closed',
      coursePartial: 'Some holes under maintenance',

      // Pending Actions
      pendingActions: 'Pending Actions',
      membershipApplications: 'Membership Applications',
      unreadMessages: 'Unread Messages',
      expiringThisMonth: 'Expiring This Month',
      pendingRegistrations: 'Pending Registrations',
      allCaughtUp: 'All caught up! No pending actions.',
      itemsNeedAttention: 'items need attention',

      // Alerts
      alertsNotifications: 'Alerts & Notifications',
      atRiskMembers: 'At-Risk Members',
      expiringMemberships: 'Expiring Memberships',
      lowEventRegistration: 'Low Event Registration',
      pendingApplications: 'Pending Applications',

      // Milestones
      memberMilestones: 'Member Milestones',
      thisWeek: 'this week',
      birthdays: 'Birthdays',
      anniversaries: 'Anniversaries',
      newMembers: 'New Members',
      achievements: 'Achievements',
      yearMember: 'year member',
      yearsMember: 'years member',

      // Weekly Snapshot
      weeklySnapshot: 'Weekly Snapshot',
      thisWeeksRevenue: "This Week's Revenue",
      roundsPlayed: 'Rounds Played',
      eventAttendance: 'Event Attendance',

      // GM Command Center sections
      clubHealthScore: 'Club Health Score',
      healthGood: 'Good',
      healthNeedsAttention: 'Needs Attention',
      healthAtRisk: 'At Risk',
      weekNarrative: 'This Week',
      memberLifecycle: 'Member Lifecycle',
      revenueBySource: 'Revenue by Source',
      coachingPerformance: 'Coaching Performance',
      eventROI: 'Event Performance',
      handicapTrends: 'Handicap Trends',
      weatherImpact: 'Weather Impact',
      commsPerformance: 'Communications',
      memberEngagementScore: 'Member Engagement',

      // Greeting variants
      goodAfternoon: 'Good afternoon',
      goodEvening: 'Good evening',

      // Dashboard section labels
      sectionToday: 'Today',
      sectionTodaySub: 'Operational status, actions & celebrations',
      sectionMembers: 'Members',
      sectionMembersSub: 'Lifecycle, engagement & onboarding',
      sectionMoneyService: 'Money & service',
      sectionMoneyServiceSub: 'Revenue mix, coaching utilisation',
      sectionService: 'Service',
      sectionServiceSub: 'Coaching utilisation',
      sectionPerformance: 'Performance & Communications',
      sectionPerformanceSub: 'Communications & outcomes',

      // Error state
      someDataFailed: 'Some data failed to load',

      // Alert titles (used for routing click handlers)
      alertRevenueDrop: 'Revenue Drop',
      alertTeeSheetDrop: 'Tee Sheet Drop',
      alertNewMembersNotOnboarding: 'New Members Not Onboarding',

      // Health ring label
      clubHealth: 'Club Health',

      // Pillar bar labels
      pillarRetention: 'Retention',
      pillarTeeSheet: 'Tee Sheet',
      pillarRevenue: 'Revenue',
      pillarEngagement: 'Engagement',
      pillarSatisfaction: 'Satisfaction',

      // KPI tile labels
      kpiRevenue: 'Revenue',
      kpiRounds: 'Rounds',
      kpiEventAtt: 'Event att.',
      kpiNewMembers: 'New members',
      kpiLessons: 'Lessons',

      // At-risk drawer — email/chat side panels
      emailEyebrow: 'Send re-engagement email',
      sendMessageEyebrow: 'Send message',
      cancel: 'Cancel',
      sendEmail: 'Send email',
      sending: 'Sending…',
      emailTo: 'To',
      emailSubject: 'Subject',
      emailMessage: 'Message',
      openingConversation: 'Opening conversation…',

      // At-risk drawer — churned label
      atRiskChurned: 'Churned',

      // Handicap drawer — tooltip
      handicapTooltipHandicap: 'Handicap',
      handicapTooltip30DayChange: '30-day change',
      handicapTooltipImproved: 'Improved',
      handicapTooltipDeclined: 'Declined',
      handicapTooltipStable: 'Stable',
      handicapTooltipNoData: 'Not enough member data',
      handicapNotSet: 'Not set',

      // Milestones section
      noMilestonesThisWeek: 'No milestones this week',
      checkBackLater: 'Check back later',
      milestonesUpcomingEvents: 'Upcoming events',
      milestonesViewAll: 'View all',
      sendCelebrationEmail: 'Send celebration email',
      milestoneBirthday: 'Birthday',
      milestoneNewMember: 'New member',
      milestoneAchievement: 'Achievement',
      milestoneYear: 'year',
      milestoneYears: 'years',

      // Comms drawer — top-performer views label
      topPerformerViews: 'views',

      // Event performance drawer — upcoming tab
      evtTabPast: 'Past events',
      evtTabUpcoming: 'Upcoming',
      evtUpcomingOnTrack: 'On Track',
      evtUpcomingNeedsAttention: 'Needs attention',
      evtUpcomingGe80: '≥ 80% full',
      evtUpcomingLt60: '< 60% full',
      evtRegistrationFillRate: 'Registration fill rate',
      evtRegistered: 'registered',
      evtInDays: 'in',
      evtNoUpcoming: 'No upcoming events',
      evtNoUpcomingHint: 'Events scheduled in the next 60 days will appear here.',
      evtNotEnoughWeeklyData: 'Not enough weekly data yet.',

      // Messages drawer — empty state
      messagesNoUnread: 'No unread messages',

      // Engagement drawer — labels
      engInactive: 'inactive',
      engUnassigned: 'Unassigned',
      engRoundsLegend: 'rounds',
      engEventsLegend: 'events',
      engCoachingLegend: 'coaching',

      // Expiring memberships — empty state
      expiringNoMemberships: 'No memberships expiring this month',

      // New members drawer — empty state + panel labels
      newMembersNoneStuck: 'No members stuck in onboarding',
      newMembersSendInApp: 'Send in-app message',
      newMembersSendWelcomeEyebrow: 'Send welcome email',
      newMembersSendMessageEyebrow: 'Send message',

      // Event registrations — empty state
      eventRegNoPending: 'No pending registrations',

      // Revenue by source — source labels
      revSourceGreenFees: 'Green Fees',
      revSourceMemberships: 'Memberships',
      revSourceProShop: 'Pro Shop',
      revSourceLessons: 'Lessons',

      // Shared email/chat panel labels (reused across several drawers)
      emailPanelTo: 'To',
      emailPanelSubject: 'Subject',
      emailPanelMessage: 'Message',
      emailPanelCancel: 'Cancel',
      emailPanelSendEmail: 'Send email',
      emailPanelSending: 'Sending…',
      chatOpeningConversation: 'Opening conversation…'
    },

    // Metrics
    metrics: {
      totalMembers: 'Total Members',
      activeToday: 'Active Today',
      activeThisWeek: 'Active This Week', 
      activeThisMonth: 'Active This Month',
      activeThisYear: 'Active This Year',
      newSignups: 'New Signups',
      courseRounds: 'Course Rounds',
      channelMessages: 'Channel Messages',
      eventsCreated: 'Events Created',
      vsLastPeriod: '+{change}% vs last period',
      vsYesterday: '+{change} from yesterday',
      vsLastWeek: '+{change} from last week',
      vsLastMonth: '+{change} from last month',
      vsLastYear: '+{change} from last year'
    },
    
    // Activity chart
    activityChart: {
      title: 'Member Activity Trends',
      memberActivity: 'Member Activity',
      timeframes: {
        daily: 'Daily',
        weekly: 'Weekly',
        monthly: 'Monthly',
        yearly: 'Yearly'
      },
      dayLabels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      weekLabels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
      monthLabels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      yearLabels: ['2020', '2021', '2022', '2023', '2024'],
      stats: {
        peakActivity: 'Peak Activity',
        average: 'Average',
        growth: 'Growth'
      }
    },
    
    // Recent activity
    recentActivity: {
      title: 'Recent Activity',
      viewAll: 'View All',
      actions: {
        joinedClub: 'joined the club',
        postedRound: 'posted a round on',
        postedMessage: 'posted in',
        createdTournament: 'created tournament',
        earnedAchievement: 'earned achievement'
      },
      timeAgo: {
        minutesAgo: '{minutes} minutes ago',
        hourAgo: '{hours} hour ago',
        hoursAgo: '{hours} hours ago'
      },
      emptyState: {
        title: 'No recent activity',
        description: 'Activity will appear here as members interact with the club'
      },
      summary: {
        recentActions: 'Recent Actions',
        newMembers: 'New Members',
        engagement: 'Engagement'
      }
    },
    
    // Pending tasks
    pendingTasks: {
      title: 'Needs Attention',
      pendingCount: '{count} pending tasks requiring action',
      urgentCount: '{count} urgent',
      taskTitles: {
        membershipApplications: 'Membership Applications',
        courseMaintenance: 'Course Maintenance Schedule',
        memberComplaints: 'Member Complaints',
        tournamentSetup: 'Tournament Setup',
        systemUpdate: 'System Update Required'
      },
      taskDescriptions: {
        reviewApplications: 'Review and approve new member requests',
        updateMaintenance: 'Update maintenance calendar for Championship Course',
        addressComplaints: 'Address unresolved member feedback',
        configureTournament: 'Configure Spring Championship tournament details',
        updateSystem: 'Update club information and policies'
      },
      dueDates: {
        dueInDays: 'Due in {days} days'
      },
      priorities: {
        high: 'high',
        medium: 'medium',
        low: 'low'
      },
      actions: {
        markComplete: 'Mark as complete',
        viewDetails: 'View details'
      },
      emptyState: {
        title: 'All caught up!',
        description: 'No pending tasks requiring attention'
      },
      summary: {
        totalTasks: 'Total Tasks',
        highPriority: 'High Priority',
        mediumPriority: 'Medium Priority',
        completionRate: 'Completion Rate'
      }
    },
    
    // Common actions
    actions: {
      addCourse: 'Add Course',
      export: 'Export',
      newBooking: 'New Booking',
      addCart: 'Add Cart',
      newCartBooking: 'New Booking',
      createChannel: 'Create Channel',
      newAnnouncement: 'New Announcement',
      inviteMember: 'Invite Member',
      bulkImport: 'Bulk Import',
      filters: 'Filters',
      saving: 'Saving...',
      saveChanges: 'Save Changes',
      scheduleShift: 'Schedule Shift',
      createTask: 'Create Task'
    },

    // Date ranges
    dateRanges: {
      last7Days: 'Last 7 days',
      last30Days: 'Last 30 days',
      last3Months: 'Last 3 months',
      lastYear: 'Last year'
    },
    
    // Status messages
    status: {
      adminPanel: 'Admin Panel',
      noClubSelected: 'No Club Selected',
      unableToLoadClub: 'Unable to load club data',
      clubAdmin: 'Club Admin'
    },

    // Staff Tasks
    staffTasks: {
      category: 'Category',
      estimated: 'Estimated',
      searchStaff: 'Search Staff',
      filterByRole: 'Filter by Role',
      allRoles: 'All roles',
      selectStaffMember: 'Select Staff Member',
      noStaffFound: 'No staff members found matching your criteria.',
      description: 'Description',
      dueDate: 'Due Date',
      assignedTo: 'Assigned To',
      selectStaffOrUnassigned: 'Select staff member or leave unassigned',
      unassigned: 'Unassigned',
      assignmentChange: 'Assignment Change',
      removingAssignment: 'Removing Assignment',
      editingCompletedTask: 'Editing Completed Task',
      tasksWillAppear: 'Tasks will appear here when available',
      createNewTask: 'Create New Task',
      taskTitle: 'Task Title *',
      priority: 'Priority',
      assignTo: 'Assign To',
      selectStaffOptional: 'Select staff member (optional)',
      estimatedTimeLabel: 'Estimated Time (minutes)',
      cancel: 'Cancel',
      createTask: 'Create Task',
      creatingTask: 'Creating...',
      categoryMaintenance: 'Maintenance',
      categoryRange: 'Driving Range',
      categoryProShop: 'Pro Shop',
      categoryFoodService: 'Food Service',
      categoryCartService: 'Cart Service',
      categoryOther: 'Other',
      priorityLow: 'Low',
      priorityNormal: 'Normal',
      priorityHigh: 'High',
      priorityUrgent: 'Urgent',
      taskCreatedSuccess: 'Task created successfully',
      failedToCreateTask: 'Failed to create task: {error}',
      titleRequired: 'Task title is required',
      // CreateCommunicationTaskModal
      createCommunicationTask: 'Create Communication Task',
      createCommunicationTaskDesc: 'Create a task related to member communications or requests.',
      categoryMemberCommunications: 'Member Communications',
      categoryMaintenanceRequest: 'Maintenance Request',
      assignToOptional: 'Assign To (optional)',
      pleaseEnterTaskTitle: 'Please enter a task title',
      // TaskEditModal
      editTask: 'Edit Task',
      titleLabel: 'Title *',
      categoryLabel: 'Category *',
      priorityLabel: 'Priority *',
      estimatedTimeMinutes: 'Estimated Time (minutes)',
      currentlyAssignedTo: 'Currently assigned to:',
      reassignTask: 'This will reassign the task to a different staff member.',
      assignUnassignedTask: 'This will assign the currently unassigned task to a staff member.',
      unassignTask: 'This will unassign the task and make it available for claiming again.',
      editingCompletedTaskWarning: 'This task is already completed. Changes will be saved but won\'t affect the completion status.',
      updateTask: 'Update Task',
      taskUpdatedSuccess: 'Task updated successfully!',
      taskUpdatedAndAssigned: 'Task updated and assigned to {name}!',
      taskUpdatedAndUnassigned: 'Task updated and unassigned!',
      failedToUpdateTask: 'Failed to update task. Please try again.',
      estimatedTimeMustBePositive: 'Estimated time must be a positive number',
    },

    // Staff Schedule (CreateShiftDialog)
    staffSchedule: {
      scheduleNewShift: 'Schedule New Shift',
      staffMember: 'Staff Member',
      selectStaffMember: 'Select staff member',
      date: 'Date',
      startTime: 'Start Time',
      endTime: 'End Time',
      duration: 'Duration',
      hours: 'hours',
      role: 'Role',
      notes: 'Notes',
      notesPlaceholder: 'Optional notes about this shift',
      // Roles
      roles: {
        manager: 'Manager',
        rangeAttendant: 'Range Attendant',
        cartAttendant: 'Cart Attendant',
        maintenance: 'Maintenance',
        proShop: 'Pro Shop',
        foodService: 'Food Service',
      },
      // Conflict detection
      checkingConflicts: 'Checking for scheduling conflicts...',
      conflictDetected: 'Scheduling Conflict Detected!',
      conflictMessage: 'This staff member already has a shift scheduled during this time period. Please choose a different time or staff member.',
      noConflicts: 'No scheduling conflicts found. This shift can be created.',
      // Buttons
      cancel: 'Cancel',
      scheduling: 'Scheduling...',
      scheduleShift: 'Schedule Shift',
      // Toasts
      shiftScheduledSuccess: 'Shift scheduled successfully',
      failedToScheduleShift: 'Failed to schedule shift: {error}',
      selectStaffMemberError: 'Please select a staff member',
      fillRequiredFields: 'Please fill in all required fields',
      endTimeAfterStart: 'End time must be after start time',
    },

    // Shift Calendar (ShiftCalendar.tsx)
    shiftCalendar: {
      today: 'Today',
      noShiftsScheduled: 'No shifts scheduled for this day',
      createShiftToStart: 'Create a new shift to get started',
      addShift: 'Add Shift',
      unknown: 'Unknown',
      staff: 'Staff',
      unassigned: 'Unassigned',
      clockIn: 'Clock In',
      clockOut: 'Clock Out',
      hoursScheduled: 'h scheduled',
      weekOf: 'Week of',
      swipeToSeeFullWeek: 'Swipe to see full week',
      time: 'Time',
      myShifts: 'My Shifts',
      working: 'Working',
      completed: 'Completed',
      missed: 'Missed',
    },

    // Events & subpages (/club-admin/events/*)
    events: {
      totalEvents: 'Total Events',
      upcoming: 'Upcoming',
      active: 'Active',
      completed: 'Completed',
      past: 'Past',
      noEventsFound: 'No events found',
      noUpcomingEvents: 'No upcoming events',
      noActiveEvents: 'No active events',
      noPastEvents: 'No past events',
      createFirstEvent: 'Create your first event',
      eventsWillAppearHere: 'Events will appear here',
      filterByType: 'Filter by type',
      filterByStatus: 'Filter by status',
      searchEventsPlaceholder: 'Search events...',
      pleaseLogInToCreate: 'Please log in to create events',
      selectClubToCreate: 'Please select a valid club to create events',
      eventCreatedSuccess: 'Event created successfully!',
      confirmDeleteEvent: 'Are you sure you want to delete this event? This will also remove all registrations for this event.',
      eventDeletedSuccess: 'Event deleted successfully',
      failedToDeleteEvent: 'Failed to delete event',
      eventPublishedSuccess: 'Event published successfully',
      eventUnpublishedSuccess: 'Event unpublished successfully',
      failedToUpdatePublication: 'Failed to update event',
      analytics: 'Analytics',
      eventManagement: 'Event Management',
      eventShort: 'Event',
      event: 'Event',
      back: 'Back',
      editEvent: 'Edit Event',
      deleteEvent: 'Delete Event',
      deleting: 'Deleting...',
      confirmDeleteEventPermanent: 'Are you sure you want to delete this event? This action cannot be undone.',
      eventNotFound: 'Event not found',
      eventNotFoundDescription: 'The requested event could not be loaded.',
      startDate: 'Start Date',
      registrations: 'Registrations',
      waitlisted: 'waitlisted',
      free: 'Free',
      markWhoAttended: 'Mark who attended the social event',
      trackParticipation: 'Track participation and progress',
      unknown: 'Unknown',
      registrationsTab: 'Registrations',
      tournamentTab: 'Tournament',
      attendanceTab: 'Attendance',
      leagueManagementTab: 'League',
      matchResolutionTab: 'Match Resolution',
      communicationsTab: 'Communications',
      manageEvent: 'Manage Event',
      viewDetails: 'View Details',
      viewDetailsDesc: 'Complete event information and settings',
      manageRegistrations: 'Manage Registrations',
      manageRegistrationsDesc: '{confirmed} confirmed, {waitlist} waitlisted',
      tournamentManagement: 'Tournament Management',
      tournamentManagementDesc: 'Manage matchplay tournament bracket',
      trackAttendance: 'Track Attendance',
      leagueManagement: 'League Management',
      leagueManagementDesc: 'View standings, match history, and scheduled games',
      matchResolution: 'Match Resolution',
      matchResolutionDesc: 'Resolve disputed or incomplete match results',
      communicationsDesc: 'Send updates and messages to participants',
      // Event Registrations
      member: 'Member',
      status: 'Status',
      joinDate: 'Join Date',
      waitlistPosition: 'Waitlist Position',
      notes: 'Notes',
      actions: 'Actions',
      noRegistrations: 'No registrations yet',
      noRegistrationsSearch: 'No registrations match your search criteria',
      unknownUser: 'Unknown User',
      backToEvents: 'Back to Events',
      exportComingSoon: 'Export functionality coming soon',
      registrationCancelledSuccess: 'Registration cancelled successfully',
      promotedFromWaitlistSuccess: 'Member promoted from waitlist successfully',
      noSpotsToPromote: 'No available spots to promote this member',
      failedToCancelRegistration: 'Failed to cancel registration',
      failedToUpdateRegistration: 'Failed to update registration status',
      adminCancellation: 'Admin cancellation',
      // Registration management actions
      register: 'Register',
      bulk: 'Bulk',
      export: 'Export',
      confirmed: 'Confirmed',
      cancelled: 'Cancelled',
      available: 'Available',
      promoteToConfirmed: 'Promote to Confirmed',
      removeFromWaitlist: 'Remove from Waitlist',
      moveToWaitlist: 'Move to Waitlist',
      confirm: 'Confirm',
      // Event Communications
      eventCommunications: 'Event Communications',
      communicationsShort: 'Communications',
      manageNotificationsAndMessaging: 'Manage event notifications and messaging',
      createCommunication: 'Create Communication',
      createNewCommunication: 'Create New Communication',
      sendMessage: 'Send Message',
      editCommunication: 'Edit Communication',
      messageType: 'Message Type',
      recipients: 'Recipients',
      confirmation: 'Confirmation',
      reminder: 'Reminder',
      update: 'Update',
      cancellation: 'Cancellation',
      allParticipants: 'All participants',
      provideSubjectAndMessage: 'Please provide both subject and message',
      selectScheduleDate: 'Please select a date and time for scheduling',
      eventInfoNotAvailable: 'Event information not available',
      authenticationRequired: 'Authentication required',
      communicationScheduledSuccess: 'Communication {action} for {date}',
      failedToSendMessage: 'Failed to send message',
      editCancelled: 'Edit cancelled',
      communicationLoadedForEdit: 'Communication loaded for editing. Make your changes and update.',
      scheduledCommunicationCancelled: 'Scheduled communication cancelled',
      failedToCancelCommunication: 'Failed to cancel communication',
      subject: 'Subject',
      message: 'Message',
      schedule: 'Schedule',
      sendNow: 'Send now',
      scheduleForLater: 'Schedule for later',
      pastCommunications: 'Past communications',
      sent: 'Sent',
      failed: 'Failed',
      scheduled: 'Scheduled',
      pending: 'Pending',
      // Event Analytics
      eventsLabel: 'Events',
      exportLabel: 'Export',
      last30Days: 'Last 30 Days',
      last90Days: 'Last 90 Days',
      thisYear: 'This Year',
      selectClubForAnalytics: 'Please select a club to view analytics.',
      failedToLoadAnalytics: 'Failed to load analytics data.',
      // Event Analytics - KPI Cards
      totalParticipants: 'Total Participants',
      avgFillRate: 'Avg Fill Rate',
      eventRevenue: 'Event Revenue',
      eventsByType: 'Event Distribution',
      // Event Analytics - Performance Sidebar
      eventPerformance: 'Event Performance',
      revenueByType: 'Revenue by Type',
      fillRateByType: 'Fill Rate by Type',
      registrationTimingTitle: 'Registration Timing',
      earlyBirdLabel: 'Early Bird (7+ days)',
      standardLabel: 'Standard (3-7 days)',
      lastMinuteLabel: 'Last Minute (1-3 days)',
      dayOfLabel: 'Day-of',
      avgLeadTime: 'Avg lead time: {days} days',
      topParticipantsTitle: 'Top Participants',
      eventsCount: '{count} events',
      noParticipantData: 'No participant data available',
      quickInsightsTitle: 'Quick Insights',
      mostPopular: 'Most Popular',
      bestDay: 'Best Day',
      lowestNoShows: 'Lowest No-Shows',
      growthOpportunity: 'Growth Opportunity',
      // Event Analytics - Recent Events Table
      recentEventsTitle: 'Recent Events',
      eventColumn: 'Event',
      typeColumn: 'Type',
      dateColumn: 'Date',
      capacityColumn: 'Capacity',
      fillColumn: 'Fill',
      attendedColumn: 'Attended',
      revenueColumn: 'Revenue',
      mainCourse: 'Main Course',
      noShowsLabel: '{count} no-shows',
      showingXOfY: 'Showing {showing} of {total} events',
      noEventsMatchSearch: 'No events match your search',
      noRecentEventsLabel: 'No recent events',
      previousButton: 'Previous',
      nextButton: 'Next',
      tournamentLabel: 'Tournament',
      competitionLabel: 'Competition',
      socialLabel: 'Social',
      leagueLabel: 'League',
      // Event Analytics - Trend Chart
      trendTitle: 'Trend',
      participantsChartLabel: 'Participants',

      // Page titles
      pageTitle: 'Events Management',
      createEvent: 'Create Event',

      // Filters
      allTypes: 'All Types',
      tournaments: 'Tournaments',
      competitions: 'Competitions',
      socialEvents: 'Social Events',
      activities: 'Activities',
      allEvents: 'All Events',
      publishedOnly: 'Published Only',
      draftsOnly: 'Drafts Only',

      // Status badges
      statusCompleted: 'Completed',
      statusActive: 'Active',
      statusUpcoming: 'Upcoming',
      statusScheduled: 'Scheduled',
      statusDraft: 'Draft',

      // Empty states
      noClubSelected: 'No club selected',
      selectClubToManage: 'Please select a club to manage events',

      // Event card
      registered: 'Registered',
      waitlist: 'Waitlist',
      spotsLeft: 'spots left',
      full: 'Full',
      manage: 'Manage',
      communications: 'Communications',
      scores: 'Scores',
      bracket: 'Bracket',
      leaderboard: 'Leaderboard',
      publish: 'Publish',
      unpublish: 'Unpublish',
      editSettings: 'Edit Settings',

      // Tournament specific
      strokePlay: 'Stroke Play',
      matchPlay: 'Match Play',
      stableford: 'Stableford',
      singleRound: 'Single Round',
      multiRound: 'Multi-Round',
      championship: 'Championship',
      league: 'League',
      roundOf: 'Round {current} of {total}',
      currentRound: 'Current Round',
      semifinals: 'Semifinals',
      quarterfinals: 'Quarterfinals',
      finals: 'Finals',
      strokePlayChampionship: 'Stroke Play Championship',
      matchPlayTournament: 'Match Play Tournament',
      participants: 'participants',
      matchesInProgress: 'matches in progress',
      roundsComplete: 'rounds complete',
      matchesComplete: 'matches complete',
      roundOf16: 'Round of 16',
      notify: 'Notify',

      // Event creation wizard
      eventType: 'Event Type',
      tournamentFormat: 'Tournament Format',
      leagueConfiguration: 'League Configuration',
      eventDetails: 'Event Details',
      configuration: 'Configuration',
      type: 'Type',
      format: 'Format',
      configure: 'Configure',
      details: 'Details',
      createNewEvent: 'Create New Event',
      selectEventType: 'Select Event Type',
      selectTournamentFormat: 'Select Tournament Format',
      configureSettings: 'Configure Settings',
      enterEventDetails: 'Enter Event Details',

      // Event types descriptions
      tournamentDesc: 'Competitive golf event with scoring and rankings',
      competitionDesc: 'Club competition with prizes and standings',
      socialEventDesc: 'Casual social gathering for members',
      activityDesc: 'Club activity or workshop',
      leagueDesc: 'Ongoing league with multiple matches',

      // Event type selection wizard
      whatKindOfEvent: 'What kind of event are you planning?',
      selectFormatDescription: 'Select the format that best matches your competition style',
      singleRoundTitle: 'Single Round',
      singleRoundSubtitle: 'One-day competition',
      singleRoundDescription: 'Traditional tournament or social round',
      championshipTitle: 'Championship',
      championshipSubtitle: 'Multi-round tournament',
      championshipDescription: 'Multiple rounds with cumulative scoring',
      leagueTitle: 'League',
      leagueSubtitle: 'Ongoing competition',
      leagueDescription: 'Matchplay challenges with rankings',
      socialTitle: 'Social',
      socialSubtitle: 'Community event',
      socialDescription: 'Casual gatherings and activities',
      activityTitle: 'Activity',
      activitySubtitle: 'Learning event',
      activityDescription: 'Lessons, clinics, and workshops',
      popularBadge: 'Popular',
      newBadge: 'New',
      continueButton: 'Continue',

      // Form labels
      eventName: 'Event Name',
      eventNamePlaceholder: 'Enter event name',
      description: 'Description',
      descriptionPlaceholder: 'Enter event description',
      endDate: 'End Date',
      location: 'Location',
      locationPlaceholder: 'Enter location',
      capacity: 'Capacity',
      capacityPlaceholder: 'Maximum participants',
      entryFee: 'Entry Fee',
      entryFeePlaceholder: 'Entry fee amount',
      registrationDeadline: 'Registration Deadline',
      selectCourse: 'Select Course',
      selectTees: 'Select Tees',

      // Registration
      openRegistration: 'Open Registration',
      closeRegistration: 'Close Registration',
      registrationOpen: 'Registration Open',
      registrationClosed: 'Registration Closed',
      spotsAvailable: 'spots available',
      waitlistEnabled: 'Waitlist Enabled',
      allowWaitlist: 'Allow Waitlist',
      maxWaitlistSize: 'Max Waitlist Size',

      // Communications
      sendReminder: 'Send Reminder',
      communicationsTitle: 'Event Communications',
      scheduledCommunications: 'Scheduled Communications',
      noCommunicationsScheduled: 'No communications scheduled',
      emailAllParticipants: 'Email All Participants',
      customMessage: 'Custom Message',
      pleaseProvideSubjectAndMessage: 'Please provide both subject and message',
      pleaseSelectDateTime: 'Please select a date and time for scheduling',
      failedToUpdateCommunication: 'Failed to update communication',
      failedToCreateCommunication: 'Failed to create communication',
      failedToSendCommunication: 'Failed to send communication',
      communicationSentTo: 'Communication sent to',
      communicationUpdatedFor: 'Communication updated for',
      communicationScheduledFor: 'Communication scheduled for',
      reminderLabel: 'Reminder',
      allRegistrants: 'All Registrants',
      confirmedOnly: 'Confirmed Only',
      waitlistOnly: 'Waitlist Only',
      whenToSend: 'When to send',
      sendImmediately: 'Send immediately',
      selectDateTimeForSending: 'Select date and time for sending',
      updateCommunication: 'Update Communication',
      quickTemplates: 'Quick Templates',
      eventReminder: 'Event Reminder',
      friendlyReminderMessage: 'This is a friendly reminder about the upcoming event',
      scheduledFor: 'scheduled for',
      lookForwardToSeeingYou: 'We look forward to seeing you there!',
      importantUpdate: 'Important Update',
      weHaveImportantUpdate: 'We have an important update regarding the event',
      pleaseAddUpdateDetails: 'Please add your update details here',
      eventUpdate: 'Event Update',
      communicationHistory: 'Communication History',
      noCommunicationsSentYet: 'No communications sent yet',
      messagesWillAppearHere: 'Messages will appear here after you send them',
      createdBy: 'Created by',

      // Scores and results
      enterScores: 'Enter Scores',
      viewScores: 'View Scores',
      pendingScores: 'Pending Scores',
      confirmedScores: 'Confirmed Scores',
      resultsPublished: 'Results Published',
      publishResults: 'Publish Results',

      // Match resolution
      resolveConflicts: 'Resolve Conflicts',
      pendingMatches: 'Pending Matches',
      completedMatches: 'Completed Matches',

      // Match Resolution Center page
      matchResolutionCenter: 'Match Resolution Center',
      resolution: 'Resolution',
      memberGuestTournament: 'Member-Guest Tournament',
      conflicts: 'Conflicts',
      noShows: 'No-Shows',
      pendingConflicts: 'Pending Conflicts',
      noShowMatches: 'No-Show Matches',
      highPriority: 'High Priority',
      noPendingConflicts: 'No pending conflicts',
      noPendingNoShows: 'No pending no-shows',
      deadlinePassed: 'Deadline passed',
      remind: 'Remind',
      award: 'Award',
      conflictDetails: 'Conflict Details',
      sReport: "'s Report",
      resultClaimed: 'Result Claimed',
      victory: 'Victory',
      submitted: 'Submitted',
      call: 'Call',
      communicationLog: 'Communication Log',
      adminResolution: 'Admin Resolution',
      resolutionDecision: 'Resolution Decision',
      awardMatchTo: 'Award Match to',
      requestMatchReplay: 'Request Match Replay',
      furtherInvestigationRequired: 'Further Investigation Required',
      resolutionNotes: 'Resolution Notes',
      saveResolution: 'Save Resolution',
      contactBothPlayers: 'Contact Both Players',
      selectAConflict: 'Select a Conflict',
      selectConflictDescription: 'Choose a conflict from the list to view details and resolve the dispute.',
      pleaseSelectResolution: 'Please select a resolution',
      conflictResolvedSuccessfully: 'Conflict resolved successfully',
      callingPlayer: 'Calling',
      emailingPlayer: 'Emailing',
      matchResolved: 'Match resolved',
      advancesByDefault: 'advances by default',
      reminderSentToBothPlayers: 'Reminder sent to both players',

      // Bracket validation errors
      groupsStillIncomplete: '{count} groups still incomplete',
      needAtLeastQualifiedPlayers: 'Need at least 2 qualified players, found {count}',
      bracketSizeNotOptimal: 'Bracket size {size} is not optimal. Recommended: {recommended} (will add byes)',

      // Event details step
      eventDetailsHeader: 'Event Details',
      addFinalEventInfo: 'Add final event information to complete setup',
      eventNameRequired: 'Event Name *',
      capacityRequired: 'Capacity *',
      startDateTimeRequired: 'Start Date & Time *',
      endDateTimeRequired: 'End Date & Time *',
      registrationDeadlineRequired: 'Registration Deadline *',
      membersCanRegisterUntil: 'Members can register until this date and time',
      entryFeeLabel: 'Entry Fee',
      optional: '(optional)',
      leaveEmptyForFree: 'Leave empty or 0 for free events',
      entryFeePerParticipant: 'Entry fee per participant (leave empty for free events)',
      acceptedPaymentMethods: 'Accepted Payment Methods',
      selectHowToPay: 'Select how participants can pay for this event',
      onlineCardPayment: 'Online Card Payment',
      payByCardDuringRegistration: 'Pay by card during registration',
      payAtClub: 'Pay at Club',
      payAtClubBeforeEvent: 'Pay at the club before the event',
      cashPayment: 'Cash Payment',
      acceptCashAtEvent: 'Accept cash at the event',
      selectAtLeastOnePayment: 'Please select at least one payment method',
      descriptionLabel: 'Description',
      chooseFromAvailableCourses: 'Choose from any available golf course, including external venues',
      prizesLabel: 'Prizes',
      automaticReminderSettings: 'Automatic Reminder Settings',
      configureReminderEmails: 'Configure when automatic reminder emails are sent to registered members',
      sevenDaysBeforeEvent: '7 days before event',
      sendWeekAheadReminder: 'Send a week-ahead reminder',
      oneDayBeforeEvent: '1 day before event',
      sendFinalReminder: 'Send a final reminder',
      remindersWillBeSent: 'Reminders will be sent:',
      sevenDaysBeforeTheEvent: '7 days before the event',
      oneDayBeforeTheEvent: '1 day before the event',
      noAutoRemindersEnabled: 'No automatic reminders enabled',
      multiRoundChampionshipSelected: 'Multi-Round Championship Selected',
      strokePlayFormat: 'Stroke Play',
      matchPlayFormat: 'Match Play',
      singleElimination: 'Single Elimination',
      groupPlusKnockout: 'Group + Knockout',
      playersCount: 'players',
      rounds: 'rounds',
      bestScores: 'Best {count} scores',
      totalStrokes: 'Total strokes',
      pointSystem: 'point system',
      singlesFormat: 'Singles',
      doublesFormat: 'Doubles',
      mixedFormat: 'Mixed',
      failedToCreateEvent: 'Failed to create event',
      createEventButton: 'Create Event',

      // Common actions
      save: 'Save',
      cancel: 'Cancel',
      next: 'Next',
      finish: 'Finish',
      close: 'Close',
      loading: 'Loading...',
      saving: 'Saving...',
      creating: 'Creating...',

      // Tournament dashboard tabs
      structureManager: 'Structure Manager',
      groups: 'Groups',
      overview: 'Overview',
      timeline: 'Timeline',
      gameHistory: 'Game History',

      // Registration flow steps
      reviewEvent: 'Review Event',
      registration: 'Registration',
      payment: 'Payment',

      // Group phase / match play
      allSquare: 'All Square',
      halved: 'Halved',
      wins: 'Wins',
      draws: 'Draws',
      losses: 'Losses',
      played: 'Played',
      points: 'Points',
      qualified: 'Qualified',
      position: 'Position',
      player: 'Player',
      handicap: 'Handicap',
      standings: 'Standings',
      matchNumber: 'Match #',
      winner: 'Winner',
      score: 'Score',
      draw: 'Draw',
      unknownMember: 'Unknown Member',
      unknownPlayer: 'Unknown Player',
      largeMargins: 'Large Margins',
      closeFinishes: 'Close Finishes',
      done: 'Done',
      edit: 'Edit',

      // Match Play Leaderboard table headers
      matchPlayLeaderboard: 'Match Play Leaderboard',
      matchPlayLeaderboardDesc: 'Live standings and qualification',
      searchPlayersPlaceholder: 'Search players...',
      statusFilter: 'Filter by status',
      all: 'All',
      possible: 'Possible',
      eliminated: 'Eliminated',
      playerName: 'Player Name',
      hdcp: 'HDCP',
      matchesPlayed: 'Matches',
      win: 'W',
      lost: 'L',
      ups: 'Up',
      down: 'Down',
      upDown: '+/-',
      totalPoints: 'Pts',
      noPlayersMatchCriteria: 'No players match your search criteria',
      exportBtn: 'Export',
      printBtn: 'Print',
      shareBtn: 'Share',

      noPlayersAssignedToGroup: 'No players assigned to this group',
      noMatchesScheduledYet: 'No matches scheduled yet',

      // FunctionalGroupPhaseManager match card translations
      vs: 'vs',
      roundLabel: 'Round',
      won: 'Won',
      clickToEdit: 'Click to edit',
      matchResult: 'Match Result',

      // MatchCard component translations
      due: 'Due',
      unscheduled: 'Unscheduled',
      enterResult: 'Enter Result',
      inProgress: 'In Progress',
      strokeGiven: 'stroke given',
      strokesGiven: 'strokes given',
      // MatchResultEntry form
      enterMatchResult: 'Enter Match Result',
      selectWinnerPlaceholder: 'Select winner...',
      marginOfVictory: 'Margin of Victory',
      selectMarginPlaceholder: 'Select margin...',
      submitResult: 'Submit Result',

      generateRoundRobinMatches: 'Generate Round-Robin Matches',
      scoreEntryMatchResult: 'Score Entry - Match Result',
      winsLabel: 'Wins',
      drawAllSquare: 'Draw / All Square',
      matchScore: 'Match Score',
      selectScore: 'Select Score',
      matchScoreExplanation: '"X&Y" means won by X holes with Y holes remaining. "X up" means won by X holes after 18 holes.',
      matchNotesOptional: 'Match Notes (Optional)',
      additionalMatchDetails: 'Additional details about the match...',

      // Group config panel
      createGroups: 'Create Groups',
      creatingGroups: 'Creating Groups...',
      createGroupsAndAssign: 'Create Groups & Assign Members',
      randomDistribution: 'Members randomly distributed across groups',
      handicapBalanced: 'Groups balanced by handicap for fair competition',
      seededDistribution: 'Top seeds distributed evenly (1-2-3-4, 8-7-6-5, etc.)',
      failedToCreateGroups: 'Failed to create groups',
      groupPhaseConfiguration: 'Group Phase Configuration',
      numberOfGroups: 'Number of Groups',
      membersPerGroupCalc: '{members} members ÷ {groups} groups = {result} members per group',
      needAtLeastNMembers: 'Need at least {count} members for {count} groups',
      assignmentMethod: 'Assignment Method',
      randomAssignment: 'Random Assignment',
      handicapBalancedLabel: 'Handicap Balanced',
      seededSnakeDraft: 'Seeded (Snake Draft)',
      playersAdvancePerGroup: 'Players Advance per Group',
      playersAdvanceCalc: '{groups} groups × {players} = {total} players advance to knockout',
      configurationSummary: 'Configuration Summary',
      totalMembersLabel: 'Total Members',
      groupsLabel: 'Groups',
      membersPerGroupLabel: 'Members per Group',
      advanceToKnockoutLabel: 'Advance to Knockout',
      waitForRegistrations: 'Wait for member registrations before creating groups',
      groupsSetupRequired: 'Groups Setup Required',
      noGroupsCreatedYet: 'No groups have been created yet. Create groups and assign players.',
      groupsExistNoMembers: 'Groups exist but have no members assigned. Please assign players to groups.',

      // Manual group editor
      addPlayer: 'Add Player',
      addPlayerToGroup: 'Add Player to Group',
      selectPlayer: 'Select a player...',
      loadingPlayers: 'Loading players...',
      selectTargetGroup: 'Select target group...',
      createNewGroup: 'Create New Group',
      groupName: 'Group name',
      saveChanges: 'Save changes',
      editGroupSettings: 'Edit group settings',
      deleteGroup: 'Delete Group',
      removeFromGroup: 'Remove from group',
      groupAssignments: 'Group Assignments',
      manualGroupEditor: 'Manual Group Editor',
      changesMade: 'Changes Made',
      refresh: 'Refresh',
      unassignedPool: 'Unassigned Pool',
      unassignedParticipants: 'Unassigned Participants',
      allParticipantsAssigned: 'All participants have been assigned to groups',
      deleteGroupConfirm: 'Are you sure you want to delete group',
      createGroup: 'Create Group',
      membersLabel: 'members',

      // Championship leaderboard
      championshipLeaderboard: 'Championship Leaderboard',
      noParticipantsYet: 'No participants registered yet',
      leaderboardWillPopulate: 'Leaderboard will populate as players register and submit scores',
      groupStageStandings: 'Group stage standings - Top players advance to knockout round',
      topAdvanceToKnockout: 'Top {count} advance to knockout round',

      // Score entry
      searchParticipant: 'Search participant...',
      searchMember: 'Search member...',
      eagle: 'Eagle',
      birdie: 'Birdie',
      par: 'Par',
      bogey: 'Bogey',
      simple: 'Simple',
      detailed: 'Detailed',
      submissionNotAvailable: 'Submission not available',
      roundsCompletedOrNotAvailable: 'All rounds completed or not yet available',
      scoreSubmitted: 'Score submitted',
      golfCourse: 'Golf Course',

      // Group phase export
      exportResults: 'Export Results',
      matchesGeneratedSuccess: 'Matches generated successfully!',
      failedToGenerateMatches: 'Failed to generate matches',
      matchResultSubmitted: 'Match result submitted!',
      failedToSubmitResult: 'Failed to submit result',

      // Event card progress
      complete: 'Complete',
      tryAdjustingFilters: 'Try adjusting your filters to see more events.',
      getStartedCreatingFirst: 'Get started by creating your first tournament or event.',
      currentPhase: 'Current Phase',
      progress: 'Progress',
      liveStatus: 'Live Status',

      // Search placeholders
      searchEvents: 'Search events...',
      searchPlayers: 'Search players...',
      searchByName: 'Search by name...',
      searchByPlayer: 'Search by player name...',
      searchByNameOrEmail: 'Search by name or email...',
      searchRegistrations: 'Search registrations...',

      // Course/tee selection
      selectWinner: 'Select winner',
      selectMatchResult: 'Select match result',
      scheduledDateTime: 'Scheduled Date & Time',
      partner1: 'Partner 1',
      partner2: 'Partner 2',
      marginLabel: 'Margin',

      // Admin score entry
      chooseAMember: 'Choose a member...',
      enterTotalScore: 'Enter total score...',

      // Match play config
      numberOfPlayersRequired: 'Number of players is required',
      atLeast2PlayersRequired: 'At least 2 players required',
      straightBracket: 'Straight Bracket',
      groupPhaseKnockout: 'Group Phase + Knockout',
      createTournament: 'Create Tournament',

      // League
      leagueOverview: 'League Overview',
      yourTeam: 'Your Team',

      // Export labels
      groupLabel: 'Group',
      totalGroups: 'Total Groups',
      totalMatches: 'Total Matches',
      completionPercentage: 'Completion %',
      players: 'Players',
      matchResults: 'Match Results',
      summary: 'Summary',
      groupStageSummary: 'Group Stage Summary',

      // Tournament structure tab
      addRound: 'Add Round',

      // Tournament bracket
      loadingBracket: 'Loading bracket...',
      tbd: 'TBD',
      match: 'Match',
      editMatch: 'Edit Match',
      matchDetails: 'Match Details',
      singlesMatchplay: 'Singles Matchplay',
      doublesMatchplay: 'Doubles Matchplay',
      team1: 'Team 1',
      team2: 'Team 2',
      player1: 'Player 1',
      player2: 'Player 2',
      formatLabel: 'Format',
      resultLabel: 'Result',
      winnerLabel: 'Winner',
      matchStatus: 'Match Status',
      selectStatus: 'Select status',
      live: 'Live',
      noWinner: 'No winner',
      scheduledTime: 'Scheduled Time',
      clickToAssignPlayer: 'Click to assign player',
      tournamentBracket: 'Tournament Bracket',
      tournamentCompleted: 'Tournament completed!',
      bracketPhaseInProgress: 'Bracket phase in progress',
      bracketPreviewFull: 'Bracket preview - will populate when groups complete',
      previewMode: 'Preview mode',
      final: 'Final',
      preview: 'Preview',
      bracketManagement: 'Bracket Management',
      configureAndGenerateBracket: 'Configure and generate tournament bracket',
      bracketStructure: 'Bracket Structure',
      bracketGeneration: 'Bracket Generation',
      chooseYourStartingRoundSize: 'Choose your starting round size',
      autoSizeBasedOnParticipants: 'Auto-size based on participants',
      auto: 'Auto',
      custom: 'Custom',
      selectTournamentRounds: 'Select Tournament Rounds',
      roundOf64: 'Round of 64',
      roundOf32: 'Round of 32',
      selectAtLeastOneRound: 'Select at least one round',
      generateCustom: 'Generate Custom',
      generateBracket: 'Generate Bracket',
      refreshData: 'Refresh Data',
      mockDataActive: 'Mock Data Active',
      generateRealBracketToEnable: 'Generate a real bracket to enable player assignments.',
      tip: 'Tip',
      generateRealBracketThenClick: 'Generate a real bracket first, then click',
      click: 'Click',
      onAnyPlayerToReassign: 'on any player to reassign them',
      debugInfo: 'Debug Info',
      mockData: 'Mock data',
      requirements: 'Requirements',
      assignPlayerTo: 'Assign Player to',
      position1: 'Position 1',
      position2: 'Position 2',
      typePlayerNameToSearch: 'Type player name to search...',
      removePlayer: 'Remove Player',
      loadingTournamentParticipants: 'Loading tournament participants...',
      errorLoadingParticipants: 'Error loading participants',
      noTournamentParticipantsFound: 'No tournament participants found',
      eventId: 'Event ID',
      tryRegisteringPlayersFirst: 'Try registering players for this tournament first',
      noPlayersFoundMatching: 'No players found matching',
      seed: 'Seed',
      noSeed: 'No seed',
      mock: 'Mock',

      // Group phase management - missing keys
      addedPlayerToGroup: 'Added player to group',
      addedToUnassigned: 'Added to unassigned',
      adminOnlyEdit: 'Only administrators can edit',
      advance: 'Advance',
      allGroupsCleared: 'All groups cleared successfully',
      allMatchesExist: 'All matches already exist',
      anyDietaryRestrictions: 'Any dietary restrictions?',
      casualMember: 'Casual Member',
      completedMatchesPreserved: 'Completed matches preserved',
      createGroupQuestion: 'Create a new group?',
      createdGroup: 'Created group',
      deletedGroup: 'Deleted group',
      dragDropTip: 'Drag and drop players to assign them to groups',
      dragToAssign: 'Drag to assign',
      engagementImpact: 'Engagement Impact',
      expectedMember360Points: 'Expected Member360 Points',
      expectedSocialInteractions: 'Expected Social Interactions',
      failedToClearGroups: 'Failed to clear groups',
      failedToDeleteGroup: 'Failed to delete group',
      failedToDetermineGroupName: 'Failed to determine group name',
      failedToGetMembershipId: 'Failed to get membership ID',
      failedToMovePlayer: 'Failed to move player',
      failedToUpdateGroup: 'Failed to update group',
      groupCreatedEmpty: 'Group created (empty)',
      groupNotFound: 'Group not found',
      groupResultsExported: 'Group results exported successfully!',
      invalidMembershipCannotAdd: 'Invalid membership - cannot add',
      invalidMembershipId: 'Invalid membership ID',
      matchesGeneratedForAllGroups: 'Matches generated for all groups!',
      membersAdvanceLabel: 'Members advance',
      metricLabel: 'Metric',
      movePlayerBetweenGroups: 'Move player between groups',
      movedPlayerAndMatches: 'Moved player and updated matches',
      newMatch: 'New match',
      newMatches: 'New matches',
      noGroupDataToExport: 'No group data to export',
      noMatchChangesNeeded: 'No match changes needed',
      noPendingMatchesToRemove: 'No pending matches to remove',
      pendingMatch: 'Pending match',
      permanentDeleteWarning: 'This action cannot be undone',
      playerAlreadyInUnassigned: 'Player already in unassigned',
      playerNotFound: 'Player not found',
      pleaseSelectPlayerAndGroup: 'Please select a player and group',
      previewing: 'Previewing',
      removePlayerFromGroup: 'Remove player from group',
      removedPlayerAndMatches: 'Removed player and matches',
      resultsWillUpdateMember360: 'Results will update Member360 scores',
      roundStartsIn: 'Round starts in',
      steadyPlay: 'Steady Play',
      updatedGroupSettings: 'Updated group settings',
      valueLabel: 'Value',
      willCreateMatches: 'Will create matches',
      willRemoveMatches: 'Will remove matches',

      // Tournament leaderboard tab
      overall: 'Overall',
      byRound: 'By Round',
      gross: 'Gross',
      net: 'Net',
      both: 'Both',
      pos: 'Pos',
      toPar: '+/-',
      indicatesTiedPosition: 'indicates tied position',
      missingScores: 'Missing scores',
      exportToCsv: 'Export to CSV',
      printLeaderboard: 'Print Leaderboard',
      shareLink: 'Share Link',

      // Live group phase standings
      loadingGroupStandings: 'Loading group standings...',
      errorLoadingStandings: 'Error loading standings',
      noStandingsData: 'No standings data available',
      realTime: 'Real-time',
      instantUpdates: 'Instant Updates',
      tournamentProgress: 'Tournament Progress',
      lastUpdated: 'Last updated',
      realTimeSubscriptionsActive: 'Real-time subscriptions active + polling every',
      autoRefreshesEvery: 'Auto-refreshes every',

      // Tournament round manager
      roundCreatedSuccessfully: 'Round created successfully',
      failedToCreateRound: 'Failed to create round',
      roundUpdatedSuccessfully: 'Round updated successfully',
      failedToUpdateRound: 'Failed to update round',
      roundDeletedSuccessfully: 'Round deleted successfully',
      failedToDeleteRound: 'Failed to delete round',
      tournamentMustHaveAtLeastOneRound: 'Tournament must have at least one round',
      cannotDeleteCompletedRounds: 'Cannot delete completed rounds',
      confirmDeleteRound: 'Are you sure you want to delete this round? This action cannot be undone.',
      tournamentStructureAdminOnly: 'Tournament structure management is only available to administrators.',
      tournamentStructure: 'Tournament Structure',
      of: 'of',
      adding: 'Adding...',
      round: 'Round',
      scoreDeadline: 'Score Deadline',
      completion: 'Completion',
      averageScore: 'Average Score',
      roundDate: 'Round Date',
      scoreEntryDeadline: 'Score Entry Deadline',
      // UnifiedTournamentPage
      loadingTournament: 'Loading tournament...',
      errorLoadingTournament: 'Error loading tournament information',
      tournamentManagementTitle: 'Tournament Management',
      phase: 'Phase',

      // TournamentGameHistory
      groupMatches: 'Group Matches',
      bracketMatches: 'Bracket Matches',
      totalRoundsLabel: 'Total Rounds',
      matchHistory: 'Match History',
      roundHistory: 'Round History',
      matchUpdatedSuccessfully: 'Match updated successfully',
      timePeriod: 'Time Period',
      allTime: 'All Time',
      last14Days: 'Last 14 Days',
      matches: 'matches',
      noMatchesFound: 'No matches found',
      noRoundsFound: 'No rounds found',
      tryAdjustingSearchCriteria: 'Try adjusting your search criteria',
      noMatchesPlayedYet: 'No matches have been played yet',
      noRoundsPlayedYet: 'No rounds have been played yet',
      pts: 'Pts',
      groupStage: 'Group Stage',
    },

    // Channel Manager (Tasks tab and dialogs)
    channelManager: {
      deleteChannel: 'Delete Channel',
      deleteChannelConfirm: 'Are you sure you want to delete #{{name}}? This action cannot be undone.',
      cancel: 'Cancel',
      allTasks: 'All Tasks',
      urgentTask: '🔴 Urgent',
      highTask: '🟠 High',
      normalTask: '🟡 Normal',
      lowTask: '🟢 Low',
      allStatus: 'All Status',
      open: 'Open',
      inProgress: 'In Progress',
      completed: 'Completed',
      onHold: 'On Hold',
      createTask: 'Create Task',
      add: 'Add',
      noTasksFound: 'No communication tasks found',
      last24h: 'Last 24h',
      last7Days: 'Last 7 days',
      last30Days: 'Last 30 days',
      last90Days: 'Last 90 days',
      createNewChannel: 'Create New Channel',
      channelName: 'Channel Name',
      description: 'Description',
      channelType: 'Channel Type',
      privateChannel: 'Private Channel',
      defaultChannel: 'Default Channel',
      channelPermissions: 'Channel Permissions',
      adminOnly: 'Admin Only',
      adminOnlyDescription: 'Only administrators can post in this channel',
      membersCanPost: 'Members Can Post',
      membersCanPostDescription: 'Allow all members to post messages',
      save: 'Save',
      saving: 'Saving...',
      convertMessageToTask: 'Convert Message to Task',
      editChannel: 'Edit Channel',
      // Tabs
      tabs: {
        channels: 'Channels',
        liveChat: 'Live Chat',
        memberMessages: 'Member Messages',
        tasks: 'Tasks',
        insights: 'Insights'
      },
      // Channel types (for create/edit forms)
      channelTypes: {
        text: 'General Discussion',
        announcements: 'Announcements',
        advice: 'Tips & Advice',
        events: 'Events & Tournaments',
        roundShares: 'Round Shares'
      },
      // Toasts
      toasts: {
        failedToLoad: 'Failed to load channels',
        noClubSelected: 'No club selected',
        channelCreated: 'Channel created successfully',
        failedToCreate: 'Failed to create channel',
        channelUpdated: 'Channel updated successfully',
        failedToUpdate: 'Failed to update channel',
        channelDeleted: 'Channel deleted successfully',
        failedToDelete: 'Failed to delete channel',
        channelOrderUpdated: 'Channel order updated',
        messageEdited: 'Message edited',
        replySentAndLogged: 'Reply sent and logged to contact history',
        messageSent: 'Message sent',
        failedToSendMessage: 'Failed to send message',
        memberNotified: 'Member notified via channel',
        loggedToContactHistory: 'Logged to contact history',
        failedToCreateTask: 'Failed to create task from message',
        failedToLoadMessages: 'Failed to load messages',
        messageSentTo: 'Message sent to {name}',
        failedToAddReaction: 'Failed to add reaction',
        failedToUpdateReaction: 'Failed to update reaction',
        messageCopied: 'Message copied to clipboard',
        messageDeleted: 'Message deleted',
        failedToDeleteMessage: 'Failed to delete message',
        taskCreatedSuccess: 'Task "{title}" created successfully'
      },
      // Permission labels (for channel badges)
      readOnly: 'Read Only',
      public: 'Public',
      members: 'members',
      noDescriptionProvided: 'No description provided',
      // Activity / status
      noActivity: 'No activity',
      justNow: 'Just now',
      activity: {
        minutesAgo: 'm ago',
        hoursAgo: 'h ago',
        daysAgo: 'd ago',
      },
      creating: 'Creating...',
      createChannel: 'Create Channel',
      create: 'Create',
      settings: 'Settings',
      tasksFromMessages: 'Tasks created from member messages will appear here',
      channelsCount: 'channels',
      active: 'active',
      totalMembers: 'total members',
      channelDescriptionPlaceholder: 'Brief description of this channel',
      noDescription: 'No description provided',
      selectChannel: 'Select a channel',
      startMonitoring: 'Start monitoring this channel',
      staff: 'Staff',
      member: 'Member',
      unknownUser: 'Unknown User',
      unknownMember: 'Unknown Member',
      noMembersFound: 'No members found',
      noMembersAvailable: 'No members available',
      tasksCount: 'tasks',
      analytics: 'Analytics',
      tasksCreated: 'Tasks Created',
      tasksByCategory: 'Tasks by Category',
      from: 'From',
      today: 'Today',
      // Live chat
      liveChat: {
        welcomeTo: 'Welcome to',
        realTimeChat: 'Real-time chat',
        live: 'Live',
        replyingTo: 'Replying to {name}',
        typeMessage: 'Type a message...',
        editYourMessage: 'Edit your message...',
        replyTo: 'Reply to {name}...',
        unknownUser: 'Unknown User',
        staff: 'Staff',
        member: 'Member',
        reply: 'Reply',
        convertToTask: 'Convert to Task',
        copy: 'Copy',
        edit: 'Edit',
        delete: 'Delete',
        deleteConfirm: 'Delete this message?',
        messageDeleted: '[This message was deleted]',
        goLive: 'Go Live',
      },
      // Online staff section
      onlineStaff: 'Online Staff',
      staffRoles: {
        manager: 'Manager',
        pro_shop: 'Pro Shop',
        range_attendant: 'Range Attendant',
        cart_attendant: 'Cart Attendant',
        maintenance: 'Maintenance',
        food_service: 'Food Service',
        admin: 'Admin',
        staff: 'Staff',
      },
      // Member messages
      memberMessages: {
        membersTitle: 'Members',
        searchMembers: 'Search members...',
        loadingMembers: 'Loading members...',
        noMembersFound: 'No members found',
        noMembersAvailable: 'No members available',
        addNewMember: 'Add New Member',
        recentConversations: 'Recent Conversations',
        hcp: 'HCP',
        selectMember: 'Select a Member',
        selectMemberDescription: 'Choose a member from the left panel to start messaging',
        noMessagesYet: 'No messages yet',
        startConversation: 'Start a conversation with {name}',
        loadingMessages: 'Loading messages...',
        message: 'Message {name}...',
        editingMessage: 'Editing message',
        original: 'Original',
        statusActive: 'active',
        statusNew: 'new',
        statusInactive: 'inactive',
        today: 'Today',
        daysAgo: '{count} days ago',
        weeksAgo: '{count} weeks ago',
      },
      // Support queue
      supportQueue: {
        title: 'Support Queue',
        take: 'Take',
        viewAllQueue: 'View All Queue',
      },
      // Outreach tools
      outreachTools: {
        title: 'Outreach Tools',
        inactiveMembers: 'Inactive Members',
        birthdayGreetings: 'Birthday Greetings',
        eventFollowups: 'Event Follow-ups',
        feedbackRequests: 'Feedback Requests',
        quickTemplates: 'Quick Templates',
        bookingConfirmation: '📋 Booking Confirmation',
        welcomeNewMember: '🎉 Welcome New Member',
        requestReview: '⭐ Request Review',
        tournamentInvite: '🏆 Tournament Invite',
      },
      // Member insights
      memberInsights: {
        title: 'Member Insights',
        handicap: 'Handicap',
        rounds: 'Rounds',
        responseRate: 'Response Rate',
        avgResponse: 'Avg Response',
        satisfaction: 'Satisfaction',
        viewFullProfile: 'View Full Profile',
      },
      // Tasks tab
      tasksTab: {
        urgent: 'urgent',
        inProgressCount: 'in progress',
        completedCount: 'completed',
        tasksLabel: 'Tasks',
        urgentLabel: 'Urgent',
        inProgressLabel: 'In Progress',
        completedLabel: 'Completed',
        totalLabel: 'Total',
        loadingTasks: 'Loading tasks...',
        from: 'From',
        via: 'Via',
        created: 'Created',
        dueDate: 'Due',
        assignedTo: 'Assigned to',
        unassigned: 'Unassigned',
        unknownMember: 'Unknown Member',
        tasksFromMessages: 'Tasks are created from member messages and conversations',
      },
      // Settings dialog
      settingsDialog: {
        title: 'Channel Settings',
        notifications: 'Notifications',
        notifyNewMessages: 'Notify on new messages',
        notifyMentions: 'Notify on mentions only',
        moderation: 'Moderation',
        requireApproval: 'Require approval for new posts',
        allowReactions: 'Allow reactions',
        defaults: 'Default Behavior',
        autoJoinNew: 'Auto-join new members to default channels',
        saved: 'Settings saved',
      },
      // Quick actions
      quickActions: {
        title: 'Quick Actions',
        createTask: 'Create Task',
        chatHistory: 'Chat History',
        assignStaff: 'Assign Staff',
        followUp: 'Follow-up',
        settings: 'Settings',
      },
      // Channel card labels
      channelCard: {
        members: 'Members',
        messages: 'Messages',
        activity: 'Activity',
        createNewChannel: 'Create New Channel',
        createDescription: 'Add a new communication channel for your club',
      },
      // Channel descriptions
      channelDescriptions: {
        general: 'Main club discussion',
        courseTips: 'Share course knowledge and tips',
        announcements: 'Official club announcements and updates',
        playingToday: 'Find playing partners',
        tournaments: 'Tournament discussion and coordination',
        equipment: 'Discuss golf equipment',
        clubEvents: 'Club tournaments and events',
        roundShares: 'Automated round sharing',
      },
      // Insights tab
      insightsTab: {
        title: 'Insights',
        messages: 'messages',
        avgResponseTime: 'avg response',
        satisfactionRate: 'satisfaction',
        exportReport: 'Export Report',
        export: 'Export',
        totalMessages: 'Total Messages',
        avgResponse: 'Avg Response',
        satisfaction: 'Satisfaction',
        tasksCreated: 'Tasks Created',
        communicationVolume: 'Communication Volume',
        messagesPerDay: 'Messages per day over the last 7 days',
        channelActivity: 'Channel Activity',
        messageDistribution: 'Message distribution across channels',
        topPerformers: 'Top Performers',
        staffResponseMetrics: 'Staff response metrics',
        responseTimes: 'Response Times',
        averageByTimeOfDay: 'Average by time of day',
        taskInsights: 'Task Insights',
        communicationToTaskConversion: 'Communication to task conversion',
        conversionRate: 'Conversion Rate',
        completionRate: 'Completion Rate',
        tasksByCategory: 'Tasks by Category',
        excellent: 'Excellent',
        good: 'Good',
        slow: 'Slow',
        poor: 'Poor',
        generalDiscussion: 'General Discussion',
        memberSupport: 'Member Support',
        eventUpdates: 'Event Updates',
        courseConditions: 'Course Conditions',
        proShop: 'Pro Shop',
        cartManager: 'Cart Manager',
        clubManager: 'Club Manager',
        maintenance: 'Maintenance',
        msgs: 'msgs',
        high: 'High',
        peak: 'Peak',
        medium: 'Medium',
        low: 'Low',
        veryLow: 'Very Low',
        mon: 'Mon',
        tue: 'Tue',
        wed: 'Wed',
        thu: 'Thu',
        fri: 'Fri',
        sat: 'Sat',
        sun: 'Sun',
        time9to11: '9-11 AM',
        time11to1: '11-1 PM',
        time1to3: '1-3 PM',
        time3to5: '3-5 PM',
        time5to7: '5-7 PM',
        timeAfter7: 'After 7 PM',
        booking: 'Booking',
        cartIssues: 'Cart Issues',
        events: 'Events',
        memberEngagementAnalysis: 'Member Engagement Analysis',
        memberEngagementDescription: 'Communication patterns and member behavior insights',
        mostActiveMembers: 'Most Active Members',
        communicationTypes: 'Communication Types',
        questions: 'Questions',
        feedback: 'Feedback',
        complaints: 'Complaints',
        compliments: 'Compliments',
        peakHours: 'Peak Hours',
        satisfactionScores: 'Satisfaction Scores',
        fiveStars: '5 Stars',
        fourStars: '4 Stars',
        threeStars: '3 Stars',
        oneToTwoStars: '1-2 Stars',
        resolutionStatus: 'Resolution Status',
        issueResolutionQuality: 'Issue resolution quality',
        confirmedResolved: 'Confirmed Resolved',
        pendingResponse: 'Pending Response',
        reopened: 'Reopened',
        unresolved: 'Unresolved',
        recommendedActions: 'Recommended Actions',
        immediateActions: 'Immediate Actions',
        reduceResponseTime: 'Reduce 3-5 PM response time',
        currentTarget: 'Current: {current}, Target: {target}',
        addressCartBacklog: 'Address cart maintenance backlog',
        pendingCartTasks: '{count} pending cart-related tasks',
        optimizationOpportunities: 'Optimization Opportunities',
        automateBookingConfirmations: 'Automate booking confirmations',
        bookingRelatedMessages: '{percentage}% of messages are booking-related',
        createFaq: 'Create FAQ for common questions',
        questionsPercentage: '{percentage}% of messages are questions',
      },
    },

    // CommsConversation component
    commsConversation: {
      loading: 'Loading...',
      memberFallback: 'Member',
      participantsLabel: '2 participants · ',
    },

    // ChannelConfigList component
    channelConfig: {
      panelEyebrow: 'Settings',
      createChannelBtn: 'Create Channel',
      channelsCount: 'channels',
      activeCount: 'active',
      settingsBtn: 'Settings',
    },

    // CommsTaskDetail component
    commsTask: {
      justNow: 'just now',
      minutesAgo: 'm ago',
      hoursAgo: 'h ago',
      daysAgo: 'd ago',
    },

    // Revenue dashboard (/club-admin/revenue)
    revenue: {
      demoData: 'Demo Data',
      today: 'Today',
      thisWeek: 'This Week',
      thisMonth: 'This Month',
      thisYear: 'This Year',
      export: 'Export',
      revenue: 'Revenue',
      vsLastPeriod: 'vs last period',
      transactions: 'Transactions',
      avgValue: 'Avg. Value',
      perTransaction: 'per transaction',
      pending: 'Pending',
      awaiting: 'awaiting',
      revenueTrend: 'Revenue Trend',
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
      noTransactionData: 'No transaction data available',
      byCategory: 'By Category',
      recentTransactions: 'Recent Transactions',
      viewAllTransactions: 'View all',
      customer: 'Customer',
      type: 'Type',
      description: 'Description',
      amount: 'Amount',
      status: 'Status',
      time: 'Time',
      completed: 'Completed',
      refunded: 'Refunded',
      // Category names
      categoryNames: {
        drivingRange: 'Driving Range',
        events: 'Events',
        coaching: 'Coaching',
        memberships: 'Memberships',
        proshop: 'Pro Shop',
        restaurant: 'Restaurant',
        other: 'Other',
      },
    },

    // Transactions page (/club-admin/revenue/transactions)
    transactions: {
      pageTitle: 'Transactions',
      newTransaction: 'New Transaction',
      searchPlaceholder: 'Search by member, transaction ID, or description...',
      allTypes: 'All Types',
      allStatus: 'All Status',
      allTime: 'All time',
      last7Days: 'Last 7 days',
      last90Days: 'Last 90 days',
      membership: 'Membership',
      greenFee: 'Green Fee',
      lesson: 'Lesson',
      event: 'Event',
      other: 'Other',
      dateRange: 'Date Range',
      today: 'Today',
      thisWeek: 'This Week',
      last30Days: 'Last 30 days',
      thisMonth: 'This Month',
      thisYear: 'This Year',
      teeTimes: 'Tee Times',
      lessons: 'Lessons',
      events: 'Events',
      services: 'Services',
      completed: 'Completed',
      pending: 'Pending',
      refunded: 'Refunded',
      total: 'Total',
      transactions: 'Transactions',
      vsPrevious: '↑ vs previous',
      transactionId: 'Transaction ID',
      customer: 'Customer',
      type: 'Type',
      description: 'Description',
      amount: 'Amount',
      status: 'Status',
      date: 'Date',
      remind: 'Remind',
      paid: 'Paid',
      markAsPaid: 'Mark as Paid',
      sendReminder: 'Send Reminder',
      viewDetails: 'View Details',
      refund: 'Refund',
      showingPage: 'Showing {from} to {to} of {total}',
      sendReceipt: 'Send Receipt',
      downloadPdf: 'Download PDF',
      issueRefund: 'Issue Refund',
      transactionDetails: 'Transaction Details',
      transactionAmount: 'Transaction Amount',
      dateTime: 'Date & Time',
      paymentMethod: 'Payment Method',
      lineItems: 'Line Items',
      activityLog: 'Activity Log',
      paymentCompleted: 'Payment completed',
      paymentPending: 'Payment pending',
      paymentRefunded: 'Payment refunded',
      confirmationEmailSent: 'Confirmation email sent',
      bookingCreated: 'Booking created',
      showing: 'Showing',
      of: 'of',
      paymentConfirmed: 'Payment confirmed for',
      reminderSent: 'Payment reminder sent to',
      cannotMarkAsPaid: 'Cannot mark as paid: Transaction ID not found',
      cannotSendReminder: 'Cannot send reminder: Transaction ID not found',
      failedToMarkAsPaid: 'Failed to mark payment as paid',
      failedToSendReminder: 'Failed to send payment reminder',
      guest: 'Guest',
      member: 'Member',
      demoData: 'Demo Data',
      export: 'Export',
    },

    // Payment Settings (/club-admin/revenue/settings)
    paymentSettings: {
      paymentProvider: 'Payment Provider',
      pricingRules: 'Pricing Rules',
      refundPolicy: 'Refund Policy',
      notifications: 'Notifications',
      invoiceSettings: 'Invoice Settings',
      taxSettings: 'Tax Settings',
      demo: 'Demo',
      thisMonth: 'This Month',
      transactionsProcessed: 'transactions processed',
      processingFees: 'Processing fees',
      cancel: 'Cancel',
      saveChanges: 'Save Changes',
      saving: 'Saving...',
      demoModeWarning: 'Demo mode - changes won\'t persist',
      paymentProviderTitle: 'Payment Provider',
      paymentProviderSubtitle: 'Connect and manage your payment processor',
      stripe: 'Stripe',
      connected: 'Connected',
      account: 'Account',
      dashboard: 'Dashboard',
      dash: 'Dash',
      disconnect: 'Disconnect',
      connectStripeAccount: 'Connect your Stripe account to accept payments',
      connectStripe: 'Connect Stripe',
      defaultCurrency: 'Default Currency',
      statementDescriptor: 'Statement Descriptor',
      statementDescriptorPlaceholder: 'YOUR CLUB NAME',
      statementDescriptorHelp: 'Max 22 characters. Appears on bank statements.',
      environment: 'Environment',
      live: 'Live',
      testMode: 'Test Mode',
      paymentCapture: 'Payment Capture',
      automaticRecommended: 'Automatic (recommended)',
      manual: 'Manual',
      paymentOptions: 'Payment Options',
      paymentOptionsSubtitle: 'Configure additional payment options for your club',
      onlinePaymentInfo: 'Online payment methods',
      onlinePaymentDescription: 'Cards, Apple Pay, Google Pay, Bancontact, and other online payment methods are managed in your',
      stripeDashboard: 'Stripe Dashboard',
      payAtClub: 'Pay at Club (Cash/Terminal)',
      payAtClubDescription: 'Members can reserve online and pay at the pro shop',
      pricingRulesTitle: 'Pricing Rules',
      pricingRulesSubtitle: 'Configure dynamic pricing and discounts',
      memberGuestPricing: 'Member & Guest Pricing',
      memberDiscount: 'Member Discount (%)',
      guestSurcharge: 'Guest Surcharge (%)',
      timeBasedPricing: 'Time-Based Pricing',
      peakTimeSurcharge: 'Peak Time Surcharge (%)',
      offPeakDiscount: 'Off-Peak Discount (%)',
      definePeakHours: 'Define when peak hours apply',
      weekdayPeakHours: 'Weekday Peak Hours (Mon-Fri)',
      to: 'to',
      weekendPeakHours: 'Weekend Peak Hours (Sat-Sun)',
      bookingIncentives: 'Booking Incentives',
      earlyBirdDiscount: 'Early Bird Discount (%)',
      daysInAdvance: 'Days in Advance',
      lastMinuteDiscount: 'Last Minute Discount (%)',
      hoursBeforeTeeTime: 'Hours Before Tee Time',
      refundPolicyTitle: 'Refund Policy',
      refundPolicySubtitle: 'Set up refund rules and time windows',
      allowRefunds: 'Allow Refunds',
      allowRefundsDescription: 'Enable refund processing for bookings',
      refundTimeWindows: 'Refund Time Windows',
      fullRefundHours: 'Full Refund (hours before)',
      fullRefundHelp: '100% refund',
      partialRefundHours: 'Partial Refund (hours before)',
      partialRefundHelp: '% refund',
      noRefundHours: 'No Refund (hours before)',
      noRefundHelp: '0% refund',
      partialRefundPercentage: 'Partial Refund Percentage (%)',
      cancellationFee: 'Cancellation Fee (€)',
      autoRefundOnCancel: 'Auto-refund on Cancel',
      autoRefundDescription: 'Automatically process refunds when bookings are cancelled',
      refundToCredit: 'Refund to Club Credit',
      refundToCreditDescription: 'Issue refunds as club credit instead of payment reversal',
      paymentNotifications: 'Payment Notifications',
      notificationsSubtitle: 'Configure email and SMS notifications',
      emailNotifications: 'Email Notifications',
      paymentConfirmation: 'Payment Confirmation',
      paymentReceipt: 'Payment Receipt',
      refundNotification: 'Refund Notification',
      paymentFailedAlert: 'Payment Failed Alert',
      paymentReminder: 'Payment Reminder',
      smsNotifications: 'SMS Notifications',
      reminderHoursBefore: 'Reminder Hours Before',
      reminderHoursHelp: 'When to send payment reminders before due date',
      invoiceSettingsTitle: 'Invoice Settings',
      invoiceSettingsSubtitle: 'Customize invoice templates and numbering',
      autoGenerateInvoices: 'Auto-generate Invoices',
      autoGenerateDescription: 'Automatically create invoices for all payments',
      invoiceNumbering: 'Invoice Numbering',
      invoicePrefix: 'Invoice Prefix',
      invoicePrefixPlaceholder: 'INV',
      nextInvoiceNumber: 'Next Invoice Number',
      invoicePreview: 'Preview',
      companyInformation: 'Company Information',
      companyName: 'Company Name',
      companyNamePlaceholder: 'Your Golf Club Name',
      vatNumber: 'VAT Number',
      vatNumberPlaceholder: 'BE0123456789',
      address: 'Address',
      addressPlaceholder: '123 Fairway Drive, City, Postal Code',
      email: 'Email',
      emailPlaceholder: 'billing@yourclub.com',
      phone: 'Phone',
      phonePlaceholder: '+32 2 123 4567',
      paymentTermsDays: 'Payment Terms (days)',
      invoiceFooterText: 'Invoice Footer Text',
      footerPlaceholder: 'Thank you for your business!',
      taxSettingsTitle: 'Tax Settings',
      taxSettingsSubtitle: 'Configure VAT and tax calculation rules',
      enableTax: 'Enable Tax Calculations',
      enableTaxDescription: 'Apply tax to all transactions',
      defaultTaxRate: 'Default Tax Rate (%)',
      taxLabel: 'Tax Label',
      taxLabelPlaceholder: 'VAT',
      taxRegistrationNumber: 'Tax Registration Number',
      taxRegNumberPlaceholder: 'BE0123456789',
      pricingDisplay: 'Pricing Display',
      taxInclusive: 'Tax Inclusive',
      taxExclusive: 'Tax Exclusive',
      taxInclusiveHelp: 'Prices shown include tax (e.g., €121 includes €21 VAT)',
      taxExclusiveHelp: 'Prices shown exclude tax (e.g., €100 + €21 VAT = €121)',
      exemptMembers: 'Exempt Members from Tax',
      exemptMembersDescription: 'Members don\'t pay tax on transactions',
      noClubSelected: 'No club selected',
      unknown: 'Unknown',
      na: 'N/A',
      eurCurrency: 'EUR (€) - Euro',
      usdCurrency: 'USD ($) - US Dollar',
      gbpCurrency: 'GBP (£) - British Pound',
    },

    // Members management
    members: {
      // Statistics
      stats: {
        totalMembers: 'Total Members',
        active: 'Active',
        pending: 'Pending',
        suspended: 'Suspended',
        staff: 'Staff'
      },
      
      // Search and filters
      search: {
        placeholder: 'Search members...',
        allStatus: 'All Status',
        allRoles: 'All Roles',
        allTiers: 'All Tiers'
      },
      
      // Table headers
      table: {
        member: 'Member',
        role: 'Role',
        status: 'Status',
        tier: 'Tier',
        handicap: 'Handicap',
        lastActivity: 'Last Activity',
        actions: 'Actions',
        joinDate: 'Join Date',
        phone: 'Phone',
        totalRounds: 'Total Rounds',
        nationalNumber: 'National Number',
        membershipRenewal: 'Membership Renewal',
        homeClub: 'Home Club',
        memberType: 'Member Type',
        clubNumber: 'Club Number',
        age: 'Age',
        vatNumber: 'VAT Number',
        organisation: 'Organisation',
        memberAccount: 'Member Account',
        federationMap: 'Federation Map',
        email: 'Email',
        gender: 'Gender',
        address: 'Address',
        emergencyContact: 'Emergency Contact',
        points: 'Points'
      },
      
      // Status values
      statusValues: {
        active: 'Active',
        pending: 'Pending',
        suspended: 'Suspended',
        inactive: 'Inactive'
      },
      
      // Role values
      roleValues: {
        member: 'Member',
        staff: 'Staff',
        admin: 'Admin',
        owner: 'Owner',
        members: 'Members',
        admins: 'Admins',
        owners: 'Owners'
      },
      
      // Membership tiers
      tiers: {
        bronze: 'Bronze',
        silver: 'Silver',
        gold: 'Gold',
        platinum: 'Platinum'
      },
      
      // Dialogs
      dialogs: {
        memberDetails: 'Member Details',
        editMember: 'Edit Member',
        inviteNewMember: 'Invite New Member',
        removeMember: 'Remove Member'
      },
      
      // Form labels
      form: {
        emailAddress: 'Email Address',
        firstName: 'First Name',
        lastName: 'Last Name',
        phone: 'Phone',
        address: 'Address',
        emergencyContact: 'Emergency Contact',
        role: 'Role',
        status: 'Status',
        handicap: 'Handicap',
        gender: 'Gender',
        totalRounds: 'Total Rounds',
        points: 'Points',
        joined: 'Joined',
        lastActivity: 'Last Activity',
        personalMessage: 'Personal Message (Optional)',
        publicProfile: 'Public Profile',
        placeholders: {
          email: 'member@example.com',
          emergencyContact: 'Name - Phone Number',
          personalMessage: 'Welcome to our golf club...'
        }
      },
      
      // Actions and buttons
      actions: {
        cancel: 'Cancel',
        updateMember: 'Update Member',
        updating: 'Updating...',
        sendInvitation: 'Send Invitation',
        sending: 'Sending...',
        removeMember: 'Remove Member'
      },
      
      // Messages and confirmations
      messages: {
        memberUpdated: 'Member updated successfully',
        invitationSent: 'Invitation sent successfully',
        memberRemoved: 'Member removed successfully',
        statusUpdated: 'Member status updated to {status}',
        exporting: 'Exporting members...',
        updateFailed: 'Failed to update member',
        inviteFailed: 'Failed to send invitation',
        removeFailed: 'Failed to remove member',
        statusUpdateFailed: 'Failed to update member status',
        removeConfirmation: 'Are you sure you want to remove {memberName} from the club? This action cannot be undone.'
      },
      
      // Empty states
      emptyState: {
        title: 'No members found',
        description: 'Try adjusting your search filters'
      },
      
      // Date formatting
      dateFormat: {
        never: 'Never'
      }
    },

    // Member Manager component
    memberManager: {
      failedToOpenConversation: 'Failed to open conversation',
      noClubSelected: 'No club selected',
      memberAndAccountRemoved: 'Member and account removed successfully',
      sendEmail: 'Send email',
      sendMessage: 'Send message',
      editMember: 'Edit member',
      approve: 'Approve',
      suspend: 'Suspend',
      reactivate: 'Reactivate',
      removeMemberOption: 'Remove member',
      alsoDeleteAccount: 'Also delete user account',
      deleteAccountWarning: "This will permanently delete their auth account and profile if they have no other club memberships. They won't be able to log in anymore.",
      emptyAll: 'No members found',
      emptyActive: 'No active members',
      emptyPending: 'No pending members',
      emptySuspended: 'No suspended members',
      emptyStaff: 'No staff members',
      emptyAllHint: 'Invite your first member',
      emptyFilterHint: 'Members will appear here',
      showing: 'Showing',
      badgeActive: 'active',
      badgeSuspended: 'suspended',
      panelEyebrow: 'Members',
      membersImported: 'Members imported successfully!',
      emailPanelEyebrow: 'Member',
      sendEmailTitle: 'Send Email',
      cancelEmail: 'Cancel',
      sendingEmail: 'Sending…',
      sendEmailButton: 'Send Email',
      emailFieldTo: 'To',
      emailFieldSubject: 'Subject',
      emailSubjectPlaceholder: 'Subject…',
      emailFieldMessage: 'Message',
      emailBodyPlaceholder: 'Write your message…',
      chatPanelEyebrow: 'Message',
      chatPanelTitle: 'Message',
      openingChat: 'Opening chat…',
      unableToOpenChat: 'Unable to open chat',
      emailSendFailed: 'Failed to send email',
      clearFilters: 'Clear'
    },

    // Memberships management
    memberships: {
      overview: 'Overview',
      tiers: 'Tiers & Pricing',
      renewals: 'Renewals',
      applications: 'Applications',
      demoData: 'Demo Data',
      activeMembers: 'Active Members',
      expiringSoon: 'Expiring Soon',
      overdue: 'Overdue',
      annualRevenue: 'Annual Revenue',
      membershipBreakdown: 'Membership Breakdown',
      total: 'total',
      renewalPipeline: 'Renewal Pipeline',
      viewAll: 'View All',
      period: 'Period',
      renewalsDue: 'Renewals Due',
      estRevenue: 'Est. Revenue',
      actions: 'Actions',
      members: 'members',
      sendReminders: 'Send Reminders',
      urgent: 'URGENT',
      thisMonth: 'This Month',
      renewalRate: 'Renewal Rate',
      avgDues: 'Avg. Dues',
      newMembers: 'New Members',
      lapsed: 'Lapsed',
      needsAttention: 'Needs Attention',
      overdueRenewals: 'overdue renewals',
      lastReminder: 'Last reminder',
      weeksAgo: 'weeks ago',
      send: 'Send',
      pendingApplications: 'pending applications',
      awaitingReview: 'Awaiting review',
      review: 'Review',
      upgradeRequests: 'upgrade requests',
      pendingApproval: 'Pending approval',
      process: 'Process',
      recentRenewals: 'Recent Renewals',
      annualRevenueLabel: 'Annual Revenue',
      activeTiers: 'Active Tiers',
      membersLabel: 'Members',
      membershipTiers: 'Membership Tiers',
      addTier: 'Add Tier',
      noTiersYet: 'No membership tiers yet',
      noTiersDescription: 'Create your first membership tier to start managing member pricing and benefits.',
      createFirstTier: 'Create First Tier',
      active: 'Active',
      annualDues: 'Annual Dues',
      initiation: 'Initiation',
      free: 'Free',
      benefitsIncluded: 'Benefits Included',
      revenueFromTier: 'Revenue from this tier',
      annual: 'Annual (Dues)',
      percentOfTotal: '% of Total',
      viewMembers: 'View Members',
      editTier: 'Edit Tier',
      editMembershipTier: 'Edit Membership Tier',
      editTierDescription: 'Update the details for this membership tier. Changes will affect all members in this tier.',
      tierName: 'Tier Name',
      tierNamePlaceholder: 'e.g., Full Membership',
      description: 'Description',
      descriptionPlaceholder: 'Brief description of this tier',
      annualDuesEuro: 'Annual Dues (€)',
      annualDuesPlaceholder: '2450',
      initiationFeeEuro: 'Initiation Fee (€)',
      initiationFeePlaceholder: '5000',
      tierColor: 'Tier Color',
      benefits: 'Benefits',
      addBenefit: 'Add Benefit',
      benefitPlaceholder: 'Enter benefit description',
      noBenefitsYet: 'No benefits added yet. Click "Add Benefit" to add one.',
      cancel: 'Cancel',
      saveChanges: 'Save Changes',
      saving: 'Saving...',
      addNewTier: 'Add New Membership Tier',
      addTierDescription: 'Create a new membership tier with pricing and benefits. This tier will be available for new memberships.',
      createTier: 'Create Tier',
      creating: 'Creating...',
      totalMembers: 'Total Members',
      paid: 'Paid',
      dueSoon: 'Due Soon',
      renewalsTab: 'Renewals',
      remind: 'Remind',
      reminding: 'Reminding...',
      sending: 'Sending...',
      export: 'Export',
      memberColumn: 'Member',
      tier: 'Tier',
      dueDate: 'Due Date',
      amount: 'Amount',
      status: 'Status',
      actionsColumn: 'Actions',
      daysOverdue: 'days overdue',
      markAsPaid: 'Mark as Paid',
      resetPayment: 'Reset Payment',
      sendReminder: 'Send Reminder',
      viewProfile: 'View Profile',
      pendingApplicationsTab: 'Pending Applications',
      rejectApplication: 'Reject Application',
      waitlist: 'Waitlist',
      approve: 'Approve',
      onWaitlist: 'On Waitlist',
      avgWait: 'Avg. Wait',
      nextInLine: 'Next in Line',
      manageWaitlist: 'Manage Waitlist',
      quickActions: 'Quick Actions',
      emailAllWaitlist: 'Email All Waitlist',
      promoteFromWaitlist: 'Promote from Waitlist',
      // V3 dues table
      daysOverdueCount: '{count}d overdue',
      dueToday: 'Due today',
      inDays: 'in {count}d',
      savingEllipsis: 'Saving…',
      assignTier: 'Assign tier',
      removeTier: 'Remove tier',
      noMembersMatchFilter: 'No members match this filter.',
      undo: 'Undo',
      markUnpaid: 'Mark as unpaid',
      markPaid: 'Mark Paid',
      colApplicant: 'Applicant',
      requestedTier: 'Requested tier',
      applied: 'Applied',
      referral: 'Referral',
      noApplicationsSearch: 'No applications match your search.',
      noPendingApplications: 'No pending applications.',
      today: 'Today',
      yesterday: 'Yesterday',
      daysAgoShort: '{count}d ago',
      pendingStatus: 'Pending',
      approvingEllipsis: 'Approving…',
      decline: 'Decline',
      backToTiers: 'Back to tiers',
      colour: 'Colour',
      optionalShownToMembers: 'Optional — shown to members',
      noTiersCreate: 'Create your first membership tier below.',
      editTierBtn: 'Edit tier',
      deleteTierBtn: 'Delete tier',
      searchMember: 'Search member, email, tier…',
      searchApplicant: 'Search applicant, tier, referral…',
      filterByTier: 'Filter by tier',
      clear: 'Clear',
      sendRemindersCount: 'Send reminders ({count})',
      loadingEllipsis: 'Loading…',
      membershipsEyebrow: 'Memberships',
      tiersButton: 'Tiers',
      nameRequired: 'Name *',
      annualDuesEuroForm: 'Annual dues (€)',
      // Email/chat side panels
      sendEmailEyebrow: 'Send email',
      sendMessageEyebrow: 'Send message',
      sendEmailBtn: 'Send email',
      sendingEmailEllipsis: 'Sending…',
      panelTo: 'To',
      panelSubject: 'Subject',
      panelMessage: 'Message',
      openingConversation: 'Opening conversation…',
    },

    // Services (Range Management)
    services: {
      overview: 'Overview',
      products: 'Products',
      rangeCards: 'Range Cards',
      reports: 'Reports',
      bucketsSoldToday: 'Buckets Sold Today',
      revenueToday: 'Revenue Today',
      activeRangeCards: 'Active Range Cards',
      avgPerMember: 'Avg. per Member',
      vsYesterday: 'vs yesterday',
      recentPurchases: 'Recent Purchases',
      todayLabel: 'Today',
      viewAll: 'View All',
      app: 'App',
      pos: 'POS',
      activeRangeCardsTitle: 'Active Range Cards',
      activeLabel: 'active',
      issueCard: 'Issue Card',
      expires: 'Expires',
      issueCardTitle: 'Issue Range Card',
      issueCardDescription: 'Create a new range practice card for a member. The card will be active immediately.',
      member: 'Member',
      searchMemberPlaceholder: 'Search member...',
      cardType: 'Card Type',
      selectCardType: 'Select card type...',
      expirationDate: 'Expiration Date',
      cardSummary: 'Card Summary',
      type: 'Type',
      expiresLabel: 'Expires',
      cancel: 'Cancel',
      issueCardButton: 'Issue Card',
      // Products tab
      productsTab: {
        bucketProducts: 'Bucket Products',
        configureBallBucketSizes: 'Configure ball bucket sizes and pricing',
        addProduct: 'Add Product',
        editProduct: 'Edit Product',
        addNewProduct: 'Add New Product',
        productName: 'Product Name',
        numberOfBalls: 'Number of Balls',
        basePriceEur: 'Base Price (€)',
        memberPriceEur: 'Member Price (€)',
        guestPriceEur: 'Guest Price (€)',
        active: 'Active',
        saveChanges: 'Save Changes',
        dynamicPricingRules: 'Dynamic Pricing Rules',
        adjustPricesBasedOnTime: 'Adjust prices based on time, day, or season',
        addRule: 'Add Rule',
        editPricingRule: 'Edit Pricing Rule',
        addPricingRule: 'Add Pricing Rule',
        ruleName: 'Rule Name',
        days: 'Days',
        startTime: 'Start Time',
        endTime: 'End Time',
        adjustment: 'Adjustment',
        percentage: 'Percentage (%)',
        fixedAmountEur: 'Fixed Amount (€)',
        memberPricingTiers: 'Member Pricing Tiers',
        configureDiscountsByLevel: 'Configure discounts by membership level',
        enabled: 'Enabled',
        disabled: 'Disabled',
        enableToConfigure: 'Enable to configure membership tier discounts',
        editMemberTierDiscount: 'Edit Member Tier Discount',
        discountPercentage: 'Discount Percentage',
        exampleCalculation: 'Example Calculation',
        product: 'Product',
        balls: 'Balls',
        base: 'Base',
        member: 'Member',
        actions: 'Actions',
        offBasePrice: 'off base price',
      },
      // Range Cards tab
      cardsTab: {
        activeCards: 'Active Cards',
        totalCredits: 'Total Credits',
        lowBalance: 'Low Balance',
        expiringSoon: 'Expiring Soon',
        creditPackages: 'Credit Packages',
        prepaidCreditBundles: 'Prepaid credit bundles',
        addPackage: 'Add Package',
        addCreditPackage: 'Add Credit Package',
        packageName: 'Package Name',
        credits: 'Credits',
        priceEur: 'Price (€)',
        bonusCredits: 'Bonus Credits',
        validityDays: 'Validity (days)',
        unlimitedPasses: 'Unlimited Passes',
        allYouCanHit: 'Unlimited buckets per day',
        addPass: 'Add Pass',
        addUnlimitedPass: 'Add Unlimited Pass',
        passName: 'Pass Name',
        duration: 'Duration',
        bucketsPerDay: 'Buckets per day',
        memberCards: 'Member Cards',
        activeCardsDescription: 'Active range cards and passes',
        activeRangeCards: 'Active range cards',
        issueNewCard: 'Issue New Card',
        searchMembers: 'Search members...',
        selectType: 'Select type...',
        selectPackage: 'Select package...',
        cannotAddToDemoData: 'Cannot add to demo data',
        // Member cards list
        all: 'All',
        unlimited: 'Unlimited',
        expired: 'Expired',
        issueCard: 'Issue Card',
        member: 'Member',
        type: 'Type',
        balance: 'Balance',
        expires: 'Expires',
        actions: 'Actions',
        cannotIssueDemo: 'Cannot issue card with demo data',
        active: 'Active',
        // Issue card dialog
        issueRangeCardTitle: 'Issue Range Card',
        cardType: 'Card Type',
        creditPackage: 'Credit Package',
        unlimitedPass: 'Unlimited Pass',
        packagePass: 'Package/Pass',
        // Add/Edit pass form
        dailyBucketLimit: 'Daily Bucket Limit',
        selectPlaceholder: 'Select...',
        passNamePlaceholder: 'e.g., Monthly Unlimited',
        durationMonthly: 'Monthly',
        durationQuarterly: 'Quarterly',
        durationAnnual: 'Annual',
        bucketPerDay1: '1 bucket/day',
        bucketPerDay2: '2 buckets/day',
        bucketPerDay3: '3 buckets/day',
        unlimitedBuckets: 'Unlimited buckets',
        bucketPerDayN: '{n} bucket/day',
        editUnlimitedPass: 'Edit Unlimited Pass',
        editUnlimitedPassDescription: 'Update the details for this unlimited pass.',
        saveChanges: 'Save Changes',
        editPass: 'Edit pass',
        editPackage: 'Edit package',
        cannotEditDemoData: 'Cannot edit demo data',
        durationShortMo: 'mo',
        durationShortQtr: 'qtr',
        durationShortYr: 'yr',
        demoData: 'Demo Data',
        demoDataDescription: 'Showing sample data. Add real data in your database to see live data.',
        editCreditPackage: 'Edit Credit Package',
        editCreditPackageDescription: 'Update the details for this credit package.',
        cancel: 'Cancel',
        daysValidity: 'days validity',
        bucketPerDayLabel: 'bucket/day'
      },
      // Reports tab
      reportsTab: {
        exportReport: 'Export Report',
        bucketsSold: 'Buckets Sold',
        revenue: 'Revenue',
        uniqueCustomers: 'Unique Customers',
        avgPerCustomer: 'Avg per Customer',
        revenueByDayOfWeek: 'Revenue by day of week',
        revenueByPaymentType: 'Revenue by payment type',
        bucketBreakdown: 'Bucket breakdown',
        hourlyDistribution: 'Hourly distribution',
        topCustomers: 'Top Customers',
        weeklyTrend: 'Weekly Trend',
        paymentMethods: 'Payment Methods',
      },
    },

    // Tee Sheet
    teeSheet: {
      newBooking: 'New Booking',
      teeSheetRefreshed: 'Tee sheet refreshed',
      noSlotsMatch: 'No time slots match your filter',
      tryAdjustingFilters: 'Try adjusting your filters or search',
      time: 'Time',
      status: 'Status',
      spots: 'Spots',
      bookingDetails: 'Booking Details',
      price: 'Price',
      actions: 'Actions',
      blocked: 'Blocked',
      totalBookings: 'Total Bookings',
      available: 'Available',
      partial: 'Partial',
      full: 'Full',
      slotBlocked: 'Slot at {time} blocked',
      slotUnblocked: 'Slot at {time} unblocked',
      failedToBlockSlot: 'Failed to block slot',
      cannotUnblock: 'This slot cannot be unblocked',
      failedToUnblockSlot: 'Failed to unblock slot',
      playerCheckedIn: 'Player checked in',
      failedToCheckInPlayer: 'Failed to check in player',
      bookingCancelled: 'Booking cancelled',
      failedToCancelBooking: 'Failed to cancel booking',
      markedAsNoShow: 'Marked as no-show',
      failedToMarkNoShow: 'Failed to mark as no-show',
      maxPlayersAllowed: 'Max {count} players allowed for this slot',
      playerAlreadyAdded: 'Player already added',
      pleaseSelectTimeSlot: 'Please select a time slot',
      bookingCreated: 'Booking created successfully',
      failedToCreateBooking: 'Failed to create booking',
      playersAdded: '{count} player(s) added',
      failedToAddPlayers: 'Failed to add players',
      addBooking: 'Add booking',
      markNoShow: 'Mark No-Show',
      markNoShowTitle: 'Mark as No-Show?',
      markNoShowDesc: 'This will mark the booking by {name} at {time} as a no-show.',
      cancel: 'Cancel',
      keepBooking: 'Keep Booking',
      cancelBooking: 'Cancel Booking',
      cancelBookingTitle: 'Cancel Booking?',
      cancelBookingDesc: 'This will cancel the booking for {name} at {time}. The slot will become available again.',
      bookings: 'Bookings',
      notes: 'Notes',
      organizer: 'Organizer',
      guest: 'Guest',
      checkedIn: 'Checked In',
      date: 'Date',
      timeSlot: 'Time Slot',
      searchMembers: 'Search Members',
      addGuest: 'Add Guest',
      selectDateAndTime: 'Select a date and time slot to add players',
      addPlayers: 'Add Players',
      blockSlot: 'Block Slot',
      unblockSlot: 'Unblock Slot',
      timeSlotLabel: 'Time Slot',
      spotsAvailable: '{available} of {total} spots available',
      loading: 'Loading...',
      selectTime: 'Select time',
      guestNamePlaceholder: 'Guest name',
      addNotesPlaceholder: 'Add any notes about this booking...',
      searchByName: 'Search by name (min 2 characters)...',
      checkIn: 'Check In',
      createBooking: 'Create Booking',
      noAvailableSlots: 'No available slots',
      // Additional translations for TeeSheetManager dialogs
      today: 'Today',
      timeSlotWithTime: 'Time Slot: {time}',
      blockedColon: 'Blocked:',
      bookingDetailsTitle: 'Booking Details',
      playersCount: '{count} players',
      playersAndCheckedIn: '{players} players • {checkedIn} checked in',
      playersOfTotal: 'Players ({count}/{total})',
      addPlayersWithSpots: 'Add Players ({spots} spots)',
      hcp: 'HCP:',
      blockTimeSlotTitle: 'Block Time Slot',
      blockSlotDesc: 'Block the {time} time slot on {date}?',
      reasonOptional: 'Reason (optional)',
      blockReasonPlaceholder: 'e.g., Tournament reservation, Maintenance, Private event...',
      players: 'players',
      maintenance: 'Maintenance',
      slotBecomeAvailable: 'The slot will become available again.',
      noTimeSlotsMatch: 'No time slots match your filter',
      // QRCodeScanner translations
      scanQRCode: 'Scan QR Code',
      camera: 'Camera',
      upload: 'Upload',
      manual: 'Manual',
      cameraAccessDenied: 'Camera access denied. Please enable camera permissions.',
      unableToAccessCamera: 'Unable to access camera. Try uploading an image instead.',
      invalidQRCodeFormat: 'Invalid QR code format',
      failedToProcessImage: 'Failed to process image',
      noQRCodeFoundInImage: 'No QR code found in image',
      failedToReadImageFile: 'Failed to read image file',
      invalidConfirmationCodeFormat: 'Invalid confirmation code format',
      tryAgain: 'Try Again',
      positionQRCodeWithinFrame: 'Position the QR code within the frame',
      qrCodeScannedSuccessfully: 'QR code scanned successfully!',
      startingCamera: 'Starting camera...',
      processing: 'Processing...',
      qrCodeFound: 'QR Code Found!',
      uploadQRCodeImage: 'Upload QR Code Image',
      clickToSelectImageFile: 'Click to select an image file',
      enterConfirmationCodeFromBooking: 'Enter the confirmation code from the booking',
      confirmationCodePlaceholder: 'e.g., ABC123',
      found: 'Found!',
      lookUpBooking: 'Look Up Booking',
      // TeeSheetManager additional keys
      openingSettings: 'Opening settings...',
      blockedByAdmin: 'Blocked by admin',
      cancelledByAdmin: 'Cancelled by admin',
      paymentMarkedReceived: 'Payment marked as received',
      failedToUpdatePayment: 'Failed to update payment status',
      statusConfirmed: 'Confirmed',
      statusPending: 'Pending',
      statusCancelled: 'Cancelled',
      statusCompleted: 'Completed',
      statusNoShow: 'No-Show',
      payment: 'Payment',
      payAtClub: 'Pay at Club',
      paid: 'Paid',
      free: 'Free',
      markAsPaid: 'Mark as Paid',
      addPlayersTitle: 'Add Players to Booking',
      selectedPlayers: 'Selected Players',
      playersToAdd: 'Players to Add',
      addNPlayers: 'Add {count}',
      searching: 'Searching...',
      loadingSlots: 'Loading...',
      creating: 'Creating...',
      adding: 'Adding...',
      addBookingTitle: 'Add booking',
    },

    // Tee Sheet Analytics
    teeSheetAnalytics: {
      teeSheet: 'Tee Sheet',
      sevenDays: '7 Days',
      thirtyDays: '30 Days',
      ninetyDays: '90 Days',
      export: 'Export',
      // Page content
      failedToLoadAnalytics: 'Failed to load analytics',
      noTeeSheetConfigured: 'No tee sheet configured',
      setUpTeeSheet: 'Set up a tee sheet to view analytics.',
      demoMode: 'Demo Mode:',
      demoModeDescription: 'Showing sample data. Book some tee times to see real analytics!',
      // KPIs
      utilization: 'Utilization',
      bookings: 'Bookings',
      cancelRate: 'Cancel Rate',
      ofBookings: 'of bookings',
      noShows: 'No-Shows',
      // Heatmap
      utilizationHeatmap: 'Utilization Heatmap',
      weeklyUtilizationDesc: 'Weekly utilization by time slot and day',
      dailyBookingTrend: 'Daily Booking Trend',
      // Detailed Stats
      detailedStatistics: 'Detailed Statistics',
      performance: 'Performance',
      avgDailyBookings: 'Avg Daily Bookings',
      weekendUtilization: 'Weekend Utilization',
      weekdayUtilization: 'Weekday Utilization',
      peakHours: 'Peak Hours (8-11)',
      bookingsSuffix: 'bookings',
      // Reliability
      reliability: 'Reliability',
      cancellationRate: 'Cancellation Rate',
      noShowRate: 'No-Show Rate',
      avgGroupSize: 'Avg Group Size',
      players: 'players',
      totalPlayers: 'Total Players',
      // Top Members
      topMembers: 'Top Members',
      noBookingDataYet: 'No booking data yet',
      // Charts
      peakBookingTimes: 'Peak Booking Times',
      bookingsByDay: 'Bookings by Day',
    },

    // Tee Sheet Calendar
    teeSheetCalendar: {
      clickDayToManage: 'Click a day to manage availability',
      month: 'Month',
      week: 'Week',
      day: 'Day',
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
      sun: 'Sun',
      bookingsLegend: 'Bookings',
      blockedSlotsLegend: 'Blocked slots',
      todayLegend: 'Today',
      swipeToSeeWeek: 'Swipe to see more days',
      blocked: 'Blocked',
      full: 'Full',
      spotsOpen: '{count} spots open',
      selectADay: 'Select a day',
      viewAndManageBlocks: 'View and manage blocks',
      clickDayOnCalendar: 'Click a day on the calendar',
      selectDayToViewDetails: 'Select a day to view details',
      available: 'Available',
      reserved: 'Reserved',
      blockTimes: 'Block Times',
      reservations: 'Reservations',
      guest: 'Guest',
      member: 'Member',
      noReservationsForDay: 'No reservations for this day',
      currentBlocks: 'Current Blocks',
      allDay: 'All Day',
      noBlocksForDay: 'No blocks for this day',
      blockTimesTitle: 'Block Times',
      selectDateFirst: 'Select a date first',
      whatToBlock: 'What to block?',
      entireDay: 'Entire Day',
      singleTimeSlot: 'Single Time Slot',
      timeRange: 'Time Range',
      startTime: 'Start Time',
      endTime: 'End Time',
      blockType: 'Block Type',
      blockedType: 'Blocked',
      maintenanceType: 'Maintenance',
      tournamentType: 'Tournament',
      reasonOptional: 'Reason (optional)',
      blockReasonPlaceholder: 'e.g., Course maintenance, Private event...',
      cancel: 'Cancel',
      blocking: 'Blocking...',
      block: 'Block',
      pleaseSelectDateFirst: 'Please select a date first',
      teeSheetNotConfigured: 'Tee sheet not configured',
      dayBlockedSuccess: 'Day blocked successfully',
      pleaseSelectTime: 'Please select a time',
      timeSlotBlockedSuccess: 'Time slot blocked successfully',
      pleaseSelectStartTime: 'Please select start time',
      pleaseSelectEndTime: 'Please select end time',
      timeRangeBlockedSuccess: 'Time range blocked successfully',
      failedToBlockTimeSlot: 'Failed to block time slot',
      blockRemoved: 'Block removed',
      failedToRemoveBlock: 'Failed to remove block',
    },

    // Tee Generate
    teeGenerate: {
      noClubSelected: 'No club selected',
    },

    // Tee Settings
    teeSettings: {
      teeSheetSettings: {
        teeSheetActive: 'Tee Sheet Active',
        teeSheetPaused: 'Tee Sheet Paused',
        membersCanBook: 'Members can view and book tee times',
        bookingDisabled: 'Booking is currently disabled',
        active: 'Active',
        paused: 'Paused',
        operatingHours: 'Operating Hours',
        operatingHoursDescription: 'Set when tee times are available',
        openingTime: 'Opening Time',
        closingTime: 'Closing Time',
        timeSlotInterval: 'Time Slot Interval',
        playersPerSlot: 'Players Per Slot',
        infoApproxSlots: 'With {interval}-minute intervals from {open} to {close}, you have approximately {count} time slots per day.',
        minutes: 'minutes',
        players: 'players',
        bookingRules: 'Booking Rules',
        bookingRulesDescription: 'Configure booking restrictions',
        advanceBooking: 'Advance Booking',
        advanceBookingHelp: 'How far in advance members can book',
        minimumNotice: 'Minimum Notice',
        minimumNoticeHelp: 'Minimum time before tee time',
        cancellationWindow: 'Cancellation Window',
        cancellationWindowHelp: 'Latest cancellation time',
        maxPlayersPerBooking: 'Max Players Per Booking',
        maxPlayersHelp: 'Per individual booking',
        daysAdvance: 'days',
        noMinimum: 'No minimum',
        hour: 'hour',
        hours: 'hours',
        hoursAdvance: 'hours',
        noLimit: 'No limit',
        hoursBefore: 'hours before',
        player: 'player',
        guestPolicies: 'Guest Policies',
        guestPoliciesDescription: 'Manage non-member bookings',
        allowGuestBookings: 'Allow Guest Bookings',
        allowGuestBookingsHelp: 'Members can bring non-member guests',
        maximumGuestsPerBooking: 'Maximum Guests Per Booking',
        requireApprovalForGuests: 'Require Approval for Guests',
        requireApprovalForGuestsHelp: 'Admin must approve guest bookings',
        guest: 'guest',
        guests: 'guests',
        joinPolicies: 'Join Policies',
        joinPoliciesDescription: 'Allow members to join partial groups',
        allowPartialJoins: 'Allow Partial Joins',
        allowPartialJoinsHelp: 'Members can request to join groups with open spots',
        requireApprovalForJoins: 'Require Approval for Joins',
        requireApprovalForJoinsHelp: 'Group organizer must approve join requests',
        joinInfoEnabled: 'When enabled, members can see partially filled slots and request to join.',
        joinInfoApprovalRequired: 'The booking organizer must approve each request.',
        joinInfoAutoApproval: 'Requests are automatically approved when spots are available.',
        defaultPricing: 'Default Pricing',
        defaultPricingDescription: 'Set the default green fee for tee times',
        defaultPricePerPlayer: 'Default Price Per Player',
        currency: 'Currency',
        fullGroup: 'Full group ({count} players):',
        unsavedChanges: 'You have unsaved changes',
        discard: 'Discard',
        save: 'Save',
        settingsSavedSuccessfully: 'Settings saved successfully',
        failedToSaveSettings: 'Failed to save settings',
        changesDiscarded: 'Changes discarded',
        noClubSelected: 'No club selected',
        failedToLoadSettings: 'Failed to load settings',
      },
    },

    // Check-in Station
    checkInStation: {
      export: 'Export',
      addWalkIn: 'Add Walk-in',
      pleaseSelectAtLeastOnePlayer: 'Please select at least one player',
      playersCheckedIn: 'players checked in',
      failedToCheckInPlayers: 'Failed to check in players',
      allPlayersCheckedIn: 'All players checked in',
      failedToCheckInAllPlayers: 'Failed to check in all players',
      bookingMarkedAsNoShow: 'Booking marked as no-show',
      failedToMarkAsNoShow: 'Failed to mark as no-show',
      playerCheckedInSuccessfully: 'Player checked in successfully',
      failedToCheckInPlayer: 'Failed to check in player',
      notesUpdatedSuccessfully: 'Notes updated successfully',
      failedToUpdateNotes: 'Failed to update notes',
      confirmationCodeCopied: 'Confirmation code copied',
      foundBooking: 'Found booking',
      booking: 'Booking',
      bookingAlreadyCheckedIn: 'is already checked in',
      bookingNotFound: 'Booking not found:',
      pleaseEnterGuestName: 'Please enter guest name',
      pleaseSelectTimeSlot: 'Please select a time slot',
      pleaseAddAtLeastOnePlayer: 'Please add at least one player',
      failedToCreateWalkInBooking: 'Failed to create walk-in booking',
      bookingCreatedButCheckInFailed: 'Booking created but check-in failed',
      walkInBookingCreatedSuccessfully: 'Walk-in booking created successfully',
      checkInsExportedSuccessfully: 'Check-ins exported successfully',
      searchPlaceholder: 'Search by name or confirmation code...',
      allStatus: 'All Status',
      // UI Labels
      guest: 'Guest',
      member: 'Member',
      unknown: 'Unknown',
      today: 'Today',
      yesterday: 'Yesterday',
      currentTime: 'Current Time',
      bookings: 'Bookings',
      allBookingsForToday: 'All bookings for today',
      allBookingsFor: 'All bookings for',
      bookingCount: 'booking',
      bookingsCount: 'bookings',
      allCaughtUp: 'All caught up!',
      noUpcomingBookings: 'No upcoming bookings in the next hour',
      playerSelected: 'player selected',
      playersSelected: 'players selected',
      checkInSelected: 'Check In Selected',
      checkedIn: 'Checked In',
      onCourseNow: 'On course now',
      todaysCheckIns: "Today's Check-ins",
      noCheckInsYet: 'No check-ins yet today',
      timeRange: 'Time Range',
      saveNotes: 'Save Notes',
      notSelected: 'Not selected',
      addNotesBooking: 'Add notes about this booking...',
      searchMembers: 'Search members...',
      guestName: 'Enter guest name',
      walkInBooking: 'Walk-in Booking',
      createWalkInDesc: 'Create a booking for walk-in guests',
      selectTimeSlot: 'Select Time Slot',
      addPlayers: 'Add Players',
      addMember: 'Add Member',
      addGuest: 'Add Guest',
      cancel: 'Cancel',
      createBooking: 'Create Booking',
      bookingDetails: 'Booking Details',
      teeTime: 'Tee Time',
      confirmationCode: 'Confirmation Code',
      players: 'Players',
      notes: 'Notes',
      checkInAll: 'Check In All',
      markNoShow: 'Mark No-Show',
      close: 'Close',
      pending: 'Pending',
      partial: 'Partial',
      complete: 'Complete',
      noShow: 'No-Show',
      now: 'Now',
      minutesShort: 'min',
      // Filter options
      status: 'Status',
      clear: 'Clear',
      clearFilters: 'Clear Filters',
      allTimes: 'All Times',
      morning: 'Morning (5am-12pm)',
      afternoon: 'Afternoon (12pm-5pm)',
      evening: 'Evening (5pm-8pm)',
      lateArrivals: 'Late Arrivals',
      scanQR: 'Scan QR',
      // Player labels
      player: 'player',
      guests: 'guests',
      // Payment status
      paid: 'Paid',
      unpaid: 'Unpaid',
      noPaymentRequired: 'No Payment Required',
      // Sidebar
      noCheckInsYetToday: 'No check-ins yet today',
      viewAllTodaysBookings: "View All Today's Bookings",
      noShowReports: 'No-Show Reports',
      checkedInAt: 'Checked in',
      // Booking detail modal
      confirmation: 'Confirmation',
      organizer: 'Organizer',
      checkIn: 'Check In',
      saving: 'Saving...',
      checkInAllPlayers: 'Check In All Players',
      // Walk-in modal
      addWalkInBooking: 'Add Walk-In Booking',
      selectAvailableTimeSlotFor: 'Select an available time slot for',
      slotsAvailable: 'slots available',
      noAvailableSlots: 'No available slots',
      allSlotsFullyBooked: 'All time slots are fully booked for this date',
      spot: 'spot',
      spots: 'spots',
      selectedTime: 'Selected Time',
      change: 'Change',
      addPlayer: 'Add Player',
      noMembersFound: 'No members found',
      guestNameRequired: 'Guest Name *',
      handicapOptional: 'Handicap (optional)',
      emailOptional: 'Email (optional)',
      creating: 'Creating...',
      createAndCheckIn: 'Create & Check In',
      // Payment
      payment: 'Payment',
      payAtClub: 'Pay at Club',
      paymentMarkedAsReceived: 'Payment marked as received',
      markAsPaid: 'Mark as Paid',
      // No-show dialog
      markAsNoShowQuestion: 'Mark as No-Show?',
      markAsNoShowConfirmation: 'Are you sure you want to mark this booking as no-show?',
      actionCannotBeUndone: 'This action cannot be undone.',
      markAsNoShow: 'Mark as No-Show',
    },

    // Task conversion from messages
    tasks: {
      // Headers and titles
      headers: {
        originalMessage: 'Original Message',
        taskDetails: 'Task Details'
      },

      // Form labels
      form: {
        taskTitle: 'Task Title',
        description: 'Description',
        category: 'Category',
        priority: 'Priority',
        assignTo: 'Assign To (Optional)',
        notifyMember: 'Send confirmation message to member',
        customMessage: 'Custom Message (Optional)'
      },

      // Placeholders
      placeholders: {
        taskTitle: 'Brief description of the task',
        description: 'Detailed description of what needs to be done',
        assignTo: 'Select staff member or leave unassigned',
        customMessage: 'Leave empty for default message: \'Thanks! I\'ve created a task to address your message. We\'ll keep you updated on the progress!\''
      },

      // Categories
      categories: {
        maintenance: '🔧 Maintenance',
        maintenanceDesc: 'Repairs, course conditions, equipment fixes',
        cartService: '🚗 Cart Service',
        cartServiceDesc: 'Golf cart issues, availability, maintenance',
        proShop: '🏪 Pro Shop',
        proShopDesc: 'Equipment, merchandise, pro shop services',
        foodService: '🍽️ Food Service',
        foodServiceDesc: 'Restaurant, bar, catering services',
        range: '🎯 Driving Range',
        rangeDesc: 'Practice facilities, ball machine, range issues',
        memberServices: '💬 Member Services',
        memberServicesDesc: 'General member requests and communications',
        other: '📋 Other',
        otherDesc: 'Other tasks not covered by specific categories'
      },

      // Priorities
      priorities: {
        low: '🟢 Low',
        lowDesc: 'Can be addressed when convenient',
        normal: '🟡 Normal',
        normalDesc: 'Standard priority, address within normal timeframe',
        high: '🟠 High',
        highDesc: 'Important, should be addressed soon',
        urgent: '🔴 Urgent',
        urgentDesc: 'Immediate attention required'
      },

      // Assignment options
      assignment: {
        autoAssign: '🔄 Auto-assign later',
        suggestionHint: 'Suggestions based on category and current workload',
        tasks: 'tasks'
      },

      // Smart generation text
      smartGeneration: {
        addressPrefix: 'Address: ',
        memberRequestPrefix: 'Member request from ',
        defaultInstructions: ':\n\n"{content}"\n\nPlease investigate and resolve this issue.'
      },

      // Buttons and actions
      actions: {
        cancel: 'Cancel',
        createTask: 'Create Task',
        creating: 'Creating Task...'
      },

      // Messages and validations
      messages: {
        titleRequired: 'Please enter a task title',
        descriptionRequired: 'Please enter a task description',
        by: 'by'
      }
    },

    // Settings management
    settings: {
      // Section headers
      sections: {
        basicInformation: 'Basic Information',
        basicInformationDesc: 'Essential club details and identity',
        contactInformation: 'Contact Information',
        contactInformationDesc: 'How members can reach your club',
        location: 'Location',
        locationDesc: 'Physical address and location details',
        operations: 'Operations',
        operationsDesc: 'Operating hours and schedules',
        clubImages: 'Club Images',
        clubImagesDesc: 'Logo and cover image uploads',
        socialMedia: 'Social Media',
        socialMediaDesc: 'Connect with your members on social platforms',
        amenities: 'Facilities & Amenities',
        amenitiesDesc: 'What does your club offer to members?'
      },

      // Amenities subheadings
      amenitiesSubheadings: {
        practiceFacilities: 'Practice Facilities',
        services: 'Services',
        clubhouse: 'Clubhouse',
        additionalFacilities: 'Additional Facilities'
      },

      // Crop modal
      cropModal: {
        title: 'Crop image',
        hint: 'Drag to move · corner to resize',
        cancel: 'Cancel',
        apply: 'Apply crop'
      },

      // Operating hours
      operatingHours: {
        closed: 'Closed'
      },

      // Amenities labels
      amenities: {
        drivingRange: 'Driving Range',
        puttingGreen: 'Putting Green',
        chippingArea: 'Chipping Area',
        bunkerPractice: 'Bunker Practice',
        trackman: 'TrackMan',
        proShop: 'Pro Shop',
        restaurant: 'Restaurant',
        bar: 'Bar / Lounge',
        lockerRooms: 'Locker Rooms',
        showers: 'Showers',
        caddieService: 'Caddie Service',
        cartRental: 'Cart Rental',
        clubRental: 'Club Rental',
        lessonFacilities: 'Lesson Facilities',
        swimmingPool: 'Swimming Pool',
        tennisCourts: 'Tennis Courts',
        fitnessCenter: 'Fitness Center'
      },
      
      // Form labels
      form: {
        clubName: 'Club Name',
        clubType: 'Club Type',
        description: 'Description',
        establishedYear: 'Established Year',
        memberCapacity: 'Member Capacity',
        phoneNumber: 'Phone Number',
        emailAddress: 'Email Address',
        website: 'Website',
        websiteUrl: 'Website URL',
        streetAddress: 'Street Address',
        city: 'City',
        stateProvince: 'Province / Region',
        country: 'Country',
        operatingHours: 'Operating Hours',
        clubLogo: 'Club Logo',
        coverImage: 'Cover Image'
      },
      
      // Form placeholders
      placeholders: {
        clubDescription: 'Tell members about your club...',
        establishedYear: 'e.g., 1995',
        memberCapacity: 'e.g., 500',
        phoneNumber: '+32 2 123 45 67',
        emailAddress: 'info@yourclub.com',
        website: 'www.yourclub.com',
        websiteUrl: 'https://www.yourclub.com',
        streetAddress: '12 Rue du Golf',
        city: 'Brussels',
        stateProvince: 'Brussels',
        country: 'Belgium',
        operatingHours: 'Daily 07:00–20:00'
      },
      
      // Club types
      clubTypes: {
        private: 'Private Club',
        public: 'Public Course',
        semiPrivate: 'Semi-Private'
      },
      
      // Image upload
      images: {
        currentLogo: 'Current logo',
        changeLogo: 'Change Logo',
        uploadLogo: 'Upload your club logo',
        logoSpecs: 'Square format (1:1) • 400×400px recommended • PNG or JPG • Max 2MB',
        chooseFile: 'Choose File',
        currentCoverImage: 'Current cover image',
        changeCover: 'Change Cover',
        uploadCoverImage: 'Upload cover image',
        coverSpecs: 'Landscape format (16:9) • 1920×1080px recommended • JPG • Max 5MB • Keep main subject centered',
        clubLogoAlt: 'Club logo',
        clubCoverAlt: 'Club cover'
      },
      
      // Validation errors
      validation: {
        clubNameRequired: 'Club name is required',
        validEmailRequired: 'Please enter a valid email address',
        validUrlRequired: 'Please enter a valid URL (starting with http:// or https://)',
        validYearRequired: 'Please enter a valid year',
        memberCapacityRequired: 'Member capacity must be greater than 0'
      },
      
      // Messages and notifications
      messages: {
        profileUpdated: 'Profile Updated',
        profileUpdatedDesc: 'Club profile has been saved successfully.',
        error: 'Error',
        errorDesc: 'Failed to save club profile. Please try again.',
        validationError: 'Validation Error',
        validationErrorDesc: 'Please fix the errors below before saving.',
        noChanges: 'No Changes',
        noChangesDesc: 'No changes were made to save.',
        uploadSuccessful: 'Upload Successful',
        logoUploadedDesc: 'Logo uploaded successfully.',
        coverUploadedDesc: 'Cover image uploaded successfully.',
        uploadFailed: 'Upload Failed',
        logoUploadFailedDesc: 'Failed to upload logo',
        coverUploadFailedDesc: 'Failed to upload cover image',
        failedToLoadClubData: 'Failed to Load Club Data',
        loadErrorDesc: 'An error occurred while loading the club profile.',
        tryAgain: 'Try Again'
      },
      
      // Descriptions and help text
      descriptions: {
        operatingHours: 'Describe when your club is open for members'
      }
    },

    // Courses management
    courses: {
      // Headers and titles
      headers: {
        courseManagement: 'Course Management',
        addNewCourse: 'Add New Course',
        editCourse: 'Edit Course'
      },
      
      // Form labels
      form: {
        courseName: 'Course Name',
        address: 'Address',
        description: 'Description',
        holeCount: 'Hole Count',
        coursePar: 'Course Par',
        courseRating: 'Course Rating',
        slopeRating: 'Slope Rating',
        telephone: 'Telephone',
        website: 'Website',
        postalCode: 'Postal Code',
        measurementUnit: 'Measurement Unit',
        latitude: 'GPS Latitude',
        longitude: 'GPS Longitude'
      },
      
      // Form placeholders
      placeholders: {
        courseName: 'Enter course name',
        address: 'Enter course address',
        description: 'Describe the course features, difficulty, etc.',
        telephone: 'Enter phone number',
        website: 'Enter website URL',
        postalCode: 'Enter postal code',
        latitude: '0.000000',
        longitude: '0.000000'
      },
      
      // Options
      options: {
        yards: 'Yards',
        meters: 'Meters'
      },
      
      // Actions
      actions: {
        saveCourse: 'Save Course',
        updateCourse: 'Update Course',
        addCourse: 'Add Course',
        cancel: 'Cancel',
        adding: 'Adding...',
        updating: 'Updating...',
        getMyLocation: 'Get My Location',
        updateLocation: 'Update Location',
        viewDetails: 'View Details',
        deleteCourse: 'Delete Course',
        addFirstCourse: 'Add Your First Course'
      },
      
      // Status and states
      status: {
        gpsAvailable: 'GPS Available',
        noGps: 'No GPS',
        noGpsData: 'No GPS Data',
        loadingCourses: 'Loading courses...',
        noCourses: 'No courses found',
        getStarted: 'Get started by adding your first course'
      },
      
      // Validation errors
      validation: {
        nameRequired: 'Course name is required',
        addressRequired: 'Address is required'
      },
      
      // Messages and notifications
      messages: {
        validationError: 'Validation Error',
        validationErrorDesc: 'Please fix the errors below before saving.',
        error: 'Error',
        noClubSelected: 'No club selected. Please select a club first.',
        courseUpdated: 'Success',
        courseUpdatedDesc: 'Course updated successfully!',
        updateFailed: 'Failed to update course.',
        courseAdded: 'Course Added',
        courseAddedDesc: 'New course has been added successfully.',
        saveFailed: 'Failed to save course. Please try again.',
        deleteNotAvailable: 'Delete Not Available',
        deleteNotAvailableDesc: 'Course deletion is not yet implemented in the API.',
        toggleNotAvailable: 'Toggle Not Available',
        toggleNotAvailableDesc: 'Course status toggle is not yet implemented in the API.',
        locationUpdated: 'Location Updated',
        locationUpdatedDesc: 'GPS coordinates have been updated with your current location.',
        locationError: 'Location Error',
        locationErrorDesc: 'Unable to get your current location. Please enter coordinates manually.',
        locationNotSupported: 'Location Not Supported',
        locationNotSupportedDesc: 'Geolocation is not supported by your browser.'
      }
    },

    // Announcements management
    announcements: {
      // Headers and titles
      headers: {
        announcementManagement: 'Announcement Management',
        createNewAnnouncement: 'Create New Announcement',
        editAnnouncement: 'Edit Announcement',
        deleteAnnouncement: 'Delete Announcement'
      },
      
      // Statistics
      stats: {
        totalAnnouncements: 'Total Announcements',
        published: 'Published',
        scheduled: 'Scheduled',
        drafts: 'Drafts'
      },
      
      // Search and filters
      search: {
        placeholder: 'Search announcements...',
        allStatus: 'All Status',
        allPriority: 'All Priority'
      },
      
      // Form labels
      form: {
        title: 'Title',
        content: 'Content',
        priority: 'Priority',
        status: 'Status',
        targetAudience: 'Target Audience',
        channels: 'Channels',
        expiresAt: 'Expires At (Optional)',
        pinAnnouncement: 'Pin Announcement',
        sendEmail: 'Send Email Notification',
        sendPush: 'Send Push Notification'
      },
      
      // Form placeholders
      placeholders: {
        title: 'Announcement title',
        content: 'Announcement content...',
        selectChannels: 'Select channels...',
        scheduledDate: 'Select date and time'
      },
      
      // Priority levels
      priority: {
        low: 'Low',
        medium: 'Medium',
        high: 'High'
      },
      
      // Status types
      status: {
        draft: 'Draft',
        scheduled: 'Scheduled',
        published: 'Published'
      },
      
      // Target audiences
      audience: {
        all: 'All Club Members',
        members: 'Members Only',
        staff: 'Staff Only'
      },
      
      // Actions
      actions: {
        createAnnouncement: 'Create Announcement',
        updateAnnouncement: 'Update Announcement',
        publishNow: 'Publish Now',
        edit: 'Edit',
        delete: 'Delete',
        cancel: 'Cancel',
        creating: 'Creating...',
        updating: 'Updating...',
        useTemplate: 'Use Template'
      },
      
      // Tabs
      tabs: {
        content: 'Content',
        delivery: 'Delivery',
        templates: 'Templates'
      },
      
      // Labels and indicators
      labels: {
        email: 'Email',
        push: 'Push',
        pinned: 'Pinned',
        expired: 'Expired',
        scheduled: 'Scheduled: {date}',
        published: 'Published: {date}',
        created: 'Created: {date}',
        publishedPrefix: 'Published',
        createdPrefix: 'Created'
      },
      
      // Templates
      templates: {
        courseMaintenance: 'Course Maintenance',
        courseMaintenanceContent: 'Course maintenance is scheduled. Please check for updates.',
        tournament: 'Tournament Announcement',
        tournamentContent: 'Upcoming tournament details and registration information.',
        weather: 'Weather Update',
        weatherContent: 'Weather conditions affecting play today.',
        policy: 'Policy Update',
        policyContent: 'Updated club policy information.',
        general: 'General Announcement',
        generalContent: 'General club information and updates.'
      },
      
      // Messages and notifications
      messages: {
        loadingAnnouncements: 'Loading announcements...',
        failedToLoad: 'Failed to load announcement data',
        selectAtLeastOneChannel: 'Please select at least one channel',
        createdSuccessfully: 'Announcement created successfully',
        postedToChannels: 'Announcement posted to channels',
        scheduledSuccessfully: 'Announcement scheduled successfully',
        failedToCreate: 'Failed to create announcement',
        updatedSuccessfully: 'Announcement updated successfully',
        publishedToChannels: 'Announcement published to channels',
        failedToUpdate: 'Failed to update announcement',
        deletedSuccessfully: 'Announcement deleted successfully',
        failedToDelete: 'Failed to delete announcement',
        publishedSuccessfully: 'Announcement published successfully',
        failedToPublish: 'Failed to publish announcement',
        deleteConfirmation: 'Are you sure you want to delete this announcement? This action cannot be undone.',
        noAnnouncements: 'No announcements found',
        draftAutoSaved: 'Draft saved automatically',
        failedAutoSave: 'Failed to save draft',
        templateApplied: 'Template "{name}" applied',
        postedToSelectedChannels: 'Posted to selected channels'
      },

      // Empty states (list filters)
      emptyState: {
        noFound: 'No announcements found',
        noPublished: 'No published announcements',
        noScheduled: 'No scheduled announcements',
        noDrafts: 'No drafts',
        createFirst: 'Create your first announcement',
        willAppearHere: 'Announcements will appear here'
      },

      // Additional UI text
      ui: {
        totalViews: 'Total Views',
        archived: 'Archived',
        urgent: 'Urgent',
        useTemplate: 'Use Template',
        noClubAccess: 'No Club Access',
        noClubAccessDescription: 'You need to be a club admin to manage announcements.',
        notSet: 'Not set',
        deleteConfirmationTitle: 'Are you sure you want to delete "{title}"?',
        deleteConfirmationDescription: 'This action cannot be undone.',
        scheduleFor: 'Schedule For',
        templateInstructions: 'Click on a template to apply it to your announcement. You can then customize the content.',
        views: 'views',
        replies: 'replies',
        interactions: 'interactions',
        channels: 'channels',
        members: 'members',
        channelsAvailable: '{count} channels available',
        scheduledLabel: 'Scheduled: {date}',
        publishedLabel: 'Published: {date}',
        createdLabel: 'Created: {date}',
        specificGroups: 'Specific Groups',
        publish: 'Publish',
        pin: 'Pin',
        typeAnnouncement: 'Announcement',
        typeCommunication: 'Communication',
        loadingCommunications: 'Loading communications...'
      },
      clubOrUserUnavailable: 'Club or user information not available',

      // WhatsNewModal preview section
      whatsNew: {
        preview: {
          suggestFeature: 'Suggest a feature',
          reportIssue: 'Report an issue',
          shareFeedback: 'Share feedback',
          findInProfile: 'Find this in your Profile → Feedback',
          tapHoleToEnterScore: 'Tap a hole to enter score',
        },
      },
    },

    // Communications (UnifiedCommunicationsManager)
    communications: {
      channelEmail: 'Email',
      channelPush: 'Push',
      saveAsDraft: 'Save as Draft',
      optionalSuffix: '— optional',
      templateNewButton: 'New',
      templateNewFormHeader: 'New Template',
      templateNamePlaceholder: 'Template name',
      templateTitlePlaceholder: 'Default title (use [placeholders])',
      templateContentPlaceholder: 'Default content (use [placeholders])',
      templateCategoryGeneral: 'General',
      templateCategoryEvent: 'Event',
      templateCategoryMaintenance: 'Maintenance',
      templateCategoryPolicy: 'Policy',
      templateCategoryTournament: 'Tournament',
      templateSaved: 'Template saved',
      templateSaveFailed: 'Failed to save template',
      templateSaveButton: 'Save Template',
      templateCancelButton: 'Cancel',
      templateDeleted: 'Template deleted',
      templateDeleteFailed: 'Failed to delete template',
      templateDeleteButton: 'Delete',
      templateUseButton: 'Use Template',
      templateEmptyState: 'No templates yet.',
      templateEmptyStateSubtitle: 'Create one to speed up announcements.'
    },

    // Channel management
    channels: {
      // Headers and titles
      headers: {
        createNewChannel: 'Create New Channel',
        editChannel: 'Edit Channel',
        deleteChannel: 'Delete Channel',
        postToChannel: 'Post to #{name}',
        recentMessages: 'Recent Messages'
      },

      // Form labels
      form: {
        channelName: 'Channel Name',
        description: 'Description',
        channelType: 'Channel Type',
        privateChannel: 'Private Channel',
        defaultChannel: 'Default Channel',
        channelPermissions: 'Channel Permissions',
        adminOnly: 'Admin Only',
        adminOnlyDescription: 'Only admins can see and access this channel',
        membersCanPost: 'Members Can Post',
        membersCanPostDescription: 'Allow regular members to post messages',
        messageType: 'Message Type',
        messageContent: 'Message Content',
        pinMessage: 'Pin Message'
      },

      // Form placeholders
      placeholders: {
        channelNameExample: 'e.g., general, announcements',
        briefDescription: 'Brief description of channel purpose',
        typeYourMessage: 'Type your message here...'
      },

      // Channel types
      types: {
        generalDiscussion: 'General Discussion',
        announcements: 'Announcements',
        tipsAdvice: 'Tips & Advice',
        eventsTournaments: 'Events & Tournaments',
        roundShares: 'Round Shares'
      },

      // Message types
      messageTypes: {
        regularMessage: 'Regular Message',
        officialAnnouncement: 'Official Announcement'
      },

      // Permission labels
      permissions: {
        adminOnly: 'Admin Only',
        readOnly: 'Read Only',
        public: 'Public'
      },

      // Status labels
      status: {
        default: 'Default',
        noMessages: 'No messages',
        members: 'members',
        messages: 'messages',
        lastActivity: 'Last activity: {time}',
        replies: '{count} replies'
      },

      // Actions
      actions: {
        createChannel: 'Create Channel',
        updateChannel: 'Update Channel',
        deleteChannel: 'Delete Channel',
        postMessage: 'Post Message',
        sendMessage: 'Send Message',
        cancel: 'Cancel',
        creating: 'Creating...',
        updating: 'Updating...',
        sending: 'Sending...'
      },

      // Messages and notifications
      messages: {
        failedToLoadChannels: 'Failed to load channels',
        noClubSelected: 'No club selected',
        channelCreatedSuccessfully: 'Channel created successfully',
        failedToCreateChannel: 'Failed to create channel',
        channelUpdatedSuccessfully: 'Channel updated successfully',
        failedToUpdateChannel: 'Failed to update channel',
        channelDeletedSuccessfully: 'Channel deleted successfully',
        failedToDeleteChannel: 'Failed to delete channel',
        adminMessageSentSuccessfully: 'Admin message sent successfully',
        failedToSendMessage: 'Failed to send message',
        channelOrderUpdated: 'Channel order updated',
        deleteConfirmation: 'Are you sure you want to delete the "#{name}" channel? This action cannot be undone and will permanently delete all messages.',
        by: 'by {name}'
      }
    },

    // Cart management
    cartManager: {
      // Headers and main sections
      headers: {
        golfCartManagement: 'Golf Cart Management',
        manageFleetSchedule: 'Manage your fleet schedule and availability',
        cartSchedule: 'Cart Schedule',
        fleetInventory: 'Fleet Inventory',
        fleet: 'Fleet',
        manageCartFleet: 'Manage your golf cart fleet',
        manageYourGolfCartFleet: 'Manage your golf cart fleet',
        activeBookings: 'Active Bookings',
        currentUpcomingReservations: 'Current and upcoming reservations',
        scheduleOverview: 'Schedule Overview',
        cartInventory: 'Cart Inventory',
        schedule: 'Schedule',
        inventory: 'Inventory',
        bookings: 'Bookings',
        pricing: 'Pricing',
        reports: 'Reports'
      },

      // Statistics cards
      statistics: {
        totalCarts: 'Total Carts',
        available: 'Available',
        bookedToday: 'Booked',
        booked: 'Booked',
        maintenance: 'Maintenance'
      },

      // View modes and navigation
      views: {
        dayView: 'Day View',
        weekView: 'Week View',
        day: 'Day',
        week: 'Week',
        swipeToSeeCarts: 'Swipe to see carts',
        swipeToSeeWeek: 'Swipe to see week'
      },

      // Cart status and management
      status: {
        available: 'Available',
        booked: 'Booked',
        maintenance: 'Maintenance',
        outOfService: 'Out of Service',
        unavailable: 'Unavailable',
        allStatus: 'All Status',
        open: 'Open',
        maintenanceShort: 'Maint.',
        notAvailable: 'N/A'
      },

      // Form labels and inputs
      forms: {
        // Cart form
        cartNumber: 'Cart Number',
        model: 'Model',
        location: 'Location',
        status: 'Status',
        notes: 'Notes',
        batteryLevel: 'Battery Level',
        nextService: 'Next Service',
        selectStatus: 'Select status',
        statusControl: 'Status Control',

        // Booking form
        selectCart: 'Select Cart',
        chooseCart: 'Choose a cart',
        date: 'Date',
        players: 'Players',
        startTime: 'Start Time',
        endTime: 'End Time',
        customerName: 'Customer Name',
        email: 'Email',
        phone: 'Phone',
        totalFee: 'Total Fee (€)',
        numberOfPlayers: 'Players',

        // Placeholders
        filterByStatus: 'Filter by status',
        searchCarts: 'Search carts...',
        fullName: 'Full name',
        emailPlaceholder: 'email@example.com',
        phonePlaceholder: '+1-555-0123',
        additionalNotes: 'Additional notes...',
        specialRequests: 'Special requests or notes...',
        modelPlaceholder: 'e.g., Club Car Precedent',
        cartNumberPlaceholder: 'e.g., GC-001',
        locationPlaceholder: 'e.g., Cart Barn A'
      },

      // Dialog titles
      dialogs: {
        editGolfCart: 'Edit Golf Cart',
        addNewGolfCart: 'Add New Golf Cart',
        newCartBooking: 'New Cart Booking',
        bookingDetails: 'Booking Details'
      },

      // Action buttons and controls
      actions: {
        markAvailable: 'Mark Available',
        bookCart: 'Book Cart',
        edit: 'Edit',
        cancel: 'Cancel',
        cancelBooking: 'Cancel',
        updateCart: 'Update Cart',
        addCart: 'Add Cart',
        createBooking: 'Create Booking',
        close: 'Close',
        editBooking: 'Edit Booking',
        maintenance: 'Maintenance'
      },

      // Player options
      playerOptions: {
        onePlayer: '1 Player',
        twoPlayers: '2 Players',
        threePlayers: '3 Players',
        fourPlayers: '4 Players'
      },

      // Status change confirmations and messages
      messages: {
        cartDataExported: 'Cart data exported successfully',
        cartMarkedAvailable: 'Cart marked as available',
        cartMarkedBooked: 'Cart marked as booked',
        cartSentMaintenance: 'Cart sent to maintenance',
        cartMarkedOutOfService: 'Cart marked out of service',
        cartUpdated: 'Cart updated successfully',
        cartAdded: 'Cart added successfully',
        bookingCancelled: 'Booking cancelled successfully',
        bookingCreated: 'Booking created successfully',
        pleaseSelectCart: 'Please select a cart',
        cannotModifyDemoData: 'Cannot modify demo data. Connect to a real database to enable editing.',
        cartsUpdated: '{count} cart{plural} updated',
        cancelBookingConfirmation: 'Are you sure you want to cancel this booking? This action cannot be undone.',
        keepBooking: 'No, keep booking',
        noKeepBooking: 'No, keep booking',
        cartDataExportedSuccessfully: 'Cart data exported successfully',
        bookingCancelledSuccessfully: 'Booking cancelled successfully',
        bookingCreatedSuccessfully: 'Booking created successfully',
        cartsUpdatedPlural: 'carts updated',
        cartUpdatedSingle: 'cart updated'
      },

      // Time and scheduling
      scheduling: {
        timeFormat: 'h:mm a',
        dateFormat: 'EEEE, MMMM d, yyyy'
      },

      // Error states and empty states  
      emptyStates: {
        noCartData: 'No cart data available',
        noBookings: 'No bookings found',
        allCaughtUp: 'All caught up!',
        demoDataBadge: 'Demo Data',
        demoDataNotice: 'Showing sample data. Add real carts in your database to see live data.'
      },

      labels: {
        cart: 'Cart',
        status: 'Status',
        customer: 'Customer',
        date: 'Date',
        time: 'Time',
        players: 'Players',
        totalFee: 'Total Fee',
        contactInformation: 'Contact Information',
        notes: 'Notes',
        bookedBy: 'Booked By',
        cancelBooking: 'Cancel Booking',
        confirmCancelBooking: 'Yes, cancel booking',
        more: 'more'
      },

      pricing: {
        rentalRates: 'Rental Rates',
        configurePricingSubtitle: 'Configure pricing for different rental durations',
        addRate: 'Add Rate',
        noRatesConfigured: 'No rental rates configured',
        addFirstRate: 'Add your first rate to get started',
        deleteRate: 'Delete Rate',
        deleteRateConfirm: 'Are you sure you want to delete "{{name}}"?',
        dynamicPricingRules: 'Dynamic Pricing Rules',
        adjustPricesSubtitle: 'Adjust prices based on time, day, or demand',
        addRule: 'Add Rule',
        noRulesConfigured: 'No pricing rules configured',
        addRulesHint: 'Add rules for peak hours, weekends, etc.',
        deleteRule: 'Delete Rule',
        deleteRuleConfirm: 'Are you sure you want to delete "{{name}}"?',
        memberPricingTiers: 'Member Pricing Tiers',
        configureTiersSubtitle: 'Configure discounts by membership level',
        enabled: 'Enabled',
        disabled: 'Disabled',
        offRentalPrice: 'off rental price',
        enableToConfigure: 'Enable to configure membership tier discounts',
        durationType: 'Duration Type',
        memberPrice: 'Member Price (€)',
        guestPrice: 'Guest Price (€)',
        editRate: 'Edit Rate',
        addRateTitle: 'Add Rate',
        rateName: 'Rate Name',
        cancel: 'Cancel',
        delete: 'Delete',
        saveChanges: 'Save Changes',
        updateRate: 'Update Rate',
        createRate: 'Create Rate',
        editRule: 'Edit Rule',
        addRuleTitle: 'Add Rule',
        ruleName: 'Rule Name',
        appliesTo: 'Applies To',
        memberOnly: 'Member Only',
        guestOnly: 'Guest Only',
        allCustomers: 'All Customers',
        duration9Holes: '9 Holes',
        duration18Holes: '18 Holes',
        durationHourly: 'Hourly',
        durationFullDay: 'Full Day',
        min: 'min',
        days: 'Days',
        startTime: 'Start Time',
        endTime: 'End Time',
        adjustment: 'Adjustment',
        type: 'Type',
        percentage: 'Percentage (%)',
        fixedAmountEur: 'Fixed Amount (€)',
        ruleNamePlaceholder: 'e.g., Weekend Peak Hours',
        dayMon: 'Mon',
        dayTue: 'Tue',
        dayWed: 'Wed',
        dayThu: 'Thu',
        dayFri: 'Fri',
        daySat: 'Sat',
        daySun: 'Sun',
        rateIsActive: 'Rate is active',
        editMemberTierDiscount: 'Edit Member Tier Discount',
        updateDiscountFor: 'Update the discount percentage for {tier}',
        discountPercentage: 'Discount Percentage',
        membersReceiveOff: 'Members in this tier will receive {percent}% off the base price.',
        exampleCalculation: 'Example Calculation'
      },
      reports: {
        demoDataMode: 'Demo Data Mode',
        demoDataDesc: "You're viewing sample analytics data for demonstration purposes. Real data will appear once you add carts, rental rates, and process bookings.",
        sampleAnalytics: 'Sample Analytics',
        cartAnalytics: 'Cart Analytics',
        trackUtilizationRevenue: 'Track utilization, revenue, and booking trends',
        last3Months: 'Last 3 Months',
        last6Months: 'Last 6 Months',
        last12Months: 'Last 12 Months',
        yearToDate: 'Year to Date',
        export: 'Export',
        totalRevenue: 'Total Revenue',
        last6MonthsSubtitle: 'Last 6 months',
        utilizationRate: 'Utilization Rate',
        cartsInUse: 'carts in use',
        totalBookings: 'Total Bookings',
        avgRevenuePerDay: 'Avg. Revenue/Day',
        monthlyAverage: 'Monthly average',
        revenueTrend: 'Revenue Trend',
        avgPerMonth: 'Avg: €{{amount}}/month',
        bookingsCount: '{{count}} bookings',
        peakBookingHours: 'Peak Booking Hours',
        rateDistribution: 'Rate Distribution',
        totalRevenue6mo: 'Total Revenue (6mo)',
        topCustomers: 'Top Customers',
      }
    },

    // Course management
    courseManager: {
      // Headers and titles
      headers: {
        addNewCourse: 'Add New Course',
        editCourse: 'Edit Course'
      },

      // Form labels
      form: {
        courseName: 'Course Name',
        address: 'Address',
        description: 'Description',
        holes: 'Holes',
        par: 'Par',
        distance: 'Distance',
        rating: 'Rating',
        slope: 'Slope',
        website: 'Website',
        measurement: 'Measurement',
        gpsLatitude: 'GPS Latitude',
        gpsLongitude: 'GPS Longitude'
      },

      // Form placeholders
      placeholders: {
        courseName: 'e.g., Championship Course',
        address: '123 Golf Course Drive, City, State',
        description: 'Describe the course features, difficulty, etc.'
      },

      // Units
      units: {
        yards: 'Yards',
        meters: 'Meters'
      },

      // Status and states
      status: {
        loadingCourses: 'Loading courses...',
        gpsAvailable: 'GPS Available',
        noGps: 'No GPS',
        noGpsData: 'No GPS Data',
        noCourses: 'No courses yet',
        getStarted: 'Get started by adding your first golf course.',
        unnamedCourse: 'Unnamed Course'
      },

      // Labels
      labels: {
        gps: 'GPS',
        notAvailable: 'N/A'
      },

      // Actions
      actions: {
        saveCourse: 'Save Course',
        updateCourse: 'Update Course',
        addCourse: 'Add Course',
        cancel: 'Cancel',
        adding: 'Adding...',
        updating: 'Updating...',
        useCurrentLocation: 'Use Current Location',
        viewDetails: 'View Details',
        deleteCourse: 'Delete Course',
        addFirstCourse: 'Add Your First Course'
      },
      
      // Validation errors
      validation: {
        nameRequired: 'Course name is required',
        addressRequired: 'Address is required',
        holeCountRange: 'Hole count must be between 1 and 18',
        parGreaterThanZero: 'Par must be greater than 0',
        yardageGreaterThanZero: 'Yardage must be greater than 0'
      },
      
      // Messages and notifications
      messages: {
        validationError: 'Validation Error',
        validationErrorDesc: 'Please fix the errors below before saving.',
        error: 'Error',
        noClubSelected: 'No club selected. Please select a club first.',
        courseUpdated: 'Success',
        courseUpdatedDesc: 'Course updated successfully!',
        updateFailed: 'Failed to update course.',
        courseAdded: 'Course Added',
        courseAddedDesc: 'New course has been added successfully.',
        saveFailed: 'Failed to save course. Please try again.',
        deleteNotAvailable: 'Delete Not Available',
        deleteNotAvailableDesc: 'Course deletion is not yet implemented in the API.',
        toggleNotAvailable: 'Toggle Not Available',
        toggleNotAvailableDesc: 'Course status toggle is not yet implemented in the API.',
        locationUpdated: 'Location Updated',
        locationUpdatedDesc: 'GPS coordinates have been set to your current location.',
        locationError: 'Location Error',
        locationErrorDesc: 'Unable to get your current location. Please enter coordinates manually.',
        locationNotSupported: 'Location Not Supported',
        locationNotSupportedDesc: 'Geolocation is not supported by your browser.',
        deleteConfirmation: 'Are you sure you want to delete this course? This action cannot be undone.'
      },

      // Course detail view
      detail: {
        backToCourses: 'Back to Courses',
        courseDetails: 'Course Details',
        courseName: 'Course Name',
        address: 'Address',
        description: 'Description',
        totalPar: 'Total Par',
        calculatedFromHoles: 'Calculated from all holes',
        fromHoles: 'From {count} holes',
        holeCount: 'Hole Count',
        nineHoles: '9 Holes',
        eighteenHoles: '18 Holes',
        measurementUnit: 'Measurement Unit',
        website: 'Website',
        telephone: 'Telephone',
        postalCode: 'Postal Code',
        gpsLatitude: 'GPS Latitude',
        gpsLongitude: 'GPS Longitude',
        useCurrentLocation: 'Use Current Location',
        saveCourseDetails: 'Save Course Details',
        locationUpdated: 'Location Updated',
        locationUpdatedDesc: 'GPS coordinates have been updated with your current location.',
        locationError: 'Location Error',
        locationErrorDesc: 'Could not get your current location. Please check permissions.',
        locationNotSupported: 'Location Not Supported',
        locationNotSupportedDesc: 'Geolocation is not supported by your browser.',
        courseDetailsUpdated: 'Course Details Updated',
        courseDetailsUpdatedDesc: 'The course details have been updated successfully.',
        teeAdded: 'Tee Added',
        teeAddedDesc: 'The tee has been added successfully.',
        teeUpdated: 'Tee Updated',
        teeUpdatedDesc: 'The tee has been updated successfully.',
        teeDeleted: 'Tee Deleted',
        teeDeletedDesc: 'The tee has been deleted successfully.',
        holeUpdated: 'Hole Updated',
        holeUpdatedDesc: 'The hole details and yardages have been updated successfully.',
        holeCreated: 'Hole Created',
        holeCreatedDesc: 'The hole has been created successfully.',
        gpsCoordinatesUpdated: 'GPS Coordinates Updated',
        gpsCoordinatesUpdatedDesc: 'GPS coordinates for hole {holeNumber} have been saved.',
        manage: 'Manage'
      },

      // Tee Manager
      teeManager: {
        title: 'Course Tees',
        addTee: 'Add Tee',
        teeName: 'Tee Name',
        teeColor: 'Tee Color',
        selectColor: 'Select a color...',
        courseRatingMen: 'Course Rating (Men)',
        slopeRatingMen: 'Slope Rating (Men)',
        courseRatingWomen: 'Course Rating (Women)',
        slopeRatingWomen: 'Slope Rating (Women)',
        updateTee: 'Update Tee',
        cancel: 'Cancel',
        noTeesYet: 'No tees configured yet.',
        addTeeToStart: 'Add at least one tee to configure hole yardages.',
        teeNameHeader: 'Tee Name',
        colorHeader: 'Color',
        ratingMHeader: 'Rating (M)',
        slopeMHeader: 'Slope (M)',
        ratingWHeader: 'Rating (W)',
        slopeWHeader: 'Slope (W)',
        totalYardageHeader: 'Total Yardage',
        actionsHeader: 'Actions',
        yds: 'yds',
        notConfigured: 'Not configured',
        deleteConfirm: 'Are you sure you want to delete this tee? All yardages for this tee will also be deleted.',
        // Tee colors
        blackChampionship: 'Black (Championship)',
        blueMen: 'Blue (Men)',
        whiteMen: 'White (Men)',
        yellowSenior: 'Yellow (Senior Men)',
        redWomen: 'Red (Women)',
        greenJunior: 'Green (Junior)',
        gold: 'Gold',
        silver: 'Silver'
      },

      // Hole Manager
      holeManager: {
        title: 'Hole Details & GPS',
        holesCount: '{count} holes',
        holeHeader: 'Hole',
        parHeader: 'Par',
        siMenHeader: 'SI (M)',
        siWomenHeader: 'SI (W)',
        gpsHeader: 'GPS',
        actionsHeader: 'Actions',
        collapse: 'Collapse',
        expand: 'Expand',
        holeDetails: 'Hole {number} Details',
        par: 'Par',
        strokeIndexMen: 'Stroke Index (Men)',
        strokeIndexWomen: 'Stroke Index (Women)',
        yardagesPerTee: 'Yardages per Tee',
        gpsCoordinates: 'GPS Coordinates',
        addTee: '+ Add Tee',
        addGreen: '+ Add Green',
        noGpsCoordinates: 'No GPS coordinates set for this hole.',
        clickToStart: 'Click "Add Tee" or "Add Green" to start.',
        type: 'Type',
        latitude: 'Latitude',
        longitude: 'Longitude',
        frontTee: 'Front Tee',
        backTee: 'Back Tee',
        green: 'Green',
        saveHoleDetails: 'Save Hole Details',
        saveGps: 'Save GPS',
        nextHole: 'Next Hole →',
        noTeesConfigured: 'No Tees Configured',
        addTeesFirst: 'Please add at least one tee before configuring holes.',
        noGps: 'No GPS',
        partial: 'Partial',
        complete: 'Complete'
      },

      // GPS Modal
      gpsModal: {
        title: 'GPS Coordinates - {courseName}',
        unsavedChanges: 'Unsaved Changes',
        hole: 'Hole:',
        holeNumber: 'Hole {number}',
        addCoordinate: 'Add Coordinate',
        holeCoordinates: 'Hole {number} Coordinates',
        noCoordinatesYet: 'No coordinates added for this hole yet.',
        poiType: 'POI Type',
        location: 'Location',
        side: 'Side',
        latitude: 'Latitude',
        longitude: 'Longitude',
        tees: 'Tees',
        greens: 'Greens',
        markers: 'Markers',
        totalPoints: 'Total Points',
        import: 'Import',
        export: 'Export',
        undoAllChanges: 'Undo All Changes',
        saveCoordinates: 'Save Coordinates',
        unsavedWarning: 'You have unsaved changes that will be lost if you close this window. What would you like to do?',
        keepEditing: 'Keep Editing',
        discardChanges: 'Discard Changes',
        // POI Types
        green: 'Green',
        water: 'Water',
        trees: 'Trees',
        dogleg: 'Dogleg',
        frontTee: 'Front Tee',
        backTee: 'Back Tee',
        fairwayBunker: 'Fairway Bunker',
        // Location options
        greenFront: 'Front',
        greenMiddle: 'Middle',
        greenBack: 'Back',
        // Fairway sides
        left: 'Left',
        center: 'Center',
        right: 'Right',
        gpsUpdatedSuccess: 'The course GPS coordinates have been saved successfully.',
        cancel: 'Cancel',
        useCurrentLocation: 'Use current location',
        saveAndClose: 'Save & Close'
      }
    },

    bookingManager: {
      // Statistics card labels
      statistics: {
        todaysBookings: 'Today\'s Bookings',
        availableSlots: 'Available Slots', 
        revenueMTD: 'Revenue (MTD)',
        utilization: 'Utilization'
      },

      // Tab titles
      tabs: {
        schedule: 'Schedule',
        allBookings: 'All Bookings',
        bookingRules: 'Booking Rules',
        analytics: 'Analytics'
      },

      // Headers and titles
      headers: {
        selectDateCourse: 'Select Date & Course',
        teeTimes: 'Tee Times - {date}',
        searchFilter: 'Search and Filter',
        bookingRulesByTier: 'Booking Rules by Membership Tier',
        peakBookingTimes: 'Peak Booking Times',
        bookingTrends: 'Booking Trends',
        revenueByTier: 'Revenue by Membership Tier',
        createBooking: 'Create New Booking',
        editBooking: 'Edit Booking'
      },

      // Form labels and placeholders
      form: {
        course: 'Course',
        date: 'Date',
        time: 'Time',
        memberName: 'Member Name',
        membershipTier: 'Membership Tier',
        email: 'Email',
        phone: 'Phone',
        bookingType: 'Booking Type',
        additionalPlayers: 'Additional Players',
        totalFee: 'Total Fee',
        notes: 'Notes',
        searchBookings: 'Search bookings...',
        playerName: 'Player name',
        handicap: 'Handicap',
        notesPlaceholder: 'Additional notes or special requests...'
      },

      // Button and action text
      actions: {
        refresh: 'Refresh',
        filters: 'Filters',
        addPlayer: 'Add Player',
        cancel: 'Cancel',
        createBooking: 'Create Booking',
        updateBooking: 'Update Booking',
        creating: 'Creating...',
        updating: 'Updating...',
        deleteBooking: 'Delete Booking'
      },

      // Status and badges
      status: {
        available: 'Available',
        confirmed: 'confirmed',
        pending: 'pending',
        cancelled: 'cancelled',
        completed: 'completed',
        noShow: 'no-show',
        group: 'Group',
        guestPlayer: 'Guest Player'
      },

      // Booking types
      bookingTypes: {
        individual: 'Individual',
        group: 'Group',
        tournament: 'Tournament'
      },

      // Membership tiers
      membershipTiers: {
        bronze: 'Bronze',
        silver: 'Silver', 
        gold: 'Gold',
        platinum: 'Platinum'
      },

      // Time slot information
      timeSlots: {
        playersCount: '{count}/{max} players'
      },

      // Booking rules labels
      rules: {
        advanceBooking: 'Advance Booking',
        weeklyLimit: 'Weekly Limit',
        cancellation: 'Cancellation',
        guestsAllowed: 'Guests Allowed',
        days: '{count} days',
        bookings: '{count} bookings', 
        hours: '{count} hours',
        guests: '{count} guests',
        priorityAccess: 'Priority Access',
        bronzeMember: 'Bronze Member',
        silverMember: 'Silver Member',
        goldMember: 'Gold Member',
        platinumMember: 'Platinum Member'
      },

      // Analytics labels
      analytics: {
        peakTimes: {
          morning1: '7:00 - 9:00 AM',
          morning2: '9:00 - 11:00 AM',
          midday: '11:00 AM - 1:00 PM',
          afternoon1: '1:00 - 3:00 PM',
          afternoon2: '3:00 - 5:00 PM',
          evening: '5:00 - 7:00 PM'
        },
        trends: {
          averageDailyBookings: 'Average Daily Bookings',
          weekendUtilization: 'Weekend Utilization',
          weekdayUtilization: 'Weekday Utilization', 
          cancellationRate: 'Cancellation Rate',
          noShowRate: 'No-Show Rate'
        }
      },

      // Toast messages
      messages: {
        exportingBookings: 'Exporting bookings...',
        teeTimeBooked: 'Tee time booked successfully',
        failedToCreate: 'Failed to create booking',
        bookingUpdated: 'Booking updated successfully',
        failedToUpdate: 'Failed to update booking',
        bookingCancelled: 'Booking cancelled successfully',
        failedToCancel: 'Failed to cancel booking',
        bookingDeleted: 'Booking deleted successfully',
        failedToDelete: 'Failed to delete booking'
      },

      // Dialog content
      dialogs: {
        deleteConfirmation: 'Are you sure you want to delete this booking for {memberName}? This action cannot be undone.',
        playerDetails: 'Players:',
        notesLabel: 'Notes:'
      },

      // Course info display
      courseInfo: {
        holes: '{count} holes',
        maxPlayers: 'Max {count} players',
        bookingWindow: '{count} day booking window'
      }
    },

    analytics: {
      // Key metrics
      metrics: {
        totalMembers: 'Total Members',
        activeMembers: 'Active Members',
        totalRounds: 'Total Rounds',
        revenueMTD: 'Revenue (MTD)',
        courseUtilization: 'Course Utilization',
        engagementScore: 'Engagement Score',
        vsLastPeriod: '{change}% vs last period'
      },

      // Tab labels
      tabs: {
        overview: 'Overview',
        members: 'Members',
        courses: 'Courses',
        engagement: 'Engagement',
        financial: 'Financial'
      },

      // Chart titles and sections
      charts: {
        membershipTiers: 'Membership Tiers',
        performanceInsights: 'Performance Insights',
        memberGrowthMetrics: 'Member Growth Metrics',
        memberDemographics: 'Member Demographics',
        courseUsageAnalytics: 'Course Usage Analytics',
        peakUsageHours: 'Peak Usage Hours',
        courseMaintenance: 'Course Maintenance',
        communicationMetrics: 'Communication Metrics',
        channelActivity: 'Channel Activity',
        revenueTrend: 'Revenue Trend Analysis',
        revenueBreakdown: 'Revenue Breakdown',
        paymentStatus: 'Payment Status',
        generatedReports: 'Generated Reports'
      },

      // Performance insights
      insights: {
        averageHandicap: 'Average Handicap',
        mostActiveCourse: 'Most Active Course',
        peakUsageTime: 'Peak Usage Time',
        memberSatisfaction: 'Member Satisfaction',
        staffResponseTime: 'Staff Response Time',
        mainCourse: 'Main Course'
      },

      // Member metrics
      members: {
        newMembers: 'New Members (30d)',
        renewals: 'Renewals (30d)',
        cancellations: 'Cancellations (30d)',
        retentionRate: 'Retention Rate',
        growing: 'Growing',
        declining: 'Declining',
        totalActiveMembers: 'Total Active Members',
        members: 'members'
      },

      // Age demographics
      demographics: {
        age18to35: 'Age 18-35',
        age36to50: 'Age 36-50',
        age51to65: 'Age 51-65',
        age65plus: 'Age 65+'
      },

      // Course data
      courses: {
        mainCourse: 'Main Course',
        executiveCourse: 'Executive Course',
        practiceRange: 'Practice Range',
        puttingGreen: 'Putting Green',
        roundsPlayed: 'rounds played',
        courseCondition: 'Course Condition',
        excellent: 'Excellent',
        lastMaintenance: 'Last Maintenance',
        nextScheduled: 'Next Scheduled',
        maintenanceCost: 'Maintenance Cost',
        daysAgo: '{count} days ago',
        inDays: 'In {count} days'
      },

      // Time slots
      timeSlots: {
        morning1: '7:00 - 9:00 AM',
        morning2: '9:00 - 11:00 AM',
        midday: '11:00 AM - 1:00 PM',
        afternoon1: '1:00 - 3:00 PM',
        afternoon2: '3:00 - 5:00 PM',
        evening: '5:00 - 7:00 PM',
        usage: 'usage',
        intensity: {
          highest: 'highest',
          high: 'high',
          medium: 'medium',
          low: 'low',
          lowest: 'lowest'
        }
      },

      // Engagement data
      engagement: {
        messagesSent: 'Messages Sent',
        announcements: 'Announcements',
        eventParticipation: 'Event Participation',
        appUsage: 'App Usage (hrs)',
        general: 'General',
        announcementsChannel: 'Announcements',
        tournamentUpdates: 'Tournament Updates',
        proShop: 'Pro Shop',
        totalMessages: 'Total Messages',
        messages: 'messages'
      },

      // Financial metrics
      monthlyRevenue: 'Monthly Revenue',
      annualRevenue: 'Annual Revenue',
      avgRevenueMember: 'Avg. Revenue/Member',
      collectionRate: 'Collection Rate',
      membershipFees: 'Membership Fees',
      greenFees: 'Green Fees',
      events: 'Events',
      paidOnTime: 'Paid On Time',
      overdue1to30: 'Overdue 1-30 days',
      overdue30plus: 'Overdue 30+ days',
      totalOutstanding: 'Total Outstanding',

      // Report types and actions
      reports: {
        memberReport: 'Member Report',
        financialReport: 'Financial Report',
        download: 'Download',
        generating: 'Generating...',
        reportGenerated: '{type} report generated successfully',
        reportFailed: 'Failed to generate report',
        downloading: 'Downloading {title}...',
        member: 'member',
        course: 'course',
        communication: 'communication',
        financial: 'financial',
        ready: 'ready',
        failed: 'failed'
      },

      // Status messages
      status: {
        filtersApplied: 'Filters applied'
      }
    },
    quickActions: {
      sectionTitle: 'Quick Actions',
      sectionSubtitle: 'Common administrative tasks and shortcuts',
      postAnnouncement: 'Post Announcement',
      postAnnouncementDesc: 'Share important updates with all members',
      manageMembers: 'Manage Members',
      manageMembersDesc: 'View, add, or modify member details',
      addCourse: 'Add Course',
      addCourseDesc: 'Register a new golf course',
      viewAnalytics: 'View Analytics',
      viewAnalyticsDesc: 'Check detailed club performance metrics',
      manageCommunication: 'Manage Communication',
      manageCommunicationDesc: 'Manage communications and messages',
      clubSettings: 'Club Settings',
      clubSettingsDesc: 'Update club profile and preferences'
    },
    pendingMembers: {
      filterAll: 'All',
      filterNotInvited: 'To invite',
      filterUrgent: 'Urgent (21+ d)',
      filterFollowUp: 'Follow-up (7+ d)',
      filterWaiting: 'Waiting',
      filterRecent: 'Recent',
      inviteAll: 'Invite all',
      urgentBtn: 'Urgent',
      followUpBtn: 'Follow up',
      export: 'Export',
      filters: 'Filters',
      reset: 'Reset',
      invitationStatus: 'Invitation status',
      searchPlaceholder: 'Search by name or email...',
      emptyStateTitle: 'No recent invitations',
      emptyStateDesc: 'Members invited by email in the last 30 days will appear here.',
      emptyStateNote: 'Only members who went through the invitation process are tracked here.',
      invitationTracking: 'Invitation tracking',
      last30Days: '30d',
      kpiInvited: 'Invited',
      kpiPending: 'Pending',
      kpiConversion: 'Conversion',
      progressJoined: 'Joined',
      progressWaiting: 'Waiting',
      progressFollowUp: 'Follow-up needed',
      successRate: 'Success rate',
      statusGoalMet: 'Goal met',
      statusOnTrack: 'On track',
      statusNeedsWork: 'Needs improvement',
      selectAtLeastOneMember: 'Please select at least one member',
      invitationsSentSuccess: 'invitation(s) sent successfully',
      errorSendingInvitations: 'Error sending invitations',
      allMembersAlreadyInvited: 'All members have already been invited',
      memberNotFound: 'Member not found',
      invitationResentSuccess: 'Invitation resent successfully',
      errorResending: 'Error resending',
      confirmRemoveMember: 'Are you sure you want to remove the invitation for',
      actionIrreversible: 'This action is irreversible.',
      removedSuccess: 'has been removed successfully',
      errorRemoving: 'Error removing member',
      confirmBulkRemove: 'Remove',
      membersCount: 'member(s)',
      andOthers: 'and',
      others: 'others',
      membersRemovedSuccess: 'member(s) removed successfully',
      errorRemovingCount: 'Error removing',
      errorBulkRemove: 'Error during bulk removal',
      exportComplete: 'Export complete',
      csvFirstName: 'First Name',
      csvLastName: 'Last Name',
      csvEmail: 'Email',
      csvPhone: 'Phone',
      csvDateAdded: 'Date Added',
      csvInviteStatus: 'Invitation Status',
      csvInviteDate: 'Invitation Date',
      csvExpiry: 'Expiry Date',
      csvImportSource: 'Import Source',
      csvInvited: 'Invited',
      csvNotInvited: 'Not invited',
      sendEmail: 'Send email',
      emailRecipient: 'Recipient',
      emailSubject: 'Subject',
      emailMessage: 'Message',
      sending: 'Sending...',
      send: 'Send',
      message: 'Message',
      directMessage: 'Direct message',
      openingChat: 'Opening chat...',
      cannotOpenChat: 'Unable to open chat'
    },
    reportsEngagement: {
      activeRate: 'Active rate',
      avgRoundsPerMember: 'Avg rounds / member',
      revenueAtRisk: 'Revenue at risk',
      goingQuiet: 'Going quiet',
      unableToLoad: 'Unable to load engagement data.',
      activityTrend: 'Activity trend',
      noRevenueAtRisk: 'No revenue at risk',
      searchPlaceholder: 'Search members by name or email…',
      allMembers: 'All members',
      clearFilter: 'Clear filter',
      periodWeek: 'Week',
      periodMonth: 'Month',
      periodYear: 'Year',
      sendEmail: 'Send email',
      cancel: 'Cancel',
      sending: 'Sending…',
      send: 'Send',
      template: 'Template',
      to: 'To',
      subject: 'Subject',
      message: 'Message',
      openingConversation: 'Opening conversation…',
      noMembersFound: 'No members found',
      colMember: 'Member',
      colTier: 'Tier',
      colLastActive: 'Last active',
      colInactive: 'Inactive',
      colStatus: 'Status',
      colActions: 'Actions',
      onboardingPipeline: 'Onboarding Pipeline',
      membersCurrentlyInStage: 'Members currently in each stage',
      progressionFunnel: 'Progression Funnel',
      tapLeagueHint: 'Tap a league to see its members',
      activityDistribution: 'Activity Distribution',
      thisMonth: 'This month',

      // RevenueAtRiskCard
      membersAtRisk: 'Members at Risk',
      viewAll: 'View All',
      byType: 'By Type',

      // StaffActionDashboard
      priorityActions: 'Priority Actions',
      actionFollowUp: 'Follow-up needed',
      actionFollowUpDesc: 'Inactive 7+ days',
      actionContactNow: 'Contact Now',
      actionTierEval: 'Tier evaluations',
      actionTierEvalDesc: 'At risk or slowing',
      actionReview: 'Review',
      actionCoachingNeeded: 'Coaching needed',
      actionCoachingNeededDesc: 'Stuck on stage 14+ days',
      actionAssignCoach: 'Assign Coach',
      actionReadyToPromote: 'Ready to promote',
      actionReadyToPromoteDesc: 'Completed requirements',
      actionPromote: 'Promote',

      // STATUS_CONFIG labels
      statusActive: 'Active',
      statusSlowing: 'Slowing',
      statusAtRisk: 'At risk',

      // EMAIL_TEMPLATES
      templateMissYou: 'We miss you',
      templateFreeNineHoles: 'Free 9 holes',
      templateCoachIntro: 'Coach intro',
    },

    // Admin Management page
    adminManagement: {
      title: 'Admin Management',
      loading: 'Loading admin information...',
      totalAdmins: 'Total Admins',
      registered: 'Registered',
      pending: 'Pending',
      owners: 'Owners',
      inviteDialogTitle: 'Invite New Admin',
      inviteDialogDescription: "Add a new administrator to your club. They'll receive an email invitation.",
      emailAddressLabel: 'Email Address',
      roleLabel: 'Role',
      roleAdminOption: 'Admin - Full administrative access',
      roleCoachOption: 'Coach - Limited administrative access',
      sendingInvite: 'Sending Invite...',
      sendInvitation: 'Send Invitation',
      adminInvitedTitle: 'Admin Invited',
      adminInvitedDescription: '{email} has been added and invitation email sent.',
      adminRemovedTitle: 'Admin Removed',
      adminRemovedDescription: '{email} has been removed from admin list',
      roleUpdatedTitle: 'Role Updated',
      roleUpdatedDescription: 'Admin role has been updated to {role}',
      removeAccessTitle: 'Remove Admin Access',
      removeAccessConfirm: 'Are you sure you want to remove admin access for {email}? This action cannot be undone.',
      removeAccess: 'Remove Access',
      noAdminsTitle: 'No administrators found',
      noAdminsDescription: 'Invite administrators to help manage your club',
      permissionLevels: 'Permission Levels',
      roleOwner: 'Owner',
      roleOwnerDescription: 'Full control',
      roleAdmin: 'Admin',
      roleAdminDescription: 'Full access',
      roleCoach: 'Coach',
      roleCoachDescription: 'Limited access',
    },

    // Admin Member Selector
    adminMemberSelector: {
      selectMemberToRegister: 'Select Member to Register',
      allMembers: 'All Members',
      activeOnly: 'Active Only',
      pendingOnly: 'Pending Only',
      allTiers: 'All Tiers',
      advancedFilters: 'Advanced Filters',
      membersAvailable: 'members available',
      memberAvailable: 'member available',
      handicapRange: 'Handicap Range',
      allHandicaps: 'All Handicaps',
      lowHandicap: 'Low (0-10)',
      midHandicap: 'Mid (11-20)',
      highHandicap: 'High (21+)',
      noHandicap: 'No Handicap',
      activityLevel: 'Activity Level',
      allActivityLevels: 'All Activity Levels',
      recentlyActive: 'Recently Active',
      frequentPlayers: 'Frequent Players',
      occasionalPlayers: 'Occasional Players',
      newPlayers: 'New Players',
      inactive: 'Inactive',
      noAvailableMembers: 'No available members',
      noMembersMatchSearch: 'No members match your search criteria',
      allMembersRegistered: 'All eligible members are already registered',
      noMembersInClub: 'No members found in this club',
      tier: 'Tier',
      roundsPlayed: 'rounds played',
      selectMemberHint: 'Select a member to register them for this event',
    },

    // Member Recommendations
    memberRecommendations: {
      title: 'Personalized Recommendations',
      subtitle: 'AI-generated suggestions to improve member engagement',
      generateNew: 'Generate New',
      filtersLabel: 'Filters:',
      allCategories: 'All Categories',
      categoryGolf: 'Golf',
      categorySocial: 'Social',
      categoryCommunication: 'Communication',
      categoryCommunity: 'Community',
      categoryGeneral: 'General',
      allPriorities: 'All Priorities',
      priorityHigh: 'High Priority',
      priorityMedium: 'Medium Priority',
      priorityLow: 'Low Priority',
      recommendationsCount: 'recommendations',
      impact: 'Impact',
      effort: 'effort',
      whyThisRecommendation: 'Why this recommendation?',
      actionItems: 'Action Items',
      successMetrics: 'Success Metrics',
      relatedDimensions: 'Related dimensions:',
      implement: 'Implement',
      schedule: 'Schedule',
      dismiss: 'Dismiss',
      noRecommendationsTitle: 'No recommendations found',
      tryAdjustingFilters: 'Try adjusting your filters to see more recommendations.',
      excellentEngagement: 'This member has excellent engagement across all dimensions!',
      generateNewRecommendations: 'Generate New Recommendations',
    },

    // Bulk Member Selector
    bulkMemberSelector: {
      bulkMemberRegistration: 'Bulk Member Registration',
      searchMembersByNameOrEmail: 'Search members by name or email...',
      allStatus: 'All Status',
      activeOnly: 'Active Only',
      pendingOnly: 'Pending Only',
      allTiers: 'All Tiers',
      xOfYSelected: '{selected} of {total} selected',
      deselectAll: 'Deselect All',
      selectAll: 'Select All',
      noAvailableMembers: 'No available members',
      noMembersMatchSearch: 'No members match your search criteria',
      allMembersRegistered: 'All eligible members are already registered',
      noMembersInClub: 'No members found in this club',
      tier: 'Tier',
      roundsPlayed: '{count} rounds played',
      suspended: 'suspended',
      inactive: 'inactive',
      membersSelectedForRegistration: '{count} member(s) selected for registration',
      selectMembersToRegister: 'Select members to register for this event',
      continueWithCount: 'Continue ({count})',
    },

    memberSelector: {
      allMembers: 'All Members',
      activeOnly: 'Active Only',
      pendingOnly: 'Pending Only',
      allTiers: 'All Tiers',
      allHandicaps: 'All Handicaps',
      lowHandicap: 'Low (≤10)',
      midHandicap: 'Mid (11-20)',
      highHandicap: 'High (>20)',
      noHandicap: 'No Handicap',
      allActivityLevels: 'All Activity Levels',
      recentlyActive: 'Recently Active (30 days)',
      frequentPlayers: 'Frequent Players (20+ rounds)',
      occasionalPlayers: 'Occasional Players (5-19 rounds)',
      newPlayers: 'New Players (<5 rounds)',
      inactive: 'Inactive (90+ days)',
      handicap: 'Handicap',
      tier: 'Tier',
      selectMemberToRegister: 'Select Member to Register',
      advancedFilters: 'Advanced Filters',
      membersAvailable: 'members available',
      memberAvailable: 'member available',
      handicapRange: 'Handicap Range',
      activityLevel: 'Activity Level',
      noAvailableMembers: 'No available members',
      noMembersMatchSearch: 'No members match your search criteria',
      allMembersRegistered: 'All eligible members are already registered',
      noMembersInClub: 'No members found in this club',
      roundsPlayed: 'rounds played',
      selectMemberHint: 'Select a member to register them for this event',
      bulkMemberRegistration: 'Bulk Member Registration',
      allStatus: 'All Status',
      xOfYSelected: '{selected} of {total} selected',
      selectAll: 'Select All',
      deselectAll: 'Deselect All',
      suspended: 'suspended',
      membersSelectedForRegistration: '{count} member(s) selected for registration',
      selectMembersToRegister: 'Select members to register for this event',
      continueWithCount: 'Continue ({count})',
      searchMembersByNameOrEmail: 'Search members by name or email...',
    },
    bulkRegistration: {
      // Results screen
      registrationComplete: 'Bulk Registration Complete',
      event: 'Event',
      successful: 'Successful',
      waitlisted: 'Waitlisted',
      successfullyRegisteredCount: 'Successfully Registered ({count})',
      failedRegistrationsCount: 'Failed Registrations ({count})',
      waitlistPosition: 'Waitlist',
      completeRegistration: 'Complete Registration',
      // Form screen
      bulkRegisterCount: 'Bulk Register {count} Members',
      selectedMembersCount: 'Selected Members ({count})',
      registeringMembersProgress: 'Registering members... ({current}/{total})',
      registeringMember: 'Registering {name}...',
      bulkRegistrationNotesOptional: 'Bulk Registration Notes (Optional)',
      addNotesPlaceholder: 'Add notes that will apply to all registrations...',
      charactersCount: '{count}/500 characters',
      sendEmailNotifications: 'Send Email Notifications',
      membersWillReceiveEmails: 'All {count} members will receive confirmation emails',
      registerMembersCount: 'Register {count} Members',
      // Information box
      bulkRegistrationInfo: 'Bulk Registration Information',
      infoProcessedOneByOne: 'Members will be processed one at a time to ensure reliability',
      infoWaitlistIfFull: 'If the event becomes full, remaining members will be added to the waitlist',
      infoSameNotifications: 'All members will receive the same notifications as individual registrations',
      infoFailedMarked: 'Failed registrations will be clearly marked with error details',
      // Toast messages
      toastAllSuccess: 'Successfully registered all {count} members!',
      toastAllFailed: 'Failed to register all {count} members',
      toastPartialSuccess: 'Registered {success} members, {failed} failed',
    },
    adminRegistration: {
      // Header
      registerMemberForEvent: 'Register Member for Event',
      event: 'Event',
      selectedMember: 'Selected Member',
      // Status
      suspended: 'suspended',
      inactive: 'inactive',
      // Member info
      tierMember: '{tier} Member',
      roundsPlayed: '{count} rounds played',
      // Form
      registrationNotesOptional: 'Registration Notes (Optional)',
      addNotesPlaceholder: 'Add any notes...',
      charactersCount: '{count}/500 characters',
      sendEmailNotification: 'Send Email Notification',
      memberWillReceiveEmail: 'Member will receive confirmation email with event details',
      // Buttons
      registerMember: 'Register Member',
      registering: 'Registering...',
      // Information box
      registrationInfo: 'Registration Information',
      infoWaitlistIfFull: 'If the event is full, the member will be automatically added to the waitlist',
      infoSameNotifications: 'The member will receive the same notifications as self-registered participants',
      infoFollowsRules: 'Registration follows all existing rules and restrictions',
      // Toast messages
      toastConfirmed: '{name} successfully registered for the event!',
      toastWaitlist: '{name} added to waitlist (position {position})!',
      toastSuccess: '{name} registered successfully!',
      toastNoNotification: 'Member will not receive email notification (as requested)',
      toastFailed: 'Failed to register member',
      toastError: 'An unexpected error occurred while registering the member',
    },
    memberImport: {
      // Dialog
      importMembers: 'Import Members',
      importDescription: 'Upload and import member data from Excel or CSV files to automatically create club member accounts.',
      importProgress: 'Import Progress',
      // Status
      inProgress: 'In Progress',
      // Toast messages
      fileUploadFailed: 'File Upload Failed',
      columnMappingConfirmed: 'Column Mapping Confirmed',
      proceedingToImport: 'Proceeding to data import...',
      columnMappingFailed: 'Column Mapping Failed',
      importStarted: 'Import Started',
      memberDataImporting: 'Your member data is being imported...',
      importFailed: 'Import Failed',
      // Step 3 - Ready to Import
      readyToImport: 'Ready to Import',
      fileValidatedClickToImport: 'Your file has been validated and column mappings confirmed. Click below to start importing {count} members.',
      startingImport: 'Starting Import...',
      startImport: 'Start Import',
      // Step 4 - Import Complete
      importComplete: 'Import Complete!',
      successfullyImportedCount: 'Successfully imported {count} members.',
      failedImportsCount: '{count} failed imports.',
      skippedDuplicatesCount: '{count} skipped duplicates.',
      goToMemberManagement: 'Go to Member Management',
      // Action buttons
      processing: 'Processing...',
      // Stepper
      completedAt: 'Completed at',
      rowsValidated: '{count} rows validated',
      sessionCreated: 'Session {id}... created',
      invitationsSent: '{count} invitations sent',
      stepXofY: 'Step {current} of {total}',
      xCompleted: '{count} completed',
      // Step names
      stepUploadFile: 'Upload File',
      stepValidateFile: 'Validate File',
      stepMapColumns: 'Map Columns',
      stepImportData: 'Import Data',
      stepSendInvitations: 'Send Invitations',
      // FileUploadDropzone
      uploadMemberDataFile: 'Upload Member Data File',
      dropFileHere: 'Drop your file here',
      dragAndDropOrBrowse: 'Drag and drop your Excel or CSV file, or click to browse',
      supportsFormats: 'Supports: .xlsx, .xls, .csv (max 50MB)',
      chooseFile: 'Choose File',
      validatingFile: 'Validating file...',
      fileValidatedSuccessfully: 'File validated successfully',
      invalidFileType: 'Invalid file type. Please upload .xlsx, .xls, or .csv files only.',
      fileTooLarge: 'File too large. Maximum size is 50MB.',
      validationError: 'Validation Error',
      expectedFileFormat: 'Expected File Format',
      formatFirstRow: 'First row should contain column headers',
      formatRequiredColumns: 'Required columns: First Name, Last Name',
      formatOptionalColumns: 'Optional: Email, Phone, Membership Number, Handicap',
      formatSupportedTypes: 'Supports Excel (.xlsx, .xls) and CSV (.csv) formats',
      // AccountClaimingForm.tsx
      invalidToken: 'Invalid Token',
      tokenVerificationFailed: 'Token Verification Failed',
      passwordInvalid: 'Password Invalid',
      fixPasswordRequirements: 'Please fix password requirements before continuing',
      passwordsDontMatch: "Passwords Don't Match",
      makeSurePasswordsMatch: 'Please make sure both password fields match',
      accountCreatedSuccess: 'Account Created Successfully',
      welcomeToClub: 'Welcome to the club! You can now access your member portal.',
      accountCreationFailed: 'Account Creation Failed',
      failedToCreateAccount: 'Failed to create account',
      passwordStrong: 'Strong password',
      passwordMedium: 'Medium strength',
      passwordWeak: 'Weak password',
      passwordEnter: 'Enter a password',
      verifyingInvitation: 'Verifying your invitation...',
      invalidOrExpiredInvitation: 'Invalid or Expired Invitation',
      invitationNoLongerValid: 'This invitation link is no longer valid. Please contact your club administrator for a new invitation.',
      completeMembershipSetup: 'Complete Your Membership Setup',
      expiresSoon: 'Expires soon',
      yourMembershipDetails: 'Your Membership Details',
      nameLabel: 'Name',
      emailLabel: 'Email',
      memberNumberLabel: 'Member #',
      firstNameLabel: 'First Name',
      lastNameLabel: 'Last Name',
      emailAddressLabel: 'Email Address',
      phoneNumberLabel: 'Phone Number',
      golfHandicapLabel: 'Golf Handicap',
      createYourPassword: 'Create Your Password',
      passwordLabel: 'Password',
      generatePassword: 'Generate',
      confirmPasswordLabel: 'Confirm Password',
      passwordsDoNotMatch: 'Passwords do not match',
      creatingAccount: 'Creating Account...',
      completeSetup: 'Complete Setup',
      // ImportProgressTracker.tsx
      noImportSessionData: 'No import session data available',
      trackerStarted: 'Started',
      trackerCompleted: 'Completed',
      overallProgress: 'Overall Progress',
      complete: 'complete',
      dataProcessing: 'Data Processing',
      ofRows: 'of {total} rows',
      importedLabel: 'Imported',
      invitedLabel: 'Invited',
      claimedLabel: 'Claimed',
      membersSkipped: '{count} members skipped (duplicates or existing)',
      adoptionMetrics: 'Adoption Metrics',
      adoptionRate: 'Adoption Rate',
      activeLabel: 'Active',
      pendingLabel: 'Pending',
      recentErrors: 'Recent Errors',
      rowLabel: 'Row',
      moreErrors: 'more errors',
      retryImport: 'Retry Import',
      viewDetails: 'View Details',
      sendInvitations: 'Send Invitations',
    },

    // Range Products tab (RangeProductsTab.tsx)
    rangeProducts: {
      demoData: 'Demo Data',
      demoDataDescription: 'Showing sample data. Add real products in your database to see live data.',
      bucketProducts: 'Bucket Products',
      configureBallBucketSizes: 'Configure ball bucket sizes and pricing',
      addProduct: 'Add Product',
      editProduct: 'Edit Product',
      addNewProduct: 'Add New Product',
      productName: 'Product Name',
      numberOfBalls: 'Number of Balls',
      basePriceEur: 'Base Price (€)',
      memberPriceEur: 'Member Price (€)',
      guestPriceEur: 'Guest Price (€)',
      active: 'Active',
      cancel: 'Cancel',
      saveChanges: 'Save Changes',
      dynamicPricingRules: 'Dynamic Pricing Rules',
      adjustPricesBasedOnTime: 'Adjust prices based on time, day, or season',
      addRule: 'Add Rule',
      editPricingRule: 'Edit Pricing Rule',
      addPricingRule: 'Add Pricing Rule',
      ruleName: 'Rule Name',
      days: 'Days',
      startTime: 'Start Time',
      endTime: 'End Time',
      adjustment: 'Adjustment',
      adjustmentType: 'Type',
      percentage: 'Percentage (%)',
      fixedAmountEur: 'Fixed Amount (€)',
      memberPricingTiers: 'Member Pricing Tiers',
      configureDiscountsByLevel: 'Configure discounts by membership level',
      enabled: 'Enabled',
      disabled: 'Disabled',
      enableToConfigure: 'Enable to configure membership tier discounts',
      editMemberTierDiscount: 'Edit Member Tier Discount',
      updateDiscountFor: 'Update the discount percentage for {tier}. This discount will apply to all base prices for members in this tier.',
      discountPercentage: 'Discount Percentage',
      membersReceiveOff: 'Members in this tier will receive {percent}% off the base price of all products.',
      exampleCalculation: 'Example Calculation',
      product: 'Product',
      balls: 'Balls',
      ballsLabel: 'balls',
      base: 'Base',
      member: 'Member',
      actions: 'Actions',
      offBasePrice: 'off base price',
      tierDiscountUpdated: 'Tier discount updated',
      failedToSaveTierDiscount: 'Failed to save tier discount',
      tierDiscountsInitialized: 'Member tier discounts initialized',
      failedToInitializeTierDiscounts: 'Failed to initialize tier discounts',
    },

    // Range Reports tab (RangeReportsTab.tsx)
    rangeReports: {
      demoData: 'Demo Data',
      demoDataDescription: 'Showing sample reports. Data will be populated from actual range transactions.',
      exportReport: 'Export Report',
      today: 'Today',
      yesterday: 'Yesterday',
      thisWeek: 'This Week',
      thisMonth: 'This Month',
      thisQuarter: 'This Quarter',
      thisYear: 'This Year',
      bucketsSold: 'Buckets Sold',
      revenue: 'Revenue',
      uniqueCustomers: 'Unique Customers',
      avgPerCustomer: 'Avg per Customer',
      hourlyDistribution: 'Hourly Distribution',
      legendLow: 'Low',
      legendMedium: 'Medium',
      legendPeak: 'Peak',
      bucketSizeBreakdown: 'Bucket Size Breakdown',
      soldCount: '{count} sold',
      weeklyTrend: 'Weekly Trend',
      paymentMethods: 'Payment Methods',
      topCustomers: 'Top Customers',
      bucketsCount: '{count} buckets',
    },

    // Cart Reports tab (CartReportsTab.tsx)
    cartReports: {
      demoDataMode: 'Demo Data Mode',
      demoDataDesc: "You're viewing sample analytics data for demonstration purposes. Real data will appear once you add carts, rental rates, and process bookings.",
      sampleAnalytics: 'Sample Analytics',
      cartAnalytics: 'Cart Analytics',
      trackUtilizationRevenue: 'Track utilization, revenue, and booking trends',
      last3Months: 'Last 3 Months',
      last6Months: 'Last 6 Months',
      last12Months: 'Last 12 Months',
      yearToDate: 'Year to Date',
      export: 'Export',
      totalRevenue: 'Total Revenue',
      utilizationRate: 'Utilization Rate',
      totalBookings: 'Total Bookings',
      avgRevenuePerDay: 'Avg. Revenue/Day',
      revenueTrend: 'Revenue Trend',
      avgPerMonth: 'Avg: €{{amount}}/month',
      bookingsCount: '{{count}} bookings',
      peakBookingHours: 'Peak Booking Hours',
      rateDistribution: 'Rate Distribution',
      totalRevenue6mo: 'Total Revenue (6mo)',
      topCustomers: 'Top Customers',
      customerBookingsAvg: '{{count}} bookings - €{{avg}}/avg',
      fleetStatusSummary: 'Fleet Status Summary',
      available: 'Available',
      inUse: 'In Use',
      maintenance: 'Maintenance',
      outOfService: 'Out of Service',
    },

    // Coaching Revenue sub-page
    coachingRevenue: {
      title: 'Revenue Dashboard',
      subtitle: 'Track your coaching earnings and payments',
      thisWeek: 'This Week',
      thisMonth: 'This Month',
      thisYear: 'This Year',
      allTime: 'All Time',
      export: 'Export',
      totalRevenue: 'Total Revenue',
      paid: 'Paid',
      pending: 'Pending',
      avgPerSession: 'Avg per Session',
      averagePrice: 'Average price',
      sessions: 'sessions',
      noCoachProfile: 'No Coach Profile',
      noCoachProfileDesc: 'You need a coach profile to view revenue data. Please set up your coach profile first.',
      noRevenueData: 'No revenue data available',
      revenueByLessonType: 'Revenue by Lesson Type',
      revenueByStudent: 'Revenue by Student',
      noDataAvailable: 'No data available',
      lessonType: 'Lesson Type',
      status: 'Status',
      allTypes: 'All Types',
      allStatus: 'All Status',
      student: 'Student',
      location: 'Location',
      amount: 'Amount',
      date: 'Date',
      actions: 'Actions',
      practiceArea: 'Practice Area',
      markPaid: 'Mark Paid',
      markAsPaid: 'Mark as Paid',
      noTransactionsMatch: 'No transactions match your filters',
      noTransactionsYet: 'No transactions yet',
      showingTransactions: 'Showing {from}-{to} of {total} transactions',
      transactionDetails: 'Transaction Details',
      sessionPrice: 'Session Price',
      sessionDetails: 'Session Details',
      dateAndTime: 'Date & Time',
      paymentStatus: 'Payment Status',
      sendReceipt: 'Send Receipt',
      downloadPdf: 'Download PDF',
      activityLog: 'Activity Log',
      paymentCompleted: 'Payment completed',
      paymentPending: 'Payment pending',
      payAtClub: 'Pay at Club',
      freeSession: 'Free session',
      sessionScheduled: 'Session scheduled',
      sessionBooked: 'Session booked',
      searchPlaceholder: 'Search by student name...',
      typeLesson: 'Lesson',
      typePlayingLesson: 'Playing Lesson',
      typePractice: 'Practice',
      typeAssessment: 'Assessment',
      typeFitting: 'Club Fitting',
      statusFree: 'Free',
      statusFailed: 'Failed',
      statusRefunded: 'Refunded',
      payAtClubLabel: 'Pay at Club',
      transactionEyebrow: 'Transaction',
    },

    // Coaching Settings sub-page
    coachingSettings: {
      coachingActive: 'Coaching Active',
      coachingPaused: 'Coaching Paused',
      membersCanViewAndBook: 'Members can view coaches and book lessons',
      coachingHidden: 'Coaching features are hidden from members',
      active: 'Active',
      paused: 'Paused',
      toastEnabled: 'Coaching enabled for members',
      toastDisabled: 'Coaching disabled',
      toastFailedUpdate: 'Failed to update',
      toastChangesDiscarded: 'Changes discarded',
      toastSettingsSaved: 'Settings saved',
      bookingSettings: 'Booking Settings',
      bookingSettingsDesc: 'Configure how members book lessons',
      allowMemberBooking: 'Allow Member Booking',
      membersBookDirectly: 'Members can book lessons directly',
      requireCoachApproval: 'Require Coach Approval',
      coachMustConfirm: 'Coach must confirm bookings',
      bookingInfoTip: 'When coaching is active, members will see the Coaching section in their Club Hub and can browse available coaches.',
      myCoachSettings: 'My Coach Settings',
      paymentPreferences: 'Payment Preferences',
      paymentPreferencesDesc: 'How students pay for your lessons',
      defaultPaymentMethod: 'Default Payment Method',
      paymentOnline: 'Online Payment (Card)',
      paymentPayAtClub: 'Pay at Club',
      paymentCash: 'Cash',
      paymentFree: 'Free (No Payment)',
      acceptedPaymentMethods: 'Accepted Payment Methods',
      acceptCardViaStripe: 'Accept card payments via Stripe',
      studentPaysAtProShop: 'Student pays at pro shop',
      acceptCashDirectly: 'Accept cash payments directly',
      bookingPolicies: 'Booking Policies',
      bookingPoliciesDesc: 'Rules for booking and cancellation',
      minimumBookingNotice: 'Minimum Booking Notice',
      hoursInAdvance: 'hours in advance',
      maximumAdvanceBooking: 'Maximum Advance Booking',
      daysInAdvance: 'days in advance',
      autoAcceptBookings: 'Auto-accept Bookings',
      skipManualApproval: 'Skip manual approval',
      cancellationWindow: 'Cancellation Window',
      hoursBeforeLesson: 'hours before lesson',
      notifications: 'Notifications',
      notificationsDesc: 'When to notify you about bookings',
      newBookings: 'New Bookings',
      whenStudentBooks: 'When student books',
      cancellations: 'Cancellations',
      whenStudentCancels: 'When student cancels',
      payments: 'Payments',
      whenPaymentReceived: 'When payment received',
      saving: 'Saving...',
      savePaymentSettings: 'Save Payment Settings',
      unsavedChanges: 'You have unsaved changes',
      discard: 'Discard',
      save: 'Save',
    },

    // Coach Roster sub-page
    coachRoster: {
      panelTitle: 'Coach Profile',
      acceptingStudents: 'Accepting new students',
      notAcceptingStudents: 'Not accepting students',
      experienceLabel: 'Experience',
      rateLabel: 'Rate',
      specialtiesLabel: 'Specialties',
      certificationsLabel: 'Certifications',
      aboutLabel: 'About',
      close: 'Close',
      invitePanelTitle: 'Invite a Coach',
      searchPlaceholder: 'Search by name or email…',
      noCoachesFound: 'No coaches found',
      noCoachesMatchQuery: 'No coaches match "{query}". They need a coach profile on Strokone first.',
      noCoachesOnStrokon: 'No coaches with a Strokone profile yet.',
      statusActive: 'Active',
      statusInviteSent: 'Invite sent',
      inviteButton: 'Invite',
      invitationSent: 'Invitation sent',
      wantsToJoinBanner: 'Wants to join your club — review required',
      pillActive: 'Active',
      pillAwaitingResponse: 'Awaiting response',
      pillWantsToJoin: 'Wants to join',
      acceptingStudentsShort: 'Accepting students',
      notAcceptingShort: 'Not accepting',
      viewProfile: 'View profile',
      approve: 'Approve',
      cancelInvite: 'Cancel invite',
      remove: 'Remove',
      reject: 'Reject',
      noCoachesYet: 'No coaches yet',
      noCoachesDesc: 'Invite coaches to your club, or approve requests from coaches who want to join.',
      inviteFirstCoach: 'Invite your first coach',
      statActiveCoaches: 'Active coaches',
      statPending: 'Pending',
      statTotal: 'Total',
      sectionPendingRequests: 'Pending requests · {count}',
      sectionAwaitingResponse: 'Awaiting response · {count}',
      sectionActiveCoaches: 'Active coaches · {count}',
    },

    // Event Analytics sidebar + table
    eventAnalytics: {
      revenueByType: 'Revenue by Type',
      tournaments: 'Tournaments',
      competitions: 'Competitions',
      league: 'Leagues',
      socialTitle: 'Social',
      fillRateByType: 'Fill Rate by Type',
      socialEvents: 'Social Events',
      registrationTimingTitle: 'Registration Timing',
      earlyBirdLabel: 'Early Bird (7+ days)',
      standardLabel: 'Standard (3–7 days)',
      lastMinuteLabel: 'Last Minute (1–3 days)',
      dayOfLabel: 'Day-of',
      avgLeadTime: 'Avg lead time: {days} days',
      topParticipantsTitle: 'Top Participants',
      eventsCount: '{count} events',
      noParticipantData: 'No participant data available',
      quickInsightsTitle: 'Quick Insights',
      mostPopular: 'Most Popular',
      bestDay: 'Best Day',
      lowestNoShows: 'Lowest No-Shows',
      growthOpportunity: 'Growth Opportunity',
      recentEventsTitle: 'Recent Events',
      eventColumn: 'Event',
      typeColumn: 'Type',
      dateColumn: 'Date',
      capacityColumn: 'Capacity',
      fillColumn: 'Fill',
      attendedColumn: 'Attended',
      revenueColumn: 'Revenue',
      mainCourse: 'Main Course',
      noShowsLabel: '{count} no-shows',
      showingXOfY: 'Showing {showing} of {total} events',
      noEventsMatchSearch: 'No events match your search',
      noRecentEventsLabel: 'No recent events',
      previousButton: 'Prev',
      nextButton: 'Next',
      tournamentLabel: 'Tournament',
      competitionLabel: 'Competition',
      socialLabel: 'Social',
      leagueLabel: 'League',
    },

    // Bracket generation wizard + match edit dialog
    brackets: {
      selectWinnerPlaceholder: 'Select winner...',
      matchUpdatedSuccess: 'Match updated successfully!',
      failedToUpdateMatch: 'Failed to update match',
      winnerAdvancedSuccess: 'Winner advanced to next round!',
      failedToAdvanceWinner: 'Failed to advance winner',
      pleaseSelectWinnerFirst: 'Please select a winner before advancing',
      editMatchTitle: 'Edit Match {number}',
      editMatchDescription: 'Update match details, scores, and winner information.',
      matchInformation: 'Match Information',
      roundLabel: 'Round',
      roundValue: 'Round {number}',
      matchNumberLabel: 'Match Number',
      matchValue: 'Match {number}',
      playersAndWinner: 'Players & Winner',
      player1Badge: 'Player 1',
      player2Badge: 'Player 2',
      setWinner: 'Set Winner',
      byeMatch: 'Bye Match',
      autoWin: 'Auto-Win',
      winnerLabel: 'Winner',
      noWinnerSelected: 'No winner selected',
      matchStatusLabel: 'Match Status',
      statusPending: 'Pending',
      statusInProgress: 'In Progress',
      statusCompleted: 'Completed',
      statusWalkover: 'Walkover',
      statusCancelled: 'Cancelled',
      matchScores: 'Match Scores',
      player1HolesWon: 'Player 1 Holes Won',
      player2HolesWon: 'Player 2 Holes Won',
      holesPlayed: 'Holes Played',
      scheduledTimeOptional: 'Scheduled Time (Optional)',
      notesScoreDetail: 'Notes / Score Detail',
      notesPlaceholder: 'e.g., 3&2, 1 up, walkover due to injury...',
      advancing: 'Advancing...',
      advanceWinner: 'Advance Winner',
      cancel: 'Cancel',
      saving: 'Saving...',
      saveChanges: 'Save Changes',
      bracketGeneratedSuccess: 'Tournament bracket generated successfully!',
      loadingRegisteredPlayers: 'Loading registered players...',
      step1Title: 'Step 1: Select Players',
      registeredPlayersCount: 'Registered Players ({count})',
      selectAll: 'Select All',
      clearAll: 'Clear All',
      handicapLabel: 'Handicap',
      regPrefix: 'Reg',
      playersSelected: '{count} players selected',
      readyToProceed: 'Ready to proceed with bracket generation',
      selectAtLeast2: 'Select at least 2 players to continue',
      dragToReorder: 'Drag to Reorder Players (Top = #1 Seed)',
      dragTip: 'Drag players up or down to change their seeding order. The top player becomes #1 seed.',
      step2Title: 'Step 2: Choose Seeding Method',
      seedingRegistrationOrder: 'Registration Order',
      seedingRegistrationOrderDesc: 'First registered gets #1 seed, second gets #2 seed, etc.',
      recommended: 'Recommended',
      seedingRandom: 'Random',
      seedingRandomDesc: 'Completely random seeding for maximum unpredictability.',
      seedingHandicap: 'Handicap Based',
      seedingHandicapDesc: 'Lowest handicap gets #1 seed, highest gets last seed.',
      missingHandicaps: 'Missing Handicaps',
      seedingManual: 'Manual Seeding',
      seedingManualDesc: 'Drag-and-drop custom seeding order.',
      selectedMethod: 'Selected Method',
      selectedRegistrationOrderDesc: 'Players will be seeded based on registration order.',
      selectedRandomDesc: 'Players will be randomly seeded using a fair shuffle algorithm.',
      selectedHandicapDesc: 'Players will be seeded by handicap, lowest to highest.',
      selectedManualDesc: 'You will configure the custom seeding order in the next step.',
      configureManualSeeding: 'Configure Manual Seeding Order',
      updatingPreview: 'Updating Preview...',
      updateBracketPreview: 'Update Bracket Preview',
      step3Title: 'Step 3: Bracket Preview',
      roundsLabel: 'Rounds',
      bracketSizeLabel: 'Bracket Size',
      byesRequiredLabel: 'Byes Required',
      estDurationLabel: 'Est. Duration',
      firstRoundMatches: 'First Round Matches',
      matchLabel: 'Match {number}',
      vs: 'vs',
      bye: 'Bye',
      readyToGenerate: 'Ready to Generate Tournament Bracket',
      generateWarning: 'This will create the official tournament bracket and cannot be easily undone.',
      generateAlert: 'Once generated, matches will be available to players immediately. Make sure all settings are correct before proceeding.',
      back: 'Back',
      generatingPreview: 'Generating Preview...',
      next: 'Next',
      generating: 'Generating...',
      generateTournamentBracket: 'Generate Tournament Bracket',

      // BracketManagementInterface + AdminTournamentBracket
      totalMatches: 'Total Matches',
      tournamentManagement: 'Tournament Management',
      refresh: 'Refresh',
      export: 'Export',
      edit: 'Edit',
      view: 'View',
      deleteBracketTitle: 'Delete Tournament Bracket',
      deleteBracketDesc: 'Are you sure you want to delete this tournament bracket? This action cannot be undone. All match data will be permanently lost.',
      deleteBracketAction: 'Delete Bracket',
      tournamentProgress: 'Tournament Progress',
      roundXofY: 'Round {x} of {y}',
      percentComplete: '% Complete',
      tournamentComplete: 'Tournament Complete',
      championLabel: 'Champion:',
      tournamentActive: 'Tournament Active',
      tournamentActiveWarning: 'Tournament has started. Some management functions may be limited to preserve match integrity.',
      loadingBracket: 'Loading tournament bracket...',
      noBracketData: 'No Bracket Data',
      noBracketDataDesc: 'Bracket data is not available or failed to load.',

      // AdminTournamentBracket
      adminMode: 'Admin Mode',
      adminModeInstructions: 'Click on any match to edit details, or use quick action buttons to set winners. Changes will automatically advance winners to the next round.',
      p1Win: 'P1 Win',
      p2Win: 'P2 Win',
      matchN: 'Match {n}',
      scoreNotes: 'Score: {notes}',
      xOfYComplete: '{x}/{y} complete',
      legend: 'Legend',
      legendWinner: 'Winner',
      legendInProgress: 'In Progress',
      legendPending: 'Pending',
      legendClickToEdit: 'Click to edit',
      roundFinal: 'Final',
      roundSemifinal: 'Semifinal',
      roundQuarterfinal: 'Quarterfinal',
      roundN: 'Round {n}',
    },

    // Layout chrome strings
    layout: {
      thisWeek: 'This Week',
      lastWeek: 'Last Week',
      weeksAgo: '{count} Weeks Ago',
      thisMonth: 'This Month',
      thisYear: 'This Year',
      allTime: 'All Time',
      language: 'Language',
      logout: 'Logout',
      bulkImport: 'Bulk Import',
    },

    // GolfDataPopulationPanel
    golfDataPanel: {
      title: 'Golf Data Population',
      description: 'Populate your golf course with comprehensive data including clubs, courses, holes, tees, yardages, and GPS coordinates.',
      clubs: 'Clubs',
      courses: 'Courses',
      holes: 'Holes',
      tees: 'Tees',
      yardages: 'Yardages',
      coordinates: 'Coordinates',
    },

    // RealtimePerformanceMonitor
    realtimeMonitor: {
      systemStatusTitle: 'Real-time System Status',
      avgLatency: 'Average Latency',
      targetLatency: 'Target: <1000ms',
      activeChannels: 'Active Channels',
      realtimeConnections: 'Real-time connections',
      subscriptions: 'Subscriptions',
      memberConnections: 'Member connections',
      systemLoad: 'System Load',
      loadHigh: 'High',
      loadMedium: 'Medium',
      loadLow: 'Low',
      basedOnConnections: 'Based on connections',
      performanceAlerts: 'Performance Alerts',
      clearAll: 'Clear All',
      performanceTrends: 'Performance Trends',
      fiveSecReq: '5-Second Requirement',
      pass: 'PASS',
      fail: 'FAIL',
      concurrentSupport: 'Concurrent Support',
      targetLatencyReq: 'Target Latency',
      meet: 'MEET',
      miss: 'MISS',
      recentHistory: 'Recent Performance History',
      lastUpdated: 'Last updated:',
    },

    // SlotGenerator
    slotGenerator: {
      heading: 'Generate Slots',
      subheading: 'Create available tee time slots for booking',
      dateRangeTitle: 'Date Range',
      dateRangeDesc: 'Select the period to generate tee time slots',
      startDate: 'Start Date',
      endDate: 'End Date',
      presetNext7: 'Next 7 Days',
      presetNext14: 'Next 14 Days',
      presetNext30: 'Next 30 Days',
      presetThisMonth: 'This Month',
      previewTitle: 'Preview',
      previewDesc: 'Summary of slots to be generated',
      errorEndBeforeStart: 'End date must be after start date',
      statDays: 'Days',
      statSlotsDay: 'Slots/Day',
      statTotalSlots: 'Total Slots',
      statMaxPlayers: 'Max Players',
      basedOnSettings: 'Based on current settings:',
      operatingHours: 'Operating hours:',
      slotInterval: 'Slot interval:',
      minutes: 'minutes',
      playersPerSlot: 'Players per slot:',
      generating: 'Generating...',
      generateNSlots: 'Generate {n} Slots',
      noOverwriteNote: 'Existing slots will not be overwritten. Only new slots will be created.',
      errorInvalidRange: 'Please select a valid date range',
      errorFailedGenerate: 'Failed to generate slots. Please try again.',
      successGenerated: 'Successfully generated {n} time slots!',
    },
  },

  // Payment translations
  payment: {
    // Common payment strings
    orPayWithCard: 'Or pay with card',
    securePayment: 'Secure payment powered by Stripe',
    processing: 'Processing...',
    processingPayment: 'Processing Payment...',
    confirmingBooking: 'Confirming Booking...',
    pay: 'Pay',
    payAtClub: 'Pay at Club',
    reserveNowPay: 'Reserve now and pay when you arrive',
    reserveNowPayProShop: 'Reserve now and pay when you arrive at the pro shop',
    cancel: 'Cancel',
    or: 'Or',
    preparingCheckout: 'Preparing secure checkout...',
    goBack: 'Go Back',
    noPaymentRequired: 'No Payment Required',
    freeForMembers: 'This is free for members.',
    bookingFreeForMembers: 'This booking is free for members.',
    confirm: 'Confirm',
    confirmBooking: 'Confirm Booking',
    unableToLoadPayment: 'Unable to load payment form',
    paymentFailed: 'Payment failed',
    authenticationRequired: 'Additional authentication required. Please complete the verification.',
    paymentNotCompleted: 'Payment was not completed. Please try again.',

    // Payment step titles
    completePayment: 'Complete Payment',
    completePaymentCoaching: 'Please complete payment to confirm your coaching session.',
    completePaymentRegistration: 'Please complete payment to confirm your registration.',

    // Fee labels
    sessionFee: 'Session Fee',
    entryFee: 'Entry Fee',
    lesson: 'Lesson',
    with: 'with',
    min: 'min',
    toBePaid: 'To pay',

    // Toast messages
    error: 'Error',
    paymentProcessedButFailed: 'Payment processed but failed to update. Please contact support.',
    paymentProcessedButSessionFailed: 'Payment processed but failed to update session. Please contact support.',
    paymentProcessedButRegistrationFailed: 'Payment processed but failed to update registration. Please contact support.',

    // Cash-only / no Stripe mode
    clubNotConfigured: 'This club has not configured payment processing. Please contact the club directly.',
    usingPayAtClub: 'Your booking will be confirmed. Please pay at the pro shop before your tee time.',
    payAtClubDescription: 'Online payment is not available for this club. You can complete your booking and pay at the pro shop.',
    confirmAndPayAtClub: 'Confirm & Pay at Club',
    amountDueAtClub: 'Amount due at club'
  },

  // Member Services translations
  memberServices: {
    // Page header
    services: 'Services',

    // Tabs
    range: 'Range',
    carts: 'Carts',
    lockers: 'Lockers',

    // Checkout
    confirmPurchase: 'Confirm Purchase',
    pleaseSignIn: 'Please sign in to complete your purchase',
    clubInfoNotAvailable: 'Club information not available',
    processing: 'Processing...',
    paymentFailed: 'Payment Failed',
    somethingWentWrong: 'Something went wrong. Please try again.',
    tryAgain: 'Try Again',
    ready: 'Ready!',
    showAtDispenser: 'Show this at the dispenser',
    code: 'Code',
    done: 'Done',

    // Range tab
    rangeNotAvailable: 'Range Not Available',
    rangeNotAvailableDesc: "This club doesn't offer range services at the moment.",
    buyRangeBucket: 'Buy Range Bucket',
    balls: 'balls',
    buckets: 'buckets',
    bonus: 'bonus',
    buy: 'Buy',
    rangeCardPackages: 'Range Card Packages',
    rangeCard: 'Range Card',
    validDays: 'Valid {days} days',
    buyPackage: 'Buy Package',

    // Carts tab
    cartsNotAvailable: 'Carts Not Available',
    cartsNotAvailableDesc: "This club doesn't offer cart rentals at the moment.",
    noUpcomingRounds: 'No Upcoming Rounds',
    bookTeeTimeFirst: 'Book a tee time first, then add a cart to your round.',
    addCartToBooking: 'Add a cart to your upcoming tee time booking.',
    yourUpcomingRounds: 'Your Upcoming Rounds',
    player: 'player',
    players: 'players',
    addCart: 'Add Cart',
    holes9: '9 holes',
    holes18: '18 holes',
    cartPolicy: 'Carts can be cancelled up to 24h before tee time for a full refund.',
    policy: 'Policy',

    // Lockers tab
    lockersNotAvailable: 'Lockers Not Available',
    lockersNotAvailableDesc: "This club doesn't offer locker rentals at the moment.",
    lockerRentalPlans: 'Locker Rental Plans',
    month: 'Month',
    months: 'Months',
    monthRental: 'month rental',
    monthsRental: 'months rental',
    save: 'Save',
    needLocker: 'Need a locker?',
    requestLockerAssignment: 'Request a locker assignment',
    requestLocker: 'Request Locker',

    // Item name prefixes
    cartPrefix: 'Cart',
    lockerPrefix: 'Locker',
    at: 'at',
    dateLocale: 'en-US'
  },
  teeBooking: {
    bookTeeTime: "Book Tee Time",
    myBookings: "My Bookings",
    searchClubs: "Search clubs...",
    noClubsFound: "No clubs found",
    tryAdjustingSearch: "Try adjusting your search",
    clubsAvailable: "{count} club(s) available",
    details: "Details",
    book: "Book",
    today: "Today",
    morning: "Morning",
    afternoon: "Afternoon",
    evening: "Evening",
    selectDate: "Select Date",
    days: { sun: "Sun", mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat" },
    teeTime: "Tee Time",
    full: "Full",
    open: "Open",
    spotsOpen: "{count} open",
    yourBooking: "Your Booking",
    join: "Join",
    yours: "Yours",
    loadingTeeTimes: "Loading tee times...",
    unableToLoadTeeTimes: "Unable to load tee times",
    pleaseTryAgainLater: "Please try again later",
    goBack: "Go Back",
    golfClub: "Golf Club",
    mainCourse: "Main Course",
    championshipCourse: "Championship Course",
    holes: "{count} holes",
    upcoming: "Upcoming",
    past: "Past",
    noUpcomingBookings: "No upcoming bookings",
    bookNextTeeTime: "Book your next tee time to get started",
    noPastBookings: "No past bookings",
    bookingHistoryWillAppear: "Your booking history will appear here",
    inTime: "In {time}",
    started: "Started",
    completed: "Completed",
    bookingDetails: "Booking Details",
    confirmationNumber: "Confirmation #",
    organizer: "Organizer",
    players: "Players",
    playersCount: "Players ({count})",
    openSpot: "Open Spot",
    clickToAddPlayer: "Click to add a player",
    addPlayers: "Add Players",
    invitePlayers: "Invite Players",
    addToCalendar: "Add to Calendar",
    shareBooking: "Share Booking",
    bookingCompleted: "This booking has been completed",
    cancelBooking: "Cancel Booking",
    cancelThisBooking: "Cancel this booking?",
    cancelWarning: "This action cannot be undone. All {count} players will be notified.",
    keepBooking: "Keep Booking",
    yesCancel: "Yes, Cancel",
    cancelling: "Cancelling...",
    bookingCancelled: "Booking cancelled successfully",
    failedToCancel: "Failed to cancel booking",
    addPlayer: "Add Player",
    addAPlayer: "Add a player",
    tapToAddSomeone: "Tap to add someone",
    searchMembers: "Search members...",
    searchFriends: "Search friends...",
    noMembersFound: "No members found",
    noFriendsFound: "No friends found",
    noFriendsYet: "No friends yet",
    noRecentPartners: "No recent partners",
    searchResults: "SEARCH RESULTS",
    recentPartners: "RECENT PARTNERS",
    friends: "Friends",
    recent: "Recent",
    all: "All",
    guest: "Guest",
    addGuest: "Add Guest",
    addAGuest: "Add a Guest",
    someoneNotMember: "Someone not a member",
    guestName: "Guest Name",
    enterName: "Enter name",
    enterGuestDetails: "Enter your guest's details",
    emailOptional: "Email (optional)",
    handicapOptional: "Handicap (optional)",
    guestEmailPlaceholder: "guest@email.com",
    handicapPlaceholder: "e.g., 15",
    handicap: "Handicap {value}",
    handicapNA: "Handicap N/A",
    joinThisGroup: "Join This Group",
    requestSent: "Request Sent!",
    willBeNotified: "{name} will be notified",
    theOrganizerWillBeNotified: "The organizer will be notified",
    playersInGroup: "Players in this group",
    spotsLeft: "{count} spot(s) left",
    you: "You",
    joiningThisGroup: "Joining this group",
    messageToOrganizer: "Message to organizer (optional)",
    messagePlaceholder: "Say hi, mention your handicap, etc...",
    organizerNotificationInfo: "The organizer will be notified when you join. They can see your profile and handicap.",
    joining: "Joining...",
    failedToJoin: "Failed to join the group",
    unableToJoin: "Unable to join: Booking not found",
    joinExistingGroup: "Join existing group",
    startYourGroup: "Start your group",
    bookThisTime: "Book This Time",
    continueSolo: "Continue Solo",
    addXPlayers: "Add {count} Player(s)",
    selected: "SELECTED ({current}/{max})",
    addXPlayersButton: "Add {count} Player(s)",
    adding: "Adding...",
    failedToAddPlayers: "Failed to add players",
    failedToRemovePlayer: "Failed to remove player",
    addOns: "Add-ons",
    totalExtras: "Total extras",
    extrasAddedToBooking: "Extras added to your booking",
    failedToSaveExtras: "Failed to save extras",
    showQRCode: "Show QR Code",
    hideQRCode: "Hide QR Code",
    failedToGenerateQR: "Failed to generate QR code",
    camera: "Camera",
    upload: "Upload",
    manual: "Manual",
    positionQRCode: "Position the QR code within the frame",
    uploadQRImage: "Upload QR Code Image",
    lookUpBooking: "Look Up Booking",
    found: "Found!",
    invalidQRFormat: "Invalid QR code format",
    noQRFound: "No QR code found in image",
    failedToProcessImage: "Failed to process image",
    failedToReadImage: "Failed to read image file",
    invalidConfirmationCode: "Invalid confirmation code format",
    failedToCreatePayment: "Failed to create payment",
    failedToInitializePayment: "Failed to initialize payment",
    ok: "OK",
    golfTeeTime: "Golf Tee Time",
    need: "Need {count}",
    youPlusPlayers: "You + {count} player(s)",
    morningTime: "7:00 AM - 12:00 PM",
    afternoonTime: "12:00 PM - 5:00 PM",
    currentlyBookings: "Currently {count} booking(s)",
    teeBookingNotConfigured: "Online tee booking is not yet configured for this club. Showing available time slots.",
    loadingPlayers: "Loading players...",

    // Modal titles
    selectPlayers: "Select Players",
    addPlayersTitle: "Add Players",
    addExtrasTitle: "Add Extras",
    reviewBooking: "Review Booking",
    payment: "Payment",
    success: "Success",
    joinGroup: "Join Group",

    // Player count step
    howManyPlayers: "How many players?",
    selectNumberOfSpots: "Select the number of spots you need",

    // Add players step
    addPlayerXOfY: "Add Player {current} of {total}",
    addFriendsThroughSocial: "Add friends through the social features",
    searchMembersMin2: "Search members (min 2 characters)...",
    searchAllMembers: "Search all members...",
    searchMembersLabel: "SEARCH MEMBERS",
    typeAtLeast2Chars: "Type at least 2 characters to search",
    currentPlayers: "Current Players",

    // Add extras step
    addExtras: "Add Extras",
    enhanceRoundWithAddons: "Enhance your round with these add-ons",
    continueToReview: "Continue to Review",
    skipAndReview: "Skip & Review Booking",
    cartRentals: "Cart Rentals",
    rangeProducts: "Range Products",
    golfCarts: "GOLF CARTS",
    rangeWarmup: "RANGE WARM-UP",
    balls: "balls",
    extrasSelected: "{count} extra(s) selected",

    // Review step
    cancellationPolicy: "Cancellation Policy",
    hoursWindow: "{hours}h window",
    freeCancellationUntil: "Free cancellation until {time} on {date}. Late cancellations may incur a fee.",
    greenFeeTotal: "Green Fee Total",
    sendRemindersToPlayers: "Send reminders to players",
    hoursBeforeTeeTime: "{hours}h before tee time",
    bookingInProgress: "Booking...",
    proceedToPayment: "Proceed to Payment",
    confirmBooking: "Confirm Booking",
    total: "Total",
    greenFee: "Green Fee ({count} players)",

    // Payment step
    completePayment: "Complete Payment",
    teeTimeOn: "Tee time: {time} on {date}",

    // Success step
    youreAllSet: "You're All Set!",
    teeTimeBooked: "Your tee time has been booked",
    playersOpenSpots: "{players} player(s) • {spots} open spot(s)",
    addExtrasToRound: "Add extras to your round?",
    cartRentalRangeMore: "Cart rental, range warm-up & more",
    shareWithPlayers: "Share with Players",
    invite4thPlayer: "Invite 4th Player",
    inviteMorePlayers: "Invite More Players",
    viewMyBookings: "View My Bookings",
    edit: "Edit",

    // Share text
    bookingConfirmedShare: "Tee Time Booking Confirmed!",
    date: "Date",
    time: "Time",
    course: "Course",
    confirmation: "Confirmation",
    golfCourse: "Golf Course",
    seeYouOnCourse: "See you on the course!",
    teeTimeBooking: "Tee Time Booking",
    copiedToClipboard: "Booking details copied to clipboard",

    // CourseFilterSheet
    courseAndTee: "Course & Tee",
    reset: "Reset",
    selectCourse: "Select Course",
    holesParInfo: "{holes} holes • Par {par}",
    numberOfHoles: "Number of Holes",
    nHoles: "{n} Holes",
    selectTee: "Select Tee",
    loadingTees: "Loading tees...",
    noTeesAvailable: "No tees available for this course",
    applyFilters: "Apply Filters",

    // Booking race-condition / payment error strings
    slotFullBeforePayment: "This slot just filled up. Please choose another tee time.",
    bookingFailedAfterPayment: "Your payment was taken but the booking could not be confirmed. You will be refunded automatically — please contact support if you have questions.",
    bookingFailed: "Booking failed. Please try again or contact support."
  },

  // Event Details Page
  eventDetails: {
    // Page headers
    title: "Event Details",
    titleMobile: "Details",
    eventSummary: "Event Summary",
    participation: "Participation",
    additionalInfo: "Additional Info",

    // Buttons
    back: "Back",
    editEvent: "Edit Event",
    cancel: "Cancel",
    saveChanges: "Save Changes",
    saving: "Saving...",
    backToEvents: "Back to Events",

    // Form labels
    eventName: "Event Name",
    description: "Description",
    eventType: "Event Type",
    eventStructure: "Event Structure",
    scoringFormat: "Scoring Format",
    strokeAllowancePercentage: "Stroke Allowance Percentage",
    leaguePointSystem: "League Point System",
    win: "Win",
    draw: "Draw",
    loss: "Loss",
    startDate: "Start Date",
    endDate: "End Date",
    registrationDeadline: "Registration Deadline",
    capacity: "Capacity",
    publishedStatus: "Published Status",
    entryFee: "Entry Fee ($)",
    prizes: "Prizes",

    // Placeholders
    enterEventName: "Enter event name",
    enterEventDescription: "Enter event description",
    selectEventType: "Select event type",
    selectStructure: "Select structure",
    selectScoringFormat: "Select scoring format",
    selectStrokeAllowance: "Select stroke allowance",
    selectCourseOptional: "Select course (optional)",
    enterPrizeDetails: "Enter prize details",

    // Event type options
    tournament: "Tournament",
    league: "League",
    socialEvent: "Social Event",
    activityLesson: "Activity/Lesson",
    competition: "Competition",

    // Event structure options
    singleRound: "Single Round",
    multiRound: "Multi-Round",
    championship: "Championship",

    // Scoring format options
    strokePlay: "Stroke Play",
    stableford: "Stableford",
    matchPlay: "Match Play",
    teamPlay: "Team Play",

    // Stroke allowance options
    allowance100: "100% (Full handicap)",
    allowance95: "95% (USGA recommended for stroke play/stableford)",
    allowance90: "90%",
    allowance85: "85%",
    allowance80: "80%",
    allowance75: "75%",
    allowance70: "70%",
    allowance65: "65%",
    allowance60: "60%",
    allowance55: "55%",
    allowance50: "50%",

    // Status text
    published: "Published",
    draft: "Draft",
    courseSelected: "Course Selected",
    participants: "participants",
    at: "at",

    // Help text
    matchPlayHelp: "Reduced allowances create more competitive match play.",
    strokePlayHelp: "USGA recommends 95% for stroke play and stableford competitions.",
    pointSystemHelp: "Configure how points are awarded for match results. Standard is 3-1-0 (Win-Draw-Loss).",

    // Validation messages
    eventNameRequired: "Event name is required",
    startDateRequired: "Start date is required",
    endDateRequired: "End date is required",
    registrationDeadlineRequired: "Registration deadline is required",
    endDateMustBeAfterStart: "End date must be after start date",
    deadlineBeforeStart: "Registration deadline must be before start date",
    capacityMinimum: "Capacity must be at least 1",

    // Success/Error messages
    eventUpdated: "Event updated successfully!",
    updateFailed: "Failed to update event",
    eventNotFound: "Event not found",
    eventNotFoundDescription: "The event you're looking for doesn't exist or has been deleted.",
    authenticationRequired: "Authentication required",
    authenticationRequiredDescription: "Please log in to view events.",

    // Display text
    noDescription: "No description provided"
  },

  // Member Expenses
  expenses: {
    myExpenses: "My Expenses",
    trackYourSpending: "Track your club spending",
    noClubMembership: "No Club Membership",
    joinClubToTrack: "Join a club to track your expenses",
    browseClubs: "Browse Clubs",
    allClubs: "All Clubs",
    thisMonth: "This Month",
    thisYear: "This Year",
    subscriptions: "Subscriptions",
    prepaidBalance: "Prepaid Balance",
    credits: "{count} credits",
    activeServices: "Active Services",
    expires: "Expires {date}",
    spendingBreakdown: "Spending Breakdown",
    total: "Total",
    amount: "Amount",
    transactionHistory: "Transaction History",
    dateRange: "Date Range",
    allTime: "All Time",
    lastMonth: "Last Month",
    last30Days: "Last 30 Days",
    category: "Category",
    allCategories: "All Categories",
    loading: "Loading...",
    loadMore: "Load More",
    noTransactionsYet: "No transactions yet",
    purchasesWillAppear: "Your purchases and bookings will appear here",
    // Categories
    range: "Range",
    cart: "Cart",
    locker: "Locker",
    teeTime: "Tee Time",
    teeTimes: "Tee Times",
    event: "Event",
    events: "Events",
    coaching: "Coaching",
    // Status badges
    completed: "Completed",
    pending: "Pending",
    refunded: "Refunded",
    cancelled: "Cancelled"
  },

  // Admin translations
  admin: {
    // Club Admin Feedback page (/club-admin/feedback)
    feedback: {
      whatTypeFeedback: 'What type of feedback do you have?',
      title: 'Title',
      titlePlaceholder: 'Brief summary of your feedback',
      description: 'Description',
      descriptionPlaceholder: 'Please provide as much detail as possible...',
      characters: 'characters',
      beDetailed: 'Be as detailed as possible to help us understand your feedback',
      submitFeedback: 'Submit Feedback',
      submitting: 'Submitting...',
      submitted: 'Submitted!',
      yourFeedbackMatters: 'Your feedback matters!',
      reviewMessage: 'We review all feedback and use it to improve the platform. Thank you for helping us make the app better!',
      pleaseFillRequiredFields: 'Please fill in all required fields',
      feedbackSubmittedSuccessfully: 'Feedback submitted successfully!',
      failedToSubmitFeedback: 'Failed to submit feedback',
      categories: {
        bug: { title: 'Report a Bug', description: 'Something isn\'t working as expected' },
        feature: { title: 'Feature Request', description: 'Suggest a new feature or capability' },
        improvement: { title: 'Improvement', description: 'Suggest an improvement to existing features' },
        question: { title: 'Question', description: 'Ask a question about the platform' },
      },
    },
    memberProgress: {
      startOnboarding: 'Start Onboarding',
      title: 'Member Progress',
      subtitle: 'Track member progression and onboarding status',
      loadingMembers: 'Loading members...',
      errorLoadingMembers: 'Error loading members',
      noMembersFound: 'No members found matching your filters.',
      // Stats Row
      totalMembers: 'Total Members',
      inOnboarding: 'In Onboarding',
      learningFundamentals: 'Learning fundamentals',
      needAttention: 'Need Attention',
      inactive7Days: 'Inactive 7+ days',
      everyoneActive: 'Everyone active',
      questsCompleted: 'Quests Completed',
      thisWeek: 'This week',
      thisMonth: 'this month',
      allMembers: 'All members',
      // Filter Toolbar
      filters: 'Filters',
      tier: 'Tier',
      status: 'Status',
      sort: 'Sort',
      allTiers: 'All Tiers',
      onboarding: 'Onboarding',
      rookie: 'Rookie',
      regular: 'Regular',
      veteran: 'Veteran',
      legend: 'Legend',
      allStatus: 'All Status',
      active: 'Active',
      slowingDown: 'Slowing Down',
      atRisk: 'At Risk',
      lastActiveRecent: 'Last Active (Recent)',
      lastActiveOldest: 'Last Active (Oldest)',
      progressHighToLow: 'Progress (High to Low)',
      progressLowToHigh: 'Progress (Low to High)',
      nameAZ: 'Name (A-Z)',
      nameZA: 'Name (Z-A)',
      handicapLowToHigh: 'Handicap (Low to High)',
      handicapHighToLow: 'Handicap (High to Low)',
      // Member Progress Table
      member: 'Member',
      progress: 'Progress',
      lastActive: 'Last Active',
      actions: 'Actions',
      viewProfile: 'View Profile',
      showingMembers: 'Showing',
      members: 'members',
      total: 'Total',
      quests: 'Quests',
      complete: 'Complete',
      never: 'Never',
      stage: 'Stage',
      // Start Onboarding Modal
      selectMembersToEnroll: 'Select members to enroll in the onboarding program.',
      searchMembers: 'Search members...',
      deselectAll: 'Deselect All',
      selectAll: 'Select All',
      membersSelected: 'member(s) selected',
      noMembersAvailable: 'No members available',
      allMembersInOnboarding: 'All members are already in onboarding.',
      assignCoach: 'Assign Coach (Optional)',
      selectCoach: 'Select a coach...',
      assignLater: 'Assign later',
      loadingCoaches: 'Loading coaches...',
      unknownCoach: 'Unknown Coach',
      cancel: 'Cancel',
      starting: 'Starting...',
      onboardingStarted: 'Onboarding started',
      membersAssignedToCoach: 'Members have been assigned to a coach and enrolled in onboarding.',
      membersEnrolledNoCoach: 'Members have been enrolled in onboarding. Coach can be assigned later.',
      failedToEnroll: 'Failed to enroll member(s)',
      someMembersAlreadyOnboarding: 'Some members may already be in onboarding.',
      failedToStartOnboarding: 'Failed to start onboarding',
      // Detail page translations (member-progress/:memberId)
      loadingDetails: 'Loading member details...',
      errorLoading: 'Error Loading Member',
      errorDescription: 'Unable to find member details. Please go back and try again.',
      backToList: 'Back to Member Progress',
      message: 'Message',
      // Detail-page side panels
      panels: {
        eyebrowMember: 'Member',
        eyebrowDirectMessage: 'Direct message',
        cancelBtn: 'Cancel',
        sendEmail: 'Send Email',
        sendingEllipsis: 'Sending…',
        panelTo: 'To',
        panelSubject: 'Subject',
        subjectPlaceholder: 'Subject…',
        panelMessage: 'Message',
        messagePlaceholder: 'Write your message…',
        suspendMember: 'Suspend Member',
        suspendingEllipsis: 'Suspending…',
        suspendConfirmTitle: 'Suspend {name}?',
        suspendConfirmText: 'Suspended members cannot access club facilities or online services. This can be reversed at any time.',
        activateMember: 'Activate Member',
        activatingEllipsis: 'Activating…',
        activateConfirmTitle: 'Activate {name}?',
        activateConfirmText: 'This will restore full access to club facilities and online services.',
        newRole: 'New Role',
        roleAdmin: 'Admin',
        roleAdminDesc: 'Full club management access',
        roleStaff: 'Staff',
        roleStaffDesc: 'Limited management access',
        roleMember: 'Member',
        roleMemberDesc: 'Standard member access',
        saveRole: 'Save Role',
        savingRoleEllipsis: 'Saving…',
        selectCoachLabel: 'Select a coach',
        noCoachesFound: 'No coaches found',
        noCoachesDesc: 'Add coaches to this club first via the admin settings.',
        assignCoachBtn: 'Assign Coach',
        assigningEllipsis: 'Assigning…',
        tabOverview: 'Overview',
        tabPerformance: 'Performance',
      },
      // Retention tab
      retention: {
        tabMembers: 'Members',
        tabRetention: 'Retention',
        heroEyebrow: 'New Member Journey · This Quarter',
        needsAttentionTitle: 'Needs attention this week',
        needsAttentionCount_one: '{{count}} member',
        needsAttentionCount_other: '{{count}} members',
        renewalsAtRiskTitle: 'Renewals at risk',
        renewalsAtRiskSubtitle: 'New members flagged at risk — likely to churn before renewal',
        renewalsAtRiskAlert_one: '{{count}} member likely not to renew',
        renewalsAtRiskAlert_other: '{{count}} members likely not to renew',
        d90TrendTitle: 'D90 retention trend',
        d90TrendSubtitle: 'Are we getting better at keeping new members?',
        noTrendData: 'No trend data yet',
        noTrendDataDesc: 'D90 trend appears once members have been active for 90+ days.',
        cohortTitle: 'Cohort Retention',
        cohortSubtitle: '% of each month\'s new members still active at day 30 / 60 / 90',
        cohortFooter: 'Last 6 join months · tap a cohort for member detail',
        compareCohorts: 'Compare cohorts',
        noData: 'No cohort data yet',
        noDataDesc: 'Members appear here once their join month is at least 30 days old.',
        tagNeverActivated: 'Never activated',
        tagAtRisk: 'At risk',
        tagSlowingDown: 'Slowing down',
        contact: 'Contact',
        stageLabel: 'Stage {{stage}}',
        daysInactive: '{{days}}d inactive',
        joinedDaysAgo: 'Joined {{days}}d ago',
        moreMembers: '+{{count}} more members →',
        insightDrop: 'The {{cohort}} cohort dropped {{drop}}% between day 30 and day 90.',
        insightAtRisk: '{{count}} members from that month are currently flagged at risk.',
        d30Retention: 'D30 retention',
        d90Retention: 'D90 retention',
        membersTracked: 'Members tracked',
        thisQtrUp: '↑ {{delta}}% this qtr',
        thisQtrDown: '↓ {{delta}}% this qtr',
        // Pipeline nodes
        pipelineJoined: 'Joined',
        pipelineAppActive: 'App active',
        pipelineFirstRound: 'First round',
        pipelineDay30: 'Day 30',
        pipelineDay90: 'Day 90',
        medianDaysToFirstActivity: 'Median days to first activity',
        // Retention trend card
        retentionTrendTitle: 'Retention trend',
        trendToggleYear: 'Year',
        trendQtrAvg: 'Quarterly avg',
        trendYearAvg: 'Yearly avg',
        trendTarget: 'Target: 70%',
        trendEmptyJoin: 'Join',
        trendD30Desc: 'Appears once members have been active 30+ days.',
        trendD60Desc: 'Appears once members have been active 60+ days.',
        trendD90Desc: 'Appears once members have been active 90+ days.',
        // Activation breakdown
        activationTitle: 'How new members activate',
        activationSubtitle: 'First activity reached by each member',
        activationFirstRound: 'First round',
        activationFirstLesson: 'First lesson',
        activationFirstEvent: 'First event',
        activationFirstSocial: 'First social post',
        activationFullyActivatedNote: 'Fully activated = 3+ rounds or 1 lesson + 1 round within 60 days',
        // Attention / at-risk cards
        daysUnit: 'days',
        neverLoggedIn: 'Never logged in',
        attentionFooter: '{{count}} member needs attention',
        attentionFooterPlural: '{{count}} members need attention',
        atRiskCount: '{{count}} at risk',
        likelyNotToRenew: '{{count}} member likely not to renew',
        likelyNotToRenewPlural: '{{count}} members likely not to renew',
        // Email / chat panels
        emailPanelEyebrow: 'Send email',
        emailPanelTo: 'To',
        emailPanelSubject: 'Subject',
        emailPanelMessage: 'Message',
        emailPanelCancel: 'Cancel',
        emailPanelSend: 'Send',
        emailPanelSending: 'Sending…',
        messagePanelEyebrow: 'Message',
        openingConversation: 'Opening conversation…',
        // Cohort table columns
        tableColCohort: 'Cohort',
        tableColSize: 'Size',
        tableColAtRisk: 'At risk',
      },
      // Tabs
      tabs: {
        onboarding: 'Onboarding',
        questProgress: 'Quest Progress',
        performanceTrends: 'Performance Trends',
        rounds: 'Rounds',
        notes: 'Notes',
      },
      // Tier labels (nested)
      tiers: {
        onboarding: 'Onboarding',
        rookie: 'Rookie',
        regular: 'Regular',
        veteran: 'Veteran',
        legend: 'Legend',
      },
      // Member header
      header: {
        memberSince: 'Member since',
        currentHcp: 'Current HCP',
        totalRounds: 'Total Rounds',
        progressions: 'Progressions',
        lastActive: 'Last Active',
        onboardingProgression: 'Onboarding Progression',
        playerProgression: 'Player Progression',
        neverActive: 'Never active',
        activeAgo: 'ago',
        memberSuffix: 'Member',
      },
      // Lesson prep / onboarding tab
      lessonPrep: {
        assignedCoach: 'Assigned Coach',
        noCoachAssigned: 'No coach assigned',
        selectCoachBelow: 'Select a coach below',
        selectCoach: 'Select coach...',
        noCoach: 'No coach',
        unknownCoach: 'Unknown Coach',
        coach: 'Coach',
        coachUpdated: 'Coach assignment updated',
        coachUpdateFailed: 'Failed to update coach',
        journeyComplete: 'Journey Complete!',
        whatsNext: "What's Next?",
        graduated: 'has graduated from onboarding!',
        nowRookie: 'They are now a Rookie tier member.',
        stageAdvanced: 'Stage advanced successfully',
        failedToAdvanceStage: 'Failed to advance stage',
        errorOccurred: 'An error occurred',
        loading: 'Loading journey progress...',
        currentStage: 'Current Stage',
        exerciseAddedToStage: 'Exercise added to stage',
        failedToAddExercise: 'Failed to add exercise',
        exerciseRemovedFromStage: 'Exercise removed from stage',
        failedToRemoveExercise: 'Failed to remove exercise',
        requirementsUpdated: 'Requirements updated',
        failedToUpdateRequirements: 'Failed to update requirements',
        graduatedLabel: 'Graduated',
        stagesCompleted: 'Stages Completed',
        currentTier: 'Current Tier',
        whatsNextBody: 'Now that {name} has graduated, they have access to the full quest system. Check the Progress tab to see their skill progression by category and recommend new challenges.',
        progressTab: 'Progress',
        // Quest creation
        createNewQuest: 'Create New Quest',
        questName: 'Quest Name',
        description: 'Description',
        requirements: 'Requirements',
        icon: 'Icon',
        videoAddedToStage: 'Video added to stage',
        failedToAddVideo: 'Failed to add video',
        videoRemovedFromStage: 'Video removed from stage',
        failedToRemoveVideo: 'Failed to remove video',
        questCreated: 'Quest created',
        failedToCreateQuest: 'Failed to create quest',
        questDeleted: 'Quest deleted',
        failedToDeleteQuest: 'Failed to delete quest',
        createQuest: 'Create Quest',
      },
      // Quest progress / categories tab
      questCategories: {
        title: 'Quest Categories',
        skillBased: 'SKILL-BASED',
        outcomeBased: 'OUTCOME-BASED',
        complete: 'Complete',
        selected: 'Selected',
        focus: 'Focus',
        quests: 'Quests',
        loading: 'Loading quests...',
        validating: 'Validating...',
        validate: 'Validate',
        completed: 'Completed',
        locked: 'Locked',
        inProgress: 'In Progress',
        noQuests: 'No quests available in this category',
        noCategoryData: 'No category data available.',
        categoriesCount: 'categories',
        completeTheseToProgress: 'Complete these to progress',
        nextLabel: 'Next:',
        noQuestsInCategory: 'No quests found in this category.',
        questValidated: 'Quest validated!',
        failedToValidate: 'Failed to validate',
        adminRequired: 'Admin access required',
        confirmValidate: 'Validate "{name}" for this member?',
        completedLabel: 'completed',
        categories: {
          driving: 'Driving accuracy and distance control',
          putting: 'Putting skills and green reading',
          ironPlay: 'Iron accuracy and distance management',
          shortGame: 'Chipping, pitching, and bunker play',
          courseManagement: 'Strategic course navigation',
          scoring: 'Score improvement goals',
          consistency: 'Consistency and mental game',
          precision: 'Driving accuracy (FIR) & approach shots (GIR)',
          recovery: 'Scrambling, sand saves & short game',
          rounds: 'Playing rounds and course experience',
          social: 'Club engagement and social activities',
          special: 'Special achievements and milestones',
          default: 'Quest category',
        },
      },
      // Notes tab
      notes: {
        newNote: 'New Note',
        addNewNote: 'Add New Note',
        addNote: 'Add Note',
        notesHistory: 'Notes History',
        noNotesYet: 'No notes yet. Add your first note above.',
        noteAdded: 'Note added',
        noteAddFailed: 'Failed to add note',
        noteUpdated: 'Note updated',
        noteUpdateFailed: 'Failed to update note',
        noteDeleted: 'Note deleted',
        noteDeleteFailed: 'Failed to delete note',
        deleteConfirm: 'Are you sure you want to delete this note?',
        visibleToPlayer: 'Visible to Player',
        private: 'Private',
        linked: 'Linked',
        linkedTo: 'Linked to:',
        notesCount: 'notes',
        selectType: 'Select Type',
        linkToOptional: 'Link To (optional)',
        // NoteEditor strings
        visibleToPlayerLabel: 'Visible to Player',
        playerWillSeeNote: 'Player will see this note on their progression page',
        onlyVisibleToCoaches: 'Only visible to coaches and admins',
        privateAdminOnly: 'Private (Admin Only)',
        onlyVisibleToAdmins: 'This note is only visible to admins, not other coaches',
        visibleToAllCoaches: 'Visible to all coaches and admins',
        linkToRoundEvent: 'Link to Round/Event (Optional)',
        noLink: 'No link',
        linkedToFormat: 'Linked to: {date} at {course}',
        cancel: 'Cancel',
        saving: 'Saving...',
        saveNote: 'Save Note',
        types: {
          coachFeedback: 'Coach Feedback',
          performanceNote: 'Performance Note',
          behaviorNote: 'Behavior Note',
          adminNote: 'Admin Note',
          goalSetting: 'Goal Setting',
        },
      },
      // Round history tab
      roundHistory: {
        title: 'Round History',
        rounds: 'rounds',
        filters: {
          all: 'All',
          solo: 'Solo',
          multiplayer: 'Multiplayer',
          multiplayerShort: 'Multi',
          tournament: 'Tournament',
          tournamentShort: 'Tourn',
        },
        noRoundsYet: 'No rounds recorded yet.',
        noRoundsOfType: 'No {type} rounds found.',
        headers: {
          date: 'Date',
          course: 'Course',
          type: 'Type',
          score: 'Score',
          fir: 'FIR',
          gir: 'GIR',
          putts: 'Putts',
          hcp: 'HCP',
        },
      },
    },
    // Announcement Analytics page (/club-admin/announcements/analytics)
    announcementAnalytics: {
      announcements: 'Announcements',
      last30Days: 'Last 30 Days',
      last90Days: 'Last 90 Days',
      thisYear: 'This Year',
      export: 'Export',
      pleaseSelectClub: 'Please select a club to view analytics.',
      failedToLoadData: 'Failed to load analytics data.',
      // KPI Cards
      totalReach: 'Total Reach',
      avgEngagement: 'Avg. Engagement',
      published: 'Published',
      activePinned: 'Active Pinned',
      // Sidebar
      audienceReach: 'Audience Reach',
      of: 'of',
      membersReached: 'members reached',
      vsLastMonth: 'vs last month',
      expiringSoon: 'Expiring Soon',
      quickStats: 'Quick Stats',
      scheduled: 'Scheduled',
      drafts: 'Drafts',
      tomorrow: 'Tomorrow',
      inDays: 'In {{days}} days',
      // Priority Chart
      priorityDistribution: 'Priority Distribution',
      urgent: 'Urgent',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      total: 'Total',
      // Delivery Chart
      deliveryChannels: 'Delivery Channels',
      inApp: 'In-App',
      email: 'Email',
      push: 'Push',
      views: 'Views',
      engagements: 'Engagements',
      estimated: 'Estimated',
      channelTrackingTooltip: 'Channel-specific tracking coming soon. Currently showing estimated distribution.',
      // Age Distribution
      audienceAge: 'Audience Age',
      members: 'Members',
      // Top Performers Table
      topPerformers: 'Top Performers',
      announcement: 'Announcement',
      date: 'Date',
      priority: 'Priority',
      rate: 'Rate',
      noEngagementData: 'No announcements with engagement data yet',
      // Channel Performance
      channelPerformance: 'Channel Performance',
      noChannelData: 'No channel data available',
      sent: 'sent',

      // AnnouncementAnalytics.tsx
      noClubAccess: 'No Club Access',
      noClubAccessDesc: 'Please select a club to view analytics.',
      loadingAnalytics: 'Loading Analytics...',
      errorLoading: 'Error Loading Analytics',
      errorLoadingDesc: 'Please try again later.',
      back: 'Back',
      title: 'Announcement Analytics',
      periodLast30: 'Last 30 Days',
      periodLast90: 'Last 90 Days',
      periodThisYear: 'This Year',
    },
    // Services Config page (/club-admin/services/config)
    servicesConfig: {
      noClubSelected: 'No club selected',
      servicesVisible: 'Services Visible',
      servicesHidden: 'Services Hidden',
      membersCanSeeSection: 'Members can see the Services section in their Club Hub',
      servicesSectionHidden: 'Services section is hidden from members',
      visible: 'Visible',
      hidden: 'Hidden',
      individualServices: 'Individual Services',
      controlWhichServices: 'Control which specific services are available to members',
      comingSoon: 'Coming soon',
      active: 'Active',
      coachingManagedInSection: 'Coaching settings are managed in the Coaching section.',
      toastEnabled: 'Enabled',
      toastDisabled: 'Disabled',
      toastFailedUpdate: 'Failed to update service: {{message}}',
      toastSectionEnabled: 'Services section enabled for members',
      toastSectionHidden: 'Services section hidden from members',
      toastFailedUpdateServices: 'Failed to update services',
      serviceNames: {
        tee_time: 'Tee Time',
        range: 'Driving Range',
        cart_rental: 'Cart Rental',
        locker: 'Locker Rental',
        simulator: 'Golf Simulator',
        pro_shop: 'Pro Shop',
        restaurant: 'Restaurant',
      },
      categories: {
        booking: 'Booking',
        purchase: 'Purchase',
        rental: 'Rental',
        event: 'Event',
        subscription: 'Subscription',
      },
    },
    // Staff Dashboard translations (ManagerDashboard.tsx)
    staffDashboard: {
      // Tabs
      overview: 'Overview',
      taskManagement: 'Task Management',
      staffSchedule: 'Staff Schedule',
      communication: 'Communication',
      // Stats
      staffOnDuty: 'Staff On Duty',
      pendingTasks: 'Pending Tasks',
      urgent: 'urgent',
      unassigned: 'unassigned',
      tasksToday: 'Tasks Today',
      punctuality: 'Punctuality',
      utilization: 'utilization',
      minAvg: 'min avg',
      thisWeek: 'this week',
      // Dashboard sections
      priorityTasks: 'Priority Tasks',
      urgentItemsAttention: 'Urgent items requiring attention',
      todaysSchedule: "Today's Schedule",
      currentShiftAssignments: 'Current shift assignments',
      staffStatusOverview: 'Staff Status Overview',
      monitorTeamPerformance: "Monitor your team's performance and activity",
      onShift: 'On Shift',
      offDuty: 'Off Duty',
      activeTasks: 'Active Tasks',
      completedToday: 'Completed Today',
      // Error states
      unableToLoadDashboard: 'Unable to Load Dashboard',
      pleaseRefreshPage: 'Please refresh the page or contact support',
      staffAccessRequired: 'Staff Access Required',
      userAccountNotMatchStaff: "Your user account doesn't match any existing staff member",
      addMeAsNewStaff: 'Add Me as New Staff Member',
      linkToMe: 'Link to Me',
      // Task list
      noTasksToday: 'No tasks scheduled for today',
      tasksDueToday: 'tasks due today',
      overdue: 'Overdue',
      dueToday: 'Due Today',
      viewAllTasks: 'View All Tasks',
      // Schedule
      noShiftsToday: 'No shifts scheduled for today',
      shiftsToday: 'shifts today',
      viewFullSchedule: 'View Full Schedule',
      // Actions
      markComplete: 'Mark Complete',
      viewDetails: 'View Details',
      loading: 'Loading staff dashboard...',
      // Task Management section
      organizeAssignTasks: 'Organize and assign tasks to your team',
      createTask: 'Create Task',
      // Schedule section
      manageShiftsAvailability: 'Manage shifts and staff availability',
      scheduleShift: 'Schedule Shift',
      // Communication section
      teamCommunication: 'Team Communication',
      channel: 'Channel',
      channelConversation: 'Channel conversation',
      selectChannelToStart: 'Select a channel to start messaging',
      chooseFromChannelList: 'Choose from the channel list to view conversations',
      // Edit Channel Dialog
      editChannel: 'Edit Channel',
      updateChannelName: 'Update channel name',
      channelName: 'Channel Name',
      enterChannelName: 'Enter channel name',
      cancel: 'Cancel',
      saving: 'Saving...',
      saveChanges: 'Save Changes',
      // Convert to Task Dialog
      convertToTask: 'Convert to Task',
      createTaskFromMessage: 'Create a task from this message',
      willCreateTaskWithContent: 'This will create a new task with the message content as the description.',
      fillInTaskDetails: 'Fill in the task details',
      // Delete Channel Dialog
      deleteChannel: 'Delete Channel',
      deleteChannelConfirmPart1: 'Are you sure you want to delete the channel',
      deleteChannelConfirmPart2: '? This action cannot be undone and all messages in this channel will be inaccessible.',
      deleting: 'Deleting...',
      // Channel List
      channels: 'Channels',
      newChannel: 'New',
      activeChannels: 'Active Channels',
      unreadMessages: 'Unread Messages',
      noChannelsYet: 'No channels yet',
      createChannelsToOrganize: 'Create channels to organize team communication',
      createFirstChannel: 'Create First Channel',
      muteNotifications: 'Mute Notifications',
      enableNotifications: 'Enable Notifications',
      channelTypes: {
        announcements: 'Announcements',
        general: 'General',
        private: 'Private',
        teamChannels: 'Team Channels',
        management: 'Management',
        system: 'System',
      },
      channelCreatedBy: 'Channel created by {name}',
      // Create Channel Dialog
      createChannel: 'Create Channel',
      createChannelDescription: 'Create a new channel for your team',
      channelTypeLabel: 'Channel Type',
      channelDescriptionLabel: 'Description',
      optional: 'optional',
      enterChannelDescription: 'What is this channel about?',
      creating: 'Creating...',
      channelCreatedSuccess: 'Channel created successfully!',
      failedToCreateChannel: 'Failed to create channel',
      announcementTypeDescription: 'For important club-wide announcements',
      generalTypeDescription: 'Open channel for general discussion',
      privateTypeDescription: 'Invite-only channel for specific members',
      managersTypeDescription: 'Reserved for management team',
      // Message Input
      pressCtrlEnterToSend: 'Press Ctrl+Enter to send',
      typeAtToMention: 'Type @ to mention staff',
      replyingTo: 'Replying to',
      mentionStaffMember: 'Mention staff member:',
      sendNow: 'Send now',
      scheduled: 'Scheduled',
      in1Hour: 'In 1 hour',
      in4Hours: 'In 4 hours',
      tomorrow9Am: 'Tomorrow 9 AM',
      messageTypes: {
        announcement: 'Announcement',
        announcementDesc: 'Important announcement to all staff',
        urgent: 'Urgent',
        urgentDesc: 'Urgent message requiring immediate attention',
        direct: 'Direct',
        directDesc: 'Private message to specific staff member',
        channel: 'Channel',
        channelDesc: 'Regular channel message',
      },
      // StaffDashboard.tsx keys
      staffProfileNotFound: 'Staff Profile Not Found',
      welcomeBack: 'Welcome back',
      hoursThisWeek: 'h this week',
      myTasksLabel: 'My Tasks',
      inProgress: 'in progress',
      availableLabel: 'Available',
      tasksToClaim: 'Tasks to claim',
      thisWeekLabel: 'This Week',
      shiftsWorked: 'shifts worked',
      performanceLabel: 'Performance',
      performanceTrend: '+2% this week',
      tabTasks: 'Tasks',
      tabSchedule: 'Schedule',
      tabMessages: 'Messages',
      myCurrentTasks: 'My Current Tasks',
      tasksAssignedToYou: 'Tasks assigned to you',
      upcomingShifts: 'Upcoming Shifts',
      yourScheduledShifts: 'Your scheduled work shifts',
      availableTasks: 'Available Tasks',
      tasksYouCanClaim: 'Tasks you can claim and work on',
      manageTrackTasks: 'Manage and track your assigned tasks',
      availableToClaim: 'Available to Claim',
      pickUpNewTasks: 'Pick up new tasks',
      mySchedule: 'My Schedule',
      viewManageShifts: 'View and manage your work shifts',
      typeMessage: 'Type your message...',
    },
    // Staff People Management translations (PeopleManagementRedesign.tsx)
    staffPeople: {
      // Access levels
      accessLevels: {
        none: 'No Access',
        noneDescription: 'Cannot access this module',
        read: 'Read',
        readDescription: 'View only, no changes',
        write: 'Write',
        writeDescription: 'Create and edit items',
        full: 'Full',
        fullDescription: 'Full control including delete',
      },
      // Person card status
      status: {
        active: 'Active',
        pending: 'Pending',
        inactive: 'Inactive',
      },
      // Person type
      administrator: 'Administrator',
      staffMember: 'Staff Member',
      // Actions
      permissions: 'Permissions',
      editDetails: 'Edit Details',
      editPermissions: 'Edit Permissions',
      resendInvitation: 'Resend Invitation',
      removeAccess: 'Remove Access',
      // Permission Editor dialog
      editPermissionsTitle: 'Edit Permissions',
      roleTemplate: 'Role Template',
      quickSet: 'Quick Set',
      allNone: 'All None',
      allRead: 'All Read',
      allFull: 'All Full',
      accessSummary: 'Access Summary',
      ownersFullAccess: 'Owners have full access to all features and cannot have permissions modified.',
      noAdditionalCapabilities: 'No additional capabilities',
      savePermissions: 'Save Permissions',
      // Invite modal
      inviteTeamMember: 'Invite Team Member',
      inviteDescription: "Send an invitation to join your club's team. They'll receive an email with instructions.",
      emailAddress: 'Email Address',
      role: 'Role',
      accessLevelsPreview: 'Access levels:',
      andMore: 'more',
      sendInvitation: 'Send Invitation',
      sending: 'Sending...',
      // Role descriptions
      roles: {
        owner: 'Owner',
        ownerDesc: 'Full control over all club settings and team',
        admin: 'Full Admin',
        adminDesc: 'Full access to all features except billing and team',
        eventsManager: 'Events Manager',
        eventsManagerDesc: 'Manage events and announcements',
        coach: 'Coach',
        coachDesc: 'Coaching portal and member view',
        teesheetStaff: 'Tee Sheet Staff',
        teesheetStaffDesc: 'Bookings and check-ins',
        communications: 'Communications',
        communicationsDesc: 'Channels and announcements only',
        readonly: 'Read Only',
        readonlyDesc: 'View-only access to everything',
        custom: 'Custom',
        customDesc: 'Custom permissions set',
      },
      // Stats cards
      stats: {
        total: 'Total',
        active: 'Active',
        pending: 'Pending',
        admins: 'Admins',
        staff: 'Staff',
      },
      // Empty states
      noMatchingTeamMembers: 'No matching team members',
      noTeamMembersYet: 'No team members yet',
      tryAdjustingFilters: "Try adjusting your filters to find who you're looking for.",
      startByInviting: 'Start by inviting administrators or adding staff members to your club.',
      inviteFirstTeamMember: 'Invite First Team Member',
      // Role legend
      roleOverview: 'Role Overview',
      clickToViewPermissions: 'Click to view detailed permissions',
      // Remove dialog
      removeTeamMember: 'Remove Team Member',
      removeConfirmation: 'Are you sure you want to remove {email} from your team? They will lose all access to the club admin portal.',
      // Role preview dialog
      rolePermissions: 'Role Permissions',
      defaultPermissionsDescription: "Default permissions for this role at your club. Customize these to fit your organization's needs.",
      saveRoleTemplate: 'Save Role Template',
      roleUpdated: 'Role Updated',
      roleUpdatedDescription: 'Default permissions for {role} have been updated for your club.',
      // Toast messages
      invitationSent: 'Invitation Sent',
      invitationSentDesc: '{email} has been invited as {role}',
      accessRemoved: 'Access Removed',
      accessRemovedDesc: '{email} has been removed from the team',
      permissionsUpdated: 'Permissions Updated',
      pendingAdminPermissions: 'Pending admin permissions saved. They will have these access levels when they register.',
      accessLevelsSaved: 'Access levels saved successfully.',
      invitationResent: 'Invitation resent',
      newInvitationSent: 'A new invitation was sent to {email}',
      failedToResend: 'Failed to resend',
      couldNotResendInvitation: 'Could not resend invitation. Please try again.',
      // Filters
      allRoles: 'All Roles',
      allStatus: 'All Status',
      searchPlaceholder: 'Search by name or email...',
      // Module labels (for permission matrix)
      modules: {
        dashboard: 'Dashboard',
        dashboardDesc: 'Admin overview and quick stats',
        members: 'Members',
        membersDesc: 'Member profiles and management',
        staff: 'Staff Management',
        staffDesc: 'Staff schedules, tasks, and shifts',
        courses: 'Courses',
        coursesDesc: 'Golf course configuration',
        teesheet: 'Tee Sheet',
        teesheetDesc: 'Tee time bookings and check-ins',
        services: 'Services',
        servicesDesc: 'Range, carts, and locker management',
        revenue: 'Revenue',
        revenueDesc: 'Financial dashboard and transactions',
        events: 'Events',
        eventsDesc: 'Tournaments and club events',
        coaching: 'Coaching',
        coachingDesc: 'Coaching sessions and student management',
        channels: 'Channels',
        channelsDesc: 'Communication channels',
        announcements: 'Announcements',
        announcementsDesc: 'Club-wide announcements',
        feedback: 'Feedback',
        feedbackDesc: 'Member feedback and suggestions',
        analytics: 'Analytics',
        analyticsDesc: 'Reports and insights',
        settings: 'Club Settings',
        settingsDesc: 'Club configuration and billing',
      },
      // Common actions
      cancel: 'Cancel',
      save: 'Save',
      close: 'Close',
      error: 'Error',
      // Nested structures for PeopleManagementRedesign
      emptyState: {
        noMatchingTeamMembers: 'No matching team members',
        noTeamMembersYet: 'No team members yet',
        tryAdjustingFilters: "Try adjusting your filters to find who you're looking for.",
        startByInviting: 'Start by inviting administrators or adding staff members to your club.',
        inviteFirstTeamMember: 'Invite First Team Member',
      },
      filters: {
        role: 'Role',
        status: 'Status',
        allRoles: 'All Roles',
        allStatus: 'All Status',
      },
      toast: {
        invitationResent: 'Invitation resent',
        invitationSentTo: 'A new invitation was sent to',
        failedToResend: 'Failed to resend',
        couldNotResend: 'Could not resend invitation. Please try again.',
        roleUpdated: 'Role Updated',
        defaultPermissionsFor: 'Default permissions for',
        haveBeenUpdated: 'have been updated for your club.',
      },
      removeDialog: {
        title: 'Remove Team Member',
        confirmPart1: 'Are you sure you want to remove',
        confirmPart2: 'from your team?',
        warning: 'They will lose all access to the club admin portal.',
        cancel: 'Cancel',
        removeAccess: 'Remove Access',
      },
      rolePreview: {
        rolePermissions: 'Role Permissions',
        description: "Default permissions for this role at your club. Customize these to fit your organization's needs.",
        ownerFullAccess: 'Owners have full access to all features and cannot have permissions modified.',
        cancel: 'Cancel',
        saveRoleTemplate: 'Save Role Template',
      },
    },
    servicesLockers: {
      totalLockers: 'Total Lockers',
      occupied: 'Occupied',
      available: 'Available',
      expiringSoon: 'Expiring Soon',
      within60Days: 'Within 60 days',
      expired: 'Expired',
      actionNeeded: 'Action needed',
      lockers: 'lockers',
      viewAll: 'View All',
      daysLeft: 'days left',
      recentActivity: 'Recent Activity',
      newRental: 'New Rental',
      locker: 'Locker',
      actions: { renewed: 'Renewed', assigned: 'Assigned', released: 'Released', request: 'Request' },
      zoneOverview: 'Zone Overview',
      viewInventory: 'View Inventory',
      ofTotal: 'of {{total}} lockers',
      tabs: { overview: 'Overview', inventory: 'Inventory', rentals: 'Rentals', pricing: 'Pricing', reports: 'Reports' },
      inventory: {
        demoDataMode: 'Demo Data Mode',
        demoDataDesc: "You're viewing sample data. Create zones and lockers to see real data.",
        maintenance: 'Maintenance',
        filters: 'Filters',
        allZones: 'All Zones',
        allStatus: 'All Status',
        statusAvailable: 'Available',
        statusOccupied: 'Occupied',
        statusExpiring: 'Expiring Soon',
        statusMaintenance: 'Maintenance',
        legendExpiring: 'Expiring',
        legendMaint: 'Maint.',
        availableOfTotal: '{{available}} available of {{total}}',
        premium: 'Premium',
        standard: 'Standard',
        status: 'Status',
        zone: 'Zone',
        type: 'Type',
        assignedMember: 'Assigned Member',
        expires: 'Expires',
        assignLocker: 'Assign Locker',
        markAvailable: 'Mark Available',
        sendReminder: 'Send Reminder',
        release: 'Release',
        markForMaintenance: 'Mark for Maintenance',
        lockerTitle: 'Locker {{id}}',
      },
      rentals: {
        demoDataMode: 'Demo Data Mode',
        demoDataDesc: "You're viewing sample data. Create rental plans and assign lockers to see your actual data here.",
        loadingRentals: 'Loading locker rentals...',
        activeRentals: 'Active Rentals',
        pendingRequests: 'Pending Requests',
        currentlyAssigned: 'Currently assigned',
        needsAttention: 'Needs attention',
        awaitingAssignment: 'Awaiting assignment',
        member: 'Member',
        plan: 'Plan',
        expires: 'Expires',
        status: 'Status',
        actions: 'Actions',
        allStatus: 'All Status',
        active: 'Active',
        expiring: 'Expiring',
        expired: 'Expired',
        pending: 'Pending',
        waitlist: 'Waitlist',
        renew: 'Renew',
        daysOverdue: '{{count}} days overdue',
        daysLeft: '{{count}} days left',
        autoRenew: 'Auto-renew',
        newLockerRental: 'New Locker Rental',
        newRental: 'New Rental',
        memberLabel: 'Member',
        lockerZone: 'Locker Zone',
        availableLocker: 'Available Locker',
        rentalPlan: 'Rental Plan',
        selectMember: 'Select member',
        selectZone: 'Select zone',
        selectLocker: 'Select locker',
        selectPlan: 'Select plan',
        startDate: 'Start Date',
        enableAutoRenewal: 'Enable auto-renewal',
        cancel: 'Cancel',
        assignLocker: 'Assign Locker',
        zoneLabel: 'Zone',
        planLabel: 'Plan',
        assign: 'Assign',
        notify: 'Notify',
        rentalPlans: 'Rental Plans',
        addPlan: 'Add Plan',
        duration: 'Duration',
        price: 'Price',
        yes: 'Yes',
        no: 'No',
        renewLockerRental: 'Renew Locker Rental',
        currentPlan: 'Current plan',
        renewalPlan: 'Renewal Plan',
        newExpiryDate: 'New Expiry Date',
        amountDue: 'Amount Due',
        sendInvoiceToMember: 'Send invoice to member',
        processRenewal: 'Process Renewal',
        unknownMember: 'Unknown Member',
        unknownZone: 'Unknown Zone',
        unknownPlan: 'Unknown Plan',
        anyZone: 'Any Zone',
        newRentalPlan: 'New Rental Plan',
        planName: 'Plan Name',
        planNamePlaceholder: 'e.g., Annual Premium',
        lockerType: 'Locker Type',
        selectType: 'Select type',
        selectDuration: 'Select',
        month: 'month',
        months: 'months',
        defaultAutoRenew: 'Enable auto-renewal by default',
        createPlan: 'Create Plan',
        premium: 'Premium',
        standard: 'Standard',
      },
      pricing: {
        rentalPlans: 'Rental Plans',
        configurePlansSubtitle: 'Configure locker rental plans and pricing',
        addPlan: 'Add Plan',
        noPlansConfigured: 'No rental plans configured',
        addFirstPlan: 'Add your first plan to get started',
        deletePlan: 'Delete Plan',
        deletePlanConfirm: 'Are you sure you want to delete "{{name}}"? This will not affect existing rentals.',
        deleteRule: 'Delete Rule',
        deleteRuleConfirm: 'Are you sure you want to delete "{{name}}"?',
        dynamicPricingRules: 'Dynamic Pricing Rules',
        seasonalDiscountsSubtitle: 'Seasonal promotions and special discounts',
        addRule: 'Add Rule',
        noRulesConfigured: 'No pricing rules configured',
        addDiscounts: 'Add seasonal or promotional discounts',
        memberPricingTiers: 'Member Pricing Tiers',
        configureTiersSubtitle: 'Configure discounts by membership level',
        enabled: 'Enabled',
        disabled: 'Disabled',
        offRentalPrice: 'off rental price',
        enableToConfigure: 'Enable to configure membership tier discounts',
        editRentalPlan: 'Edit Rental Plan',
        addRentalPlan: 'Add Rental Plan',
        updatePlanConfig: 'Update the configuration for this plan',
        configureNewPlan: 'Configure a new locker rental plan',
        planName: 'Plan Name',
        duration: 'Duration',
        priceEur: 'Price (€)',
        perMonth: 'per month',
        lockerType: 'Locker Type',
        enableAutoRenewalDefault: 'Enable auto-renewal by default',
        planActiveAvailable: 'Plan is active and available for new rentals',
        updatePlan: 'Update Plan',
        createPlan: 'Create Plan',
        editPricingRule: 'Edit Pricing Rule',
        addPricingRule: 'Add Pricing Rule',
        configurePromotionalDiscounts: 'Configure promotional or seasonal discounts',
        ruleName: 'Rule Name',
        ruleType: 'Rule Type',
        startDateOptional: 'Start Date (optional)',
        endDateOptional: 'End Date (optional)',
        adjustment: 'Adjustment',
        negativeDiscountPositiveSurcharge: 'Negative = discount, Positive = surcharge',
        percentage: 'Percentage (%)',
        fixedAmount: 'Fixed Amount (€)',
        appliesTo: 'Applies To',
        allLockers: 'All Lockers',
        standardLockersOnly: 'Standard Lockers Only',
        premiumLockersOnly: 'Premium Lockers Only',
        descriptionOptional: 'Description (optional)',
        editMemberTierDiscount: 'Edit Member Tier Discount',
        discountPercentage: 'Discount Percentage',
        membersReceiveOff: 'Members in this tier will receive {{percent}}% off the rental price.',
        exampleCalculation: 'Example Calculation',
        saveChanges: 'Save Changes',
        duration1Month: '1 Month',
        duration3Months: '3 Months',
        duration6Months: '6 Months',
        duration12Months: '12 Months (Annual)',
        lockerTypeAny: 'Any Locker',
        lockerTypeStandard: 'Standard Only',
        lockerTypePremium: 'Premium Only',
        ruleTypeSeasonal: 'Seasonal',
        ruleTypePromotional: 'Promotional',
        ruleTypeRenewal: 'Renewal Discount',
        ruleTypeBulk: 'Multi-Year Discount',
        noDateRestriction: 'No date restriction',
        lockersOnly: 'lockers only',
        cancel: 'Cancel',
        type: 'Type',
        ruleNamePlaceholder: 'e.g., Summer Promotion',
        descriptionPlaceholder: 'e.g., Early bird discount for new members',
      },
      reports: {
        demoDataMode: 'Demo Data Mode',
        demoDataDesc: "You're viewing sample analytics data for demonstration purposes. Real data will appear once you add locker zones, rental plans, and assign lockers to members.",
        sampleAnalytics: 'Sample Analytics',
        lockerAnalytics: 'Locker Analytics',
        trackOccupancyRevenue: 'Track occupancy, revenue, and rental trends',
        last3Months: 'Last 3 Months',
        last6Months: 'Last 6 Months',
        last12Months: 'Last 12 Months',
        yearToDate: 'Year to Date',
        export: 'Export',
        totalRevenue: 'Total Revenue',
        last6MonthsSubtitle: 'Last 6 months',
        overallOccupancy: 'Overall Occupancy',
        currentlyAssigned: 'Currently assigned',
        renewalRate: 'Renewal Rate',
        avgMonthly: 'Avg. monthly',
        revenueTrend: 'Revenue Trend',
        avgPerMonth: 'Avg: €{{amount}}/month',
        renewalsNew: '{{renewals}} renewals, {{new}} new',
        zoneOccupancy: 'Zone Occupancy',
        planDistribution: 'Plan Distribution',
        totalRevenue6mo: 'Total Revenue (6mo)',
        renewalForecast: 'Renewal Forecast',
        renewalsCount: '{{count}} renewals',
        estRevenue: 'Est. revenue',
        projected4Months: 'Projected (4 months)',
        loyalMembers: 'Loyal Members',
        activeRentals: 'Active Rentals',
        active: 'Active',
        expired: 'Expired',
        expiringSoon: 'Expiring Soon',
        available: 'Available',
        years: 'years',
        statusSummary: 'Status Summary',
      },
    },
    // Member 360 page
    member360: {
      // Page title and loading states
      pageTitle: 'Member Profile',
      loadingProfile: 'Loading Member Profile',
      loadingMessage: 'Please wait while we gather the member information...',
      memberNotFound: 'Member Not Found',
      noMemberIdProvided: 'No member ID provided in the URL.',
      errorLoadingProfile: 'There was an error loading the member profile. Please try again.',
      backToMembers: 'Back to Members',
      tryAgain: 'Try Again',

      // Tabs
      tabActivity: 'Activity',
      tabRestaurant: 'Restaurant',
      tabProShop: 'Pro Shop',
      tabGolfStats: 'Golf Stats',
      tabContactHistory: 'Contact History',

      // Header section
      memberSince: 'Member since',
      message: 'Message',
      messageShort: 'Msg',
      opening: 'Opening...',
      emailAction: 'Email',
      emailShort: 'Mail',
      handicap: 'Handicap',
      totalRounds: 'Total Rounds',
      clubPoints: 'Club Points',
      lifetimeValue: 'Lifetime Value',
      monthlySpend: 'Monthly Spend',
      engagementScore: 'Engagement Score',
      memberBadge: 'Member',

      // Playing Activity section
      playingActivity: 'Playing Activity',
      detailedAnalytics: 'Detailed Analytics',
      golfPerformanceActivity: 'Golf Performance & Activity',
      golfPerformanceDescription: 'Handicap trends, scoring patterns, playing frequency',

      // Engagement Health section
      engagementHealth: 'Engagement Health',
      engagementDescription: 'Activity across all club dimensions',
      overallScore: 'Overall Score',
      adminInsight: 'Admin Insight',
      clubAvg: 'Club avg',
      retentionRisk: 'Retention Risk',
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      biggestOpportunity: 'Biggest Opportunity',
      lowestDimension: 'Lowest dimension',
      pointsPossible: 'points possible',
      improving: 'Improving',
      declining: 'Declining',
      stable: 'Stable',
      activeAcrossDimensions: 'Active across multiple dimensions',
      someDimensionsNeedAttention: 'Some dimensions need attention',
      lowActivityReachOut: 'Low activity — reach out soon',

      // Engagement dimensions
      golf: 'Golf',
      social: 'Social',
      socialEngagement: 'Social engagement',
      communication: 'Communication',
      community: 'Community',
      communityInvolvement: 'Community involvement',

      // Event History section
      eventHistoryTitle: 'Event History & Participation',
      eventHistoryDescription: 'Attendance patterns, event mix, upcoming registrations',
      attended: 'attended',
      attendanceRate: 'rate',
      noEvents: '0 events',
      attendanceLast12Months: 'Attendance — Last 12 Months',
      registered: 'Registered (upcoming)',
      totalAttended: 'Total attended',
      upcoming: 'Upcoming',
      monthlyAvg: 'Monthly avg',
      eventPreference: 'Event Preference',
      favourite: 'Favourite',
      eventTimeline: 'Event Timeline',
      recent: 'Recent',
      noRecentEvents: 'No recent events.',
      eventParticipationWillAppear: 'Event participation will appear here when the member registers for events',

      // Admin Notes section
      adminNotes: 'Admin Notes',
      adminNotesDescription: 'Internal comments, observations, and follow-ups',

      // Restaurant section
      restaurantSpending: 'Restaurant Spending',
      noRestaurantTransactions: 'No Restaurant Transactions',
      restaurantHistoryWillAppear: 'Restaurant transaction history will appear here when available.',

      // Pro Shop section
      proShopPurchases: 'Pro Shop Purchases',
      noProShopPurchases: 'No Pro Shop Purchases',
      proShopHistoryWillAppear: 'Pro shop purchase history will appear here when available.',

      // Contact History section
      contactHistory: 'Contact History',
      interactions: 'interactions',
      noInteractionsYet: 'No interactions yet',
      contactHistoryWillAppear: 'Contact history will appear here when staff interact with this member',
      unknown: 'Unknown',

      // Contact type labels
      typeReplies: 'Replies',
      typeTasks: 'Tasks',
      typeCompleted: 'Completed',
      typeResolved: 'Resolved',
      typeEmails: 'Emails',
      typeCalls: 'Calls',
      typeMessages: 'Messages',
      typeInquiries: 'Inquiries',
      typeNotifications: 'Notifications',
      typeRoundReports: 'Round Reports',
      typePhotoReports: 'Photo Reports',

      // Time labels
      today: 'Today',
      yesterday: 'Yesterday',
      daysAgo: '{count} days ago',
      weeksAgo: '{count} weeks ago',

      // Member360 components
      recentActivity: 'Recent Activity',
      lastActiveAgo: 'Last active {time} ago',
      noActivityFound: 'No Activity Found',
      unableToLoadActivityData: 'Unable to load activity data',
      activityWillAppear: 'Activity will appear here once the member starts playing rounds or engaging with the club',

      // Member360 profile header
      overview: 'Overview',
      averageScore: 'Average Score',
      bestScore: 'Best Score',
      performanceTrends: 'Performance Trends',
      roundsAnalyzed: 'rounds analyzed',
      noRoundsRecorded: 'This member hasn\'t recorded any rounds yet.',
      rounds: 'rounds',
      filters: 'Filters',
      courses: 'Courses',
      fromDate: 'From Date',
      toDate: 'To Date',
      scoreRange: 'Score Range',
      clearFilters: 'Clear Filters',
      noRoundsMatchFilters: 'No rounds match your current filters. Try adjusting the criteria.',
      communicationEngagement: 'Communication Engagement',
      eventParticipation: 'Event Participation',
      memberArchetype: 'Member Archetype',

      // Toast messages
      editMember: 'Edit Member',
      editMemberComingSoon: 'Member editing functionality will be available soon.',
      sendMessage: 'Send Message',
      sendMessageComingSoon: 'Messaging functionality will be available soon.',
      emailDraft: 'Email Draft',
      openingEmailComposer: 'Opening email composer...',
      addNote: 'Add Note',
      openingNoteEditor: 'Opening note editor...',
      statusUpdated: 'Status Updated',
      statusChangedTo: 'Member status changed to {status}.',
      failedToUpdateStatus: 'Failed to update member status.',
      roleUpdated: 'Role Updated',
      roleChangedTo: 'Member role changed to {role}.',
      failedToUpdateRole: 'Failed to update member role.',

      // Member Management Actions panel
      memberManagement: 'Member Management',
      statusManagement: 'Status Management',
      currentStatus: 'Current Status',
      activate: 'Activate',
      deactivate: 'Deactivate',
      role: 'Role',
      adminAccess: 'Admin Access',
      removeAdmin: 'Remove Admin',
      makeAdmin: 'Make Admin',
      memberDetails: 'Member Details',
      membershipTier: 'Membership Tier',
      email: 'Email',
      phone: 'Phone',
      joined: 'Joined',
      renewalDate: 'Renewal Date',
      missing: 'missing',
      privacyPermissions: 'Privacy & Permissions',
      profileVisibility: 'Profile Visibility',
      public: 'Public',
      private: 'Private',
      gdprNotice: 'All member data access is logged for audit purposes and complies with GDPR regulations.',
      dangerZone: 'Danger Zone',
      suspendMember: 'Suspend Member',
      suspendNotice: 'Suspended members cannot access the club facilities or online services. This action can be reversed by activating the member again.',
      insightsAlerts: 'Insights & Alerts',
      heavyCartUser: 'Heavy Cart User',
      heavyCartUserDesc: 'Rents cart 90% of rounds. Consider annual cart membership upsell.',
      sendCartOffer: 'Send Cart Membership Offer',
      vipMember: 'VIP Member',
      vipMemberDesc: 'Top 10% spender. Lifetime value: {amount}',
      eventParticipationInsight: 'Event Participation',
      eventParticipationDesc: 'Registered for {count} upcoming events. Strong tournament player.',
      invitePremiumEvents: 'Invite to Premium Events',

      // Performance Charts
      questProgression: 'Quest Progression',
      noQuestDataAvailable: 'No quest data available',
      noQuestsTrackedYet: 'No quests tracked yet',
      questCompletionPercentage: 'Completion percentage per quest achievement',
      questsComplete: 'complete',
      completed: 'Completed!',
      progress: 'Progress',
      last6Months: 'Last 6 Months',
      last12Months: 'Last 12 Months',
      last24Months: 'Last 24 Months',
      allTime: 'All Time',
      total: 'Total',
      handicapDelta: 'HCP Δ',
      trend: 'Trend',
      active: 'Active',
      noData: 'No Data',
      noPerformanceData: 'No Performance Data',
      performanceTrendsWillAppear: 'Performance trends will appear here once enough rounds have been recorded.',
      handicapProgression: 'Handicap Progression',
      handicapDescription: 'Lower is better - downward trend shows improvement',
      handicapDataNotAvailable: 'Handicap data not available',
      noHandicapDataForPeriod: 'No handicap data available for this period',
      averageScoreTrend: 'Average Score Trend',
      averageScoreDescription: 'Monthly average scores over time',
      scoreDataNotAvailable: 'Score data not available',
      avgScore: 'Avg Score',
      noScoreDataForPeriod: 'No score data available for this period',
      playingFrequency: 'Playing Frequency',
      playingFrequencyDescription: 'Number of rounds played each month',
      noRoundsDataAvailable: 'No rounds data available',
      roundsPlayed: 'Rounds Played',
      noRoundsDataForPeriod: 'No rounds data available for this period',

      // Event Participation Details
      attendedBadge: 'attended',
      rateBadge: 'rate',
      eventsCount: 'events',
      attendanceLast12MonthsChart: 'Attendance — Last 12 Months',
      attendedLegend: 'Attended',
      registeredUpcomingLegend: 'Registered (upcoming)',
      totalAttendedStat: 'Total attended',
      showUpRateStat: 'Show-up rate',
      upcomingStat: 'Upcoming',
      monthlyAvgStat: 'Monthly avg',
      eventPreferenceTitle: 'Event Preference',
      favouriteLabel: 'Favourite',
      eventTimelineTitle: 'Event Timeline',
      upcomingSection: 'Upcoming',
      recentSection: 'Recent',
      untitledEvent: 'Untitled event',
      unknownType: 'unknown',
      registeredStatus: 'registered',
      attendedStatus: 'attended',
      cancelledStatus: 'cancelled',
      noRecentEventsMessage: 'No recent events.',
      viewAllEvents: 'View all {count} events',
      lastCalculated: 'Last calculated',
      lastCalculatedJustNow: 'Last calculated just now',
      refreshButton: 'Refresh',

      // Recent Activity
      postedPrefix: 'Posted:',
      socialActivityDesc: 'Social activity',
      lastActivePrefix: 'Last active',
      agoSuffix: 'ago',

      // Quest drawer
      questBreakdownEyebrow: 'Quest breakdown',
      questsDrawerLabel: 'Quests',
      questDoneLabel: 'Done',
      tierCompleteLabel: 'Tier complete',
      noQuestsFoundForLeague: 'No quests found for this league',
      noQuestsStartedYet: 'No quests started yet',
      questsWillAppear: 'Quests will appear here once the member begins their journey',
      noHandicapDataYet: 'No handicap data yet',
      handicapWillAppear: 'Handicap progression will appear after rounds are logged',
      noScoreDataYet: 'No score data yet',
      scoreWillAppear: 'Average score trend appears after rounds are submitted',
      noRoundsLoggedYet: 'No rounds logged yet',
      frequencyWillAppear: 'Playing frequency will track once rounds are recorded',

      // Activity table labels
      scoreLabel: 'Score',
      performanceLabel: 'Performance',
      groupLabel: 'Group',
      soloLabel: 'Solo',
      hcpLabel: 'HCP',
      teesLabel: 'Tees',
      showingPagination: 'Showing {from} to {to} of {total} rounds',
      previousPage: 'Previous',
      nextPage: 'Next',

      // Communication Panel
      commPanelDescription: 'Messaging patterns, response times, and communication quality',
      tabOverview: 'Overview',
      tabPatterns: 'Patterns',
      tabQuality: 'Quality',
      tabNetwork: 'Network',
      commScore: 'Communication Score',
      percentileLabel: 'Percentile',
      rankLabel: 'Rank',
      msgSentLabel: 'Messages',
      sentThisPeriod: 'sent this {period}',
      conversationsStartedLabel: 'Conversations',
      conversationsStarted: 'started',
      responseRateLabel: 'Response Rate',
      responseRateDesc: 'response rate',
      avgResponseLabel: 'Avg Response',
      avgResponseDesc: 'average time',
      groupParticipationLabel: 'Group Participation',
      groupConversations: 'group conversations',
      helpfulResponsesLabel: 'Helpful Responses',
      helpfulAnswers: 'helpful answers',
      recentActivityLabel: 'Recent Activity',
      pointsSuffix: 'points',
      participantsSuffix: 'participants',
      responseTimePatterns: 'Response Time Patterns',
      responseSpeedDist: 'Response Speed Distribution',
      responsesLabel: 'responses',
      conversationLengthsLabel: 'Conversation Lengths',
      avgLengthLabel: 'Average Length',
      messagesCountSuffix: 'messages',
      shortestLabel: 'Shortest',
      longestLabel: 'Longest',
      weeklyActivityPattern: 'Weekly Activity Pattern',
      commQuality: 'Communication Quality',
      helpfulnessLabel: 'Helpfulness',
      clarityLabel: 'Clarity',
      positivityLabel: 'Positivity',
      professionalismLabel: 'Professionalism',
      mentorshipActivityLabel: 'Mentorship Activity',
      questionsAnsweredLabel: 'Questions Answered',
      helpProvidedLabel: 'Help Provided',
      newMembersHelpedLabel: 'New Members Helped',
      commTopicsLabel: 'Communication Topics',
      networkReachLabel: 'Network Reach',
      discussionInfluenceLabel: 'Discussion Influence',
      bridgeConnectionsLabel: 'Bridge Connections',
      primaryContactsLabel: 'Primary Contacts',
      avgResponseContact: 'avg response',
      commCirclesLabel: 'Communication Circles',
      membersCountSuffix: 'members',
      commInsightsLabel: 'Communication Insights',
      strengthsLabel: 'Strengths',
      recommendationsLabel: 'Recommendations',
      quickActionsLabel: 'Quick Actions',
      sendMessageAction: 'Send Message',
      startConversationAction: 'Start Conversation',
      provideHelpAction: 'Provide Help',
      refreshDataAction: 'Refresh Data',

      // MemberArchetypePanel
      archetypeBehavioralAnalysis: 'Behavioral analysis and personalized member development insights',
      tabClassification: 'Classification',
      tabInsights: 'Insights',
      tabGuidance: 'Guidance',
      tabEvolution: 'Evolution',
      confidenceSuffix: '% Confidence',
      secondaryTraits: 'Secondary Traits',
      coreCharacteristics: 'Core Characteristics',
      primaryTraitsLabel: 'Primary Traits',
      motivationsLabel: 'Motivations',
      behaviorPatternsLabel: 'Behavior Patterns',
      preferredActivitiesLabel: 'Preferred Activities',
      archetypeScoring: 'Archetype Scoring',
      activityPatternsLabel: 'Activity Patterns',
      engagementPreferencesLabel: 'Engagement Preferences',
      peakTimesLabel: 'Peak Times',
      communicationStyleLabel: 'Communication Style',
      socialStyleLabel: 'Social Style',
      decisionMakingLabel: 'Decision Making',
      dmStyleLabel: 'Style',
      dmTimeframeLabel: 'Timeframe',
      keyFactorsLabel: 'Key Factors:',
      learningDevelopmentLabel: 'Learning & Development',
      learningStyleLabel: 'Learning Style',
      learningPaceLabel: 'Learning Pace',
      feedbackStyleLabel: 'Feedback Style',
      motivationalProfileLabel: 'Motivational Profile',
      primaryDriversLabel: 'Primary Drivers',
      secondaryDriversLabel: 'Secondary Drivers',
      potentialDemotivatorsLabel: 'Potential Demotivators',
      strengthsToLeverageLabel: 'Strengths to Leverage',
      growthChallengesLabel: 'Growth Challenges',
      developmentPathwayLabel: 'Development Pathway',
      currentFocusAreasLabel: 'Current Focus Areas',
      nextStepsLabel: 'Next Steps',
      longTermGoalsLabel: 'Long-term Goals',
      communityContributionLabel: 'Community Contribution Opportunities',
      idealRolesLabel: 'Ideal Roles',
      leadershipOpportunitiesLabel: 'Leadership Opportunities',
      engagementStrategiesLabel: 'Personalized Engagement Strategies',
      strategyNumberPrefix: 'Strategy #',
      archetypeEvolutionLabel: 'Archetype Evolution',
      confidenceLabel: 'Confidence',
      stabilityLabel: 'Stability',
      maturityLevelLabel: 'Maturity Level',
      progressionLabel: 'Progression',
      evolutionHistoryLabel: 'Evolution History',
      presentLabel: 'Present',
      confidenceSuffixFull: '% confidence',
      potentialEvolutionPathsLabel: 'Potential Evolution Paths',
      probabilitySuffix: '% probability',
      requiredChangesLabel: 'Required Changes:',
      benefitsLabel: 'Benefits:',
      nextMilestonesLabel: 'Next Milestones',
      performanceComparisonsLabel: 'Performance Comparisons',
      vsArchetypeAverageLabel: 'vs. Archetype Average',
      vsClubAverageLabel: 'vs. Club Average',
      notIdentifiedLabel: 'Not identified',

      // EventParticipationPanel
      eventAttendanceCommunity: 'Event attendance, organization, and community involvement',
      tabOverviewLabel: 'Overview',
      tabPerformanceLabel: 'Performance',
      tabCommunityLabel: 'Community',
      tabInsightsLabel: 'Insights',
      eventParticipationScoreLabel: 'Event Participation Score',
      percentileSuffix: 'Percentile: {n}th',
      rankSuffix: 'Rank: #{n}',
      attendedLabel: 'Attended',
      eventsThisPeriod: 'events this {period}',
      organizedLabel: 'Organized',
      eventsOrganizedLabel: 'events organized',
      volunteeredLabel: 'Volunteered',
      volunteerSessionsLabel: 'volunteer sessions',
      attendanceRateLabel: 'Attendance',
      attendanceRateDesc: 'attendance rate',
      eventTypesParticipationLabel: 'Event Types Participation',
      tournamentsLabel: 'Tournaments',
      wonLabel: 'Won',
      socialEventsLabel: 'Social Events',
      educationalLabel: 'Educational',
      knowledgeSharedLabel: 'Knowledge Shared',
      volunteerLabel: 'Volunteer',
      hoursLabel: 'Hours',
      leadershipLabel: 'Leadership',
      noCompetitiveEvents: 'No competitive events to display',
      participantsLabel: 'participants',
      scoreColLabel: 'Score:',
      eventsOrganizedSection: 'Events Organized',
      upcomingCommitmentsLabel: 'Upcoming Commitments',
      attendeesLabel: 'attendees',
      leadershipRolesLabel: 'Leadership Roles',
      presentSuffix: ' - Present',
      responsibilitiesLabel: 'Responsibilities:',
      mentorshipActivitySection: 'Mentorship Activity',
      membersMentoredLabel: 'Members Mentored',
      sessionsConductedLabel: 'Sessions Conducted',
      successStoriesLabel: 'Success Stories',
      averageRatingLabel: 'Average Rating',
      volunteerContributionsLabel: 'Volunteer Contributions',
      totalHoursLabel: 'Total Hours',
      volunteerRankLabel: 'Volunteer Rank',
      impactAreasLabel: 'Impact Areas:',
      committeeParticipationLabel: 'Committee Participation',
      strengthsSection: 'Strengths',
      opportunitiesSection: 'Opportunities',
      eventRecommendationsLabel: 'Event Recommendations',
      leadershipPotentialLabel: 'Leadership Potential',
      quickActionsSection: 'Quick Actions',
      registerForEventBtn: 'Register for Event',
      volunteerBtn: 'Volunteer',
      takeLeadershipRoleBtn: 'Take Leadership Role',
      refreshDataBtn: 'Refresh Data',

      // SocialEngagementPanel
      tabContent: 'Content',
      socialActivityNetworkDesc: 'Social activity, content performance, and network engagement',
      socialEngagementScoreLabel: 'Social Engagement Score',
      postsLabel: 'Posts',
      likesLabel: 'Likes',
      likesReceivedLabel: 'received',
      sharesLabel: 'Shares',
      sharesTotalLabel: 'total',
      reachLabel: 'Reach',
      reachPeopleLabel: 'people',
      contentPerformanceLabel: 'Content Performance',
      noPostsLabel: 'No posts to display',
      contentTypesLabel: 'Content Types',
      postsSuffix: 'posts',
      avgEngagementLabel: 'avg engagement',
      networkOverviewLabel: 'Network Overview',
      connectionsLabel: 'Connections',
      influenceScoreLabel: 'Influence Score',
      topConnectionsLabel: 'Top Connections',
      scoreSuffix: 'Score:',
      networkGrowthLabel: 'Network Growth',
      personalizedRecommendationsLabel: 'Personalized Recommendations',
      nextGoalsLabel: 'Next Goals',
      createPostBtn: 'Create Post',
      connectWithMembersBtn: 'Connect with Members',

      // ComprehensiveEngagementView
      comprehensiveEngagement: 'Comprehensive Engagement',
      liveLabel: 'Live',
      multiDimensionalDesc: 'Multi-dimensional engagement analysis and insights',
      updatedLabel: 'Updated',
      overallEngagementScoreLabel: 'Overall Engagement Score',
      engagementTierLabel: 'Engagement Tier',
      classificationConfidenceLabel: 'Classification Confidence',
      tierProgressLabel: 'Tier Progress',
      pointsToNextTier: '{n} points to next tier',
      currentLabel: 'Current: {n}',
      nextTierLabel: 'Next Tier: {n}',
      recentAchievementsLabel: 'Recent Achievements',
      tabBreakdown: 'Breakdown',
      weightLabel: 'Weight: {n}% • Score: {n2}',
      lastActivityLabel: 'Last activity:',
      keyStrengthsLabel: 'Key Strengths',
      growthOpportunitiesLabel: 'Growth Opportunities',
      actionRecommendedLabel: 'Action Recommended',
      nextMilestonesSection: 'Next Milestones',
      actionCenterLabel: 'Action Center',
      playGolfBtn: 'Play Golf',
      socializeBtn: 'Socialize',
      communicateBtn: 'Communicate',
      joinEventsBtn: 'Join Events',
      engagementFailedToLoad: 'Failed to load comprehensive engagement data',
      noEngagementData: 'No engagement data available',
      engagementRetry: 'Retry',

      // MemberEngagementPanel
      unableToLoadEngagementData: 'Unable to load member engagement data.',
      golfDimensionLabel: 'Golf (25%)',
      socialDimensionLabel: 'Social (25%)',
      communicationDimensionLabel: 'Communication (25%)',
      communityDimensionLabel: 'Community (25%)',
      roundsMetricLabel: 'Rounds:',
      avgScoreMetricLabel: 'Avg Score:',
      consistencyMetricLabel: 'Consistency:',
      postsMetricLabel: 'Posts:',
      likesGivenMetricLabel: 'Likes Given:',
      commentsMetricLabel: 'Comments:',
      messagesSentMetricLabel: 'Messages:',
      conversationsMetricLabel: 'Conversations:',
      responseRateMetricLabel: 'Response Rate:',
      eventsAttendedMetricLabel: 'Events:',
      eventsOrganizedMetricLabel: 'Organized:',
      volunteerHoursMetricLabel: 'Volunteer Hours:',
      overallEngagement: 'Overall Engagement',
      comprehensiveEngagementDesc: 'Comprehensive engagement across all dimensions',
      outOf100: 'out of 100',
      lastUpdatedLabel: 'Last updated:',
      engagementDimensions: 'Engagement Dimensions',
      performanceAcrossDimensions: 'Performance across all areas of club engagement',
      clickForDetailedBreakdown: 'Click for detailed breakdown →',
      memberArchetypeLabel: 'Member Archetype',
      classificationBasedOnPatterns: 'Classification based on engagement patterns',
      strengthsTitle: 'Strengths',
      suggestionsTitle: 'Suggestions',
      engagementActions: 'Engagement Actions',
      quickActionsToImprove: 'Quick actions to improve member engagement',
      sendPersonalMessageBtn: 'Send Personal Message',
      sendPersonalMessageDesc: 'Reach out directly to the member',
      suggestActivitiesBtn: 'Suggest Activities',
      suggestActivitiesDesc: 'Recommend based on archetype',
      scheduleFollowUpBtn: 'Schedule Follow-up',
      scheduleFollowUpDesc: 'Set reminder for engagement check',
      viewFullHistoryBtn: 'View Full History',
      viewFullHistoryDesc: 'See complete engagement timeline',
      trendImproving: 'Improving',
      trendDeclining: 'Declining',
      trendStable: 'Stable',

      // MemberActivityOverview
      performanceMetrics: 'Performance Metrics',
      performanceMetricsDesc: 'Key performance indicators for the last 30 days',
      totalActivitiesLabel: 'Total Activities',
      avgEngagementStat: 'Avg Engagement',
      consistencyScoreLabel: 'Consistency Score',
      activityRegularity: 'Activity regularity',
      recentActivityFeed: 'Recent Activity',
      latestActivitiesDesc: 'Latest member activities and engagement',
      filterLabel: 'Filter',
      allActivitiesOption: 'All Activities',
      noRecentActivitiesFound: 'No recent activities found',
      noActivitiesMessage: 'This member has not recorded any activities recently.',
      noFilteredActivities: 'No {type} activities found.',
      viewAllActivitiesBtn: 'View All Activities ({count})',
      activityPatternsTitle: 'Activity Patterns',
      activityPatternsDesc: 'When this member is most active',
      mostActiveDays: 'Most Active Days',
      peakActivityHours: 'Peak Activity Hours',
      morningLabel: 'Morning',
      lateMorningLabel: 'Late Morning',
      afternoonLabel: 'Afternoon',
      lateAfternoonLabel: 'Late Afternoon',
      eveningLabel: 'Evening',
      nightLabel: 'Night',
      lastActivityPrefix: 'Last:',
      noRecentActivity: 'No recent activity',

      // MemberProfileHeader
      contactInfoLabel: 'Contact Info',
      membershipLabel: 'Membership',
      joinedLabel: 'Joined',
      unknownJoinDate: 'Unknown',
      daysAsMember: '{n} days as member',
      emergencyContactLabel: 'Emergency:',
      engagementSectionLabel: 'Engagement',
      overallScoreStat: 'Overall Score',
      engagementTierStat: 'Engagement Tier',
      memberTypeLabel: 'Member Type',
      recentActivitySection: 'Recent Activity',
      impactScoreLabel: 'Impact Score',
      noRecentActivityRecorded: 'No recent activity recorded',
      engagementBreakdownLabel: 'Engagement Breakdown',
      memberNumberLabel: 'Member #',
      memberTierSuffix: 'Member',
      hcpSuffix: 'HCP',
      messageBtn: 'Message',
      editBtn: 'Edit',
      lessThanHourAgo: 'Less than an hour ago',
      hoursAgoSuffix: '{n} hours ago',
      daysAgoSuffix: '{n} days ago',

      // MemberActivityTimeline
      activityTimeline: 'Activity Timeline',
      chronologicalViewDesc: 'Chronological view of member engagement activities',
      newestFirst: 'Newest First',
      oldestFirst: 'Oldest First',
      totalActivitiesStat: 'Total Activities',
      totalImpactStat: 'Total Impact',
      avgImpactStat: 'Avg Impact',
      categoriesStat: 'Categories',
      filterLabel2: 'Filter:',
      allTypesOption: 'All Types',
      activitiesCountLabel: '{n} activities',
      impactSuffix: 'impact',
      engagementSuffix: 'engagement',
      locationLabel: 'Location:',
      durationLabel: 'Duration:',
      durationMinSuffix: 'min',
      scoreDetailLabel: 'Score:',
      handicapChangeLabel: 'Handicap:',
      participantsDetailLabel: 'Participants:',
      contextLabel: 'Context:',
      engagementImpactLabel: 'Engagement Impact:',
      noActivitiesFoundTitle: 'No activities found',
      noActivitiesFoundDesc: 'No activities found for the selected time period.',
      noFilteredActivitiesDesc: 'No {type} activities found for the selected time period.',
      justNow: 'Just now',

      // MemberPerformanceCharts
      tabTrends: 'Trends',
      tabComparison: 'Comparison',
      tabGoals: 'Goals',
      timeframeLabel: 'Timeframe:',
      threeMonths: '3 Months',
      sixMonths: '6 Months',
      twelveMonths: '12 Months',
      engagementScoreTrends: 'Engagement Score Trends',
      historicalScoresDesc: 'Historical engagement scores across all dimensions',
      overallEngagementTrend: 'Overall Engagement Trend',
      overallProgressionDesc: 'Focus view of overall engagement progression',
      engagementDistribution: 'Engagement Distribution',
      currentBreakdownDesc: 'Current engagement breakdown by dimension',
      dimensionScores: 'Dimension Scores',
      currentScoresDesc: 'Current scores across all engagement dimensions',
      monthlyActivityBreakdown: 'Monthly Activity Breakdown',
      detailedMonthlyDesc: 'Detailed view of monthly engagement by dimension',
      performanceRadar: 'Performance Radar',
      multiDimensionalViz: 'Multi-dimensional performance visualization',
      currentPerformance: 'Current Performance',
      targetPerformance: 'Target Performance',
      benchmarkComparison: 'Benchmark Comparison',
      benchmarkDesc: 'Performance vs. club averages and targets',
      currentStat: 'Current:',
      targetStat: 'Target:',
      clubAvgStat: 'Club Avg:',
      engagementGoals: 'Engagement Goals',
      goalsProgressDesc: 'Progress towards engagement targets and milestones',
      currentProgress: 'Current Progress',
      goalAchieved: 'Goal achieved! Consider setting a higher target.',
      pointsToTarget: '{n} points to reach target',
      goalsRecommendations: 'Recommendations',
      achievementTimeline: 'Achievement Timeline',
      recentMilestones: 'Recent milestones and upcoming goals',
      recentAchievements: 'Recent Achievements',
      upcomingGoals: 'Upcoming Goals',
      engagementSuffix2: 'Engagement',
      overallLegend: 'Overall',
      overallEngagementLegend: 'Overall Engagement',

      // MemberProfile360
      member360DashboardTitle: 'Member 360 Dashboard',
      comprehensiveOverviewFor: 'Comprehensive engagement overview for {name}',
      recalculateBtn: 'Recalculate',
      thirtyDayActivities: '30-Day Activities',
      lastActivityStat: 'Last Activity',
      engagementTrendsTitle: 'Engagement Trends',
      multiDimensionalScoringDesc: 'Multi-dimensional engagement scoring across all activities',
      tabPerformance: 'Performance',
      tabEngagement: 'Engagement',
      tabTimeline: 'Timeline',
      autoRefreshEnabled: 'Auto-refresh enabled',
      failedToLoadDashboard: 'Failed to load Member 360 dashboard. Please try refreshing the page.',
      memberDataNotFound: 'Member data not found. Please check the member ID and try again.',

      // ActivityHeatmap — legend
      legendActivityTypes: 'Activity Types',
      legendIntensity: 'Intensity',
      intensityNone: 'No Activity',
      intensityModerate: 'Moderate',
      intensityHigh: 'High',
      legendTimeRef: 'Time Reference',
      legendInteraction: 'Interaction',
      patternAnalysis: 'Pattern Analysis',
      insightMostActiveOn: 'Most active on {days}',
      insightPeakDuring: 'Peak activity during {hours}',
      insightAvgSession: 'Average session: {min} minutes',
      insightConsistentPattern: 'Consistent activity pattern across {n} sessions',

      // SocialNetworkGraph (charts)
      socialNetworkMapTitle: 'Social Network Map',
      socialNetworkMapDesc: 'Visualize connections and relationships within the golf community',
      layoutForce: 'Force',
      layoutCircle: 'Circle',
      layoutHierarchy: 'Hierarchy',
      toggleLabels: 'Labels',
      toggleGroups: 'Groups',
      avgStrength: 'Avg Strength',
      interactionsLabel: 'Interactions',
      groupsLabel: 'Groups',
      typesLabel: 'Types',
      connectionTypes: 'Connection Types',
      memberTiersLegend: 'Member Tiers',
      nodeSizesLegend: 'Node Sizes',
      primaryMemberNode: 'Primary member',
      connectionsNode: 'Connections',
      legendClickNodes: 'Click nodes to view details',
      legendHover: 'Hover for connection info',
      legendLinesConnections: 'Lines show connections',
      legendDashedGroups: 'Dashed = group connections',
      topConnections: 'Top Connections',
      connectionAnalysis: 'Connection Analysis',
      connectionDistribution: 'Connection Distribution',
      networkInsights: 'Network Insights',

      // SocialNetworkGraph (visualizations)
      socialNetworkGraphTitle: 'Social Network',
      tabNetworkGraph: 'Network Graph',
      tabStatistics: 'Statistics',
      tabNodeDetails: 'Node Details',
      connectionTypeFilter: 'Connection Type',
      filterAll: 'All',
      zoomLabel: 'Zoom',
      membersConnectionsCount: '{m} members · {c} connections',
      statTotalMembers: 'Total Members',
      statConnections: 'Connections',
      statNetworkDensity: 'Network Density',
      statCentralityScore: 'Centrality Score',
      statClusters: 'Clusters',
      statInfluenceScore: 'Influence Score',
      connectionsTab: 'Connections',
      clickNodePrompt: 'Click on a node to view member details and connections',

      // ArchetypeRadarChart
      behavioralPatternTitle: 'Behavioral Pattern Analysis',
      behavioralPatternDesc: 'Comprehensive multi-dimensional behavioral analysis',
      compareMemberOnly: 'Member Only',
      compareVsClub: 'vs Club Average',
      compareVsArchetype: 'vs Archetype',
      radarMember: 'Member',
      radarClubAverage: 'Club Average',
      radarArchetypeAverage: 'Archetype Average',
      memberScore: 'Member Score',
      clubAvgLegend: 'Club Avg',
      archetypeAvgLegend: 'Archetype Avg',
      hoverDimensionsHint: 'Hover over dimensions for details',
      behavioralDimensions: 'Behavioral Dimensions',
      dimensionStrong: 'Strong',
      dimensionGrowthArea: 'Growth Area',
      clubAvgLabel: 'Club Avg',
      archetypeAvgLabel: 'Archetype Avg',
      keyTraits: 'Key Traits',
      strongDimensions: 'Strong Dimensions',
      growthOpportunities: 'Growth Opportunities',
      dimensionBreakdown: 'Dimension Breakdown',
      archetypeInsights: 'Archetype Insights',

      // PerformanceHeatmap
      intensityVeryHigh: 'Very High',
      intensityHighLabel: 'High',
      intensityMedium: 'Medium',
      intensityLowLabel: 'Low',
      intensityVeryLow: 'Very Low',
      tabHeatmap: 'Heatmap',
      tabAnalysis: 'Analysis',
      tabDetails: 'Details',
      colorScaleLabel: 'Color Scale',
      rangeLabel: 'Range',
      valueLabel: 'Value',
      intensityLabel: 'Intensity',
      statMaximum: 'Maximum',
      statMinimum: 'Minimum',
      statAverage: 'Average',
      statDataPoints: 'Data Points',
      cellsUnit: 'cells',
      dimensionAnalysis: 'Dimension Analysis',
      xDistributionLabel: '{x} Distribution',
      selectedCellDetails: 'Selected Cell Details',
      intensityColHeader: 'Intensity',
      additionalData: 'Additional Data',
      clickCellPrompt: 'Click on a cell to view details',
      heatmapInformation: 'Heatmap Information',
      metricLabel: 'Metric',
      descriptionLabel: 'Description',
      xAxisLabel: 'X-Axis',
      yAxisLabel: 'Y-Axis',
      colorScaleInfoLabel: 'Color Scale',

      // PlayingFrequencyChart
      roundsPlayedDataset: 'Rounds Played',
      noPlayingData: 'No Playing Data',
      noPlayingDataDesc: 'No playing activity found for this member.',
      statTotal: 'Total',
      statAvgMonth: 'Avg/Month',
      statPeak: 'Peak',
      monthlyRoundsSubtitle: 'Monthly rounds played',
      roundsBadge: 'rounds',
    },
    comms: {
      manageChannels: 'Manage channels',
      settings: 'Settings',
      createChannel: 'Create a channel',
      noChannelsYet: 'No channels yet',
      addNewChannel: 'Add new communication channel for your club',
      createNewChannel: 'Create new channel',
      optional: 'optional',
      cancel: 'Cancel',
      creating: 'Creating…',
      create: 'Create channel',
      edit: 'Edit',
      delete: 'Delete',
      saving: 'Saving…',
      save: 'Save',
      back: 'Back',
      autoAddMembers: 'Add new members to default channels automatically',
      communications: 'Communications',
      convertToTask: 'Convert to task',
      selectConversation: 'Select a conversation',
      selectConversationHint: 'Choose a channel, member, or task from the left',
      taskCreated: 'Task created successfully',
      searchPlaceholder: 'Search…',
      channelsSectionHeader: 'Channels',
      messagesSectionHeader: 'Messages',
      tasksSectionHeader: 'Tasks',
      noResults: 'No results',
      noChannels: 'No channels yet',
      noConversations: 'No conversations yet',
      noOpenTasks: 'No open tasks',
      createChannelAction: 'Create a channel',
      members: 'members',
      noMessagesYet: 'No messages yet',
      today: 'today',
      yesterday: 'yesterday',
      noMessagesInChannel: 'No messages yet in this channel',
      live: 'Live',
      typeMessage: 'Type a message…',
      replyTo: 'Reply to {name}…',
      editMessage: 'Edit your message…',
      messageUser: 'Message {name}…',
      reply: 'Reply',
      copyMessage: 'Copy',
      deleteMessage: 'Delete',
      unknownUser: 'Unknown User',
      staff: 'Staff',
      member: 'Member',
      replyingTo: 'Replying to {name}',
      originalMessage: 'Original:',
      editingMessage: 'Editing message',
      loadingMessages: 'Loading messages…',
      startConversation: 'Start a conversation with {name}',
    },
    reports: {
      avgRounds: {
        title: 'Rounds per Member',
        periodSummary: 'Period summary',
        activityDistribution: 'Activity distribution',
        topMembers: 'Top members by activity',
        avgByTier: 'Avg activities by tier',
        membersLabel: 'Members',
        activitiesLabel: 'Activities',
        avgPerMember: 'Avg / member',
        zeroActivities: '0 activities',
        range1: '1 – 5',
        range2: '6 – 15',
        range3: '16+',
        noMemberData: 'No member data available.',
        noTierData: 'No tier data available.',
        footer: 'Computed from member progress data',
      },
      cohortCompare: {
        title: 'Compare cohorts',
        subtitle: 'D30 / D60 / D90 retention by join month',
        d30: 'D30 retention',
        d60: 'D60 retention',
        d90: 'D90 retention',
        cohortHeader: 'Cohort',
        sizeHeader: 'Size',
        insightDrop: 'dropped D30→D90',
        insightBest: 'retained at D90',
        best: 'Best',
      },
      cohort: {
        atRiskMembers: 'At-risk members',
        worstCohort: 'Worst-performing cohort',
        membersAtRisk: 'members at risk',
        milestoneJoined: 'joined',
        milestoneRound: 'round',
        milestoneLesson: 'lesson',
        milestoneEvent: 'event',
        noAtRisk: 'No at-risk members in this cohort',
        exportList: 'Export list',
        message: 'Message',
        lastActive: 'Last active',
        daysAgo: 'd ago',
        joined: 'Joined',
        ago: 'ago',
        preActivation: 'Pre-activation',
      },
      compose: {
        messageTitle: 'Message',
        inApp: 'In-app',
        email: 'Email',
        cancel: 'Cancel',
        send: 'Send',
        suggestedMessage: 'Suggested message',
        messageLabel: 'Message',
        template: 'Template',
        placeholder: 'Type your message…',
        messageSent: 'Message sent',
        emailOpened: 'Email opened',
        emailHint: 'Complete and send the email in your default email client',
        errorSending: 'Error sending message',
      },
      memberList: {
        membersLabel: 'Members',
        discoverStages: 'Discover (Stage 1–3)',
        developStages: 'Develop (Stage 4–6)',
        performStages: 'Perform (Stage 7–9)',
        noMembers: 'No members found',
        openingConversation: 'Opening conversation…',
        selectAll: 'Select all',
        firstSkills: 'First Skills',
        stage: 'Stage',
        memberLabel: 'Member',
        lastActive: 'Last active',
        sendEmail: 'Send email',
        message: 'Message',
        to: 'To',
        subject: 'Subject',
        messageLabel: 'Message',
        fullReport: 'Full report',
        cancel: 'Cancel',
        email: 'Email',
        sending: 'Sending…',
      },
      revenueAtRisk: {
        title: 'Revenue at risk',
        riskOverview: 'Risk overview',
        byTier: 'By membership tier',
        riskReasons: 'Risk reason distribution',
        atRiskMembers: 'At-risk members',
        atRiskLabel: 'At-risk members',
        revenueExposed: 'Revenue exposed',
        avgDaysInactive: 'Avg days inactive',
        nearestRenewal: 'Nearest renewal',
        inactive30: 'Inactive 30+ days',
        decliningEngagement: 'Declining engagement',
        lowActivity: 'Low activity',
        noShowPattern: 'No-show pattern',
        atRiskFlagged: 'At risk (flagged)',
        daysInactive: 'd inactive',
        atRisk: 'at risk',
        noTierData: 'No tier breakdown available',
        noRiskReasons: 'No risk reasons recorded',
        noAtRisk: 'No at-risk members found',
        showingTop: 'Showing top 15',
        total: 'total',
        footer: 'Revenue at risk = annual dues × at-risk members',
      },
      d90: {
        title: 'D90 retention',
        cohortsTracked: 'cohort',
        cohortsTrackedPlural: 'cohorts',
        membersTracked: 'members tracked',
        avgD90Rate: 'Avg D90 rate',
        cohortsLabel: 'Cohorts',
        avgCohortSize: 'Avg cohort size',
        d90Trend: 'D90 trend',
        retentionCurve: 'Retention curve',
        newMembersPerCohort: 'New members per cohort',
        ltvSimulation: 'LTV simulation',
        d90Legend: 'D90%',
        avgLegend: 'avg',
        d30: 'D30',
        d60: 'D60',
        d90: 'D90',
        notEnoughCohorts: 'Not enough cohorts yet (need 2+)',
        noData: 'No data yet',
        retained: 'retained',
        scenarioHeader: 'Scenario',
        d90RateHeader: 'D90 rate',
        extraRetained: 'Extra retained/cohort',
        estRevenue: 'Est. annual revenue',
        current: 'Current',
        baseline: 'baseline',
        noCohortsAvailable: 'No cohort data available.',
        averagesNote: 'Averages across all cohorts with available data.',
        footerNote: 'Cohort = month of joining · D90 = still active at 90 days',
        revenueNote: 'Extra retained members multiplied by 12 monthly cohorts then by avg annual dues.',
      },
      briefing: {
        applications: {
          title: 'Membership applications',
          awaitingReview: 'awaiting review',
          pending: 'Pending',
          avgWait: 'Avg wait',
          days: 'days',
          oldest: 'Oldest',
          longestWait: 'longest wait',
          listHeader: 'Applications · oldest first',
          applicantHeader: 'Applicant',
          actionsHeader: 'Actions',
          approve: 'Approve',
          decline: 'Decline',
          reviewHint: 'Review and approve in Members',
          reviewAll: 'Review all',
        },
        atRisk: {
          title: 'At-risk members',
          atRisk: 'At risk',
          members: 'members',
          avgInactive: 'Avg inactive',
          days: 'days',
          nearChurn: 'Near churn',
          range: '61–90 days',
          listHeader: 'Members · most urgent first',
          noAtRisk: 'No at-risk members right now',
          inactivityDist: 'Inactivity distribution',
          noChannelData: 'No channel data',
          inactive: 'Members inactive 21–90 days',
          fullReport: 'Full report',
          drifting: 'Drifting',
          fading: 'Fading',
          highRisk: 'High risk',
          dInactive: 'd inactive',
          noActivity: 'No activity',
        },
        coaching: {
          title: 'Coaching performance',
          sessionsSummary: 'coaches · sessions this week',
          sessionsMo: 'Sessions (mo)',
          completion: 'Completion',
          revenueMo: 'Revenue (mo)',
          noShowRate: 'No-show rate',
          coachRoster: 'Coach roster',
          coachHeader: 'Coach',
          wkHeader: 'Wk',
          moHeader: 'Mo',
          revenueHeader: 'Revenue',
          noSessions: 'No sessions found',
          revenueTrend: '4-week revenue trend',
          recentSessions: 'Recent sessions (30 days)',
          allCoaches: 'All coaches',
          footer: 'Sessions from last 30 days · Revenue trend uses weekly aggregates',
          fullReport: 'Full report',
          completed: 'completed',
          scheduled: 'scheduled',
          noShow: 'no-show',
          cancelled: 'cancelled',
        },
        comms: {
          title: 'Communications performance',
          loading: 'Loading…',
          keyMetrics: 'Key metrics — last 30 days',
          audienceReach: 'Audience reach',
          vsPrevMonth: 'vs prev month',
          totalReach: 'Total reach',
          views: 'views',
          avgEngagement: 'Avg engagement',
          vsPrevPeriod: 'vs prev period',
          published: 'Published',
          thisMonth: 'this month',
          audienceReachMonth: 'Audience reach this month',
          ofMembers: 'of members',
          uniqueMembersReached: 'unique members reached of',
          total: 'total',
          channelPerformance: 'Channel performance',
          noChannelData: 'No channel data',
          channelHeader: 'Channel',
          sentHeader: 'Sent',
          viewsHeader: 'Views',
          engHeader: 'Eng%',
          priorityBreakdown: 'Priority breakdown',
          urgent: 'Urgent',
          high: 'High',
          medium: 'Medium',
          low: 'Low',
          pipeline: 'Pipeline and top performers',
          inPipeline: 'In pipeline:',
          scheduled: 'scheduled',
          drafts: 'drafts',
          noTopPerformers: 'No top performers this period',
          analyticsUnavailable: 'Analytics unavailable',
          footer: 'Last 30 days · Reach estimated from view_count',
          fullReport: 'Full report',
          sortedByEngagement: 'Sorted by engagement',
          noAnnouncements: 'No announcements sent this period.',
          topPerformers: 'Top performers',
          viewStats: 'View stats',
        },
        events: {
          title: 'Event performance',
          weeklyFillTrend: 'Weekly fill trend · Revenue scatter',
          onTrack: 'On Track',
          monitor: 'Monitor',
          atRisk: 'At Risk',
          fillHigh: '≥ 80% full',
          fillMid: '60 – 79%',
          fillLow: '< 60% full',
          weeklyAvgFill: 'Weekly avg fill rate',
          loading: 'Loading 90-day history…',
          weeksWindow: 'weeks · 90-day window',
          fillRateByEvent: 'Fill rate by event',
          spots: 'spots',
          fillRateVsRevenue: 'Fill rate vs. Revenue',
          revenue: 'REVENUE',
          fillRate: 'FILL RATE',
          free: 'Free',
          bubbleNote: 'Bubble size = capacity',
          totalRevenue: 'Total revenue:',
          avgFill: 'Avg fill:',
          fullReport: 'Full report',
          noRecentEvents: 'No recent events.',
          eventHeader: 'Event',
          fillRateHeader: 'Fill Rate',
          registeredHeader: 'Registered',
          revenueHeader: 'Revenue',
          eventPerformance: 'Event performance',
        },
        eventReg: {
          title: 'Pending registrations',
          awaitingConfirmation: 'awaiting confirmation',
          waitlisted: 'waitlisted',
          pending: 'Pending',
          waitlistedLabel: 'Waitlisted',
          approve: 'Approve',
          decline: 'Decline',
          registrations: 'registrations',
          onWaitlist: 'on waitlist',
          urgent: 'Urgent',
          withinTwoDays: 'within 2 days',
          listHeader: 'Registrations · soonest event first',
          manageHint: 'Confirm or decline in Events',
          manageEvents: 'Manage events',
        },
        expiring: {
          title: 'Expiring memberships',
          expiringThisMonth: 'expiring this month',
          atRisk: 'at risk',
          expiring: 'Expiring',
          thisMonth: 'this month',
          soonest: 'Soonest',
          daysLeft: 'days left',
          atRiskLabel: 'At risk',
          annualValue: 'annual value',
          members: 'members',
          listHeader: 'Members · expiring soonest first',
          emailMember: 'Email member',
          revenueAtRisk: 'Revenue at risk · by membership type',
          actBefore: 'Act before they lapse',
          viewMembers: 'View members',
          dLeft: 'd left',
        },
        handicap: {
          title: 'Handicap trends',
          tracked: 'tracked',
          notEnoughData: 'Not enough round data yet.',
          improved: 'Improved',
          stable: 'Stable',
          declined: 'Declined',
          improving: 'improving',
          avgUp: '▲ avg',
          avgDown: '▼ avg',
          trends: 'Handicap trends',
          viewStats: 'View stats',
          weekLabels: ['W-4', 'W-3', 'W-2', 'W-1', 'Now'],
          loadingTrend: 'Loading trend data…',
          membersSummary: 'members tracked · 4-week trend · Current distribution',
          fourWeekTrend: '4-week improvement trend',
          currentDist: 'Current distribution',
          aboveAverage: 'Above average',
          belowAverage: 'Below average',
          rate: 'rate',
          scatterTitle: 'Handicap vs. change distribution',
          improvedArrow: 'Improved ↓',
          declinedArrow: 'Declined ↑',
          footer: 'Weekly RPC snapshots · 30-day window',
          fullReport: 'Full report',
          members: 'members',
        },
        lowReg: {
          title: 'Low registration events',
          events: 'Events',
          under30: 'under 30%',
          avgFill: 'Avg fill',
          capacity: 'capacity',
          openSpots: 'Open spots',
          unfilled: 'unfilled',
          listHeader: 'Events · soonest first',
          today: 'Today',
          dAway: 'd away',
          registered: 'registered',
          percentFull: '% full',
          spot: 'spot',
          spots: 'spots',
          stillAvailable: 'still available',
          eventsUnder: 'Events under 30% capacity',
          manageEvents: 'Manage events',
        },
        engagement: {
          title: 'Member engagement',
          today: 'Today',
          yesterday: 'Yesterday',
          dAgo: 'd ago',
          wAgo: 'w ago',
          moAgo: 'mo ago',
          noEngaged: 'No engaged members',
          noPassive: 'No passive members',
          noAtRisk: 'No at risk members',
          engaged: 'Engaged',
          passive: 'Passive',
          atRisk: 'At Risk',
          golfRounds: 'Golf rounds',
          events: 'Events',
          coachingLabel: 'Coaching',
          activityMix: 'Activity mix — last 30 days',
          noActivity: 'No activity recorded yet',
          memberHeader: 'Member',
          inactiveHeader: 'Inactive',
          actionsHeader: 'Actions',
          sendEmail: 'Send email',
          viewMember: 'View member',
          sendMessage: 'Send message',
          activityTrend: 'Activity trend — last 30 days',
          vsLastYear: 'vs last year',
          noTrendData: 'No trend data yet',
          rounds: 'rounds',
          lastYear: 'last year',
          mostActive: 'Most active members — 90 days',
          lostTouch: 'Lost touch with — no activity 60+ days',
          emailLapsed: 'Email all lapsed members',
          footer: 'Engagement score · Last 30 days',
          fullReport: 'Full report',
          scoreLabel: 'Score:',
          members: 'members',
        },
        lifecycle: {
          title: 'Member lifecycle',
          new: 'New',
          active: 'Active',
          atRisk: 'At Risk',
          churned: 'Churned',
          range1: '0–14 days',
          range2: '15–30 days',
          range3: '31–60 days',
          members: 'members',
          cohortTimeInStage: 'Cohort time-in-stage',
          inactive: 'Inactive',
          lowActivity: 'Low activity',
          newMember: 'New member',
          memberLabel: 'Member',
          reasonHeader: 'Reason',
          actionsHeader: 'Actions',
          sendEmail: 'Send email',
          sendMessage: 'Send message',
          membersSummary: 'members · Stage breakdown · Cohort flow',
          noMembersInStage: 'No members in this stage',
          moreMembers: 'more members',
          needAttention: 'members need attention',
          reachOut: 'Reach out before they churn',
          viewAll: 'View all',
          footer: 'Stage counts from lifecycle RPC · Click a stage tile to filter member list',
        },
        messages: {
          title: 'Unread messages',
          membersWaiting: 'members waiting · oldest',
          hAgo: 'h ago',
          unread: 'Unread',
          messages: 'messages',
          oldest: 'Oldest',
          hoursAgo: 'hours ago',
          today: 'Today',
          received: 'received',
          listHeader: 'Messages · most recent first',
          replyHint: 'Reply in Messaging',
          openInbox: 'Open inbox',
          noMessages: 'No messages yet',
        },
        newMembers: {
          title: 'New members not onboarding',
          haventCompleted: 'members haven\'t completed onboarding',
          stuck: 'Stuck',
          newMembers: 'new members',
          avgJoined: 'Avg joined',
          daysAgo: 'days ago',
          zeroActivity: 'Zero activity',
          noActivity: 'no activity',
          listHeader: 'Members · longest since joining first',
          joined: 'Joined',
          dAgo: 'd ago',
          activities: 'activities',
          sendWelcome: 'Send welcome message',
          progressBreakdown: 'Onboarding progress breakdown',
          range1: '0 activities',
          range2: '1–2 activities',
          range3: '3–4 activities',
          progressLabel: 'New members · onboarding progress',
          fullReport: 'Full report',
        },
        revSource: {
          title: 'Revenue sources',
          noData: 'Revenue data unavailable.',
          sourceHeader: 'Source',
          trendHeader: 'Trend',
          thisWeekHeader: 'This Week',
          lastWeekHeader: 'Last Week',
          deltaHeader: 'Δ',
          total: 'Total',
          posNote: 'Pro Shop & Range via POS — not available',
          viewStats: 'View stats',
        },
        revDrop: {
          title: 'Revenue drop',
          down: 'Down',
          vsLastWeek: '% vs last week',
          keyMetrics: 'Key metrics',
          thisWeek: 'This week',
          revenue: 'revenue',
          lastWeek: 'Last week',
          forComparison: 'for comparison',
          drop: 'Drop',
          shortfall: 'shortfall',
          dropPercent: 'Drop %',
          weeklyRevenue: 'Weekly revenue · last 5 weeks',
          whatChanged: 'What changed',
          weeklyBreakdown: 'Weekly revenue breakdown',
          fullReport: 'Full report',
          weekLabels: ['W-4', 'W-3', 'W-2', 'W-1', 'This week'],
        },
        revGraphs: {
          title: 'Revenue by source',
          subtitle: '4-week trends · Revenue share · Bridge',
          trendsTitle: 'Trends — 4-week trajectory',
          lastWk: 'Last wk:',
          shareTitle: 'Revenue share this week',
          lastWeek: 'Last\nWeek',
          thisWeek: 'This\nWeek',
          bridgeTitle: 'Week-on-week bridge',
          posNote: 'Pro Shop & Range via POS — live data not yet available',
          fullReport: 'Full report',
        },
        teeSheet: {
          title: 'Tee sheet drop',
          down: 'Down',
          vsLastWeek: '% vs last week',
          bookingsThisWeek: 'bookings this week',
          thisWeek: 'This week',
          bookings: 'bookings',
          lastWeek: 'Last week',
          drop: 'Drop',
          decline: 'decline',
          dailyBookings: 'Daily bookings · this week vs last week',
          thisWeekLabel: 'This week',
          lastWeekLabel: 'Last week',
          dayBreakdown: 'Day breakdown',
          vs: 'vs',
          hourlyDist: 'Tee sheet · hourly distribution',
          fullReport: 'Full report',
        },
        ops: {
          title: 'Today\'s operations',
          bookingsSummary: 'bookings',
          events: 'events',
          teeUtilization: 'Tee sheet utilization',
          checkedIn: 'Checked In',
          pendingLabel: 'Pending',
          noShow: 'No-Show',
          available: 'Available',
          hourlyDist: 'Hourly booking distribution',
          loading: 'Loading hourly data…',
          noHourlyData: 'No hourly data available',
          todaysEvents: 'Today\'s events',
          scheduled: 'scheduled',
          noEvents: 'No events scheduled today',
          moreRegistrants: 'more registrants',
          noRegistrantDetails: 'No registrant details available',
          footer: 'Tee sheet · hourly distribution',
          fullReport: 'Full report',
        },
        weatherImpact: {
          noData: 'Weather data unavailable — check location settings.',
          today: 'Today',
          sunnyDays: 'Sunny days:',
          teeFill: '% tee fill',
          rainyDays: 'Rainy days:',
          preview: 'Preview · real data in',
          days: 'days',
        },
        weather: {
          title: 'Weather & tee forecast',
          warning: '⚠',
          day: 'day',
          days: 'days',
          needAttention: 'need attention',
          percentToday: '% today',
          noData: 'Weather data unavailable — check location settings.',
          outlookTitle: '5-day booking outlook',
          belowForecast: 'Below forecast',
          aboveForecast: 'Above forecast',
          onTrack: 'On track',
          noBookings: 'No bookings yet',
          predictedFill: 'Predicted fill',
          actualBookings: 'Actual bookings',
          needsAttention: 'Needs attention',
          outlookLabel: 'Tee sheet outlook',
          short: 'short',
          approx: '~',
          emptySlots: 'empty slots. Consider a last-minute offer.',
          promote: 'Promote',
          historically: 'Historically',
          rainyNote: '% fill on rainy days · bookings not open yet.',
          predictionsNote: 'Predictions estimated · real correlation data in',
          close: 'Close',
        },
        narrative: {
          title: 'This week',
          roundsPlayed: 'rounds played this week',
          revenue: 'Revenue £',
          up: 'up',
          down: 'down',
          noRounds: 'No rounds recorded yet this week.',
          teeSheet: 'Tee sheet',
          atRisk: '% at risk',
          handicapImproved: '% handicap improved',
        },
        health: {
          retention: 'Retention',
          teeSheet: 'Tee Sheet',
          revenue: 'Revenue',
          engagement: 'Engagement',
          satisfaction: 'Satisfaction',
          outOf: '/ 100',
          title: 'Club Health Score',
          beta: 'BETA',
          good: 'Good',
          needsAttention: 'Needs Attention',
          atRisk: 'At Risk',
          scoreNote: 'Score based on retention, tee sheet usage, revenue, and engagement.',
        },
      },
    },
    // Services Settings Tab (/club-admin/settings/services)
    servicesSettings: {
      servicesVisible: 'Services Visible',
      servicesHidden: 'Services Hidden',
      membersCanSeeSection: 'Members can see the Services section in their Club Hub',
      servicesSectionHidden: 'Services section is hidden from members',
      visible: 'Visible',
      hidden: 'Hidden',
      individualServices: 'Individual Services',
      controlWhichServices: 'Control which specific services are available to members',
      active: 'Active',
      coachingManagedInSection: 'Coaching settings are managed in the Coaching section.',
      toastEnabled: 'Enabled',
      toastDisabled: 'Disabled',
      toastFailedUpdate: 'Failed to update service: {{message}}',
      toastSectionEnabled: 'Services section enabled for members',
      toastSectionHidden: 'Services section hidden from members',
      toastFailedUpdateServices: 'Failed to update services',
      serviceNames: {
        tee_time: 'Tee Time',
        range: 'Driving Range',
        cart_rental: 'Cart Rental',
        locker: 'Locker Rental',
        simulator: 'Golf Simulator',
        pro_shop: 'Pro Shop',
        restaurant: 'Restaurant',
      },
      categories: {
        booking: 'Booking',
        purchase: 'Purchase',
        rental: 'Rental',
        event: 'Event',
        subscription: 'Subscription',
      },
    },
    // New Member Success Report
    newMemberReport: {
      breadcrumb: 'Reports / New Member Success',
      title: 'New Member Success',
      subtitle: 'Track activation, retention, and revenue at risk',
      activationMilestones: 'Activation milestones',
      export: 'Export',
      thisQuarter: 'This Quarter',
      d90Retention: 'D90 retention',
      tracked: 'tracked',
      noDataYet: 'No data yet',
      revenueAtRisk: 'Revenue at risk',
      activatedRevenue: 'Activated revenue',
      cohortLtv: 'Cohort LTV (12mo)',
      cohortRetention: 'Cohort Retention',
      cohortRetentionSubtitle: "% of each month's new members still active at day 30 / 60 / 90",
      cohortCol: 'Cohort',
      sizeCol: 'Size',
      atRiskCol: 'At risk',
      noCohortDataTitle: 'No cohort data yet',
      noCohortDataSubtitle: 'Members appear here once their join month is at least 30 days old.',
      last6Months: 'Last 6 join months · tap a cohort for member detail',
      compareCohorts: 'Compare cohorts',
      stageDiscover: 'Stage 1-3 · Discover',
      stageDevelop: 'Stage 4-6 · Develop',
      stagePerform: 'Stage 7-9 · Perform',
    },
    // Registration History
    registrationHistory: {
      eventHistory: 'Event Registration History',
      adminHistory: 'Admin Registration History',
      noRegistrationHistory: 'No registration history',
      noHistoryMatchSearch: 'No history entries match your search criteria',
      noHistoryForFilters: 'No registration activity found for the selected filters',
      registeredFor: 'Registered for',
      byAdmin: 'by',
      allSources: 'All Sources',
      selfRegistration: 'Self',
      adminRegistration: 'Admin',
      bulkImport: 'Import',
      system: 'System',
      unknown: 'Unknown',
      allStatus: 'All Status',
      confirmed: 'Confirmed',
      waitlist: 'Waitlist',
      cancelled: 'Cancelled',
      allTime: 'All Time',
      today: 'Today',
      yesterday: 'Yesterday',
      thisWeek: 'This Week',
      thisMonth: 'This Month',
      tabRecent: 'Recent',
      tabAdmin: 'Admin',
      tabBulk: 'Bulk',
      tabCancelled: 'Cancelled',
      sourceSelf: 'Self',
      sourceAdmin: 'Admin',
      sourceImport: 'Import',
      sourceSystem: 'System',
    },

    // Monitoring section (components/monitoring/*)
    monitoring: {
      // ClientMetricsChart.tsx
      clientPerformanceTitle: 'Client Performance (Core Web Vitals)',
      noClientData: 'No client performance data available',
      chartsComingSoon: 'Charts coming in Day 4',
      lcpLabel: 'LCP',
      lcpFullLabel: 'Largest Contentful Paint',
      fcpLabel: 'FCP',
      fcpFullLabel: 'First Contentful Paint',
      clsLabel: 'CLS',
      clsFullLabel: 'Cumulative Layout Shift',
      ttfbLabel: 'TTFB',
      ttfbFullLabel: 'Time to First Byte',
      ratingGood: 'Good',
      ratingFair: 'Fair',
      ratingPoor: 'Poor',
      goodVitalsLabel: 'Pages with Good Vitals',
      // ApiPerformanceTable.tsx
      apiPerformanceTitle: 'API Performance',
      noApiData: 'No API performance data available',
      dataWillAppear: 'Data will appear once requests are logged',
      colEndpoint: 'Endpoint',
      colRequests: 'Requests',
      colAvgTime: 'Avg Time',
      colP95: 'P95',
      colP99: 'P99',
      colErrorRate: 'Error Rate',
    },
  },

  // Coaching translations
  coaching: {
    // Common labels and navigation
    common: {
      // Navigation & Tabs
      overview: "Overview",
      students: "Students",
      schedule: "Schedule",
      exercises: "Exercises",
      videos: "Videos",
      messages: "Messages",
      lessons: "Lessons",
      progress: "Progress",

      // Common labels
      coach: "Coach",
      student: "Student",
      session: "Session",
      homework: "Homework",
      practice: "Practice",

      // Status labels
      status: "Status",
      active: "Active",
      paused: "Paused",
      ended: "Ended",
      pending: "Pending",
      completed: "Completed",
      inProgress: "In Progress",
      overdue: "Overdue",
      scheduled: "Scheduled",
      confirmed: "Confirmed",
      cancelled: "Cancelled",
      noShow: "No Show",

      // Time labels
      today: "Today",
      tomorrow: "Tomorrow",
      yesterday: "Yesterday",
      past: "Past",
      dueToday: "Due today",
      dueTomorrow: "Due tomorrow",
      noDeadline: "No deadline",

      // Actions
      view: "View",
      viewAll: "View All",
      viewProfile: "View Profile",
      viewCalendar: "View Calendar",
      assign: "Assign",
      create: "Create",
      add: "Add",
      remove: "Remove",
      move: "Move",
      retry: "Retry",
      search: "Search",
      browse: "Browse",
      confirm: "Confirm",

      // Prepositions
      from: "from",
      to: "to",
      in: "in",

      // Misc labels
      warning: "Warning",

      // Empty states
      noStudents: "No students yet",
      noSessions: "No sessions scheduled",
      noExercises: "No exercises assigned",
      noVideos: "No videos yet",
      noMessages: "No recent messages",
      noData: "No data available",
      noGoals: "No goals set yet",

      // Categories - Golf Skills
      driving: "Driving",
      ironPlay: "Iron Play",
      shortGame: "Short Game",
      putting: "Putting",
      bunker: "Bunker",
      mental: "Mental",
      fitness: "Fitness",
      warmUp: "Warm Up",
      course: "Course",

      // Session Types
      lessonType: "Lesson",
      playingLesson: "Playing Lesson",
      practiceSession: "Practice Session",
      assessment: "Assessment",
      fitting: "Club Fitting",

      // Difficulty levels
      beginner: "Beginner",
      intermediate: "Intermediate",
      advanced: "Advanced",

      // Misc
      focusAreas: "Focus Areas",
      handicap: "Handicap",
      duration: "Duration",
      reps: "Reps",
      min: "min",
      all: "All",
      allDone: "All done",
      allCaughtUp: "All caught up!",
      loading: "Loading...",
      cancel: "Cancel",
      back: "Back",
      delete: "Delete",

      // Students tab filters
      searchStudents: "Search students by name, email, or handicap...",
      allStudents: "All",
      onboardingOnly: "Onboarding",
      regularStudents: "Regular",
      inactive: "Inactive",
      new: "New",
      sortByLastSession: "Last Session",
      sortByName: "Name",
      sortByHandicap: "Handicap",
      sortByProgress: "Progress",

      // Students tab stats
      inactive7Days: "Inactive 7+ days",
      inOnboarding: "In Onboarding",
      newStudents: "New students",
      readyToAdvance: "Ready to Advance",
      awaitingApproval: "Awaiting approval",
      studentsBehind: "Students behind",
      questsCompleted: "Quests Completed",
      thisWeek: "This week",
      lastSession: "Last Session",
      noStudentsFound: "No students found",
      tryAdjustingSearch: "Try adjusting your search",
      addFirstStudentToStart: "Add your first student to get started",
      exportList: "Export List",

      // Students tab table headers
      totalStudents: "Total students",
      pendingExercisesCount: "Pending exercises",
      atRisk: "At risk",
      sessionsPerMonth: "Sessions / mo",
      tierLabel: "Tier",
      messageLabel: "Message",

      // Students tab footer
      showingOf: "Showing {shown} of {total} students",
      activeNow: "active",
      needAttentionNow: "need attention",

      // Empty state benefit cards
      trackSessionsTitle: "Track sessions",
      trackSessionsDesc: "See every lesson, past and upcoming, for each student at a glance.",
      assignHomeworkTitle: "Assign homework",
      assignHomeworkDesc: "Send practice plans and exercises directly to your students.",
      monitorProgressTitle: "Monitor progress",
      monitorProgressDesc: "Watch handicap trends and engagement evolve over time.",

      // Add student panel
      addStudentEyebrow: "Add student",
      selectMembersTitle: "Select members",
      filtersLabel: "Filters",
      sortByLabel: "Sort by",

      // Student detail
      studentNotFoundDesc: "Unable to find the requested student.",
      skillSnapshotTitle: "Skill Snapshot",
      updateAssessment: "Update",
      assessLabel: "Assess",
      createAssessmentCta: "Create Assessment",

      // StudentsList + CoachStudentDetailPage
      backToStudents: "Back to Students",
      actions: "Actions",
      noneLabel: "None",
      naLabel: "N/A",
      errorLoadingStudents: "Error Loading Students",
      studentsCoachWillAppear: "Students you coach will appear here once relationships are created",
      tryAdjustingFilters: "Try adjusting your filters or search terms",
      showingStudents: "Showing {count} {unit}",
      studentUnit: "student",
      studentsUnit: "students",
      exerciseUnit: "exercise",
      exercisesUnit: "exercises",

      // Relative date labels (formatRelativeDate)
      never: "Never",
      daysAgo: "{count} days ago",
      weeksAgo: "{count} weeks ago",
      monthsAgo: "{count} months ago",
      yearsAgo: "{count} years ago",
    },

    // Dashboard section
    dashboard: {
      // Page titles
      coachDashboard: "Coach Dashboard",
      coachingPortal: "Coaching Portal",
      myProfile: "My Profile",

      // Today's Schedule
      todaysSchedule: "Today's Schedule",
      upNext: "UP NEXT",
      ready: "Ready",
      startSession: "Start Session",
      laterToday: "Later Today",
      noSessionsToday: "No sessions scheduled today",
      addSessionToStart: "Add a session to get started",
      scheduleNewSession: "Schedule New Session",
      sessionsCount: "sessions",

      // Student Progress
      myStudents: "My Students",
      studentProgress: "Student Progress",
      activeCount: "active",
      noStudentsYet: "No students yet",
      addFirstStudent: "Add your first student to get started",

      // Progress indicators
      onTrack: "On Track",
      needsFocus: "Needs Focus",

      // Onboarding stages
      discover: "Discover",
      develop: "Develop",
      perform: "Perform",
      noStudentsInOnboarding: "No students in onboarding",
      complete: "Complete!",
      almostDone: "Almost done!",

      // Summary stats
      thisMonth: "This Month",
      sessions: "Sessions",
      rating: "Rating",

      // Attention
      needsAttention: "Needs Attention",
      pendingExercises: "pending exercises",
      earlyOnboardingStage: "Early onboarding stage",

      // Pending Homework
      pendingHomework: "Pending Homework",

      // Messages
      recentMessages: "Recent Messages",
      noRecentMessages: "No recent messages",

      // Quick Actions
      quickActions: "Quick Actions",
      newSession: "New Session",
      addStudent: "Add Student",
      assignExercise: "Assign Exercise",
      assignVideo: "Assign Video",

      // Table headers
      studentHeader: "Student",
      handicapHeader: "Handicap",
      sessionsHeader: "Sessions",
      homeworkHeader: "Homework",
      progressHeader: "Progress",
      actionsHeader: "Actions",

      // Page states
      loading: "Loading coaching dashboard...",
      errorLoading: "Error Loading Dashboard",
      welcomeTitle: "Welcome to the Coaching Portal",
      welcomeDescription: "You don't have a coach profile yet. Create one to start managing students, scheduling sessions, and assigning exercises.",
      creatingProfile: "Creating Profile...",
      createCoachProfile: "Create Coach Profile",
      selectClubFirst: "Please select a club first to create your coach profile.",

      // Enhanced stats
      upcomingThisWeek: "Upcoming",
      topImprovers: "Top Improvers",
      sessionsThisMonth: "sessions this month",
      noSessionsThisMonth: "No sessions this month",
      improving: "Improving",
      stable: "Stable",

      // Coach portal overview
      overviewOnly: 'Overview only',
      scheduleNote: 'Schedule managed in calendar',
      setupProfile: 'Set up your coach profile',
      coachingEyebrow: 'Coaching',
      bookLesson: 'Book a lesson',
      sessionBooked: 'Session booked',

      // Stats mini-table
      statsSessionsThisMonth: 'Sessions this month',
      statsActiveStudents: 'Active students',
      statsAvgHomework: 'Avg homework completion',
      statsSessionsThisWeek: 'Sessions this week',

      // Last-activity column
      lastActivityHeader: 'Last Activity',
      noSessions: 'No sessions',
      active: 'Active',
    },

    // Overview Tab
    overviewTab: {
      // Action Items card
      actionItems: 'Action Items',
      urgentBadge: '{count} urgent',
      allClearAttention: 'All clear — nothing needs your attention',
      allClearHomework: 'All clear on homework',
      // Side panel buttons
      messageBtn: 'Message',
      viewProfileBtn: 'View profile',
      viewStudentBtn: 'View student',
      messageStudent: 'Message student',
      viewFullProfile: 'View full profile',
      // Student quick-view labels
      handicapLabel: 'Handicap',
      sessionsPerMonth: 'Sessions / mo',
      pendingLabel: 'Pending',
      focusAreasLabel: 'Focus areas',
      upcomingSessionsLabel: 'Upcoming sessions',
      noUpcomingSessions: 'No upcoming sessions',
      lastActivityLabel: 'Last activity',
      noSessionsYet: 'No sessions yet',
      // Session quick-view labels
      detailsLabel: 'Details',
      typeLabel: 'Type',
      durationLabelShort: 'Duration',
      locationLabel: 'Location',
      statusLabel: 'Status',
      tbdLabel: 'TBD',
      preSessionNotes: 'Pre-session notes',
      practiceAreaFallback: 'Practice Area',
      focusPrefix: 'Focus:',
      // HomeworkPanel
      activeAssignments: 'Active assignments',
      noActiveAssignments: 'No active assignments',
      sendSessionHint: 'Send a session or close out a lesson to assign work',
      // QuickActions consolidation branch
      sendHomework: 'Send homework',
    },

    // Student Detail section
    studentDetail: {
      // Page
      studentNotFound: "Student Not Found",
      failedToLoad: "Failed to Load Student",
      studentSince: "Student since",
      activeStudent: "Active Student",
      currentHCP: "Current HCP",
      thisMonthLabel: "This Month",

      // Sessions section
      sessionsTitle: "Sessions",
      upcoming: "Upcoming",
      noUpcoming: "No Upcoming",
      noSessionScheduled: "No session scheduled",
      scheduleNextSession: "Schedule the next session",
      noCompletedSessions: "No completed sessions",
      sessionsAppearHere: "Sessions will appear here after completion",
      noHistory: "No History",

      // Skill Assessment
      skillAssessment: "Skill Assessment",
      latest: "Latest",
      noAssessmentYet: "No assessment yet",
      createAssessment: "Create a skill assessment to track progress",

      // Homework
      homeworkTitle: "Homework",
      noActiveExercises: "No active exercises",

      // Videos
      videosTitle: "Videos",
      noVideosAssigned: "No videos assigned yet",

      // Notes
      notes: "Notes",
      addNote: "Add Note",
      writeNote: "Write note",
      notePlaceholder: "Write a note about this student's progress, areas to focus on, or observations from a session...",

      // Goals
      goals: "Goals",
      noGoalsSet: "No goals set yet",
      coachWillSetGoals: "Your coach will set goals for you to work towards",

      // Onboarding
      onboardingProgress: "Onboarding Progress",
      questProgress: "Quest Progress",

      // Actions
      endRelationship: "End Relationship",
      cancelSession: "Cancel Session",

      // Messages
      message: "Message",

      // Validation
      validateCompleted: "Validate as completed",
      undoValidation: "Undo validation",
      watchVideo: "Watch player's video",
      playerSubmittedVideo: "Player submitted a video",

      // Tabs
      tabProfile: "Profile",
      tabLessons: "Lessons",
      tabPerformance: "Performance",

      // Stat tiles
      statHcp: "HCP",
      statTrend: "Trend",
      statLessons: "Lessons",
      statMonths: "Months",

      // Progress bar
      onboardingProgression: "Onboarding Progression",
      questProgression: "Quest Progression",

      // Panel eyebrows / titles
      eyebrowCoaching: "Coaching",
      bookLesson: "Book a Lesson",
      buildPracticeSession: "Build Practice Session",
      studentAssessment: "Student assessment",
      sendEmail: "Send email",
      directMessage: "Direct message",
      linkToSession: "Link to Session",
      noSavedSessions: "No saved sessions yet.",
      buildOneInSessions: "Build one in Sessions.",

      // Progression tab
      skillSnapshotTitle: "Skill Snapshot",
      updateAssessment: "Update",
      assessLabel: "Assess",
      createAssessmentCta: "Create Assessment",
      studentNotFoundDesc: "Unable to find the requested student.",

      // Session type labels (used in getSessionTypeLabel helper)
      sessionTypeLesson: "Lesson",
      sessionTypePlayingLesson: "Playing Lesson",
      sessionTypePractice: "Practice",
      sessionTypeAssessment: "Assessment",
      sessionTypeFitting: "Fitting",

      // Status badge labels
      statusActive: "Active",
      statusPaused: "Paused",
      statusEnded: "Ended",

      // Common action buttons
      cancel: "Cancel",
      send: "Send",
      sendingLabel: "Sending…",
      saveAssessment: "Save Assessment",

      // Assessment modal
      assessmentDesc: "Set where you believe this student currently stands. This informs their skill rings — with more weight when they have few rounds on record.",
      skillLevelTitle: "{name}'s Skill Level",

      // Link to session picker
      noSessionsFound: "No sessions found",

      // Send session panel
      noSavedSessionsHint: "No saved sessions yet.",
      buildInSessions: "Build one in",
      sessionsLabel: "Sessions",

      // Assign exercise/video labels (used in lesson_label)
      exerciseFromCoach: "Exercise from your coach",
      videoFromCoach: "Video from your coach",

      // Toast messages
      toastExerciseAssigned: "{name} assigned to {student}",
      toastFailedAssignExercise: "Failed to assign exercise",
      toastVideoAssigned: "{name} assigned to {student}",
      toastFailedAssignVideo: "Failed to assign video",
      toastSessionSent: '"{title}" sent to {student}',
      toastFailedSendSession: "Failed to send session",
      toastExerciseAdded: "Exercise added to stage",
      toastExerciseRemoved: "Exercise removed from stage",
      toastRequirementsUpdated: "Requirements updated",
      toastFailedOpenConversation: "Failed to open conversation",
      toastSessionUpdated: "Session updated",
      toastFailedUpdateSession: "Failed to update session",
      toastEmailSent: "Email sent",
      toastFailedSendEmail: "Failed to send email",
      toastFailedGeneric: "An error occurred",

      // Closeout banner
      closeoutBannerBody: "Close out this lesson to add your notes, rate the session, and send homework to {name}.",

      // Session chip status labels
      pastSessionLabel: "PAST SESSION",
      nowInSession: "NOW · IN SESSION",
      upcomingLabel: "UPCOMING",

      // Exercise due status
      dueInDays: "Due in {n} days",

      // Video submission
      submittedByPlayer: "Submitted by player",

      // Lesson suggestions fallback
      reviewLastSessionSuggestion: "Review last session notes before starting",

      // Focus area fallback
      lessonLabel: "Lesson",
    },

    // Player section
    player: {
      // Page titles
      coaching: "Coaching",
      findCoach: "Find a Coach",
      myCoaching: "My Coaching",
      theoryPractice: "Theory practice",
      theoryPracticeDesc: "Learn the basics of golf",

      // Home
      yourCoach: "Your Coach",
      message: "Message",
      opening: "Opening...",
      bookLesson: "Book Lesson",
      findAnotherCoach: "Find Another Coach",
      addAnotherCoach: "Add Another Coach",
      noUpcomingSessions: "No upcoming sessions scheduled",
      nextLesson: "Next Lesson",
      noCoachYet: "No Coach Yet",
      connectWithCoach: "Connect with a coach to start your personalized training journey",
      findACoach: "Find a Coach",
      pendingReviewTitle: "Notes coming soon",
      pendingReviewBody: "Your coach hasn't reviewed this lesson yet.",
      lessonNumber: "LESSON #{num}",
      fromYourLastLesson: "From your last lesson",
      skillsWorkedOn: "Skills worked on",
      homeworkDone: "Homework · {done} of {total} done",
      homeworkLabel: "Homework",
      keepItGoing: "Keep it going",
      noLessonsBookedYet: "No lessons booked yet",
      bookSessionToStart: "Book a session with your coach to get started",

      // Lessons tab
      upcomingTab: "Upcoming",
      historyTab: "History",
      yourUpcomingSessions: "Your upcoming sessions",
      lessonHistory: "Lesson History",
      noLessonHistory: "No lesson history yet",
      requestLesson: "Request a Lesson",
      rescheduleLesson: "Reschedule Lesson",
      rescheduleRequest: "Reschedule Request",

      // Request lesson modal
      requestLessonWith: "with",
      bookingsAccepted: "Bookings accepted {min}h in advance, up to {max} days out",
      preferredDate: "Preferred Date",
      preferredTime: "Preferred Time",
      durationLabel: "Duration",
      coachNotAvailableDay: "Coach is not available on this day.",
      availableDays: "Available: {days}",
      locationOptional: "Location (optional)",
      enterAddress: "Enter address",
      focusAreasOptional: "What would you like to work on? (optional)",
      focusAreasPlaceholder: "e.g. putting, short game",
      notesForCoach: "Notes for your coach (optional)",
      paymentMethodLabel: "Payment Method",
      payOnlineLabel: "Pay Online",
      payOnlineDesc: "Secure card payment",
      payAtClubLabel: "Pay at Club",
      payAtClubDesc: "Pay at the pro shop",
      cashLabel: "Cash",
      cashDesc: "Pay directly to coach",
      sendRequestButton: "Request Booking",
      bookNowButton: "Book Now",
      requestSent: "Lesson request sent to your coach!",
      bookingConfirmed: "Lesson confirmed!",
      bookingClosing: "Taking you back…",

      // Exercises tab
      homeworkTab: "Homework",
      myPracticeTab: "My Practice",
      browseTab: "Browse",
      dueSoon: "Due Soon",
      completeTheseExercises: "Complete these exercises",
      finishWhatYouStarted: "Finish what you started",
      recentlyCompleted: "Recently Completed",
      greatJobOnThese: "Great job on these!",
      thisWeeksGoal: "This Week's Goal",
      toDo: "To Do",
      recentPractice: "Recent Practice",
      noHomeworkAssigned: "No homework assigned",
      noHomeworkShort: 'No homework',
      addNoteForCoach: 'Add a note for your coach',
      customEquipmentForYou: 'Custom equipment for you',
      customInstructionsForYou: 'Custom instructions for you',
      customizedByCoach: 'Customized by your coach',
      ratingLabel: 'Rated {n} of 5',
      bundleMarkFinished: 'Mark finished',
      bundleDrop: 'Drop session',
      bundleMarkedFinished: 'Session marked as finished',
      bundleMarkFinishedError: 'Could not mark session as finished',
      bundleRemoved: 'Session removed',
      bundleRemoveError: 'Could not remove session',
      homeworkAllDone: 'All done',
      homeworkProgress: '{done}/{total} done',
      coachWillAssign: "Your coach will assign practice exercises to help improve your game.",
      noPracticePlan: "No practice plan yet",
      browseAndAdd: "Browse exercises and add them to your practice plan.",
      exercisesAvailable: "exercises available",
      noExercisesFound: "No exercises found",
      tryDifferentSearch: "Try a different search term.",
      noExercisesInCategory: "No exercises available in this category yet.",
      addToPractice: "Add to Practice",
      browseExercises: "Browse Exercises",

      // Videos tab
      assignedByCoach: "Assigned by",
      recentlyWatched: "Recently Watched",
      seeAll: "See All",
      myVideos: "My Videos",
      yourVideoCloud: "Your video cloud",
      noVideosYet: "No videos yet",
      addVideo: "Add Video",
      titleOptional: "Title (optional)",
      fromCameraRoll: "From Camera Roll",
      pasteLink: "Paste Link",
      shareWithCoach: "Share with coach",
      coachNote: "Coach's Note",

      // Progress tab
      skillRadar: "Skill Radar",
      yourGoals: "Your Goals",
      trackCoachingObjectives: "Track your coaching objectives",
      addGoal: "Add",
      coachsAssessment: "Coach's Assessment",
      lookingStrongAcrossBoard: "Looking strong across the board",
      oneAreaNeedsAttention: "One area needs attention",
      areasNeedWork: "{n} areas need work",
      pullingYouDown: "{skills} pulling you down",
      keepBuildingStrengths: "Keep building on your strengths",
      avgLabel: "avg",
      discoverPhase: "DISCOVER",
      developPhase: "DEVELOP",
      performPhase: "PERFORM",
      yourGolfJourney: "Your Golf Journey",
      stageOfTotal: "Stage {current} of {total}",
      phase: "Phase",
      requirementsToPass: "Requirements to pass",
      completeToAdvance: "Complete this stage to advance",
      coachFeedback: "Coach Feedback",
      noFeedbackYet: "No feedback yet",
      noFeedbackDesc: "Your coach hasn't shared any feedback yet. Check back after your next round!",
      coachFeedbackError: "Unable to load feedback",
      handicapProgress: "Handicap Progress",
      yourImprovementJourney: "Your improvement journey",
      current: "Current",
      skillsBreakdown: "Skills Breakdown",
      notYetAssessed: "Not yet assessed",
      currentGoals: "Current Goals",
      yourGoalsAndTargets: "Your goals and targets",
      latest: "Latest",
      viewAll: "View All",

      // Filter tabs
      lessonsTab: "Lessons",
      exercisesTab: "Exercises",
      videosTab: "Videos",
      progressTab: "Progress",

      // Latest Feedback section
      latestFeedback: "Latest Feedback",
      readFullFeedback: "Read full feedback",

      // Coach Videos section
      coachVideos: "Coach Videos",
      newVideos: "new",
      watched: "Watched",

      // Exercise detail popup
      instructions: "Instructions",
      duration: "Duration",
      reps: "Reps",
      coachNotes: "Coach's Notes",
      equipment: "Equipment",
      myVideo: "My Video",
      startExercise: "Start Exercise",
      markComplete: "Mark as Complete",
      askCoach: "Ask Coach",
      dueIn: "Due in",
      dueToday: "Due today",
      dueTomorrow: "Due tomorrow",
      overdue: "Overdue",
      completed: "Completed",
      noDueDate: "No deadline",

      // Lesson detail modal
      sessionDetails: "Session Details",
      allLessons: "All Lessons",
      focusAreas: "Focus Areas",
      confirmed: "Confirmed",

      // Session types
      sessionTypeLesson: "Lesson",
      sessionTypePlayingLesson: "Playing Lesson",
      sessionTypePractice: "Practice",
      sessionTypeAssessment: "Assessment",
      sessionTypeFitting: "Fitting",

      // Session status
      statusConfirmed: "Confirmed",
      statusScheduled: "Scheduled",
      statusCompleted: "Completed",
      statusCancelled: "Cancelled",
      statusNoShow: "No Show",
      statusPending: "Pending",

      // Countdown text
      past: "Past",
      today: "Today",
      tomorrow: "Tomorrow",
      inDays: "In {days} days",

      // Notes section
      coachsNotes: "Coach's Notes",
      notesFromCoach: "{count} note from your coach",

      // Request/Reschedule modals
      currentSession: "Current Session",
      newPreferredTime: "New preferred time",
      whyReschedule: "Why do you need to reschedule?",
      requestReschedule: "Request Reschedule",
      rescheduleRequestSent: "Reschedule request sent to your coach!",
      location: "Location",
      sessionType: "Session Type",
      additionalNotes: "Additional Notes",
      anythingCoachShouldKnow: "Anything your coach should know...",

      // Video submission
      shareVideo: "Share Video",
      uploadVideo: "Upload Video",
      uploadingVideo: "Uploading video...",
      videoShared: "Video shared",
      sentToCoach: "Sent to coach",
      shareForFeedback: "Share a video of your practice for coach feedback",

      // Status
      done: "Done",

      // Progress page (additional keys)
      changePeriod: "Change time period",
      threeMonths: "3 Months",
      sixMonths: "6 Months",
      oneYear: "1 Year",
      stageOf: "Stage {current} of {total}",
      phaseDiscover: "DISCOVER",
      phaseDevelop: "DEVELOP",
      phasePerform: "PERFORM",
      vsMonthsAgo: "vs {months} months ago",
      by: "By",
      skillDriving: "Driving",
      skillIrons: "Irons",
      skillShort: "Short",
      skillPutting: "Putting",
      skillCourse: "Course",
      skillMental: "Mental",
      skillIronPlay: "Iron Play",
      skillShortGame: "Short Game",
      skillBunker: "Bunker",
      enterNewGoal: "Enter a new goal...",
      noGoalsSetYet: "No goals set yet",
      coachWillSetGoals: "Your coach will set goals for you to work towards",

      // Video page (additional keys)
      coachsNote: "Coach's Note",
      watch: "Watch",
      yesterday: "Yesterday",
      daysAgo: "{days} days ago",
      processing: "Processing...",
      uploaded: "uploaded",
      untitledVideo: "Untitled video",
      assignedBy: "Assigned by",
      uploadToShareWithCoach: "Upload practice videos to share with your coach",
      uploadFirstVideo: "Upload First Video",
      uploadOrWaitForCoach: "Upload your own or wait for coach-assigned videos",

      // Lesson detail popup — payment & misc
      paymentDue: "Payment Due",
      paid: "Paid",
      payNow: "Pay Now",
      paymentCompletedLabel: "Payment Completed",
      sessionFee: "Session Fee",
      weatherForecast: "Weather Forecast",
      resourcesEyebrow: "Resources",
      seeAllResources: "See all resources",
      headCoach: "Head Coach",

      // Booking dialog titles
      bookWithCalendly: "Book with Calendly",
      requestALesson: "Request a Lesson",

      // Session status pills
      upcomingPill: "Upcoming",
      donePill: "Done",

      // Reschedule modal
      rescheduleWith: "with",
      dateLabel: "Date",
      timeLabel: "Time",
      reasonOptional: "Reason (optional)",
      cancel: "Cancel",
      sending: "Sending...",

      // Find Coach page
      findCoachPage: {
        title: "Find a Coach",
        loadingClubs: "Loading your clubs…",
        selectClubToSeeCoaches: "Select a club to see coaches",
        searchPlaceholder: "Search by name, specialty...",
        noClubSelected: "No Club Selected",
        selectClubFirst: "Select a club on the Club page first (the same club you want to find coaches for)",
        goToClubHub: "Go to Club Hub",
        noCoachesFound: "No coaches found",
        noCoachesAvailable: "No coaches available",
        tryAdjustingSearch: "Try adjusting your search terms",
        noCoachesAtClub: "There are currently no coaches accepting new students at this club",
        unknownCoach: "Unknown Coach",
        yearsExperience: "years experience",
        readLess: "Read less",
        readMore: "Read more",
        specialties: "Specialties",
        moreCount: "+{count} more",
        professionalCertification: "Professional Certification",
        professionalCertifications: "Professional Certifications",
        creating: "Creating...",
        requestCoaching: "Request Coaching",
        selectClubFirstToast: "Please select a club first",
        relationshipCreated: "Coaching relationship created with {name}!",
        relationshipCreatedDescription: "You can now access your coaching dashboard.",
        alreadyHaveRelationship: "You already have a coaching relationship with this coach!",
        redirectingToDashboard: "Redirecting to your coaching dashboard...",
        relationshipPaused: "Your relationship with this coach is paused",
        contactCoachToResume: "Please contact your coach to resume the relationship.",
        failedToLoadCoaches: "Failed to load coaches",
        failedToCreateRelationship: "Failed to create relationship",
        failedToSendRequest: "Failed to send request. Please try again.",
      },
    },

    // Modals section
    modals: {
      // Create Session Modal
      createSession: "Create Session",
      selectPlayer: "Select a player",
      selectDate: "Select date",
      selectTime: "Select time",
      durationLabel: "Duration",
      locationLabel: "Location",
      locationPlaceholder: "Driving range, Course, etc.",
      focusAreasLabel: "Focus Areas",
      focusAreasPlaceholder: "Putting, Driver, Short game",
      notesLabel: "Notes",
      preparationNotes: "Any preparation notes...",

      // Add Student Modal
      addStudentTitle: "Add Student",
      searchByEmail: "Search by email",
      noMemberFound: "No member found with this email address.",
      failedToSearch: "Failed to search. Please try again.",
      addingStudent: "Adding...",

      // Assign Exercise Modal
      assignExerciseTitle: "Assign Exercise",
      selectStudent: "Select a student",
      selectExercise: "Select an exercise",
      equipment: "Equipment",
      dueDateOptional: "Due Date (optional)",
      notesForStudent: "Notes for Student (optional)",
      playerSpecificInstructions: "Player-specific instructions or modifications...",
      focusOnTempo: "Focus on tempo, this will help with your consistency...",

      // Assign Video Modal
      assignVideoTitle: "Assign Video",
      selectVideo: "Select a video",
      coachInstructions: "Coach Instructions",
      payAttentionTo: "Pay attention to the grip change at 2:30...",

      // Create Exercise Modal
      createExercise: "Create Exercise",
      exerciseName: "Exercise Name",
      exerciseNamePlaceholder: "Gate Putting Drill",
      selectCategory: "Select category",
      whatDoesThisDevelop: "What does this develop?",
      description: "Description",
      describeExercise: "Describe what this exercise is about...",
      setupInstructions: "Setup Instructions",
      howToSetUp: "How to set up the drill...",
      executionSteps: "Execution Steps",
      whatToDo: "What to do...",
      keyFocusPoints: "Key Focus Points",
      whatToFocusOn: "What to focus on...",
      equipmentNeeded: "Equipment Needed",
      customEquipment: "Custom items not listed above",
      privateNotes: "Private notes about this exercise...",

      // Add Video Modal
      addVideoTitle: "Add Video",
      videoTitle: "Video Title",
      videoTitlePlaceholder: "Grip Fundamentals",
      videoCategory: "Category",
      videoDescription: "Description",
      overviewOfTechnique: "Overview of proper grip technique...",

      // Block Time Modal
      blockTime: "Block Time",
      reason: "Reason",
      reasonPlaceholder: "Vacation, Personal, Training...",
      recurring: "Recurring",
      oneTime: "One-time",

      // Group Session Modal
      createGroupSession: "Create Group Session",
      sessionDetails: "Session Details",

      // Manual Advance Modal
      manuallyAdvance: "Manually Advance Onboarding Stage",
      currentStage: "Current Stage",
      nextStage: "Next Stage",
      graduation: "Graduation!",
      congratulatoryMessage: "Add a congratulatory message or feedback for the student...",
      confirmAdvance: "Confirm Advance",
      advancing: "Advancing...",
      finalStageWarning: "Advancing will graduate {name} from onboarding to the Rookie tier.",
      overrideAutoProgression: "Override automatic progression to advance",
      toNextStage: "to the next stage.",
      stage: "Stage",
      feedbackForStudent: "Feedback for Student",
      messageWillBeShown: "This message will be shown to the student and recorded in their progress history.",
      finalStage: "Final Stage",

      // Common modal actions
      cancel: "Cancel",
      save: "Save",
      close: "Close"
    },

    // Onboarding section
    onboarding: {
      onboardingProgress: "Onboarding Progress",
      journeyProgress: "Journey Progress",
      stageOf: "Stage {current} of {total}",
      requirementsMet: "Requirements met!",
      hasCompletedRequirements: "{name} has completed all required exercises for",
      trainingExercises: "Training Exercises",
      loadingExercises: "Loading exercises...",
      noTrainingExercises: "No training exercises assigned yet",
      coachCanAssign: "Coach can assign exercises",
      addExercise: "Add Exercise",
      noRequirementsDefined: "No requirements defined for this stage",
      enterRequirement: "Enter requirement...",
      addNewRequirement: "Add new requirement...",
      selectExercise: "Select an exercise...",
      autoCompleted: "Auto-completed",
      coachApproved: "Coach approved",
      locked: "Locked",
      coachNotes: "Coach notes"
    },

    // Onboarding loose keys
    isCurrentlyInPhase: "is currently in the",
    phase: "phase",
    viewingExercises: "Viewing exercises",
    validated: "Validated",
    requirements: "Requirements",
    criteriaToPass: "Criteria to pass this stage",
    practiceExercisesHelp: "Practice exercises to help achieve requirements",
    addRequirements: "Add Requirements",
    addExerciseToStage: "Add Exercise to Stage",
    addExerciseDescription: "Add an exercise to {stageName}. This will apply to all members in onboarding.",
    allExercisesAssigned: "All available exercises are already assigned to this stage.",
    noExercisesFoundMatching: "No exercises found matching \"{query}\"",
    hasCompletedAllRequirements: "has completed all required exercises for this stage and will auto-advance once confirmed.",

    // Manual Advance Modal (loose keys)
    manuallyAdvance: "Manually Advance Onboarding Stage",
    currentStage: "Current Stage",
    nextStage: "Next Stage",
    graduation: "Graduation!",
    congratulatoryMessage: "Add a congratulatory message or feedback for the student...",
    confirmAdvance: "Confirm Advance",
    advancing: "Advancing...",
    finalStageWarning: "Advancing will graduate {name} from onboarding to the Rookie tier.",
    overrideAutoProgression: "Override automatic progression to advance",
    toNextStage: "to the next stage.",
    stage: "Stage",
    feedbackForStudent: "Feedback for Student",
    messageWillBeShown: "This message will be shown to the student and recorded in their progress history.",
    finalStage: "Final Stage",

    // Exercise Library section
    exerciseLibrary: {
      // Sidebar
      categories: "Categories",
      source: "Source",
      clubLibrary: "Club Library",
      myCustom: "My Custom",
      usageStats: "Usage Stats",
      mostAssigned: "Most Assigned",
      thisMonth: "This Month",
      completionRate: "Completion Rate",

      // Search and sort
      searchExercises: "Search exercises...",
      createExercise: "Create Exercise",
      sortBy: "Sort by",
      sortByPopular: "Sort by: Popular",
      sortByRecent: "Sort by: Recent",
      sortByNameAZ: "Sort by: Name A-Z",
      sortByNameZA: "Sort by: Name Z-A",
      popular: "Popular",
      recent: "Recent",
      nameAZ: "Name A-Z",
      nameZA: "Name Z-A",

      // View modes
      grid: "Grid",
      list: "List",
      view: "View",

      // Loading states
      loadingExercises: "Loading exercises...",
      failedToLoadExercises: "Failed to load exercises",
      noExercisesFound: "No exercises found",
      tryAdjustingFilters: "Try adjusting your filters",

      // Pagination
      total: "total",
      exercises: "exercises",
      showing: "Showing",
      of: "of",
      previous: "Previous",
      next: "Next",

      // Actions
      assign: "Assign",
      assigned: "assigned",
      custom: "Custom",

      // Exercise detail popup labels
      description: "Description",
      equipmentNeeded: "Equipment Needed",
      video: "Video",
      watchDemoVideo: "Watch Demo Video",
      assignToStudent: "Assign to Student",
      close: "Close",

      // Categories
      allExercises: "All Exercises",
      categoryDriving: "Driving",
      categoryApproach: "Approach",
      categoryShortGame: "Short Game",
      categoryPutting: "Putting",
      categoryBunker: "Bunker",
      categoryMental: "Mental",
      categoryFitness: "Fitness",
      categoryWarmUp: "Warm Up",

      // Difficulty
      difficultyBeginner: "Beginner",
      difficultyIntermediate: "Intermediate",
      difficultyAdvanced: "Advanced",

      // Page header
      pageTitle: "Exercise Library",
      pageSubtitle: "Manage and assign practice exercises",
      // Empty state
      noExercisesYet: "No exercises yet",
      noExercisesYetHint: "Create your first exercise to start building your coaching library.",
      createFirstExercise: "Create First Exercise",
      // Form modal
      editExercise: "Edit Exercise",
      updateExerciseDesc: "Update exercise details",
      addExerciseDesc: "Add a new exercise to your library",
      updateExercise: "Update Exercise",
      savingLabel: "Saving...",
      cancel: "Cancel",
      toastCreated: "Exercise created successfully",
      toastUpdated: "Exercise updated successfully",
      toastDeleted: "Exercise deleted successfully",
      toastFailedCreate: "Failed to create exercise",
      toastFailedUpdate: "Failed to update exercise",
      toastFailedDelete: "Failed to delete exercise",
      toastFailedAssign: "Failed to assign exercise",
      confirmDelete: "Are you sure you want to delete \"{name}\"?",
    },

    // Theory section
    theory: {
      title: 'Theory Practice',
      subtitle: 'GVB Exam Preparation',
      myProgress: 'My Progress',
      theoryPractice: 'Theory',
      startPractice: 'Start Practice Round',
      practiceAgain: 'Practice Again',
      backToProgress: 'Back to Progress',
      questionsAnswered: 'Questions Answered',
      questionsAnsweredCount: '{count} questions answered',
      questionsPerRound: '10 questions per round',
      answered: 'Answered',
      accuracy: 'Accuracy',
      sessions: 'Sessions',
      recentSessions: 'Recent Sessions',
      today: 'Today',
      questionOf: 'Question {current} of {total}',
      nextQuestion: 'Next Question',
      finishQuiz: 'Finish Quiz',
      submitting: 'Submitting...',
      practiceComplete: 'Practice Complete!',
      correct: 'Correct',
      incorrect: 'Incorrect',
      yourAnswer: 'Your answer',
      correctAnswer: 'Correct answer',
      yourAnswers: 'Your Answers',
      thisRound: 'This round',
      overallAvg: 'Overall avg',
      showMoreQuestions: 'Show {count} more questions',
      showLess: 'Show less',
      noQuestionsYet: 'No practice sessions yet',
      startFirstRound: 'Start your first practice round to track progress',
      exit: 'Exit',
      tip: 'Tip',
      tipText: 'Practice regularly to improve your score. Questions you get wrong will appear more often.',
      history: 'History',
      yourPastSessions: 'Your past sessions',
      practiceRound: 'Practice Round',
    },

    // Assign Exercise Modal (detailed subsection from useAppTranslations)
    assignExercise: {
      title: 'Assign Exercise',
      studentLabel: 'Student *',
      selectStudent: 'Select student...',
      exerciseLabel: 'Exercise *',
      loadingExercises: 'Loading exercises...',
      selectExercise: 'Select exercise...',
      equipment: 'Equipment',
      reps: 'Reps',
      customizeForPlayer: 'Customize for this player',
      overrideDescription: 'Override exercise details for this specific assignment',
      customizationNote: 'These changes only apply to this assignment. The original exercise remains unchanged.',
      equipmentOverride: 'Equipment (override)',
      enterEquipment: 'Enter equipment',
      default: 'Default',
      noneSpecified: 'None specified',
      repetitionsOverride: 'Repetitions (override)',
      enterRepetitions: 'Enter repetitions',
      durationMinutes: 'Duration (minutes)',
      minutes: 'minutes',
      successCriteriaOverride: 'Success Criteria (override)',
      enterCriteria: 'Enter criteria',
      customSuccessCriteria: 'Custom success criteria for this player',
      additionalInstructions: 'Additional Instructions',
      dueDateOptional: 'Due Date (optional)',
      notesForStudentOptional: 'Notes for Student (optional)',
      cancel: 'Cancel',
      assign: 'Assign Exercise',
      assigning: 'Assigning...',
      failedToAssign: 'Failed to assign exercise',
    },

    // Add Student Modal (detailed subsection from useAppTranslations)
    addStudent: {
      title: 'Add Students',
      description: 'Select club members to add as your students. They will automatically be enrolled in the onboarding program.',
      searchPlaceholder: 'Search by name or email...',
      allMembers: 'All Members',
      activeOnly: 'Active Only',
      pendingOnly: 'Pending Only',
      selectAll: 'Select All',
      deselectAll: 'Deselect All',
      available: 'available',
      membersSelected: 'member(s) selected',
      clear: 'Clear',
      loadingMembers: 'Loading members...',
      noAvailableMembers: 'No available members',
      noMembersMatchSearch: 'No members match your search criteria',
      allMembersAreStudents: 'All eligible members are already your students',
      noMembersInClub: 'No members found in this club',
      hcp: 'HCP',
      tier: 'Tier',
      rounds: 'rounds',
      readyToAdd: 'Ready to add',
      student: 'student',
      students: 'students',
      selectMembersToAdd: 'Select members to add as students',
      cancel: 'Cancel',
      addStudents: 'Add Students',
      selectClubFirst: 'Select a club first',
      selectClubDesc: 'Switch to a specific club in the sidebar to add its members as students. Students can also find and request you directly.',
      approvalRequired: 'Approval required',
      pendingApproval: 'Your request to join {club} is pending approval. Once approved by the club admin, you\'ll be able to add members as students.',
      needsApproval: 'You need to be an approved coach at {club} before you can access its members.',
      availableCount: '{n} available',
      selectedCount: '{n} selected',
      noEmail: 'No email',
      hcpLabel: '· HCP {n}',
      addNStudents: 'Add {n} student',
      addNStudentsPlural: 'Add {n} students',
    },

    // Assign Video Modal (detailed subsection from useAppTranslations)
    assignVideo: {
      title: 'Assign Video',
      studentLabel: 'Student',
      selectStudent: 'Select student...',
      videoLabel: 'Video',
      loadingVideos: 'Loading videos...',
      noVideosYet: 'No videos in library yet.',
      selectVideo: 'Select video...',
      notesForStudentOptional: 'Notes for Student (optional)',
      failedToAssign: 'Failed to assign video',
      cancel: 'Cancel',
      assigning: 'Assigning...',
      assign: 'Assign Video',
      min: 'min',
    },

    // Create Exercise Modal (detailed subsection from useAppTranslations)
    createExercise: {
      title: 'Create Custom Exercise',
      basicInfo: 'Basic Information',
      exerciseName: 'Exercise Name *',
      category: 'Category *',
      selectCategory: 'Select category',
      skillFocus: 'Skill Focus',
      selectSkill: 'Select skill',
      difficulty: 'Difficulty',
      durationMinutes: 'Duration (minutes)',
      min: 'min',
      instructions: 'Instructions',
      description: 'Description *',
      characters: 'characters',
      optionalDetailedStructure: 'Optional: Add Detailed Structure',
      setupInstructions: 'Setup Instructions',
      executionSteps: 'Execution Steps',
      coachingCues: 'Coaching Cues / Focus Points',
      requirements: 'Requirements',
      repetitionsSets: 'Repetitions / Sets',
      equipmentNeeded: 'Equipment Needed',
      otherEquipment: 'Other Equipment (comma-separated)',
      successCriteria: 'Success Criteria',
      howMeasureSuccess: 'How do you measure success?',
      completionOnly: 'Completion only (yes/no)',
      quantitative: 'Quantitative (e.g., "8/10 shots successful")',
      qualitative: 'Qualitative (coach assessment)',
      targetPassingStandard: 'Target / Passing Standard',
      optional: 'Optional',
      additionalResources: 'Additional Resources',
      demoVideoUrl: 'Demo Video URL',
      youtubeVimeoLink: 'YouTube or Vimeo link',
      internalNotes: 'Internal Notes (visible only to you)',
      descriptionPlaceholder: 'What does this exercise work on and how does it work?',
      otherEquipmentPlaceholder: 'Other equipment, comma-separated',
      cancel: 'Cancel',
      creating: 'Creating...',
      create: 'Create Exercise',
      failedToCreate: 'Failed to create exercise',
      // Categories
      driving: 'Driving',
      iron_play: 'Iron Play',
      short_game: 'Short Game',
      putting: 'Putting',
      bunker: 'Bunker',
      mental: 'Mental',
      fitness: 'Fitness',
      warm_up: 'Warm-up',
      // Difficulties
      beginner: 'Beginner',
      intermediate: 'Intermediate',
      advanced: 'Advanced',
      // Skill Focus Options
      skillDistanceControl: 'Distance Control',
      skillAccuracy: 'Accuracy',
      skillConsistency: 'Consistency',
      skillTempoRhythm: 'Tempo & Rhythm',
      skillBallStriking: 'Ball Striking',
      skillShortGameTouch: 'Short Game Touch',
      skillDistance: 'Distance',
      skillGreenReading: 'Green Reading',
      skillAlignment: 'Alignment',
      skillPreShotRoutine: 'Pre-shot Routine',
      skillFocusConcentration: 'Focus & Concentration',
      skillCourseManagement: 'Course Management',
      skillStrengthFlexibility: 'Strength & Flexibility',
      skillBalanceStability: 'Balance & Stability',
      // Equipment Options
      equipPutter: 'Putter',
      equipWedges: 'Wedges',
      equipIrons: 'Irons',
      equipDriver: 'Driver',
      equipAlignmentSticks: 'Alignment Sticks',
      equipTees: 'Tees',
      equipGolfBalls: 'Golf Balls',
      equipCones: 'Cones',
      equipTargetFlags: 'Target Flags',
      equipTrainingAids: 'Training Aids',
      equipResistanceBands: 'Resistance Bands',
      equipYogaMat: 'Yoga Mat',
      exerciseNamePlaceholder: 'e.g. 50-yard pitch drill',
      repsSetsPlaceholder: 'e.g. 3 sets of 10',
      subtitle: 'Add to your coaching library',
    },

    // Block Time Modal (detailed subsection from useAppTranslations)
    blockTime: {
      title: 'Block Time Off',
      startDate: 'Start Date',
      startTime: 'Start Time',
      endDate: 'End Date',
      endTime: 'End Time',
      reasonOptional: 'Reason (optional)',
      repeat: 'Repeat',
      oneTime: 'One-time',
      weekly: 'Weekly',
      biweekly: 'Biweekly',
      monthly: 'Monthly',
      cancel: 'Cancel',
      blocking: 'Blocking...',
      blockTime: 'Block Time',
    },
    sessionBuilder: {
      sessionTitlePlaceholder: 'Session title…',
      notesPlaceholder: 'Add session notes for your student…',
      assignStudent: 'Assign a student',
      tapToChange: 'Tap to change',
      optionalCanSendLater: 'Optional — can send later',
      linkToLesson: 'Link to a lesson',
      linkToLessonSub: 'Available after migration · ties this plan to a calendar lesson',
      programmeLabel: 'Programme',
      item: 'item',
      items: 'items',
      lockedStudentStarted: 'Locked — student started',
      noItemsYet: 'No items yet',
      noItemsHint: 'Add exercises or videos from your library below',
      addExercise: 'Add Exercise',
      addVideo: 'Add Video',
      save: 'Save',
      saveDraft: 'Save',
      sent: 'Sent',
      send: 'Send',
      sendTo: 'Send to',
      pickStudent: 'Pick a student',
      pickStudentHint: "Drafts don't need one — only sending does",
      noStudentsYet: 'No students yet',
      noStudentsHint: 'Students will appear here once they join',
      exerciseLibraryLabel: 'Exercise Library',
      addAnExercise: 'Add an Exercise',
      videoLibraryLabel: 'Video Library',
      addToSession: 'Add to Session',
      noExercisesFound: 'No exercises found',
      tryDifferentSearch: 'Try a different search term',
      noExercisesInCategory: 'No exercises in this category yet',
      noVideosYet: 'No videos yet',
      addVideosHint: 'Add videos via the Videos tab in your coach portal',
      noVideosFound: 'No videos found',
      statusDraft: 'Draft',
      statusInProgress: 'In progress',
      statusCompleted: 'Completed',
      loading: 'Loading...',
      available: 'available',
    },
    addToSession: {
      title: 'Add to session',
      subtitle: 'Write a cue, then pick a session',
      coachingCue: 'Coaching cue',
      optional: '— optional',
      chooseSession: 'Choose session',
      loading: 'Loading…',
      noActiveSessions: 'No sessions yet',
      createSessionFirst: 'Create a session first',
      statusDraft: 'Saved',
      statusLive: 'Live',
      statusSent: 'Sent',
      statusDone: 'Done',
    },
    closeout: {
      lessonJustEnded: 'Lesson just ended · close it out',
      editingPrefix: 'Editing ·',
      howDidItGo: 'How did it go?',
      quickNotes: 'Quick notes',
      privateForYou: '— private, just for you',
      groupNote: 'Group note',
      sentToAllParticipants: '— sent to all participants with the homework',
      theySeethisWith: '— they see this with the homework',
      noteFor: 'Note for',
      individualNotes: 'Individual notes per player',
      skillsWorkedOn: 'Skills worked on',
      skillsHint: '— tap to select, then rate',
      homeworkForAll: 'Homework for all',
      participants: 'participants',
      homeworkFor: 'Homework for',
      exercise: 'Exercise',
      video: 'Video',
      session: 'Session',
      recordUpload: 'Record / Upload',
      uploading: 'Uploading',
      noHomework: 'No homework · save lesson notes only',
      saving: 'Saving…',
      saveChanges: 'Save changes',
      sendToPlayers: 'Send to',
      players: 'players',
      saveLesson: 'Save lesson',
      sentTo: 'sent to',
      itemsPlural: 'items',
      ratingOfFive: '{rating} of 5',
      sApp: "'s app",
      addSession: 'Add a session',
      templateLabel: 'template',
      templatesLabel: 'templates',
      inYourLibrary: 'in your library',
      noSavedSessions: 'No saved sessions yet.',
      buildOneHint: 'Build one in',
      coachPortalSessions: 'Coach Portal → Sessions',
      toastLessonUpdated: 'Lesson updated',
      toastLessonClosed: 'Lesson closed out and homework sent to {name}',
      toastUploadFailed: 'Upload failed',
      toastVideoAddedHomework: 'Feedback video added to homework',
      toastMissingClub: 'Missing club context — cannot upload video',
      toastFailedSaveVideo: 'Failed to save video to library',
      toastSomethingWrong: 'Something went wrong. Please try again.',
      placeholderPostNotes: 'What happened? What clicked? What needs work?',
      placeholderPlayerNotes: 'Encourage them, explain a drill, point them to the video…',
      placeholderPersonalNote: 'Personal note for {name}…',
    },
    prepCard: {
      prepLabel: 'Prep ·',
      lesson: 'lesson',
      sessionDetails: 'Session details',
      durationLabel: 'Duration (min)',
      locationLabel: 'Location',
      locationPlaceholder: 'Range, Course – Full 9…',
      focusAreas: 'Focus areas',
      focusAreasHint: '— what are you working on?',
      preLessonNotes: 'Pre-lesson notes',
      preLessonNotesHint: '— private, just for you',
      planForLesson: 'Plan for this lesson',
      planCoachOnly: '— coach-side only, not sent to',
      session: 'Session',
      video: 'Video',
      exercise: 'Exercise',
      recordUpload: 'Record / Upload',
      uploading: 'Uploading…',
      addSession: 'Add a session',
      templateSingular: 'template',
      templatesPlural: 'templates',
      inYourLibrary: 'in your library',
      buildSessionHint: 'Build a session in Coach Portal → Sessions',
      itemsPlanned: 'planned · coach-side only',
      fullProfile: 'Full profile →',
      saving: 'Saving…',
      savePrep: 'Save prep',
    },
    lessonsIdle: {
      notClosedOut: 'not closed out',
      closedOut: 'closed out',
      homework: 'Homework',
      lastLesson: 'Last lesson',
      actionNeeded: 'Action needed',
      noHomeworkYet: 'No homework yet',
      ideas: 'Ideas for this lesson',
      progressLabel: 'Homework progress',
      skillSnapshot: 'Skill snapshot',
      fullDetail: 'Full detail',
      allLessons: 'All lessons',
      show: 'Show',
      nowLabel: 'Now',
      gapDays: '{n}d gap',
      betweenLessons: 'between lessons',
      nextLesson: 'next lesson',
      scheduleOne: 'schedule one',
      notStarted: '{n} not started',
      closeItOut: 'Close it out →',
      closeOutBody: 'Close out this lesson to add your notes, rate the session, and send homework to {name}.',
      postLessonNotes: 'Post-lesson notes',
      skillAssessment: 'Skill assessment',
      nudge: 'Nudge',
      nextLessonLabel: 'Next lesson',
      upcomingLabel: 'Upcoming · {date}',
      closeOutFirst: 'Close out first',
      closeOutFirstSuffix: 'close out last lesson first',
      prepare: 'Prepare',
      closeOutFirstBody: 'Close out the last lesson first to assign homework for this one.',
      continueFocus: 'Continue focus on {area} from last lesson',
      reviewIncomplete: 'Review incomplete homework before drilling deeper',
      openSession: 'Open session →',
      exercisePlaceholder: 'Ex',
      lessonFallback: 'Lesson',
      noPreviousLesson: 'No previous lesson',
    },
    studentPanel: {
      chooseAction: 'Choose action',
      messageStudent: 'Message student',
      openConversation: 'Open coaching conversation',
      logNote: 'Log a note',
      internalNoteHint: 'Internal note for your records',
      scheduleSession: 'Schedule session',
      bookSessionHint: 'Book a coaching session',
      logNoteButton: 'Log note',
      forLabel: 'for',
      discussPlaceholder: 'What did you discuss or observe?',
      cancel: 'Cancel',
      saveNote: 'Save note',
      markHandled: 'Mark as handled',
      recheckAfter: 'Recheck after',
      threeDays: '3 days',
      sevenDays: '7 days',
      fourteenDays: '14 days',
      quickRecheck: 'Quick recheck',
      standardWindow: 'Standard window',
      longerFollowUp: 'Longer follow-up',
      snooze: 'Snooze',
      saving: 'Saving…',
      viewProfile: 'View profile',
      close: 'Close',
      tabActions: 'Actions',
      tabHistory: 'History',
      toastMarkedHandled: 'Marked as handled',
      toastMarkedHandledDesc: '{name} hidden from attention for {days} days',
      toastNoteSaved: 'Note saved',
      toastNoteSavedDesc: 'Saved for {name}',
      snoozeBody: "Hide from your attention list for a while. They'll still show as at-risk — you've just acknowledged you're on it.",
      outcomeWorked: 'Worked ✓',
      outcomeNoResponse: 'No response',
      outcomeAcknowledged: 'Acknowledged',
      outcomeAwaiting: 'Awaiting · checking in 7d',
      historyUnavailable: 'History unavailable — no club context.',
      noActionsLogged: 'No actions logged yet.',
      noActionsHint: 'Use the Actions tab to log a note or message.',
      couldNotLoadHistory: 'Could not load action history',
      snoozeRemovedDesc: 'Snooze removed',
      snoozedForDesc: 'Snoozed for {days}d',
      markedHandledDesc: 'Marked handled',
      actionReEngagement: 'Re-engagement',
      actionFollowUpNote: 'Follow-up note',
      actionComp: 'Comp',
      actionReassignCoach: 'Reassign coach',
      actionMarkedHandled: 'Marked handled',
      statusAtRisk: 'At risk',
      statusSnoozed: 'Snoozed',
      statusOnboarding: 'Onboarding · Stage {stage}/9',
      statusHcp: 'HCP {hcp}',
      statusActive: 'Active student',
      statusInactive: '{days}d inactive',
      statusPending: '{count} pending',
    },
    timeline: {
      notClosedOut: 'not closed out',
      closedOut: 'closed out',
      comingUp: 'Coming up',
      daysGap: 'd gap',
      firstLesson: 'first lesson',
      homework: 'Homework',
      lastLessonNotClosed: 'Last lesson · not closed out',
      lastLesson: 'Last lesson',
      closeOut: 'Close out →',
      closedOutLabel: 'Closed out',
      notesNotSent: 'Notes & homework not sent yet',
      closeItOut: 'Close it out →',
      postLessonNotes: 'Post-lesson notes',
      skillAssessment: 'Skill assessment',
      homeworkSent: 'Homework sent',
      prepNotes: 'Prep notes',
      noPrepNotes: 'No prep notes yet — tap to add',
      homeworkProgress: 'Homework progress',
      planForLesson: 'Plan for this lesson',
      coachSideOnly: 'coach-side only',
      skillSnapshot: 'Skill snapshot',
      allLessons: 'All lessons',
      showLess: 'Show less',
      show: 'Show',
    },
    sessionDetail: {
      couldNotLoad: 'Could not load session.',
      notSentYet: 'This session hasn\'t been sent to anyone yet.',
      editSession: 'Edit session',
      deleteSession: 'Delete',
      duplicate: 'Duplicate',
      sendToStudent: 'Send to student',
      sendAgain: 'Send again',
      activeWith: 'Active with',
      oneStudentActive: '1 student active',
      assignedTo: 'Assigned to',
      exercises: 'exercises',
      videos: 'videos',
      exercise: 'exercise',
      video: 'video',
      message: 'Message',
      viewProfile: 'View student profile',
      complete: 'Complete ✓',
      done: 'done',
    },
    profile: {
      contactProfile: 'Contact & profile',
      edit: 'Edit',
      phone: 'Phone',
      membership: 'Membership',
      dateOfBirth: 'Date of birth',
      fullName: 'Full name',
      email: 'Email',
      preferredTee: 'Preferred tee',
      goals: 'Goals',
      addGoal: 'Add goal',
      editGoal: 'Edit goal',
      lessonNotes: 'Lesson notes',
      noLessonsYet: 'No lessons yet',
      bookFirstLesson: 'Book first lesson',
      noNotesYet: 'No notes yet — close out a lesson to add notes.',
      sharedWithPlayer: 'Shared with player',
      skillAssessment: 'Skill assessment',
      engagementHealth: 'Engagement Health',
      golf: 'Golf',
      social: 'Social',
      communication: 'Communication',
      community: 'Community',
      adminInsight: 'Admin Insight',
      vsClubAverage: 'vs. Club Average',
      retentionRisk: 'Retention Risk',
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      activeDimensions: 'Active across dimensions',
      someDimensions: 'Some dims need attention',
      lowActivity: 'Low activity — reach out',
      biggestOpportunity: 'Biggest Opportunity',
      eventHistory: 'Event History & Participation',
      attended: 'attended',
      rate: 'rate',
      attendanceTitle: 'Attendance — Last 12 Months',
      attendedTab: 'Attended',
      registeredTab: 'Registered (upcoming)',
      eventPreference: 'Event Preference',
      favourite: 'Favourite',
      eventTimeline: 'Event Timeline',
      upcoming: 'Upcoming',
      registered: 'registered',
      recent: 'Recent',
      cancelled: 'cancelled',
      quickActions: 'Quick Actions',
      bookLesson: 'Book Lesson',
      sendSession: 'Send a Session',
      assignExercise: 'Assign Exercise',
      assignVideo: 'Assign Video',
      buildSession: 'Build Practice Session',
      focusAreas: 'Focus areas',
      noFocusAreas: 'No focus areas set',
      contactHistory: 'Contact History',
      interaction: 'interaction',
      interactions: 'interactions',
      noInteractions: 'No interactions yet',
      contactHistoryEmpty: 'Contact history will appear here when staff interact with this member.',
      today: 'Today',
      yesterday: 'Yesterday',
      memberNotes: 'Member Notes',
      noMemberNotes: 'No notes yet.',
      deleteNote: 'Delete this note?',
      memberEyebrow: 'MEMBER',
      editNote: 'Edit Note',
      addNote: 'Add Note',
      cancel: 'Cancel',
      saveNote: 'Save Note',
      note: 'Note',
      writeNote: 'Write your note here…',
      linkToRound: 'Link to round or event',
      optional: 'optional',
      noLink: 'No link',
      eventsTournaments: 'Events / Tournaments',
      multiplayerRounds: 'Multiplayer Rounds',
      soloRounds: 'Solo Rounds',
      recentActivity: 'Recent Activity',
      noRecentActivity: 'No recent activity found.',
      upcomingLesson: 'Upcoming lesson',
      nextLesson: 'Next lesson',
      away: 'away',
      prepare: 'Prepare',
      noLessonScheduled: 'No lesson scheduled',
      bookLessonButton: 'Book Lesson',
      homeworkSection: 'Homework from last lesson',
      done: 'done',
      noHomework: 'No homework sent for the last lesson',
      sendHomework: 'Send homework',
      openLesson: 'Open lesson',
      goal: 'Goal',
      saving: 'Saving…',
      save: 'Save',
      noGoalsSet: 'No goals set yet',
      lessonSingular: 'lesson',
      lessonPlural: 'lessons',
      socialActivity: 'Social Activity',
      postedAnUpdate: 'Posted an update',
      totalAttended: 'Total attended',
      showUpRate: 'Show-up rate',
      upcomingEvents: 'Upcoming',
      monthlyAvg: 'Monthly avg',
      startOnboarding: 'Start Onboarding',
      alreadyInOnboarding: 'Already in onboarding',
      sendEmail: 'Send Email',
      assignCoach: 'Assign Coach',
      makeAdmin: 'Make Admin',
      message: 'Message',
      suspendMember: 'Suspend Member',
      activateMember: 'Activate Member',
      lastLesson: 'Last lesson',
    },
    exerciseBrowse: {
      title: 'Browse Exercises',
      loading: 'Loading...',
      available: 'exercises available',
      searchPlaceholder: 'Search exercises by name or description...',
      all: 'All',
      longGame: 'Long Game',
      chipping: 'Chipping',
      pitching: 'Pitching',
      putting: 'Putting',
      bunker: 'Bunker',
      mental: 'Mental',
      warmUp: 'Warm Up',
      noExercisesFound: 'No exercises found',
      tryDifferentSearch: 'Try a different search term',
      noCategoryExercises: 'No exercises in this category yet',
      play: 'Play',
      preview: 'Preview',
      saveForLater: 'Save for Later',
    },
    sentSessions: {
      title: 'Practice Sessions',
      activeFromCoach: 'active from your coach',
      completed: 'completed',
      done: 'done',
      inProgress: 'In progress',
      sent: 'Sent',
      notStarted: 'not started',
    },
    practiceExercise: {
      notFound: 'Exercise not found.',
      goBack: 'Go back',
      of: 'of',
      allExercises: 'All exercises',
      done: 'done',
      instructions: 'Instructions',
      equipment: 'Equipment',
      duration: 'Duration',
      min: 'min',
      reps: 'Reps',
      pause: 'Pause',
      resume: 'Resume',
      complete: 'Complete',
      nextExercise: 'Next exercise…',
      sessionDone: 'Session done!',
      markDoneNextUp: 'Mark Done · Next up',
      markDone: 'Mark as Done',
    },
    sessions: {
      statusDraft: 'Draft',
      statusSent: 'Sent',
      statusInProgress: 'In progress',
      statusCompleted: 'Completed',
      coachProfileError: "We couldn't find a coach profile for your current club selection.",
      coachProfileHint: 'Make sure you have a coach profile set up under',
      coachProfileLink: 'Coach Profile',
      back: 'Back',
      newSession: 'New session',
      searchPlaceholder: 'Search sessions or students…',
      filter: 'Filter',
      filterStatus: 'Status',
      clearFilter: 'Clear',
      sectionLive: 'Live',
      sectionLibrary: 'Library',
      sectionDrafts: 'Drafts',
      statusLive: 'Live',
      editSession: 'Edit session',
      deleteSession: 'Delete',
      sendToStudent: 'Send to student',
      sendAgain: 'Send again',
      sessionEyebrow: 'Session',
      tabSessions: 'Sessions',
      tabExercises: 'Exercises',
      tabVideos: 'Videos',
      newExercise: 'New exercise',
      addVideo: 'Add video',
      emptyFiltered: 'Nothing here',
      emptyTitle: 'No sessions yet',
      emptyFilteredDesc: 'Try a different filter, or build a new session.',
      emptyDesc: 'Build your first practice session from exercises and videos in your library.',
      buildSession: 'Build a session',
      filterAll: 'All',
      filterLive: 'Live',
      filterLibrary: 'Library',
      filterDrafts: 'Drafts',
    },
    theoryPage: {
      title: 'Theory Practice',
    },
    quest: {
      createTitle: 'Create New Quest',
      cancel: 'Cancel',
      createButton: 'Create Quest',
      categoryLabel: 'Category',
      autoIconNote: 'auto-generates icon',
      questNameLabel: 'Quest Name',
      questNameExample: 'e.g., Master the Grip',
      requirementsLabel: 'Requirements',
      requirementsHint: 'What do members need to do to complete this quest?',
      leaguePointsLabel: 'League Points (XP Reward)',
      ptsLabel: 'pts',
      pointsHint: 'Points earned when quest is completed (default: 50)',
    },

    // My Practice tab
    myPractice: {
      skillsOverview: 'Skills Overview',
      tapSkillHint: 'Tap a skill to see programs & exercises',
      avgLabel: 'Avg {n}%',
      savedExercisesLabel: 'Saved exercises',
      planSessionLabel: 'Plan a session',
      needsWork: 'Needs work — focus here',
      lookingGood: 'Looking good!',
      recommendedProgram: 'Recommended Program',
      recommendedExercises: 'Recommended Exercises',
      browseAll: 'All exercises',
      saved: 'Saved',
      save: 'Save',
      noExercises: 'No exercises yet',
      noExercisesSub: 'Your coach can add some, or browse the library below',
      play: 'Play',
      programBadge: 'Program',
      lesson: 'Lesson',
      active: 'Active',
      addArrow: 'Add →',
      doneBefore: 'Done before',
      trainNow: 'Train Now',
      trainNowSub: 'Pick your focus and go',
      completing: 'Completing...',
      markAllComplete: 'Mark All Complete',
      yourQueue: 'Your Queue',
      focusArea: 'Focus Area',
      duration: 'Duration',
      startPractice: 'Start Practice',
      startPractising: 'Start Practising',
      noActivePrograms: 'No active programs',
      noActiveProgramsSub: 'Tap Browse to start one',
      browsePrograms: 'Browse programs',
      continue: 'Continue',
      startProgram: 'Start Program',
      noExercisesLinked: 'No exercises linked yet',
      exercises: 'Exercises',
      libraryTitle: 'My Exercise Library',
      savedTab: 'Saved',
      historyTab: 'History',
      searchSaved: 'Search saved exercises…',
      searchHistory: 'Search history…',
      noSavedYet: 'No saved exercises yet',
      noMatches: 'No matches',
      tryDifferentFilter: 'Try a different search or filter',
      tapToSaveHint: 'Tap + on any exercise to save it here',
      noHistoryYet: 'No completed exercises yet',
      noHistorySub: 'Complete exercises to see your history here',
      noHistoryCoachSub: 'Complete exercises from your coach to see them here',
      remove: 'Remove',
      viewArrow: 'View →',
      all: 'All',
      exerciseSingular: 'exercise',
      exercisePlural: 'exercises',
      queuedSuffix: 'queued',
      xpSuffix: 'XP',
    },
  },
  bookSession: {
    bookNewSession: 'Book New Session',
    player: 'Player',
    selectPlayer: 'Select player...',
    sessionType: 'Session Type',
    lesson: 'Lesson',
    playingLesson: 'Playing Lesson',
    practiceSession: 'Practice Session',
    assessment: 'Assessment',
    clubFitting: 'Club Fitting',
    date: 'Date',
    time: 'Time',
    durationMinutes: 'Duration (minutes)',
    minutes30: '30 minutes',
    minutes60: '60 minutes',
    minutes90: '90 minutes',
    location: 'Location',
    focusAreas: 'Focus Areas (comma-separated)',
    preSessionNotes: 'Pre-Session Notes',
    cancel: 'Cancel',
    bookSession: 'Book Session',
    booking: 'Booking...',
    enterAddress: 'Enter address',
    enterName: 'Enter name',
    addNotes: 'Add notes...',
    optional: 'optional',
    min: 'min',
    searchPlayers: 'Search players...',
    noPlayersFound: 'No players found',
  },

  // Mascot / tier celebration
  mascot: {
    tier: {
      firstSkills: 'First Skills',
      courseReady: 'Course Ready',
      developing: 'Developing',
      advancing: 'Advancing',
      skilled: 'Skilled',
      elite: 'Elite',
      master: 'Master',
    },
    evolution: {
      babyBirdie: 'Baby Birdie',
      fledgling: 'Fledgling',
      apprentice: 'Apprentice',
      journeyman: 'Journeyman',
      skilled: 'Skilled Birdie',
      elite: 'Elite Birdie',
      master: 'Master Birdie',
    },
    celebration: {
      legendary: 'Legendary Status!',
      evolved: "You've evolved!",
      evolvedInto: 'Birdie evolved into',
      tapToContinue: 'Tap anywhere to continue',
    },
  },

  // Coach affiliations page
  coachAffiliations: {
    title: 'Club Affiliations',
    searchClubs: 'Search Clubs',
    activeClubs: 'Active Clubs',
    pendingRequests: 'Pending Requests',
    totalAffiliations: 'Total Affiliations',
    noAffiliationsTitle: 'No Club Affiliations Yet',
    noAffiliationsBody: 'Clubs can invite you, or search and request to join a club below.',
    noFilteredAffiliations: 'No affiliations in this filter.',
    joined: 'Joined',
    accepted: 'Accepted',
    clubInvitedYou: 'Club invited you',
    awaitingResponse: 'Awaiting response…',
    accept: 'Accept',
    decline: 'Decline',
    leave: 'Leave',
    statusActive: 'Active',
    statusPending: 'Pending',
    statusInvite: 'Invite',
    searchClubsPlaceholder: 'Search clubs by name or location...',
    noClubsFound: 'No clubs found',
    noClubsFoundQuery: 'No clubs found matching your search. Try a different search.',
    searchHint: 'Start typing to search for clubs',
    affiliated: 'Affiliated',
    requestToJoin: 'Request to Join',
    noCoachProfileError: 'Coach profile not found. Please complete your profile setup first.',
    requestSentSuccess: 'Request sent! The club will review your application.',
    requestFailedError: 'Failed to request club affiliation',
  },

  // Practice / my practice
  practice: {
    focusArea: 'Focus area',
    tapForPrograms: 'Tap to see practice programs',
    programsAvailable: 'programs available',
    needsWork: 'Needs work — focus here',
    lookingGood: 'Looking good!',
    noPrograms: 'No programs yet',
    noProgramsHint: 'Your coach can assign one, or browse the library',
    programBadge: 'Program',
    queued: 'Queued',
    queue: '+ Queue',
    enroll: 'Enroll',
    programAdded: 'Program added',
    addFailed: 'Could not add program',
    addedToQueue: 'Added to queue',
    queueFailed: 'Could not add to queue',
    // CreateSessionPage strings
    pageTitle: 'My Practice',
    pageSubtitle: 'Build your session',
    skillsBreakdown: 'Skills Breakdown',
    skillsOverview: 'Skills Overview',
    browseAllExercises: 'Browse all exercises',
    myPrograms: 'My Programs',
    browse: 'Browse',
    recentSessions: 'Recent Sessions',
    viewAll: 'View all',
    todaysSession: "Today's Session",
    trainNow: 'Train Now',
    pickFocusAndGo: 'Pick your focus and go',
    yourQueue: 'Your Queue',
    focusAreaLabel: 'Focus Area',
    durationLabel: 'Duration',
    startPractice: 'Start Practice',
    addToPractice: 'Add to Practice',
    savedForPractice: 'Saved for Practice',
    noSavedExercises: 'No saved exercises',
    tapToSave: 'Tap + on any exercise to save it here',
    browsePrograms: 'Browse Programs',
    sessionHistory: 'Session History',
    searchHistory: 'Search history…',
    noCompletedExercises: 'No completed exercises yet',
    noMatches: 'No matches',
    completeToSeeHistory: 'Complete exercises to see your history here',
    tryDifferentSearch: 'Try a different search',
    noActivePrograms: 'No active programs',
    tapBrowseToStart: 'Tap Browse to start one',
    browseProgramsLink: 'Browse programs',
    noExercisesLinked: 'No exercises linked yet',
    markFinished: 'Mark finished',
    dropProgram: 'Drop program',
    startProgram: 'Start Program',
    active: 'Active',
    exercises: 'Exercises',
    addLabel: 'Add',
    lesson: 'Lesson',
    // Skill category names
    skillShortGame: 'Short Game',
    skillPutting: 'Putting',
    skillLongGame: 'Long Game',
    skillBunker: 'Bunker',
    skillMentalGame: 'Mental Game',
    // Focus chips (training focus areas)
    focusLongGame: 'Long Game',
    focusShortGame: 'Short Game',
    focusPutting: 'Putting',
    focusBunker: 'Bunker',
    focusMental: 'Mental',
    // Duration options
    dur15min: '15 min',
    dur30min: '30 min',
    dur45min: '45 min',
    dur60min: '60 min',
  },
  clubActivity: {
    pageTitle: 'Activity Hub',
    yourAchievementCenter: 'Your golf achievement center',
    clubSetupRequired: 'Club setup required',
    clubSetupDesc: 'Please join a club or set up your club membership to access activity features.',
    unableToLoad: 'Unable to load activity data',
    anonymousPlayer: 'Anonymous Player',
    clubMember: 'Club Member',
    tapToSeeProgress: 'Tap to see progress',
    leagueLabel: 'League',
    levelLabel: 'Level',
    totalAchievements: 'Total Achievements',
    questAchievements: 'Quest Achievements',
    questsDone: 'Quests Done',
    completed: 'completed',
    points: 'Points',
    xp: 'XP',
    leagueProgress: 'League Progress',
    completeQuestsToAdvance: 'Complete quests to advance leagues',
    completeChallengesForXP: 'Complete challenges to gain XP',
    pointsToNextLevel: 'points to next level',
    tabQuests: 'Quests',
    tabChallenges: 'Challenges',
    availableQuestAchievements: 'Available Quest Achievements',
    leaguePoints: 'League Points',
    clickToViewRequirements: 'Click to view requirements',
    noAvailableQuests: 'No Available Quests',
    noAvailableQuestsDesc: 'Complete rounds to unlock new quest achievements!',
    accomplishedQuests: 'Accomplished Quests',
    noAccomplishedQuests: 'No Accomplished Quests Yet',
    noAccomplishedQuestsDesc: 'Complete your first quest to see your achievements here!',
    completedOn: 'Completed on',
    completedBadge: '✓ Completed',
    challengesComingSoon: 'Challenges Coming Soon',
    challengesComingSoonDesc: 'Focus on quests to advance your tier and earn XP.',
    showLess: 'Show Less',
    viewAll: 'View All ({count})',
    // League tier labels
    leagueTierFirstSkills: 'First Skills',
    leagueTierCourseReady: 'Course Ready',
    leagueTierDeveloping: 'Developing',
    leagueTierAdvancing: 'Advancing',
    leagueTierSkilled: 'Skilled',
    leagueTierElite: 'Elite',
    leagueTierMaster: 'Master',
    // Level titles
    levelTitleNewAdventurer: 'New Adventurer',
    levelTitleBeginnerGolfer: 'Beginner Golfer',
    levelTitleDevelopingGolfer: 'Developing Golfer',
    levelTitleExperiencedGolfer: 'Experienced Golfer',
    levelTitleSkilledGolfer: 'Skilled Golfer',
    levelTitleAdvancedGolfer: 'Advanced Golfer',
    levelTitleGolfPro: 'Golf Pro',
    levelTitleGolfLegend: 'Golf Legend',
    levelTitleGolfMaster: 'Golf Master',
  },
  scoringAnalytics: {
    pageTitle: 'Scoring Analytics',
    pageSubtitle: 'Score analysis and performance trends',
    loading: 'Loading scoring analytics...',
    errorTitle: 'Scoring Data Unavailable',
    noData: 'No scoring data available',
    currentAverage: 'Current Average',
    personalBest: 'Personal Best',
    consistencyIndex: 'Consistency Index',
    standardDeviation: 'Standard deviation',
    roundsUnder90: 'Rounds Under 90',
    ofRounds: '% of rounds',
    tabTrends: 'Scoring Trends',
    tabDistribution: 'Distribution',
    tabCourses: 'By Course',
    tabInsights: 'Insights',
    scoringAverageTrend: 'Scoring Average Trend',
    scoreDistribution: 'Score Distribution',
    performanceZones: 'Performance Zones',
    excellentLabel: 'Excellent (Under 80)',
    goodLabel: 'Good (80-89)',
    fairLabel: 'Fair (90+)',
    performanceByCourse: 'Performance by Course',
    avgBest: 'Avg (Best: {best})',
    currentStreaks: 'Current Streaks',
    currentStreak: 'Current Streak',
    bestStreak: 'Best Streak',
    keyInsights: 'Key Insights',
    insightTrend: 'Trend',
    insightStrength: 'Strength',
    insightGoal: 'Goal',
    insightRecommendation: 'Recommendation',
  },
  coach: {
    nav: {
      schedule: 'Schedule',
      myStudents: 'My Students',
      messages: 'Messages',
      sessions: 'Practice Sessions',
      revenue: 'Revenue',
      profile: 'Profile',
      settings: 'Settings',
      coachPortal: 'Coach Portal',
    },
    header: {
      scheduleTitle: 'Schedule',
      studentsTitle: 'My Students',
      messagesTitle: 'Messages',
      sessionsTitle: 'Practice Sessions',
      revenueTitle: 'Revenue',
      profileTitle: 'Profile',
      settingsTitle: 'Settings',
      studentProfileTitle: 'Student Profile',
      scheduleSubtitle: 'Manage bookings, availability, and your calendar',
      studentsSubtitle: 'Manage your students and track progress',
      messagesSubtitle: 'Chat with your students',
      sessionsSubtitle: 'Build and send practice sessions',
      revenueSubtitle: 'Track your coaching earnings',
      profileSubtitle: 'Manage your public coaching profile',
      settingsSubtitle: 'Notifications, integrations, and preferences',
      studentProfileSubtitle: 'Student progress and session history',
    },
    clubSwitcher: {
      allStudents: 'All Students',
      selectClub: 'Select Club',
      manageAffiliations: 'Manage affiliations',
      pending: '(pending)',
    },
    affiliations: {
      title: 'Club Affiliations',
      eyebrow: 'Coach Portal',
      noAffiliationsTitle: 'No club affiliations yet',
      noAffiliationsBody: 'Clubs can invite you to join their team, or search to request joining directly.',
      statsActive: 'Active',
      statsPending: 'Pending',
      statsTotal: 'Total',
      sectionPending: 'Pending',
      sectionActive: 'Active clubs',
      clubInvitedYou: 'Club invited you',
      awaitingResponse: 'Awaiting response…',
      accept: 'Accept',
      decline: 'Decline',
      leaveClub: 'Leave club',
      close: 'Close',
      searchClubs: 'Search clubs',
    },
    search: {
      title: 'Search Clubs',
      eyebrow: 'Coach Portal',
      placeholder: 'Club name or location…',
      noClubsFound: 'No clubs found',
      noResultsQuery: 'No results for this search. Try a different search.',
      startTyping: 'Start typing to search.',
      affiliated: 'Affiliated',
      request: 'Request',
      close: 'Close',
    },
    session: {
      untitled: 'Untitled',
      coachingCuePlaceholder: 'Coaching cue — e.g. \'Focus on tempo on the takeaway\'',
      done: 'Done',
      exerciseBadge: 'EX',
      videoBadge: 'VID',
      exercise: 'Exercise',
      video: 'Video',
      uploaded: 'Uploaded',
      noDescriptionAvailable: 'No description available for this exercise yet.',
    },
    items: {
      description: 'Description',
      duration: 'Duration',
      reps: 'Reps',
      coachNotes: 'Coach notes',
      completed: 'Completed',
      min: 'min',
      play: 'Play',
      markAsDone: 'Mark as done',
      doneBang: 'Done!',
      noVideoAvailable: 'No video available',
      couldNotSaveProgress: 'Could not save progress. Try again.',
      equipment: 'Equipment',
      hoursAgo: '{n}h ago',
      yesterday: 'yesterday',
      daysAgo: '{n} days ago',
      weekAgo: '1 week ago',
      weeksAgo: '{n} weeks ago',
    },
    lessonPrep: {
      panelEyebrow: 'Lesson prep',
      panelEyebrowGroup: 'Group Lesson Prep',
      editDurationLabel: 'Duration (minutes)',
      editLocationLabel: 'Location',
      editLocationPlaceholder: 'e.g. Range, Course – Full 9…',
      editPreNotesLabel: 'Pre-lesson notes',
      editPreNotesPlaceholder: 'Notes visible to you before the session…',
      nextLessonAway: 'Next lesson · {cd} away',
      planForThisLesson: 'Plan for this lesson',
      itemCount: '{n} item',
      itemCountPlural: '{n} items',
      typeVideo: 'Video',
      typeExercise: 'Exercise',
      prepNotesLabel: 'Prep notes',
      prepNotesEmpty: 'No prep notes yet — tap to add',
      lastLesson: 'Last lesson',
      statusClosed: 'Closed',
      statusNeeded: 'Needed',
      coachNotes: 'Coach notes',
      sharedWithPlayer: 'Shared with player',
      homeworkProgress: 'Homework progress',
      homeworkDone: '{done}/{total} done',
      itemPending: 'pending',
      notClosedTitle: 'Last lesson not closed out',
      notClosedBody: 'Add notes and send homework before this lesson starts.',
      skillAssessment: 'Skill assessment',
      skillAvg: 'Avg {avg}/100',
      sessionRating: 'Session rating',
      assessmentNotes: 'Assessment notes',
      recentHistory: 'Recent history',
      btnBack: 'Back',
      btnSaveChanges: 'Save changes',
      btnEditSession: 'Edit session',
      btnFullProfile: 'Full profile →',
      toastUpdated: 'Session updated',
      toastUpdateFailed: 'Failed to update session',
      sharedBadge: 'Shared',
      nowCountdown: 'now',

      // Session side panel — section headers
      pastSession: 'Past session',
      upcomingSession: 'Upcoming session',
      nowInSession: 'NOW · IN SESSION',
      upcomingPrefix: 'UPCOMING',
      pastSessionPrefix: 'PAST SESSION',
      ideasForLesson: 'Ideas for this lesson',
      videosAssigned: 'Videos assigned',
      homework: 'Homework',
      watchedBadge: 'Watched',
      assignedBadge: 'Assigned',
      editNotes: 'Edit notes',
      closeItOut: 'Close it out',
      viewSession: 'View session →',
      closeOut: 'Close out →',
      cancellingLabel: 'Cancelling…',
      prepareArrow: 'Prepare →',

      // Edit form fields
      durationLabel: 'Duration (minutes)',
      locationLabel: 'Location',

      // Email compose panel
      toLabel: 'To',
      subjectLabel: 'Subject',
      messageLabel: 'Message',
      subjectPlaceholder: 'Subject…',
      messagePlaceholder: 'Write your message…',
    },
    revenue: {
      byLessonType: 'By Lesson Type',
      byStudent: 'By Student',
      recentTransactions: 'Recent Transactions',
      searchPlaceholder: 'Search by student, type or status…',
      clearFilter: 'Clear',
      typeLabel: 'Type',
      noTransactions: 'No transactions yet',
      noTransactionsMatch: 'No transactions match',
      clearSearch: 'Clear search',
      date: 'Date',
      amount: 'Amount',
    },
  },
  crm: {
    actionMenu: {
      actionsFor: 'Actions',
      sendReEngagement: 'Send re-engagement',
      reassignCoach: 'Reassign coach',
      compSomething: 'Comp something',
      logFollowUp: 'Log follow-up',
      markAsHandled: 'Mark as handled',
      undoSnooze: 'Undo snooze',
      viewFullProfile: 'View full profile',
    },
    bulkAction: {
      selected: 'selected',
      member: 'member',
      members: 'members',
      clear: 'Clear',
    },
    timeline: {
      noActionsTitle: 'No actions logged yet.',
      noActionsHint: 'Use the Send tab to log a follow-up.',
      couldNotLoad: 'Could not load action history',
      outcomeEngaged: 'Previous action worked ✓',
      outcomeNoResponse: 'No response followed',
      outcomeAcknowledged: 'Acknowledged',
      outcomePending: 'Awaiting response · checking in 7d',
      actionTypes: {
        reEngagement: 'Re-engagement',
        followUp: 'Follow-up note',
        comp: 'Comp',
        reassignCoach: 'Reassign coach',
        markedHandled: 'Marked handled',
      },
    },
    confirmSheets: {
      sendReEngagement: 'Send re-engagement',
      to: 'to',
      templateLabel: 'Template',
      subjectLabel: 'Subject',
      messageLabel: 'Message',
      noteLabel: 'Note',
      recipientsLabel: 'Recipients',
      messagePreviewLabel: 'Message preview',
      actionWillBeLogged: "Action will be logged to",
      sProfile: "'s profile",
      savedWithTimestamp: 'Saved with timestamp to',
      editMessage: 'Edit message',
      sendNow: 'Send now',
      sending: 'Sending…',
      saveNote: 'Save note',
      cancel: 'Cancel',
      logFollowUp: 'Log follow-up',
      reassignCoach: 'Reassign coach',
      currently: 'Currently:',
      daysSinceLastSession: 'd since last session',
      coachPickerComingSoon: 'Coach picker coming in v2 — requires coach availability data.',
      confirmReassignment: 'Confirm reassignment',
      compSomething: 'Comp something',
      chooseWhatToOffer: 'Choose what to offer',
      voucherSystemComingSoon: 'Voucher system integration coming in v2.',
      confirmComp: 'Confirm comp',
      markAsHandled: 'Mark as handled',
      snoozeBody: "Hide this member from urgent attention for a while. They'll still count as at-risk on the dashboard.",
      recheckAfter: 'Recheck after',
      returnsToRed: 'Returns to red "Action needed" if no activity by then',
      saving: 'Saving…',
      undoSnooze: 'Undo snooze',
      undoSnoozeBody: 'Remove the "Handled" snooze and return this member to your urgent at-risk view immediately.',
      bulkSend: 'Bulk send',
      personalised: 'is personalised per recipient.',
      actionLoggedEachProfile: "Action will be logged to each member's profile",
      confirm: 'Confirm',
      comingSoon: 'Coming soon',
      messageSent: 'Message sent',
      sentTo: 'Sent to',
      failedToSend: 'Failed to send',
      followUpLogged: 'Follow-up logged',
      savedFor: 'Saved for',
      snoozeRemoved: 'Snooze removed',
      backOnUrgentList: 'back on urgent list',
      messagesSent: 'Messages sent',
      failedToUndo: 'Failed to undo',
      markedAsHandled: 'Marked as handled',
      hiddenFromUrgent: 'hidden from urgent attention for',
      days: 'days',
    },
    // Tee Manager (direct alias of courseManager.teeManager)
    teeManager: {
      title: 'Course Tees',
      addTee: 'Add Tee',
      teeName: 'Tee Name',
      teeColor: 'Tee Color',
      selectColor: 'Select a color...',
      courseRatingMen: 'Course Rating (Men)',
      slopeRatingMen: 'Slope Rating (Men)',
      courseRatingWomen: 'Course Rating (Women)',
      slopeRatingWomen: 'Slope Rating (Women)',
      updateTee: 'Update Tee',
      cancel: 'Cancel',
      noTeesYet: 'No tees configured yet.',
      addTeeToStart: 'Add at least one tee to configure hole yardages.',
      teeNameHeader: 'Tee Name',
      colorHeader: 'Color',
      ratingMHeader: 'Rating (M)',
      slopeMHeader: 'Slope (M)',
      ratingWHeader: 'Rating (W)',
      slopeWHeader: 'Slope (W)',
      totalYardageHeader: 'Total Yardage',
      actionsHeader: 'Actions',
      yds: 'yds',
      notConfigured: 'Not configured',
      deleteConfirm: 'Are you sure you want to delete this tee? All yardages for this tee will also be deleted.',
      blackChampionship: 'Black (Championship)',
      blueMen: 'Blue (Men)',
      whiteMen: 'White (Men)',
      yellowSenior: 'Yellow (Senior Men)',
      redWomen: 'Red (Women)',
      greenJunior: 'Green (Junior)',
      gold: 'Gold',
      silver: 'Silver',
    },

    // Hole Manager (direct alias of courseManager.holeManager)
    holeManager: {
      title: 'Hole Details & GPS',
      holesCount: '{count} holes',
      holeHeader: 'Hole',
      parHeader: 'Par',
      siMenHeader: 'SI (M)',
      siWomenHeader: 'SI (W)',
      gpsHeader: 'GPS',
      actionsHeader: 'Actions',
      collapse: 'Collapse',
      expand: 'Expand',
      holeDetails: 'Hole {number} Details',
      par: 'Par',
      strokeIndexMen: 'Stroke Index (Men)',
      strokeIndexWomen: 'Stroke Index (Women)',
      yardagesPerTee: 'Yardages per Tee',
      gpsCoordinates: 'GPS Coordinates',
      addTee: '+ Add Tee',
      addGreen: '+ Add Green',
      noGpsCoordinates: 'No GPS coordinates set for this hole.',
      clickToStart: 'Click "Add Tee" or "Add Green" to start.',
      type: 'Type',
      latitude: 'Latitude',
      longitude: 'Longitude',
      frontTee: 'Front Tee',
      backTee: 'Back Tee',
      green: 'Green',
      saveHoleDetails: 'Save Hole Details',
      saveGps: 'Save GPS',
      nextHole: 'Next Hole →',
      noTeesConfigured: 'No Tees Configured',
      addTeesFirst: 'Please add at least one tee before configuring holes.',
      noGps: 'No GPS',
      partial: 'Partial',
      complete: 'Complete',
    },

    // Communication Task Modal
    commsTaskModal: {
      title: 'Create Communication Task',
      description: 'Create a task related to member communications or requests.',
      taskTitle: 'Task Title *',
      taskTitlePlaceholder: 'e.g., Follow up with member about tournament registration',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'Add details about the task...',
      category: 'Category',
      priority: 'Priority',
      assignTo: 'Assign To (optional)',
      selectStaffMember: 'Select staff member...',
      unassigned: 'Unassigned',
      cancel: 'Cancel',
      createTask: 'Create Task',
      creating: 'Creating...',
      taskCreatedSuccess: 'Task created successfully',
      failedToCreateTask: 'Failed to create task: {error}',
      pleaseEnterTaskTitle: 'Please enter a task title',
      categoryMemberCommunications: 'Member Communications',
      categoryMaintenanceRequest: 'Maintenance Request',
      categoryProShop: 'Pro Shop',
      categoryFoodService: 'Food Service',
      categoryCartService: 'Cart Service',
      categoryRange: 'Range',
      categoryOther: 'Other',
      priorityLow: 'Low',
      priorityNormal: 'Normal',
      priorityHigh: 'High',
      priorityUrgent: 'Urgent',
    },

    // Start Onboarding Side Panel
    onboardingModal: {
      eyebrow: 'Onboarding',
      panelTitle: 'Start Onboarding',
      memberLabel: 'Member',
      assignCoachLabel: 'Assign Coach',
      assignCoachOptional: '— optional',
      selectCoach: 'Select a coach...',
      assignLater: 'Assign later',
      loadingCoaches: 'Loading coaches...',
      unknownCoach: 'Unknown Coach',
      membersLabel: 'Members',
      nSelected: '{count} selected',
      deselectAll: 'Deselect all',
      selectAll: 'Select all',
      searchMembers: 'Search members...',
      loadingMembers: 'Loading members...',
      noMembersAvailable: 'No members available',
      allMembersInOnboarding: 'All members are already in onboarding.',
      cancel: 'Cancel',
      starting: 'Starting...',
      startOnboarding: 'Start Onboarding',
      membersAssignedToCoach: 'Members assigned to coach and enrolled.',
      membersEnrolledNoCoach: 'Members enrolled. Coach can be assigned later.',
      failedToEnroll: 'Failed to enroll {count} member(s)',
      someMembersAlreadyOnboarding: 'Some members may already be in onboarding.',
      failedToStartOnboarding: 'Failed to start onboarding',
    },

    memberPanel: {
      atRisk: 'At risk',
      snoozed: 'Snoozed',
      inactive: 'd inactive',
      noCoach: 'No coach',
      until: 'until',
      history: 'History',
      send: 'Send',
      chooseAction: 'Choose action',
      sendReEngagement: 'Send re-engagement',
      sendReEngagementSub: 'Email or in-app message',
      logFollowUp: 'Log follow-up',
      logFollowUpSub: 'Internal note for the team',
      compSomething: 'Comp something',
      compSomethingSub: 'Free 9 holes, range tokens…',
      reassignCoach: 'Reassign coach',
      reassignCoachSub: 'Match with a new coach',
      undoSnooze: 'Undo snooze',
      undoSnoozeSub: 'Re-activate at-risk status',
      snooze: 'Snooze',
      fullProfile: 'Full profile',
    },
  },

  events: {
    waitlist: {
      title: 'Waitlist Statistics',
      totalWaitlisted: 'Total Waitlisted',
      avgWaitTime: 'Avg Wait Time',
      promotionRate: 'Promotion Rate',
      nextPromotion: 'Next Promotion',
      rateHigh: 'High',
      rateMedium: 'Medium',
      rateLow: 'Low',
    },

    resultsManager: {
      participants: 'Participants',
      completionRate: 'Completion Rate',
      averageScore: 'Average Score',
      verified: 'Verified',
      resultsManagement: 'Results Management',
      searchParticipants: 'Search Participants',
      searchByName: 'Search by name...',
      statusFilter: 'Status Filter',
      allParticipants: 'All Participants',
      withResults: 'With Results',
      missingResults: 'Missing Results',
      colParticipant: 'Participant',
      colScore: 'Score',
      colStatus: 'Status',
      colHoles: 'Holes',
      colActions: 'Actions',
      statusNoScore: 'No Score',
      statusVerified: 'Verified',
      statusSubmitted: 'Submitted',
      scoreEntryTitle: 'Score Entry',
      scoreEntryFor: 'Score Entry - {name}',
      toastVerified: 'Result verified successfully',
      toastVerifyFailed: 'Failed to verify result: {error}',
      toastDeleted: 'Result deleted successfully',
      toastDeleteFailed: 'Failed to delete result: {error}',
      confirmDelete: 'Are you sure you want to delete this result?',
    },

    liveTournament: {
      backToEvent: 'Back to Event',
      pageTitle: 'Live Tournament',
      liveView: 'Live View',
      tournamentView: 'Tournament View',
      autoRefresh: 'Auto Refresh',
      tournamentNavigation: 'Tournament Navigation',
      tournamentNavigationDesc: 'Access other tournament management pages',
      groups: 'Groups',
      bracket: 'Bracket',
      statPlayers: 'Players',
      statCurrentRound: 'Current Round',
      statLiveMatches: 'Live Matches',
      statChampionship: 'Championship',
      sectionLiveMatches: 'Live Matches',
      liveMatchesActive: '{count} Active',
      badgeLive: 'LIVE',
      badgeComplete: 'COMPLETE',
      badgeScheduled: 'SCHEDULED',
      holeLabel: 'Hole {number}',
      watchingCount: '{count} watching',
      startedAt: 'Started {time}',
      tbdPlayer: 'TBD',
      vsLabel: 'vs',
      sectionBracket: 'Tournament Bracket',
      sectionUpcoming: 'Upcoming Matches',
      estDuration: 'Est. {duration}',
      handicapLabel: 'Handicap {value}',
    },

    wizard: {
      tournamentStructureTitle: 'Tournament Structure',
      numberOfRounds: 'Number of Rounds',
      roundsInfo: 'Most championships use 3-4 rounds',
      roundsBadge: '{count} Rounds',
      scoringMethodLabel: 'Scoring Method',
      totalStrokes: 'Total Strokes',
      totalStrokesRecommended: 'Recommended',
      totalStrokesDesc: 'Sum of all round scores',
      bestNRounds: 'Best N Rounds',
      bestNRoundsDesc: 'Count only best performing rounds',
      bestLabel: 'Best',
      ofRounds: 'of {count} rounds',
      scoringTypeLabel: 'Scoring Type',
      grossOnly: 'Gross Only',
      netOnly: 'Net Only',
      bothDivisions: 'Both Divisions',
      bothDivisionsPopular: 'Popular',
      roundScheduleTitle: 'Round Schedule',
      addRound: 'Add Round',
      roundBadge: 'Round {number}',
      roundDateLabel: 'Date',
      scoreDeadlineLabel: 'Score Deadline',
      previewTitle: 'Tournament Preview',
      previewLiveBadge: 'Live',
      leaderboardHeader: 'LEADERBOARD',
      leaderboardColPlayer: 'Player',
      leaderboardColTotal: 'Total',
      statTotalRounds: 'Total Rounds',
      statScoring: 'Scoring',
      statDuration: 'Duration',
      statDurationDays: '{count} days',
      statType: 'Type',
      scoringCumulative: 'Cumulative',
      scoringBestN: 'Best {count}',
      typeGrossNet: 'Gross & Net',
      btnBack: 'Back',
      btnContinue: 'Continue to Details',
    },

    leagueHistory: {
      filterAdvanced: 'Advanced',
      filterAllTime: 'All Time',
      filterLast7Days: 'Last 7 Days',
      filterLast14Days: 'Last 14 Days',
      filterLast30Days: 'Last 30 Days',
      filterAllFormats: 'All Formats',
      filter1v1Only: '1v1 Only',
      filter2v2Only: '2v2 Only',
      filterAllMatches: 'All Matches',
      filterCompleted: 'Completed',
      filterPending: 'Pending',
      filterConfirmed: 'Confirmed',
      filterCancelled: 'Cancelled',
      cardTotalMatches: 'Total Matches',
      cardCompleted: 'Completed',
      cardScheduled: 'Scheduled',
      cardSingles: 'Singles',
      cardDoubles: 'Doubles',
      sectionTitle: 'Match History ({count} matches)',
      noMatchesFound: 'No matches found',
      noMatchesHint: 'Try adjusting your search criteria',
      noMatchesYet: 'No matches have been played yet',
      statusCompleted: 'Completed',
      statusPending: 'Pending',
      statusConfirmed: 'Confirmed',
      statusCancelled: 'Cancelled',
      winnerLabel: 'Winner: {name}',
      scoreDisplay: 'Score: {score} • {holes} holes played',
      scheduledFor: 'Scheduled for {date}',
      btnDetails: 'Details',
      btnEdit: 'Edit',
      dialogEditTitle: 'Edit Match',
      errorLoadHistory: 'match history',
    },

    tournamentGroups: {
      createTournamentGroups: 'Create Tournament Groups',
      numberOfGroups: 'Number of Groups',
      assignmentMethod: 'Assignment Method',
      playersAdvancePerGroup: 'Players Advance per Group',
      groupsLabel: 'Groups',
      handicapBalanced: 'Handicap Balanced',
      random: 'Random',
      seeded: 'Seeded (Snake Draft)',
      configSummary: 'Configuration Summary',
      participants: 'Participants',
      perGroup: 'Per Group',
      advance: 'Advance',
      cannotDivideEvenly: 'Cannot evenly divide {total} participants into {groups} groups. Try a different number of groups.',
      creatingGroups: 'Creating Groups...',
      createGroupsAndAssign: 'Create Groups & Assign Members',
      tournamentProgress: 'Tournament Progress',
      clearGroups: 'Clear Groups',
      clearing: 'Clearing...',
      completedMatches: 'Completed Matches',
      pendingMatches: 'Pending Matches',
      overallProgress: 'Overall Progress',
      clearConfirm: 'Are you sure you want to clear all groups? This action cannot be undone.',
      unableToLoad: 'Unable to Load Tournament Groups',
      unexpectedError: 'An unexpected error occurred while loading the tournament data.',
      tryAgain: 'Try Again',
      noParticipants: 'No Participants',
      participantsMustRegister: 'Participants must register for the tournament before groups can be created.',
    },

    registration: {
      eventRegistration: 'Event Registration',
      stepOf: 'Step {current} of {total}',
      percentComplete: '{pct}% Complete',
      reviewEventTab: 'Review Event',
      registrationTab: 'Registration',
      paymentTab: 'Payment',
      confirmationTab: 'Confirmation',
      reviewEventDetails: 'Review Event Details',
      reviewEventSubtitle: 'Please review the event information before completing your registration.',
      dateLabel: 'Date: ',
      timeLabel: 'Time: ',
      capacityLabel: 'Capacity: ',
      capacityValue: '{confirmed} / {total} registered',
      deadlineLabel: 'Deadline: ',
      entryFeeLabel: 'Entry Fee:',
      spotsAvailable: 'Spots Available',
      spotsRemaining_one: '{count} spot remaining',
      spotsRemaining_other: '{count} spots remaining',
      eventFull: 'Event Full - Join Waitlist',
      waitlistNotice: 'You will be notified if a spot becomes available',
      cancel: 'Cancel',
      continue: 'Continue',
      registrationInformation: 'Registration Information',
      registrationSubtitle: 'Please provide any additional information for your registration.',
      additionalNotes: 'Additional Notes',
      optional: '(Optional)',
      notesPlaceholder: 'Any dietary restrictions, special requirements, or comments...',
      notesExamples: 'Examples: Vegetarian meal, cart required, playing with friends',
      registrationSummary: 'Registration Summary',
      eventLabel: 'Event:',
      statusLabel: 'Status:',
      statusConfirmed: 'Confirmed',
      statusWaitlisted: 'Waitlisted',
      registrationTerms: 'Registration Terms',
      termCancellation: 'Cancellations must be made at least 24 hours before the event',
      termNoShow: 'No-shows will be charged the full entry fee if applicable',
      termWeather: 'Weather-related cancellations will be communicated via email',
      termEntryFee: 'Entry fees are due at the time of the event unless otherwise specified',
      registrationFailed: 'Registration Failed',
      unexpectedError: 'An unexpected error occurred',
      back: 'Back',
      registering: 'Registering...',
      completeRegistration: 'Complete Registration',
      addedToWaitlist: 'Added to Waitlist!',
      registrationConfirmed: 'Registration Confirmed!',
      registrationDetails: 'Registration Details',
      confirmationCode: 'Confirmation Code:',
      dateDetailLabel: 'Date:',
      timeDetailLabel: 'Time:',
      statusDetailLabel: 'Status:',
      waitlistPosition: 'Waitlist Position:',
      paymentLabel: 'Payment:',
      paid: 'Paid',
      payAtClub: 'Pay at Club',
      whatsNext: "What's Next?",
      done: 'Done',
    },

    multiRound: {
      championshipSetup: 'Multi-Round Championship Setup',
      chooseTournamentFormat: "Choose your tournament format. You'll configure specific settings in the next step.",
      strokePlayChampionship: 'Stroke Play Championship',
      popular: 'Popular',
      strokePlayDesc: 'Multi-round medal play where lowest total score wins',
      totalStrokesOrBestN: 'Total strokes or best-N rounds',
      grossAndNet: 'Gross & net scoring',
      realtimeLeaderboard: 'Real-time leaderboard',
      strokePlayBestFor: 'Club championships, seasonal competitions, member tournaments',
      matchPlayTournament: 'Match Play Tournament',
      headToHead: 'Head-to-Head',
      matchPlayDesc: 'Bracket-style competition with head-to-head matches',
      singleElimOrGroup: 'Single elimination or group phase',
      handicapAllocation: 'Handicap stroke allocation',
      dynamicBracket: 'Dynamic bracket updates',
      matchPlayBestFor: 'Knockout tournaments, member matchups, championship finals',
      cancel: 'Cancel',
      continue: 'Continue',
      quickComparison: 'Quick Comparison',
      strokePlay: 'Stroke Play',
      strokePlayBullet1: 'Everyone plays all rounds',
      strokePlayBullet2: 'Cumulative scoring',
      strokePlayBullet3: 'Ties share position',
      strokePlayBullet4: 'Score entry per round',
      matchPlay: 'Match Play',
      matchPlayBullet1: 'Elimination format',
      matchPlayBullet2: 'Hole-by-hole scoring',
      matchPlayBullet3: 'Winner advances',
      matchPlayBullet4: 'Match deadlines',
      bestFor: 'Best for:',
    },

    matchResult: {
      submitMatchResult: 'Submit Match Result',
      entryMethod: 'Entry Method',
      simple: 'Simple',
      simpleDesc: 'Just pick the result',
      scorecard: 'Scorecard',
      scorecardDesc: 'Enter detailed scores',
      matchResultLabel: 'Match Result',
      iWon: 'I Won',
      iWonDesc: '{player} wins the match',
      iLost: 'I Lost',
      iLostDesc: '{opponent} wins the match',
      draw: 'Draw',
      drawDesc: 'Match ended in a tie',
      matchScores: 'Match Scores',
      scorePlaceholder: 'Score',
      upsDowns: 'Ups & Downs (Optional)',
      upsDownsDesc: 'Track holes won/lost for tiebreaker purposes',
      ups: 'Ups (Holes Won)',
      downs: 'Downs (Holes Lost)',
      drawResult: 'Draw - Match tied',
      winsBy: '{player} wins by {count} hole(s)',
      pleaseSelectResult: 'Please select a result',
      pleaseEnterScores: 'Please enter scores for both players',
      failedToSubmit: 'Failed to submit result. Please try again.',
      submitting: 'Submitting...',
      submitResult: 'Submit Result',
      opponentNotified: 'Your opponent will be notified of this result',
    },

    championship: {
      frontNine: 'Front Nine',
      backNine: 'Back Nine',
      teamMembers: 'Team Members',
      teamCombinedPoints: 'Team Combined Points',
      teamCombinedScores: 'Team Combined Scores',
      all: 'All',
    },

    crmDashboard: {
      unableToLoad: 'Unable to load tournament or member data',
      expectedPerformance: 'Expected Performance',
      strategy: 'Strategy',
      type: 'Type',
      structure: 'Structure',
      format: 'Format',
      engagementWeight: 'Engagement Weight',
      member360Profile: 'Member 360 Profile',
      golfScore: 'Golf Score',
      socialScore: 'Social Score',
      communication: 'Communication',
      totalEngagement: 'Total Engagement',
      recommendedActions: 'Recommended Actions',
    },

    scheduleMatch: {
      matchFormat: 'Match Format',
      chooseSinglesOrDoubles: 'Choose singles or doubles',
      courseSelection: 'Course Selection',
      chooseWhereToPlay: 'Choose where to play',
      yourTeam: 'Your Team',
      opponentTeam: 'Opponent Team',
      matchDetails: 'Match Details',
      singles: 'Singles',
      singlesSubtitle: '1v1 Match',
      doubles: 'Doubles',
      doublesSubtitle: '2v2 Match',
      selectCourse: 'Select Course',
      selectTees: 'Select Tees',
      selectOpponent: 'Select Opponent',
      selectYourPartner: 'Select Your Partner',
      selectOpponentsPartner: "Select Opponent's Partner",
      date: 'Date',
      time: 'Time',
      notes: 'Notes',
      optional: 'Optional',
      leagueMatchCourseHint: 'League matches can be played on any course — search by course or club name',
      leagueMatchCourseHintShort: 'Search by course or club name',
      noRatingsAvailable: 'No ratings available',
      usga: 'Required for accurate USGA handicap calculations',
      noTeeDataTitle: 'No Tee Data Available',
      incompleteTeeRatingsTitle: 'Incomplete Tee Ratings',
      noTeeDataBody: "This course doesn't have tee data configured.",
      incompleteTeeRatingsBody: 'The tees for this course are missing rating data.',
      handicapUsesIndexDirectly: 'Handicap calculations will use player indexes directly.',
      sendTeeData: 'If you have tee data, send it to',
      loadingOpponent: 'Loading opponent details. You can still pick someone else if needed.',
      you: 'You',
      locked: 'Locked',
      opponent: 'Opponent',
      loadingLeagueMembers: 'Loading league members...',
      loadingCourses: 'Loading courses...',
      noCoursesFound: 'No courses found.',
      loadingTees: 'Loading tees...',
      pleaseSelectOpponent: 'Please select an opponent',
      pleaseSelectPartner: 'Please select your partner',
      pleaseSelectOpponentPartner: "Please select opponent's partner",
      pleaseSelectDate: 'Please select a date',
      pleaseSelectTime: 'Please select a time',
      pleaseSelectCourse: 'Please select a course',
      pleaseSelectTees: 'Please select which tees you will play from',
      cannotSelectSamePlayer: 'Cannot select the same player multiple times',
      cancel: 'Cancel',
      scheduleMatchBtn: 'Schedule Match',
      scheduling: 'Scheduling...',
      matchScheduledSuccess: 'Match scheduled successfully!',
      failedToSchedule: 'Failed to schedule match',
      membershipNotFound: 'User membership not found',
    },

    leagueScore: {
      yourLeagueSchedule: 'Your League Schedule',
      viewAndManageMatches: 'View and manage your matches',
      currentMatch: 'Current Match',
      noActiveMatch: 'No Active Match',
      youWon: 'You Won!',
      matchLost: 'Match Lost',
      otherScheduledMatches: 'Other Scheduled Matches',
      noAdditionalMatches: 'No Additional Matches',
      noOtherMatchesDesc: 'You currently have no other scheduled matches in this league.',
      planningTip: 'Planning Tip',
      planningTipBody: 'You can play these matches in any order. Coordinate with your opponents to arrange convenient times that work for both of you.',
      gotIt: 'Got it',
      enterMatchResult: 'Enter Match Result',
      quickResultEntry: 'Quick result entry',
      matchPlayScoring: 'Match Play Scoring',
      yourResult: 'Your Result',
      theirResult: 'Their Result',
      selectResult: 'Select result...',
      submitting: 'Submitting...',
      noActiveMatchBtn: 'No Active Match',
      submitMatchResult: 'Submit Match Result',
      loadingYourMatch: 'Loading your match...',
      loadingSchedule: 'Loading schedule...',
      matchResultSubmitted: 'Match result submitted successfully!',
      failedToSubmitResult: 'Failed to submit match result. Please try again.',
      noActiveMatchToSubmit: 'No active match to submit result for',
      pleaseSelectResult: 'Please select a match result',
      back: 'Back',
      selected: 'Selected',
      select: 'Select',
    },

    tournament: {
      eventInformation: 'Event Information',
      tournamentInformation: 'Tournament Information',
      description: 'Description',
      registered: 'Registered',
      entryFee: 'Entry Fee',
      tournamentFormat: 'Tournament Format',
      structure: 'Structure:',
      format: 'Format:',
      currentPhase: 'Current Phase:',
      enterYourScore: 'Enter Your Score',
      groupMatchEntry: 'Group Match Entry',
      groupMatchEntryDesc: 'Enter your group phase match result',
      championshipLeaderboard: 'Championship Leaderboard',
      eventResults: 'Event Results',
      resultsAvailableAfterEvent: 'Results will be available after the event',
    },

    roundConfig: {
      roundConfiguration: 'Round Configuration',
      addRound: 'Add Round',
      addNewRound: 'Add New Round',
      roundNumber: 'Round Number',
      roundDate: 'Round Date',
      scoreEntryDeadline: 'Score Entry Deadline',
      status: 'Status',
      editRoundTitle: 'Edit Round {number}',
      updateRound: 'Update Round',
      roundLabel: 'Round {number}',
      deadline: 'Deadline:',
      statusScheduled: 'Scheduled',
      statusActive: 'Active',
      statusCompleted: 'Completed',
      noRoundsConfigured: 'No rounds configured yet. Add your first round to get started.',
      deleteRoundConfirm: 'Are you sure you want to delete this round?',
    },

    eventDetails: {
      eventNotFound: 'Event not found',
      eventNotFoundDesc: "The event you're looking for doesn't exist or has been removed.",
      tryAgain: 'Try Again',
      share: 'Share',
      edit: 'Edit',
      viewRegistrations: 'View Registrations',
      shareEvent: 'Share Event',
      cancelEvent: 'Cancel Event',
      draft: 'Draft',
      details: 'Details',
      registrations: 'Registrations',
      results: 'Results',
      updates: 'Updates',
      eventInformation: 'Event Information',
      date: 'Date',
      time: 'Time',
      location: 'Location',
      capacity: 'Capacity',
      capacityValue: '{confirmed} / {total} registered',
      entryFee: 'Entry Fee',
      format: 'Format',
      prizes: 'Prizes',
      eventStartsIn: 'Event Starts In',
      days: 'Days',
      hours: 'Hours',
      minutes: 'Minutes',
      registration: 'Registration',
      cancelRegistration: 'Cancel Registration',
      cancelling: 'Cancelling...',
      registerNow: 'Register Now',
      registering: 'Registering...',
      cannotRegister: 'Cannot Register',
      availableSpots: 'Available Spots:',
      waitlist: 'Waitlist:',
      registrationDeadline: 'Registration Deadline:',
      regStatus: 'Status:',
      open: 'Open',
      closed: 'Closed',
      quickStats: 'Quick Stats',
      totalRegistered: 'Total Registered',
      confirmed: 'Confirmed',
      waitlisted: 'Waitlisted',
      capacityUtilization: 'Capacity Utilization',
      registrationListPlaceholder: 'Registration list will be implemented here.',
      eventUpdatesPlaceholder: 'Event updates and communications will be shown here.',
    },

    leagueWizard: {
      configureYourLeague: 'Configure Your League',
      configureLeagueDesc: 'Set up scoring rules and league structure for your competition',
      pointSystem: 'Point System',
      pointSystemDesc: 'How points are awarded for match results',
      win: 'Win',
      draw: 'Draw',
      loss: 'Loss',
      pointSystemPreview: 'Point System Preview',
      presetStandard: 'Standard',
      presetClassic: 'Classic',
      presetSimple: 'Simple',
      leagueStructure: 'League Structure',
      leagueStructureDesc: 'Configure league settings and format',
      matchFormat: 'Match Format',
      singles: 'Singles',
      singlesOnly: '1v1 Only',
      doubles: 'Doubles',
      doublesOnly: '2v2 Only',
      mixed: 'Mixed',
      mixedBoth: 'Both 1v1 & 2v2',
      seasonLength: 'Season Length',
      ongoing: 'Ongoing',
      ongoingDesc: 'No end date',
      threeMonths: '3 Months',
      threeMonthsDesc: 'Quarterly',
      sixMonths: '6 Months',
      sixMonthsDesc: 'Semi-annual',
      oneYear: '1 Year',
      oneYearDesc: 'Annual',
      leagueFormat: 'League Format',
      formatSinglesOnly: 'Singles only',
      formatDoublesOnly: 'Doubles only',
      formatSinglesAndDoubles: 'Singles and Doubles',
      durationNoEndDate: 'No end date',
      challengesAllowed: 'Members can challenge each other to matches',
      back: 'Back',
      continueToDetails: 'Continue to Event Details',
    },

    leagueOverview: {
      leagueStatus: 'League Status',
      players: 'Players',
      daysLeft: 'Days left',
      matchPlay: 'Match Play',
      format: 'Format',
      yourPerformance: 'Your Performance',
      position: 'Position',
      rating: 'Rating',
      points: 'Points',
      matches: 'Matches',
      record: 'Record (W-L)',
      recordWithDraws: 'Record (W-L-D)',
      leagueMatchHistory: 'Your League Match History',
      won: 'Won',
      lost: 'Lost',
      tied: 'Tied',
      completed: 'Completed',
      noMatchesYet: 'No matches played yet',
      goToChallengeTab: 'Head to the Challenge tab to schedule your matches',
      matchHistoryWillAppear: 'Your completed match history will appear here',
    },

    leagueChallenge: {
      scheduledMatches: 'Scheduled Matches',
      viewAndManageMatches: 'View and manage your matches',
      scheduleNewMatch: 'Schedule New Match',
      loadingMatches: 'Loading scheduled matches...',
      noScheduledMatches: 'No Scheduled Matches',
      challengePlayersBelow: 'Challenge players below to schedule matches',
      leaguePlayers: 'League Players',
      challengePlayersDesc: 'Challenge players to schedule matches',
      showLess: 'Show less',
      morePlayers: 'more players',
      noPlayersFound: 'No Players Found',
      noOtherParticipants: 'No Other Participants',
      tryDifferentSearch: 'Try a different search term',
      morePlayersWillAppear: 'More players will appear as they register',
      editChallenge: 'Edit Challenge',
      matchDetails: 'Match Details',
      updateChallengeInfo: 'Update challenge information',
      opponent: 'Opponent',
      date: 'Date',
      time: 'Time',
      course: 'Course',
      selectTees: 'Select Tees',
      teesRequired: 'Tee selection is required for accurate USGA handicap calculations',
      loadingTees: 'Loading tees...',
      noTeeDataAvailable: 'No Tee Data Available',
      incompleteTeeRatings: 'Incomplete Tee Ratings',
      noTeeDataDesc: "This course doesn't have tee data configured.",
      incompleteTeeDesc: 'The tees for this course are missing rating data.',
      noRatings: 'No ratings',
      notesOptional: 'Notes (Optional)',
      matchStatus: 'Match Status',
      pending: 'Pending',
      confirmed: 'Confirmed',
      cancelled: 'Cancelled',
      cancelButton: 'Cancel',
      updateChallenge: 'Update Challenge',
      scheduledTime: 'Scheduled Time',
      formatLabel: 'Format',
      singlesMatchplay: 'Singles Matchplay',
      doublesMatchplay: 'Doubles Matchplay',
      courseLabel: 'Course',
      notesLabel: 'Notes',
      editMatch: 'Edit Match',
      startMatch: 'Start Match',
      viewDetails: 'View Details',
      edit: 'Edit',
      confirm: 'Confirm',
      decline: 'Decline',
      team1: 'Team 1',
      team2: 'Team 2',
      player1: 'Player 1',
      player2: 'Player 2',
    },

    tournamentStructure: {
      tournamentConfiguration: 'Tournament Configuration',
      format: 'Format',
      strokePlay: 'Stroke Play',
      matchPlay: 'Match Play',
      scoringMethod: 'Scoring Method',
      totalStrokes: 'Total Strokes',
      scoringType: 'Scoring Type',
      players: 'Players',
      cancelEdit: 'Cancel Edit',
      editConfiguration: 'Edit Configuration',
      roundSchedule: 'Round Schedule',
      addRound: 'Add Round',
      noRoundsYet: 'No rounds configured yet',
      addRoundHint: 'Click "Add Round" to create the tournament schedule',
      inProgress: 'In Progress',
      complete: 'Complete',
      upcoming: 'Upcoming',
      deadline: 'Deadline:',
      tournamentStructureTitle: 'Tournament Structure',
      bracketManagement: 'Bracket Management',
      bracketAutoGenerated: 'The bracket structure is automatically generated based on player count. View and manage matches in the Bracket tab.',
      totalPlayers: 'Total Players',
      tournamentRounds: 'Tournament Rounds',
    },

    eventsList: {
      eventsAndCompetitions: 'Events & Competitions',
      createEvent: 'Create Event',
      draft: 'Draft',
      past: 'Past',
      active: 'Active',
      registrationClosed: 'Registration Closed',
      open: 'Open',
      noEventsFound: 'No events found',
      tryAdjustingFilters: 'Try adjusting your filters to see more events.',
      noEventsCreatedYet: 'No events have been created yet.',
      createFirstEvent: 'Create First Event',
      viewDetails: 'View Details',
      allStatus: 'All Status',
      upcoming: 'Upcoming',
      openRegistration: 'Open Registration',
      allTypes: 'All Types',
      tournament: 'Tournament',
      competition: 'Competition',
      social: 'Social',
      activity: 'Activity',
    },

    courseSelector: {
      golfCourse: 'Golf Course',
      homeCourse: 'Home Course',
      externalCourse: 'External Course',
      searchCourses: 'Search courses...',
      allCourses: 'All Courses',
      clubCourses: 'Club Courses',
      externalCourses: 'External Courses',
      noCourseSelected: 'No course selected',
      noCoursesAvailable: 'No courses available',
      home: 'Home',
      external: 'External',
    },

    leagueResults: {
      enterMatchResult: 'Enter Match Result',
      quickResultEntry: 'Quick result entry - use scorecard for full stats',
      enterNewMatchResult: 'Enter New Match Result',
      opponent: 'Opponent *',
      matchDate: 'Match Date *',
      result: 'Result *',
      notesOptional: 'Notes (Optional)',
      win: 'Win',
      loss: 'Loss',
      draw: 'Draw',
      submitting: 'Submitting...',
      submitResult: 'Submit Result',
      recentMatches: 'Recent Matches',
      noRecentMatches: 'No recent matches',
      matchesWillAppear: 'Your completed matches will appear here',
      howScoringWorks: 'How League Scoring Works',
      scoringExplanation: 'Enter your match results after playing against other league members. Wins earn you points that contribute to your league ranking. Make sure to coordinate with your opponent before submitting results.',
      fillAllFields: 'Please fill in all required fields',
      matchSubmitted: 'Match result submitted successfully!',
    },

    matchplay: {
      updateMatchResult: 'Update Match Result',
      enterMatchResult: 'Enter Match Result',
      youVs: 'You vs',
      matchResult: 'Match Result',
      holesCompleted: 'Holes Completed',
      holesHint: 'For matches ending early (e.g., 3&2), enter the actual holes played',
      finalScore: 'Final Score (Optional)',
      finalScoreFormats: 'Standard formats: "3&2" (won by 3 holes with 2 to play), "1 up" (won by 1 hole)',
      matchNotes: 'Match Notes (Optional)',
      pleaseSelectResultError: 'Please select a match result',
      mustComplete118Error: 'Must complete 1-18 holes',
      verifiedResult: 'Verified Result',
      saving: 'Saving...',
      updateResult: 'Update Result',
      submitResult: 'Submit Result',
      cancel: 'Cancel',
      won: 'Won',
      lost: 'Lost',
      tied: 'Tied',
      pending: 'Pending',
    },
  },

  analytics: {
    clubAnalysis: {
      playMoreRoundsPerformance: 'Play more rounds to see club performance analysis',
      playMoreRoundsDistance: 'Play more rounds to see club distance analysis',
      notEnoughDataGaps: 'Not enough data to analyze club gaps',
      playMoreRoundsStats: 'Play more rounds to see detailed club statistics',
      drivingNotAvailable: 'Driving analysis not available in detailed view',
      noClubDataTitle: 'No Club Data Available',
      noClubDataDesc: 'Complete more rounds to see your club performance analysis.',
      pageTitle: 'Detailed Club Analysis',
      pageSubtitle: 'Original complex data view',
      backToDashboard: 'Back to Dashboard',
      heroLabel: 'Club Performance',
      mostConsistent: 'Most Consistent',
      longestClub: 'Longest Club',
      tabOverview: 'Overview',
      tabAnalysis: 'Analysis',
      tabDriving: 'Driving',
      totalClubsLabel: 'TOTAL CLUBS',
      inYourBag: 'In your bag',
      gapIssuesLabel: 'GAP ISSUES',
      wellGapped: 'Well gapped',
      needAttention: 'Need attention',
      clubDistancesTitle: 'Club Distances',
      clubGapsTitle: 'Club Gaps Analysis',
      clubPerformanceAnalysisTitle: 'Club Performance Analysis',
      detailedStatsTitle: 'Detailed Club Statistics',
      drivingAnalysisTitle: 'Driving Analysis',
      consistencyAnalysisTitle: 'Consistency Analysis',
      leastConsistentClubs: 'Least Consistent',
      mostConsistentClubs: 'Most Consistent',
      clubsLabel: 'Clubs',
      shotsLabel: 'shots',
      accuracyAnalysisTitle: 'Accuracy Analysis',
      leastAccurateClubs: 'Least Accurate',
      mostAccurateClubs: 'Most Accurate',
      distancePerformanceSubtitle: 'Distance performance across your clubs',
      distanceYardsAxis: 'Distance (yards)',
      averageDistanceTooltip: 'Average Distance',
      averageDistancesNote: 'Average distances based on recent rounds',
      gapSubtitle: 'Identify distance gaps and overlaps in your club setup',
      wellGappedSetupTitle: 'Well-Gapped Setup',
      wellGappedSetupDesc: 'Your clubs have good distance spacing',
      distanceGapsLabel: 'Distance Gaps',
      overlapsLabel: 'Overlaps',
      avgGapSizeLabel: 'Avg Gap Size',
      distanceGapsSectionTitle: 'Distance Gaps',
      distanceGapsSectionDesc: 'Clubs with large distance gaps',
      sevCritical: 'Critical',
      sevHigh: 'High',
      sevMedium: 'Medium',
      sevLow: 'Low',
      ydGap: 'yd gap',
      idealGapLabel: 'Ideal gap:',
      actualLabel: 'Actual:',
      distanceOverlapsSectionTitle: 'Distance Overlaps',
      distanceOverlapsSectionDesc: 'Clubs with similar distances',
      ydOverlap: 'yd overlap',
      overlapConfusionNote: 'Similar distance ranges may cause club selection confusion',
      clubGappingTipsTitle: 'Club Gapping Tips',
      gappingTip1: 'Ideal gap between consecutive clubs: 10-15 yards',
      gappingTip2: 'Consider adding hybrid clubs to fill large gaps',
      gappingTip3: 'Remove overlapping clubs to simplify decision-making',
      gappingTip4: 'Test clubs at different swing speeds to find optimal gapping',
      comprehensiveAnalysisSubtitle: 'Comprehensive distance analysis and consistency tracking for your clubs',
      yardsLabel: 'yards',
      medianDistanceLabel: 'Median Distance',
      rangeQ1Q3Label: 'Range (Q1-Q3)',
      fullRangeLabel: 'Full Range:',
      spreadLabel: 'Spread:',
      consistencyLabel: 'Consistency',
      shortRangeLabel: 'Short Range',
      longRangeLabel: 'Long Range',
      accuracyDecreaseWarning: 'Accuracy decreases with distance - consider shorter swings',
      shotConditionsTitle: 'Shot Conditions',
      fairwayLabel: 'Fairway:',
      roughLabel: 'Rough:',
      bunkerLabel: 'Bunker:',
      identifyClubsSubtitle: 'Identify clubs needing attention vs reliable performers',
    },

    clubPerformance: {
      backToDashboard: 'Back to Dashboard',
      back: 'Back',
      noClubDataTitle: 'No Club Data Available',
      noClubDataDesc: 'Start tracking club distances in your rounds to see performance analysis.',
      pageTitle: 'Club Performance',
      pageSubtitle: 'Distance analysis and consistency tracking for your clubs',
      averageConsistencyLabel: 'Average Consistency',
      clubsTrackedNote: 'clubs tracked',
      clubsTrackedLabel: 'Clubs Tracked',
      avgConsistencyLabel: 'Avg Consistency',
      mostConsistentLabel: 'Most Consistent',
      gapIssuesLabel: 'Gap Issues',
      naLabel: 'N/A',
      distanceAnalysisTitle: 'Club Distance Analysis (P-Avg)',
      yardsLabel: 'yards',
      medianDistanceLabel: 'Median Distance',
      rangeQ1Q3Label: 'Range (Q1-Q3)',
      fullRangeLabel: 'Full Range:',
      spreadLabel: 'Spread:',
      consistencyLabel: 'Consistency',
      accuracySectionTitle: 'Accuracy Analysis',
      shortRangeLabel: 'Short Range',
      longRangeLabel: 'Long Range',
      accuracyDecreaseWarning: 'Accuracy decreases with distance - consider shorter swings',
      shotConditionsTitle: 'Shot Conditions',
      fairwayLabel: 'Fairway:',
      roughLabel: 'Rough:',
      bunkerLabel: 'Bunker:',
      distanceGapAnalysisTitle: 'Distance Gap Analysis',
      gapIssueBadge: 'Gap Issue',
      goodBadge: 'Good',
      idealLabel: 'ideal:',
      recommendationTitle: 'Recommendation',
      performanceInsightsTitle: 'Club Performance Insights',
      mostConsistentClubTitle: 'Most Consistent Club',
      distanceGapsDetectedTitle: 'Distance Gaps Detected',
      focusConsistencyTitle: 'Focus on Consistency',
      par4vsPar5Title: 'Par 4 vs Par 5 Performance',
      par4PerformanceTitle: 'Par 4 Performance',
      par5PerformanceTitle: 'Par 5 Performance',
      averageScoreLabel: 'Average Score',
      fairwaysLabel: 'Fairways',
      holesTrackedLabel: 'holes tracked',
      notEnoughDataTitle: 'Not Enough Data',
      playingStyleLabel: 'Playing Style',
      aggressiveStyle: 'Aggressive',
      conservativeStyle: 'Conservative',
      aggressivePlayerTitle: 'Aggressive Player',
      aggressivePlayerDesc: 'You score better on Par 5s, indicating you take risks for birdie opportunities. Consider this strategy on longer holes with wider fairways.',
      precisionPlayerTitle: 'Precision Player',
      precisionPlayerDesc: "You score better on Par 4s, suggesting precision over power. Consider playing more conservatively on Par 5s to avoid big numbers.",
      insufficientStyleTitle: 'Insufficient Data for Playing Style Analysis',
      insufficientStyleDesc: 'Play more rounds with both Par 4s and Par 5s to determine your playing style preference.',
      holeDistanceTitle: 'Performance by Hole Distance',
      holesLabel: 'holes',
      courseArchitectureTitle: 'Course Architecture Insight',
      courseArchitectureDesc: 'Golf course architects typically provide wider fairways for longer holes. If you struggle on long holes despite wider targets, consider distance training.',
      drivingImpactTitle: 'Driving Impact on Round Performance',
      correlationLabel: 'Correlation',
      strengthLabel: 'Strength',
      impactLabel: 'Impact',
      highDrivingImpactTitle: 'High Driving Impact',
      highDrivingImpactDesc: 'Your driving performance strongly correlates with round scores. Focus on driving accuracy and distance for maximum score improvement.',
      moderateDrivingImpactTitle: 'Moderate Driving Impact',
      moderateDrivingImpactDesc: "Driving affects your scores but isn't the only factor. Balance driving practice with short game improvement.",
      shortGameHeroTitle: 'Short Game Hero',
      shortGameHeroDesc: "Your round scores don't depend heavily on driving performance. You're likely strong with your short game and recovery shots.",
      notEnoughCorrelationTitle: 'Not Enough Data for Correlation Analysis',
    },

    metricDrilldown: {
      backToDashboard: 'Back to Dashboard',
      currentValueTitle: 'Current Value',
      targetProgressTitle: 'Target Progress',
      keyInsightsTitle: 'Key Insights',
      vsRecent: 'vs recent',
      percentToTarget: '% to target',
      currentLabel: 'Current',
      performanceExcellent: 'Excellent',
      performanceGood: 'Good',
      performanceNeedsImprovement: 'Needs Improvement',
      keepPlayingInsights: 'Keep playing to generate insights about your',
      tabTrend: 'Trend Analysis',
      tabDistribution: 'Performance Distribution',
      tabPeer: 'Peer Comparison',
      tabGoals: 'Goals & Benchmarks',
      tabTips: 'Improvement Tips',
      avgScoresTitle: 'AVG. SCORES',
      eighteenHoles: '18 Holes',
      goalLabel: 'Goal',
      targetLabel: 'Target',
      front9Label: 'Front 9',
      back9Label: 'Back 9',
      viewScoringRecord: 'View scoring record',
      byHoleParTitle: 'BY HOLE PAR',
      par3Label: "Par 3's",
      par4Label: "Par 4's",
      par5Label: "Par 5's",
      noScoringData: 'No scoring data available for distribution analysis.',
      avgPuttsTitle: 'AVG. PUTTS %',
      strengthLabel: 'Strength',
      puttsLabel: 'Putts',
      holesLabel: 'Holes',
      puttsPerRound: 'Putts per round',
      noPuttingData: 'No putting data available for distribution analysis.',
      performanceDistributionTitle: 'Performance Distribution',
      percentileRankingTitle: 'Your Percentile Ranking',
      percentileLabel: 'percentile',
      peerAverageLabel: 'Peer Average:',
      sampleSizeLabel: 'Sample Size:',
      simulatedData: 'Simulated data',
      noPeerDataTitle: 'No Peer Data Available',
      noPeerDataDesc: 'Complete more rounds to enable peer comparisons',
      howYouCompare: 'How you compare to similar handicap golfers',
      top10Pct: 'Top 10%',
      top25Pct: 'Top 25%',
      averageRange: 'Average',
      belowAvg: 'Below Avg',
      bottom10Pct: 'Bottom 10%',
      youAreHere: 'You are here',
      peerDistributionUnavailable: 'Peer distribution data unavailable',
      benchmarkingInsightsTitle: 'Benchmarking Insights',
      vsPeerAverage: 'vs peer average',
      performanceLevelLabel: 'performance level',
      golfersBehindYou: 'of golfers behind you',
      levelElite: 'Elite',
      levelAboveAverage: 'Above Average',
      levelAverage: 'Average',
      levelNeedsWork: 'Needs Work',
      currentGoalProgressTitle: 'Current Goal Progress',
      progressToGoalLabel: 'Progress to goal',
      peerComparisonTitle: 'Peer Comparison',
      yourPercentileLabel: 'Your percentile:',
      goalManagementTitle: 'Goal Management',
      improvementTipsTitle: 'Improvement Tips',
      personalizedInsightsTitle: 'Personalized Insights',
      metricTypeNotFound: 'Metric type not found:',
    },

    handicapContext: {
      cardTitle: 'Handicap Performance Context',
      cardSubtitle: 'How your performance relates to your handicap level and progression goals',
      tabCurrentLevel: 'Current Level',
      tabProgression: 'Progression',
      tabTierComparison: 'Tier Comparison',
      tierScratchToLow: 'Scratch to Low Handicap',
      tierLow: 'Low Handicap',
      tierMid: 'Mid Handicap',
      tierHigh: 'High Handicap',
      tierBeginner: 'Beginner',
      currentHandicapLevelTitle: 'Current Handicap Level',
      yourCurrentLabel: 'Your Current',
      peerAverageLabel: 'Peer Average',
      recentPerformanceTitle: 'Recent Performance',
      recentAvgDifferential: 'Recent Average Differential',
      consistencyScoreLabel: 'Consistency Score',
      handicapTrendTitle: 'Handicap Trend',
      trendImproving: 'Improving',
      trendUp: 'Trending Up',
      trendStable: 'Stable',
      currentHandicapLabel: 'Current Handicap',
      targetHandicapLabel: 'Target Handicap',
      recentTrendLabel: 'Recent Trend',
      progressToNextTier: 'Progress to Next Tier',
      tracking: 'Tracking...',
      nextMilestoneTitle: 'Next Milestone',
      targetMetricPerformance: 'Target performance:',
      currentTierTitle: 'Current Tier',
      nextTierTitle: 'Next Tier',
      expectedMetricLabel: 'Expected performance:',
      perfGapTitle: 'Performance Gap to Next Tier',
      currentPerfLabel: 'Current Performance',
      nextTierTargetLabel: 'Next Tier Target',
      gapToCloseLabel: 'Gap to Close',
    },

    activityAnalytics: {
      pageTitle: 'Activity Analytics',
      pageSubtitle: 'Playing patterns and frequency analysis',
      totalRounds: 'Total Rounds',
      roundsPerWeek: 'Rounds/Week',
      currentStreak: 'Current Streak',
      avgRoundTime: 'Avg Round Time',
      tabPlayingPatterns: 'Playing Patterns',
      tabFrequency: 'Frequency',
      tabTiming: 'Timing',
      tabTrends: 'Trends',
      dayOfWeekTitle: 'Day of Week Patterns',
      timeDistributionTitle: 'Time Distribution',
      monthlyActivityTitle: 'Monthly Activity vs Target',
      actualRounds: 'Actual Rounds',
      preferredTimesTitle: 'Preferred Playing Times',
      activityInsightsTitle: 'Activity Insights',
      insightBadge: 'Insight',
      trendBadge: 'Trend',
      recommendationBadge: 'Recommendation',
      activityTrendsTitle: 'Activity Trends Over Time',
      loadingMessage: 'Loading activity analytics...',
      errorTitle: 'Activity Data Unavailable',
    },

    firAnalysis: {
      fairwaysHit: 'Fairways Hit',
      overall: 'Overall',
      vsRecent: 'vs Recent',
      vsPeers: 'vs Peers',
      backToDashboard: 'Back to Dashboard',
      noDataTitle: 'No FIR Data Available',
      noDataDesc: 'Complete more rounds to see your driving accuracy.',
      tabOverview: 'Overview',
      tabAnalysis: 'Analysis',
      keyInsightsTitle: 'Key Insights',
      keyInsightsSubtitle: 'Top performance highlights',
      whatToFocusTitle: 'What to Focus On',
      whatToFocusSubtitle: 'Practice priorities',
      moreRoundsInsights: 'Complete more rounds to see insights',
      performanceAnalysisTitle: 'Performance Analysis',
    },

    metricAnalytics: {
      currentValue: 'Current Value',
      bestValue: 'Best Value',
      personalRecord: 'Personal record',
      change: 'Change',
      vsPreviousPeriod: 'vs previous period',
      dataPoints: 'Data Points',
      tabTrend: 'Trend Analysis',
      tabBreakdown: 'Breakdown',
      tabInsights: 'Insights',
      performanceBreakdown: 'Performance Breakdown',
      categoryDetails: 'Category Details',
      keyInsights: 'Key Insights',
      recommendations: 'Recommendations',
      trendBadge: 'Trend',
      performanceBadge: 'Performance',
      recommendationFocus: 'Focus',
      recommendationGoal: 'Goal',
      recommendationPractice: 'Practice',
      errorTitle: 'Metric Data Unavailable',
    },

    break80: {
      noDataDesc: 'Complete a few rounds to see Break 80 analysis',
      alreadyBreakingTitle: "You're Breaking 80!",
      currentAverage: 'Current Average',
      trendVsRecent: 'Trend vs Recent',
      goalAchieved: 'Goal Achieved!',
      cardTitle: 'Break 80 Calculator',
      cardDesc: 'Track your progress toward consistently shooting under 80',
      progressToGoal: 'Progress to Goal',
      strokesToGoal: 'Strokes to Goal',
      improvementAnalysis: 'Improvement Analysis',
      improvingTrend: 'Improving trend!',
      focusNeeded: 'Focus needed',
      focusNeededDesc: "Your scores haven't been improving recently. Consider targeted practice areas.",
      realisticTimeline: 'Realistic timeline:',
      setGoalButton: 'Set Break 80 as Goal',
      creatingGoal: 'Creating Goal...',
      goalDashboardNote: 'This will create a trackable goal in your dashboard',
    },

    drivingAccuracy: {
      noDataDesc: 'No driving accuracy data available yet. Track fairways hit during your rounds.',
      headerTitle: 'Driving Accuracy Deep Dive',
      fairwaysHit: 'Fairways Hit',
      par4s: 'Par 4s',
      par5s: 'Par 5s',
      attempts: 'attempts',
      missPatternTitle: 'Miss Pattern Analysis',
      leftMisses: 'Left Misses',
      rightMisses: 'Right Misses',
      shortMisses: 'Short Misses',
      patternDetected: 'Pattern Detected:',
      scoreImpactTitle: 'Score Impact',
      scoreImpactSubtitle: 'How fairway hits affect your scoring',
      whenHit: 'WHEN HIT',
      whenMissed: 'WHEN MISSED',
      avgScoreToPar: 'Avg score to par',
      impactLabel: 'Impact:',
      accuracyTrendTitle: 'Accuracy Trend Over Time',
    },

    interactiveTimeline: {
      timeRangeLabel: 'Time Range',
      last5Rounds: 'Last 5 Rounds',
      last15Rounds: 'Last 15 Rounds',
      allRounds: 'All Rounds',
      latestLabel: 'Latest',
      recentTrend: 'Recent Trend',
      improving: 'Improving',
      declining: 'Declining',
      avgChange: 'Avg Change',
      performanceLabel: 'Performance',
      strengthsTitle: 'Strengths',
      focusAreasTitle: 'Focus Areas',
      recommendationsTitle: 'Recommendations',
    },

    publicRound: {
      loadingMessage: 'Loading round analysis...',
      notAvailableTitle: 'Round Not Available',
      notAvailableDesc: 'This round could not be found or is not accessible.',
      privateRoundTitle: 'Private Round',
      roundSummaryTitle: 'Round Summary',
      totalScore: 'Total Score',
      toPar: 'To Par',
      holes: 'Holes',
      achievementsTitle: 'Achievements',
      ownRoundNote: 'This is your round - you have full access to all details.',
      sharedByPrefix: 'Shared by',
      viewOnly: 'View only',
    },

    puttingAnalysisPage: {
      loadingMessage: 'Loading putting analysis...',
      errorTitle: 'Error Loading Data',
      errorDesc: 'Failed to load putting data',
    },

    performanceTrends: {
      pageTitle: 'Performance Trends',
      noDataBadge: 'Complete rounds to see trends',
      puttsPerRound: 'Putts/Round',
      girPercent: 'GIR %',
      fairwayPercent: 'Fairway %',
      scramblingPercent: 'Scrambling %',
      bestLabel: 'Best',
      puttsPerRoundChart: 'Putts per Round',
      girChart: 'Greens in Regulation %',
      fairwayChart: 'Fairway Accuracy %',
      scramblingChart: 'Scrambling %',
      noDataTitle: 'No performance trends available yet',
      noDataDesc: 'Complete more rounds to see your performance metrics timeline',
    },

    scorePerformance: {
      pageTitle: 'Score Performance Trends',
      noRoundsTitle: 'No rounds completed yet',
      noRoundsDesc: 'Start playing to see your performance trends!',
      improved: 'Improved',
      progress: 'Progress',
      strokesBetter: 'strokes better',
      roomToImprove: 'room to improve',
      bestLabel: 'Best',
      personalRecord: 'personal record',
      averageLabel: 'Average',
      recentRounds: 'recent rounds',
      showingLast: 'Showing your last',
      completedRounds: 'completed rounds across all game modes',
    },

    metricAnalysis: {
      unsupportedMetric: 'Unsupported Metric',
      backToDashboard: 'Back to Dashboard',
      tabOverview: 'Overview',
      tabPerHole: 'Per-Hole',
      vsRecent: 'vs Recent',
      vsPeers: 'vs Peers',
      vsHandicap: 'vs Handicap',
      performanceInsights: 'Performance Insights',
      improvementTips: 'Improvement Tips',
      perHoleAnalysis: 'Per-Hole Analysis',
    },

    contextCards: {
      sectionTitle: 'Performance Context & Insights',
      insightsAvailable: 'insights available',
      actionPlan: 'Action Plan',
      current: 'Current',
      confidence: 'Confidence',
      noDataDesc: 'Play more rounds to generate insights',
    },

    clubGapping: {
      noDataDesc: 'Need at least 2 clubs with data to analyze gapping',
      headerTitle: 'Gapping Analysis',
      optimalGaps: 'Optimal Gaps',
      largeGaps: 'Large Gaps',
      overlapping: 'Overlapping',
      waterfallTitle: 'Distance Waterfall',
      missingRangesTitle: 'Missing Distance Ranges',
      yardGap: 'yard gap',
      optimizationTips: 'Optimization Tips',
    },

    playerStats: {
      sectionTitle: 'Tournament Statistics',
      previewBadge: 'Preview Feature',
      played: 'Played',
      tournaments: 'tournaments',
      wins: 'Wins',
      firstPlace: 'first place',
      topThree: 'Top 3',
      finishes: 'finishes',
      streak: 'Streak',
      bestScore: 'Best Score',
      averageScore: 'Average Score',
      preferredFormat: 'Preferred Format',
      favoritePartner: 'Favorite Partner',
      previewNote: '* This is preview data to demonstrate player statistics and tournament history tracking',
    },

    simulatorIndex: {
      pageTitle: 'Handicap Simulator',
      pageSubtitle: 'See how your next round affects your index',
    },
  },

  performance: {
    // PerformanceOverview
    overviewTitle: 'Performance Overview',
    statTotalStrokes: 'Total Strokes',
    statToPar: 'To Par',
    statHolesPlayed: 'Holes Played',
    statAvgPerHole: 'Avg per Hole',
    keyMetricsTitle: 'Key Golf Metrics',
    keyMetricsSubtitle: 'Critical performance indicators for your round',
    fairwaysHit: 'Fairways Hit',
    tourAvgFairways: 'Tour avg: ~60%',
    greensInReg: 'Greens in Regulation',
    tourAvgGir: 'Tour avg: ~66%',
    scramblingLabel: 'Scrambling',
    scramblingNote: 'Up & down saves',
    totalPuttsLabel: 'Total Putts',
    onePutts: 'one-putts',
    threePutts: 'three-putts',
    penaltyStroke: 'penalty stroke',
    penaltyStrokes: 'penalty strokes',
    tourAvgPenalties: 'Tour avg: ~1.5 per round',
    front9Label: 'Front 9',
    back9Label: 'Back 9',
    // ModernPerformanceHighlights
    finalStanding: 'Final Standing',
    ofLabel: 'of',
    beatPlayers: 'Beat',
    playersLabel: 'players',
    tiedLabel: 'Tied',
    roundLabel: 'Round',
    inFieldLabel: 'in field',
    bestHole: 'Best Hole',
    worstHole: 'Room for Improvement',
    holeLabel: 'Hole',
    eaglesPlural: 'Eagles',
    eagleSingular: 'Eagle',
    outstanding: 'Outstanding!',
    birdiesPlural: 'Birdies',
    birdieSingular: 'Birdie',
    excellent: 'Excellent!',
    parsPlural: 'Pars',
    parSingular: 'Par',
    steady: 'Steady',
    underParLabel: 'Under Par',
    successRate: 'Success Rate',
    roundConsistency: 'Round Consistency',
    stabilityMetrics: 'Performance stability metrics',
    noBlowups: 'No Blow-ups',
    doubleBogeyPlus: 'Double Bogey+',
    parRate: 'Par Rate',
    atUnderPar: 'At/Under Par',
  },

  // Monthly competitions top-level (MonthlyCompetitionsDashboard.tsx)
  competitions: {
    monthlyCompetitions: 'Monthly Competitions',
    competeWithMembers: 'Compete with club members in various challenges',
    tryAgain: 'Try Again',
    initializing: 'Initializing...',
    initializeCompetitions: 'Initialize Competitions',
    initializeMonthlyCompetitions: 'Initialize Monthly Competitions',
    activeCompetitions: 'Active Competitions',
    totalParticipants: 'Total Participants',
    daysRemaining: 'Days Remaining',
    prizePool: 'Prize Pool',
    competitionOverview: 'Competition Overview',
    leaderboard: 'Leaderboard',
    noMonthlyCompetitionsYet: 'No Monthly Competitions Yet',
    noCompetitionsSetUp: "Monthly competitions haven't been set up for this club yet. Initialize them to start competing with other members!",
    quickActions: 'Quick Actions',
    addChampionship: 'Add Championship',
    addImprovement: 'Add Improvement',
    addSocial: 'Add Social',
    addIronMan: 'Add Iron Man',
  },

  // Multiplayer player selection (EnhancedPlayerSelection.tsx, FriendInviteSelector.tsx)
  multiplayer: {
    invitePlayers: 'Invite Players',
    selected: 'selected',
    tabRecent: 'Recent',
    tabContacts: 'Contacts',
    tabFriends: 'Friends',
    tabManual: 'Manual',
    loadingRecentPartners: 'Loading recent partners...',
    noRecentPartners: 'No recent playing partners found',
    playSomegamesForSuggestions: 'Play some games to see suggestions here',
    contactsApiNotSupported: 'Contacts API not supported',
    useManualEntry: 'Use manual entry to add players',
    accessContactsToInvite: 'Access your contacts to quickly invite players',
    syncingContacts: 'Syncing...',
    syncContacts: 'Sync Contacts',
    contactsStoredLocally: 'Contacts are stored locally and never shared',
    loadingContacts: 'Loading contacts...',
    contactsLabel: 'contacts',
    refresh: 'Refresh',
    noContactsFound: 'No contacts found',
    contactPermissionRequired: 'Contact permission required to sync contacts',
    syncedContacts: 'Synced',
    failedToSyncContacts: 'Failed to sync contacts',
    maxPlayersInvited: 'Maximum {max} players can be invited',
    enterNameAndContact: 'Please enter both name and contact info',
    addedToInvitations: 'Added {name} to invitations',
    usedTimes: 'Used {count} times',
    gamesTogether: 'games together',
    lastPlayed: 'Last played',
    playerName: 'Player Name',
    emailOrPhone: 'Email or Phone',
    addPlayer: 'Add Player',
    manualEntries: 'Manual Entries',
    remove: 'Remove',
    selectedPlayers: 'Selected Players',
    // FriendInviteSelector.tsx
    inviteFriends: 'Invite Friends',
    searchFriendsByName: 'Search friends by name...',
    friendsAvailablePlural: 'friends available',
    friendAvailableSingular: 'friend available',
    clearAll: 'Clear All',
    selectAll: 'Select All',
    loadingFriends: 'Loading friends...',
    noFriendsYet: 'No friends yet',
    addFriendsViaeSocial: 'Add friends through the social features to invite them to games',
    noFriendsMatchSearch: 'No friends match your search',
    tryDifferentSearchTerm: 'Try a different search term',
    invitationPreview: 'Invitation Preview',
    friendsWillBeInvitedPlural: "friends will be invited to join your game when it's created.",
    friendWillBeInvitedSingular: "friend will be invited to join your game when it's created.",

    analyticsSummary: {
      competitivePerformance: 'Competitive Performance',
      yourScore: 'Your Score',
      groupAverage: 'Group Average',
      vsAverage: 'vs Average',
      finalStandings: 'Final Standings',
      showMore: 'Show',
      morePlayers: 'more players',
      yourPerformanceAnalysis: 'Your Performance Analysis',
      eagles: 'Eagles',
      birdies: 'Birdies',
      pars: 'Pars',
      consistency: 'Consistency',
      bestHole: 'Best Hole',
      holePrefix: 'Hole',
    },
  },

  // Email templates (emails/templates/*)
  emails: {
    // ClubOnboardingInvitation.tsx
    clubOnboardingInvitation: {
      previewText: 'Join our golf club management platform - {clubName}',
      headerTitle: 'Platform Invitation',
      headerSubtitle: 'Join our golf club management platform',
      greeting: 'Hello {firstName}!',
      invitationIntro: "You're receiving this invitation on behalf of",
      invitationIntroLocation: 'in {location}',
      personalMessageTitle: 'Personal Message:',
      clubDetailsTitle: 'Club Details:',
      clubNameLabel: 'Club Name:',
      locationLabel: 'Location:',
      contactLabel: 'Contact:',
      ctaButton: 'Register Your Club',
      benefitsTitle: "What you'll get:",
      benefit1: 'Comprehensive member management system',
      benefit2: 'Event organization and registration tools',
      benefit3: 'Performance tracking and analytics',
      benefit4: 'Tournament management features',
      benefit5: 'Club communication tools',
      helpText: 'Questions? Contact our support team for assistance.',
      disclaimerText: 'This invitation was sent to {email} for {clubName}',
    },
  },
};
